import React, { useState, useMemo, useRef, useEffect } from "react";

/* ════════════════════ ERROR BOUNDARY ═══════════════════════════════════════ */
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{padding:30,fontFamily:"monospace",background:"#fff0f0",minHeight:"100vh",color:"#c00"}}>
          <h2>❌ Błąd aplikacji</h2>
          <p style={{fontWeight:700}}>{this.state.error.message}</p>
          <pre style={{fontSize:11,whiteSpace:"pre-wrap",background:"#fff",padding:16,borderRadius:8,border:"1px solid #fcc"}}>
            {this.state.error.stack}
          </pre>
          <button onClick={()=>window.location.reload()} style={{marginTop:16,padding:"10px 24px",background:"#c00",color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700}}>
            🔄 Przeładuj stronę
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ════════════════════ RESPONSIVE HOOK ═══════════════════════════════════════ */
const BreakpointContext = React.createContext(null);

function BreakpointProvider({ children }) {
  const [size, setSize] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth  : 1024,
    h: typeof window !== "undefined" ? window.innerHeight : 768,
  }));
  useEffect(() => {
    const fn = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", fn, { passive: true });
    return () => window.removeEventListener("resize", fn);
  }, []);
  const bp = {
    w: size.w,
    xs: size.w < 480, sm: size.w < 768, md: size.w < 1024, lg: size.w >= 1024,
    isMobile: size.w < 768, isTablet: size.w >= 768 && size.w < 1024, isDesktop: size.w >= 1024
  };
  return <BreakpointContext.Provider value={bp}>{children}</BreakpointContext.Provider>;
}

// Breakpoints: xs<480 sm<768 md<1024 lg>=1024
function useBreakpoint() {
  const bp = React.useContext(BreakpointContext);
  if (bp) return bp;
  // Fallback gdy używany poza BreakpointProvider
  const w = window.innerWidth;
  return { w, xs: w < 480, sm: w < 768, md: w < 1024, lg: w >= 1024, isMobile: w < 768, isTablet: w >= 768 && w < 1024, isDesktop: w >= 1024 };
}

/* ════════════════════ GLOBAL RESPONSIVE STYLES ══════════════════════════════ */
/* ═══════════════ BAKERY THEME PALETTE ══════════════════════════════════════
   Primary brown:  #6B3F1E   Accent amber:  #C8621A   Gold honey: #E8A020
   Cream bg:       #FDF6EC   Warm surface:  #FFFAF3   Muted:      #8B6A4A
   Border biscuit: #E8D5BA   Espresso dark: #3B1F0A
══════════════════════════════════════════════════════════════════════════ */
/* Kolory klienta (piekarniczo-cukiernicze) */
const TC = {
  bg:       "#FDF6EC",
  surface:  "#FFFAF3",
  primary:  "#6B3F1E",
  accent:   "#C8621A",
  gold:     "#E8A020",
  muted:    "#8B6A4A",
  border:   "#E8D5BA",
  dark:     "#3B1F0A",
  text:     "#3B1F0A",
  textSoft: "#8B6A4A",
  error:    "#C0392B",
  green:    "#27AE60",
  navBg:    "linear-gradient(135deg,#3B1F0A 0%,#6B3F1E 100%)",
  cardShadow: "0 2px 12px rgba(107,63,30,0.10)",
  inputBorder: "#D4B896",
};

/* Kolory admina (profesjonalny GPOS) */
const TA = {
  bg:        "#f0f2f5",
  surface:   "#ffffff",
  primary:   "#16213e",
  accent:    "#2563eb",
  accentSoft:"#eff6ff",
  muted:     "#6b7280",
  border:    "#e5e7eb",
  text:      "#111827",
  textSoft:  "#6b7280",
  error:     "#ef4444",
  green:     "#22c55e",
  orange:    "#f97316",
  sidebar:   "#16213e",
  cardShadow:"0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
  inputBorder:"#d1d5db",
};

/* T — alias używany w komponentach (domyślnie styl admina) */
const T = {
  ...TA,
  /* zachowujemy pole gold dla badge */
  gold: "#f59e0b",
  navBg: "linear-gradient(135deg,#16213e 0%,#0f3460 100%)",
};

const S = {
  navBtn:    {flex:1,padding:"9px",borderRadius:8,border:"none",background:"rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.75)",fontWeight:600,cursor:"pointer",fontSize:13,position:"relative",letterSpacing:0.1},
  badge:     {background:"#ef4444",color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:10,fontWeight:700,marginLeft:4},
  pill:      {padding:"6px 14px",borderRadius:7,border:"1.5px solid "+TA.border,background:"#fff",color:TA.muted,fontWeight:500,cursor:"pointer",fontSize:12,whiteSpace:"nowrap",transition:"all .15s"},
  pillOn:    {background:TA.accent,color:"#fff",border:"1.5px solid "+TA.accent},
  subPill:   {padding:"5px 12px",borderRadius:6,border:"1.5px solid "+TA.border,background:"#fff",color:TA.muted,fontWeight:500,cursor:"pointer",fontSize:11,whiteSpace:"nowrap"},
  subPillOn: {background:TA.accentSoft,color:TA.accent,border:"1.5px solid "+TA.accent,fontWeight:600},
  label:     {display:"block",fontSize:11,fontWeight:600,color:TA.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.6},
  inp:       {padding:"9px 12px",borderRadius:8,border:"1.5px solid "+TA.inputBorder,fontSize:13,outline:"none",width:"100%",boxSizing:"border-box",background:"#fff",color:TA.text,transition:"border .15s"},
  qBtn:      {width:28,height:28,borderRadius:6,border:"1.5px solid "+TA.border,background:"#fff",fontSize:15,cursor:"pointer",fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"},
  addBtn:    {padding:"8px 14px",borderRadius:7,color:"#fff",border:"none",fontWeight:600,cursor:"pointer",fontSize:12},
  card:      {background:"#fff",borderRadius:10,padding:14,boxShadow:TA.cardShadow,marginBottom:10,border:"1px solid "+TA.border},
  formCard:  {background:"#fff",borderRadius:10,padding:16,marginBottom:12,boxShadow:TA.cardShadow,border:"1px solid "+TA.border},
  formTitle: {fontWeight:700,fontSize:14,color:TA.text,marginBottom:10,paddingBottom:8,borderBottom:"1px solid "+TA.border},
  fGrid:     {display:"grid",gridTemplateColumns:"1fr 1fr",gap:10},
  fGridFull: {display:"grid",gridTemplateColumns:"1fr",gap:10},
  darkBtn:   {padding:"9px 18px",background:TA.accent,color:"#fff",border:"none",borderRadius:8,fontWeight:600,cursor:"pointer",fontSize:13},
  cancelBtn: {padding:"9px 18px",background:"#f3f4f6",color:TA.muted,border:"1px solid "+TA.border,borderRadius:8,fontWeight:500,cursor:"pointer",fontSize:12},
  actBtn:    {background:"#f9fafb",border:"1px solid "+TA.border,borderRadius:7,padding:"6px 10px",cursor:"pointer",fontSize:13,color:TA.muted},
  smBtn:     {display:"block",padding:"6px 12px",background:"#f9fafb",color:TA.muted,border:"1px solid "+TA.border,borderRadius:7,fontWeight:500,cursor:"pointer",fontSize:11},
  empty:     {textAlign:"center",padding:"48px 14px",color:TA.muted},
  accentBtn: col=>({width:"100%",padding:11,background:col||TA.accent,color:"#fff",border:"none",borderRadius:8,fontWeight:600,cursor:"pointer",fontSize:13,marginBottom:10,boxShadow:"0 2px 8px rgba(37,99,235,0.25)"}),
  backdrop:  {position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",padding:16},
  modalBox:  {background:"#fff",borderRadius:12,padding:22,width:"100%",maxWidth:480,maxHeight:"90vh",overflowY:"auto",border:"1px solid "+TA.border,boxShadow:"0 20px 60px rgba(0,0,0,0.18)"},
  closeBtn:  {background:"#f9fafb",border:"1px solid "+TA.border,borderRadius:7,padding:"5px 10px",cursor:"pointer",fontSize:13,fontWeight:500,color:TA.muted},
  dropZone:  {border:"2px dashed "+TA.border,borderRadius:9,padding:"22px 14px",textAlign:"center",cursor:"pointer",marginBottom:12,background:"#f9fafb"},
  previewBox:{borderRadius:9,overflow:"hidden",background:"#f9fafb",height:175,display:"flex",alignItems:"center",justifyContent:"center"},
};

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Playfair+Display:wght@700;900&display=swap');
    * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
    html { font-size: 16px; }
    body { margin: 0; padding: 0; overflow-x: hidden; font-family: 'Inter', system-ui, sans-serif; }
    input, button, select, textarea { font-family: 'Inter', system-ui, sans-serif; }
    img { max-width: 100%; }
    ::-webkit-scrollbar { width: 5px; height: 5px; }
    ::-webkit-scrollbar-track { background: #f0f2f5; }
    ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
    input:focus, select:focus, textarea:focus { outline: none; border-color: #2563eb !important; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
    @keyframes blikSpin { to { transform: rotate(360deg); } }
    @keyframes fadeSlide { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
    @keyframes popIn { from { transform:scale(0.92); opacity:0; } to { transform:scale(1); opacity:1; } }
    @keyframes slideIn { from { opacity:0; transform:translateX(-6px); } to { opacity:1; transform:translateX(0); } }
    .admin-card { animation: fadeSlide .18s ease; transition: box-shadow .15s; }
    .admin-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important; }
    .sidebar-item { transition: all .12s; }
    .sidebar-item:hover { background: rgba(255,255,255,0.08) !important; }
    .bottom-nav::-webkit-scrollbar { display: none; }
    .bottom-nav { -ms-overflow-style: none; scrollbar-width: none; }
    button:active { transform: scale(0.98); }
    .admin-panel input:focus { border-color: #2563eb !important; box-shadow: 0 0 0 3px rgba(37,99,235,0.12); }

    /* ── RESPONSIVE GRID ── */
    .resp-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .resp-grid-3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; }
    .resp-grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 10px; }

    /* Phone (< 480px) */
    @media (max-width: 479px) {
      .resp-grid-2 { grid-template-columns: 1fr; }
      .resp-grid-3 { grid-template-columns: 1fr 1fr; }
      .resp-grid-4 { grid-template-columns: 1fr 1fr; }
      .hide-xs { display: none !important; }
      .admin-content-pad { padding: 10px !important; }
      .menu-list-grid { grid-template-columns: 1fr !important; }
      .order-grid { grid-template-columns: repeat(2,1fr) !important; }
      .modal-box { padding: 14px !important; border-radius: 12px !important; }
      .form-grid-2 { grid-template-columns: 1fr !important; }
      .page-header { padding: 0 12px !important; font-size: 15px !important; }
      .bottom-nav button { min-width: 48px !important; padding: 7px 6px 9px !important; }
      .bottom-nav button span:last-child { font-size: 8px !important; }
    }

    /* Tablet (768–1023px) */
    @media (min-width: 768px) and (max-width: 1023px) {
      .resp-grid-3 { grid-template-columns: 1fr 1fr; }
      .resp-grid-4 { grid-template-columns: 1fr 1fr; }
      .menu-list-grid { display: grid !important; grid-template-columns: repeat(2,1fr) !important; }
      .order-grid { grid-template-columns: repeat(3,1fr) !important; }
      .sidebar-label { display: none !important; }
      .admin-content-pad { padding: 16px !important; }
      .form-grid-2 { grid-template-columns: 1fr 1fr !important; }
    }

    /* Desktop (>= 1024px) */
    @media (min-width: 1024px) {
      .resp-grid-3 { grid-template-columns: repeat(3,1fr); }
      .resp-grid-4 { grid-template-columns: repeat(4,1fr); }
      .menu-list-grid { display: grid !important; grid-template-columns: repeat(3,1fr) !important; }
      .order-grid { grid-template-columns: repeat(auto-fill,minmax(200px,1fr)) !important; }
      .admin-content-pad { padding: 24px !important; }
      .form-grid-2 { grid-template-columns: 1fr 1fr !important; }
    }

    /* Large desktop (>= 1440px) */
    @media (min-width: 1440px) {
      .menu-list-grid { grid-template-columns: repeat(4,1fr) !important; }
      .order-grid { grid-template-columns: repeat(auto-fill,minmax(220px,1fr)) !important; }
      .resp-grid-4 { grid-template-columns: repeat(4,1fr); }
    }

    /* Touch devices */
    @media (hover: none) {
      .admin-card:hover { box-shadow: none !important; }
      button:active { transform: scale(0.96); }
    }

    /* Safe area for phones with notch */
    @supports (padding: max(0px)) {
      .bottom-nav { padding-bottom: max(9px, env(safe-area-inset-bottom)) !important; }
      .customer-bottom { padding-bottom: max(14px, env(safe-area-inset-bottom)) !important; }
    }
  `}</style>
);

/* ═══════════════════════════════════ DATA ═══════════════════════════════════ */
/* Role definitions — notifications flags per role */
const ROLE_DEFS = {
  superadmin: { label:"Super Administrator", icon:"🛡", color:"#6d28d9", bg:"#ede9fe",
    notifications: { newOrder:true,  orderIssued:true,  newReservation:true,  lowStock:true  } },
  admin:  { label:"Główny administrator", icon:"👑", color:"#856404", bg:"#fff3cd",
    notifications: { newOrder:true,  orderIssued:true,  newReservation:true,  lowStock:true  } },
  seller: { label:"Sprzedawca",           icon:"🏷", color:"#15803d", bg:"#dcfce7",
    notifications: { newOrder:true,  orderIssued:false, newReservation:true,  lowStock:false } },
  cook:   { label:"Kucharz",              icon:"👨‍🍳", color:"#9a3412", bg:"#ffedd5",
    notifications: { newOrder:true,  orderIssued:false, newReservation:false, lowStock:true  } },
};
const NOTIF_LABELS = {
  newOrder:       "Nowe zamówienie",
  orderIssued:    "Zamówienie wydane",
  newReservation: "Nowa rezerwacja",
  lowStock:       "Niski stan produktów",
};

/* ── Statusy kuchenne zamówień ── */
const COOK_STATUS = {
  new:     { label: "Nowe",                    icon: "🆕", color: "#f39c12", bg: "#fff8e1", border: "#f39c12" },
  cooking: { label: "W trakcie przygotowania", icon: "👨‍🍳", color: "#0984e3", bg: "#e8f4fd", border: "#0984e3" },
  ready:   { label: "Gotowe do wydania",       icon: "🔔", color: "#27ae60", bg: "#e8f5e9", border: "#27ae60" },
};

/* ── Hasła haszowane PBKDF2-SHA-256 (hex). Nigdy nie przechowujemy plain-text.
   Domyślne: admin→admin123, sprzedawca→sell123, kucharz→cook123
   Aby zmienić hasło: Ustawienia → Użytkownicy → Edytuj → wpisz nowe hasło.
   PBKDF2 z 100 000 iteracji — odporny na ataki słownikowe.
   Sól = "smakidomu-v3-" + login (deterministyczna, unikalna per użytkownik). ── */
async function hashPassword(plain, login) {
  try {
    const saltStr = "smakidomu-v3-" + (login || "");
    const keyMaterial = await crypto.subtle.importKey(
      "raw", new TextEncoder().encode(plain), "PBKDF2", false, ["deriveBits"]
    );
    const bits = await crypto.subtle.deriveBits(
      { name: "PBKDF2", salt: new TextEncoder().encode(saltStr), iterations: 100000, hash: "SHA-256" },
      keyMaterial, 256
    );
    return Array.from(new Uint8Array(bits)).map(b => b.toString(16).padStart(2,"0")).join("");
  } catch(e) {
    // Fallback: SHA-256 (dla środowisk bez PBKDF2 — nie powinno się zdarzać)
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(plain));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,"0")).join("");
  }
}
// Stara funkcja SHA-256 — używana WYŁĄCZNIE do migracji starych hashy przy logowaniu
async function hashPasswordLegacy(plain) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(plain));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,"0")).join("");
}

/* Pre-computed PBKDF2(100k iter, sól="smakidomu-v3-"+login) hashes dla domyślnych haseł.
   admin→admin123, admin2→admin123, sprzedawca→sell123, kucharz→cook123
   UWAGA: Przy zmianie loginu hash przestaje być ważny — użytkownik musi zmienić hasło. */
const INIT_USERS = [
  { id: 1, login: "admin",       passwordHash: "5773208025dc4ca005fc2ddc7e781142c7d17cf47cd01f92c0254910db843a9f", role: "superadmin",  name: "Jan Kowalski",
    notifications: { newOrder:true,  orderIssued:true,  newReservation:true,  lowStock:true  } },
  /* Administrator 2 — rola "admin" (Główny administrator): pełny dostęp identyczny jak superadmin,
     ALE nie może zmieniać hasła ani usunąć konta Superadministratora (id:1).
     Domyślne hasło: admin123 */
  { id: 4, login: "admin2",      passwordHash: "1142aee43a040beaf46766fbdef71ed0ee91c3f562203a4d81324144332087db", role: "admin",       name: "Administrator 2",
    notifications: { newOrder:true,  orderIssued:true,  newReservation:true,  lowStock:true  } },
  { id: 2, login: "sprzedawca",  passwordHash: "928d59fe63252cebc1e2827749bba9f0aeb25d389b2523557eb67fbcfc9063b4", role: "seller", name: "Anna Nowak",
    notifications: { newOrder:true,  orderIssued:false, newReservation:true,  lowStock:false } },
  { id: 3, login: "kucharz",     passwordHash: "05d53170a3e6e301413599b71deda03655008c1d03cfa28d78930f3a36c1757c", role: "cook",   name: "Marek Wiśniewski",
    notifications: { newOrder:true,  orderIssued:false, newReservation:false, lowStock:true  } },
];

const INIT_CATS = [
  { id: "sniadania",      name: "Śniadania",         parentId: null,         emoji: "🍳" },
  { id: "przystawki",     name: "Przystawki",         parentId: null,         emoji: "🧆" },
  { id: "salatki",        name: "Sałatki",            parentId: null,         emoji: "🥗" },
  { id: "dla-dzieci",     name: "Dla dzieci",         parentId: null,         emoji: "👶" },
  { id: "burgery",        name: "Burgery",            parentId: null,         emoji: "🍔" },
  { id: "steki",          name: "Steki",              parentId: null,         emoji: "🥩" },
  { id: "meksykanskie",   name: "Dania meksykańskie", parentId: null,         emoji: "🌮" },
  { id: "keto",           name: "Dieta Keto",         parentId: null,         emoji: "🥑" },
  { id: "desery",         name: "Desery",             parentId: null,         emoji: "🍰" },
  { id: "wina",           name: "Wina",               parentId: null,         emoji: "🍷" },
  { id: "drinki",         name: "Drinki",             parentId: null,         emoji: "🍹" },
  { id: "piwo",           name: "Piwo",               parentId: null,         emoji: "🍺" },
  { id: "napoje-cieple",  name: "Napoje ciepłe",      parentId: null,         emoji: "☕" },
  { id: "napoje-zimne",   name: "Napoje zimne",        parentId: null,         emoji: "🧊" },
];

const INIT_MENU = [
  /* ── ŚNIADANIA ── */
  { id:101, catId:"sniadania", name:"Jajecznica",                price:16.00, promoPrice:null, wait:10, emoji:"🍳", desc:"3 jajka, masło, pieczywo", active:true, image:null, ingredients:"3 jajka, masło, pieczywo", allergens:"jaja, gluten, nabiał" },
  { id:102, catId:"sniadania", name:"Omlet",                     price:16.00, promoPrice:null, wait:12, emoji:"🥚", desc:"Z 4 jajek, 300g", active:true, image:null, ingredients:"4 jajka, masło", allergens:"jaja, nabiał" },
  { id:103, catId:"sniadania", name:"Śniadanie angielskie",      price:28.00, promoPrice:null, wait:15, emoji:"🍽", desc:"Frankfurterki, 2 jajka sadzone, fasolka, bekon, grzanka", active:true, image:null, ingredients:"frankfurterki, 2 jajka sadzone, fasolka, bekon, grzanka", allergens:"jaja, gluten, nabiał" },
  { id:104, catId:"sniadania", name:"Śniadanie Piekarza",        price:28.00, promoPrice:null, wait:15, emoji:"🥐", desc:"Gotowane jajko, ser, wędlina, warzywa, twaróg, koszyk pieczywa", active:true, image:null, ingredients:"gotowane jajko, ser, wędlina, warzywa, twaróg, koszyk pieczywa", allergens:"jaja, gluten, nabiał" },
  { id:105, catId:"sniadania", name:"Kanapki (witryna)",         price:17.00, promoPrice:null, wait:5,  emoji:"🥪", desc:"Kanapki z witryny", active:true, image:null, ingredients:"pieczywo, wędlina, ser, warzywa", allergens:"gluten, nabiał" },
  { id:106, catId:"sniadania", name:"Owsianka",                  price:17.00, promoPrice:null, wait:8,  emoji:"🥣", desc:"300g", active:true, image:null, ingredients:"płatki owsiane, mleko, owoce", allergens:"gluten, nabiał" },
  { id:107, catId:"sniadania", name:"Szakszuka",                 price:28.00, promoPrice:null, wait:15, emoji:"🍅", desc:"Pomidor, czosnek, papryka, cebula, 3 jajka", active:true, image:null, ingredients:"pomidor, czosnek, papryka, cebula, 3 jajka", allergens:"jaja" },
  { id:108, catId:"sniadania", name:"Jajko benedyktyńskie",      price:26.00, promoPrice:null, wait:15, emoji:"🍳", desc:"2 jajka w koszulce, pancetta, sos holenderski, parmezan, tost", active:true, image:null, ingredients:"2 jajka w koszulce, pancetta, sos holenderski, parmezan, tost", allergens:"jaja, gluten, nabiał" },
  { id:109, catId:"sniadania", name:"Tost",                      price:20.00, promoPrice:null, wait:8,  emoji:"🍞", desc:"Jajko, ser, bekon, tost 2 szt.", active:true, image:null, ingredients:"jajko, ser, bekon, tost 2 szt.", allergens:"jaja, gluten, nabiał" },
  { id:110, catId:"sniadania", name:"Śniadaniowy Wrap",          price:25.00, promoPrice:null, wait:10, emoji:"🌯", desc:"Jajka, cebula, pieczarki, ser, tortilla", active:true, image:null, ingredients:"jajka, cebula, pieczarki, ser, tortilla", allergens:"jaja, gluten, nabiał" },
  { id:111, catId:"sniadania", name:"Naleśnik",                  price:24.00, promoPrice:null, wait:10, emoji:"🥞", desc:"Z twarogiem lub dżemem owocowym, przysmażany na maśle", active:true, image:null, ingredients:"mąka pszenna, jajka, mleko, masło, twaróg lub dżem", allergens:"jaja, gluten, nabiał" },
  { id:112, catId:"sniadania", name:"Bajgiel jajko bekon",       price:22.00, promoPrice:null, wait:10, emoji:"🥯", desc:"Bajgiel z jajkiem i bekonem", active:true, image:null, ingredients:"bajgiel, jajko, bekon", allergens:"jaja, gluten, nabiał, sezam" },
  { id:113, catId:"sniadania", name:"Bajgiel Nowojorski z łososiem", price:25.00, promoPrice:null, wait:10, emoji:"🐟", desc:"Bajgiel z łososiem", active:true, image:null, ingredients:"bajgiel, łosoś wędzony, twaróg, kapary, cebula", allergens:"ryby, gluten, nabiał, sezam" },

  /* ── PRZYSTAWKI ── */
  { id:201, catId:"przystawki", name:"Paluchy czosnkowe",        price:20.00, promoPrice:null, wait:10, emoji:"🧄", desc:"Z ciasta drożdżowego, 5 szt.", active:true, image:null, ingredients:"ciasto drożdżowe, czosnek, masło, pietruszka", allergens:"gluten, nabiał" },
  { id:202, catId:"przystawki", name:"Nachos Guacamole",         price:30.00, promoPrice:null, wait:10, emoji:"🌽", desc:"Nachos z guacamole", active:true, image:null, ingredients:"nachos kukurydziane, awokado, pomidor, cebula, limonka, kolendra", allergens:"" },
  { id:203, catId:"przystawki", name:"Skrzydełka pieczone BBQ",  price:34.00, promoPrice:null, wait:20, emoji:"🍗", desc:"W sosie BBQ, 8 szt.", active:true, image:null, ingredients:"skrzydełka kurczaka, sos BBQ, czosnek, papryka", allergens:"" },
  { id:204, catId:"przystawki", name:"Tost Burrata-Awokado",     price:32.00, promoPrice:null, wait:10, emoji:"🥑", desc:"Awokado, pomidor, czosnek, burrata, oliwa, ocet balsamiczny", active:true, image:null, ingredients:"pieczywo, awokado, pomidor, czosnek, burrata, oliwa z oliwek, ocet balsamiczny", allergens:"gluten, nabiał" },
  { id:205, catId:"przystawki", name:"Krewetki Black Tiger",     price:50.00, promoPrice:null, wait:15, emoji:"🍤", desc:"Z sosem aioli oraz sweet chilli, 10 szt.", active:true, image:null, ingredients:"krewetki Black Tiger, sos aioli, sos sweet chilli, czosnek", allergens:"skorupiaki, jaja" },
  { id:206, catId:"przystawki", name:"Panierowane Kalmary",      price:42.00, promoPrice:null, wait:15, emoji:"🦑", desc:"Z sosem czosnkowym, 10 szt.", active:true, image:null, ingredients:"kalmary, panierka, sos czosnkowy", allergens:"gluten, jaja, mięczaki" },
  { id:207, catId:"przystawki", name:"Tatar Wołowy",             price:50.00, promoPrice:null, wait:10, emoji:"🥩", desc:"150g", active:true, image:null, ingredients:"wołowina 150g, ogórek kiszony, cebula, kapary, żółtko, przyprawy", allergens:"jaja, gorczyca" },
  { id:208, catId:"przystawki", name:"Frytki z batatów",         price:29.00, promoPrice:null, wait:15, emoji:"🍠", desc:"Z twarożkiem ziołowym, 200g", active:true, image:null, ingredients:"bataty, twarożek ziołowy, sól, olej", allergens:"nabiał" },
  { id:209, catId:"przystawki", name:"Frytki",                   price:14.00, promoPrice:null, wait:10, emoji:"🍟", desc:"200g", active:true, image:null, ingredients:"ziemniaki, sól, olej", allergens:"" },
  { id:210, catId:"przystawki", name:"Zupa dnia",                price:20.00, promoPrice:null, wait:10, emoji:"🍲", desc:"300g", active:true, image:null, ingredients:"składniki sezonowe", allergens:"gluten, nabiał" },
  { id:211, catId:"przystawki", name:"Zupa gulaszowa",           price:25.00, promoPrice:null, wait:10, emoji:"🍛", desc:"300g", active:true, image:null, ingredients:"wołowina, cebula, papryka, ziemniaki, przyprawy", allergens:"seler" },
  { id:212, catId:"przystawki", name:"Zupa flaczki",             price:25.00, promoPrice:null, wait:10, emoji:"🍜", desc:"300g", active:true, image:null, ingredients:"flaki wołowe, warzywa, majeranek, pieprz, imbir", allergens:"seler, gluten" },

  /* ── SAŁATKI ── */
  { id:301, catId:"salatki", name:"Cezar z kurczakiem",          price:42.00, promoPrice:null, wait:12, emoji:"🥗", desc:"400g", active:true, image:null, ingredients:"sałata rzymska, kurczak grillowany, parmezan, croutons, sos Caesar", allergens:"jaja, gluten, nabiał, ryby" },
  { id:302, catId:"salatki", name:"Cezar z krewetkami",          price:50.00, promoPrice:null, wait:12, emoji:"🥗", desc:"400g", active:true, image:null, ingredients:"sałata rzymska, krewetki, parmezan, croutons, sos Caesar", allergens:"skorupiaki, jaja, gluten, nabiał, ryby" },
  { id:303, catId:"salatki", name:"Sałatka Grecka",              price:40.00, promoPrice:null, wait:10, emoji:"🫒", desc:"400g", active:true, image:null, ingredients:"pomidor, ogórek, cebula czerwona, oliwki, feta, oregano", allergens:"nabiał" },
  { id:304, catId:"salatki", name:"Sałatka Nicejska",            price:42.00, promoPrice:null, wait:12, emoji:"🐟", desc:"400g", active:true, image:null, ingredients:"sałata, tuńczyk, jajko, pomidor, oliwki, anchois, dressing musztardowy", allergens:"ryby, jaja, gorczyca" },

  /* ── DLA DZIECI ── */
  { id:401, catId:"dla-dzieci", name:"Chicken Strips",           price:34.00, promoPrice:null, wait:15, emoji:"🍗", desc:"4 sztuki, frytki, ketchup", active:true, image:null, ingredients:"piersi z kurczaka w panierce, frytki, ketchup", allergens:"gluten, jaja" },
  { id:402, catId:"dla-dzieci", name:"Chicken Nuggets",          price:32.00, promoPrice:null, wait:15, emoji:"🐥", desc:"8 sztuk, frytki, ketchup", active:true, image:null, ingredients:"nuggetsy z kurczaka, frytki, ketchup", allergens:"gluten, jaja" },

  /* ── BURGERY ── */
  { id:501, catId:"burgery", name:"Burger Classic",              price:40.00, promoPrice:null, wait:15, emoji:"🍔", desc:"150g wołowiny, sałata, salsa lodowa, cebula, musztarda, ketchup + frytki + sosy", active:true, image:null, ingredients:"bułka pszenna, wołowina 150g, sałata, salsa lodowa, cebula, musztarda, ketchup, frytki", allergens:"gluten, nabiał, jaja, sezam, gorczyca" },
  { id:502, catId:"burgery", name:"Cheeseburger",                price:41.00, promoPrice:null, wait:15, emoji:"🧀", desc:"150g wołowiny, ser, sałata lodowa, cebula, musztarda, pikle, ketchup + frytki + sosy", active:true, image:null, ingredients:"bułka pszenna, wołowina 150g, ser, sałata lodowa, cebula, musztarda, pikle, ketchup, frytki", allergens:"gluten, nabiał, jaja, sezam, gorczyca" },
  { id:503, catId:"burgery", name:"Bekon Cheeseburger",          price:42.00, promoPrice:null, wait:15, emoji:"🥓", desc:"Bekon, 150g wołowiny, ser, sałata, pomidor, sos BBQ, mayo, cebula + frytki + sosy", active:true, image:null, ingredients:"bułka pszenna, bekon, wołowina 150g, ser, sałata lodowa, pomidor, sos BBQ, majonez, cebula, frytki", allergens:"gluten, nabiał, jaja, sezam, gorczyca" },
  { id:504, catId:"burgery", name:"Burger Jalapeño",             price:42.00, promoPrice:null, wait:15, emoji:"🌶", desc:"150g wołowiny, sałata, cebula, jalapeño, ser, sos BBQ, sos mayo + frytki + sosy", active:true, image:null, ingredients:"bułka pszenna, wołowina 150g, sałata, cebula, jalapeño, ser, sos BBQ, majonez, frytki", allergens:"gluten, nabiał, jaja, sezam" },
  { id:505, catId:"burgery", name:"Fish Burger",                 price:42.00, promoPrice:null, wait:15, emoji:"🐟", desc:"Półpółwiec z dorsza 150g, colesław, sos miodowo-pieprzowy + frytki + sosy", active:true, image:null, ingredients:"bułka pszenna, dorsz 150g, panierka, colesław, sos miodowo-pieprzowy, frytki", allergens:"gluten, nabiał, jaja, ryby, sezam, gorczyca" },
  { id:506, catId:"burgery", name:"Burger Chicken Buffalo",      price:42.00, promoPrice:null, wait:15, emoji:"🍗", desc:"Grillowany kurczak, sos buffalo, sałata, pikle, mayo", active:true, image:null, ingredients:"bułka pszenna, kurczak grillowany, sos buffalo, sałata, pikle, majonez", allergens:"gluten, nabiał, jaja, sezam" },
  { id:507, catId:"burgery", name:"Burger Crispy Chicken",       price:42.00, promoPrice:null, wait:15, emoji:"🍗", desc:"Udko kurczaka, sos burgerowy, sałata, ser, cebula", active:true, image:null, ingredients:"bułka pszenna, udko kurczaka w panierce, sos burgerowy, sałata, ser, cebula", allergens:"gluten, nabiał, jaja, sezam" },

  /* ── STEKI ── */
  { id:601, catId:"steki", name:"Stek 250g – puree",             price:110.00, promoPrice:null, wait:25, emoji:"🥩", desc:"Podany na puree ziemniaczanym z sosem grzybowym oraz warzywami masłanymi", active:true, image:null, ingredients:"stek wołowy 250g, puree ziemniaczane, sos grzybowy, warzywa masłane", allergens:"nabiał, seler" },
  { id:602, catId:"steki", name:"Stek 250g – frytki",            price:105.00, promoPrice:null, wait:25, emoji:"🥩", desc:"Z frytkami, sałatką sezonową oraz sosem chutney paprykowo-rozmarynowym", active:true, image:null, ingredients:"stek wołowy 250g, frytki, sałatka sezonowa, chutney paprykowo-rozmarynowy", allergens:"seler" },

  /* ── DANIA MEKSYKAŃSKIE ── */
  { id:701, catId:"meksykanskie", name:"Burrito 600g",           price:36.00, promoPrice:null, wait:15, emoji:"🌯", desc:"Tortilla pszenna, fasola, kukurydza, ryż, mięso wieprzowo-wołowe, papryka, kolendra", active:true, image:null, ingredients:"tortilla pszenna, fasola, kukurydza, ryż, mięso wieprzowo-wołowe, papryka, kolendra", allergens:"gluten" },
  { id:702, catId:"meksykanskie", name:"Burrito Vege 500g",      price:38.00, promoPrice:null, wait:15, emoji:"🥦", desc:"Tortilla pszenna, grillowane warzywa, szpinak, śmietana, salsa verde, ser", active:true, image:null, ingredients:"tortilla pszenna, grillowane warzywa, szpinak, śmietana, salsa verde, ser", allergens:"gluten, nabiał" },
  { id:703, catId:"meksykanskie", name:"Tacos 400g",             price:44.00, promoPrice:null, wait:15, emoji:"🌮", desc:"3 tortille kukurydziane, grillowany kurczak, mix sałat, sos, fasola, cebula, twarożek, jalapeño, pomidor", active:true, image:null, ingredients:"3 tortille kukurydziane, kurczak grillowany, mix sałat, fasola, cebula, twarożek, jalapeño, pomidor", allergens:"nabiał" },
  { id:704, catId:"meksykanskie", name:"Tacos 500g",             price:48.00, promoPrice:null, wait:15, emoji:"🌮", desc:"3 tortille z szarpaną wieprzowiną i serem, guacamole, pikłowana czerwona cebula z chilli, salsa pomidorowa, kolendra", active:true, image:null, ingredients:"3 tortille, szarpana wieprzowina, ser, guacamole, czerwona cebula, chilli, salsa pomidorowa, kolendra", allergens:"gluten, nabiał" },
  { id:705, catId:"meksykanskie", name:"Fajita 500g",            price:52.00, promoPrice:null, wait:20, emoji:"🫔", desc:"Do wyboru wołowina/kurczak smażone z kolorową papryką i cebulą, fasolą pinto, pomidor, twarożek, tortilla pszenna", active:true, image:null, ingredients:"wołowina lub kurczak, kolorowa papryka, cebula, fasola pinto, pomidor, twarożek, tortilla pszenna", allergens:"gluten, nabiał" },
  { id:706, catId:"meksykanskie", name:"Chili Con Carne 350g",   price:39.00, promoPrice:null, wait:15, emoji:"🌶", desc:"Wołowina, pomidor, cebula, fasola, kukurydza, chilli, śmietana, ser, makaron z tortilli", active:true, image:null, ingredients:"wołowina, pomidor, cebula, fasola, kukurydza, chilli, śmietana, ser, makaron z tortilli", allergens:"gluten, nabiał" },
  { id:707, catId:"meksykanskie", name:"Enchiladas 400g",        price:50.00, promoPrice:null, wait:20, emoji:"🫔", desc:"Pszenna tortilla zapiekana z serem, do wyboru wołowina/kurczak", active:true, image:null, ingredients:"tortilla pszenna, ser, wołowina lub kurczak, sos pomidorowy, śmietana", allergens:"gluten, nabiał" },

  /* ── DIETA KETO ── */
  { id:801, catId:"keto", name:"Jajecznica Keto",                price:29.00, promoPrice:null, wait:10, emoji:"🥑", desc:"3 jajka, bekon x5, sałatka warzywna, oliwa z oliwek, czosnek, pół awokado – węgle 3g, 400 kcal", active:true, image:null, ingredients:"3 jajka, bekon x5, sałatka warzywna, oliwa z oliwek, czosnek, awokado", allergens:"jaja" },
  { id:802, catId:"keto", name:"Jajka Sadzone Keto",             price:29.00, promoPrice:null, wait:10, emoji:"🍳", desc:"2 jajka, pół awokado, sałatka warzywna, czosnek, oliwa z oliwek – węgle 3g, 400 kcal", active:true, image:null, ingredients:"2 jajka, awokado, sałatka warzywna, czosnek, oliwa z oliwek", allergens:"jaja" },
  { id:803, catId:"keto", name:"Omlet Keto",                     price:29.00, promoPrice:null, wait:12, emoji:"🥚", desc:"3 jajka ze szpinakiem i pieczarkami – węgle 3g, 400 kcal", active:true, image:null, ingredients:"3 jajka, szpinak, pieczarki, masło", allergens:"jaja, nabiał" },
  { id:804, catId:"keto", name:"Tost Awokado Keto",              price:29.00, promoPrice:null, wait:10, emoji:"🥑", desc:"Jajka na wodzie 2 szt., ogórek zielony, twaróg, sałata, pomidor, sos winegret – 3g, 400 kcal", active:true, image:null, ingredients:"jajka w koszulce, ogórek zielony, twaróg, sałata, pomidor, sos winegret", allergens:"jaja, nabiał" },
  { id:805, catId:"keto", name:"Koktajl Keto Orzechowy",         price:22.00, promoPrice:null, wait:5,  emoji:"🥥", desc:"Mleko kokosowe 200ml, masło orzechowe 20g, olej MCT 5g, proszek proteinowy waniliowy 20g – 300ml, 364 kcal", active:true, image:null, ingredients:"mleko kokosowe, masło orzechowe, olej MCT, proszek proteinowy waniliowy, sól kłodawska", allergens:"orzechy" },
  { id:806, catId:"keto", name:"Koktajl Keto Owocowy",           price:22.00, promoPrice:null, wait:5,  emoji:"🫐", desc:"Owoce leśne 100g – 57,4 kcal", active:true, image:null, ingredients:"owoce leśne, mleko kokosowe", allergens:"" },
  { id:807, catId:"keto", name:"Burger Keto",                    price:50.00, promoPrice:null, wait:20, emoji:"🍔", desc:"160g wołowiny, tost 2 szt., sałata, pomidor, bekon, ser żółty, jajko sadzone, parmezan, majonez – białko 39g, tłuszcz 34g, węgle 2g", active:true, image:null, ingredients:"wołowina 160g, tost 2 szt., sałata, pomidor, bekon, ser żółty, jajko sadzone, parmezan, majonez", allergens:"jaja, gluten, nabiał, gorczyca" },

  /* ── DESERY ── */
  { id:901, catId:"desery", name:"Czekoladowa Rozkosz",          price:27.00, promoPrice:null, wait:8,  emoji:"🍫", desc:"Deser lodowy – śmietana, lód, owocowy mus, 200g", active:true, image:null, ingredients:"śmietana, lody czekoladowe, mus owocowy", allergens:"nabiał, jaja" },
  { id:902, catId:"desery", name:"Waniliowy Przysmak",           price:27.00, promoPrice:null, wait:8,  emoji:"🍨", desc:"Deser lodowy – mascarpone, lód, owoce, beza, 200g", active:true, image:null, ingredients:"mascarpone, lody waniliowe, owoce, beza", allergens:"nabiał, jaja, gluten" },
  { id:903, catId:"desery", name:"Orzeźwiający Sorbet",          price:27.00, promoPrice:null, wait:8,  emoji:"🍧", desc:"Deser lodowy – beza, sorbet, bita śmietana, kruszonka, sos, 200g", active:true, image:null, ingredients:"beza, sorbet owocowy, bita śmietana, kruszonka", allergens:"nabiał, jaja, gluten" },
  { id:904, catId:"desery", name:"Szarlotka Jabłkowa",           price:27.00, promoPrice:null, wait:10, emoji:"🍎", desc:"Na gorąco z lodami waniliowymi, 150g", active:true, image:null, ingredients:"jabłka, ciasto kruche, cynamon, lody waniliowe", allergens:"gluten, nabiał, jaja" },
  { id:905, catId:"desery", name:"Deser Tiramisu",               price:27.00, promoPrice:null, wait:8,  emoji:"☕", desc:"150g", active:true, image:null, ingredients:"mascarpone, biszkopty, espresso, kakao, jajka", allergens:"nabiał, jaja, gluten" },
  { id:906, catId:"desery", name:"Sernik na Gorąco",             price:25.00, promoPrice:null, wait:10, emoji:"🧀", desc:"Z powidłami i owocami, 150g", active:true, image:null, ingredients:"twaróg, jajka, cukier, mąka, powidła, owoce", allergens:"nabiał, jaja, gluten" },
  { id:907, catId:"desery", name:"Porcja tortu",                 price:18.00, promoPrice:null, wait:5,  emoji:"🎂", desc:"Lub innego wybranego ciasta, 120g", active:true, image:null, ingredients:"ciasto sezonowe", allergens:"gluten, nabiał, jaja" },
  { id:908, catId:"desery", name:"Lody gałka",                   price:9.00,  promoPrice:null, wait:3,  emoji:"🍦", desc:"60g", active:true, image:null, ingredients:"lody", allergens:"nabiał, jaja" },
  { id:909, catId:"desery", name:"Gofr z owocami",               price:25.00, promoPrice:null, wait:10, emoji:"🧇", desc:"Z owocami i bitą śmietaną", active:true, image:null, ingredients:"ciasto gofra, owoce, bita śmietana", allergens:"gluten, nabiał, jaja" },
  { id:910, catId:"desery", name:"Naleśnik z owocami",           price:25.00, promoPrice:null, wait:10, emoji:"🥞", desc:"Z owocami i bitą śmietaną", active:true, image:null, ingredients:"ciasto naleśnikowe, owoce, bita śmietana", allergens:"gluten, nabiał, jaja" },
  { id:911, catId:"desery", name:"Beza Pavlova",                 price:27.00, promoPrice:null, wait:8,  emoji:"🍰", desc:"Beza Pavlova", active:true, image:null, ingredients:"beza, śmietana, owoce świeże", allergens:"jaja, nabiał" },

  /* ── WINA ── */
  { id:1001, catId:"wina", name:"Lamita Chardonnay",             price:95.00, promoPrice:null, wait:2,  emoji:"🍾", desc:"Białe wytrawne, 0,75l", active:true, image:null, ingredients:"wino białe wytrawne Chardonnay", allergens:"siarczyny" },
  { id:1002, catId:"wina", name:"Lamita Cabernet Sauvignon",     price:95.00, promoPrice:null, wait:2,  emoji:"🍷", desc:"Czerwone wytrawne, 0,75l", active:true, image:null, ingredients:"wino czerwone wytrawne Cabernet Sauvignon", allergens:"siarczyny" },
  { id:1003, catId:"wina", name:"Sol del Mediterráneo Moscatel", price:95.00, promoPrice:null, wait:2,  emoji:"🍷", desc:"Białe słodkie, 0,75l", active:true, image:null, ingredients:"wino białe słodkie Moscatel", allergens:"siarczyny" },
  { id:1004, catId:"wina", name:"Turnau Johanniter",             price:130.00, promoPrice:null, wait:2, emoji:"🍾", desc:"Białe półsłodkie, 0,75l", active:true, image:null, ingredients:"wino białe półsłodkie Johanniter", allergens:"siarczyny" },

  /* ── DRINKI ── */
  { id:1101, catId:"drinki", name:"Aperol",                      price:28.00, promoPrice:null, wait:5,  emoji:"🍊", desc:"400ml", active:true, image:null, ingredients:"Aperol, prosecco, woda gazowana, pomarańcza", allergens:"siarczyny" },
  { id:1102, catId:"drinki", name:"Mimosa",                      price:23.00, promoPrice:null, wait:5,  emoji:"🥂", desc:"Prosecco, świeży sok z pomarańczy, 200ml", active:true, image:null, ingredients:"prosecco, świeży sok z pomarańczy", allergens:"siarczyny" },
  { id:1103, catId:"drinki", name:"Prosecco z nalewaka",         price:11.00, promoPrice:null, wait:3,  emoji:"🥂", desc:"100ml", active:true, image:null, ingredients:"prosecco", allergens:"siarczyny" },
  { id:1104, catId:"drinki", name:"Prosecco",                    price:20.00, promoPrice:null, wait:3,  emoji:"🥂", desc:"200ml, smak malinowy lub inna – zapytaj obsługę", active:true, image:null, ingredients:"prosecco", allergens:"siarczyny" },
  { id:1105, catId:"drinki", name:"Martini Dry / Bianco",        price:22.00, promoPrice:null, wait:5,  emoji:"🍸", desc:"100ml", active:true, image:null, ingredients:"Martini Dry lub Bianco", allergens:"siarczyny" },
  { id:1106, catId:"drinki", name:"Martini z miętą i limonką",   price:28.00, promoPrice:null, wait:5,  emoji:"🍸", desc:"Z lodem, 300ml", active:true, image:null, ingredients:"Martini, mięta, limonka, lód", allergens:"siarczyny" },
  { id:1107, catId:"drinki", name:"Wino grzane z miodem",        price:25.00, promoPrice:null, wait:5,  emoji:"🍷", desc:"Grzane wino z miodem i przyprawami", active:true, image:null, ingredients:"wino czerwone, miód, cynamon, goździki, pomarańcza", allergens:"siarczyny" },

  /* ── PIWO ── */
  { id:1201, catId:"piwo", name:"Pilsner Urquell",               price:17.00, promoPrice:null, wait:3,  emoji:"🍺", desc:"0,5l", active:true, image:null, ingredients:"piwo jasne pełne", allergens:"gluten" },
  { id:1202, catId:"piwo", name:"Połczyn Miodowe",               price:14.00, promoPrice:null, wait:3,  emoji:"🍺", desc:"0,5l", active:true, image:null, ingredients:"piwo miodowe", allergens:"gluten" },
  { id:1203, catId:"piwo", name:"Książęce Pszeniczne",           price:14.00, promoPrice:null, wait:3,  emoji:"🍺", desc:"0,5l", active:true, image:null, ingredients:"piwo pszeniczne", allergens:"gluten" },
  { id:1204, catId:"piwo", name:"Lech Free (bezalkoholowe)",     price:10.00, promoPrice:null, wait:3,  emoji:"🍺", desc:"330ml, bezalkoholowe", active:true, image:null, ingredients:"piwo bezalkoholowe", allergens:"gluten" },
  { id:1205, catId:"piwo", name:"Piwo na gorąco z miodem",       price:20.00, promoPrice:null, wait:5,  emoji:"🍯", desc:"0,5l", active:true, image:null, ingredients:"piwo, miód, przyprawy", allergens:"gluten" },

  /* ── NAPOJE CIEPŁE ── */
  { id:1301, catId:"napoje-cieple", name:"Caffe Crema / Americano", price:10.00, promoPrice:null, wait:3, emoji:"☕", desc:"", active:true, image:null, ingredients:"kawa, woda", allergens:"" },
  { id:1302, catId:"napoje-cieple", name:"Espresso",             price:10.00, promoPrice:null, wait:3,  emoji:"☕", desc:"Pojedyncze / podwójne 16 zł", active:true, image:null, ingredients:"kawa espresso", allergens:"" },
  { id:1303, catId:"napoje-cieple", name:"Espresso Macchiato",   price:10.00, promoPrice:null, wait:3,  emoji:"☕", desc:"", active:true, image:null, ingredients:"espresso, mleko", allergens:"nabiał" },
  { id:1304, catId:"napoje-cieple", name:"Cappuccino",           price:14.00, promoPrice:null, wait:4,  emoji:"☕", desc:"", active:true, image:null, ingredients:"espresso, mleko spienione", allergens:"nabiał" },
  { id:1305, catId:"napoje-cieple", name:"Café Latte",           price:14.00, promoPrice:null, wait:4,  emoji:"☕", desc:"", active:true, image:null, ingredients:"espresso, mleko", allergens:"nabiał" },
  { id:1306, catId:"napoje-cieple", name:"Caffe Latte Macchiato",price:16.00, promoPrice:null, wait:4,  emoji:"☕", desc:"", active:true, image:null, ingredients:"espresso, mleko warstwowe", allergens:"nabiał" },
  { id:1307, catId:"napoje-cieple", name:"ICE Coffee",           price:20.00, promoPrice:null, wait:5,  emoji:"🧊", desc:"", active:true, image:null, ingredients:"espresso, mleko, lód", allergens:"nabiał" },
  { id:1308, catId:"napoje-cieple", name:"Caffe Mokka",          price:20.00, promoPrice:null, wait:5,  emoji:"🍫", desc:"Z syropem czekoladowym i bitą śmietaną", active:true, image:null, ingredients:"espresso, syrop czekoladowy, mleko, bita śmietana", allergens:"nabiał" },
  { id:1309, catId:"napoje-cieple", name:"Herbata",              price:10.00, promoPrice:null, wait:3,  emoji:"🍵", desc:"Różne do wyboru", active:true, image:null, ingredients:"herbata liściasta lub ekspresowa", allergens:"" },
  { id:1310, catId:"napoje-cieple", name:"Herbata zielona z owocami", price:22.00, promoPrice:null, wait:5, emoji:"🍵", desc:"", active:true, image:null, ingredients:"herbata zielona, owoce", allergens:"" },
  { id:1311, catId:"napoje-cieple", name:"Herbata zimowa z rumem",price:26.00, promoPrice:null, wait:5, emoji:"🍵", desc:"350ml", active:true, image:null, ingredients:"herbata, rum, miód, cytryna, przyprawy korzenne", allergens:"" },
  { id:1312, catId:"napoje-cieple", name:"Herbata zimowa",       price:22.00, promoPrice:null, wait:5,  emoji:"🍵", desc:"", active:true, image:null, ingredients:"herbata, miód, cytryna, przyprawy korzenne", allergens:"" },
  { id:1313, catId:"napoje-cieple", name:"Czekolada na gorąco duża", price:23.00, promoPrice:null, wait:5, emoji:"🍫", desc:"350ml", active:true, image:null, ingredients:"czekolada, mleko, śmietana", allergens:"nabiał, soja" },
  { id:1314, catId:"napoje-cieple", name:"Czekolada na gorąco mała", price:21.00, promoPrice:null, wait:5, emoji:"🍫", desc:"200ml", active:true, image:null, ingredients:"czekolada, mleko, śmietana", allergens:"nabiał, soja" },

  /* ── NAPOJE ZIMNE ── */
  { id:1401, catId:"napoje-zimne", name:"Woda",                  price:8.00,  promoPrice:null, wait:2,  emoji:"💧", desc:"330ml", active:true, image:null, ingredients:"woda mineralna", allergens:"" },
  { id:1402, catId:"napoje-zimne", name:"Cisowianka Perlage",    price:9.00,  promoPrice:null, wait:2,  emoji:"💧", desc:"330ml, musująca", active:true, image:null, ingredients:"woda mineralna gazowana", allergens:"" },
  { id:1403, catId:"napoje-zimne", name:"Pepsi / Coca-Cola",     price:9.00,  promoPrice:null, wait:2,  emoji:"🥤", desc:"0,2l", active:true, image:null, ingredients:"napój gazowany", allergens:"" },
  { id:1404, catId:"napoje-zimne", name:"7UP / Sprite",          price:9.00,  promoPrice:null, wait:2,  emoji:"🥤", desc:"0,2l", active:true, image:null, ingredients:"napój gazowany", allergens:"" },
  { id:1405, catId:"napoje-zimne", name:"Mirinda / Fanta",       price:9.00,  promoPrice:null, wait:2,  emoji:"🥤", desc:"0,2l", active:true, image:null, ingredients:"napój gazowany", allergens:"" },
  { id:1406, catId:"napoje-zimne", name:"Lipton Ice Tea",        price:9.00,  promoPrice:null, wait:2,  emoji:"🍹", desc:"0,2l", active:true, image:null, ingredients:"herbata mrożona", allergens:"" },
  { id:1407, catId:"napoje-zimne", name:"Red Bull",              price:15.00, promoPrice:null, wait:2,  emoji:"⚡", desc:"250ml", active:true, image:null, ingredients:"woda, sacharoza, glukoza, tauryna, kofeina, witaminy B", allergens:"" },
  { id:1408, catId:"napoje-zimne", name:"Sok pomarańczowy",      price:9.00,  promoPrice:null, wait:2,  emoji:"🍊", desc:"200ml", active:true, image:null, ingredients:"sok pomarańczowy", allergens:"" },
  { id:1409, catId:"napoje-zimne", name:"Sok jabłkowy",          price:9.00,  promoPrice:null, wait:2,  emoji:"🍏", desc:"200ml", active:true, image:null, ingredients:"sok jabłkowy", allergens:"" },
  { id:1410, catId:"napoje-zimne", name:"Mojito Free",           price:23.00, promoPrice:null, wait:5,  emoji:"🍃", desc:"300ml, bezalkoholowe", active:true, image:null, ingredients:"limonka, mięta, cukier, woda gazowana, lód", allergens:"" },
  { id:1411, catId:"napoje-zimne", name:"Lemonade dnia",         price:16.00, promoPrice:null, wait:5,  emoji:"🍋", desc:"Lemoniada dnia", active:true, image:null, ingredients:"cytryna, cukier, woda, mięta, lód", allergens:"" },
  { id:1412, catId:"napoje-zimne", name:"Shake",                 price:20.00, promoPrice:null, wait:7,  emoji:"🥤", desc:"300ml – do wyboru: bananowy, truskawkowy lub malinowy", active:true, image:null, ingredients:"mleko, lody, owoce, bita śmietana", allergens:"nabiał, jaja" },
  { id:1413, catId:"napoje-zimne", name:"Sok świeżo wyciskany mały", price:18.00, promoPrice:null, wait:5, emoji:"🍊", desc:"200ml – pomarańcza lub grapefruit", active:true, image:null, ingredients:"świeże pomarańcze lub grejpfrut", allergens:"" },
  { id:1414, catId:"napoje-zimne", name:"Sok świeżo wyciskany duży", price:24.00, promoPrice:null, wait:5, emoji:"🍊", desc:"350ml – pomarańcza lub grapefruit", active:true, image:null, ingredients:"świeże pomarańcze lub grejpfrut", allergens:"" },
];

const INIT_SETTINGS = {
  restaurantName:"I.K. Pokutyńscy", logo:null, accentColor:"#C8621A",
  address: "", nip: "", phone: "", email: "",
  receipt: {
    showLogo:     true,
    companyLine:  "I.K. Pokutyńscy",
    addressLine:  "ul. Przykładowa 1, 00-000 Warszawa",
    nipLine:      "NIP: 000-000-00-00",
    confirmLabel: "Potwierdzenie zamówienia",
    footerLine:   "Dziekujemy za wizyte!\nSmacznego!",
    footerNote:   "",
  },
  paymentMethods: {
    blik:    { enabled: true,  label: "BLIK",    icon: "📱", color: "#C8621A", desc: "Szybka płatność telefonem" },
    card:    { enabled: true,  label: "Karta",   icon: "💳", color: "#0984e3", desc: "Terminal przy kasie" },
    cash:    { enabled: true,  label: "Gotówka", icon: "💵", color: "#27ae60", desc: "Zapłać przy odbiorze" },
    paypal:  { enabled: false, label: "PayPal",  icon: "🅿", color: "#003087", desc: "Płatność przez PayPal" },
  },
  paymentApiKeys: {
    blik:   { apiKey: "", merchantId: "", sandbox: true, provider: "Przelewy24" },
    card:   { apiKey: "", merchantId: "", sandbox: true, provider: "Stripe" },
    paypal: { clientId: "", secret: "", sandbox: true, provider: "PayPal" },
  },
  qrEnabled:  true,
  nfcEnabled: true,
  outdoorTablesEnabled: false,
  takeawayEnabled: true,
  deliveryEnabled: true,
  deliveryZones: [
    { id:1, name:"Strefa 1", kmFrom:0, kmTo:3,  price:5 },
    { id:2, name:"Strefa 2", kmFrom:3, kmTo:7,  price:10 },
    { id:3, name:"Strefa 3", kmFrom:7, kmTo:15, price:15 },
  ],
  imgbbApiKey: "",
  customerBgStyle: "bakery",
  customerBgColor: "#FDF6EC",
  customerBgImage: null,
  tableSelectorOpacity: 0.97,
  navbarBgColor: "#2C1509",
  navbarOpacity: 1.0,
  navbarHidden: false,
  cartBarBgColor: "#16213e",
  cartBarOpacity: 1.0,
  vouchersEnabled: true,
  dailySpecials: {
    enabled: false,
    title: "🌟 Polecamy dzisiaj",
    subtitle: "Sprawdź nasze propozycje na dziś!",
    accentColor: "#C8621A",
    bgColor: "#FDF6EC",
    items: [],
    showDate: true,
    autoShowOnEntry: true,
  },
  privacyPolicy: {
    enabled: true,
    title: "Polityka prywatności",
    content: "Korzystając z naszego systemu zamówień, wyrażasz zgodę na przetwarzanie danych osobowych (numer stolika, treść zamówienia) przez restaurację wyłącznie w celu realizacji zamówienia.\n\nDane nie są udostępniane podmiotom trzecim i są przechowywane przez czas niezbędny do obsługi zamówienia.\n\nMasz prawo dostępu do swoich danych, ich sprostowania i usunięcia.\n\nAdministratorem danych jest właściciel restauracji.",
    acceptLabel: "Akceptuję i przechodzę do zamówienia",
    declineLabel: "Nie akceptuję",
    declineMessage: "Bez akceptacji polityki prywatności zamówienie online nie jest możliwe. Zapraszamy do złożenia zamówienia bezpośrednio u kelnera lub przy kasie. 🙏",
  },
  menuHeaderBgColor: "#16213e",
  menuHeaderOpacity: 1.0,
  bannersTop:[
    { id:1, image:null, label:"🍔 Burgery od 40 zł",            sub:"Świeże składniki każdego dnia",           link:"burgery"      },
    { id:2, image:null, label:"🥑 Dieta Keto",                  sub:"Dania keto – zdrowo i smacznie",         link:"keto"         },
    { id:3, image:null, label:"🌮 Dania meksykańskie",          sub:"Burrito, tacos, fajita i więcej",        link:"meksykanskie" },
  ],
  bannersBottom:[
    { id:1, image:null, label:"🍳 Śniadania cały dzień",        sub:"Serwujemy śniadania przez całą dobę",    link:"sniadania"    },
    { id:2, image:null, label:"🍰 Desery i ciasta",             sub:"Domowe wypieki Cukierni Pokutyńskich",   link:"desery"       },
  ],
  /* Widoczność kategorii menu panelu admina per rola.
     superadmin zawsze widzi wszystko — nie można tego zablokować.
     null / brak klucza = domyślna widoczność (jak dotychczas). */
  menuVisibility: {
    admin:  { menu:true, promo:true, cats:true, tables:true, takeaway:true, online:true, clients:true, campaigns:true, vouchers:true, sales:true, messages:true, settings:true, extras:true, ksef:true, specials:true },
    seller: { tables:true, takeaway:true, online:true, clients:true, specials:true },
    cook:   {},
  },
  adminPermissions: {
    adminVouchers:      true,
    adminPrivacyPolicy: true,
    adminQrNfc:         true,
    adminNfcCards:      true,
    adminTakeaway:      true,
    adminReceipt:       true,
    adminLogs:          true,
  },
};

const INIT_CAMPAIGNS = [
  {
    id:1, name:"Weekend Burger Fest", active:true,
    dateFrom:"2025-01-01", dateTo:"2099-12-31",
    image:null, accentColor:"#e94560",
    headline:"🍔 Weekend Burger Fest!",
    desc:"Kup dowolnego burgera i zgarnij kupon na 10% zniżki na kolejne zamówienie.",
    voucherCode:"BURGER10",
    ctaLabel:"Odbierz kupon →",
    showOnTop:true, showModal:true,
  },
];

const INIT_VOUCHERS = [
  { id:1, code:"BURGER10", type:"percent", value:10, minOrder:0,  active:true, usedCount:0, maxUses:null, desc:"10% na kolejne zamówienie" },
  { id:2, code:"PIZZA5",   type:"amount",  value:5,  minOrder:30, active:true, usedCount:0, maxUses:50,   desc:"5 zł przy zamówieniu pizza za min. 30 zł" },
];
const INIT_TABLES = Array.from({ length:20 }, (_,i) => ({
  id: i+1, number: i+1, seats: 4, active: true,
  reservation: null, qrEnabled: true, nfcEnabled: true
}));
const todayStr = () => new Date().toISOString().split("T")[0];
const genId = () => (typeof crypto !== "undefined" && crypto.randomUUID)
  ? crypto.randomUUID()
  : Date.now().toString(36) + Math.random().toString(36).slice(2);
// ── Globalny logger aktywności (klik/wejście w zakładkę) — zapisuje do logs (Firebase) ──
function logActivity(setLogs, user, action, details) {
  try {
    if (!setLogs) return;
    const entry = {
      id: genId(),
      ts: new Date().toISOString(),
      type: "activity",
      login: user?.login || "",
      name:  user?.name  || "",
      role:  user?.role  || "",
      action: action || "",
      details: details || "",
    };
    setLogs(p => [...(p||[]), entry].slice(-2000));
  } catch(e) {}
}
const fmt = n => Number(n).toFixed(2);
const ep  = item => item.promoPrice != null ? item.promoPrice : item.price;
const getRoots    = cats => cats.filter(c => !c.parentId);
const getChildren = (cats,pid) => cats.filter(c => c.parentId === pid);
const getDescendantIds = (cats,id) => { const kids=getChildren(cats,id).map(c=>c.id); return [id,...kids]; };

/* ════════════════════ FIREBASE STORAGE UPLOAD ═══════════════════════════════
   Uploaduje plik do Firebase Storage i zwraca publiczny download URL.
   Wymaga skonfigurowanego Firebase (storageBucket w konfiguracji).
   Dokumentacja REST API: https://firebase.google.com/docs/storage/web/upload-files
══════════════════════════════════════════════════════════════════════════════ */
// eslint-disable-next-line no-unused-vars
async function uploadToFirebaseStorage(file, path) {
  const cfg = getFirebaseConfig();
  if (!cfg || !cfg.storageBucket) {
    // Fallback: base64 gdy Storage niezskonfigurowane
    return new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
  }

  const bucket = cfg.storageBucket.replace("gs://", "").replace(/\/$/, "");
  const encodedPath = encodeURIComponent(path);
  const uploadUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o?uploadType=media&name=${encodedPath}`;

  const headers = { "Content-Type": file.type || "image/jpeg" };
  const res = await fetch(uploadUrl, { method: "POST", headers, body: file });
  if (!res.ok) {
    const err = await res.text();
    throw new Error("Storage upload failed: " + err);
  }
  const data = await res.json();

  // Zwróć publiczny URL z tokenem dostępu
  const token = data.downloadTokens;
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media&token=${token}`;
}

async function uploadToImgbb(apiKey, file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result.replace(/^data:image\/[a-z]+;base64,/, "");
        const fd = new FormData();
        fd.append("key", apiKey);
        fd.append("image", base64);
        const res  = await fetch("https://api.imgbb.com/1/upload", { method: "POST", body: fd });
        let json = null;
        try { json = await res.json(); } catch(e) { resolve(null); return; }
        resolve(json && json.success ? json.data.url : null);
      } catch(e) { resolve(null); }
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}
function ImagePickerModal({ title, current, onSave, onClose, imgbbApiKey }) {
  const [mode,      setMode]      = useState("upload");
  const [urlVal,    setUrlVal]    = useState("");
  const [preview,   setPreview]   = useState(current || null);
  const [urlErr,    setUrlErr]    = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleFile = async e => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 32 * 1024 * 1024) { alert("Maks. 32 MB"); return; }
    e.target.value = "";
    if (imgbbApiKey) {
      setUploading(true); setUrlErr("");
      const url = await uploadToImgbb(imgbbApiKey, f);
      setUploading(false);
      if (url) { setPreview(url); }
      else { setUrlErr("❌ Błąd uploadu — sprawdź klucz Imgbb w Ustawieniach"); }
    } else {
      const r = new FileReader();
      r.onload = () => setPreview(r.result);
      r.readAsDataURL(f);
    }
  };

  const tryUrl = () => {
    if (!urlVal.trim()) return;
    const img = new Image();
    img.onload  = () => { setPreview(urlVal.trim()); setUrlErr(""); };
    img.onerror = () => setUrlErr("Nie można załadować obrazu z tego URL");
    img.src = urlVal.trim();
  };

  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <span style={{fontWeight:800,fontSize:15,color:T.text}}>{title || "📷 Zdjęcie"}</span>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {imgbbApiKey ? (
          <div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"6px 10px",
            fontSize:11,color:"#15803d",fontWeight:700,marginBottom:12}}>
            ☁ Imgbb aktywne — zdjęcia dostępne na wszystkich urządzeniach
          </div>
        ) : (
          <div style={{background:"#FFF8E1",border:"1px solid #f0c040",borderRadius:8,padding:"6px 10px",
            fontSize:11,color:"#856404",marginBottom:12}}>
            ⚠ Brak klucza Imgbb — zdjęcia zapisane tylko lokalnie. Dodaj klucz w Ustawieniach → Synchronizacja.
          </div>
        )}

        <div style={{display:"flex",gap:6,marginBottom:12}}>
          {[["upload","📁 Z urządzenia"],["url","🔗 Link URL"]].map(([k,l]) => (
            <button key={k} style={{...S.pill,...(mode===k?S.pillOn:{}),flex:1,fontSize:12}} onClick={()=>setMode(k)}>{l}</button>
          ))}
        </div>

        {mode === "upload" ? (
          <div style={{...S.dropZone,cursor:uploading?"wait":"pointer"}} onClick={()=>!uploading&&fileRef.current.click()}>
            <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleFile}/>
            {uploading ? (
              <>
                <div style={{fontSize:28,marginBottom:8}}>⬆</div>
                <div style={{fontWeight:700,color:T.primary,fontSize:13}}>Przesyłanie do Imgbb...</div>
              </>
            ) : (
              <>
                <div style={{fontSize:32}}>📤</div>
                <div style={{fontWeight:700,color:T.muted,fontSize:13,marginTop:6}}>Kliknij lub przeciągnij plik</div>
                <div style={{color:T.border,fontSize:11,marginTop:3}}>JPG, PNG, WebP · max 32 MB</div>
              </>
            )}
          </div>
        ) : (
          <div style={{marginBottom:12}}>
            <div style={{display:"flex",gap:7}}>
              <input style={{...S.inp,flex:1}} placeholder="https://..." value={urlVal}
                onChange={e=>{setUrlVal(e.target.value);setUrlErr("");}}
                onKeyDown={e=>e.key==="Enter"&&tryUrl()}/>
              <button style={S.darkBtn} onClick={tryUrl}>Załaduj</button>
            </div>
          </div>
        )}

        {urlErr && <div style={{color:"#e94560",fontSize:12,marginBottom:8,fontWeight:600}}>{urlErr}</div>}

        <div style={S.previewBox}>
          {preview
            ? <img src={preview} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}
                onError={()=>setPreview(null)}/>
            : <div style={{textAlign:"center",color:"#ccc"}}><div style={{fontSize:42}}>🖼</div><div style={{fontSize:12,marginTop:6}}>Podgląd</div></div>
          }
        </div>

        <div style={{display:"flex",gap:7,marginTop:11}}>
          {preview && <button style={{...S.darkBtn,background:"#ffebee",color:"#e94560"}} onClick={()=>setPreview(null)}>🗑 Usuń</button>}
          <button style={{...S.darkBtn,flex:1}} disabled={uploading} onClick={()=>{onSave(preview);onClose();}}>
            {uploading ? "⏳ Przesyłanie..." : preview ? "✓ Zapisz" : "Zapisz bez zdjęcia"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════ THUMB ══════════════════════════════════════════ */
function Thumb({ item, size=72, onClick }) {
  return (
    <div onClick={onClick} style={{width:size,height:size,borderRadius:12,overflow:"hidden",flexShrink:0,
      background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",
      cursor:onClick?"pointer":"default",border:"1.5px solid #e8e8e8",position:"relative"}}>
      {item.image
        ? <img src={item.image} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
        : <span style={{fontSize:size*0.44}}>{item.emoji}</span>}
      {item.promoPrice&&(
        <div style={{position:"absolute",top:3,left:3,background:"#e94560",color:"#fff",
          borderRadius:5,padding:"1px 5px",fontSize:9,fontWeight:800}}>PROMO</div>
      )}
    </div>
  );
}



/* ════════════════════ VOUCHER HELPERS ══════════════════════════════════════ */
function calcDiscount(voucher, total) {
  if (!voucher || !voucher.active) return 0;
  if (voucher.minOrder && total < voucher.minOrder) return 0;
  if (voucher.type === "percent") return Math.round(total * voucher.value / 100 * 100) / 100;
  if (voucher.type === "amount")  return Math.min(voucher.value, total);
  return 0;
}

/* ════════════════════ CAMPAIGN MODAL (customer) ════════════════════════════ */
function CampaignModal({ campaign, voucher, onClose, ac }) {
  const [copied, setCopied] = useState(false);
  const tr = useTr();
  const code = campaign.voucherCode;
  const copyCode = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(code);
    setCopied(true); setTimeout(()=>setCopied(false), 2000);
  };
  const col = campaign.accentColor || ac;
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.78)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:T.surface,borderRadius:22,width:"100%",maxWidth:370,overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,0.4)"}}>
        {/* Hero */}
        <div style={{height:160,background:campaign.image?`url(${campaign.image}) center/cover`:`linear-gradient(135deg,${col},#1a1a2e)`,position:"relative",display:"flex",alignItems:"flex-end"}}>
          {campaign.image&&<div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.4)"}}/>}
          <div style={{position:"relative",padding:"0 18px 16px",width:"100%"}}>
            <div style={{color:"#fff",fontWeight:900,fontSize:19,textShadow:"0 1px 6px rgba(0,0,0,0.5)",lineHeight:1.2}}>{campaign.headline}</div>
          </div>
          <button onClick={onClose} style={{position:"absolute",top:12,right:12,background:"rgba(0,0,0,0.4)",border:"none",borderRadius:"50%",width:30,height:30,color:"#fff",fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
        </div>
        {/* Body */}
        <div style={{padding:"18px 18px 22px"}}>
          <p style={{fontSize:13,color:T.muted,lineHeight:1.5,margin:"0 0 16px"}}>{campaign.desc}</p>
          {code && (
            <div>
              <div style={{fontSize:11,color:T.textSoft,fontWeight:700,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>{tr.campaignCodeLabel}</div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <div style={{flex:1,background:T.bg,border:`2px dashed ${col}`,borderRadius:10,padding:"10px 14px",
                  fontFamily:"monospace",fontSize:18,fontWeight:900,color:col,letterSpacing:2,textAlign:"center"}}>
                  {code}
                </div>
                <button onClick={copyCode} style={{padding:"10px 14px",background:copied?"#27ae60":col,color:"#fff",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer",fontSize:12,transition:"background .2s",whiteSpace:"nowrap"}}>
                  {copied ? tr.campaignCopied : tr.campaignCopy}
                </button>
              </div>
              {voucher && (
                <div style={{marginTop:8,fontSize:11,color:T.textSoft,textAlign:"center"}}>
                  {voucher.type==="percent" ? `${voucher.value}% zniżki` : `${voucher.value} zł zniżki`}
                  {voucher.minOrder ? ` · min. zamówienie ${fmt(voucher.minOrder)} zł` : ""}
                </div>
              )}
            </div>
          )}
          <button onClick={onClose} style={{width:"100%",marginTop:16,padding:12,background:col,color:"#fff",border:"none",borderRadius:12,fontWeight:800,cursor:"pointer",fontSize:14}}>
            {campaign.ctaLabel || tr.campaignSee}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ VOUCHER INPUT BAR (in cart) ══════════════════════════ */
function VoucherBar({ vouchers, usedCodes, cartTotal, onApply, applied, ac }) {
  const [code, setCode] = useState("");
  const [err,  setErr]  = useState("");
  const tr = useTr();
  const submit = () => {
    const v = vouchers.find(v=>v.code.toUpperCase()===code.trim().toUpperCase()&&v.active);
    if (!v) { setErr(tr.voucherNotFound); return; }
    const timesUsed = (usedCodes||[]).filter(c=>c===v.code).length;
    if (v.maxUses && timesUsed >= v.maxUses) { setErr(tr.voucherMaxUses); return; }
    if (v.minOrder && cartTotal < v.minOrder) { setErr(tr.voucherMinOrder(v.minOrder)); return; }
    setErr(""); onApply(v);
  };
  if (applied) return (
    <div style={{background:"#e8f5e9",borderRadius:10,padding:"8px 12px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
      <div>
        <div style={{fontWeight:800,fontSize:13,color:"#27ae60"}}>🎟 {applied.code}</div>
        <div style={{fontSize:11,color:T.muted}}>{applied.type==="percent"?`-${applied.value}%`:`-${fmt(applied.value)} zł`}</div>
      </div>
      <button onClick={()=>onApply(null)} style={{background:"none",border:"none",color:"#e94560",fontWeight:700,cursor:"pointer",fontSize:18}}>✕</button>
    </div>
  );
  return (
    <div style={{marginBottom:10}}>
      <div style={{display:"flex",gap:7}}>
        <input style={{flex:1,padding:"9px 11px",borderRadius:9,border:"1.5px solid "+(err?"#e94560":"#ddd"),fontSize:13,outline:"none",fontFamily:"monospace",letterSpacing:1,textTransform:"uppercase"}}
          placeholder={tr.voucherPlaceholder} value={code} onChange={e=>{setCode(e.target.value);setErr("");}}
          onKeyDown={e=>e.key==="Enter"&&submit()}/>
        <button onClick={submit} style={{padding:"9px 15px",background:ac,color:"#fff",border:"none",borderRadius:9,fontWeight:700,cursor:"pointer",fontSize:12}}>{tr.voucherUse}</button>
      </div>
      {err&&<div style={{fontSize:11,color:"#e94560",marginTop:5,fontWeight:600}}>{err}</div>}
    </div>
  );
}


/* ════════════════════ VOUCHER ADMIN PANEL ══════════════════════════════════ */
function VoucherAdminPanel({ vouchers, setVouchers, usedCodes, ac }) {
  const BLANK = { code:"", type:"percent", value:"", minOrder:"", maxUses:"", desc:"", active:true };
  const [form, setForm] = useState(BLANK);
  const [showAdd, setShowAdd] = useState(false);

  const genCode = () => {
    const chars="ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const code=Array.from({length:8},()=>chars[Math.floor(Math.random()*chars.length)]).join("");
    setForm(p=>({...p,code}));
  };
  const add = () => {
    if (!form.code||!form.value) return;
    setVouchers(p=>[...p,{...form,id:genId(),usedCount:0,
      value:parseFloat(form.value)||0,
      minOrder:form.minOrder?parseFloat(form.minOrder):0,
      maxUses:form.maxUses?parseInt(form.maxUses):null}]);
    setForm(BLANK); setShowAdd(false);
  };

  return (
    <div>
      <button style={S.accentBtn(ac)} onClick={()=>setShowAdd(p=>!p)}>+ Dodaj bon zniżkowy</button>
      {showAdd&&(
        <div style={S.formCard}>
          <div style={S.formTitle}>Nowy bon zniżkowy</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:7,marginBottom:7}}>
            <input style={{...S.inp,fontFamily:"monospace",letterSpacing:1,textTransform:"uppercase"}} placeholder="KOD KUPONU" value={form.code}
              onChange={e=>setForm(p=>({...p,code:e.target.value.toUpperCase()}))}/>
            <button style={{...S.darkBtn,whiteSpace:"nowrap"}} onClick={genCode}>🎲 Losuj</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7}}>
            <select style={S.inp} value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}>
              <option value="percent">% Procentowy</option>
              <option value="amount">zł Kwotowy</option>
            </select>
            <input style={S.inp} type="number" placeholder={form.type==="percent"?"Wartość (%)":"Wartość (zł)"} value={form.value}
              onChange={e=>setForm(p=>({...p,value:e.target.value}))}/>
            <input style={S.inp} type="number" placeholder="Min. zamówienie (zł)" value={form.minOrder}
              onChange={e=>setForm(p=>({...p,minOrder:e.target.value}))}/>
            <input style={S.inp} type="number" placeholder="Maks. użyć (puste=∞)" value={form.maxUses}
              onChange={e=>setForm(p=>({...p,maxUses:e.target.value}))}/>
            <input style={{...S.inp,gridColumn:"span 2"}} placeholder="Opis (wewnętrzny)" value={form.desc}
              onChange={e=>setForm(p=>({...p,desc:e.target.value}))}/>
          </div>
          <div style={{display:"flex",gap:7,marginTop:9}}>
            <button style={S.darkBtn} onClick={add}>Zapisz bon</button>
            <button style={S.cancelBtn} onClick={()=>setShowAdd(false)}>Anuluj</button>
          </div>
        </div>
      )}

      {vouchers.length===0&&<div style={S.empty}><div style={{fontSize:40}}>🎟</div><p>Brak bonów zniżkowych</p></div>}
      {vouchers.map(v=>{
        const timesUsed = (usedCodes||[]).filter(code=>code===v.code).length;
        const pct = v.maxUses ? Math.min(100,Math.round(timesUsed/v.maxUses*100)) : null;
        return (
          <div key={v.id} style={{...S.card,borderLeft:"4px solid "+(v.active?"#27ae60":"#bbb")}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
              <div>
                <div style={{fontFamily:"monospace",fontWeight:900,fontSize:17,color:T.text,letterSpacing:1}}>{v.code}</div>
                <div style={{fontSize:11,color:T.textSoft,marginTop:2}}>{v.desc||"Bez opisu"}</div>
              </div>
              <div style={{display:"flex",gap:5,alignItems:"center"}}>
                <div style={{background:v.type==="percent"?"#e8f0ff":"#fff8e1",color:v.type==="percent"?"#3a86ff":"#f39c12",
                  borderRadius:8,padding:"3px 9px",fontWeight:800,fontSize:12}}>
                  {v.type==="percent"?`${v.value}%`:`${fmt(v.value)} zł`}
                </div>
                <button style={{...S.actBtn,background:v.active?"#e8f5e9":"#fff3e0"}}
                  onClick={()=>setVouchers(p=>p.map(x=>x.id===v.id?{...x,active:!x.active}:x))}>
                  {v.active?"✅":"⏸"}
                </button>
                <button style={{...S.actBtn,background:"#ffebee"}} onClick={()=>setVouchers(p=>p.filter(x=>x.id!==v.id))}>🗑</button>
              </div>
            </div>
            <div style={{display:"flex",gap:12,fontSize:11,color:T.textSoft,flexWrap:"wrap"}}>
              {v.minOrder>0&&<span>Min. zamówienie: {fmt(v.minOrder)} zł</span>}
              <span>Użyto: <strong style={{color:T.text}}>{timesUsed}</strong>{v.maxUses?` / ${v.maxUses}`:""}</span>
              <span style={{marginLeft:"auto",color:v.active?"#27ae60":"#bbb",fontWeight:700}}>{v.active?"Aktywny":"Nieaktywny"}</span>
            </div>
            {pct!==null&&(
              <div style={{marginTop:8}}>
                <div style={{height:5,borderRadius:3,background:"#eee",overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${pct}%`,background:pct>90?"#e94560":pct>60?"#f39c12":"#27ae60",transition:"width .3s"}}/>
                </div>
                <div style={{fontSize:9,color:T.muted,marginTop:3,textAlign:"right"}}>{pct}% wykorzystany</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════ BANNER SLIDER ════════════════════════════════════ */
function BannerSlider({ banners, ac, onCatClick, position="top" }) {
  const [cur, setCur] = useState(0);
  const [paused, setPaused] = useState(false);
  const tr = useTr();
  const total = banners.length;

  useEffect(()=>{
    if (total <= 1 || paused) return;
    const t = setInterval(()=>setCur(p=>(p+1)%total), 3800);
    return ()=>clearInterval(t);
  },[total, paused]);

  if (!total) return null;
  const b = banners[cur];
  const isTop = position==="top";

  return (
    <div
      style={{position:"relative", overflow:"hidden", cursor:"pointer",
        height: isTop ? 130 : 80,
        background: b.image ? "transparent" : (isTop
          ? "linear-gradient(135deg,"+ac+"dd,#1a1a2e)"
          : "linear-gradient(90deg,#1a1a2edd,"+ac+"99)"),
      }}
      onClick={()=>{ if(b.link && onCatClick) onCatClick(b.link); }}
      onMouseEnter={()=>setPaused(true)}
      onMouseLeave={()=>setPaused(false)}
      onTouchStart={()=>setPaused(true)}
      onTouchEnd={()=>setPaused(false)}
    >
      {/* Background image */}
      {b.image && (
        <img src={b.image} alt="" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>
      )}
      {/* Overlay */}
      <div style={{position:"absolute",inset:0,
        background: isTop
          ? "linear-gradient(90deg,rgba(0,0,0,0.58) 0%,rgba(0,0,0,0.18) 100%)"
          : "linear-gradient(90deg,rgba(0,0,0,0.65) 0%,rgba(0,0,0,0.1) 100%)"
      }}/>

      {/* Text content */}
      <div style={{position:"relative",zIndex:2,padding: isTop ? "18px 16px" : "10px 16px",height:"100%",display:"flex",flexDirection:"column",justifyContent:"center"}}>
        <div style={{color:"#fff",fontWeight:900,fontSize: isTop ? 17 : 13,textShadow:"0 1px 4px rgba(0,0,0,0.4)",lineHeight:1.2}}>{b.label}</div>
        {b.sub && <div style={{color:"rgba(255,255,255,0.85)",fontSize: isTop ? 12 : 11,marginTop:4,fontWeight:500}}>{b.sub}</div>}
        {isTop && b.link && (
          <div style={{marginTop:8,display:"inline-flex",alignItems:"center",gap:4,background:"rgba(255,255,255,0.2)",
            borderRadius:20,padding:"4px 12px",fontSize:11,color:"#fff",fontWeight:700,width:"fit-content",backdropFilter:"blur(4px)"}}>
            {tr.bannerSeeOffer}
          </div>
        )}
      </div>

      {/* Dots */}
      {total > 1 && (
        <div style={{position:"absolute",bottom: isTop ? 10 : 6,right:14,display:"flex",gap:5,zIndex:3}}>
          {banners.map((_,i)=>(
            <div key={i} onClick={e=>{e.stopPropagation();setCur(i);}}
              style={{width: i===cur ? 18 : 6,height:6,borderRadius:3,
                background: i===cur ? "#fff" : "rgba(255,255,255,0.45)",
                transition:"all .25s",cursor:"pointer"}}/>
          ))}
        </div>
      )}

      {/* Prev/Next arrows */}
      {total > 1 && (
        <>
          <button onClick={e=>{e.stopPropagation();setCur(p=>(p-1+total)%total);}}
            style={{position:"absolute",left:6,top:"50%",transform:"translateY(-50%)",
              background:"rgba(0,0,0,0.3)",color:"#fff",border:"none",borderRadius:"50%",
              width:26,height:26,fontSize:14,cursor:"pointer",zIndex:3,display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
          <button onClick={e=>{e.stopPropagation();setCur(p=>(p+1)%total);}}
            style={{position:"absolute",right:26,top:"50%",transform:"translateY(-50%)",
              background:"rgba(0,0,0,0.3)",color:"#fff",border:"none",borderRadius:"50%",
              width:26,height:26,fontSize:14,cursor:"pointer",zIndex:3,display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════ LOGIN ══════════════════════════════════════════ */
/* ── Licznik czasu blokady konta ── */
function LockoutTimer({ until, onExpire }) {
  const [remaining, setRemaining] = React.useState(Math.max(0, until - Date.now()));
  React.useEffect(() => {
    const iv = setInterval(() => {
      const r = Math.max(0, until - Date.now());
      setRemaining(r);
      if (r === 0) { clearInterval(iv); onExpire && onExpire(); }
    }, 1000);
    return () => clearInterval(iv);
  }, [until]); // eslint-disable-line
  const mins = String(Math.floor(remaining / 60000)).padStart(2,"0");
  const secs = String(Math.floor((remaining % 60000) / 1000)).padStart(2,"0");
  return (
    <div style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:8,padding:"9px 13px",fontSize:12,marginBottom:12,textAlign:"center",color:"#c2410c",fontWeight:600}}>
      🔒 Odblokowanie za: {mins}:{secs}
    </div>
  );
}

/* ── Pomocniki anty brute-force (localStorage, czyszczone po sukcesie) ── */
const BF_KEY = "smakidomu_bf_";
const BF_MAX = 5;          // maks. nieudanych prób przed blokadą
const BF_WINDOW = 15 * 60 * 1000; // 15 minut blokady
function bfGet(login) {
  try { return JSON.parse(localStorage.getItem(BF_KEY + login) || "null") || { count: 0, until: 0 }; }
  catch(_) { return { count: 0, until: 0 }; }
}
function bfSet(login, data) {
  try { localStorage.setItem(BF_KEY + login, JSON.stringify(data)); } catch(_) {}
}
function bfClear(login) {
  try { localStorage.removeItem(BF_KEY + login); } catch(_) {}
}

function LoginScreen({ users, setUsers, settings, onLogin, setLogs }) {
  const [login, setLogin] = useState("");
  const [pass,  setPass]  = useState("");
  const [error, setError] = useState("");
  const [showP, setShowP] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [nfcStatus, setNfcStatus] = useState("");  // "" | "scanning" | "error"
  const [lockUntil, setLockUntil] = useState(0);   // timestamp końca blokady
  const [, setAttemptsLeft] = useState(BF_MAX); // pozostałe próby
  // eslint-disable-next-line no-unused-vars
  const ac = settings.accentColor;
  const nfcAbortRef = React.useRef(null);

  // ── NFC auto-login — nasłuchuj kart pracowniczych ──
  React.useEffect(() => {
    if (!("NDEFReader" in window)) return;
    let controller;
    const startNfc = async () => {
      try {
        controller = new AbortController();
        nfcAbortRef.current = controller;
        const ndef = new window.NDEFReader();
        await ndef.scan({ signal: controller.signal });
        setNfcStatus("scanning");
        ndef.addEventListener("reading", async ({ serialNumber }) => {
          if (!serialNumber) return;
          const cardId = serialNumber.replace(/:/g,"").toUpperCase();
          const u = users.find(u => u.nfcCard === cardId);
          if (u) {
            setNfcStatus("");
            onLogin(u);
          } else {
            setNfcStatus("error");
            setTimeout(() => setNfcStatus("scanning"), 2000);
          }
        });
      } catch(e) {
        setNfcStatus("");
      }
    };
    startNfc();
    return () => { try { if (nfcAbortRef.current) nfcAbortRef.current.abort(); } catch(_){} };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const captureGeoAndLog = async (user, success) => {
    const ts = new Date().toISOString();
    const ua = navigator.userAgent || "";
    const entry = {
      id: genId(), ts, type: success ? "login" : "login_fail",
      login: login.trim(), name: user?.name || "", role: user?.role || "",
      ip: "—", city: "—", country: "—", region: "—", isp: "—", ua,
    };
    try {
      const res  = await fetch("https://ipapi.co/json/");
      if (res.ok) {
        const geo = await res.json();
        entry.ip      = geo.ip      || "—";
        entry.city    = geo.city    || "—";
        entry.country = geo.country_name || "—";
        entry.region  = geo.region  || "—";
        entry.isp     = geo.org     || "—";
      }
    } catch(_) {}
    if (setLogs) setLogs(p => [...(p||[]), entry].slice(-2000));
  };

  const submit = async () => {
    if (!login.trim() || !pass.trim()) { setError("Wpisz login i hasło"); return; }

    // Sprawdź blokadę brute-force
    const bf = bfGet(login.trim());
    if (bf.until > Date.now()) {
      const mins = Math.ceil((bf.until - Date.now()) / 60000);
      setError(`Zbyt wiele nieudanych prób. Konto zablokowane na ${mins} min.`);
      setLockUntil(bf.until);
      return;
    }

    setLoading(true);
    try {
      const loginTrimmed = login.trim();
      // Spróbuj nowym PBKDF2 (v3)
      const hash = await hashPassword(pass, loginTrimmed);
      let u = users.find(u => u.login === loginTrimmed && u.passwordHash === hash);

      // Fallback: stary SHA-256 bez soli (v2) — migrujemy przy sukcesie
      if (!u) {
        const legacyHash = await hashPasswordLegacy(pass);
        const legacyUser = users.find(u => u.login === loginTrimmed && u.passwordHash === legacyHash);
        if (legacyUser) {
          // Migruj hash do PBKDF2 w tle
          const migrated = { ...legacyUser, passwordHash: hash };
          delete migrated.password;
          setUsers(prev => prev.map(x => x.id === migrated.id ? migrated : x));
          u = migrated;
        }
      }
      if (u) {
        // Sukces — czyścimy licznik prób
        bfClear(login.trim());
        setLockUntil(0);
        setAttemptsLeft(BF_MAX);
        captureGeoAndLog(u, true);
        setError(""); onLogin(u);
      } else {
        captureGeoAndLog(null, false);
        // Aktualizuj licznik nieudanych prób
        const bf2 = bfGet(login.trim());
        const newCount = (bf2.count || 0) + 1;
        const blocked  = newCount >= BF_MAX;
        bfSet(login.trim(), {
          count: blocked ? newCount : newCount,
          until: blocked ? Date.now() + BF_WINDOW : 0,
        });
        if (blocked) {
          setLockUntil(Date.now() + BF_WINDOW);
          setError(`Zbyt wiele nieudanych prób. Konto zablokowane na 15 min.`);
        } else {
          setAttemptsLeft(BF_MAX - newCount);
          setError(`Nieprawidłowy login lub hasło. Pozostało prób: ${BF_MAX - newCount}`);
        }
      }
    } catch(e) {
      setError("Błąd logowania. Spróbuj ponownie.");
    }
    setLoading(false);
  };
  return (
    <div style={{minHeight:"100vh",display:"flex",background:"#16213e",padding:20}}>
      {/* Left panel — branding */}
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 20px"}}/>

      {/* Login card */}
      <div style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{background:"#fff",borderRadius:14,padding:"36px 30px",width:"100%",maxWidth:380,boxShadow:"0 24px 64px rgba(0,0,0,0.35)"}}>
          <div style={{textAlign:"center",marginBottom:28}}>
            {settings.logo
              ? <img src={settings.logo} alt="logo" style={{height:56,width:56,borderRadius:12,objectFit:"cover",marginBottom:12,border:"2px solid "+TA.border}}/>
              : <div style={{width:56,height:56,borderRadius:14,background:"#16213e",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,margin:"0 auto 12px",color:"#fff",fontWeight:700}}>G</div>
            }
            <h2 style={{margin:0,fontSize:20,fontWeight:700,color:TA.text}}>{settings.restaurantName||"System POS"}</h2>
            <p style={{margin:"6px 0 0",color:TA.muted,fontSize:13}}>Panel zarządzania</p>
          </div>
          <div style={{marginBottom:14}}>
            <label style={S.label}>Login</label>
            <input style={S.inp} value={login} onChange={e=>setLogin(e.target.value)} placeholder="Wpisz login" autoComplete="username" onKeyDown={e=>e.key==="Enter"&&submit()}/>
          </div>
          <div style={{marginBottom:16}}>
            <label style={S.label}>Hasło</label>
            <div style={{position:"relative"}}>
              <input style={{...S.inp,paddingRight:40}} type={showP?"text":"password"} value={pass} autoComplete="current-password"
                onChange={e=>setPass(e.target.value)} placeholder="Wpisz hasło" onKeyDown={e=>e.key==="Enter"&&submit()}/>
              <button style={{position:"absolute",right:11,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:15,color:TA.muted}}
                onClick={()=>setShowP(p=>!p)} type="button">{showP?"🙈":"👁"}</button>
            </div>
          </div>
          {error&&<div style={{background:"#fef2f2",color:"#dc2626",borderRadius:8,padding:"9px 13px",fontSize:13,marginBottom:12,textAlign:"center",border:"1px solid #fecaca"}}>{error}</div>}

          {/* Licznik pozostałego czasu blokady */}
          {lockUntil > 0 && lockUntil > Date.now() && (
            <LockoutTimer until={lockUntil} onExpire={() => { setLockUntil(0); setError(""); setAttemptsLeft(BF_MAX); }} />
          )}

          {/* NFC status */}
          {/* NFC — pokazuj zawsze gdy jest choć jeden użytkownik z kartą */}
          {users.some(u=>u.nfcCard) && (
            <div style={{
              display:"flex",alignItems:"center",gap:8,
              borderRadius:8,padding:"9px 13px",fontSize:12,marginBottom:12,
              background: nfcStatus==="error"?"#fef2f2":nfcStatus==="scanning"?"#f0fdf4":"#f8fafc",
              border:"1px solid "+(nfcStatus==="error"?"#fecaca":nfcStatus==="scanning"?"#bbf7d0":"#e2e8f0"),
              color: nfcStatus==="error"?"#dc2626":nfcStatus==="scanning"?"#15803d":"#64748b",
            }}>
              <span style={{fontSize:18,animation:nfcStatus==="scanning"?"blikSpin 2s linear infinite":"none",display:"inline-block"}}>
                {nfcStatus==="error"?"❌":nfcStatus==="scanning"?"📡":"💳"}
              </span>
              <div>
                <div style={{fontWeight:700,fontSize:12}}>
                  {nfcStatus==="error"?"Nieznana karta NFC":nfcStatus==="scanning"?"Czytnik aktywny — przyłóż kartę":"Logowanie kartą NFC"}
                </div>
                <div style={{fontSize:10,marginTop:1,opacity:0.8}}>
                  {nfcStatus==="error"
                    ?"Ta karta nie jest przypisana do żadnego konta"
                    :nfcStatus==="scanning"
                      ?"Przyłóż zaprogramowaną kartę pracowniczą lub zbliż telefon do tagu NFC"
                      :"Przyłóż kartę pracowniczą lub zbliż telefon do tagu NFC przy wejściu"}
                </div>
              </div>
            </div>
          )}

          <button
            disabled={loading || lockUntil > Date.now()}
            style={{width:"100%",padding:"11px",background:(loading||lockUntil>Date.now())?"#9ca3af":TA.accent,color:"#fff",border:"none",borderRadius:8,fontWeight:600,fontSize:15,cursor:(loading||lockUntil>Date.now())?"not-allowed":"pointer",boxShadow:(loading||lockUntil>Date.now())?"none":"0 4px 14px rgba(37,99,235,0.35)",transition:"all .2s"}}
            onClick={submit}>
            {loading ? "Logowanie..." : lockUntil > Date.now() ? "🔒 Konto zablokowane" : "Zaloguj się →"}
          </button>
          <div style={{marginTop:16,borderTop:"1px solid "+TA.border,paddingTop:14}}>
            <button
              onClick={()=>{
                if(!window.confirm("Zresetować wszystkie dane lokalne?\nSpowoduje to przywrócenie ustawień fabrycznych i domyślnych haseł.")) return;
                Object.keys(localStorage).filter(k=>k.startsWith("smakidomu_")).forEach(k=>localStorage.removeItem(k));
                window.location.reload();
              }}
              style={{width:"100%",padding:"7px",background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:7,fontWeight:500,cursor:"pointer",fontSize:11}}>
              🔄 Reset danych lokalnych
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ SOUND NOTIFICATIONS ══════════════════════════════════ */
// Shared AudioContext — musi być tworzony po interakcji użytkownika (wymóg Chrome/Safari)
let _sharedAudioCtx = null;
function getAudioCtx() {
  if (!_sharedAudioCtx) {
    _sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Przeglądarka mogła zawiesić kontekst — wznawiamy
  if (_sharedAudioCtx.state === "suspended") {
    _sharedAudioCtx.resume();
  }
  return _sharedAudioCtx;
}

// Odblokowanie AudioContext przy pierwszej interakcji użytkownika (wymagane przez Chrome/Safari)
function unlockAudioContext() {
  try { getAudioCtx(); } catch(e) {}
}
if (typeof document !== "undefined") {
  ["click","touchstart","keydown"].forEach(evt =>
    document.addEventListener(evt, unlockAudioContext, { once: true, passive: true })
  );
}

// Generate a pleasant notification chime using Web Audio API
function playOrderSound() {
  try {
    const ctx = getAudioCtx();
    // Play a warm "ding-dong" sequence (bakery bell style)
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5 E5 G5 C6
    notes.forEach((freq, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.18;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.35, start + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.5);
      osc.start(start);
      osc.stop(start + 0.55);
    });
    // Low resonant "thud" at the end
    const sub  = ctx.createOscillator();
    const subG = ctx.createGain();
    sub.connect(subG);
    subG.connect(ctx.destination);
    sub.type = "sine";
    sub.frequency.value = 130;
    const t = ctx.currentTime + 0.55;
    subG.gain.setValueAtTime(0.25, t);
    subG.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    sub.start(t);
    sub.stop(t + 0.45);
  } catch(e) {
    // Audio not available — silent fail
  }
}

// Dźwięk wezwania obsługi — wyższy, bardziej natarczywy
function playStaffCallSound() {
  try {
    const ctx = getAudioCtx();
    const notes = [880, 1046.50, 880, 1046.50]; // A5 C6 x2
    notes.forEach((freq, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "triangle";
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.2, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.2);
      osc.start(start);
      osc.stop(start + 0.25);
    });
  } catch(e) {}
}

// Dźwięk gotowego zamówienia z kuchni — krótki, jasny "ping"
function playReadySound() {
  try {
    const ctx = getAudioCtx();
    const notes = [987.77, 1318.51]; // B5 E6
    notes.forEach((freq, i) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = ctx.currentTime + i * 0.1;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);
      osc.start(start);
      osc.stop(start + 0.35);
    });
  } catch(e) {}
}
// Głośna syrena alarmowa — powiadomienie kucharza/sprzedawcy
// Miły melodyjny dzwonek przypominający — jak dzwonek hotelowy / restauracyjny
// Gra przez 5 sekund, powtarzając motyw co ~1.2s
function playSirenSound() {
  try {
    const ctx = getAudioCtx();
    // Motyw: 4 nuty (G5 B5 D6 G6) — wesoły, restauracyjny dzwonek
    const MELODY = [
      { freq: 784.00, dur: 0.18, vol: 0.38 }, // G5
      { freq: 987.77, dur: 0.18, vol: 0.34 }, // B5
      { freq: 1174.66,dur: 0.18, vol: 0.30 }, // D6
      { freq: 1567.98,dur: 0.28, vol: 0.26 }, // G6 — dłuższa, wysoka
      { freq: 1174.66,dur: 0.18, vol: 0.22 }, // D6 — zejście
      { freq: 987.77, dur: 0.22, vol: 0.18 }, // B5 — wyciszenie
    ];
    const CYCLE   = 1.25; // przerwa między powtórzeniami (s)
    const REPEATS = 4;    // 4 powtórzenia ≈ 5 s

    for (let rep = 0; rep < REPEATS; rep++) {
      let offset = rep * CYCLE;
      MELODY.forEach(note => {
        const t    = ctx.currentTime + offset;
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        // Dodaj delikatny reverb (drugi oscylator lekko opóźniony)
        const osc2  = ctx.createOscillator();
        const gain2 = ctx.createGain();
        [osc, osc2].forEach((o, i) => {
          o.type = "sine";
          o.frequency.value = note.freq * (i === 1 ? 1.002 : 1); // lekkie detune
          const g = i === 0 ? gain : gain2;
          const v = note.vol * (i === 1 ? 0.35 : 1);
          o.connect(g);
          g.connect(ctx.destination);
          g.gain.setValueAtTime(0, t + (i === 1 ? 0.015 : 0));
          g.gain.linearRampToValueAtTime(v, t + 0.025 + (i === 1 ? 0.015 : 0));
          g.gain.exponentialRampToValueAtTime(0.001, t + note.dur + 0.18);
          o.start(t + (i === 1 ? 0.015 : 0));
          o.stop(t + note.dur + 0.22);
        });
        offset += note.dur;
      });
    }
  } catch(e) {}
}


/* ════════ STAFF ALERTS — Firebase cross-device notifications ════════
   /smakidomu/staff_alert → { ts, type, from, fromRole, msg, targetRole }
   targetRole: "all" | "seller" | "cook" | "admin"
   Polling co 3s — każdy zalogowany sprawdza czy przyszło coś nowego.
══════════════════════════════════════════════════════════════════════ */
async function sendStaffAlert(fbConfig, type, from, fromRole, msg, targetRole) {
  try {
    const cfg = fbConfig || getFirebaseConfig();
    if (!cfg || !cfg.databaseURL) return false;
    const url  = API_BASE + "/staff_alert";
    await apiFetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ts: Date.now(), type, from: from || "Obsługa",
        fromRole: fromRole || "", msg: msg || "",
        targetRole: targetRole || "all",
      }),
    });
    return true;
  } catch(e) { return false; }
}

function useStaffAlerts({ fbConfig, user, onAlert }) {
  const lastTs  = React.useRef(0);
  const ready   = React.useRef(false);

  React.useEffect(() => {
    const cfg = fbConfig || getFirebaseConfig();
    if (!cfg || !cfg.databaseURL) return;
    const url  = API_BASE + "/staff_alert";

    const poll = async () => {
      try {
        const res  = await apiFetch(url);
        if (!res.ok) return;
        const data = await res.json();
        if (!data || !data.ts) return;
        if (!ready.current) { lastTs.current = data.ts; ready.current = true; return; }
        if (data.ts <= lastTs.current) return;
        lastTs.current = data.ts;
        // Ignoruj własne powiadomienia
        if (data.from === (user.name || user.login)) return;
        // Sprawdź czy powiadomienie jest do mnie
        const tgt = data.targetRole || "all";
        if (tgt !== "all" && tgt !== user.role) return;
        onAlert(data);
      } catch(e) {}
    };

    ready.current = false;
    const iv = setInterval(poll, 3000);
    poll();
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbConfig?.databaseURL, user?.role]);
}

// Hook to use in AdminPanel — plays sound when new orders or staff calls arrive
function useOrderNotification(orders, userNotifs, staffCalls) {
  const prevOrderCount = React.useRef(orders.length);
  const prevStaffCount = React.useRef((staffCalls||[]).length);
  const mountedRef     = React.useRef(false);
  const [soundEnabled, setSoundEnabled] = React.useState(true);
  const [latestNewOrder, setLatestNewOrder] = React.useState(null);
  const [latestStaffCall, setLatestStaffCall] = React.useState(null);
  const prevReadyCount = React.useRef(orders.filter(o => o.cookStatus === "ready" && !o.issued).length);

  // Reakcja na nowe zamówienia
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      prevOrderCount.current = orders.length;
      return;
    }
    if (orders.length > prevOrderCount.current && soundEnabled) {
      const shouldPlay = !userNotifs || userNotifs.newOrder !== false;
      if (shouldPlay) playOrderSound();
      setLatestNewOrder(orders[orders.length - 1] || null);
    }
    prevOrderCount.current = orders.length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders.length, soundEnabled, userNotifs]);

  // Reakcja na zamówienia gotowe do wydania (dla sprzedawcy)
  useEffect(() => {
    const currentReady = orders.filter(o => o.cookStatus === "ready" && !o.issued).length;
    if (!mountedRef.current) {
      prevReadyCount.current = currentReady;
      return;
    }
    if (currentReady > prevReadyCount.current && soundEnabled) {
      const shouldPlay = !userNotifs || userNotifs.orderIssued !== false;
      if (shouldPlay) playReadySound();
    }
    prevReadyCount.current = currentReady;
  }, [orders, soundEnabled, userNotifs]);

  // Reakcja na nowe wezwania obsługi
  useEffect(() => {
    const currentCount = (staffCalls||[]).length;
    if (!mountedRef.current) { prevStaffCount.current = currentCount; return; }
    if (currentCount > prevStaffCount.current && soundEnabled) {
      playStaffCallSound();
      const latest = staffCalls[staffCalls.length - 1];
      if (latest) setLatestStaffCall(latest);
    }
    prevStaffCount.current = currentCount;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(staffCalls||[]).length, soundEnabled]);

  return { soundEnabled, setSoundEnabled, latestNewOrder, latestStaffCall, setLatestStaffCall };
}


/* ════════════════════ NEW ORDER ALERT MODAL ════════════════════════════════ */
function NewOrderAlert({ order, onClose, onAccept, isSeller }) {
  const [routing, setRouting] = useState(() =>
    (order.cart||[]).reduce((acc, c) => ({ ...acc, [c.id]: "kitchen" }), {})
  );

  useEffect(() => {
    if (!isSeller) {
      const t = setTimeout(onClose, 30000);
      return () => clearTimeout(t);
    }
  }, [onClose, isSeller]);

  if (!order) return null;

  const toggleRoute = (id) =>
    setRouting(p => ({ ...p, [id]: p[id] === "kitchen" ? "bar" : "kitchen" }));

  const handleAccept = () => {
    const cartWithRouting = (order.cart||[]).map(c => ({ ...c, serveAt: routing[c.id] || "kitchen" }));
    onAccept(cartWithRouting);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(20,10,0,0.92)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, animation: "fadeSlide .25s ease",
    }}>
      <div style={{
        background: "#FFFAF3", borderRadius: 24, width: "100%", maxWidth: 500,
        overflow: "hidden", boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
        border: "3px solid #E8A020", maxHeight: "90vh", display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg,#3B1F0A,#6B3F1E)", padding: "18px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ fontSize: 38, lineHeight: 1 }}>🔔</div>
            <div>
              <div style={{ color: "#E8A020", fontWeight: 900, fontSize: 12, letterSpacing: 2, textTransform: "uppercase", marginBottom: 3 }}>
                Nowe zamówienie!
              </div>
              <div style={{ color: "#fff", fontWeight: 900, fontSize: 24, fontFamily: "'Playfair Display',serif" }}>
                Stolik nr {order.tableNumber ?? order.table ?? "—"}
              </div>
              <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, marginTop: 2 }}>
                {order.time} · #{String(order.id).slice(-5)}
              </div>
            </div>
          </div>
          {isSeller && (
            <div style={{ marginTop: 12, background: "rgba(255,255,255,0.12)", borderRadius: 10, padding: "8px 12px" }}>
              <div style={{ color: "#E8A020", fontSize: 11, fontWeight: 700 }}>
                👆 Kliknij każdą pozycję aby wybrać gdzie trafi
              </div>
            </div>
          )}
        </div>

        {/* Items */}
        <div style={{ padding: "14px 20px", overflowY: "auto", flex: 1 }}>
          {(order.cart || []).map((item) => (
            <div key={item.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "10px 0", borderBottom: "1px solid #E8D5BA",
            }}>
              {/* Foto/emoji */}
              <div style={{
                width: 42, height: 42, borderRadius: 10, flexShrink: 0,
                background: "#FDF6EC", border: "1.5px solid #E8D5BA",
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden", fontSize: 20,
              }}>
                {item.image ? <img src={item.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : item.emoji}
              </div>
              {/* Nazwa */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#3B1F0A" }}>{item.name} ×{item.qty}</div>
                {item.allergens && <div style={{ fontSize: 10, color: "#c0392b", fontWeight: 600 }}>⚠ {item.allergens}</div>}
                {item.note && <div style={{ fontSize: 10, color: "#C8621A", fontWeight: 600 }}>📝 {item.note}</div>}
              </div>
              {/* Toggle kuchnia/bar — tylko dla sprzedawcy */}
              {isSeller ? (
                <button
                  onClick={() => toggleRoute(item.id)}
                  style={{
                    padding: "6px 12px", borderRadius: 10, border: "2px solid",
                    borderColor: routing[item.id] === "kitchen" ? "#f39c12" : "#0984e3",
                    background: routing[item.id] === "kitchen" ? "#fff8e1" : "#e8f4fd",
                    color: routing[item.id] === "kitchen" ? "#d97706" : "#0984e3",
                    fontWeight: 800, cursor: "pointer", fontSize: 12, flexShrink: 0,
                    transition: "all .15s",
                  }}>
                  {routing[item.id] === "kitchen" ? "👨‍🍳 Kuchnia" : "🥤 Bar"}
                </button>
              ) : (
                <span style={{ background: "#6B3F1E", color: "#fff", borderRadius: 8, padding: "2px 9px", fontSize: 13, fontWeight: 900 }}>
                  x{item.qty}
                </span>
              )}
            </div>
          ))}

          {order.orderNote && (
            <div style={{ marginTop: 10, background: "#FFF8E1", borderRadius: 10, padding: "10px 14px", border: "1px solid #F9A825" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#C8621A", marginBottom: 3 }}>Uwaga do zamówienia</div>
              <div style={{ fontSize: 13, color: "#3B1F0A" }}>{order.orderNote}</div>
            </div>
          )}

          {/* Podsumowanie routingu — tylko dla sprzedawcy */}
          {isSeller && (
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <div style={{ flex: 1, background: "#fff8e1", borderRadius: 10, padding: "8px 12px", border: "1px solid #fcd34d" }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: "#d97706" }}>👨‍🍳 DO KUCHNI</div>
                {Object.entries(routing).filter(([,v])=>v==="kitchen").map(([id]) => {
                  const c = (order.cart||[]).find(x=>x.id===id||String(x.id)===id);
                  return c ? <div key={id}>
                    <div style={{ fontSize: 11, color: "#92400e", fontWeight: 600 }}>{c.emoji} {c.name} ×{c.qty}</div>
                    {(c.selectedAddons||[]).map((a,ai)=><div key={ai} style={{fontSize:10,color:"#b45309",paddingLeft:14}}>+ {a.name} ×{a.qty}</div>)}
                  </div> : null;
                })}
                {Object.values(routing).every(v=>v==="bar") && <div style={{ fontSize: 11, color: "#d97706" }}>— brak —</div>}
              </div>
              <div style={{ flex: 1, background: "#e8f4fd", borderRadius: 10, padding: "8px 12px", border: "1px solid #7dd3fc" }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: "#0369a1" }}>🥤 WYDAJĘ Z BARU</div>
                {Object.entries(routing).filter(([,v])=>v==="bar").map(([id]) => {
                  const c = (order.cart||[]).find(x=>x.id===id||String(x.id)===id);
                  return c ? <div key={id}>
                    <div style={{ fontSize: 11, color: "#0369a1", fontWeight: 600 }}>{c.emoji} {c.name} ×{c.qty}</div>
                    {(c.selectedAddons||[]).map((a,ai)=><div key={ai} style={{fontSize:10,color:"#0369a1",paddingLeft:14}}>+ {a.name} ×{a.qty}</div>)}
                  </div> : null;
                })}
                {Object.values(routing).every(v=>v==="kitchen") && <div style={{ fontSize: 11, color: "#0369a1" }}>— brak —</div>}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px 18px", borderTop: "1px solid #E8D5BA" }}>
          {isSeller ? (
            <button onClick={handleAccept} style={{
              width: "100%", padding: "14px", borderRadius: 14,
              background: "linear-gradient(135deg,#27ae60,#2ecc71)",
              color: "#fff", border: "none", fontWeight: 900, fontSize: 16,
              cursor: "pointer", boxShadow: "0 4px 14px rgba(39,174,96,0.45)",
            }}>
              ✅ Zatwierdź i wyślij do kuchni
            </button>
          ) : (
            <button onClick={onClose} style={{
              width: "100%", padding: "14px", borderRadius: 14,
              background: "linear-gradient(135deg,#C8621A,#E8A020)",
              color: "#fff", border: "none", fontWeight: 900, fontSize: 16,
              cursor: "pointer", boxShadow: "0 4px 14px rgba(200,98,26,0.45)",
            }}>
              Rozumiem
            </button>
          )}
          <div style={{ textAlign: "center", fontSize: 11, color: "#8B6A4A", marginTop: 8 }}>
            {isSeller ? "Możesz zmienić routing klikając każdą pozycję" : "Okno zamknie się automatycznie po 30 sekundach"}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ STAFF CALL ALERT ══════════════════════════════════════ */
function StaffCallAlert({ call, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 20000);
    return () => clearTimeout(t);
  }, [onClose]);

  if (!call) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.85)",
      display: "flex", alignItems: "center", justifyContent: "center",
      animation: "fadeIn .2s ease"
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 28,
        width: "100%", maxWidth: 400, margin: 16,
        overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
        animation: "slideUp .3s ease"
      }}>
        <div style={{
          background: "linear-gradient(135deg,#f39c12,#e67e22)",
          padding: "24px",
          textAlign: "center"
        }}>
          <div style={{ fontSize: 60, marginBottom: 8, animation: "popIn .4s ease" }}>🔔</div>
          <div style={{ color: "#fff", fontWeight: 900, fontSize: 22 }}>Klient wzywa obsługę!</div>
          <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 15, marginTop: 6 }}>
            Stolik <strong style={{ fontSize: 20 }}>#{call.tableNumber}</strong> · {call.time}
          </div>
        </div>
        <div style={{ padding: "20px 24px" }}>
          <button
            onClick={onClose}
            style={{
              width: "100%", padding: "16px",
              background: "linear-gradient(135deg,#f39c12,#e67e22)",
              color: "#fff", border: "none", borderRadius: 14,
              fontWeight: 900, cursor: "pointer", fontSize: 18,
              boxShadow: "0 4px 14px rgba(243,156,18,0.45)"
            }}>
            ✓ Idę do stolika
          </button>
          <div style={{ textAlign: "center", fontSize: 11, color: "#aaa", marginTop: 8 }}>
            Okno zamknie się automatycznie po 20 sekundach
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ QR CODE GENERATOR ════════════════════════════════════ */
// Generuje prawdziwy QR kod używając zewnętrznej biblioteki qrcodejs
// Fallback do prostego SVG gdy biblioteka niedostępna
function QRCode({ value, size = 160, color = "#3B1F0A", bg = "#FFFAF3" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = "";
    // Używamy zewnętrznego API jako primary (działa offline po cache)
    const img = document.createElement("img");
    img.src = "https://api.qrserver.com/v1/create-qr-code/?size=" + size + "x" + size +
      "&data=" + encodeURIComponent(value) +
      "&color=" + color.replace("#","") +
      "&bgcolor=" + bg.replace("#","");
    img.width = size;
    img.height = size;
    img.style.borderRadius = "8px";
    img.style.display = "block";
    img.onerror = () => {
      // Fallback: prosty SVG z napisem
      ref.current.innerHTML = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="border-radius:8px">
        <rect width="${size}" height="${size}" fill="${bg}"/>
        <text x="${size/2}" y="${size/2}" text-anchor="middle" dominant-baseline="central" font-size="10" fill="${color}" font-family="monospace">QR offline</text>
      </svg>`;
    };
    ref.current.appendChild(img);
  }, [value, size, color, bg]);
  return <div ref={ref} style={{display:"inline-block"}}/>;
}

/* ════════════════════ PRINT QR HELPER ══════════════════════════════════════ */
function printTableQR(table, baseUrl) {
  const url = baseUrl + "?table=" + table.id;
  const win = window.open("", "_blank");
  if (!win) { alert("Zezwól na otwieranie okien popup w przeglądarce"); return; }
  win.document.write(`<!DOCTYPE html>
<html lang="pl"><head><meta charset="utf-8"><title>QR Stolik #${table.number}</title>
<style>
  body { font-family: Georgia, serif; text-align: center; padding: 40px; background: #FDF6EC; }
  h1 { color: #6B3F1E; font-size: 32px; margin-bottom: 4px; }
  p  { color: #8B6A4A; font-size: 16px; margin: 4px 0 20px; }
  .url { font-size: 11px; color: #8B6A4A; word-break: break-all; margin-top: 16px; }
  .qr  { border: 3px solid #E8D5BA; border-radius: 16px; padding: 16px; display: inline-block; background: #FFFAF3; }
  @media print { body { padding: 20px; } }
</style></head>
<body>
  <div class="qr">
    <h1>🥐 Stolik #${table.number}</h1>
    <p>Zeskanuj kod QR i złóż zamówienie</p>
    <img src="https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(url)}&color=6B3F1E&bgcolor=FFFAF3"
         width="220" height="220" style="border-radius:8px" alt="QR kod stolika ${table.number}"/>
    <p class="url">${url}</p>
  </div>
  <script>window.onload = () => window.print();</script>
</body></html>`);
  win.document.close();
}

/* ════════════════════ TABLE QR MODAL ════════════════════════════════════════ */
function TableQRModal({ table, baseUrl, onClose }) {
  const url = baseUrl + "?table=" + table.id;
  const [copied, setCopied] = useState(false);

  const copy = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const print = () => printTableQR(table, baseUrl);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(59,31,10,0.75)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:T.surface,borderRadius:22,padding:24,maxWidth:340,width:"100%",border:"1px solid "+T.border,textAlign:"center",boxShadow:"0 20px 60px rgba(59,31,10,0.35)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontWeight:800,fontSize:15,color:T.primary,fontFamily:"'Playfair Display',serif"}}>
            🥐 Kod QR — Stolik #{table.number}
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* QR code — uses external API for real QR */}
        <div style={{background:T.bg,borderRadius:16,padding:20,border:"2px solid "+T.border,marginBottom:16,display:"inline-block"}}>
          <img
            src={"https://api.qrserver.com/v1/create-qr-code/?size=200x200&data="+encodeURIComponent(url)+"&color=6B3F1E&bgcolor=FFFAF3"}
            width={200} height={200}
            alt={"QR stolik "+table.number}
            style={{borderRadius:8,display:"block"}}
            onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="block"; }}
          />
          {/* Fallback SVG QR if no internet */}
          <div style={{display:"none"}}>
            <QRCode value={url} size={200} color={T.primary} bg={T.bg}/>
          </div>
        </div>

        <div style={{fontSize:12,color:T.muted,marginBottom:4}}>Stolik #{table.number} · {table.seats} miejsc</div>
        <div style={{fontSize:11,color:T.textSoft,wordBreak:"break-all",background:T.bg,borderRadius:8,padding:"6px 10px",marginBottom:16,border:"1px solid "+T.border}}>
          {url}
        </div>

        <div style={{display:"flex",gap:8}}>
          <button style={{...S.darkBtn,flex:1,fontSize:12}} onClick={copy}>
            {copied ? "✓ Skopiowano!" : "📋 Kopiuj link"}
          </button>
          <button style={{...S.darkBtn,flex:1,fontSize:12,background:T.accent}} onClick={print}>
            🖨 Drukuj QR
          </button>
        </div>

        <div style={{marginTop:14,fontSize:11,color:T.muted,lineHeight:1.5,background:"#FFF8E1",borderRadius:8,padding:"8px 10px",border:"1px solid #E8D5BA",textAlign:"left"}}>
          💡 Umieść wydrukowany kod QR na stoliku. Klient skanuje smartfonem — zamówienie automatycznie przypisuje się do Stolika #{table.number}.
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ NFC WRITER PANEL ══════════════════════════════════════ */
function NFCWriterPanel({ table, baseUrl, onClose }) {
  const [status,  setStatus]  = useState("idle"); // idle | writing | success | error | unsupported
  const [message, setMessage] = useState("");
  const url = baseUrl + "?table=" + table.id;

  useEffect(() => {
    if (!("NDEFReader" in window)) {
      setStatus("unsupported");
      setMessage("Twoja przeglądarka nie obsługuje Web NFC. Użyj Chrome na Androidzie.");
    }
  }, []);

  const writeNFC = async () => {
    if (!("NDEFReader" in window)) return;
    try {
      setStatus("writing");
      setMessage("Przyłóż tag NFC do tylnej części telefonu...");
      const ndef = new window.NDEFReader();
      await ndef.write({ records: [{ recordType: "url", data: url }] });
      setStatus("success");
      setMessage("Tag NFC zapisany pomyślnie!");
    } catch (err) {
      setStatus("error");
      setMessage("Błąd: " + (err.message || "Nie można zapisać tagu NFC"));
    }
  };

  const statusIcon  = { idle:"📡", writing:"⏳", success:"✅", error:"❌", unsupported:"🚫" };
  const statusColor = { idle:T.primary, writing:T.gold, success:T.green, error:"#C0392B", unsupported:T.muted };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(59,31,10,0.75)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:T.surface,borderRadius:22,padding:24,maxWidth:340,width:"100%",border:"1px solid "+T.border,boxShadow:"0 20px 60px rgba(59,31,10,0.35)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontWeight:800,fontSize:15,color:T.primary,fontFamily:"'Playfair Display',serif"}}>
            📡 Zapis NFC — Stolik #{table.number}
          </div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={{textAlign:"center",padding:"20px 0"}}>
          <div style={{fontSize:60,marginBottom:12,
            animation:status==="writing"?"blikSpin 1.5s linear infinite":"none",
            display:"inline-block"}}>
            {statusIcon[status]}
          </div>
          <div style={{fontWeight:700,fontSize:15,color:statusColor[status],marginBottom:8}}>
            {status==="idle"     && "Gotowy do zapisu"}
            {status==="writing"  && "Zapisuję tag NFC..."}
            {status==="success"  && "Sukces!"}
            {status==="error"    && "Błąd zapisu"}
            {status==="unsupported" && "NFC nieobsługiwane"}
          </div>
          {message && <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>{message}</div>}
        </div>

        <div style={{background:T.bg,borderRadius:10,padding:"10px 12px",marginBottom:16,border:"1px solid "+T.border}}>
          <div style={{fontSize:11,color:T.muted,marginBottom:4,fontWeight:700}}>URL do zapisu:</div>
          <div style={{fontSize:11,color:T.text,wordBreak:"break-all",fontFamily:"monospace"}}>{url}</div>
        </div>

        {status !== "unsupported" && (
          <button
            onClick={writeNFC}
            disabled={status==="writing"}
            style={{width:"100%",padding:13,background:status==="success"?T.green:T.accent,color:"#fff",border:"none",borderRadius:12,fontWeight:800,fontSize:14,cursor:status==="writing"?"wait":"pointer",marginBottom:8}}>
            {status==="writing" ? "Zapisuję..." : status==="success" ? "✅ Zapisano! Zapisz kolejny" : "📡 Zacznij zapis NFC"}
          </button>
        )}
        <button style={{...S.cancelBtn,width:"100%",textAlign:"center"}} onClick={onClose}>Zamknij</button>

        <div style={{marginTop:12,fontSize:11,color:T.muted,lineHeight:1.5,background:"#FFF8E1",borderRadius:8,padding:"8px 10px",border:"1px solid #E8D5BA"}}>
          💡 Wymagany: tag NFC (NTAG213/215/216) + Chrome na Androidzie. Klient przykłada telefon do tagu — automatycznie otwiera stronę z przypisanym stolikiem.
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ LANGUAGE CONTEXT & TRANSLATIONS ══════════════════════ */
const LangContext = React.createContext({ lang: "pl", setLang: () => {}, customTranslations: {} });
function useLang() { return React.useContext(LangContext); }

const LANG = {
  pl: {
    /* ── Table selector ── */
    chooseTable:        "Wybierz stolik",
    scanHint:           "Dotknij numer lub zeskanuj kod QR / NFC",
    qrLabel:            "Kod QR",
    qrSub:              "Zeskanuj aparatem",
    nfcLabel:           "NFC",
    nfcScanning:        "Skanowanie...",
    nfcHint:            "Przyłóż telefon",
    orManual:           "lub wybierz ręcznie",
    legendFree:         "Wolny",
    legendReserved:     "Zarezerwowany",
    legendOccupied:     "Zajęty",
    occupiedTitle:      "🍽 Ten stolik jest zajęty",
    occupiedDesc:       "Chcesz coś domówić lub potrzebujesz pomocy? Wezwij obsługę — kelner podejdzie do stolika.",
    staffCalled:        "Obsługa została wezwana!",
    staffSoon:          (n) => `Kelner wkrótce podejdzie do stolika #${n}`,
    callStaff:          (n) => `🔔 Wezwij obsługę do stolika #${n}`,
    reservationWarn:    "⚠ Ten stolik ma rezerwację:",
    reservationAt:      (time, guests) => `Godz. ${time} · ${guests} os.`,
    seats:              (n) => `Miejsca: ${n}`,
    confirmTable:       (n) => `Potwierdź stolik #${n} →`,
    confirmEmpty:       "Potwierdź stolik #— →",
    guestsQuestion:     "Ile osób przy stoliku?",
    guestsTooMany:      (n) => `Ten stolik ma tylko ${n} miejsc`,
    guestsConfirm:      (n, t) => `Potwierdź stolik #${t} · ${n} os. →`,
    /* ── Nav ── */
    navOrders:          "🛍 Zamówienia",
    navAdmin:           "⚙ Admin",
    /* ── Menu / categories ── */
    catAll:             "Wszystkie",
    catPromo:           "Promocje",
    promoToday:         "Promocje dnia!",
    promoCount:         (n) => `${n} produkt${n===1?"":n<5?"y":"ów"} w niższej cenie`,
    noProducts:         "Brak produktów",
    ingredients:        "Skład",
    allergens:          "⚠ alergeny",
    addToCart:          "Dodaj",
    itemNotePlaceholder:"Uwaga (np. bez cebuli, alergie...)",
    tableLabel:         (n) => `Stolik #${n}`,
    /* ── Cart bar ── */
    cartLabel:          (qty) => `Koszyk · ${qty} szt.`,
    orderNotePlaceholder:"Uwaga do zamówienia (np. alergie, preferencje)...",
    placeOrder:         "Zamów →",
    voucherPlaceholder: "KOD KUPONU",
    voucherUse:         "Użyj",
    /* ── Voucher messages ── */
    voucherNotFound:    "Nie znaleziono kodu",
    voucherInactive:    "Kod jest nieaktywny",
    voucherUsed:        "Kod już wykorzystany",
    voucherMinOrder:    (n) => `Min. zamówienie: ${fmt(n)} zł`,
    voucherMaxUses:     "Kod wyczerpany",
    voucherOk:          (v) => v.type==="percent" ? `✓ ${v.value}% zniżki` : `✓ -${fmt(v.value)} zł`,
    /* ── Item zoom modal ── */
    zoomIngredients:    "Skład",
    zoomAllergens:      "Alergeny",
    zoomWait:           (n) => `⏱ ~${n} min`,
    zoomAddToCart:      "Dodaj do koszyka",
    /* ── Campaign strip ── */
    campaignSee:        "Zobacz",
    campaignCode:       "Kod",
    campaignCodeLabel:  "Twój kod zniżkowy",
    campaignCopy:       "📋 Kopiuj",
    campaignCopied:     "✓ Skopiowano!",
    /* ── Banner ── */
    bannerSeeOffer:     "Zobacz ofertę →",
    /* ── Payment picker ── */
    payTitle:           "Jak chcesz zapłacić?",
    payTable:           (n) => `Stolik #${n}`,
    payBack:            "← Wróć do koszyka",
    payMethodCard:      "Karta",
    payMethodCardSub:   "Terminal przy kasie",
    payMethodBlik:      "BLIK",
    payMethodBlikSub:   "Szybka płatność telefonem",
    payMethodCash:      "Gotówka",
    payMethodCashSub:   "Zapłać przy odbiorze",
    payMethodPaypal:    "PayPal",
    payMethodPaypalSub: "Płatność przez PayPal",
    payBtnCard:         "Zamów i zapłać kartą",
    payBtnBlik:         "Przejdź do płatności BLIK",
    payBtnCash:         "Zamów i zapłać gotówką",
    payBtnPaypal:       "Zamów przez PayPal",
    payCoupon:          (code, disc) => `🎟 Kupon ${code} · -${fmt(disc)} zł`,
    /* ── BLIK screen ── */
    blikTitle:          "Płatność BLIK",
    blikSubtitle:       "Otwórz aplikację bankową i wygeneruj kod BLIK",
    blikAmount:         "Kwota do zapłaty",
    blikCodeLabel:      "Kod BLIK (6 cyfr)",
    blikSteps: [
      "Otwórz aplikację bankową na telefonie",
      "Znajdź opcję BLIK lub Kod BLIK",
      "Wpisz wyświetlony 6-cyfrowy kod powyżej",
      "Potwierdź płatność w aplikacji bankowej",
    ],
    blikConfirm:        "Potwierdź płatność BLIK",
    blikEnter:          "Wprowadź kod BLIK",
    blikBack:           "Wróć do koszyka",
    blikWaiting:        "Oczekiwanie na potwierdzenie",
    blikWaitSub:        "Potwierdź płatność w aplikacji bankowej",
    blikAmount2:        "Kwota",
    blikCancel:         "Anuluj",
    blikSuccess:        "Płatność przyjęta!",
    blikSuccessSub:     "Przekazuję zamówienie do kuchni...",
    /* ── Success screen ── */
    successTitle:       "Zamówienie przyjęte!",
    successBlik:        "Płatność BLIK potwierdzona ✓",
    successCash:        "Zapłać gotówką przy odbiorze",
    successCard:        "Zapłać kartą przy odbiorze",
    successTable:       "Stolik",
    successTime:        "Czas",
    successPayment:     "Płatność",
    successCoupon:      "Kupon",
    successDiscount:    "Zniżka",
    successTotal:       "Zapłacono",
    successPayBlik:     "📱 BLIK",
    successPayCard:     "💳 Karta",
    successPayCash:     "💵 Gotówka",
    successNote:        "Uwaga do zamówienia:",
    successWaiter:      "Kelner wkrótce przyniesie zamówienie",
    successReceipt:     "🧾 Pobierz paragon",
    successNewOrder:    "Nowe zamówienie",
    /* ── Scan messages ── */
    scanQrOk:           (n) => `QR: Stolik #${n} przypisany`,
    scanQrDisabled:     "QR jest wyłączony",
    scanQrTableDisabled:"QR dla tego stolika jest wyłączony",
    scanNfcOk:          (n) => `NFC: Stolik #${n} przypisany!`,
    scanNfcDisabled:    "NFC jest wyłączone",
    scanNfcTableDisabled:"NFC dla stolika wyłączone",
    scanNfcNA:          "NFC niedostępne — użyj QR",
    scanNfcError:       (msg) => `Błąd NFC: ${msg}`,
    scanNfcHint:        "Przyłóż telefon do tagu NFC...",
    /* ── Takeaway / Online order ── */
    pickupBtn:          "🏃 Odbiór osobisty",
    deliveryBtn:        "🚗 Dostawa",
    orTakeaway:         "lub zamów na wynos",
    outdoor:            "🌿 Ogródek / Zewnętrzne",
    indoor:             "🏠 Wewnętrzne",
    orderBack:          "← Wróć",
    orderSummary:       "🛒 Zamówienie",
    deliveryZoneLabel:  "Strefa dowozu * — wybierz odległość od restauracji",
    deliveryFree:       "Bezpłatnie",
    deliveryAddrHint:   "ul. Przykładowa 12/3, 00-000 Miasto",
    /* ── Addons ── */
    addonsTitle:        "Wybierz dodatki",
    addonsBase:         "Cena bazowa",
    addonsTotal:        "Razem",
    addonsFree:         "Gratis",
    addonsAdd:          "Dodaj do koszyka",
    /* ── OnlineOrderForm ── */
    onlineDeliveryTitle:"🚗 Zamówienie z dostawą",
    onlinePickupTitle:  "🏃 Zamówienie — odbiór osobisty",
    onlineStep1:        "1. Dane",
    onlineStep2:        "2. Menu",
    onlineStep3:        "3. Podsumowanie",
    onlineYourData:     "📝 Twoje dane",
    onlineFirstName:    "Imię *",
    onlineLastName:     "Nazwisko *",
    onlinePhone:        "Telefon *",
    onlineAddress:      "Adres dostawy *",
    onlineNoteOpt:      "Uwagi (opcjonalnie)",
    onlineNoteHint:     "np. bez cebuli, kod do domofonu...",
    onlineZoneLabel:    "Strefa dowozu * — wybierz odległość od restauracji",
    onlineKmFrom:       (a, b) => `${a}–${b} km od restauracji`,
    onlineNext:         "Dalej — wybierz menu →",
    onlineFillRequired: "Wypełnij wymagane pola",
    onlineFillDelivery: "Wypełnij imię, nazwisko i telefon",
    onlineSelectZone:   "Wybierz strefę dowozu",
    onlineAddMin:       "Dodaj przynajmniej jedną pozycję",
    onlineSearchMenu:   "🔍 Szukaj w menu...",
    onlineHasAddons:    "+dodatki",
    onlineSummaryBtn:   (qty, total) => `🛒 Podsumowanie · ${qty} poz. · ${total} zł →`,
    onlineDataTitle:    "👤 Dane",
    onlineOrderTitle:   "🛒 Zamówienie",
    onlineDeliveryLine: (name) => `🚗 Dostawa (${name})`,
    onlineFreeDelivery: "Bezpłatnie",
    onlineTotalLine:    (total) => `Łącznie: ${total} zł`,
    onlinePlaceBtn:     (isDelivery) => `✅ Złóż zamówienie ${isDelivery ? "z dostawą" : "na odbiór"}`,
    onlineBackBtn:      "← Wróć",
    onlineAddonFree:    "Gratis",
    onlineAddonMax:     (n) => `max ${n} szt.`,
    /* ── CartEditModal ── */
    cartTitle:          "🛒 Twój koszyk",
    cartEmpty:          "Koszyk jest pusty",
    cartItems:          (qty, wait) => `${qty} pozycji · ~${wait} min`,
    cartItemNote:       "Uwagi do pozycji (np. bez cebuli)",
    cartOrderNote:      "Uwagi do całego zamówienia",
    cartSubtotal:       "Suma przed rabatem:",
    cartDiscount:       (code) => `Rabat (${code}):`,
    cartToPay:          "Do zapłaty:",
    cartOrderBtn:       (price) => `Zamów · ${price} zł`,
    /* ── Category grid ── */
    catItemCount:       (n) => `${n} poz.`,
    /* ── Privacy declined ── */
    privacyNoConsent:   "Brak zgody",
    privacyHowTitle:    "Jak złożyć zamówienie?",
    privacyOpt1:        "Zamów u kelnera przy stoliku",
    privacyOpt2:        "Złóż zamówienie przy kasie",
    privacyOpt3:        "Zadzwoń do restauracji",
    privacyGoBack:      "← Wróć i zaakceptuj politykę",
    privacyScrollHint:  "↓ Przewiń aby przeczytać całość",
    privacyReadFirst:   "Prosimy o zapoznanie się przed złożeniem zamówienia",
    /* ── Menu item translations (pl uses original INIT_MENU data) ── */
    menuItems: {},
    /* ── Navbar ── */
    navEditCart:        "✏ edytuj",
  },
  en: {
    /* ── Table selector ── */
    chooseTable:        "Choose a table",
    scanHint:           "Tap a number or scan QR / NFC code",
    qrLabel:            "QR Code",
    qrSub:              "Scan with camera",
    nfcLabel:           "NFC",
    nfcScanning:        "Scanning...",
    nfcHint:            "Hold phone to tag",
    orManual:           "or select manually",
    legendFree:         "Free",
    legendReserved:     "Reserved",
    legendOccupied:     "Occupied",
    occupiedTitle:      "🍽 This table is occupied",
    occupiedDesc:       "Want to add something or need help? Call the staff — a waiter will come to your table.",
    staffCalled:        "Staff has been called!",
    staffSoon:          (n) => `A waiter will come to table #${n} shortly`,
    callStaff:          (n) => `🔔 Call staff to table #${n}`,
    reservationWarn:    "⚠ This table has a reservation:",
    reservationAt:      (time, guests) => `Time: ${time} · ${guests} guests`,
    seats:              (n) => `Seats: ${n}`,
    confirmTable:       (n) => `Confirm table #${n} →`,
    confirmEmpty:       "Confirm table #— →",
    guestsQuestion:     "How many guests?",
    guestsTooMany:      (n) => `This table only has ${n} seats`,
    guestsConfirm:      (n, tb) => `Confirm table #${tb} · ${n} guests →`,
    /* ── Nav ── */
    navOrders:          "🛍 Orders",
    navAdmin:           "⚙ Admin",
    /* ── Menu / categories ── */
    catAll:             "All",
    catPromo:           "Specials",
    promoToday:         "Today's specials!",
    promoCount:         (n) => `${n} item${n===1?"":"s"} at a lower price`,
    noProducts:         "No products",
    ingredients:        "Ingredients",
    allergens:          "⚠ allergens",
    addToCart:          "Add",
    itemNotePlaceholder:"Note (e.g. no onion, allergies...)",
    tableLabel:         (n) => `Table #${n}`,
    /* ── Cart bar ── */
    cartLabel:          (qty) => `Cart · ${qty} item${qty===1?"":"s"}`,
    orderNotePlaceholder:"Order note (e.g. allergies, preferences)...",
    placeOrder:         "Order →",
    voucherPlaceholder: "COUPON CODE",
    voucherUse:         "Apply",
    /* ── Voucher messages ── */
    voucherNotFound:    "Code not found",
    voucherInactive:    "Code is inactive",
    voucherUsed:        "Code already used",
    voucherMinOrder:    (n) => `Min. order: ${fmt(n)} PLN`,
    voucherMaxUses:     "Code exhausted",
    voucherOk:          (v) => v.type==="percent" ? `✓ ${v.value}% off` : `✓ -${fmt(v.value)} PLN off`,
    /* ── Item zoom modal ── */
    zoomIngredients:    "Ingredients",
    zoomAllergens:      "Allergens",
    zoomWait:           (n) => `⏱ ~${n} min`,
    zoomAddToCart:      "Add to cart",
    /* ── Campaign strip ── */
    campaignSee:        "See",
    campaignCode:       "Code",
    campaignCodeLabel:  "Your discount code",
    campaignCopy:       "📋 Copy",
    campaignCopied:     "✓ Copied!",
    /* ── Banner ── */
    bannerSeeOffer:     "See offer →",
    /* ── Payment picker ── */
    payTitle:           "How would you like to pay?",
    payTable:           (n) => `Table #${n}`,
    payBack:            "← Back to cart",
    payMethodCard:      "Card",
    payMethodCardSub:   "Terminal at checkout",
    payMethodBlik:      "BLIK",
    payMethodBlikSub:   "Quick mobile payment",
    payMethodCash:      "Cash",
    payMethodCashSub:   "Pay on collection",
    payMethodPaypal:    "PayPal",
    payMethodPaypalSub: "Pay via PayPal",
    payBtnCard:         "Order and pay by card",
    payBtnBlik:         "Go to BLIK payment",
    payBtnCash:         "Order and pay cash",
    payBtnPaypal:       "Order via PayPal",
    payCoupon:          (code, disc) => `🎟 Coupon ${code} · -${fmt(disc)} PLN`,
    /* ── BLIK screen ── */
    blikTitle:          "BLIK Payment",
    blikSubtitle:       "Open your banking app and generate a BLIK code",
    blikAmount:         "Amount to pay",
    blikCodeLabel:      "BLIK code (6 digits)",
    blikSteps: [
      "Open your banking app on your phone",
      "Find the BLIK or BLIK Code option",
      "Enter the 6-digit code shown above",
      "Confirm the payment in your banking app",
    ],
    blikConfirm:        "Confirm BLIK payment",
    blikEnter:          "Enter BLIK code",
    blikBack:           "Back to cart",
    blikWaiting:        "Waiting for confirmation",
    blikWaitSub:        "Confirm the payment in your banking app",
    blikAmount2:        "Amount",
    blikCancel:         "Cancel",
    blikSuccess:        "Payment accepted!",
    blikSuccessSub:     "Sending your order to the kitchen...",
    /* ── Success screen ── */
    successTitle:       "Order placed!",
    successBlik:        "BLIK payment confirmed ✓",
    successCash:        "Pay cash on collection",
    successCard:        "Pay by card on collection",
    successTable:       "Table",
    successTime:        "Time",
    successPayment:     "Payment",
    successCoupon:      "Coupon",
    successDiscount:    "Discount",
    successTotal:       "Total paid",
    successPayBlik:     "📱 BLIK",
    successPayCard:     "💳 Card",
    successPayCash:     "💵 Cash",
    successNote:        "Order note:",
    successWaiter:      "Your waiter will bring the order shortly",
    successReceipt:     "🧾 Download receipt",
    successNewOrder:    "New order",
    /* ── Scan messages ── */
    scanQrOk:           (n) => `QR: Table #${n} assigned`,
    scanQrDisabled:     "QR is disabled",
    scanQrTableDisabled:"QR for this table is disabled",
    scanNfcOk:          (n) => `NFC: Table #${n} assigned!`,
    scanNfcDisabled:    "NFC is disabled",
    scanNfcTableDisabled:"NFC for this table is disabled",
    scanNfcNA:          "NFC unavailable – use QR",
    scanNfcError:       (msg) => `NFC error: ${msg}`,
    scanNfcHint:        "Hold phone to NFC tag...",
    /* ── Takeaway / Online order ── */
    pickupBtn:          "🏃 Pickup",
    deliveryBtn:        "🚗 Delivery",
    orTakeaway:         "or order to go",
    outdoor:            "🌿 Garden / Outdoor",
    indoor:             "🏠 Indoor",
    orderBack:          "← Back",
    orderSummary:       "🛒 Order",
    deliveryZoneLabel:  "Delivery zone * — select distance from restaurant",
    deliveryFree:       "Free",
    deliveryAddrHint:   "123 Main St, City",
    /* ── Addons ── */
    addonsTitle:        "Choose extras",
    addonsBase:         "Base price",
    addonsTotal:        "Total",
    addonsFree:         "Free",
    addonsAdd:          "Add to cart",
    /* ── OnlineOrderForm ── */
    onlineDeliveryTitle:"🚗 Delivery order",
    onlinePickupTitle:  "🏃 Order — pickup",
    onlineStep1:        "1. Details",
    onlineStep2:        "2. Menu",
    onlineStep3:        "3. Summary",
    onlineYourData:     "📝 Your details",
    onlineFirstName:    "First name *",
    onlineLastName:     "Last name *",
    onlinePhone:        "Phone *",
    onlineAddress:      "Delivery address *",
    onlineNoteOpt:      "Notes (optional)",
    onlineNoteHint:     "e.g. no onion, gate code...",
    onlineZoneLabel:    "Delivery zone * — select distance from restaurant",
    onlineKmFrom:       (a, b) => `${a}–${b} km from restaurant`,
    onlineNext:         "Next — choose menu →",
    onlineFillRequired: "Please fill in all required fields",
    onlineFillDelivery: "Please fill in first name, last name and phone",
    onlineSelectZone:   "Please select a delivery zone",
    onlineAddMin:       "Please add at least one item",
    onlineSearchMenu:   "🔍 Search menu...",
    onlineHasAddons:    "+extras",
    onlineSummaryBtn:   (qty, total) => `🛒 Summary · ${qty} items · ${total} →`,
    onlineDataTitle:    "👤 Details",
    onlineOrderTitle:   "🛒 Order",
    onlineDeliveryLine: (name) => `🚗 Delivery (${name})`,
    onlineFreeDelivery: "Free",
    onlineTotalLine:    (total) => `Total: ${total}`,
    onlinePlaceBtn:     (isDelivery) => `✅ Place order ${isDelivery ? "with delivery" : "for pickup"}`,
    onlineBackBtn:      "← Back",
    onlineAddonFree:    "Free",
    onlineAddonMax:     (n) => `max ${n} pcs.`,
    /* ── CartEditModal ── */
    cartTitle:          "🛒 Your cart",
    cartEmpty:          "Your cart is empty",
    cartItems:          (qty, wait) => `${qty} item${qty===1?"":"s"} · ~${wait} min`,
    cartItemNote:       "Note (e.g. no onion)",
    cartOrderNote:      "Note for the whole order",
    cartSubtotal:       "Subtotal:",
    cartDiscount:       (code) => `Discount (${code}):`,
    cartToPay:          "To pay:",
    cartOrderBtn:       (price) => `Order · ${price}`,
    /* ── Category grid ── */
    catItemCount:       (n) => `${n} item${n===1?"":"s"}`,
    /* ── Privacy declined ── */
    privacyNoConsent:   "No consent",
    privacyHowTitle:    "How to place an order?",
    privacyOpt1:        "Order from your waiter at the table",
    privacyOpt2:        "Place your order at the counter",
    privacyOpt3:        "Call the restaurant",
    privacyGoBack:      "← Back to accept privacy policy",
    privacyScrollHint:  "↓ Scroll to read in full",
    privacyReadFirst:   "Please read before placing your order",

    /* ── Menu item translations ── */
    menuItems: {
      101: { name: "Scrambled Eggs",            desc: "3 eggs, butter, bread",                                                                         ingredients: "3 eggs, butter, bread",                                                              allergens: "eggs, gluten, dairy" },
      102: { name: "Omelette",                   desc: "Made from 4 eggs, 300g",                                                                        ingredients: "4 eggs, butter",                                                                      allergens: "eggs, dairy" },
      103: { name: "English Breakfast",          desc: "Frankfurters, 2 fried eggs, baked beans, bacon, toast",                                         ingredients: "frankfurters, 2 fried eggs, baked beans, bacon, toast",                               allergens: "eggs, gluten, dairy" },
      104: { name: "Baker's Breakfast",          desc: "Boiled egg, cheese, cold cuts, vegetables, cottage cheese, bread basket",                        ingredients: "boiled egg, cheese, cold cuts, vegetables, cottage cheese, bread basket",             allergens: "eggs, gluten, dairy" },
      105: { name: "Display Sandwiches",         desc: "Sandwiches from the display",                                                                   ingredients: "bread, cold cuts, cheese, vegetables",                                               allergens: "gluten, dairy" },
      106: { name: "Oatmeal",                    desc: "300g",                                                                                          ingredients: "oat flakes, milk, fruit",                                                             allergens: "gluten, dairy" },
      107: { name: "Shakshuka",                  desc: "Tomato, garlic, bell pepper, onion, 3 eggs",                                                    ingredients: "tomato, garlic, bell pepper, onion, 3 eggs",                                          allergens: "eggs" },
      108: { name: "Eggs Benedict",              desc: "2 poached eggs, pancetta, hollandaise sauce, parmesan, toast",                                  ingredients: "2 poached eggs, pancetta, hollandaise sauce, parmesan, toast",                        allergens: "eggs, gluten, dairy" },
      109: { name: "Toast",                      desc: "Egg, cheese, bacon, 2 slices of toast",                                                         ingredients: "egg, cheese, bacon, 2 slices of toast",                                              allergens: "eggs, gluten, dairy" },
      110: { name: "Breakfast Wrap",             desc: "Eggs, onion, mushrooms, cheese, tortilla",                                                      ingredients: "eggs, onion, mushrooms, cheese, tortilla",                                            allergens: "eggs, gluten, dairy" },
      111: { name: "Pancake",                    desc: "With cottage cheese or fruit jam, pan-fried in butter",                                         ingredients: "wheat flour, eggs, milk, butter, cottage cheese or jam",                              allergens: "eggs, gluten, dairy" },
      112: { name: "Bagel with Egg & Bacon",     desc: "Bagel with egg and bacon",                                                                      ingredients: "bagel, egg, bacon",                                                                   allergens: "eggs, gluten, dairy, sesame" },
      113: { name: "New York Bagel with Salmon", desc: "Bagel with salmon",                                                                             ingredients: "bagel, smoked salmon, cottage cheese, capers, onion",                                allergens: "fish, gluten, dairy, sesame" },
      201: { name: "Garlic Bread Sticks",        desc: "Yeast dough, 5 pcs",                                                                            ingredients: "yeast dough, garlic, butter, parsley",                                               allergens: "gluten, dairy" },
      202: { name: "Nachos Guacamole",           desc: "Nachos with guacamole",                                                                         ingredients: "corn nachos, avocado, tomato, onion, lime, coriander",                               allergens: "" },
      203: { name: "BBQ Baked Wings",            desc: "In BBQ sauce, 8 pcs",                                                                           ingredients: "chicken wings, BBQ sauce, garlic, paprika",                                           allergens: "" },
      204: { name: "Burrata & Avocado Toast",    desc: "Avocado, tomato, garlic, burrata, olive oil, balsamic vinegar",                                 ingredients: "bread, avocado, tomato, garlic, burrata, olive oil, balsamic vinegar",               allergens: "gluten, dairy" },
      205: { name: "Black Tiger Prawns",         desc: "With aioli and sweet chilli sauce, 10 pcs",                                                     ingredients: "Black Tiger prawns, aioli sauce, sweet chilli sauce, garlic",                        allergens: "crustaceans, eggs" },
      206: { name: "Breaded Calamari",           desc: "With garlic sauce, 10 pcs",                                                                     ingredients: "calamari, breadcrumbs, garlic sauce",                                                allergens: "gluten, eggs, molluscs" },
      207: { name: "Beef Tartare",               desc: "150g",                                                                                          ingredients: "beef 150g, pickled cucumber, onion, capers, egg yolk, seasoning",                    allergens: "eggs, mustard" },
      208: { name: "Sweet Potato Fries",         desc: "With herb cream cheese, 200g",                                                                  ingredients: "sweet potato, herb cream cheese, salt, oil",                                         allergens: "dairy" },
      209: { name: "Fries",                      desc: "200g",                                                                                          ingredients: "potatoes, salt, oil",                                                                allergens: "" },
      210: { name: "Soup of the Day",            desc: "300g",                                                                                          ingredients: "seasonal ingredients",                                                               allergens: "gluten, dairy" },
      211: { name: "Goulash Soup",               desc: "300g",                                                                                          ingredients: "beef, onion, bell pepper, potatoes, spices",                                         allergens: "celery" },
      212: { name: "Tripe Soup",                 desc: "300g",                                                                                          ingredients: "beef tripe, vegetables, marjoram, pepper, ginger",                                   allergens: "celery, gluten" },
      301: { name: "Caesar with Chicken",        desc: "400g",                                                                                          ingredients: "romaine lettuce, grilled chicken, parmesan, croutons, Caesar dressing",             allergens: "eggs, gluten, dairy, fish" },
      302: { name: "Caesar with Prawns",         desc: "400g",                                                                                          ingredients: "romaine lettuce, prawns, parmesan, croutons, Caesar dressing",                      allergens: "crustaceans, eggs, gluten, dairy, fish" },
      303: { name: "Greek Salad",                desc: "400g",                                                                                          ingredients: "tomato, cucumber, red onion, olives, feta, oregano",                                 allergens: "dairy" },
      304: { name: "Niçoise Salad",              desc: "400g",                                                                                          ingredients: "lettuce, tuna, egg, tomato, olives, anchovies, mustard dressing",                   allergens: "fish, eggs, mustard" },
      401: { name: "Chicken Strips",             desc: "4 pieces, fries, ketchup",                                                                      ingredients: "breaded chicken breast, fries, ketchup",                                             allergens: "gluten, eggs" },
      402: { name: "Chicken Nuggets",            desc: "8 pieces, fries, ketchup",                                                                      ingredients: "chicken nuggets, fries, ketchup",                                                    allergens: "gluten, eggs" },
      501: { name: "Classic Burger",             desc: "150g beef, lettuce, iceberg salsa, onion, mustard, ketchup + fries + sauces",                   ingredients: "wheat bun, beef 150g, lettuce, iceberg salsa, onion, mustard, ketchup, fries",      allergens: "gluten, dairy, eggs, sesame, mustard" },
      502: { name: "Cheeseburger",               desc: "150g beef, cheese, iceberg lettuce, onion, mustard, pickles, ketchup + fries + sauces",         ingredients: "wheat bun, beef 150g, cheese, iceberg lettuce, onion, mustard, pickles, ketchup, fries", allergens: "gluten, dairy, eggs, sesame, mustard" },
      503: { name: "Bacon Cheeseburger",         desc: "Bacon, 150g beef, cheese, lettuce, tomato, BBQ sauce, mayo, onion + fries + sauces",            ingredients: "wheat bun, bacon, beef 150g, cheese, iceberg lettuce, tomato, BBQ sauce, mayo, onion, fries", allergens: "gluten, dairy, eggs, sesame, mustard" },
      504: { name: "Jalapeño Burger",            desc: "150g beef, lettuce, onion, jalapeño, cheese, BBQ sauce, mayo + fries + sauces",                 ingredients: "wheat bun, beef 150g, lettuce, onion, jalapeño, cheese, BBQ sauce, mayo, fries",   allergens: "gluten, dairy, eggs, sesame" },
      505: { name: "Fish Burger",                desc: "Cod fillet 150g, coleslaw, honey-pepper sauce + fries + sauces",                                ingredients: "wheat bun, cod 150g, breadcrumbs, coleslaw, honey-pepper sauce, fries",             allergens: "gluten, dairy, eggs, fish, sesame, mustard" },
      506: { name: "Buffalo Chicken Burger",     desc: "Grilled chicken, buffalo sauce, lettuce, pickles, mayo",                                        ingredients: "wheat bun, grilled chicken, buffalo sauce, lettuce, pickles, mayo",                 allergens: "gluten, dairy, eggs, sesame" },
      507: { name: "Crispy Chicken Burger",      desc: "Chicken thigh, burger sauce, lettuce, cheese, onion",                                           ingredients: "wheat bun, breaded chicken thigh, burger sauce, lettuce, cheese, onion",             allergens: "gluten, dairy, eggs, sesame" },
      601: { name: "Steak 250g – mash",          desc: "Served on mashed potato with mushroom sauce and buttered vegetables",                            ingredients: "beef steak 250g, mashed potato, mushroom sauce, buttered vegetables",               allergens: "dairy, celery" },
      602: { name: "Steak 250g – fries",         desc: "With fries, seasonal salad and paprika-rosemary chutney sauce",                                 ingredients: "beef steak 250g, fries, seasonal salad, paprika-rosemary chutney",                  allergens: "celery" },
      701: { name: "Burrito 600g",               desc: "Wheat tortilla, beans, corn, rice, pork-beef blend, bell pepper, coriander",                    ingredients: "wheat tortilla, beans, corn, rice, pork-beef blend, bell pepper, coriander",        allergens: "gluten" },
      702: { name: "Vege Burrito 500g",          desc: "Wheat tortilla, grilled vegetables, spinach, sour cream, salsa verde, cheese",                  ingredients: "wheat tortilla, grilled vegetables, spinach, sour cream, salsa verde, cheese",      allergens: "gluten, dairy" },
      703: { name: "Tacos 400g",                 desc: "3 corn tortillas, grilled chicken, mixed greens, sauce, beans, onion, cream cheese, jalapeño, tomato", ingredients: "3 corn tortillas, grilled chicken, mixed greens, beans, onion, cream cheese, jalapeño, tomato", allergens: "dairy" },
      704: { name: "Tacos 500g",                 desc: "3 tortillas with pulled pork and cheese, guacamole, pickled red onion with chilli, tomato salsa, coriander", ingredients: "3 tortillas, pulled pork, cheese, guacamole, red onion, chilli, tomato salsa, coriander", allergens: "gluten, dairy" },
      705: { name: "Fajita 500g",                desc: "Choice of beef or chicken with colourful peppers and onion, pinto beans, tomato, cream cheese, wheat tortilla", ingredients: "beef or chicken, colourful peppers, onion, pinto beans, tomato, cream cheese, wheat tortilla", allergens: "gluten, dairy" },
      706: { name: "Chili Con Carne 350g",       desc: "Beef, tomato, onion, beans, corn, chilli, sour cream, cheese, tortilla pasta",                  ingredients: "beef, tomato, onion, beans, corn, chilli, sour cream, cheese, tortilla pasta",      allergens: "gluten, dairy" },
      707: { name: "Enchiladas 400g",            desc: "Wheat tortilla baked with cheese, choice of beef or chicken",                                   ingredients: "wheat tortilla, cheese, beef or chicken, tomato sauce, sour cream",                 allergens: "gluten, dairy" },
      801: { name: "Keto Scrambled Eggs",        desc: "3 eggs, bacon ×5, veggie salad, olive oil, garlic, half avocado – carbs 3g, 400 kcal",          ingredients: "3 eggs, bacon ×5, veggie salad, olive oil, garlic, avocado",                        allergens: "eggs" },
      802: { name: "Keto Fried Eggs",            desc: "2 eggs, half avocado, veggie salad, garlic, olive oil – carbs 3g, 400 kcal",                    ingredients: "2 eggs, avocado, veggie salad, garlic, olive oil",                                   allergens: "eggs" },
      803: { name: "Keto Omelette",              desc: "3 eggs with spinach and mushrooms – carbs 3g, 400 kcal",                                        ingredients: "3 eggs, spinach, mushrooms, butter",                                                allergens: "eggs, dairy" },
      804: { name: "Keto Avocado Toast",         desc: "2 poached eggs, green cucumber, cottage cheese, lettuce, tomato, vinaigrette – 3g, 400 kcal",   ingredients: "poached eggs, green cucumber, cottage cheese, lettuce, tomato, vinaigrette",        allergens: "eggs, dairy" },
      805: { name: "Keto Nut Shake",             desc: "Coconut milk 200ml, peanut butter 20g, MCT oil 5g, vanilla protein powder 20g – 300ml, 364 kcal", ingredients: "coconut milk, peanut butter, MCT oil, vanilla protein powder",                     allergens: "nuts" },
      806: { name: "Keto Berry Shake",           desc: "Forest berries 100g – 57.4 kcal",                                                              ingredients: "forest berries, coconut milk",                                                       allergens: "" },
      807: { name: "Keto Burger",                desc: "160g beef, 2 toasts, lettuce, tomato, bacon, yellow cheese, fried egg, parmesan, mayo – protein 39g, fat 34g, carbs 2g", ingredients: "beef 160g, 2 toasts, lettuce, tomato, bacon, yellow cheese, fried egg, parmesan, mayo", allergens: "eggs, gluten, dairy, mustard" },
      901: { name: "Chocolate Delight",          desc: "Ice dessert – cream, ice cream, fruit mousse, 200g",                                            ingredients: "cream, chocolate ice cream, fruit mousse",                                           allergens: "dairy, eggs" },
      902: { name: "Vanilla Treat",              desc: "Ice dessert – mascarpone, ice cream, fruit, meringue, 200g",                                    ingredients: "mascarpone, vanilla ice cream, fruit, meringue",                                     allergens: "dairy, eggs, gluten" },
      903: { name: "Refreshing Sorbet",          desc: "Ice dessert – meringue, sorbet, whipped cream, crumble, sauce, 200g",                           ingredients: "meringue, fruit sorbet, whipped cream, crumble",                                    allergens: "dairy, eggs, gluten" },
      904: { name: "Apple Pie",                  desc: "Warm, with vanilla ice cream, 150g",                                                            ingredients: "apples, shortcrust pastry, cinnamon, vanilla ice cream",                             allergens: "gluten, dairy, eggs" },
      905: { name: "Tiramisu",                   desc: "150g",                                                                                          ingredients: "mascarpone, ladyfingers, espresso, cocoa, eggs",                                     allergens: "dairy, eggs, gluten" },
      906: { name: "Warm Cheesecake",            desc: "With plum jam and fruit, 150g",                                                                 ingredients: "cottage cheese, eggs, sugar, flour, plum jam, fruit",                                allergens: "dairy, eggs, gluten" },
      907: { name: "Cake Slice",                 desc: "Or other selected cake, 120g",                                                                  ingredients: "seasonal cake",                                                                      allergens: "gluten, dairy, eggs" },
      908: { name: "Ice Cream Scoop",            desc: "60g",                                                                                           ingredients: "ice cream",                                                                          allergens: "dairy, eggs" },
      909: { name: "Waffle with Fruit",          desc: "With fruit and whipped cream",                                                                  ingredients: "waffle batter, fruit, whipped cream",                                                allergens: "gluten, dairy, eggs" },
      910: { name: "Pancake with Fruit",         desc: "With fruit and whipped cream",                                                                  ingredients: "pancake batter, fruit, whipped cream",                                               allergens: "gluten, dairy, eggs" },
      911: { name: "Pavlova Meringue",           desc: "Meringue with cream and fresh fruit",                                                           ingredients: "meringue, cream, fresh fruit",                                                       allergens: "eggs, dairy" },
      1001: { name: "Lamita Chardonnay",         desc: "Dry white wine, 0.75l",                                                                         ingredients: "dry white Chardonnay wine",                                                          allergens: "sulphites" },
      1002: { name: "Lamita Cabernet Sauvignon", desc: "Dry red wine, 0.75l",                                                                           ingredients: "dry red Cabernet Sauvignon wine",                                                    allergens: "sulphites" },
      1003: { name: "Sol del Mediterráneo Moscatel", desc: "Sweet white wine, 0.75l",                                                                   ingredients: "sweet white Moscatel wine",                                                          allergens: "sulphites" },
      1004: { name: "Turnau Johanniter",         desc: "Semi-sweet white wine, 0.75l",                                                                  ingredients: "semi-sweet white Johanniter wine",                                                   allergens: "sulphites" },
      1101: { name: "Aperol Spritz",             desc: "400ml",                                                                                         ingredients: "Aperol, prosecco, sparkling water, orange",                                          allergens: "sulphites" },
      1102: { name: "Mimosa",                    desc: "Prosecco, fresh orange juice, 200ml",                                                           ingredients: "prosecco, fresh orange juice",                                                       allergens: "sulphites" },
      1103: { name: "Prosecco on Tap",           desc: "100ml",                                                                                         ingredients: "prosecco",                                                                           allergens: "sulphites" },
      1104: { name: "Prosecco",                  desc: "200ml, raspberry flavour or other – ask staff",                                                 ingredients: "prosecco",                                                                           allergens: "sulphites" },
      1105: { name: "Martini Dry / Bianco",      desc: "100ml",                                                                                         ingredients: "Martini Dry or Bianco",                                                              allergens: "sulphites" },
      1106: { name: "Martini with Mint & Lime",  desc: "On ice, 300ml",                                                                                 ingredients: "Martini, mint, lime, ice",                                                           allergens: "sulphites" },
      1107: { name: "Hot Mulled Wine with Honey",desc: "Mulled wine with honey and spices",                                                             ingredients: "red wine, honey, cinnamon, cloves, orange",                                         allergens: "sulphites" },
      1201: { name: "Pilsner Urquell",           desc: "0.5l",                                                                                          ingredients: "pale lager",                                                                         allergens: "gluten" },
      1202: { name: "Połczyn Honey Beer",        desc: "0.5l",                                                                                          ingredients: "honey beer",                                                                         allergens: "gluten" },
      1203: { name: "Książęce Wheat Beer",       desc: "0.5l",                                                                                          ingredients: "wheat beer",                                                                         allergens: "gluten" },
      1204: { name: "Lech Free (non-alcoholic)", desc: "330ml, non-alcoholic",                                                                          ingredients: "non-alcoholic beer",                                                                 allergens: "gluten" },
      1205: { name: "Hot Honey Beer",            desc: "0.5l",                                                                                          ingredients: "beer, honey, spices",                                                                allergens: "gluten" },
      1301: { name: "Caffe Crema / Americano",   desc: "",                                                                                              ingredients: "coffee, water",                                                                      allergens: "" },
      1302: { name: "Espresso",                  desc: "Single / double 16 PLN",                                                                        ingredients: "espresso coffee",                                                                    allergens: "" },
      1303: { name: "Espresso Macchiato",        desc: "",                                                                                              ingredients: "espresso, milk",                                                                     allergens: "dairy" },
      1304: { name: "Cappuccino",                desc: "",                                                                                              ingredients: "espresso, frothed milk",                                                             allergens: "dairy" },
      1305: { name: "Café Latte",                desc: "",                                                                                              ingredients: "espresso, milk",                                                                     allergens: "dairy" },
      1306: { name: "Caffè Latte Macchiato",     desc: "",                                                                                              ingredients: "espresso, layered milk",                                                             allergens: "dairy" },
      1307: { name: "Iced Coffee",               desc: "",                                                                                              ingredients: "espresso, milk, ice",                                                                allergens: "dairy" },
      1308: { name: "Caffè Mocha",               desc: "With chocolate syrup and whipped cream",                                                        ingredients: "espresso, chocolate syrup, milk, whipped cream",                                     allergens: "dairy" },
      1309: { name: "Tea",                       desc: "Various flavours available",                                                                    ingredients: "loose-leaf or tea bag",                                                              allergens: "" },
      1310: { name: "Green Tea with Fruit",      desc: "",                                                                                              ingredients: "green tea, fruit",                                                                   allergens: "" },
      1311: { name: "Winter Tea with Rum",       desc: "350ml",                                                                                         ingredients: "tea, rum, honey, lemon, spices",                                                     allergens: "" },
      1312: { name: "Winter Tea",                desc: "",                                                                                              ingredients: "tea, honey, lemon, spices",                                                          allergens: "" },
      1313: { name: "Hot Chocolate Large",       desc: "350ml",                                                                                         ingredients: "chocolate, milk, cream",                                                             allergens: "dairy, soy" },
      1314: { name: "Hot Chocolate Small",       desc: "200ml",                                                                                         ingredients: "chocolate, milk, cream",                                                             allergens: "dairy, soy" },
      1401: { name: "Water",                     desc: "330ml",                                                                                         ingredients: "mineral water",                                                                      allergens: "" },
      1402: { name: "Cisowianka Perlage",        desc: "330ml, sparkling",                                                                              ingredients: "sparkling mineral water",                                                             allergens: "" },
      1403: { name: "Pepsi / Coca-Cola",         desc: "0.2l",                                                                                          ingredients: "carbonated soft drink",                                                              allergens: "" },
      1404: { name: "7UP / Sprite",              desc: "0.2l",                                                                                          ingredients: "carbonated soft drink",                                                              allergens: "" },
      1405: { name: "Mirinda / Fanta",           desc: "0.2l",                                                                                          ingredients: "carbonated soft drink",                                                              allergens: "" },
      1406: { name: "Lipton Iced Tea",           desc: "0.2l",                                                                                          ingredients: "iced tea",                                                                           allergens: "" },
      1407: { name: "Red Bull",                  desc: "250ml",                                                                                         ingredients: "water, sucrose, glucose, taurine, caffeine, B vitamins",                             allergens: "" },
      1408: { name: "Orange Juice",              desc: "200ml",                                                                                         ingredients: "orange juice",                                                                       allergens: "" },
      1409: { name: "Apple Juice",               desc: "200ml",                                                                                         ingredients: "apple juice",                                                                        allergens: "" },
      1410: { name: "Mojito Free",               desc: "300ml, non-alcoholic",                                                                          ingredients: "lime, mint, sugar, sparkling water, ice",                                            allergens: "" },
      1411: { name: "Lemonade of the Day",       desc: "Today's lemonade",                                                                              ingredients: "lemon, sugar, water, mint, ice",                                                     allergens: "" },
      1412: { name: "Shake",                     desc: "300ml – choice of: banana, strawberry or raspberry",                                            ingredients: "milk, ice cream, fruit, whipped cream",                                              allergens: "dairy, eggs" },
      1413: { name: "Fresh Juice Small",         desc: "200ml – orange or grapefruit",                                                                  ingredients: "fresh oranges or grapefruit",                                                        allergens: "" },
      1414: { name: "Fresh Juice Large",         desc: "350ml – orange or grapefruit",                                                                  ingredients: "fresh oranges or grapefruit",                                                        allergens: "" },
    },
    /* ── Navbar ── */
    navEditCart:        "✏ edit",
  },
};
function useTr() {
  const { lang, customTranslations } = useLang();
  const base = LANG[lang] || LANG.pl;
  const overrides = (customTranslations || {})[lang] || {};
  // Only override string keys (not functions)
  const merged = { ...base };
  Object.entries(overrides).forEach(([k, v]) => {
    if (typeof v === "string" && v.trim() !== "") merged[k] = v;
  });
  return merged;
}
// eslint-disable-next-line no-unused-vars
function getCatName(cat, tr) { return (tr.catNames && tr.catNames[cat.id]) || cat.name; }
function getItemTr(item, tr, field) { return (tr.menuItems && tr.menuItems[item.id] && tr.menuItems[item.id][field]) || item[field] || ''; }

/* ════════════════════ ONLINE ORDER FORM ════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════════════════════
   SKLEP ONLINE — całkowicie osobny system zamówień
══════════════════════════════════════════════════════════════════════════════ */
function TableSelector({ settings, tables, orders, onSelect, onNFC, nfcScanning, onCallStaff, onOnlineOrder, onOnlineShop, onAdminTap }) {
  const [sel, setSel] = useState(null);
  const [guests, setGuests] = useState(2);
  const [staffCalled, setStaffCalled] = useState(false);
  const [logoTapsTS, setLogoTapsTS] = useState(0);
  const logoTapTimerTS = useRef(null);
  const bp = useBreakpoint();
  const ac = settings.accentColor;
  const { lang, setLang } = useLang();
  const activeTables = (tables||[]).filter(t=>t.active);
  const selTable = activeTables.find(t=>t.id===sel);
  const today = todayStr();
  const activeOrders = (orders||[]).filter(o => o.date === today);
  const occupiedIds = new Set(activeOrders.map(o => o.table));
  const isOccupied = t => t.manuallyFreed ? false : (t.occupied || occupiedIds.has(t.id) || occupiedIds.has(t.number));

  const handleCallStaff = () => {
    if (!selTable) return;
    onCallStaff && onCallStaff({ tableId: selTable.id, tableNumber: selTable.number, time: new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}) });
    setStaffCalled(true);
    setTimeout(() => setStaffCalled(false), 10000);
  };

  const t = useTr();
  const selOccupied = selTable && isOccupied(selTable);
  const tableInfo = (settings.tablePageInfo || "").trim();
  const tooMany = selTable && guests > selTable.seats;

  const bgStyle  = settings.customerBgStyle || "bakery";
  const bgImage  = settings.customerBgImage || null;
  const bgColor  = settings.customerBgColor || "#FDF6EC";
  const opacity  = settings.tableSelectorOpacity !== undefined ? Number(settings.tableSelectorOpacity) : 0.97;

  const pageBg = bgStyle === "custom" && bgImage
    ? `url(${bgImage}) center/cover no-repeat fixed`
    : bgStyle === "plain"
      ? bgColor
      : "linear-gradient(160deg,#3B1F0A 0%,#6B3F1E 45%,#C8621A 100%)";

  const cardBase = bgStyle === "bakery" ? "253,246,236" : "255,255,255";
  const cardBg   = `rgba(${cardBase},${opacity})`;
  const cardBorder = bgStyle === "bakery" ? "#E8D5BA" : T.border;
  const acBakery   = bgStyle === "bakery" ? "#C8621A" : ac;

  const isPhone   = bp && bp.xs;
  const isMobile  = bp && bp.sm && !bp.isTablet;
  const isTablet  = bp && bp.isTablet;
  const isDesktop = bp && bp.isDesktop;

  const gridCols = isPhone   ? "repeat(5,1fr)"
    : isMobile  ? "repeat(6,1fr)"
    : isTablet  ? "repeat(8,1fr)"
    : "repeat(10,1fr)";

  const btnPad  = isPhone ? "8px 0" : "11px 0";
  const btnFont = isPhone ? 12 : isMobile ? 13 : 14;
  const btnRad  = isPhone ? 9 : 12;

  const cardStyle = isDesktop ? {
    width:"100%", maxWidth:520,
    borderRadius:24, padding:"28px 26px",
    boxShadow:"0 20px 60px rgba(0,0,0,0.28)",
    border:"1.5px solid "+cardBorder,
  } : isTablet ? {
    width:"100%", maxWidth:"88vw",
    borderRadius:22, padding:"24px 22px",
    boxShadow:"0 16px 50px rgba(0,0,0,0.22)",
    border:"1.5px solid "+cardBorder,
  } : {
    width:"100%", maxWidth:"100%",
    borderRadius:18, padding:"16px 13px",
    boxShadow:"0 8px 30px rgba(0,0,0,0.18)",
    border:"1.5px solid "+cardBorder,
  };

  const renderGrid = (tbList) => (
    <div style={{display:"grid", gridTemplateColumns:gridCols, gap:isPhone?5:7, marginBottom:8}}>
      {tbList.map(tb=>{
        const reserved   = !!tb.reservation;
        const occupied   = isOccupied(tb);
        const isSelected = sel===tb.id;
        return (
          <button key={tb.id}
            style={{
              padding:btnPad, borderRadius:btnRad,
              border:"2px solid", fontWeight:700, fontSize:btnFont,
              cursor:"pointer", transition:"all .12s",
              borderColor: isSelected?(occupied?"#e94560":ac):occupied?"#e94560":reserved?"#f39c12":"#eee",
              background:  isSelected?(occupied?"#c0392b":ac):occupied?"#fde8ec":reserved?"#fff8e1":"#f8f8f8",
              color:       isSelected?"#fff":occupied?"#e94560":reserved?"#e67e22":"#444",
              position:"relative",
            }}
            onClick={()=>{ setSel(tb.id); setStaffCalled(false); }}>
            {tb.number}
            {occupied&&!isSelected&&<span style={{position:"absolute",top:1,right:1,fontSize:7}}>🍽</span>}
            {reserved&&!occupied&&!isSelected&&<span style={{position:"absolute",top:1,right:2,fontSize:7,color:"#f39c12",fontWeight:900}}>🔒</span>}
          </button>
        );
      })}
    </div>
  );

  const indoorTables  = activeTables.filter(tb=>tb.type!=="outdoor");
  const outdoorTables = activeTables.filter(tb=>tb.type==="outdoor");
  const outdoorOn     = settings?.outdoorTablesEnabled;

  return (
    <>
    <div style={{
      minHeight:"calc(100vh - 52px)",
      display:"flex",
      flexDirection:"column",
      alignItems:"center",
      justifyContent: isPhone ? "flex-start" : "center",
      background:pageBg,
      padding: isPhone ? "10px 8px" : isTablet ? "20px" : "24px",
      gap: isPhone ? 12 : 16,
      position:"relative",
      overflow:"hidden",
    }}>
      {bgStyle==="bakery"&&<>
        <div style={{position:"absolute",top:-80,right:-80,width:320,height:320,borderRadius:"50%",background:"rgba(200,98,26,0.12)",pointerEvents:"none"}}/>
        <div style={{position:"absolute",bottom:-100,left:-60,width:380,height:380,borderRadius:"50%",background:"rgba(59,31,10,0.15)",pointerEvents:"none"}}/>
        <div style={{position:"absolute",top:"30%",left:-40,width:180,height:180,borderRadius:"50%",background:"rgba(200,98,26,0.08)",pointerEvents:"none"}}/>
      </>}

      <div style={{background:cardBg, textAlign:"center", position:"relative", zIndex:1, ...cardStyle}}>

        {/* Logo + tytuł + język w jednym wierszu */}
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:isPhone?12:18,position:"relative"}}>
          <div style={{position:"absolute",right:0,top:0,display:"flex",background:"rgba(255,255,255,0.55)",borderRadius:20,border:"1.5px solid "+cardBorder,overflow:"hidden",padding:2,gap:2,flexShrink:0}}>
            {[["pl","🇵🇱"],["en","🇬🇧"]].map(([code,flag])=>(
              <button key={code} onClick={()=>setLang(code)}
                style={{padding:isPhone?"4px 6px":"5px 10px",borderRadius:16,border:"none",cursor:"pointer",
                  fontWeight:700,fontSize:isPhone?11:12,transition:"all .15s",
                  background: lang===code ? (bgStyle==="bakery"?"#3B1F0A":T.primary) : "transparent",
                  color: lang===code ? "#fff" : bgStyle==="bakery"?"#6B3F1E":T.muted,
                }}>
                {flag}
              </button>
            ))}
          </div>

          <div onClick={()=>{
            if (!onAdminTap) return;
            const next = logoTapsTS + 1;
            setLogoTapsTS(next);
            if (logoTapTimerTS.current) clearTimeout(logoTapTimerTS.current);
            if (next >= 5) { setLogoTapsTS(0); onAdminTap(); }
            else { logoTapTimerTS.current = setTimeout(()=>setLogoTapsTS(0), 3000); }
          }} style={{cursor:"default",userSelect:"none",marginBottom:10}}>
            {settings.logo
              ? <img src={settings.logo} alt="logo" style={{height:isPhone?50:isTablet?64:72,width:isPhone?50:isTablet?64:72,borderRadius:16,objectFit:"cover",boxShadow:"0 6px 16px rgba(0,0,0,0.15)",display:"block"}}/>
              : <div style={{fontSize:isPhone?36:48}}>🪑</div>
            }
          </div>
          <div style={{textAlign:"center"}}>
            <div style={{fontWeight:900,fontSize:isPhone?18:isTablet?22:24,color:bgStyle==="bakery"?"#3B1F0A":T.primary,fontFamily:"'Playfair Display',serif",lineHeight:1.2}}>
              {t.chooseTable}
            </div>
            <p style={{color:bgStyle==="bakery"?"#8B6A4A":T.textSoft,fontSize:isPhone?11:13,margin:"4px 0 0"}}>{t.scanHint}</p>
          </div>
        </div>


        {/* Informacja admina */}
        {tableInfo && (
          <div style={{background:"linear-gradient(135deg,#fff8e1,#fffef5)",border:"1.5px solid #E8A020",borderRadius:12,padding:"9px 12px",marginBottom:11,textAlign:"left",animation:"fadeSlide .3s ease"}}>
            <div style={{display:"flex",gap:7,alignItems:"flex-start"}}>
              <span style={{fontSize:14,flexShrink:0}}>ℹ</span>
              <div style={{fontSize:11,color:"#6B3F1E",lineHeight:1.65,whiteSpace:"pre-wrap"}}>{tableInfo}</div>
            </div>
          </div>
        )}

        {/* QR + NFC */}
        <div style={{display:"flex",gap:7,marginBottom:11}}>
          <div style={{flex:1,background:"rgba(255,255,255,0.55)",border:"1.5px dashed "+cardBorder,borderRadius:11,padding:isPhone?"7px 4px":"10px 8px",textAlign:"center"}}>
            <div style={{fontSize:isPhone?18:22,marginBottom:2}}>📷</div>
            <div style={{fontWeight:700,color:bgStyle==="bakery"?"#3B1F0A":T.primary,fontSize:isPhone?10:12}}>{t.qrLabel}</div>
            {!isPhone&&<div style={{fontSize:10,color:T.muted,marginTop:2}}>{t.qrSub}</div>}
          </div>
          <button onClick={onNFC} disabled={nfcScanning}
            style={{flex:1,background:nfcScanning?"#f39c12":bgStyle==="bakery"?"#3B1F0A":T.primary,border:"none",borderRadius:11,padding:isPhone?"7px 4px":"10px 8px",textAlign:"center",cursor:nfcScanning?"wait":"pointer",transition:"all .2s"}}>
            <div style={{fontSize:isPhone?18:22,marginBottom:2,animation:nfcScanning?"blikSpin 1.5s linear infinite":"none",display:"inline-block"}}>📡</div>
            <div style={{fontWeight:700,color:"#fff",fontSize:isPhone?10:12}}>{t.nfcLabel}</div>
            {!isPhone&&<div style={{fontSize:10,color:"rgba(255,255,255,0.8)",marginTop:2}}>{nfcScanning?t.nfcScanning:t.nfcHint}</div>}
          </button>
        </div>

        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:9}}>
          <div style={{flex:1,height:1,background:cardBorder}}/>
          <span style={{fontSize:10,fontWeight:600,color:bgStyle==="bakery"?"#C8A87A":T.muted}}>{t.orManual}</span>
          <div style={{flex:1,height:1,background:cardBorder}}/>
        </div>

        {/* Legenda */}
        <div style={{display:"flex",gap:isPhone?6:12,justifyContent:"center",marginBottom:isPhone?8:12,fontSize:10,color:bgStyle==="bakery"?"#8B6A4A":T.textSoft,flexWrap:"wrap"}}>
          <span><span style={{display:"inline-block",width:8,height:8,borderRadius:3,background:"#f8f8f8",border:"1px solid #ddd",marginRight:3}}/>{t.legendFree}</span>
          <span><span style={{display:"inline-block",width:8,height:8,borderRadius:3,background:"#fff3e0",border:"1px solid #f39c12",marginRight:3}}/>{t.legendReserved}</span>
          <span><span style={{display:"inline-block",width:8,height:8,borderRadius:3,background:"#fde8ec",border:"1px solid #e94560",marginRight:3}}/>{t.legendOccupied}</span>
        </div>

        {/* Siatka stolików */}
        {renderGrid(indoorTables)}
        {outdoorOn && outdoorTables.length > 0 && (
          <>
            <div style={{fontSize:11,fontWeight:700,color:bgStyle==="bakery"?"#8B6A4A":T.muted,textAlign:"left",marginBottom:5,marginTop:4}}>🌿 Zewnętrzne</div>
            {renderGrid(outdoorTables)}
          </>
        )}

        {/* Zajęty stolik */}
        {selOccupied && (
          <div style={{background:"#fde8ec",border:"1.5px solid #e94560",borderRadius:12,padding:"11px 13px",marginBottom:11,textAlign:"left",animation:"fadeSlide .2s ease"}}>
            <div style={{fontWeight:800,fontSize:13,color:"#c0392b",marginBottom:4}}>{t.occupiedTitle}</div>
            <div style={{fontSize:12,color:"#922b21",lineHeight:1.55,marginBottom:9}}>{t.occupiedDesc}</div>
            {staffCalled ? (
              <div style={{background:"#e8f5e9",border:"1.5px solid #27ae60",borderRadius:10,padding:"9px 12px",textAlign:"center",animation:"popIn .25s ease"}}>
                <div style={{fontSize:18,marginBottom:3}}>✅</div>
                <div style={{fontWeight:800,fontSize:13,color:"#1e8449"}}>{t.staffCalled}</div>
                <div style={{fontSize:11,color:"#27ae60",marginTop:2}}>{t.staffSoon(selTable.number)}</div>
              </div>
            ) : (
              <button onClick={handleCallStaff}
                style={{width:"100%",padding:"10px 0",background:"#e94560",color:"#fff",border:"none",borderRadius:10,fontWeight:800,fontSize:13,cursor:"pointer",boxShadow:"0 3px 10px rgba(233,69,96,0.35)",transition:"all .15s"}}>
                {t.callStaff(selTable.number)}
              </button>
            )}
          </div>
        )}

        {/* Rezerwacja */}
        {selTable&&selTable.reservation&&!selOccupied&&(
          <div style={{background:"#fff8e1",border:"1px solid #f39c12",borderRadius:10,padding:"8px 12px",marginBottom:11,fontSize:12,color:"#8a6000",textAlign:"left"}}>
            <div style={{fontWeight:700,marginBottom:2}}>{t.reservationWarn}</div>
            <div>{t.reservationAt(selTable.reservation.time, selTable.reservation.guests)}</div>
          </div>
        )}

        {selTable&&!selOccupied&&<div style={{fontSize:11,color:bgStyle==="bakery"?"#8B6A4A":T.muted,marginBottom:9}}>{t.seats(selTable.seats)}</div>}

        {/* Liczba osób */}
        {selTable && !selOccupied && (
          <div style={{marginBottom:13,animation:"fadeSlide .2s ease"}}>
            <div style={{fontSize:12,fontWeight:700,color:bgStyle==="bakery"?"#3B1F0A":T.primary,marginBottom:7,textAlign:"left"}}>
              👥 {t.guestsQuestion}
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:0,
              background:"rgba(255,255,255,0.55)",borderRadius:14,
              border:"1.5px solid "+(tooMany?"#e94560":cardBorder),overflow:"hidden"}}>
              <button onClick={()=>setGuests(g=>Math.max(1,g-1))}
                style={{width:isPhone?42:52,height:isPhone?42:52,border:"none",background:"transparent",fontSize:22,fontWeight:700,
                  cursor:"pointer",color:guests<=1?cardBorder:bgStyle==="bakery"?"#3B1F0A":T.primary,transition:"all .1s",flexShrink:0}}>−</button>
              <div style={{flex:1,textAlign:"center"}}>
                <div style={{fontSize:isPhone?22:28,fontWeight:900,color:tooMany?"#e94560":bgStyle==="bakery"?"#3B1F0A":T.primary,lineHeight:1}}>{guests}</div>
                <div style={{fontSize:10,color:bgStyle==="bakery"?"#8B6A4A":T.muted,marginTop:2}}>{lang==="en"?`of ${selTable.seats}`:`z ${selTable.seats} miejsc`}</div>
              </div>
              <button onClick={()=>setGuests(g=>g+1)}
                style={{width:isPhone?42:52,height:isPhone?42:52,border:"none",background:"transparent",fontSize:22,fontWeight:700,
                  cursor:"pointer",color:bgStyle==="bakery"?"#3B1F0A":T.primary,transition:"all .1s",flexShrink:0}}>+</button>
            </div>
            {(!isPhone || selTable.seats <= 8) && (
              <div style={{display:"flex",gap:4,justifyContent:"center",marginTop:8,flexWrap:"wrap"}}>
                {Array.from({length:selTable.seats}).map((_,i)=>(
                  <div key={i} onClick={()=>setGuests(i+1)}
                    style={{width:isPhone?24:30,height:isPhone?24:30,borderRadius:"50%",cursor:"pointer",
                      fontSize:isPhone?11:14,display:"flex",alignItems:"center",justifyContent:"center",
                      transition:"all .15s",
                      background: i < guests ? (tooMany?"#fde8ec":ac+"22") : "rgba(255,255,255,0.5)",
                      border:"2px solid "+(i < guests ? (tooMany?"#e94560":ac) : cardBorder),
                      transform: i===guests-1?"scale(1.12)":"scale(1)"}}>
                    🧑
                  </div>
                ))}
              </div>
            )}
            {tooMany && (
              <div style={{marginTop:7,fontSize:12,color:"#e94560",fontWeight:700,textAlign:"center",animation:"fadeSlide .15s ease"}}>
                ⚠ {t.guestsTooMany(selTable.seats)}
              </div>
            )}
          </div>
        )}

        {/* Przycisk potwierdzenia */}
        {!selOccupied && (
          <button
            style={{width:"100%",padding:isPhone?10:13,
              background:"transparent",
              color: sel&&!tooMany ? acBakery : "#ccc",
              border:"2px solid "+(sel&&!tooMany ? acBakery : "#ccc"),
              borderRadius:12,fontWeight:800,fontSize:isPhone?13:14,
              cursor:sel&&!tooMany?"pointer":"not-allowed",transition:"all .15s"}}
            onClick={()=>sel&&!selOccupied&&!tooMany&&onSelect(sel,guests)}
            disabled={!sel||tooMany}>
            {selTable ? t.guestsConfirm(guests, selTable.number) : t.confirmEmpty}
          </button>
        )}

        {/* stare przyciski online usunięte — teraz osobna karta poniżej */}
      </div>

      {/* ══ OSOBNA KARTA: SKLEP ONLINE — tuż pod kartą stolika ══ */}
      {onOnlineShop && (
        <div style={{
          width:"100%",
          maxWidth: isDesktop?520:isTablet?"88vw":"100%",
          borderRadius: isPhone?18:22,
          overflow:"hidden",
          boxShadow:"0 8px 32px rgba(0,0,0,0.22)",
          position:"relative", zIndex:1,
        }}>
          {/* Górny pasek */}
          <div style={{
            background: settings?.onlineCardBgImage
              ? `url(${settings.onlineCardBgImage}) center/cover`
              : `linear-gradient(135deg,${settings?.onlineCardColor||(bgStyle==="bakery"?"#7B4F2E":"#1565C0")},${settings?.onlineCardGradient2||(bgStyle==="bakery"?"#A0522D":"#0984e3")})`,
            opacity: settings?.onlineCardHeaderOpacity !== undefined ? settings.onlineCardHeaderOpacity : 1,
            padding: isPhone?"14px 16px":"18px 22px",
            display:"flex", alignItems:"center", gap:14,
          }}>
            <div style={{
              width:46,height:46,borderRadius:13,
              background:"rgba(255,255,255,0.18)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:24,flexShrink:0,overflow:"hidden",
            }}>
              {settings?.onlineCardLogo
                ? <img src={settings.onlineCardLogo} alt="" style={{width:46,height:46,objectFit:"cover",borderRadius:13}}/>
                : settings?.onlineCardIcon||"🌐"}
            </div>
            <div style={{flex:1}}>
              <div style={{color:"#fff",fontWeight:900,fontSize:isPhone?16:18,lineHeight:1.2}}>
                {settings?.onlineCardTitle||"Zamów online"}
              </div>
              <div style={{color:"rgba(255,255,255,0.7)",fontSize:isPhone?11:12,marginTop:3}}>
                {settings?.restaurantName||"Restauracja"} · dostawa lub odbiór
              </div>
            </div>
            <div style={{
              background:"rgba(255,255,255,0.2)",
              borderRadius:10,padding:"4px 10px",
              color:"#fff",fontSize:10,fontWeight:700,
            }}>ONLINE</div>
          </div>
          {/* Dolna sekcja */}
          <div style={{
            background: settings?.onlineCardBtnBg||(bgStyle==="bakery"?"#FFF8F0":"#f0f7ff"),
            padding: isPhone?"14px 16px":"16px 22px",
            display:"flex", gap:10,
            borderTop:"none",
          }}>
            <button onClick={onOnlineShop} style={{
              flex:1,
              padding: isPhone?"13px 8px":"15px 12px",
              borderRadius:14,
              border:"none",
              background: settings?.onlineCardBtnColor||(bgStyle==="bakery"?"#A0522D":"#0984e3"),
              color:"#fff",
              fontWeight:900,
              cursor:"pointer",
              fontSize: isPhone?13:14,
              boxShadow: `0 4px 14px ${settings?.onlineCardBtnColor||(bgStyle==="bakery"?"rgba(160,82,45,0.4)":"rgba(9,132,227,0.4)")}`,
              display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            }}>
              🛒 {settings?.onlineCardBtnLabel||"Przejdź do sklepu online"}
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}


/* ════════════════════ BLIK PAYMENT SCREEN ═══════════════════════════════════ */
function BlikScreen({ order, ac, onSuccess, onBack }) {
  const tr = useTr();
  const [code,  setCode]  = useState("");
  const [phase, setPhase] = useState("input");
  const [dots,  setDots]  = useState(0);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  const digits    = code.replace(/[^0-9]/g, "").slice(0, 6);
  const formatted = digits.length > 3 ? digits.slice(0,3) + "-" + digits.slice(3) : digits;
  const isReady   = digits.length === 6;

  useEffect(() => {
    if (phase !== "processing") return;
    const t = setInterval(() => setDots(d => (d + 1) % 4), 500);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => {
    if (phase === "input" && inputRef.current) inputRef.current.focus();
  }, [phase]);

  const handleInput = e => {
    const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setCode(val);
  };

  const submit = () => {
    if (!isReady) return;
    setPhase("processing");
    timerRef.current = setTimeout(() => {
      setPhase("done");
      setTimeout(() => onSuccess({ ...order, paymentMethod: "blik", blikCode: digits }), 1200);
    }, 3000);
  };

  const cancel = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPhase("input");
    setCode("");
  };

  const outerStyle = {
    minHeight: "calc(100vh - 52px)", display: "flex", alignItems: "center",
    justifyContent: "center", background: T.bg, padding: 20
  };
  const cardStyle = {
    background: T.surface, borderRadius: 24, padding: "28px 24px",
    width: "100%", maxWidth: 380, boxShadow: T.cardShadow, border: "1px solid "+T.border
  };

  if (phase === "input") return (
    <div style={outerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 72, height: 72, borderRadius: 20,
            background: "linear-gradient(135deg,#e94560,#c0392b)",
            marginBottom: 12, boxShadow: "0 4px 16px rgba(233,69,96,0.35)"
          }}>
            <span style={{ fontSize: 34 }}>📱</span>
          </div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: "#1a1a2e" }}>{tr.blikTitle}</h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "#888" }}>{tr.blikSubtitle}</p>
        </div>

        <div style={{
          background: T.bg, border: "1.5px solid "+T.border,
          borderRadius: 14, padding: "12px 16px", textAlign: "center", marginBottom: 20
        }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 3 }}>{tr.blikAmount}</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: ac }}>{fmt(order.total)} zł</div>
          <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>Stolik #{order.tableNumber || order.table}</div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#666", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {tr.blikCodeLabel}
          </label>
          <input
            ref={inputRef}
            type="tel"
            value={formatted}
            onChange={handleInput}
            placeholder="000-000"
            style={{
              width: "100%", boxSizing: "border-box", padding: "16px 20px",
              fontSize: 32, fontWeight: 900, borderRadius: 14,
              border: "2px solid " + (isReady ? ac : "#ddd"),
              outline: "none", textAlign: "center", letterSpacing: 6,
              fontFamily: "monospace", color: "#1a1a2e",
              background: isReady ? "#f0faf4" : "#fff", transition: "all .2s"
            }}
          />
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 10 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{
                width: 10, height: 10, borderRadius: "50%",
                background: i < digits.length ? ac : "#e0e0e0",
                transform: i < digits.length ? "scale(1.2)" : "scale(1)",
                transition: "all .15s"
              }} />
            ))}
          </div>
        </div>

        <div style={{ background: "#f8f8f8", borderRadius: 12, padding: "12px 14px", marginBottom: 20 }}>
          {[
            ["1", tr.blikSteps[0]],
            ["2", tr.blikSteps[1]],
            ["3", tr.blikSteps[2]],
            ["4", tr.blikSteps[3]],
          ].map(([n, t]) => (
            <div key={n} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 7 }}>
              <div style={{
                width: 20, height: 20, borderRadius: "50%", background: ac, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 900, flexShrink: 0, marginTop: 1
              }}>{n}</div>
              <div style={{ fontSize: 12, color: "#555", lineHeight: 1.4 }}>{t}</div>
            </div>
          ))}
        </div>

        <button onClick={submit} disabled={!isReady} style={{
          width: "100%", padding: 15, background: isReady ? ac : "#ccc",
          color: "#fff", border: "none", borderRadius: 14,
          fontWeight: 800, fontSize: 16, cursor: isReady ? "pointer" : "not-allowed",
          boxShadow: isReady ? "0 4px 16px rgba(0,0,0,0.2)" : "none",
          transition: "all .2s", marginBottom: 10
        }}>
          {isReady ? tr.blikConfirm : tr.blikEnter}
        </button>
        <button onClick={onBack} style={{
          width: "100%", padding: 12, background: "#f0f2f5", color: "#666",
          border: "none", borderRadius: 12, fontWeight: 600, cursor: "pointer", fontSize: 13
        }}>
          {tr.blikBack}
        </button>
      </div>
    </div>
  );

  if (phase === "processing") return (
    <div style={outerStyle}>
      <div style={{ ...cardStyle, textAlign: "center" }}>
        <div style={{
          width: 80, height: 80, borderRadius: "50%",
          border: "5px solid "+T.border, borderTop: "5px solid " + T.accent,
          margin: "0 auto 24px",
          animation: "blikSpin 0.9s linear infinite"
        }} />
        <style>{".blikSpin-anim { animation: blikSpin 0.9s linear infinite; } @keyframes blikSpin { to { transform: rotate(360deg); } }"}</style>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "#1a1a2e", margin: "0 0 8px" }}>
          {tr.blikWaiting}
        </h2>
        <p style={{ fontSize: 13, color: "#888", margin: "0 0 16px" }}>
          {tr.blikWaitSub + ".".repeat(dots + 1)}
        </p>
        <div style={{
          background: "#f8f8f8", borderRadius: 12, padding: "10px 16px",
          display: "inline-block", marginBottom: 20
        }}>
          <div style={{ fontFamily: "monospace", fontSize: 28, fontWeight: 900, color: ac, letterSpacing: 4 }}>
            {digits.slice(0, 3)}-{digits.slice(3, 6)}
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#aaa", marginBottom: 20 }}>
          {tr.blikAmount2}: <strong style={{ color: "#1a1a2e" }}>{fmt(order.total)} zł</strong>
        </div>
        <button onClick={cancel} style={{
          padding: "9px 20px", background: "#f0f2f5", color: "#888",
          border: "none", borderRadius: 10, fontWeight: 600, cursor: "pointer", fontSize: 12
        }}>{tr.blikCancel}</button>
      </div>
    </div>
  );

  if (phase === "done") return (
    <div style={outerStyle}>
      <div style={{ ...cardStyle, textAlign: "center" }}>
        <div style={{ fontSize: 72, marginBottom: 12 }}>✅</div>
        <h2 style={{ fontSize: 22, fontWeight: 900, color: T.green, margin: "0 0 6px", fontFamily:"'Playfair Display',serif" }}>{tr.blikSuccess}</h2>
        <p style={{ fontSize: 13, color: "#888", margin: 0 }}>{tr.blikSuccessSub}</p>
      </div>
    </div>
  );

  return null;
}

/* ════════════════════ PAYMENT METHOD PICKER ═════════════════════════════════ */
function PaymentPicker({ order, ac, settings, onBlik, onCash, onBack }) {
  const tr = useTr();
  const PM_DEFAULTS = {
    card:   { id:"card",   icon:"💳", label:tr.payMethodCard,   sub:tr.payMethodCardSub,   color:"#0984e3", btnLabel:tr.payBtnCard },
    blik:   { id:"blik",   icon:"📱", label:tr.payMethodBlik,   sub:tr.payMethodBlikSub,   color:"#e94560", btnLabel:tr.payBtnBlik },
    cash:   { id:"cash",   icon:"💵", label:tr.payMethodCash,   sub:tr.payMethodCashSub,   color:"#27ae60", btnLabel:tr.payBtnCash },
    paypal: { id:"paypal", icon:"🅿", label:tr.payMethodPaypal, sub:tr.payMethodPaypalSub, color:"#003087", btnLabel:tr.payBtnPaypal },
  };
  const pm = settings.paymentMethods || {};
  const methods = Object.entries(PM_DEFAULTS)
    .filter(([key]) => !pm[key] || pm[key].enabled)
    .map(([key, def]) => ({ ...def, ...(pm[key] || {}) }));

  return (
    <div style={{ minHeight:"calc(100vh - 52px)", background:T.bg, padding:"20px 16px" }}>

      {/* Header */}
      <div style={{ textAlign:"center", marginBottom:18 }}>
        {settings.logo
          ? <img src={settings.logo} alt="" style={{ height:42, borderRadius:10, objectFit:"contain", marginBottom:8 }}/>
          : <div style={{ fontSize:38, marginBottom:6 }}>🍽</div>}
        <div style={{ fontSize:13, color:T.muted }}>{tr.payTable(order.tableNumber || order.table)}</div>
        <div style={{ fontWeight:900, fontSize:22, color:T.primary, fontFamily:"'Playfair Display',serif", marginTop:2 }}>
          {tr.payTitle}
        </div>
      </div>

      {/* Total */}
      <div style={{ background:T.surface, border:"1.5px solid "+T.border, borderRadius:16,
        padding:"14px 18px", textAlign:"center", marginBottom:20,
        boxShadow:"0 2px 10px rgba(107,63,30,0.07)" }}>
        {order.discount > 0 && (
          <div style={{ fontSize:12, color:"#aaa", textDecoration:"line-through", marginBottom:2 }}>
            {fmt(order.totalBeforeDiscount)} zł
          </div>
        )}
        <div style={{ fontSize:36, fontWeight:900, color:ac, lineHeight:1 }}>{fmt(order.total)} zł</div>
        {order.voucherCode && (
          <div style={{ fontSize:11, color:"#27ae60", marginTop:4, fontWeight:700 }}>
            {tr.payCoupon(order.voucherCode, order.discount)}
          </div>
        )}
      </div>

      {/* Payment method cards — one per row */}
      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:18 }}>
        {methods.map(m => (
          <button key={m.id}
            onClick={() => { if (m.id === "blik") onBlik(); else onCash(m.id); }}
            style={{
              width:"100%", padding:"16px 18px",
              borderRadius:16,
              border:"2px solid "+m.color+"55",
              background:"#fff",
              cursor:"pointer",
              display:"flex", alignItems:"center", gap:16, textAlign:"left",
              boxShadow:"0 2px 8px "+m.color+"18",
              transition:"transform .12s, box-shadow .12s",
            }}
            onMouseDown={e => e.currentTarget.style.transform="scale(0.98)"}
            onMouseUp={e => e.currentTarget.style.transform="scale(1)"}
            onTouchStart={e => e.currentTarget.style.transform="scale(0.98)"}
            onTouchEnd={e => e.currentTarget.style.transform="scale(1)"}
          >
            {/* Icon circle */}
            <div style={{
              width:52, height:52, borderRadius:14, flexShrink:0,
              background:m.color+"18",
              display:"flex", alignItems:"center", justifyContent:"center", fontSize:26
            }}>{m.icon}</div>

            {/* Text */}
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:800, fontSize:16, color:T.text }}>{m.label}</div>
              <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>{m.sub}</div>
            </div>

            {/* Arrow */}
            <div style={{
              width:32, height:32, borderRadius:"50%", flexShrink:0,
              background:m.color, display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:15, color:"#fff", fontWeight:900
            }}>›</div>
          </button>
        ))}
      </div>

      {/* Back */}
      <button onClick={onBack} style={{
        width:"100%", padding:12, background:T.bg, color:T.muted,
        border:"1.5px solid "+T.border, borderRadius:12,
        fontWeight:600, cursor:"pointer", fontSize:13
      }}>
        {tr.payBack}
      </button>
    </div>
  );
}


/* ════════════════════ TABLES PANEL ══════════════════════════════════════════ */
/* ═══════════════════ SELLER TABLES PANEL ════════════════════════════════════
   Uproszczony widok stolików dla sprzedawcy — tylko rezerwacje, bez zarządzania.
══════════════════════════════════════════════════════════════════════════════ */
function SellerTablesPanel({ tables, setTables, orders, ac, settings }) {
  useBreakpoint();
  const BLANK_RES = { name: "", phone: "", time: "", guests: "2", note: "" };
  const [resModal, setResModal] = useState(null);
  const [resForm,  setResForm]  = useState(BLANK_RES);
  const [search,   setSearch]   = useState("");
  const [section,  setSection]  = useState("indoor"); // indoor | outdoor

  const todayActive = orders.filter(o => o.date === todayStr());
  const outdoorOn   = settings?.outdoorTablesEnabled;

  const statusOf = t => {
    const hasOrder = todayActive.some(o => o.table === t.id || o.table === t.number);
    const busy = t.manuallyFreed ? false : (t.occupied || hasOrder);
    if (busy)          return { label: "Zajęty",        icon: "🍽", color: "#e94560", bg: "#fde8ec" };
    if (t.reservation) return { label: "Zarezerwowany", icon: "🔒", color: "#f39c12", bg: "#fff8e1" };
    if (!t.active)     return { label: "Nieaktywny",    icon: "⛔", color: "#bbb",    bg: "#f5f5f5" };
    return               { label: "Wolny",             icon: "✅", color: "#27ae60", bg: "#e8f5e9" };
  };

  const openRes = id => {
    const t = tables.find(t => t.id === id);
    setResForm(t && t.reservation ? { ...t.reservation } : BLANK_RES);
    setResModal(id);
  };

  const saveRes = () => {
    if (!resForm.name.trim() || !resForm.time) { alert("Wpisz imię i godzinę"); return; }
    setTables(p => p.map(t => t.id === resModal
      ? { ...t, reservation: { ...resForm, guests: parseInt(resForm.guests) || 2 } }
      : t));
    setResModal(null);
  };

  const clearRes = id => {
    if (!window.confirm("Czy na pewno usunąć rezerwację?")) return;
    setTables(p => p.map(t => t.id === id ? { ...t, reservation: null } : t));
  };

  const resTable = resModal !== null ? tables.find(t => t.id === resModal) : null;

  const allActive = tables.filter(t => t.active);
  const indoorTables  = allActive.filter(t => t.type !== "outdoor");
  const outdoorTables = allActive.filter(t => t.type === "outdoor");
  const currentTables = (outdoorOn && section === "outdoor" ? outdoorTables : indoorTables)
    .filter(t => !search || String(t.number).includes(search) || (t.reservation && t.reservation.name.toLowerCase().includes(search.toLowerCase())))
    .sort((a, b) => a.number - b.number);

  const statsFor = tList => ({
    wolne:  tList.filter(t => !t.reservation && !todayActive.some(o => o.table===t.id||o.table===t.number)).length,
    zajete: tList.filter(t => !t.manuallyFreed && (t.occupied || todayActive.some(o => o.table===t.id||o.table===t.number))).length,
    rezerw: tList.filter(t => !!t.reservation).length,
  });
  const stats = statsFor(section==="outdoor"&&outdoorOn ? outdoorTables : indoorTables);

  return (
    <div>
      {/* Reservation modal */}
      {resModal !== null && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.72)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <div style={{ background:"#fff", borderRadius:20, padding:20, width:"100%", maxWidth:370, maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <span style={{ fontWeight:800, fontSize:15, color:T.text }}>
                {resTable?.type==="outdoor"?"🌿":"🪑"} Stolik #{resTable ? resTable.number : ""}
              </span>
              <button style={S.closeBtn} onClick={() => setResModal(null)}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
              <div style={{ gridColumn:"span 2" }}>
                <label style={S.label}>Imię i nazwisko *</label>
                <input style={S.inp} placeholder="Jan Kowalski" value={resForm.name}
                  onChange={e => setResForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Telefon</label>
                <input style={S.inp} placeholder="123 456 789" value={resForm.phone}
                  onChange={e => setResForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Godzina *</label>
                <input style={S.inp} type="time" value={resForm.time}
                  onChange={e => setResForm(p => ({ ...p, time: e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Liczba osób</label>
                <input style={S.inp} type="number" min="1" max="20" value={resForm.guests}
                  onChange={e => setResForm(p => ({ ...p, guests: e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Notatka</label>
                <input style={S.inp} placeholder="Urodziny, alergie..." value={resForm.note}
                  onChange={e => setResForm(p => ({ ...p, note: e.target.value }))} />
              </div>
            </div>
            <div style={{ display:"flex", gap:7, marginTop:12 }}>
              <button style={{ ...S.darkBtn, flex:1, background:ac }} onClick={saveRes}>
                {resTable && resTable.reservation ? "✏ Aktualizuj" : "✅ Zapisz rezerwację"}
              </button>
              {resTable && resTable.reservation && (
                <button style={{ ...S.cancelBtn, background:"#ffebee", color:"#e94560" }}
                  onClick={() => { clearRes(resModal); setResModal(null); }}>
                  🗑 Usuń
                </button>
              )}
              <button style={S.cancelBtn} onClick={() => setResModal(null)}>Anuluj</button>
            </div>
          </div>
        </div>
      )}

      {/* Sekcja wewnętrzne/zewnętrzne — tylko gdy outdoorOn */}
      {outdoorOn && outdoorTables.length > 0 && (
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          {[["indoor","🏠 Wewnętrzne",indoorTables.length],["outdoor","🌿 Ogródek",outdoorTables.length]].map(([v,l,cnt])=>(
            <button key={v} onClick={()=>setSection(v)} style={{
              flex:1, padding:"10px 8px", borderRadius:12,
              border:"2px solid "+(section===v?(v==="outdoor"?"#15803d":ac):T.border),
              background:section===v?(v==="outdoor"?"#f0fdf4":"#fff8e1"):T.surface,
              color:section===v?(v==="outdoor"?"#15803d":ac):T.muted,
              fontWeight:800, cursor:"pointer", fontSize:13
            }}>{l} <span style={{background:"rgba(0,0,0,0.08)",borderRadius:10,padding:"1px 7px",fontSize:11}}>{cnt}</span></button>
          ))}
        </div>
      )}

      {/* Stats bar */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:14 }}>
        {[
          { icon:"✅", val:stats.wolne,  label:"Wolnych",        color:"#27ae60", bg:"#e8f5e9" },
          { icon:"🍽", val:stats.zajete, label:"Zajętych",       color:"#e94560", bg:"#fde8ec" },
          { icon:"🔒", val:stats.rezerw, label:"Zarezerwowanych", color:"#f39c12", bg:"#fff8e1" },
        ].map(s => (
          <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:"10px 8px", textAlign:"center", border:`1.5px solid ${s.color}33` }}>
            <div style={{ fontSize:18 }}>{s.icon}</div>
            <div style={{ fontWeight:900, fontSize:18, color:s.color }}>{s.val}</div>
            <div style={{ fontSize:9, color:s.color, fontWeight:700 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <input style={{ ...S.inp, marginBottom:12 }} placeholder="🔍 Szukaj stolika lub nazwiska..."
        value={search} onChange={e => setSearch(e.target.value)} />

      {/* Table grid */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        {currentTables.map(t => {
          const st = statusOf(t);
          return (
            <div key={t.id} style={{ background:T.surface, borderRadius:14, padding:13, boxShadow:T.cardShadow,
              border:`2px solid ${st.color}44`, borderTop:`3px solid ${t.type==="outdoor"?"#15803d":st.color}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{ fontWeight:900, fontSize:16, color:T.text }}>Stolik #{t.number}</span>
                  {t.type==="outdoor"&&<span style={{background:"#f0fdf4",color:"#15803d",borderRadius:8,padding:"1px 6px",fontSize:9,fontWeight:800}}>🌿 zewn.</span>}
                </div>
                <span style={{ background:st.bg, color:st.color, borderRadius:20, padding:"2px 9px", fontSize:10, fontWeight:800 }}>
                  {st.icon} {st.label}
                </span>
              </div>
              {/* Seats inline editor */}
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                <span style={{ fontSize:11, color:T.muted }}>🪑 Miejsca:</span>
                <button
                  onClick={()=>setTables(p=>p.map(x=>x.id===t.id?{...x,seats:Math.max(1,(x.seats||1)-1)}:x))}
                  style={{ width:22,height:22,borderRadius:"50%",border:"1.5px solid "+T.border,
                    background:T.bg,fontSize:13,fontWeight:700,cursor:"pointer",
                    display:"flex",alignItems:"center",justifyContent:"center",color:T.muted }}>−</button>
                <span style={{ fontWeight:800, fontSize:14, color:T.primary, minWidth:18, textAlign:"center" }}>{t.seats||1}</span>
                <button
                  onClick={()=>setTables(p=>p.map(x=>x.id===t.id?{...x,seats:Math.min(20,(x.seats||1)+1)}:x))}
                  style={{ width:22,height:22,borderRadius:"50%",border:"1.5px solid "+T.border,
                    background:T.bg,fontSize:13,fontWeight:700,cursor:"pointer",
                    display:"flex",alignItems:"center",justifyContent:"center",color:T.muted }}>+</button>
              </div>

              {t.reservation && (
                <div style={{ background:"#fff8e1", borderRadius:9, padding:"8px 10px", marginBottom:9, fontSize:11 }}>
                  <div style={{ fontWeight:700, color:"#f39c12", marginBottom:3 }}>🔒 Rezerwacja</div>
                  <div style={{ color:T.text, fontWeight:600 }}>{t.reservation.name}</div>
                  {t.reservation.phone && <div style={{ color:T.muted }}>📞 {t.reservation.phone}</div>}
                  <div style={{ color:T.muted }}>🕐 {t.reservation.time} · 👥 {t.reservation.guests} os.</div>
                  {t.reservation.note && <div style={{ color:T.muted, fontStyle:"italic" }}>💬 {t.reservation.note}</div>}
                </div>
              )}

              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {st.label==="Zajęty" ? (
                  <button
                    onClick={()=>setTables(p=>p.map(x=>x.id===t.id?{...x,occupied:false,manuallyFreed:true}:x))}
                    style={{flex:1,padding:"7px 10px",background:"#e8f5e9",color:"#27ae60",
                      border:"1.5px solid #27ae60",borderRadius:9,fontWeight:700,cursor:"pointer",fontSize:11}}>
                    ✅ Zwolnij stolik
                  </button>
                ) : (
                  <button
                    onClick={()=>setTables(p=>p.map(x=>x.id===t.id?{...x,occupied:true,manuallyFreed:false}:x))}
                    style={{flex:1,padding:"7px 10px",background:"#fde8ec",color:"#e94560",
                      border:"1.5px solid #e94560",borderRadius:9,fontWeight:700,cursor:"pointer",fontSize:11}}>
                    🍽 Zajmij stolik
                  </button>
                )}
                <button
                  onClick={() => openRes(t.id)}
                  style={{ padding:"7px 10px", background:t.reservation?T.bg:ac, color:t.reservation?T.muted:"#fff",
                    border:`1.5px solid ${t.reservation?T.border:ac}`, borderRadius:9, fontWeight:700, cursor:"pointer", fontSize:11 }}>
                  {t.reservation ? "✏" : "+ Rez."}
                </button>
                {t.reservation && (
                  <button onClick={() => clearRes(t.id)}
                    style={{ padding:"7px 10px", background:"#ffebee", color:"#e94560",
                      border:"1.5px solid #e94560", borderRadius:9, fontWeight:700, cursor:"pointer", fontSize:11 }}>
                    🗑
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {currentTables.length === 0 && (
        <div style={S.empty}><div style={{ fontSize:40 }}>{section==="outdoor"?"🌿":"🪑"}</div><p>Brak stolików {section==="outdoor"?"zewnętrznych":""}</p></div>
      )}
    </div>
  );
}

function TablesPanel({ tables, setTables, orders, ac, settings, setSettings }) {
  useBreakpoint();
  const [view,     setView]     = useState("list");
  const [resModal, setResModal] = useState(null);
  const [qrTable,  setQrTable]  = useState(null);
  const [nfcTable, setNfcTable] = useState(null);
  const [showAdd,  setShowAdd]  = useState(false);
  const [newTable, setNewTable] = useState({ number: "", seats: 4, type: "indoor" });

  // Base URL for QR/NFC links
  const baseUrl = window.location.origin + window.location.pathname;
  const BLANK_RES = { name: "", phone: "", time: "", guests: "2", note: "" };
  const [resForm,  setResForm]  = useState(BLANK_RES);

  const todayActive = orders.filter(o => o.date === todayStr());

  const addTable = () => {
    if (!newTable.number) return;
    setTables(p => [...p, {
      id: genId(), number: parseInt(newTable.number),
      seats: parseInt(newTable.seats) || 4, active: true, reservation: null,
      type: newTable.type || "indoor"
    }]);
    setNewTable({ number: "", seats: 4, type: "indoor" });
    setShowAdd(false);
  };

  const openResModal = id => {
    const t = tables.find(t => t.id === id);
    setResForm(t && t.reservation ? { ...t.reservation } : BLANK_RES);
    setResModal(id);
  };

  const saveReservation = () => {
    if (!resForm.name || !resForm.time) return;
    setTables(p => p.map(t => t.id === resModal
      ? { ...t, reservation: { ...resForm, guests: parseInt(resForm.guests) || 2 } }
      : t));
    setResModal(null);
  };

  const clearReservation = id => setTables(p => p.map(t => t.id === id ? { ...t, reservation: null } : t));
  const toggleActive     = id => setTables(p => p.map(t => t.id === id ? { ...t, active: !t.active } : t));
  const deleteTable      = id => setTables(p => p.filter(t => t.id !== id));

  const statusOf = t => {
    const hasOrder = todayActive.some(o => o.table === t.id || o.table === t.number);
    const busy = t.manuallyFreed ? false : (t.occupied || hasOrder);
    if (busy)           return { label: "Zajęty", icon: "🍽",  color: "#e94560", bg: "#fde8ec" };
    if (t.reservation)  return { label: "Zarezerwowany", icon: "🔒", color: "#f39c12", bg: "#fff8e1" };
    if (!t.active)      return { label: "Nieaktywny", icon: "⛔", color: "#bbb",    bg: "#f5f5f5" };
    return               { label: "Wolny", icon: "✅",         color: "#27ae60", bg: "#e8f5e9" };
  };

  const resTable = resModal !== null ? tables.find(t => t.id === resModal) : null;

  return (
    <div>
      {/* Reservation modal */}
      {resModal !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: 20, width: "100%", maxWidth: 370, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontWeight: 800, fontSize: 15, color: "#1a1a2e" }}>
                Rezerwacja - Stolik #{resTable ? resTable.number : ""}
              </span>
              <button style={S.closeBtn} onClick={() => setResModal(null)}>x</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <input style={{ ...S.inp, gridColumn: "span 2" }} placeholder="Imię i nazwisko *" value={resForm.name}
                onChange={e => setResForm(p => ({ ...p, name: e.target.value }))} />
              <input style={S.inp} placeholder="Telefon" value={resForm.phone}
                onChange={e => setResForm(p => ({ ...p, phone: e.target.value }))} />
              <input style={S.inp} type="time" value={resForm.time}
                onChange={e => setResForm(p => ({ ...p, time: e.target.value }))} />
              <input style={S.inp} type="number" min="1" max="20" placeholder="Liczba osób" value={resForm.guests}
                onChange={e => setResForm(p => ({ ...p, guests: e.target.value }))} />
              <input style={S.inp} placeholder="Notatka" value={resForm.note}
                onChange={e => setResForm(p => ({ ...p, note: e.target.value }))} />
            </div>
            <div style={{ display: "flex", gap: 7, marginTop: 10 }}>
              <button style={{ ...S.darkBtn, flex: 1 }} onClick={saveReservation}>
                {resTable && resTable.reservation ? "Aktualizuj" : "Zapisz rezerwację"}
              </button>
              {resTable && resTable.reservation && (
                <button style={{ ...S.cancelBtn, background: "#ffebee", color: "#e94560" }}
                  onClick={() => { clearReservation(resModal); setResModal(null); }}>
                  Usuń
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {qrTable && <TableQRModal table={qrTable} baseUrl={baseUrl} onClose={() => setQrTable(null)}/>}
      {/* NFC Modal */}
      {nfcTable && <NFCWriterPanel table={nfcTable} baseUrl={baseUrl} onClose={() => setNfcTable(null)}/>}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 14, color: T.primary }}>🪑 Zarządzanie stolikami</div>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { k: "list", label: "📋 Lista" },
            { k: "qrnfc", label: "📷 QR / NFC" },
            { k: "map",  label: "🗺 Mapa" },
          ].map(v => (
            <button key={v.k}
              style={{ ...S.actBtn, ...(view === v.k ? { background: T.primary, color: "#fff", borderColor: T.primary } : {}), fontSize: 12, padding: "6px 12px", fontWeight: 600 }}
              onClick={() => setView(v.k)}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
        <button style={S.accentBtn(ac)} onClick={() => setShowAdd(p => !p)}>+ Dodaj stolik</button>
        <button
          onClick={()=>setSettings&&setSettings(p=>({...p,outdoorTablesEnabled:!p.outdoorTablesEnabled}))}
          style={{padding:"8px 14px",borderRadius:10,border:"2px solid "+(settings?.outdoorTablesEnabled?"#27ae60":"#bbb"),
            background:settings?.outdoorTablesEnabled?"#e8f5e9":"#fff",
            color:settings?.outdoorTablesEnabled?"#27ae60":"#888",
            fontWeight:700,cursor:"pointer",fontSize:13,transition:"all .2s"}}>
          {settings?.outdoorTablesEnabled?"🌿 Stoliki zewnętrzne: ON":"🌿 Stoliki zewnętrzne: OFF"}
        </button>
      </div>

      {showAdd && (
        <div style={S.formCard}>
          <div style={S.formTitle}>Nowy stolik</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7, marginBottom: 9 }}>
            <input style={S.inp} type="number" placeholder="Numer stolika" value={newTable.number}
              onChange={e => setNewTable(p => ({ ...p, number: e.target.value }))} />
            <input style={S.inp} type="number" placeholder="Liczba miejsc" value={newTable.seats}
              onChange={e => setNewTable(p => ({ ...p, seats: e.target.value }))} />
            <div style={{gridColumn:"span 2"}}>
              <label style={S.label}>Typ stolika</label>
              <div style={{display:"flex",gap:8}}>
                {[["indoor","🏠 Wewnętrzny"],["outdoor","🌿 Zewnętrzny/Sezonowy"]].map(([v,l])=>(
                  <button key={v} type="button"
                    onClick={()=>setNewTable(p=>({...p,type:v}))}
                    style={{flex:1,padding:"8px",borderRadius:9,border:"2px solid",
                      borderColor:(newTable.type||"indoor")===v?ac:T.border,
                      background:(newTable.type||"indoor")===v?"#fff8e1":"#fff",
                      color:(newTable.type||"indoor")===v?ac:T.muted,
                      fontWeight:700,cursor:"pointer",fontSize:12}}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 7 }}>
            <button style={S.darkBtn} onClick={addTable}>Dodaj</button>
            <button style={S.cancelBtn} onClick={() => setShowAdd(false)}>Anuluj</button>
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        {[["#27ae60","Wolny"],["#e94560","Zajęty"],["#f39c12","Zarezerwowany"],["#bbb","Nieaktywny"]].map(([col,lbl]) => (
          <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#666" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: col }} />
            {lbl}
          </div>
        ))}
      </div>

      {/* QR / NFC VIEW */}
      {view === "qrnfc" && (
        <div>
          {/* Info banner */}
          <div style={{ background: "linear-gradient(135deg,"+T.primary+","+T.accent+")", borderRadius: 14, padding: 16, marginBottom: 14, color: "#fff" }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>📷 Kody QR i tagi NFC</div>
            <div style={{ fontSize: 12, opacity: 0.88, lineHeight: 1.5 }}>
              Każdy stolik ma unikalny link. Włącz lub wyłącz QR/NFC per stolik lub globalnie w Ustawieniach.
            </div>
          </div>

          {/* Global status pills */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            {[
              { key: "qrEnabled",  icon: "📷", label: "QR globalnie" },
              { key: "nfcEnabled", icon: "📡", label: "NFC globalnie" },
            ].map(item => {
              const on = settings && settings[item.key] !== false;
              return (
                <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
                  borderRadius: 20, background: on ? "#dcfce7" : T.bg, border: "1.5px solid " + (on ? "#86efac" : T.border),
                  fontSize: 12, fontWeight: 700, color: on ? "#15803d" : T.muted }}>
                  {item.icon} {item.label}: <span style={{ color: on ? "#15803d" : "#ef4444" }}>{on ? "Włączone" : "Wyłączone"}</span>
                </div>
              );
            })}
            <div style={{ fontSize: 11, color: T.muted, alignSelf: "center", marginLeft: 4 }}>
              (zmień w ⚙ Ustawienia)
            </div>
          </div>

          {/* Source stats from today's orders */}
          {(() => {
            const todayOrd = orders.filter(o => o.date === todayStr());
            const qrCount  = todayOrd.filter(o => o.scanSource === "qr").length;
            const nfcCount = todayOrd.filter(o => o.scanSource === "nfc").length;
            const manCount = todayOrd.filter(o => !o.scanSource).length;
            return todayOrd.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginBottom: 14 }}>
                {[
                  { icon: "📷", label: "QR",     val: qrCount,  col: T.accent },
                  { icon: "📡", label: "NFC",     val: nfcCount, col: "#7c3aed" },
                  { icon: "👆", label: "Ręcznie", val: manCount, col: T.muted },
                ].map(s => (
                  <div key={s.label} style={{ background: T.surface, borderRadius: 12, padding: "10px 8px", textAlign: "center", border: "1px solid "+T.border }}>
                    <div style={{ fontSize: 20 }}>{s.icon}</div>
                    <div style={{ fontWeight: 900, fontSize: 18, color: s.col, margin: "4px 0 2px" }}>{s.val}</div>
                    <div style={{ fontSize: 10, color: T.muted }}>{s.label} dziś</div>
                  </div>
                ))}
              </div>
            ) : null;
          })()}

          {/* Table QR/NFC cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tables.filter(t => t.active).sort((a,b) => a.number - b.number).map(t => {
              const url = baseUrl + "?table=" + t.id;
              const todayOrdersForTable = orders.filter(o =>
                o.date === todayStr() && (o.table === t.id || o.table === t.number)
              );
              const qrOrders  = todayOrdersForTable.filter(o => o.scanSource === "qr").length;
              const nfcOrders = todayOrdersForTable.filter(o => o.scanSource === "nfc").length;
              const st = statusOf(t);

              return (
                <div key={t.id} style={{ ...S.card, borderLeft: "4px solid "+st.color }}>
                  {/* Table header row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: st.bg, border: "2px solid "+st.color+"44",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontWeight: 900, fontSize: 15, color: T.primary }}>#{t.number}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: T.primary }}>Stolik #{t.number}</div>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                        {t.seats} miejsc · {st.icon} {st.label}
                        {(qrOrders + nfcOrders) > 0 && (
                          <span style={{ marginLeft: 8 }}>
                            {qrOrders > 0 && <span style={{ color: T.accent }}>📷 {qrOrders} QR  </span>}
                            {nfcOrders > 0 && <span style={{ color: "#7c3aed" }}>📡 {nfcOrders} NFC</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* URL display */}
                  <div style={{ background: T.bg, borderRadius: 8, padding: "6px 10px", marginBottom: 10,
                    border: "1px solid "+T.border, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10, color: T.muted, fontFamily: "monospace", flex: 1, wordBreak: "break-all" }}>{url}</span>
                  </div>

                  {/* QR preview thumbnail */}
                  <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
                    {/* Thumbnail QR */}
                    <div style={{ background: T.surface, border: "1.5px solid "+T.border, borderRadius: 10, padding: 6, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <img
                        src={"https://api.qrserver.com/v1/create-qr-code/?size=70x70&data="+encodeURIComponent(url)+"&color=6B3F1E&bgcolor=FFFAF3"}
                        width={70} height={70}
                        alt={"QR #"+t.number}
                        style={{ borderRadius: 6, display: "block" }}
                        loading="lazy"
                      />
                    </div>
                    {/* Per-table QR/NFC toggles */}
                    <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                      {[
                        { key: "qrEnabled",  icon: "📷", label: "QR",  activeCol: "#15803d", activeBg: "#dcfce7", border: "#86efac" },
                        { key: "nfcEnabled", icon: "📡", label: "NFC", activeCol: "#7c3aed", activeBg: "#f3e8ff", border: "#d8b4fe" },
                      ].map(tog => {
                        const isOn = t[tog.key] !== false;
                        const globalOff = settings && settings[tog.key] === false;
                        return (
                          <button key={tog.key}
                            onClick={() => setTables(p => p.map(x => x.id === t.id ? { ...x, [tog.key]: !x[tog.key] } : x))}
                            style={{ flex: 1, padding: "7px 6px", borderRadius: 9, border: "1.5px solid " + (isOn && !globalOff ? tog.border : T.border),
                              background: isOn && !globalOff ? tog.activeBg : T.bg,
                              color: isOn && !globalOff ? tog.activeCol : T.muted,
                              fontWeight: 700, cursor: "pointer", fontSize: 11,
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                              opacity: globalOff ? 0.5 : 1 }}>
                            {tog.icon} {tog.label} {isOn && !globalOff ? "✓" : "✗"}
                            {globalOff && <span style={{ fontSize: 9 }}>(global off)</span>}
                          </button>
                        );
                      })}
                    </div>

                    {/* Action buttons */}
                    <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      <button onClick={() => setQrTable(t)}
                        disabled={t.qrEnabled === false || (settings && settings.qrEnabled === false)}
                        style={{ padding: "8px 6px", background: t.qrEnabled !== false && !(settings && settings.qrEnabled === false) ? "#f0fdf4" : T.bg,
                          color: t.qrEnabled !== false && !(settings && settings.qrEnabled === false) ? "#15803d" : T.muted,
                          border: "1.5px solid #bbf7d0", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 12,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: t.qrEnabled === false ? 0.4 : 1 }}>
                        📷 Pełny QR
                      </button>
                      <button onClick={() => setNfcTable(t)}
                        disabled={t.nfcEnabled === false || (settings && settings.nfcEnabled === false)}
                        style={{ padding: "8px 6px", background: t.nfcEnabled !== false && !(settings && settings.nfcEnabled === false) ? "#faf0ff" : T.bg,
                          color: t.nfcEnabled !== false && !(settings && settings.nfcEnabled === false) ? "#7c3aed" : T.muted,
                          border: "1.5px solid #e9d5ff", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 12,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 4, opacity: t.nfcEnabled === false ? 0.4 : 1 }}>
                        📡 Zapisz NFC
                      </button>
                      <button onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(url); }}
                        style={{ padding: "8px 6px", background: T.bg, color: T.muted, border: "1.5px solid "+T.border, borderRadius: 10, fontWeight: 600, cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                        📋 Kopiuj link
                      </button>
                      <button onClick={() => printTableQR(t, baseUrl)}
                        style={{ padding: "8px 6px", background: T.primary, color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                        🖨 Drukuj
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MAP view */}
      {view === "map" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
          {tables.slice().sort((a,b) => a.number - b.number).map(t => {
            const st = statusOf(t);
            return (
              <div key={t.id} onClick={() => setQrTable(t)}
                style={{
                  borderRadius: 14, padding: "12px 6px", textAlign: "center",
                  background: st.bg, border: "2px solid " + st.color + "44",
                  cursor: "pointer", transition: "all .15s", position: "relative",
                  opacity: t.active ? 1 : 0.5
                }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: "#1a1a2e" }}>#{t.number}</div>
                <div style={{ fontSize: 9, color: "#999", marginTop: 1 }}>{t.seats} miejsc</div>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: st.color, margin: "5px auto 0" }} />
                <div style={{ fontSize: 9, color: st.color, fontWeight: 700, marginTop: 2 }}>{st.icon}</div>
                {t.reservation && (
                  <div style={{ fontSize: 8, color: "#f39c12", marginTop: 1 }}>{t.reservation.time}</div>
                )}
                <div style={{fontSize:8,color:T.muted,marginTop:2,opacity:0.6}}>QR·NFC</div>
              </div>
            );
          })}
        </div>
      )}

      {/* LIST view */}
      {view === "list" && (()=>{
        const sorted = tables.slice().sort((a,b)=>a.number-b.number);
        const indoor  = sorted.filter(t=>t.type!=="outdoor");
        const outdoor = sorted.filter(t=>t.type==="outdoor");
        const renderTable = t => {
          const st = statusOf(t);
          const isBusy = t.manuallyFreed ? false : (t.occupied || todayActive.some(o=>o.table===t.id||o.table===t.number));
          return (
          <div key={t.id} style={{ ...S.card, borderLeft: "4px solid " + st.color, opacity: t.active ? 1 : 0.6 }}>

            {/* ── Row 1: number + status + seats editor ── */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              <div style={{
                width:40, height:40, borderRadius:10, flexShrink:0,
                background:st.bg, border:"2px solid "+st.color+"44",
                display:"flex", alignItems:"center", justifyContent:"center"
              }}>
                <span style={{ fontWeight:900, fontSize:15, color:"#1a1a2e" }}>#{t.number}</span>
              </div>

              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                  <span style={{ fontWeight:700, fontSize:13, color:"#1a1a2e" }}>Stolik #{t.number}</span>
                  <span style={{ background:st.bg, color:st.color, borderRadius:20, padding:"2px 8px", fontSize:10, fontWeight:700 }}>
                    {st.icon} {st.label}
                  </span>
                  {t.type==="outdoor"&&<span style={{background:"#f0fdf4",color:"#15803d",borderRadius:10,padding:"1px 7px",fontSize:10,fontWeight:800}}>🌿 Zewnętrzny</span>}
                  {t.reservation && (
                    <span style={{ fontSize:10, color:"#f39c12", fontWeight:600 }}>
                      🔒 {t.reservation.name} {t.reservation.time}
                    </span>
                  )}
                </div>

                {/* QR / NFC badges */}
                <div style={{ display:"flex", gap:4, marginTop:4 }}>
                  <span style={{ fontSize:10, padding:"1px 6px", borderRadius:10, fontWeight:700,
                    background: t.qrEnabled!==false?"#dcfce7":"#fee2e2",
                    color: t.qrEnabled!==false?"#15803d":"#dc2626" }}>
                    📷 {t.qrEnabled!==false?"QR ✓":"QR ✗"}
                  </span>
                  <span style={{ fontSize:10, padding:"1px 6px", borderRadius:10, fontWeight:700,
                    background: t.nfcEnabled!==false?"#f3e8ff":"#fee2e2",
                    color: t.nfcEnabled!==false?"#7c3aed":"#dc2626" }}>
                    📡 {t.nfcEnabled!==false?"NFC ✓":"NFC ✗"}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Row 2: seats editor ── */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10,
              background:T.bg, borderRadius:10, padding:"7px 10px", border:"1px solid "+T.border }}>
              <span style={{ fontSize:11, color:T.muted, fontWeight:600, flexShrink:0 }}>🪑 Miejsca:</span>
              <button
                onClick={()=>setTables(p=>p.map(x=>x.id===t.id?{...x,seats:Math.max(1,(x.seats||1)-1)}:x))}
                style={{ width:26, height:26, borderRadius:"50%", border:"1.5px solid "+T.border,
                  background:T.surface, fontSize:16, fontWeight:700, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  color:(t.seats||1)<=1?T.border:T.primary, flexShrink:0 }}>
                −
              </button>
              <span style={{ fontWeight:900, fontSize:18, color:T.primary, minWidth:24, textAlign:"center" }}>
                {t.seats||1}
              </span>
              <button
                onClick={()=>setTables(p=>p.map(x=>x.id===t.id?{...x,seats:Math.min(20,(x.seats||1)+1)}:x))}
                style={{ width:26, height:26, borderRadius:"50%", border:"1.5px solid "+T.border,
                  background:T.surface, fontSize:16, fontWeight:700, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  color:(t.seats||1)>=20?T.border:T.primary, flexShrink:0 }}>
                +
              </button>
              <div style={{ display:"flex", gap:3, flexWrap:"wrap", flex:1 }}>
                {Array.from({length:Math.min(t.seats||1,10)}).map((_,i)=>(
                  <span key={i} style={{ fontSize:11 }}>🪑</span>
                ))}
                {(t.seats||1)>10 && (
                  <span style={{ fontSize:10, color:T.muted, fontWeight:700, alignSelf:"center" }}>
                    +{(t.seats||1)-10}
                  </span>
                )}
              </div>
            </div>

            {/* ── Row 3: action buttons ── */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr auto auto", gap:5 }}>
              {isBusy ? (
                <button style={{ background:"#e8f5e9", color:"#27ae60", border:"1.5px solid #27ae60",
                  borderRadius:8, padding:"6px 4px", fontWeight:700, cursor:"pointer", fontSize:11, gridColumn:"span 1" }}
                  onClick={()=>setTables(p=>p.map(x=>x.id===t.id?{...x,occupied:false,manuallyFreed:true}:x))}>
                  ✅ Zwolnij
                </button>
              ) : (
                <button style={{ background:"#fde8ec", color:"#e94560", border:"1.5px solid #e94560",
                  borderRadius:8, padding:"6px 4px", fontWeight:700, cursor:"pointer", fontSize:11 }}
                  onClick={()=>setTables(p=>p.map(x=>x.id===t.id?{...x,occupied:true,manuallyFreed:false}:x))}>
                  🍽 Zajmij
                </button>
              )}
              <button style={{ background:"#e8f0ff", color:"#0984e3", border:"1.5px solid #0984e3",
                borderRadius:8, padding:"6px 4px", fontWeight:700, cursor:"pointer", fontSize:11 }}
                onClick={()=>openResModal(t.id)}>
                {t.reservation?"✏ Rez.":"+ Rez."}
              </button>
              <button style={{ background:"#f0fdf4", color:"#15803d", border:"1.5px solid #15803d",
                borderRadius:8, padding:"6px 4px", fontWeight:700, cursor:"pointer", fontSize:11 }}
                onClick={()=>setQrTable(t)}>
                📷 QR
              </button>
              <button style={{ background:"#faf0ff", color:"#7c3aed", border:"1.5px solid #7c3aed",
                borderRadius:8, padding:"6px 4px", fontWeight:700, cursor:"pointer", fontSize:11 }}
                onClick={()=>setNfcTable(t)}>
                📡 NFC
              </button>
              <button style={{ ...S.actBtn, background:t.active?"#fff3e0":"#e8f5e9", padding:"6px 8px" }}
                onClick={()=>toggleActive(t.id)}>
                {t.active?"⏸":"▶"}
              </button>
              <button style={{ ...S.actBtn, background:"#ffebee", color:"#e94560", padding:"6px 8px" }}
                onClick={()=>deleteTable(t.id)}>
                🗑
              </button>
            </div>
          </div>
          );
        };
        return (<>
          {indoor.length>0&&<>
            <div style={{fontSize:11,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:8,marginTop:4}}>🏠 Wewnętrzne ({indoor.length})</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:8}}>
              {indoor.map(renderTable)}
            </div>
          </>}
          {outdoor.length>0&&<>
            <div style={{fontSize:11,fontWeight:800,color:"#15803d",textTransform:"uppercase",letterSpacing:1,marginBottom:8,marginTop:16,display:"flex",alignItems:"center",gap:8}}>
              <span>🌿 Zewnętrzne / Sezonowe ({outdoor.length})</span>
              {!settings?.outdoorTablesEnabled&&<span style={{background:"#fff3cd",color:"#856404",borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>⚠ Ukryte dla klientów</span>}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {outdoor.map(renderTable)}
            </div>
          </>}
        </>);
      })()}
    </div>
  );
}

/* ══════════════════════ CUSTOMER APP ════════════════════════════════════════ */
/* ════════════════════ PARAGON / RECEIPT ════════════════════════════════════ */
function printReceipt(order, settings) {
  const fmt2 = n => Number(n).toFixed(2);
  const ep2  = item => item.promoPrice != null ? item.promoPrice : item.price;
  const payLabel = order.paymentMethod === "blik" ? "BLIK"
                 : order.paymentMethod === "card" ? "Karta"
                 : order.paymentMethod === "paypal" ? "PayPal"
                 : "Gotówka";
  const now  = new Date();
  const dateStr = now.toLocaleDateString("pl-PL");
  const timeStr = now.toLocaleTimeString("pl-PL", { hour:"2-digit", minute:"2-digit" });
  const name = settings.restaurantName || "Restauracja";
  const orderId = String(order.id).replace(/-/g,"").slice(0,8).toUpperCase();
  const fileName = `paragon_${orderId}_${dateStr.replace(/\./g,"-")}.pdf`;

  // Ustawienia paragonu z konfiguracji
  const rc = settings.receipt || {};
  const companyLine = rc.companyLine || name;
  const addressLine = rc.addressLine || "";
  const nipLine     = rc.nipLine     || "";
  const footerLine  = (rc.footerLine || "Dziekujemy za wizyte!\nSmacznego!").replace(/\n/g, "<br/>");
  const footerNote  = rc.footerNote  || "";
  const confirmLabel = rc.confirmLabel || "Potwierdzenie zamówienia";
  const showLogo    = rc.showLogo !== false;
  const logoSrc     = settings.logo || "";

  const rows = order.cart.map(ci => {
    const addonsRows = (ci.selectedAddons||[]).map(a=>
      `<tr>
        <td style="padding:2px 4px 2px 20px;font-size:10px;color:#888;">+ ${a.name} x${a.qty}</td>
        <td style="padding:2px 4px;font-size:10px;color:#888;text-align:center;white-space:nowrap;"></td>
        <td style="padding:2px 4px;font-size:10px;color:#888;text-align:right;white-space:nowrap;">+${fmt2(a.price)} zl</td>
        <td style="padding:2px 4px;font-size:10px;color:#C8621A;text-align:right;font-weight:700;white-space:nowrap;">+${fmt2(a.price*a.qty)} zl</td>
      </tr>`
    ).join("");
    const linePrice = ci.effectivePrice ?? ep2(ci);
    return `
    <tr>
      <td style="padding:5px 4px;border-bottom:${ci.selectedAddons&&ci.selectedAddons.length?"none":"1px dashed #ddd"};">${ci.name}${ci.note ? `<br/><span style="font-size:10px;color:#888;font-style:italic;">${ci.note}</span>` : ""}</td>
      <td style="padding:5px 4px;border-bottom:${ci.selectedAddons&&ci.selectedAddons.length?"none":"1px dashed #ddd"};text-align:center;white-space:nowrap;">${ci.qty} x</td>
      <td style="padding:5px 4px;border-bottom:${ci.selectedAddons&&ci.selectedAddons.length?"none":"1px dashed #ddd"};text-align:right;white-space:nowrap;">${fmt2(linePrice)} zl</td>
      <td style="padding:5px 4px;border-bottom:${ci.selectedAddons&&ci.selectedAddons.length?"none":"1px dashed #ddd"};text-align:right;font-weight:700;white-space:nowrap;">${fmt2(linePrice*ci.qty)} zl</td>
    </tr>${addonsRows}${ci.selectedAddons&&ci.selectedAddons.length?`<tr><td colspan="4" style="border-bottom:1px dashed #ddd;padding:0;"></td></tr>`:""}`;
  }).join("");

  const html = `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8"/>
  <title>Paragon - ${name}</title>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Courier New', monospace; background:#f5f5f5; color:#222; display:flex; flex-direction:column; align-items:center; padding:20px; }
    .receipt { width:320px; background:#fff; padding:20px 14px; box-shadow:0 2px 12px rgba(0,0,0,0.12); border-radius:8px; }
    .center  { text-align:center; }
    .sep     { border:none; border-top:1px dashed #bbb; margin:10px 0; }
    h1       { font-size:18px; font-weight:900; letter-spacing:1px; margin-bottom:2px; }
    .sub     { font-size:11px; color:#666; margin-bottom:3px; }
    table    { width:100%; border-collapse:collapse; font-size:12px; }
    th       { font-size:10px; color:#888; text-align:left; padding:3px 4px; border-bottom:1px solid #ccc; }
    th:nth-child(2), th:nth-child(3), th:nth-child(4) { text-align:right; }
    .total-row td { font-size:15px; font-weight:900; padding:8px 4px 0; }
    .meta    { font-size:11px; color:#555; display:flex; justify-content:space-between; padding:3px 0; }
    .footer  { font-size:11px; color:#888; text-align:center; margin-top:12px; line-height:1.6; }
    .footer-note { font-size:10px; color:#aaa; text-align:center; margin-top:6px; font-style:italic; }
    .logo-img { width:64px; height:64px; object-fit:cover; border-radius:10px; margin-bottom:8px; }
    .btn-bar { margin-top:22px; display:flex; gap:10px; }
    .btn-dl  { padding:10px 28px;background:#6B3F1E;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer; }
    .btn-cl  { padding:10px 18px;background:#eee;color:#555;border:none;border-radius:8px;font-size:14px;cursor:pointer; }
  </style>
</head>
<body>
  <div class="receipt" id="receipt-content">
    <div class="center">
      ${showLogo && logoSrc ? `<img src="${logoSrc}" class="logo-img" alt="logo"/>` : ""}
      <h1>${companyLine}</h1>
      ${addressLine ? `<div class="sub">${addressLine}</div>` : ""}
      ${nipLine     ? `<div class="sub">${nipLine}</div>`     : ""}
      <div class="sub" style="margin-top:4px;">${confirmLabel}</div>
    </div>
    <hr class="sep"/>
    <div class="meta"><span>Data:</span><span>${dateStr} ${timeStr}</span></div>
    <div class="meta"><span>Stolik:</span><span>#${order.tableNumber || order.table}</span></div>
    <div class="meta"><span>Nr zamówienia:</span><span>${orderId}</span></div>
    <hr class="sep"/>
    <table>
      <thead><tr>
        <th>Pozycja</th><th style="text-align:center;">Szt.</th><th style="text-align:right;">Cena</th><th style="text-align:right;">Suma</th>
      </tr></thead>
      <tbody>${rows}</tbody>
      <tfoot>
        ${order.discount > 0 ? `
        <tr><td colspan="3" style="padding:6px 4px 0;font-size:12px;color:#888;">Rabat (${order.voucherCode})</td>
            <td style="padding:6px 4px 0;text-align:right;color:#e94560;font-size:12px;">-${fmt2(order.discount)} zl</td></tr>` : ""}
        <tr class="total-row">
          <td colspan="3">RAZEM</td>
          <td style="text-align:right;">${fmt2(order.total)} zl</td>
        </tr>
      </tfoot>
    </table>
    <hr class="sep"/>
    <div class="meta"><span>Płatność:</span><span>${payLabel}</span></div>
    ${order.orderNote ? `<hr class="sep"/><div style="font-size:11px;color:#555;">Uwaga: ${order.orderNote}</div>` : ""}
    <hr class="sep"/>
    <div class="footer">${footerLine}</div>
    ${footerNote ? `<div class="footer-note">${footerNote}</div>` : ""}
  </div>
  <div class="btn-bar">
    <button class="btn-dl" id="btn-pdf">&#11015; Pobierz PDF</button>
    <button class="btn-cl" onclick="window.close()">&#10005; Zamknij</button>
  </div>
  <script>
    document.getElementById('btn-pdf').addEventListener('click', function() {
      var btn = this;
      btn.disabled = true;
      btn.textContent = 'Generowanie...';
      var opt = {
        margin:       [8, 8, 8, 8],
        filename:     '${fileName}',
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true },
        jsPDF:        { unit: 'mm', format: [90, 200], orientation: 'portrait' }
      };
      html2pdf().set(opt).from(document.getElementById('receipt-content')).save()
        .then(function() {
          btn.disabled = false;
          btn.textContent = '\\u2713 Pobrano!';
          setTimeout(function() { btn.textContent = '\\u2B07 Pobierz PDF'; }, 2500);
        });
    });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=420,height=680,scrollbars=yes");
  if (w) { w.document.write(html); w.document.close(); }
}

/* ═══════════════ CATEGORY SEARCH WRAPPER ═══════════════════════════════════ */
function CatSearchWrapper({ promoItems, roots, cats, activeMenu, ac, tr, setSelRoot, setSelSub, T, TC }) {
  const [catSearch, setCatSearch] = useState("");

  const visibleRoots = roots.filter(r => activeMenu.some(i => getDescendantIds(cats, r.id).includes(i.catId)));

  // Filtruj kategorie po wyszukiwaniu
  const filteredRoots = catSearch.trim()
    ? visibleRoots.filter(r => r.name.toLowerCase().includes(catSearch.toLowerCase()))
    : visibleRoots;

  // Gdy jest wyszukiwanie, pokaż też produkty pasujące do nazwy
  const searchedItems = catSearch.trim()
    ? activeMenu.filter(i => i.name.toLowerCase().includes(catSearch.toLowerCase()))
    : [];

  return (
    <>
      {/* Pasek wyszukiwania */}
      <div style={{position:"relative",marginBottom:14}}>
        <span style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",fontSize:16,pointerEvents:"none",opacity:0.5}}>🔍</span>
        <input
          type="text"
          value={catSearch}
          onChange={e => setCatSearch(e.target.value)}
          placeholder="Szukaj w menu po nazwie..."
          style={{
            width:"100%",boxSizing:"border-box",
            padding:"11px 36px 11px 40px",
            borderRadius:14,
            border:"2px solid rgba(200,147,10,0.4)",
            background:"rgba(255,255,255,0.92)",
            fontSize:14,fontWeight:500,color:"#3B1F0A",
            outline:"none",
            boxShadow:"0 2px 10px rgba(44,21,9,0.12)",
            transition:"border .15s",
          }}
        />
        {catSearch && (
          <button
            onClick={() => setCatSearch("")}
            style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",
              background:"none",border:"none",cursor:"pointer",fontSize:17,color:"#8B6A4A",padding:"2px 5px"}}>
            ✕
          </button>
        )}
      </div>

      {/* Wyniki wyszukiwania produktów */}
      {catSearch.trim() && (
        <div style={{marginBottom:14}}>
          {searchedItems.length === 0 && filteredRoots.length === 0 ? (
            <div style={{textAlign:"center",padding:"28px 14px",color:"#8B6A4A",fontSize:14}}>
              <div style={{fontSize:36,marginBottom:8}}>🍽</div>
              Brak wyników dla „{catSearch}"
            </div>
          ) : searchedItems.length > 0 && (
            <>
              <div style={{fontSize:11,fontWeight:700,color:"#8B6A4A",textTransform:"uppercase",letterSpacing:0.6,marginBottom:8}}>
                Znalezione dania ({searchedItems.length})
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {searchedItems.map(item => (
                  <div key={item.id}
                    onClick={() => {
                      setSelRoot(item.catId);
                      setSelSub(null);
                      setCatSearch("");
                    }}
                    style={{
                      display:"flex",alignItems:"center",gap:11,
                      background:"rgba(255,255,255,0.92)",borderRadius:13,
                      padding:"10px 13px",cursor:"pointer",
                      border:"1.5px solid rgba(200,147,10,0.25)",
                      boxShadow:"0 1px 6px rgba(44,21,9,0.07)",
                    }}>
                    <div style={{width:44,height:44,borderRadius:10,overflow:"hidden",flexShrink:0,background:"#F5ECD7",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {item.image
                        ? <img src={item.image} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                        : <span style={{fontSize:22}}>{item.emoji}</span>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13,color:"#3B1F0A"}}>{getItemTr(item,tr,"name")}</div>
                      <div style={{fontSize:11,color:"#8B6A4A",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{getItemTr(item,tr,"desc")}</div>
                    </div>
                    <div style={{fontWeight:800,fontSize:14,color:ac,flexShrink:0}}>
                      {Number(item.promoPrice ?? item.price).toFixed(2)} zł
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Promo kafelek — tylko gdy nie ma wyszukiwania lub pasuje */}
      {(!catSearch.trim() || "promocje".includes(catSearch.toLowerCase())) && promoItems.length > 0 && (
        <button onClick={()=>{setSelRoot("promo");setSelSub(null);}}
          style={{width:"100%",display:"flex",alignItems:"center",gap:14,
            background:"linear-gradient(90deg,"+ac+",#ff6b35)",
            border:"none",borderRadius:14,padding:"14px 16px",
            marginBottom:10,cursor:"pointer",boxShadow:"0 4px 12px "+ac+"44",
            textAlign:"left"}}>
          <span style={{fontSize:34,flexShrink:0}}>🔥</span>
          <div style={{flex:1}}>
            <div style={{fontWeight:800,fontSize:15,color:"#fff"}}>{tr.catPromo}</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.85)",marginTop:1}}>{tr.promoCount(promoItems.length)}</div>
          </div>
          <span style={{color:"rgba(255,255,255,0.8)",fontSize:20,fontWeight:900}}>›</span>
        </button>
      )}

      {/* Siatka kategorii — jednolity rozmiar zdjęć */}
      {filteredRoots.length > 0 && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:10}}>
          {filteredRoots.map(r => {
            const count = activeMenu.filter(i => getDescendantIds(cats, r.id).includes(i.catId)).length;
            return (
              <button key={r.id}
                onClick={() => { setSelRoot(r.id); setSelSub(null); }}
                style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-start",
                  gap:0,padding:"0",borderRadius:16,overflow:"hidden",
                  border:"2px solid rgba(200,147,10,0.3)",background:"rgba(255,255,255,0.93)",cursor:"pointer",
                  boxShadow:"0 2px 10px rgba(44,21,9,0.12)",transition:"transform .12s, box-shadow .12s",
                  textAlign:"center"}}>
                {/* Zdjęcie — zawsze 100px wysokości, objectFit:cover wyrównuje */}
                <div style={{
                  width:"100%",height:100,flexShrink:0,
                  background:"#F5ECD7",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  overflow:"hidden",position:"relative",
                  borderRadius:"14px 14px 0 0",
                }}>
                  {r.image
                    ? <img src={r.image} alt={r.name}
                        style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                    : <span style={{fontSize:44}}>{r.emoji}</span>}
                  {r.image && <div style={{position:"absolute",inset:0,background:"linear-gradient(to bottom,transparent 40%,rgba(0,0,0,0.28))"}}/>}
                </div>
                <div style={{padding:"7px 6px 9px",width:"100%",flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#3B1F0A",lineHeight:1.2,wordBreak:"break-word"}}>{r.emoji} {r.name}</div>
                  <div style={{fontSize:10,color:"#8B6A4A",fontWeight:500,marginTop:2}}>{tr.catItemCount(count)}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

/* ════════════════════ CART EDIT MODAL ═══════════════════════════════════════ */
function CartEditModal({ cart, cartComments, orderNote, appliedVoucher, vouchers, usedCodes,
  vouchersEnabled,
  totalPrice, finalPrice, discount, totalQty, totalWait, ac, tr, fmt,
  onIncrease, onDecrease, onRemove, onNoteChange, onOrderNoteChange,
  onApplyVoucher, onClose, onPlaceOrder }) {

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:600,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
      onClick={onClose}>
      <div style={{background:"#fff",borderRadius:"20px 20px 0 0",width:"100%",maxWidth:560,maxHeight:"90vh",display:"flex",flexDirection:"column"}}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{padding:"16px 18px 12px",borderBottom:"1px solid #f0f0f0",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontWeight:800,fontSize:18,color:"#111"}}>{tr.cartTitle}</div>
            <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>{tr.cartItems(totalQty, totalWait)}</div>
          </div>
          <button onClick={onClose}
            style={{background:"#f3f4f6",border:"none",borderRadius:"50%",width:36,height:36,cursor:"pointer",
              fontSize:18,color:"#6b7280",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>
            ✕
          </button>
        </div>

        {/* Scrollable list */}
        <div style={{flex:1,overflowY:"auto",padding:"12px 16px"}}>
          {cart.length === 0 ? (
            <div style={{textAlign:"center",padding:"48px 0",color:"#9ca3af"}}>
              <div style={{fontSize:48}}>🛒</div>
              <div style={{fontWeight:600,marginTop:10}}>{tr.cartEmpty}</div>
            </div>
          ) : cart.map(ci => {
            const key = ci.cartKey || ci.id;
            const price = ci.effectivePrice ?? (ci.promoPrice ?? ci.price);
            return (
              <div key={key} style={{marginBottom:12,background:"#f9fafb",borderRadius:14,padding:"11px 13px",border:"1.5px solid #f0f0f0"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  {/* Emoji / thumb */}
                  <div style={{width:44,height:44,borderRadius:10,overflow:"hidden",background:"#f0f0f0",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>
                    {ci.image ? <img src={ci.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : ci.emoji}
                  </div>
                  {/* Name & addons */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:14,color:"#111",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ci.name}</div>
                    {(ci.selectedAddons||[]).length > 0 && (
                      <div style={{fontSize:11,color:ac,marginTop:2}}>+ {ci.selectedAddons.map(a=>a.name+(a.qty>1?" ×"+a.qty:"")).join(", ")}</div>
                    )}
                    <div style={{fontSize:12,color:ac,fontWeight:700,marginTop:2}}>{fmt(price * ci.qty)} zł</div>
                  </div>
                  {/* Qty controls */}
                  <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                    <button onClick={() => onDecrease(key)}
                      style={{width:30,height:30,borderRadius:8,border:"1.5px solid "+ac,background:"#fff",
                        color:ac,fontWeight:800,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      −
                    </button>
                    <span style={{fontWeight:800,fontSize:15,minWidth:20,textAlign:"center"}}>{ci.qty}</span>
                    <button onClick={() => onIncrease(key)}
                      style={{width:30,height:30,borderRadius:8,border:"1.5px solid "+ac,background:ac,
                        color:"#fff",fontWeight:800,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      +
                    </button>
                    <button onClick={() => onRemove(key)}
                      style={{width:30,height:30,borderRadius:8,border:"1.5px solid #fecaca",background:"#fff",
                        color:"#ef4444",fontWeight:800,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      🗑
                    </button>
                  </div>
                </div>
                {/* Item note */}
                <div style={{marginTop:8,display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:13,color:"#9ca3af"}}>✏</span>
                  <input
                    value={cartComments[key] || ""}
                    onChange={e => onNoteChange(key, e.target.value)}
                    placeholder={tr.cartItemNote}
                    style={{flex:1,padding:"5px 9px",borderRadius:8,border:"1.5px solid "+(cartComments[key]?ac:"#e5e7eb"),
                      fontSize:11,outline:"none",background:"#fff",color:"#111",
                      fontStyle:cartComments[key]?"normal":"italic"}}
                  />
                  {cartComments[key] && (
                    <button onClick={() => onNoteChange(key, "")}
                      style={{background:"none",border:"none",color:"#9ca3af",fontSize:15,cursor:"pointer",padding:0}}>✕</button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Order note */}
          {cart.length > 0 && (
            <div style={{marginTop:4,marginBottom:4,display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:14,flexShrink:0}}>📝</span>
              <input
                value={orderNote}
                onChange={e => onOrderNoteChange(e.target.value)}
                placeholder={tr.cartOrderNote}
                style={{flex:1,padding:"7px 11px",borderRadius:9,border:"1.5px solid "+(orderNote?ac:"#e5e7eb"),
                  fontSize:12,outline:"none",background:"#f9fafb",color:"#111",
                  fontStyle:orderNote?"normal":"italic"}}
              />
              {orderNote && (
                <button onClick={() => onOrderNoteChange("")}
                  style={{background:"none",border:"none",color:"#9ca3af",fontSize:15,cursor:"pointer",padding:0}}>✕</button>
              )}
            </div>
          )}

          {/* Voucher */}
          {cart.length > 0 && vouchersEnabled && (
            <div style={{marginTop:8}}>
              <VoucherBar vouchers={vouchers} usedCodes={usedCodes} cartTotal={totalPrice}
                applied={appliedVoucher} onApply={onApplyVoucher} ac={ac}/>
            </div>
          )}
        </div>

        {/* Footer summary + button */}
        {cart.length > 0 && (
          <div style={{padding:"14px 16px 24px",borderTop:"1px solid #f0f0f0",flexShrink:0,background:"#fff"}}>
            {discount > 0 && (
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#6b7280",marginBottom:4}}>
                <span>{tr.cartSubtotal}</span>
                <span style={{textDecoration:"line-through"}}>{fmt(totalPrice)} zł</span>
              </div>
            )}
            {discount > 0 && (
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#27ae60",marginBottom:4,fontWeight:700}}>
                <span>{tr.cartDiscount(appliedVoucher?.code)}</span>
                <span>−{fmt(discount)} zł</span>
              </div>
            )}
            <div style={{display:"flex",justifyContent:"space-between",fontWeight:800,fontSize:17,color:"#111",marginBottom:12}}>
              <span>{tr.cartToPay}</span>
              <span style={{color:ac}}>{fmt(finalPrice)} zł</span>
            </div>
            <button onClick={onPlaceOrder}
              style={{width:"100%",padding:14,background:ac,color:"#fff",border:"none",borderRadius:14,
                fontWeight:800,fontSize:16,cursor:"pointer",boxShadow:"0 4px 14px rgba(200,98,26,0.35)"}}>
              {tr.cartOrderBtn(fmt(finalPrice))}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════ DAILY SPECIALS MODAL ══════════════════════════════════ */
function DailySpecialsModal({ specials, onClose }) {
  const bp = useBreakpoint();
  const isPhone = bp && bp.xs;
  const accent = specials.accentColor || "#C8621A";
  const accent2 = specials.accentColor2 || "#3B1F0A";
  const bg = specials.bgColor || "#FDF6EC";
  const today = new Date().toLocaleDateString("pl-PL", { weekday:"long", year:"numeric", month:"long", day:"numeric" });

  const HEADER_PRESETS = {
    granite:  "linear-gradient(135deg,#3d3d3d 0%,#1a1a1a 50%,#2e2e2e 100%)",
    slate:    "linear-gradient(135deg,#334155 0%,#0f172a 100%)",
    gold:     "linear-gradient(135deg,#b45309 0%,#78350f 100%)",
    emerald:  "linear-gradient(135deg,#065f46 0%,#022c22 100%)",
    wine:     "linear-gradient(135deg,#881337 0%,#3b0764 100%)",
    midnight: "linear-gradient(135deg,#1e3a5f 0%,#0f172a 100%)",
    rose:     "linear-gradient(135deg,#be185d 0%,#881337 100%)",
  };
  const headerGradient = HEADER_PRESETS[specials.headerPreset] ||
    `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`;

  // Kolor przycisku — pierwszy kolor presetu lub accent
  const btnBg = specials.headerPreset && HEADER_PRESETS[specials.headerPreset]
    ? headerGradient
    : `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`;

  const categoryGroups = {};
  (specials.items || []).forEach(item => {
    const cat = item.category || "Pozostałe";
    if (!categoryGroups[cat]) categoryGroups[cat] = [];
    categoryGroups[cat].push(item);
  });
  const cats = Object.keys(categoryGroups);

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:1000,
      background:"rgba(30,10,0,0.72)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding: isPhone ? 0 : 16,
      animation:"fadeSlide .25s ease",
      backdropFilter:"blur(4px)",
    }}>
      <div style={{
        background: bg,
        borderRadius: isPhone ? 0 : 24,
        width: isPhone ? "100%" : "100%",
        maxWidth: isPhone ? "100vw" : 520,
        maxHeight: isPhone ? "100dvh" : "92vh",
        height: isPhone ? "100dvh" : "auto",
        display:"flex", flexDirection:"column",
        boxShadow:"0 24px 80px rgba(0,0,0,0.45)",
        border: isPhone ? "none" : `2px solid ${accent}`,
        overflow:"hidden",
        animation:"popIn .28s ease",
        position:"relative",
      }}>
        {/* Obrazek tła modalu */}
        {specials.bgImage && (
          <img src={specials.bgImage} alt=""
            style={{
              position:"absolute", inset:0, width:"100%", height:"100%",
              objectFit:"cover", pointerEvents:"none", zIndex:0,
              opacity: specials.bgImageOpacity ?? 0.15,
            }}
            onError={e=>{e.target.style.display="none";}}
          />
        )}

        {/* Header */}
        <div style={{
          position:"relative", overflow:"hidden",
          padding: isPhone ? "22px 18px 16px" : "26px 24px 20px",
          flexShrink:0, zIndex:1,
        }}>
          {/* Obrazek nagłówka */}
          {specials.headerImage && (
            <img src={specials.headerImage} alt=""
              style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",pointerEvents:"none"}}
              onError={e=>{e.target.style.display="none";}}/>
          )}
          {/* Gradient (zawsze, regulowane kryciem gdy jest zdjęcie) */}
          <div style={{
            position:"absolute", inset:0,
            background: headerGradient,
            opacity: specials.headerImage ? (specials.headerImageOverlay ?? 0.55) : 1,
          }}/>
          <button onClick={onClose} style={{
            position:"absolute", top:12, right:12, zIndex:2,
            background:"rgba(255,255,255,0.18)", border:"none",
            color:"#fff", borderRadius:"50%", width:32, height:32,
            cursor:"pointer", fontWeight:700, fontSize:16,
            display:"flex", alignItems:"center", justifyContent:"center",
            backdropFilter:"blur(4px)",
          }}>✕</button>

          <div style={{textAlign:"center", position:"relative", zIndex:1}}>
            <div style={{fontSize: isPhone ? 36 : 44, marginBottom:6, filter:"drop-shadow(0 2px 4px rgba(0,0,0,0.3))"}}>
              {specials.logo ? (
                <img src={specials.logo} alt="logo"
                  style={{
                    width: isPhone ? 64 : 80, height: isPhone ? 64 : 80,
                    borderRadius: "50%", objectFit:"cover",
                    border:"3px solid rgba(255,255,255,0.4)",
                    boxShadow:"0 4px 16px rgba(0,0,0,0.3)",
                    display:"block", margin:"0 auto",
                  }}
                  onError={e=>{e.target.style.display="none"; e.target.nextSibling.style.display="block";}}
                />
              ) : null}
              <span style={{display: specials.logo ? "none" : "block"}}>⭐</span>
            </div>
            <div style={{
              fontFamily:"'Playfair Display',serif",
              fontWeight:900, fontSize: isPhone ? 22 : 26,
              color:"#fff", lineHeight:1.2, marginBottom:4,
              textShadow:"0 2px 8px rgba(0,0,0,0.35)",
            }}>{specials.title || "🌟 Polecamy dzisiaj"}</div>
            {specials.subtitle && (
              <div style={{color:"rgba(255,255,255,0.82)", fontSize: isPhone ? 12 : 14, fontWeight:500}}>
                {specials.subtitle}
              </div>
            )}
            {specials.showDate !== false && (
              <div style={{
                marginTop:8, display:"inline-block",
                background:"rgba(255,255,255,0.18)", borderRadius:20,
                padding:"3px 12px", fontSize:11, color:"rgba(255,255,255,0.9)",
                fontWeight:600, textTransform:"capitalize",
              }}>{today}</div>
            )}
          </div>
        </div>

        {/* Items */}
        <div style={{flex:1, overflowY:"auto", padding: isPhone ? "16px 14px" : "20px 22px", position:"relative", zIndex:1}}>
          {(specials.items||[]).length === 0 ? (
            <div style={{textAlign:"center", padding:"40px 20px", color:"#8B6A4A"}}>
              <div style={{fontSize:42, marginBottom:12}}>🍽</div>
              <div style={{fontWeight:700, fontSize:16}}>Brak pozycji</div>
              <div style={{fontSize:13, marginTop:6, opacity:0.7}}>Administrator jeszcze nie dodał dzisiejszych poleceń.</div>
            </div>
          ) : (
            <>
              {cats.length > 0 && cats.map(cat => (
                <div key={cat} style={{marginBottom:16}}>
                  {cats.length > 1 && (
                    <div style={{
                      fontWeight:800, fontSize:12, color:accent,
                      textTransform:"uppercase", letterSpacing:1.2,
                      marginBottom:8, paddingBottom:5,
                      borderBottom:`2px solid ${accent}22`,
                    }}>{cat}</div>
                  )}
                  <div style={{display:"grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap:12}}>
                    {categoryGroups[cat].map((item, idx) => (
                      item.image ? (
                        /* ── Karta z dużym zdjęciem ── */
                        <div key={idx} style={{
                          background:"rgba(255,255,255,0.88)", borderRadius:16,
                          border:`1.5px solid ${accent}33`,
                          overflow:"hidden",
                          boxShadow:"0 4px 16px rgba(107,63,30,0.12)",
                          animation:`fadeSlide .2s ease ${idx*0.05}s both`,
                          display:"flex", flexDirection:"column",
                        }}>
                          {/* Zdjęcie */}
                          <div style={{position:"relative", width:"100%", paddingTop:"62%", flexShrink:0}}>
                            <img src={item.image} alt={item.name} style={{
                              position:"absolute", inset:0,
                              width:"100%", height:"100%",
                              objectFit:"cover",
                            }} onError={e=>{e.target.parentElement.style.display="none";}}/>
                            {item.price && (
                              <div style={{
                                position:"absolute", top:8, right:8,
                                background:accent, color:"#fff",
                                borderRadius:10, padding:"4px 10px",
                                fontWeight:800, fontSize:13,
                                boxShadow:`0 2px 8px ${accent}66`,
                              }}>{item.price}</div>
                            )}
                          </div>
                          {/* Treść */}
                          <div style={{padding:"11px 13px 13px", flex:1}}>
                            {item.emoji && (
                              <span style={{fontSize:16, marginRight:5}}>{item.emoji}</span>
                            )}
                            <span style={{fontWeight:800, fontSize:14, color:"#3B1F0A", lineHeight:1.3}}>
                              {item.name}
                            </span>
                            {item.desc && (
                              <div style={{fontSize:11, color:"#8B6A4A", marginTop:5, lineHeight:1.55}}>
                                {item.desc}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        /* ── Karta bez zdjęcia — układ poziomy z emoji ── */
                        <div key={idx} style={{
                          background:"rgba(255,255,255,0.75)", borderRadius:14,
                          border:`1.5px solid ${accent}33`,
                          padding: isPhone ? "11px 12px" : "13px 14px",
                          display:"flex", alignItems:"center", gap:12,
                          boxShadow:"0 2px 8px rgba(107,63,30,0.08)",
                          animation:`fadeSlide .2s ease ${idx*0.05}s both`,
                        }}>
                          {item.emoji ? (
                            <div style={{
                              fontSize: isPhone ? 28 : 32, flexShrink:0,
                              width: isPhone ? 52 : 60, height: isPhone ? 52 : 60,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              background:`${accent}18`, borderRadius:12,
                            }}>{item.emoji}</div>
                          ) : null}
                          <div style={{flex:1, minWidth:0}}>
                            <div style={{fontWeight:800, fontSize: isPhone ? 14 : 15, color:"#3B1F0A", lineHeight:1.2}}>
                              {item.name}
                            </div>
                            {item.desc && (
                              <div style={{fontSize:11, color:"#8B6A4A", marginTop:3, lineHeight:1.5}}>{item.desc}</div>
                            )}
                          </div>
                          {item.price && (
                            <div style={{
                              flexShrink:0, background:accent, color:"#fff",
                              borderRadius:10, padding:"4px 10px",
                              fontWeight:800, fontSize: isPhone ? 13 : 14,
                              boxShadow:`0 2px 6px ${accent}55`,
                            }}>{item.price}</div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: isPhone ? "12px 14px 20px" : "14px 22px 18px",
          flexShrink:0, borderTop:`1.5px solid ${accent}22`,
          background:"rgba(255,255,255,0.5)",
          position:"relative", zIndex:1,
        }}>
          <button onClick={onClose} style={{
            width:"100%", padding: isPhone ? 13 : 14,
            background: btnBg,
            color:"#fff", border:"none", borderRadius:14,
            fontWeight:800, fontSize: isPhone ? 15 : 16,
            cursor:"pointer",
            boxShadow:`0 4px 16px rgba(0,0,0,0.3)`,
            letterSpacing:0.3,
          }}>
            🍽 Przejdź do wyboru stolika
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ PRIVACY POLICY MODAL ══════════════════════════════════ */
function PrivacyModal({ policy, ac, onAccept, onDecline }) {
  const [scrolled, setScrolled] = useState(false);
  const bodyRef = useRef(null);
  const tr = useTr();

  const handleScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setScrolled(true);
  };

  useEffect(() => {
    // If content is short enough, allow accept immediately
    const el = bodyRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 30) setScrolled(true);
  }, []);

  const title   = policy.title   || "Polityka prywatności";
  const content = policy.content || "";
  const acceptLabel  = policy.acceptLabel  || "Akceptuję";
  const declineLabel = policy.declineLabel || "Nie akceptuję";

  return (
    <div style={{
      position:"fixed",inset:0,zIndex:800,
      background:"rgba(0,0,0,0.65)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",
    }}>
      <div style={{
        background:"#fff",
        borderRadius:"22px 22px 0 0",
        width:"100%",maxWidth:560,
        maxHeight:"90vh",
        display:"flex",flexDirection:"column",
        boxShadow:"0 -8px 40px rgba(0,0,0,0.3)",
        animation:"slideInUp .3s cubic-bezier(.22,1,.36,1)",
      }}>
        <style>{`@keyframes slideInUp{from{transform:translateY(60px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{
          padding:"20px 22px 14px",
          borderBottom:"1px solid #f0f0f0",
          flexShrink:0,
        }}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:40,height:40,borderRadius:12,background:ac+"22",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>🔒</div>
            <div>
              <div style={{fontWeight:800,fontSize:17,color:"#111"}}>{title}</div>
              <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{tr.privacyReadFirst}</div>
            </div>
          </div>
        </div>

        {/* Content — scrollable */}
        <div
          ref={bodyRef}
          onScroll={handleScroll}
          style={{
            flex:1,overflowY:"auto",
            padding:"16px 22px",
            fontSize:13,color:"#374151",lineHeight:1.75,
            whiteSpace:"pre-wrap",
          }}>
          {content}
          {!scrolled && (
            <div style={{textAlign:"center",color:"#9ca3af",fontSize:11,marginTop:16,padding:"8px",background:"#f9fafb",borderRadius:8}}>
              {tr.privacyScrollHint}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{padding:"14px 22px 32px",borderTop:"1px solid #f0f0f0",flexShrink:0,display:"flex",flexDirection:"column",gap:10}}>
          <button
            onClick={onAccept}
            disabled={!scrolled}
            style={{
              width:"100%",padding:"14px",
              background:scrolled ? ac : "#d1d5db",
              color:"#fff",border:"none",borderRadius:14,
              fontWeight:800,fontSize:15,cursor:scrolled?"pointer":"not-allowed",
              transition:"background .2s",
              boxShadow:scrolled?"0 4px 14px rgba(0,0,0,0.2)":"none",
            }}>
            ✅ {acceptLabel}
          </button>
          <button
            onClick={onDecline}
            style={{
              width:"100%",padding:"11px",
              background:"#f9fafb",color:"#6b7280",
              border:"1.5px solid #e5e7eb",borderRadius:14,
              fontWeight:600,fontSize:13,cursor:"pointer",
            }}>
            {declineLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function PrivacyDeclined({ policy, ac, onBack }) {
  const tr = useTr();
  const msg = policy.declineMessage || "Zamówienie online nie jest możliwe. Zapraszamy do złożenia zamówienia u kelnera lub przy kasie.";
  return (
    <div style={{
      minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",
      padding:24,background:"linear-gradient(160deg,#3B1F0A 0%,#6B3F1E 45%,#C8621A 100%)",
    }}>
      <div style={{
        background:"#fff",borderRadius:24,padding:32,maxWidth:380,width:"100%",
        textAlign:"center",boxShadow:"0 20px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{fontSize:56,marginBottom:16}}>🔒</div>
        <div style={{fontWeight:800,fontSize:20,color:"#111",marginBottom:10}}>
          {tr.privacyNoConsent}
        </div>
        <div style={{fontSize:14,color:"#6b7280",lineHeight:1.7,marginBottom:24,whiteSpace:"pre-wrap"}}>
          {msg}
        </div>
        <div style={{background:"#f9fafb",borderRadius:14,padding:"16px 18px",marginBottom:20,border:"1.5px solid #e5e7eb"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#374151",marginBottom:8}}>{tr.privacyHowTitle}</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {[
              {icon:"👨‍💼", text:tr.privacyOpt1},
              {icon:"🏪",  text:tr.privacyOpt2},
              {icon:"📞",  text:tr.privacyOpt3},
            ].map(({icon,text}) => (
              <div key={text} style={{display:"flex",alignItems:"center",gap:10,fontSize:13,color:"#374151"}}>
                <span style={{fontSize:18,flexShrink:0}}>{icon}</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={onBack}
          style={{
            width:"100%",padding:"13px",background:ac,color:"#fff",
            border:"none",borderRadius:14,fontWeight:700,fontSize:14,cursor:"pointer",
          }}>
          {tr.privacyGoBack}
        </button>
      </div>
    </div>
  );
}

function CustomerApp({ menu, cats, settings, tables, setTables, orders, campaigns, vouchers, usedCodes, setUsedCodes, onPlaceOrder, staffCalls, setStaffCalls, clients, setClients, onAdminTap, onlineMenu, onlineCats, setOnlineOrders, onOnlineShop }) {
  const bp = useBreakpoint();
  const [table,        setTable]        = useState(null);
  const [cart,         setCart]         = useState([]);
  const [cartComments, setCartComments] = useState({});
  const [orderNote,    setOrderNote]    = useState("");
  // eslint-disable-next-line no-unused-vars
  const [showNoteBar,  setShowNoteBar]  = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [commentOpen,  setCommentOpen]  = useState(null);
  const [selRoot,      setSelRoot]      = useState("all");
  const [selSub,       setSelSub]       = useState(null);
  const [success,      setSuccess]      = useState(null);
  const [zoom,         setZoom]         = useState(null);
  const [payStep,      setPayStep]      = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [nfcScanning,  setNfcScanning]  = useState(false);
  const [scanMsg,      setScanMsg]      = useState("");
  const [lang,         setLang]         = useState("pl");
  const [guestCount,   setGuestCount]   = useState(1);
  const [onlineMode,   setOnlineMode]   = useState(null); // null | "pickup" | "delivery"
  const [logoTapsCust, setLogoTapsCust] = useState(0);
  const logoTapTimerCust = useRef(null);

  const ac = settings.accentColor;
  const tr = LANG[lang] || LANG.pl;

  const activeCampaigns = useMemo(() => (campaigns||[]).filter(cp => {
    if (!cp.active) return false;
    const now = todayStr();
    return (!cp.dateFrom || cp.dateFrom <= now) && (!cp.dateTo || cp.dateTo >= now);
  }), [campaigns]);
  const [shownModal,        setShownModal]        = useState(null);
  const [appliedVoucher,    setAppliedVoucher]    = useState(null);
  const [campaignDismissed, setCampaignDismissed] = useState([]);

  useEffect(() => {
    const cp = activeCampaigns.find(cp => cp.showModal && !campaignDismissed.includes(cp.id));
    if (cp) setShownModal(cp.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaigns.length, campaignDismissed]); // Zależy od aktywnych kampanii i zwolnionych

  const modalCampaign = shownModal ? activeCampaigns.find(cp => cp.id === shownModal) : null;
  const modalVoucher  = modalCampaign && modalCampaign.voucherCode
    ? (vouchers||[]).find(v => v.code === modalCampaign.voucherCode) : null;

  const activeMenu = useMemo(() => menu.filter(i => i.active), [menu]);
  const roots      = useMemo(() => getRoots(cats), [cats]);
  const promoItems = useMemo(() => activeMenu.filter(i => i.promoPrice), [activeMenu]);
  const subCats    = useMemo(() =>
    selRoot !== "all" && selRoot !== "promo" ? getChildren(cats, selRoot) : []
  , [selRoot, cats]);
  const filtered   = useMemo(() => {
    if (selRoot === "all")   return activeMenu;
    if (selRoot === "promo") return promoItems;
    const ids = selSub ? [selSub] : getDescendantIds(cats, selRoot);
    return activeMenu.filter(i => ids.includes(i.catId));
  }, [selRoot, selSub, activeMenu, promoItems, cats]);

  const [addonModal, setAddonModal] = useState(null); // {item, selectedAddons: {id: qty}}
  const [cartModalOpen, setCartModalOpen] = useState(false);
  // null = not yet shown, "accepted" = OK, "declined" = declined
  const [privacyStatus, setPrivacyStatus] = useState(null);
  // Daily specials modal — shown once per session
  const [specialsDismissed, setSpecialsDismissed] = useState(false);

  const addToCart = item => {
    // Jesli produkt ma dodatki — otworz modal
    if (item.addons && item.addons.length > 0) {
      setAddonModal({ item, selectedAddons: {} });
      return;
    }
    setCart(p => {
      const ex = p.find(c => c.id === item.id);
      return ex ? p.map(c => c.id === item.id ? {...c, qty: c.qty + 1} : c) : [...p, {...item, qty: 1, note: ""}];
    });
  };

  const addToCartWithAddons = (item, selectedAddons) => {
    // Oblicz cene z dodatkami
    const addonsArr = Object.entries(selectedAddons)
      .filter(([,qty])=>qty>0)
      .map(([id,qty])=>{
        const addon = item.addons.find(a=>a.id===id);
        return addon ? {...addon, qty} : null;
      }).filter(Boolean);
    const addonsPrice = addonsArr.reduce((s,a)=>s+a.price*a.qty, 0);
    const cartKey = item.id + (addonsArr.length ? "_" + addonsArr.map(a=>a.id+"x"+a.qty).join("_") : "");
    const cartItem = {
      ...item,
      qty: 1,
      note: "",
      cartKey,
      selectedAddons: addonsArr,
      addonsPrice,
      // Cena bazowa + dodatki
      effectivePrice: (item.promoPrice ?? item.price) + addonsPrice,
    };
    setCart(p => {
      const ex = p.find(c => c.cartKey === cartKey);
      return ex ? p.map(c => c.cartKey === cartKey ? {...c, qty: c.qty+1} : c) : [...p, cartItem];
    });
    setAddonModal(null);
  };
  const remFromCart = id => {
    setCart(p => {
      const ex = p.find(c => (c.cartKey||c.id) === id);
      if (ex && ex.qty === 1) setCartComments(prev => { const n = {...prev}; delete n[id]; return n; });
      return ex && ex.qty === 1 ? p.filter(c => (c.cartKey||c.id) !== id) : p.map(c => (c.cartKey||c.id) === id ? {...c, qty: c.qty - 1} : c);
    });
  };
  const setItemNote = (id, note) => setCartComments(p => ({...p, [id]: note}));

  const totalPrice = cart.reduce((s, c) => s + (c.effectivePrice ?? (c.promoPrice ?? c.price)) * c.qty, 0);
  const discount   = calcDiscount(appliedVoucher, totalPrice);
  const finalPrice = Math.max(0, totalPrice - discount);
  const totalWait  = cart.length ? Math.max(...cart.map(c => c.wait)) : 0;
  const totalQty   = cart.reduce((s, c) => s + c.qty, 0);

  const buildOrder = (extra) => {
    const tbl = (tables||[]).find(t => t.id === table);
    return {
      id: genId(),
      cart: cart.map(ci => ({
        id: ci.id, name: ci.name, emoji: ci.emoji,
        price: ci.price, promoPrice: ci.promoPrice,
        effectivePrice: ci.effectivePrice,
        selectedAddons: ci.selectedAddons||[],
        addonsPrice: ci.addonsPrice||0,
        cartKey: ci.cartKey||ci.id,
        qty: ci.qty, wait: ci.wait, catId: ci.catId,
        note: cartComments[ci.cartKey||ci.id] || cartComments[ci.id] || "",
      })),
      total: finalPrice, totalBeforeDiscount: totalPrice,
      discount, voucherCode: appliedVoucher ? appliedVoucher.code : null,
      orderNote: orderNote.trim(),
      wait: totalWait, table, date: todayStr(),
      time: new Date().toLocaleTimeString("pl-PL", {hour: "2-digit", minute: "2-digit"}),
      tableNumber: tbl ? tbl.number : table,
      tableType: tbl ? (tbl.type || "indoor") : "indoor",
      guests: guestCount,
      scanSource: sessionStorage.getItem("smakidomu_scanSource") || null,
      ...(extra || {}),
    };
  };

  const placeOrder = () => {
    const o = buildOrder();
    setPendingOrder(o);
    setPayStep("picker");
  };

  const confirmOrder = o => {
    if (appliedVoucher && setUsedCodes) {
      const code = appliedVoucher.code;
      setUsedCodes(p => [...p, code]);
      apiFetch("/api/used-codes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, usedAt: new Date().toISOString() }),
      }).catch(() => {});
    }
    // Oznacz stolik jako zajęty — widoczne na wszystkich urządzeniach przez Firebase
    setTables(p => p.map(t => t.id === o.table ? { ...t, occupied: true, manuallyFreed: false } : t));
    onPlaceOrder(o);
    setSuccess(o);
    setCart([]);
    setAppliedVoucher(null);
    setPendingOrder(null);
    setPayStep(null);
    setGuestCount(1);
    sessionStorage.removeItem("smakidomu_scanSource");
    setCartComments({});
    setOrderNote("");
    setShowNoteBar(false);
    setCommentOpen(null);
  };

  const tablesRef  = useRef(tables);
  const settingsRef = useRef(settings);
  const trRef       = useRef(tr);
  useEffect(() => { tablesRef.current  = tables;   }, [tables]);
  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { trRef.current       = tr;       }, [tr]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tid = params.get("table");
    if (!tid) return;
    // Używamy ref-ów żeby nie zależeć od stanu przy montowaniu,
    // ale obsługujemy też opóźnione załadowanie danych z Firebase
    const tryAssign = (attempt = 0) => {
      const currentTables   = tablesRef.current;
      const currentSettings = settingsRef.current;
      const currentTr       = trRef.current;
      const found = (currentTables||[]).find(t => String(t.id) === String(tid) && t.active);
      if (!found) {
        // Poczekaj chwilę na załadowanie danych z Firebase (max 3s)
        if (attempt < 6) { setTimeout(() => tryAssign(attempt + 1), 500); return; }
        return;
      }
      if (currentSettings && currentSettings.qrEnabled === false) {
        setScanMsg(currentTr.scanQrDisabled); setTimeout(() => setScanMsg(""), 3000); return;
      }
      if (found.qrEnabled === false) {
        setScanMsg(currentTr.scanQrTableDisabled); setTimeout(() => setScanMsg(""), 3000); return;
      }
      setTable(found.id);
      setTables(p => p.map(t => t.id === found.id
        ? {...t, occupied: true, manuallyFreed: false}
        : t));
      sessionStorage.setItem("smakidomu_scanSource", "qr");
      window.history.replaceState({}, "", window.location.pathname);
      setScanMsg(currentTr.scanQrOk(found.number));
      setTimeout(() => setScanMsg(""), 3000);
    };
    tryAssign();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Celowo pusta tablica — efekt tylko przy montowaniu (odczyt URL); dane pobierane przez ref

  const ndefRef = useRef(null);

  const startNFC = async () => {
    if (!("NDEFReader" in window)) { setScanMsg(tr.scanNfcNA); setTimeout(() => setScanMsg(""), 3000); return; }
    // Wyczyść poprzedni listener jeśli był aktywny
    if (ndefRef.current) {
      try { ndefRef.current.removeEventListener("reading", ndefRef.current._handler); } catch(e) {}
      ndefRef.current = null;
    }
    try {
      setNfcScanning(true);
      setScanMsg(tr.scanNfcHint);
      const ndef = new window.NDEFReader();
      const handler = ({message: msg}) => {
        for (const record of msg.records) {
          if (record.recordType === "url") {
            const url = new TextDecoder().decode(record.data);
            const tid = new URLSearchParams((url.split("?")[1])||"").get("table");
            if (!tid) continue;
            const found = (tablesRef.current||[]).find(t => String(t.id) === String(tid) && t.active);
            if (!found) continue;
            if (settingsRef.current && settingsRef.current.nfcEnabled === false) { setScanMsg(trRef.current.scanNfcDisabled); setNfcScanning(false); return; }
            if (found.nfcEnabled === false) { setScanMsg(trRef.current.scanNfcTableDisabled); setNfcScanning(false); return; }
            setTable(found.id);
            setTables(p => p.map(t => t.id === found.id
              ? {...t, occupied: true, manuallyFreed: false}
              : t));
            sessionStorage.setItem("smakidomu_scanSource", "nfc");
            setScanMsg(trRef.current.scanNfcOk(found.number));
            setNfcScanning(false);
          }
        }
      };
      ndef._handler = handler;
      ndefRef.current = ndef;
      await ndef.scan();
      ndef.addEventListener("reading", handler);
    } catch(e) {
      setScanMsg(tr.scanNfcError(e.message || "nieznany"));
      setNfcScanning(false);
      setTimeout(() => setScanMsg(""), 3000);
    }
  };

  // Sprzątamy NFC listener przy odmontowaniu komponentu
  useEffect(() => {
    return () => {
      if (ndefRef.current) {
        try { ndefRef.current.removeEventListener("reading", ndefRef.current._handler); } catch(e) {}
        ndefRef.current = null;
      }
    };
  }, []);

  return (
    <LangContext.Provider value={{ lang, setLang, customTranslations: settings.customTranslations || {} }}>
    <div>

      {/* MODAL EDYCJI KOSZYKA */}
      {cartModalOpen && (
        <CartEditModal
          cart={cart}
          cartComments={cartComments}
          orderNote={orderNote}
          appliedVoucher={appliedVoucher}
          vouchers={vouchers||[]}
          usedCodes={usedCodes||[]}
          vouchersEnabled={settings.vouchersEnabled !== false}
          totalPrice={totalPrice}
          finalPrice={finalPrice}
          discount={discount}
          totalQty={totalQty}
          totalWait={totalWait}
          ac={ac}
          tr={tr}
          fmt={fmt}
          onIncrease={id => {
            const item = cart.find(c => (c.cartKey||c.id) === id);
            if (item) setCart(p => p.map(c => (c.cartKey||c.id) === id ? {...c, qty: c.qty+1} : c));
          }}
          onDecrease={remFromCart}
          onRemove={id => {
            setCartComments(prev => { const n={...prev}; delete n[id]; return n; });
            setCart(p => p.filter(c => (c.cartKey||c.id) !== id));
          }}
          onNoteChange={setItemNote}
          onOrderNoteChange={setOrderNote}
          onApplyVoucher={v => setAppliedVoucher(v)}
          onClose={() => setCartModalOpen(false)}
          onPlaceOrder={() => { setCartModalOpen(false); placeOrder(); }}
        />
      )}

      {/* MODAL WYBORU DODATKOW */}
      {addonModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:600,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:"20px 20px 0 0",width:"100%",maxWidth:520,maxHeight:"85vh",overflowY:"auto",padding:"20px 20px 32px"}}>
            {/* Header */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
              <div>
                <div style={{fontWeight:800,fontSize:17,color:"#111"}}>
                  {addonModal.item.emoji} {addonModal.item.name}
                </div>
                <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>
                  Cena bazowa: {Number(addonModal.item.promoPrice??addonModal.item.price).toFixed(2)} zł
                </div>
              </div>
              <button onClick={()=>setAddonModal(null)} style={{background:"#f3f4f6",border:"none",borderRadius:"50%",width:34,height:34,cursor:"pointer",fontSize:18,color:"#6b7280",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>

            <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:10}}>Wybierz dodatki:</div>

            {addonModal.item.addons.map(addon=>{
              const qty = addonModal.selectedAddons[addon.id]||0;
              return (
                <div key={addon.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderRadius:12,border:"1.5px solid "+(qty>0?ac:"#e5e7eb"),background:qty>0?"#fff8f0":"#f9fafb",marginBottom:8,transition:"all .15s"}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:14,color:"#111"}}>{addon.name}</div>
                    <div style={{fontSize:12,color:qty>0?ac:"#6b7280",fontWeight:qty>0?700:400}}>
                      {addon.price===0?"Gratis":"+"+Number(addon.price).toFixed(2)+" zł"}
                      {addon.max>1&&<span style={{color:"#9ca3af",marginLeft:6}}>max {addon.max} szt.</span>}
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    {addon.max===1?(
                      // Toggle — max 1 szt.
                      <button onClick={()=>setAddonModal(p=>({...p,selectedAddons:{...p.selectedAddons,[addon.id]:qty?0:1}}))}
                        style={{width:32,height:32,borderRadius:8,border:"2px solid "+(qty?ac:"#d1d5db"),background:qty?ac:"#fff",cursor:"pointer",color:qty?"#fff":"#6b7280",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
                        {qty?"✓":""}
                      </button>
                    ):(
                      // Licznik — max > 1
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <button onClick={()=>setAddonModal(p=>({...p,selectedAddons:{...p.selectedAddons,[addon.id]:Math.max(0,qty-1)}}))}
                          style={{width:30,height:30,borderRadius:7,border:"1.5px solid #d1d5db",background:"#fff",cursor:"pointer",fontWeight:700,fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                        <span style={{fontWeight:700,fontSize:15,minWidth:20,textAlign:"center",color:qty>0?ac:"#111"}}>{qty}</span>
                        <button onClick={()=>setAddonModal(p=>({...p,selectedAddons:{...p.selectedAddons,[addon.id]:Math.min(addon.max,qty+1)}}))}
                          style={{width:30,height:30,borderRadius:7,border:"1.5px solid "+ac,background:ac,cursor:"pointer",fontWeight:700,fontSize:16,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Podsumowanie i przycisk */}
            <div style={{marginTop:16,padding:"12px 14px",background:"#f9fafb",borderRadius:12,border:"1px solid #e5e7eb",marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#6b7280",marginBottom:4}}>
                <span>Cena bazowa:</span>
                <span>{Number(addonModal.item.promoPrice??addonModal.item.price).toFixed(2)} zł</span>
              </div>
              {Object.entries(addonModal.selectedAddons).filter(([,q])=>q>0).map(([id,q])=>{
                const a=addonModal.item.addons.find(x=>x.id===id);
                return a&&<div key={id} style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#374151",marginBottom:2}}>
                  <span>+ {a.name} ×{q}</span>
                  <span style={{color:ac,fontWeight:600}}>+{(a.price*q).toFixed(2)} zł</span>
                </div>;
              })}
              <div style={{display:"flex",justifyContent:"space-between",fontWeight:800,fontSize:15,color:"#111",marginTop:8,borderTop:"1px solid #e5e7eb",paddingTop:8}}>
                <span>Razem:</span>
                <span style={{color:ac}}>
                  {(Number(addonModal.item.promoPrice??addonModal.item.price)+Object.entries(addonModal.selectedAddons).reduce((s,[id,q])=>{const a=addonModal.item.addons.find(x=>x.id===id);return s+(a?a.price*q:0);},0)).toFixed(2)} zł
                </span>
              </div>
            </div>
            <button onClick={()=>addToCartWithAddons(addonModal.item, addonModal.selectedAddons)}
              style={{width:"100%",padding:"14px",background:ac,color:"#fff",border:"none",borderRadius:12,fontWeight:800,fontSize:16,cursor:"pointer",boxShadow:"0 4px 14px rgba(200,98,26,0.35)"}}>
              Dodaj do koszyka
            </button>
          </div>
        </div>
      )}
      {/* sklep online obsługiwany przez view=online w App */}

      {/* ── DAILY SPECIALS MODAL ── */}
      {!table && !onlineMode && (() => {
        const ds = settings.dailySpecials;
        if (!ds || !ds.enabled) return null;
        if (specialsDismissed) return null;
        if (ds.autoShowOnEntry === false) return null;
        // Show only if privacy is either disabled or already accepted
        const pp = settings.privacyPolicy;
        const ppEnabled = pp && pp.enabled;
        if (ppEnabled && privacyStatus !== "accepted") return null;
        return (
          <DailySpecialsModal
            specials={{...ds, logo: settings.logo || null}}
            onClose={() => setSpecialsDismissed(true)}
          />
        );
      })()}

      {/* ── PRIVACY POLICY ── */}
      {!table && !onlineMode && (() => {
        const pp = settings.privacyPolicy;
        const ppEnabled = pp && pp.enabled;
        // If disabled — skip entirely
        if (!ppEnabled) return null;
        // Declined
        if (privacyStatus === "declined") {
          return (
            <PrivacyDeclined
              policy={pp}
              ac={ac}
              onBack={() => setPrivacyStatus(null)}
            />
          );
        }
        // Not yet answered — show modal on top of table selector
        if (privacyStatus !== "accepted") {
          return (
            <PrivacyModal
              policy={pp}
              ac={ac}
              onAccept={() => setPrivacyStatus("accepted")}
              onDecline={() => setPrivacyStatus("declined")}
            />
          );
        }
        return null;
      })()}

      {/* ── TABLE SELECTOR ── */}
      {!table && !onlineMode && (
        <div>
          {scanMsg && (
            <div style={{position:"fixed",top:60,left:"50%",transform:"translateX(-50%)",zIndex:300,
              background:T.primary,color:"#fff",borderRadius:12,padding:"10px 20px",
              fontSize:13,fontWeight:700,boxShadow:"0 4px 16px rgba(59,31,10,0.3)",whiteSpace:"nowrap"}}>
              {scanMsg}
            </div>
          )}
          <TableSelector settings={settings} tables={tables} orders={orders} onSelect={(id,guests) => { setTable(id); setGuestCount(guests||1); }} onNFC={startNFC} nfcScanning={nfcScanning}
            onCallStaff={call => setStaffCalls && setStaffCalls(p => [...p, { id: genId(), ...call, dismissed: false }])}
            onOnlineOrder={mode=>setOnlineMode(mode)}
            onOnlineShop={onOnlineShop}
            onAdminTap={onAdminTap}/>
        </div>
      )}

      {/* ── PAYMENT PICKER ── */}
      {!!(payStep === "picker" && pendingOrder) && (
        <PaymentPicker
          order={pendingOrder} ac={ac} settings={settings}
          onBlik={() => setPayStep("blik")}
          onCash={method => confirmOrder({...pendingOrder, paymentMethod: method})}
          onBack={() => { setPayStep(null); setPendingOrder(null); }}
        />
      )}

      {/* ── BLIK SCREEN ── */}
      {!!(payStep === "blik" && pendingOrder) && (
        <BlikScreen
          order={pendingOrder} ac={ac}
          onSuccess={o => confirmOrder(o)}
          onBack={() => setPayStep("picker")}
        />
      )}

      {/* ── SUCCESS SCREEN ── */}
      {!!success && (
        <div style={{minHeight:"calc(100vh - 52px)",display:"flex",alignItems:"center",justifyContent:"center",background:T.bg,padding:20}}>
          <div style={{background:T.surface,borderRadius:24,padding:26,textAlign:"center",boxShadow:T.cardShadow,border:"1px solid "+T.border,maxWidth:360,width:"100%"}}>
            <div style={{fontSize:54}}>{success.paymentMethod === "blik" ? "📱" : "✅"}</div>
            <h2 style={{fontSize:20,fontWeight:800,color:T.primary,margin:"8px 0 4px",fontFamily:"'Playfair Display',serif"}}>{tr.successTitle}</h2>
            <div style={{fontSize:13,color:T.textSoft,marginBottom:14}}>
              {success.paymentMethod === "blik" && tr.successBlik}
              {success.paymentMethod === "cash" && tr.successCash}
              {success.paymentMethod === "card" && tr.successCard}
            </div>
            <div style={{background:T.bg,borderRadius:12,padding:13,marginBottom:13}}>
              {[
                [tr.successTable, "#" + (success.tableNumber || success.table)],
                ...(success.guests ? [[lang==="en"?"Guests":"Osoby", success.guests + (lang==="en"?" guests":" os.")]] : []),
                [tr.successTime, "~" + success.wait + " min"],
                [tr.successPayment, success.paymentMethod === "blik" ? tr.successPayBlik : success.paymentMethod === "card" ? tr.successPayCard : tr.successPayCash],
                ...(success.discount > 0 ? [[tr.successCoupon, success.voucherCode], [tr.successDiscount, "-" + fmt(success.discount) + " zł"]] : []),
                [tr.successTotal, fmt(success.total) + " zł"],
              ].map(([k, v]) => (
                <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",fontSize:13,color:T.muted,borderBottom:"1px solid "+T.border}}>
                  <span>{k}</span><strong>{v}</strong>
                </div>
              ))}
            </div>
            {success.cart.map(ci => (
              <div key={ci.id} style={{padding:"5px 0",borderBottom:"1px solid "+T.border+"44"}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.muted,alignItems:"center",gap:7}}>
                  <div style={{display:"flex",alignItems:"center",gap:7}}>
                    <div style={{width:26,height:26,borderRadius:6,overflow:"hidden",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {ci.image ? <img src={ci.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{fontSize:14}}>{ci.emoji}</span>}
                    </div>
                    <span style={{fontWeight:600}}>{ci.name} x{ci.qty}</span>
                  </div>
                  <span style={{fontWeight:700}}>{fmt(ep(ci) * ci.qty)} zł</span>
                </div>
                {ci.note && <div style={{fontSize:11,color:ac,marginTop:3,marginLeft:33,fontStyle:"italic"}}>{ci.note}</div>}
              </div>
            ))}
            {success.orderNote && (
              <div style={{background:T.bg,border:"1.5px solid "+T.border,borderRadius:10,padding:"8px 12px",margin:"10px 0",textAlign:"left"}}>
                <div style={{fontSize:11,color:T.muted,marginBottom:2,fontWeight:700}}>{tr.successNote}</div>
                <div style={{fontSize:12,color:T.text,fontStyle:"italic"}}>{success.orderNote}</div>
              </div>
            )}
            <p style={{color:T.muted,fontSize:12,margin:"13px 0 12px"}}>{tr.successWaiter}</p>
            <button
              onClick={() => printReceipt(success, settings)}
              style={{width:"100%",padding:12,background:T.bg,color:T.primary,
                border:"2px solid "+T.primary,borderRadius:12,fontWeight:700,cursor:"pointer",
                fontSize:14,marginBottom:9,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              {tr.successReceipt}
            </button>
            <button style={{width:"100%",padding:12,background:T.primary,color:"#fff",border:"none",borderRadius:12,fontWeight:700,cursor:"pointer",fontSize:14}}
              onClick={() => { setSuccess(null); setTable(null); }}>{tr.successNewOrder}</button>
          </div>
        </div>
      )}

      {/* ── MAIN MENU (only when table selected, no other screen active) ── */}
      {!!(table && !payStep && !success) && (
        <div style={{paddingBottom:96}}>
          {/* Campaign Modal */}
          {modalCampaign && (
            <CampaignModal campaign={modalCampaign} voucher={modalVoucher} ac={ac}
              onClose={() => { setCampaignDismissed(p => [...p, modalCampaign.id]); setShownModal(null); }}/>
          )}

          {/* Zoom */}
          {zoom && (
            <div style={S.backdrop} onClick={() => setZoom(null)}>
              <div style={{borderRadius:20,overflow:"hidden",maxWidth:340,width:"100%",background:T.surface}} onClick={e => e.stopPropagation()}>
                <div style={{width:"100%",height:210,background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>
                  {zoom.image ? <img src={zoom.image} alt={zoom.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{fontSize:84}}>{zoom.emoji}</span>}
                  {zoom.promoPrice && <div style={{position:"absolute",top:10,left:10,background:ac,color:"#fff",borderRadius:20,padding:"3px 11px",fontWeight:800,fontSize:12}}>PROMOCJA</div>}
                </div>
                <div style={{padding:"13px 16px 17px"}}>
                  <div style={{fontWeight:800,fontSize:17,color:T.primary}}>{getItemTr(zoom,tr,"name")}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:3,marginBottom:8}}>{getItemTr(zoom,tr,"desc")}</div>
                  {zoom.ingredients && (
                    <div style={{background:T.bg,borderRadius:9,padding:"8px 11px",marginBottom:7,border:"1px solid "+T.border}}>
                      <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:3}}>{tr.zoomIngredients}</div>
                      <div style={{fontSize:11,color:T.text,lineHeight:1.5}}>{getItemTr(zoom,tr,"ingredients")}</div>
                    </div>
                  )}
                  {zoom.allergens && (
                    <div style={{background:"#FFF8E1",borderRadius:9,padding:"7px 11px",marginBottom:7,border:"1px solid #F0C040",display:"flex",alignItems:"flex-start",gap:6}}>
                      <span style={{fontSize:14,flexShrink:0}}>⚠</span>
                      <div>
                        <div style={{fontSize:10,fontWeight:700,color:"#856404",textTransform:"uppercase",letterSpacing:0.5,marginBottom:2}}>{tr.zoomAllergens}</div>
                        <div style={{fontSize:11,color:"#856404"}}>{zoom.allergens}</div>
                      </div>
                    </div>
                  )}
                  <div style={{display:"flex",justifyContent:"space-between",marginTop:9,alignItems:"center"}}>
                    <span style={{fontSize:11,color:T.muted}}>{tr.zoomWait(zoom.wait)}</span>
                    <div style={{textAlign:"right"}}>
                      {zoom.promoPrice && <div style={{fontSize:11,color:T.border,textDecoration:"line-through"}}>{fmt(zoom.price)} zl</div>}
                      <div style={{fontSize:19,fontWeight:900,color:zoom.promoPrice ? ac : T.primary}}>{fmt(ep(zoom))} zl</div>
                    </div>
                  </div>
                  <button style={{width:"100%",marginTop:11,padding:11,background:ac,color:"#fff",border:"none",borderRadius:10,fontWeight:800,cursor:"pointer",fontSize:13}}
                    onClick={() => { addToCart(zoom); setZoom(null); }}>{tr.zoomAddToCart}</button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div style={{background:(()=>{
            const hex = settings.menuHeaderBgColor || "#16213e";
            const op  = settings.menuHeaderOpacity !== undefined ? settings.menuHeaderOpacity : 1.0;
            if (op >= 0.99) return hex;
            const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
            return `rgba(${r},${g},${b},${op})`;
          })(),padding:"14px 16px 12px",position:"relative",overflow:"hidden",
          backdropFilter:(settings.menuHeaderOpacity!==undefined&&settings.menuHeaderOpacity<0.99)?"blur(8px)":"none",
          WebkitBackdropFilter:(settings.menuHeaderOpacity!==undefined&&settings.menuHeaderOpacity<0.99)?"blur(8px)":"none",
          }} className="bakery-header">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{display:"flex",alignItems:"center",gap:11}}>
                {/* Logo — 5× tap otwiera panel logowania (gdy navbar ukryty) */}
                <div onClick={()=>{
                  if (!onAdminTap) return;
                  const next = logoTapsCust + 1;
                  setLogoTapsCust(next);
                  if (logoTapTimerCust.current) clearTimeout(logoTapTimerCust.current);
                  if (next >= 5) { setLogoTapsCust(0); onAdminTap(); }
                  else { logoTapTimerCust.current = setTimeout(()=>setLogoTapsCust(0), 3000); }
                }} style={{position:"relative",cursor:onAdminTap?"pointer":"default",userSelect:"none",flexShrink:0}}>
                  {settings.logo
                    ? <img src={settings.logo} alt="logo" style={{height:42,width:42,borderRadius:10,objectFit:"cover",border:"2px solid rgba(255,255,255,0.2)",display:"block"}}/>
                    : <div style={{fontSize:34}}>🍽</div>}

                </div>
                <div>
                  <div style={{fontSize:9,color:T.gold,fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>{tr.tableLabel(table)}</div>
                  <div style={{color:"#fff",fontWeight:900,fontSize:19,fontFamily:"'Playfair Display',serif"}}>{settings.restaurantName}</div>
                </div>
              </div>
              <div
                onClick={() => totalQty > 0 && setCartModalOpen(true)}
                style={{background: totalQty > 0 ? ac : "rgba(255,255,255,0.12)",borderRadius:20,padding:"6px 13px",fontSize:16,color:"#fff",cursor:totalQty>0?"pointer":"default",transition:"background .15s",display:"flex",alignItems:"center",gap:6}}>
                🛒{totalQty > 0 && <><span style={{fontWeight:800,fontSize:14}}>{totalQty}</span><span style={{fontSize:11,opacity:0.85}}>{tr.navEditCart}</span></>}
              </div>
            </div>
          </div>

          {/* Promo banner */}
          {promoItems.length > 0 && (
            <div style={{background:"linear-gradient(90deg,"+ac+",#ff6b35)",padding:"9px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}
              onClick={() => { setSelRoot("promo"); setSelSub(null); }}>
              <div style={{color:"#fff"}}>
                <div style={{fontWeight:800,fontSize:13}}>{tr.promoToday}</div>
                <div style={{fontSize:11,opacity:0.85}}>{tr.promoCount(promoItems.length)}</div>
              </div>
              <div style={{color:"#fff",fontSize:18,fontWeight:900}}>→</div>
            </div>
          )}

          {/* Active campaign strip */}
          {activeCampaigns.filter(cp => cp.showOnTop).map(cp => (
            <div key={cp.id}
              style={{background:cp.image ? ("url("+cp.image+") center/cover") : ("linear-gradient(90deg,"+(cp.accentColor||ac)+","+(cp.accentColor||ac)+"99)"),
                padding:"10px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",position:"relative"}}
              onClick={() => setShownModal(cp.id)}>
              {cp.image && <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.45)"}}/>}
              <div style={{position:"relative",flex:1}}>
                <div style={{color:"#fff",fontWeight:800,fontSize:13}}>{cp.headline}</div>
                {cp.voucherCode && <div style={{color:"rgba(255,255,255,0.85)",fontSize:11,marginTop:2}}>{tr.campaignCode}: {cp.voucherCode}</div>}
              </div>
              <div style={{position:"relative",background:"rgba(255,255,255,0.2)",color:"#fff",borderRadius:20,padding:"5px 12px",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
                {cp.ctaLabel || tr.campaignSee}
              </div>
            </div>
          ))}

          {/* TOP banner slider */}
          {settings.bannersTop && settings.bannersTop.length > 0 && (
            <BannerSlider banners={settings.bannersTop} ac={ac} position="top"
              onCatClick={link => { if(link){ setSelRoot(link); setSelSub(null); }}}/>
          )}

          {/* Category grid — pokazuj kategorie jako kafelki, po kliknięciu filtruj menu */}
          {selRoot === "all" ? (
            <div style={{padding:"14px 13px"}}>
              <CatSearchWrapper
                promoItems={promoItems} roots={roots} cats={cats} activeMenu={activeMenu}
                ac={ac} tr={tr} setSelRoot={setSelRoot} setSelSub={setSelSub} T={T} TC={TC}
              />
            </div>
          ) : (
            <>
              {/* Pasek nawigacji wewnątrz kategorii */}
              <div style={{background:T.surface,borderBottom:"1px solid "+T.border,position:"sticky",top:0,zIndex:30}}>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 13px"}}>
                  <button onClick={()=>{setSelRoot("all");setSelSub(null);}}
                    style={{background:T.bg,border:"1.5px solid "+T.border,borderRadius:20,
                      padding:"6px 12px",cursor:"pointer",fontWeight:600,fontSize:12,color:T.muted,
                      display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                    ← Kategorie
                  </button>
                  <div style={{flex:1,overflowX:"auto",display:"flex",gap:6}}>
                    {selRoot==="promo" ? (
                      <span style={{fontWeight:800,fontSize:14,color:ac,whiteSpace:"nowrap"}}>🔥 {tr.catPromo}</span>
                    ) : (
                      <>
                        {(()=>{const r=roots.find(x=>x.id===selRoot);return r?<span style={{fontWeight:800,fontSize:14,color:TC.text,whiteSpace:"nowrap"}}>{r.emoji} {r.name}</span>:null;})()}
                        {subCats.filter(s => activeMenu.some(i => i.catId === s.id)).length > 0 && (
                          <div style={{display:"flex",gap:5,overflowX:"auto",marginLeft:8}}>
                            <button style={{...S.subPill,...(selSub===null?S.subPillOn:{}),flexShrink:0}} onClick={() => setSelSub(null)}>{tr.catAll}</button>
                            {subCats.filter(s => activeMenu.some(i => i.catId === s.id)).map(s => (
                              <button key={s.id} style={{...S.subPill,...(selSub===s.id?S.subPillOn:{}),flexShrink:0}} onClick={() => setSelSub(s.id)}>
                                {s.emoji} {s.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

          {/* Menu items */}
          <div style={{padding:"10px 13px",display:"flex",flexDirection:"column",gap:8}}>
            {filtered.length === 0 && <div style={S.empty}><div style={{fontSize:42}}>🍽</div><p>{tr.noProducts}</p></div>}
            {filtered.map(item => {
              const ci = cart.find(c => c.id === item.id);
              const isUnavailable = item.available === false;
              return (
                <div key={item.id} style={{background:T.surface,borderRadius:16,padding:"11px 12px",border:"1.5px solid "+(isUnavailable?"#fecaca":item.promoPrice ? ac+"33" : "transparent"),opacity:isUnavailable?0.72:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{position:"relative",flexShrink:0}}>
                      <Thumb item={item} size={72} onClick={() => !isUnavailable && setZoom(item)}/>
                      {item.image && !isUnavailable && <div style={{position:"absolute",bottom:3,right:3,background:"rgba(0,0,0,0.4)",color:"#fff",borderRadius:5,padding:"1px 4px",fontSize:9}}>🔍</div>}
                      {isUnavailable && <div style={{position:"absolute",inset:0,borderRadius:10,background:"rgba(255,255,255,0.6)",display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontSize:9,fontWeight:800,color:"#dc2626",background:"#fef2f2",padding:"2px 5px",borderRadius:5}}>BRAK</span></div>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:2,flexWrap:"wrap"}}>
                        <span style={{fontWeight:700,fontSize:14,color:T.text}}>{getItemTr(item,tr,"name")}</span>
                        {item.promoPrice && !isUnavailable && <span style={{background:ac,color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:9,fontWeight:800}}>PROMO</span>}
                        {isUnavailable && <span style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:20,padding:"1px 7px",fontSize:9,fontWeight:800}}>NIEDOSTĘPNY</span>}
                        {item.allergens && !isUnavailable && <span style={{background:"#FFF8E1",color:"#856404",borderRadius:20,padding:"1px 7px",fontSize:9,fontWeight:700,cursor:"pointer"}} onClick={() => setZoom(item)}>{tr.allergens}</span>}
                      </div>
                      <div style={{fontSize:11,color:T.muted,marginBottom:4,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{getItemTr(item,tr,"desc")}</div>
                      {item.ingredients && !isUnavailable && (
                        <div style={{fontSize:10,color:T.textSoft,marginBottom:3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontStyle:"italic"}}>
                          {tr.ingredients}: {getItemTr(item,tr,"ingredients")}
                        </div>
                      )}
                      <div style={{display:"flex",gap:7,alignItems:"center"}}>
                        {isUnavailable
                          ? <span style={{fontSize:12,color:"#dc2626",fontWeight:700}}>Chwilowo niedostępny</span>
                          : <>
                              <span style={{fontSize:11,color:T.muted}}>⏱ ~{item.wait} min</span>
                              {item.promoPrice
                                ? <><span style={{fontSize:11,color:T.border,textDecoration:"line-through"}}>{fmt(item.price)} zl</span>
                                    <span style={{fontSize:14,fontWeight:800,color:ac}}>{fmt(ep(item))} zl</span></>
                                : <span style={{fontSize:14,fontWeight:800,color:T.text}}>{fmt(item.price)} zl</span>}
                            </>
                        }
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                      {isUnavailable ? (
                        <span style={{fontSize:20}}>🚫</span>
                      ) : ci ? (
                        <>
                          <button style={{...S.qBtn,borderColor:ac,color:ac}} onClick={() => remFromCart(item.id)}>−</button>
                          <span style={{fontWeight:800,fontSize:14,minWidth:17,textAlign:"center",color:T.text}}>{ci.qty}</span>
                          <button style={{...S.qBtn,borderColor:ac,color:ac}} onClick={() => addToCart(item)}>+</button>
                        </>
                      ) : (
                        <button style={{...S.addBtn,background:ac}} onClick={() => addToCart(item)}>{tr.addToCart}</button>
                      )}
                    </div>
                  </div>
                  {ci && !isUnavailable && (
                    <div style={{marginTop:6,paddingTop:6,borderTop:"1px dashed "+T.border,display:"flex",gap:6,alignItems:"center"}}>
                      <span style={{fontSize:13,flexShrink:0,color:T.muted}}>✏</span>
                      <input
                        value={cartComments[item.id] || ""}
                        onChange={e => setItemNote(item.id, e.target.value)}
                        placeholder={tr.itemNotePlaceholder}
                        style={{flex:1,padding:"5px 9px",borderRadius:7,
                          border:"1.5px solid "+(cartComments[item.id] ? ac : T.border),
                          fontSize:11,outline:"none",background:T.bg,color:T.text,
                          fontStyle:cartComments[item.id] ? "normal" : "italic"}}
                      />
                      {cartComments[item.id] && (
                        <button onClick={() => setItemNote(item.id, "")}
                          style={{background:"none",border:"none",color:T.muted,fontSize:15,cursor:"pointer",flexShrink:0,padding:0}}>✕</button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom banner */}
          {settings.bannersBottom && settings.bannersBottom.length > 0 && (
            <div style={{marginBottom: cart.length > 0 ? 70 : 0}}>
              <BannerSlider banners={settings.bannersBottom} ac={ac} position="bottom"
                onCatClick={link => { if(link){ setSelRoot(link); setSelSub(null); }}}/>
            </div>
          )}
            </>
          )}

          {/* Cart bar */}
          {cart.length > 0 && (
            <div style={{
              position:"fixed",bottom:0,
              left: bp.isDesktop ? "220px" : 0,
              right: 0,
              width: bp.isDesktop ? "calc(100% - 220px)" : "100%",
              background:(()=>{
                const hex = settings.cartBarBgColor || "#16213e";
                const op  = settings.cartBarOpacity !== undefined ? settings.cartBarOpacity : 1.0;
                if (op >= 0.99) return hex;
                const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
                return `rgba(${r},${g},${b},${op})`;
              })(),
              backdropFilter:(settings.cartBarOpacity!==undefined&&settings.cartBarOpacity<0.99)?"blur(10px)":"none",
              WebkitBackdropFilter:(settings.cartBarOpacity!==undefined&&settings.cartBarOpacity<0.99)?"blur(10px)":"none",
              padding: bp.isDesktop ? "10px 28px 12px" : bp.isTablet ? "10px 22px 12px" : "8px 12px 10px",
              zIndex:50,boxSizing:"border-box",
              boxShadow:"0 -2px 16px rgba(0,0,0,0.25)",
            }}>
              {/* Voucher bar — only when enabled */}
              {settings.vouchersEnabled !== false && (
                <VoucherBar vouchers={vouchers||[]} usedCodes={usedCodes||[]} cartTotal={totalPrice}
                  applied={appliedVoucher} onApply={v => setAppliedVoucher(v)} ac={ac}/>
              )}
              {/* Order note */}
              <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:8}}>
                <span style={{fontSize:14,flexShrink:0}}>📝</span>
                <input
                  value={orderNote}
                  onChange={e => setOrderNote(e.target.value)}
                  placeholder={tr.orderNotePlaceholder}
                  style={{flex:1,padding:"7px 10px",borderRadius:9,
                    border:"1.5px solid "+(orderNote ? ac : "rgba(255,255,255,0.2)"),
                    fontSize:11,outline:"none",
                    background:"rgba(255,255,255,0.1)",
                    color:"#fff"}}
                />
                {orderNote && (
                  <button onClick={() => setOrderNote("")}
                    style={{background:"none",border:"none",color:"rgba(255,255,255,0.5)",fontSize:16,cursor:"pointer",flexShrink:0,padding:"0 2px"}}>✕</button>
                )}
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                <div onClick={() => setCartModalOpen(true)} style={{cursor:"pointer",flex:1,minWidth:0}}>
                  <div style={{color:"#fff",fontWeight:700,fontSize:bp.isMobile?13:15,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    {tr.cartLabel(totalQty)}
                    <span style={{fontSize:10,opacity:0.7,fontWeight:500,background:"rgba(255,255,255,0.15)",borderRadius:6,padding:"1px 6px",flexShrink:0}}>{tr.navEditCart}</span>
                  </div>
                  <div style={{color:"rgba(255,255,255,0.6)",fontSize:bp.isMobile?11:12,marginTop:2,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span>⏱ ~{totalWait} min</span>
                    {discount > 0 && <span style={{textDecoration:"line-through",opacity:0.6}}>{fmt(totalPrice)} zł</span>}
                    <span style={{color:discount>0?"#4ade80":"rgba(255,255,255,0.85)",fontWeight:discount>0?800:500}}>{fmt(finalPrice)} zł</span>
                  </div>
                </div>
                <button
                  style={{
                    background:ac,color:"#fff",border:"none",
                    padding: bp.isMobile ? "10px 16px" : "12px 24px",
                    borderRadius:12,fontWeight:800,
                    fontSize: bp.isMobile ? 13 : 15,
                    cursor:"pointer",flexShrink:0,
                    boxShadow:"0 2px 10px rgba(0,0,0,0.25)",
                    whiteSpace:"nowrap",
                  }}
                  onClick={placeOrder}>
                  {tr.placeOrder}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </LangContext.Provider>
  );
}



/* ════════════════════ USER FORM COMPONENT ═══════════════════════════════════ */
/* ════════════════════ ROLE FORM COMPONENT ════════════════════════════════════ */
// eslint-disable-next-line no-unused-vars
function RoleForm({ form, setForm, onSave, onCancel, title }) {
  const NOTIF_KEYS = [
    { key:"newOrder",       label:"🛍 Nowe zamówienie",     desc:"Powiadomienie o każdym nowym zamówieniu" },
    { key:"orderIssued",    label:"✅ Zamówienie wydane",    desc:"Gdy zamówienie zostanie wydane klientowi" },
    { key:"newReservation", label:"📅 Nowa rezerwacja",      desc:"Powiadomienie o nowej rezerwacji stolika" },
    { key:"lowStock",       label:"📦 Niski stan produktów", desc:"Alert gdy produkt ma niski stan" },
  ];
  const notifs = form.notifications || { newOrder:false, orderIssued:false, newReservation:false, lowStock:false };

  const PRESET_COLORS = [
    {color:"#2563eb",bg:"#eff6ff"},{color:"#7c3aed",bg:"#f5f3ff"},
    {color:"#15803d",bg:"#dcfce7"},{color:"#b45309",bg:"#fef3c7"},
    {color:"#be123c",bg:"#fff1f2"},{color:"#0e7490",bg:"#ecfeff"},
    {color:"#6d28d9",bg:"#ede9fe"},{color:"#9a3412",bg:"#ffedd5"},
  ];
  const PRESET_ICONS = ["👤","🧑‍💼","👷","🧑‍🍳","🧑‍💻","📦","🔧","🎯","📋","🏷","🔔","⭐","🛡","🔑"];

  return (
    <div style={{background:"#f8fafc",border:"1.5px solid "+T.border,borderRadius:10,padding:14,marginBottom:10}}>
      <div style={{fontWeight:800,fontSize:13,color:T.primary,marginBottom:12}}>{title}</div>

      {/* Nazwa i ID */}
      <div style={{display:"grid",gridTemplateColumns:"1fr",gap:8,marginBottom:10}}>
        <div>
          <label style={S.label}>Nazwa roli *</label>
          <input style={S.inp} placeholder="np. Kierownik sali, Barista..." value={form.label||""}
            onChange={e=>setForm({...form,label:e.target.value})}/>
        </div>
      </div>

      {/* Ikona */}
      <div style={{marginBottom:10}}>
        <label style={S.label}>Ikona</label>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
          {PRESET_ICONS.map(ic=>(
            <button key={ic} onClick={()=>setForm({...form,icon:ic})}
              style={{width:34,height:34,borderRadius:8,border:"2px solid "+(form.icon===ic?T.accent:T.border),
                background:form.icon===ic?T.accentSoft:"#fff",fontSize:16,cursor:"pointer",display:"flex",
                alignItems:"center",justifyContent:"center"}}>
              {ic}
            </button>
          ))}
          <input style={{...S.inp,width:60,textAlign:"center",fontSize:18}} maxLength={2}
            value={form.icon||""} onChange={e=>setForm({...form,icon:e.target.value})}
            placeholder="✏"/>
        </div>
      </div>

      {/* Kolor */}
      <div style={{marginBottom:12}}>
        <label style={S.label}>Kolor roli</label>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          {PRESET_COLORS.map(({color,bg})=>(
            <button key={color} onClick={()=>setForm({...form,color,bg})}
              style={{width:28,height:28,borderRadius:"50%",background:color,border:"3px solid "+(form.color===color?"#111":"transparent"),
                cursor:"pointer",flexShrink:0}}>
            </button>
          ))}
        </div>
        {/* Podgląd */}
        <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,padding:"8px 12px",
          borderRadius:9,background:form.bg||"#eff6ff",border:"1.5px solid "+(form.color||"#2563eb")+"44"}}>
          <span style={{fontSize:18}}>{form.icon||"👤"}</span>
          <span style={{fontWeight:700,fontSize:12,color:form.color||"#2563eb"}}>{form.label||"Podgląd roli"}</span>
        </div>
      </div>

      {/* Uprawnienia */}
      <div style={{marginBottom:12}}>
        <label style={S.label}>Uprawnienia</label>
        <div style={{...S.card,padding:"10px 12px",marginBottom:0}}>
          <div onClick={()=>setForm({...form,canChangePassword:!form.canChangePassword})}
            style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"6px 0"}}>
            <div style={{width:20,height:20,borderRadius:5,flexShrink:0,
              background:form.canChangePassword?T.accent:T.border,
              display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
              {form.canChangePassword&&<div style={{width:10,height:10,background:"#fff",borderRadius:2,
                backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 10 10' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 5l2.5 2.5L8 3' stroke='white' stroke-width='1.5' fill='none'/%3E%3C/svg%3E\")",
                backgroundSize:"cover"}}/>}
            </div>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:12,color:T.text}}>🔑 Może zmieniać hasła innych użytkowników</div>
              <div style={{fontSize:10,color:T.muted}}>Użytkownik tej roli może edytować hasła kont niższego poziomu</div>
            </div>
          </div>
        </div>
      </div>

      {/* Powiadomienia */}
      <div style={{marginBottom:12}}>
        <label style={S.label}>🔔 Powiadomienia dźwiękowe</label>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
          {NOTIF_KEYS.map(({key,label,desc})=>{
            const on = notifs[key];
            return (
              <div key={key} onClick={()=>setForm({...form,notifications:{...notifs,[key]:!on}})}
                style={{display:"flex",alignItems:"flex-start",gap:8,padding:"9px 11px",borderRadius:10,
                  border:"1.5px solid "+(on?T.accent:T.border),
                  background:on?"#eff6ff":"#fff",cursor:"pointer",transition:"all .15s"}}>
                <div style={{width:18,height:18,borderRadius:"50%",flexShrink:0,marginTop:1,
                  background:on?T.accent:T.border,
                  display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
                  {on&&<div style={{width:7,height:7,borderRadius:"50%",background:"#fff"}}/>}
                </div>
                <div>
                  <div style={{fontSize:11,fontWeight:on?700:500,color:on?T.accent:T.muted,lineHeight:1.2}}>{label}</div>
                  <div style={{fontSize:9,color:T.muted,marginTop:1}}>{desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{display:"flex",gap:7}}>
        <button style={S.darkBtn} onClick={onSave}>Zapisz rolę</button>
        <button style={S.cancelBtn} onClick={onCancel}>Anuluj</button>
      </div>
    </div>
  );
}

function UserForm({ title, form, setForm, onSave, onCancel, currentUser, customRoles }) {
  const mergedRoles = {
    ...ROLE_DEFS,
    ...Object.fromEntries((customRoles||[]).map(r=>[r.id,r]))
  };
  const roleNotifs = mergedRoles[form.role] ? mergedRoles[form.role].notifications : ROLE_DEFS.seller.notifications;
  const notifs = form.notifications || roleNotifs;
  const [plainPass, setPlainPass] = useState("");
  const [saving, setSaving] = useState(false);

  const isSuperAdminUser = currentUser && currentUser.role === "superadmin";
  const isEditingSuperAdmin = form.role === "superadmin";
  const canChangePassword = isSuperAdminUser || !isEditingSuperAdmin;

  const handleRoleChange = role => {
    const rd = mergedRoles[role] || ROLE_DEFS.seller;
    setForm({...form, role, notifications: rd.notifications});
  };
  const toggleNotif = key => {
    setForm({...form, notifications: {...notifs, [key]: !notifs[key]}});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (plainPass.trim()) {
        // Używamy PBKDF2 z loginem jako solą (ta sama logika co w LoginScreen)
        const loginForSalt = (form.login || "").trim();
        const hash = await hashPassword(plainPass.trim(), loginForSalt);
        setForm(prev => {
          const updated = {...prev, passwordHash: hash};
          delete updated.password;
          return updated;
        });
        setTimeout(() => { onSave(); setSaving(false); }, 0);
      } else {
        onSave();
        setSaving(false);
      }
    } catch(e) {
      setSaving(false);
    }
  };

  const rd = mergedRoles[form.role] || ROLE_DEFS.seller;

  return (
    <div>
      <div style={{fontWeight:800,fontSize:13,color:T.primary,marginBottom:10}}>{title}</div>

      {/* Basic info */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,marginBottom:10}}>
        <input style={S.inp} placeholder="Imię i nazwisko" value={form.name}
          onChange={e=>setForm({...form,name:e.target.value})}/>
        <input style={S.inp} placeholder="Login" value={form.login} autoComplete="off"
          onChange={e=>setForm({...form,login:e.target.value})}/>
        <input style={{...S.inp,gridColumn:"span 2",opacity:canChangePassword?1:0.5,cursor:canChangePassword?"text":"not-allowed"}}
          type="password" autoComplete="new-password"
          placeholder={
            !canChangePassword
              ? "🔒 Tylko Super Administrator może zmienić to hasło"
              : form.passwordHash ? "Nowe hasło (zostaw puste aby nie zmieniać)" : "Hasło"
          }
          disabled={!canChangePassword}
          value={plainPass} onChange={e=>setPlainPass(e.target.value)}/>
        <select style={{...S.inp,gridColumn:"span 2",
          opacity:(!isSuperAdminUser&&isEditingSuperAdmin)?0.5:1,
          cursor:(!isSuperAdminUser&&isEditingSuperAdmin)?"not-allowed":"pointer"}}
          value={form.role}
          disabled={!isSuperAdminUser && isEditingSuperAdmin}
          onChange={e=>handleRoleChange(e.target.value)}>
          {isSuperAdminUser && <option value="superadmin">🛡 Super Administrator</option>}
          <option value="admin">👑 Główny administrator</option>
          <option value="seller">🏷 Sprzedawca</option>
          <option value="cook">👨‍🍳 Kucharz</option>
          {(customRoles||[]).length>0&&(
            <optgroup label="── Niestandardowe role ──">
              {(customRoles||[]).map(r=>(
                <option key={r.id} value={r.id}>{r.icon} {r.label}</option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      {/* Role preview */}
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:10,
        background:rd.bg||"#f9fafb",border:"1.5px solid "+(rd.color||"#9ca3af")+"44",marginBottom:12}}>
        <span style={{fontSize:18}}>{rd.icon||"👤"}</span>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:12,color:rd.color||T.text}}>{rd.label}</div>
          <div style={{fontSize:10,color:T.muted,marginTop:1}}>
            {form.role==="superadmin" && "Pełna kontrola systemu — zarządzanie wszystkim, w tym Super Administratorem"}
            {form.role==="admin"  && "Pełny dostęp — menu, zamówienia, ustawienia, użytkownicy"}
            {form.role==="seller" && "Widok zamówień, klientów i rezerwacji"}
            {form.role==="cook"   && "Widok zamówień do kuchni — produkty, alergie, kolejność"}
            {!["superadmin","admin","seller","cook"].includes(form.role) && (rd.canChangePassword?"Może zmieniać hasła":"Rola niestandardowa")}
          </div>
          {rd.canChangePassword && !["superadmin","admin"].includes(form.role) && (
            <div style={{fontSize:9,color:"#1d4ed8",marginTop:2,fontWeight:600}}>🔑 Uprawnienie: zmiana haseł</div>
          )}
        </div>
      </div>

      {/* ── Dodatkowe role ── */}
      {(customRoles||[]).length > 0 || Object.keys(ROLE_DEFS).filter(r=>r!==form.role&&r!=="superadmin").length > 0 ? (
        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>
            🎭 Dodatkowe role (opcjonalne)
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {Object.entries(mergedRoles)
              .filter(([k])=>k!==form.role && k!=="superadmin")
              .map(([k,r])=>{
                const isActive = (form.extraRoles||[]).includes(k);
                return (
                  <button type="button" key={k}
                    onClick={()=>setForm(p=>({
                      ...p,
                      extraRoles: isActive
                        ? (p.extraRoles||[]).filter(e=>e!==k)
                        : [...(p.extraRoles||[]), k]
                    }))}
                    style={{
                      padding:"5px 12px",borderRadius:20,
                      border:"1.5px solid "+(isActive?r.color||T.accent:T.border),
                      background:isActive?r.bg||"#fff8f0":"#fff",
                      color:isActive?r.color||T.accent:T.muted,
                      fontWeight:700,fontSize:11,cursor:"pointer",transition:"all .15s",
                    }}>
                    {isActive?"✓ ":""}{r.icon||"👤"} {r.label}
                  </button>
                );
              })}
          </div>
          <div style={{fontSize:10,color:T.muted,marginTop:5,fontStyle:"italic"}}>
            Dodatkowe role rozszerzają uprawnienia — użytkownik widzi widoki wszystkich przypisanych ról
          </div>
        </div>
      ) : null}

      {/* Notifications per role */}
      <div style={{marginBottom:12}}>
        <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:0.5,marginBottom:8}}>
          🔔 Powiadomienia dźwiękowe
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
          {Object.entries(NOTIF_LABELS).map(([key,label])=>{
            const on = notifs[key];
            return (
              <div key={key} onClick={()=>toggleNotif(key)}
                style={{display:"flex",alignItems:"center",gap:8,padding:"9px 11px",borderRadius:10,
                  border:"1.5px solid "+(on?T.accent:T.border),
                  background:on?"#fff8f0":T.bg,cursor:"pointer",transition:"all .15s"}}>
                <div style={{width:20,height:20,borderRadius:"50%",flexShrink:0,
                  background:on?T.accent:T.border,
                  display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
                  {on&&<div style={{width:8,height:8,borderRadius:"50%",background:"#fff"}}/>}
                </div>
                <span style={{fontSize:11,fontWeight:on?700:400,color:on?T.accent:T.muted,lineHeight:1.2}}>{label}</span>
              </div>
            );
          })}
        </div>
        <div style={{fontSize:10,color:T.muted,marginTop:6,fontStyle:"italic"}}>
          Powiadomienia dotyczą sesji zalogowanego użytkownika
        </div>
      </div>

      <div style={{display:"flex",gap:7}}>
        <button style={{...S.darkBtn,opacity:saving?0.7:1}} onClick={handleSave} disabled={saving}>
          {saving ? "Zapisuję..." : "Zapisz"}
        </button>
        <button style={S.cancelBtn} onClick={onCancel}>Anuluj</button>
      </div>
    </div>
  );
}

/* ════════════════════ USER CARD COMPONENT ════════════════════════════════════ */
// eslint-disable-next-line no-unused-vars
function UserCard({ u, isSelf, onEdit, onDelete, allRoleDefs, currentUserRole }) {
  const rd = (allRoleDefs && allRoleDefs[u.role]) || ROLE_DEFS[u.role] || ROLE_DEFS.seller;
  const notifs = u.notifications || rd.notifications;
  const activeNotifs = Object.entries(notifs).filter(([,v])=>v).map(([k])=>NOTIF_LABELS[k]);
  const isSuperAdminAccount = u.role === "superadmin";
  // Główny administrator (admin) NIE może edytować ani usuwać konta Super Administratora
  const isBlockedByRole = isSuperAdminAccount && currentUserRole !== "superadmin";

  return (
    <div>
      <div style={{display:"flex",gap:10,alignItems:"center"}}>
        <div style={{width:42,height:42,borderRadius:"50%",background:rd.bg,
          border:"2px solid "+rd.color+"44",
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
          {rd.icon}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:700,fontSize:13,color:T.text,display:"flex",alignItems:"center",gap:6}}>
            {u.name||u.login}
            {isSuperAdminAccount && <span style={{fontSize:9,background:"#6d28d9",color:"#fff",borderRadius:10,padding:"1px 7px",fontWeight:700}}>🔒 CHRONIONE</span>}
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center",marginTop:3,flexWrap:"wrap"}}>
            <span style={{fontSize:10,fontWeight:700,borderRadius:20,padding:"2px 9px",
              background:rd.bg,color:rd.color}}>
              {rd.icon} {rd.label}
            </span>
            <span style={{fontSize:10,color:T.muted}}>@{u.login}</span>
          </div>
          {activeNotifs.length>0&&(
            <div style={{fontSize:9,color:T.muted,marginTop:3,display:"flex",gap:3,flexWrap:"wrap"}}>
              🔔
              {activeNotifs.map(n=>(
                <span key={n} style={{background:T.bg,border:"1px solid "+T.border,
                  borderRadius:10,padding:"1px 6px",color:T.muted}}>{n}</span>
              ))}
            </div>
          )}
          {/* NFC card badge */}
          <div style={{marginTop:3}}>
            {u.nfcCard
              ? <span style={{fontSize:9,background:"#dcfce7",color:"#15803d",borderRadius:8,
                  padding:"1px 7px",fontWeight:700,display:"inline-flex",alignItems:"center",gap:3}}>
                  💳 Karta NFC przypisana
                </span>
              : <span style={{fontSize:9,color:"#9ca3af",fontStyle:"italic"}}>
                  💳 Brak karty NFC
                </span>
            }
          </div>
          {isBlockedByRole && (
            <div style={{fontSize:9,color:"#6d28d9",marginTop:3,fontWeight:600}}>
              🔒 Edycja dostępna wyłącznie dla Super Administratora
            </div>
          )}
        </div>
        <div style={{display:"flex",gap:5,flexShrink:0}}>
          {!isBlockedByRole && <button style={S.actBtn} onClick={onEdit}>✏</button>}
          {!isSelf && !isBlockedByRole && <button style={{...S.actBtn,background:"#ffebee"}} onClick={onDelete}>🗑</button>}
        </div>
      </div>
    </div>
  );
}


/* ════════════════════ NFC CARDS PANEL ════════════════════════════════════════
   Zarządzanie kartami NFC dla pracowników.
   Admin przypisuje każdemu pracownikowi unikalny identyfikator karty NFC
   (odczytywany przez Web NFC API). Po przyłożeniu karty do czytnika na
   ekranie logowania pracownik jest logowany automatycznie.
═════════════════════════════════════════════════════════════════════════════ */
/* ════════ NFC CARD TOKEN — AES-GCM encryption ════════════════════════════
   Każda karta jest szyfrowana kluczem = SHA-256(cardId + APP_SECRET).
   URL param: ?sc=<base64url(iv + ciphertext)>
   Odczytanie tokenu bez znajomości cardId i APP_SECRET jest niemożliwe.
═══════════════════════════════════════════════════════════════════════════ */
const NFC_APP_SECRET = "smakidomu-nfc-v1";   // klucz aplikacji — zmień w ustawieniach produkcyjnych

async function nfcEncryptToken(cardId) {
  // Klucz: SHA-256(cardId + secret) — deterministyczny, nie wymaga przechowywania
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(cardId + NFC_APP_SECRET),
    { name: "PBKDF2" }, false, ["deriveKey"]
  );
  const key = await crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: new TextEncoder().encode("nfc-salt-2024"), iterations: 100000, hash: "SHA-256" },
    keyMaterial, { name: "AES-GCM", length: 256 }, false, ["encrypt"]
  );
  const iv         = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(cardId)   // szyfrujemy sam cardId
  );
  // Sklejamy iv (12B) + ciphertext, base64url
  const combined = new Uint8Array(12 + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), 12);
  return btoa(String.fromCharCode(...combined))
    .replace(/\+/g,"-").replace(/\//g,"_").replace(/=/g,"");
}

async function nfcDecryptToken(token, cardId) {
  // Zwraca true jeśli token odpowiada danemu cardId
  try {
    const bin = Uint8Array.from(atob(token.replace(/-/g,"+").replace(/_/g,"/")), c => c.charCodeAt(0));
    const iv         = bin.slice(0, 12);
    const ciphertext = bin.slice(12);
    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(cardId + NFC_APP_SECRET),
      { name: "PBKDF2" }, false, ["deriveKey"]
    );
    const key = await crypto.subtle.deriveKey(
      { name: "PBKDF2", salt: new TextEncoder().encode("nfc-salt-2024"), iterations: 100000, hash: "SHA-256" },
      keyMaterial, { name: "AES-GCM", length: 256 }, false, ["decrypt"]
    );
    const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
    const decoded = new TextDecoder().decode(plain);
    return decoded === cardId;
  } catch(_) { return false; }
}

// Znajdź użytkownika pasującego do zaszyfrowanego tokenu
async function findUserByNfcToken(token, users) {
  for (const u of users) {
    if (!u.nfcCard) continue;
    if (await nfcDecryptToken(token, u.nfcCard)) return u;
  }
  return null;
}

/* ─────────────────────────────────────────────────────────────────────────────
   NfcCardsPanel
   Dwa tryby dla każdego pracownika:
   1. PRZYPISZ KARTĘ  – odczytaj serialNumber karty (jak dotychczas)
   2. ZAPROGRAMUJ     – zapisz na tag NFC URL aplikacji z param ?staffCard=ID
      Po przyłożeniu takiej karty dowolnym telefonem otwiera się aplikacja
      i od razu loguje pracownika (patrz: obsługa ?staffCard w App root).
───────────────────────────────────────────────────────────────────────────── */
function NfcCardsPanel({ users, setUsers, currentUser }) {
  /* action: null | { userId, mode: "assign"|"program" } */
  const [action,      setAction]      = React.useState(null);
  const [actionMsg,   setActionMsg]   = React.useState("");
  const [actionState, setActionState] = React.useState("idle"); // idle|busy|ok|err
  const abortRef = React.useRef(null);
  const nfcSupported = "NDEFReader" in window;

  /* ── helpers ── */
  const baseUrl = window.location.origin + window.location.pathname;

  const stopAction = React.useCallback(() => {
    try { if (abortRef.current) abortRef.current.abort(); } catch(_) {}
    abortRef.current = null;
    setAction(null);
    setActionMsg("");
    setActionState("idle");
  }, []);

  /* ── ASSIGN: odczytaj serialNumber i zapisz w profilu ── */
  const startAssign = async (userId) => {
    if (!nfcSupported) { setActionMsg("Web NFC wymaga Chrome na Androidzie."); setActionState("err"); return; }
    stopAction();
    setAction({ userId, mode: "assign" });
    setActionState("busy");
    setActionMsg("📡 Przyłóż kartę do czytnika...");
    try {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const ndef = new window.NDEFReader();
      await ndef.scan({ signal: ctrl.signal });
      ndef.addEventListener("reading", ({ serialNumber }) => {
        const cardId = (serialNumber || "").replace(/:/g, "").toUpperCase();
        if (!cardId) return;
        const conflict = users.find(u => u.nfcCard === cardId && u.id !== userId);
        if (conflict) {
          setActionMsg("⚠ Ta karta należy do: " + (conflict.name || conflict.login));
          setActionState("err");
          setTimeout(stopAction, 3000);
          return;
        }
        setUsers(p => p.map(u => u.id === userId ? { ...u, nfcCard: cardId } : u));
        setActionMsg("✅ Karta przypisana!");
        setActionState("ok");
        setTimeout(stopAction, 1800);
      }, { once: true });
    } catch(e) {
      setActionMsg("❌ " + (e.message || "Błąd NFC"));
      setActionState("err");
      setTimeout(stopAction, 2500);
    }
  };

  /* ── PROGRAM: zapisz zaszyfrowany URL na tag NFC ── */
  const startProgram = async (u) => {
    if (!nfcSupported) { setActionMsg("Web NFC wymaga Chrome na Androidzie."); setActionState("err"); return; }
    if (!u.nfcCard) {
      setActionMsg("⚠ Najpierw przypisz kartę (odczytaj jej ID), potem ją zaprogramuj.");
      setActionState("err");
      setTimeout(() => { setActionState("idle"); setActionMsg(""); }, 3500);
      return;
    }
    stopAction();
    setAction({ userId: u.id, mode: "program" });
    setActionState("busy");
    setActionMsg("🔐 Generuję zaszyfrowany token...");
    try {
      // Szyfrujemy cardId — URL nie zdradza tożsamości pracownika
      const token    = await nfcEncryptToken(u.nfcCard);
      const loginUrl = baseUrl + "?sc=" + token;
      setActionMsg("📡 Przyłóż tag NFC do zaprogramowania...");
      const ndef = new window.NDEFReader();
      await ndef.write({ records: [{ recordType: "url", data: loginUrl }] });
      setActionMsg("✅ Tag zaprogramowany! URL jest zaszyfrowany — nie zdradza danych pracownika. Po przyłożeniu karty aplikacja otworzy się i zaloguje " + (u.name || u.login) + " automatycznie.");
      setActionState("ok");
      setTimeout(stopAction, 5000);
    } catch(e) {
      setActionMsg("❌ " + (e.message || "Błąd zapisu NFC"));
      setActionState("err");
      setTimeout(stopAction, 2500);
    }
  };

  const removeCard = (userId) =>
    setUsers(p => p.map(u => u.id === userId ? { ...u, nfcCard: undefined } : u));

  const allRoles       = { ...ROLE_DEFS };
  const isSuperAdmin   = currentUser.role === "superadmin";
  // Admin nie widzi kont superadmina — może zarządzać tylko "niższymi" rolami
  const visibleUsers   = isSuperAdmin
    ? users
    : users.filter(u => u.role !== "superadmin");

  /* ── msgBox helper ── */
  const MsgBox = () => !actionMsg ? null : (
    <div style={{
      padding:"10px 14px", borderRadius:10, marginBottom:12, fontSize:13, fontWeight:600,
      display:"flex", alignItems:"flex-start", gap:8,
      background: actionState==="ok"?"#f0fdf4": actionState==="err"?"#fef2f2":"#eff6ff",
      color:       actionState==="ok"?"#15803d": actionState==="err"?"#dc2626":"#1d4ed8",
      border:"1px solid "+(actionState==="ok"?"#bbf7d0": actionState==="err"?"#fecaca":"#bfdbfe"),
    }}>
      {actionState==="busy" && <span style={{fontSize:16,flexShrink:0,animation:"blikSpin 1.2s linear infinite",display:"inline-block"}}>📡</span>}
      <span style={{flex:1,lineHeight:1.5}}>{actionMsg}</span>
      {action && (
        <button onClick={stopAction} style={{flexShrink:0,background:"rgba(0,0,0,0.07)",border:"none",
          borderRadius:6,padding:"2px 9px",cursor:"pointer",fontSize:11,fontWeight:700}}>
          Anuluj
        </button>
      )}
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{fontWeight:800,fontSize:15,color:T.primary,marginBottom:4}}>💳 Karty logowania NFC</div>

      {/* ── Instrukcja ── */}
      <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.65,
        background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:10,padding:"11px 14px"}}>
        <strong>Jak to działa?</strong> Każda karta ma dwa kroki:<br/>
        <strong>1. Przypisz kartę</strong> — przyłóż kartę, odczytamy jej unikalny ID i zapiszemy w profilu.<br/>
        <strong>2. Zaprogramuj tag</strong> — zapisujemy na kartę URL aplikacji z kodem logowania.
        Od tej chwili wystarczy przyłożyć kartę do telefonu — aplikacja otworzy się i <strong>zaloguje pracownika automatycznie</strong>, bez ekranu logowania.<br/>
        <span style={{color:"#0369a1"}}>⚠ Wymaga Chrome na Androidzie (Web NFC API).</span>
      </div>

      {!nfcSupported && (
        <div style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:10,padding:"10px 14px",
          fontSize:12,color:"#c2410c",marginBottom:14}}>
          📵 To urządzenie nie obsługuje Web NFC. Możesz wpisać ID karty ręcznie (krok 1), ale <strong>programowanie (krok 2)</strong> wymaga Android Chrome.
        </div>
      )}

      <MsgBox/>

      {/* ── Lista pracowników ── */}
      {!isSuperAdmin && (
        <div style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:9,
          padding:"8px 12px",fontSize:11,color:"#92400e",marginBottom:10}}>
          🔒 Konta Super Administratora są ukryte i zarządzane wyłącznie przez Super Administratora.
        </div>
      )}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {visibleUsers.map(u => {
          const rd         = allRoles[u.role] || ROLE_DEFS.seller;
          const isMe       = u.id === currentUser.id;
          const isActive   = action?.userId === u.id;
          const isAssign   = isActive && action.mode === "assign";
          const isProgram  = isActive && action.mode === "program";
          const hasCard    = !!u.nfcCard;

          return (
            <div key={u.id} style={{
              border:"1.5px solid "+(isAssign?"#2563eb":isProgram?"#7c3aed":hasCard?"#22c55e":T.border),
              borderRadius:12, padding:"12px 14px",
              background: isAssign?"#eff6ff": isProgram?"#f5f3ff": hasCard?"#f0fdf4":"#fff",
              transition:"all .2s",
              boxShadow: isActive?"0 0 0 3px "+(isProgram?"rgba(124,58,237,0.15)":"rgba(37,99,235,0.15)"):"none"
            }}>
              <div style={{display:"flex",alignItems:"flex-start",gap:10}}>

                {/* Avatar */}
                <div style={{width:40,height:40,borderRadius:"50%",background:rd.bg,flexShrink:0,
                  border:"2px solid "+rd.color+"44",
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,marginTop:1}}>
                  {rd.icon}
                </div>

                {/* Info */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:13,color:T.text,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    {u.name||u.login}
                    {isMe && <span style={{fontSize:9,background:"#dbeafe",color:"#1d4ed8",borderRadius:8,padding:"1px 6px"}}>Ty</span>}
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    <span style={{background:rd.bg,color:rd.color,borderRadius:10,padding:"1px 7px",fontWeight:700}}>
                      {rd.icon} {rd.label}
                    </span>
                    <span>@{u.login}</span>
                  </div>

                  {/* Status karty + URL podgląd */}
                  <div style={{marginTop:7}}>
                    {hasCard ? (
                      <div style={{display:"flex",flexWrap:"wrap",gap:5,alignItems:"center"}}>
                        <span style={{fontSize:11,fontWeight:700,color:"#15803d",background:"#dcfce7",
                          borderRadius:8,padding:"2px 9px",display:"inline-flex",alignItems:"center",gap:3}}>
                          ✅ ID karty przypisany
                        </span>
                        <code style={{fontSize:9,color:T.muted,background:T.bg,borderRadius:6,
                          padding:"2px 7px",fontFamily:"monospace",letterSpacing:1}}>
                          {u.nfcCard}
                        </code>
                      </div>
                    ) : (
                      <span style={{fontSize:11,color:"#f59e0b",fontWeight:600}}>
                        ⚠ Brak przypisanej karty — wykonaj krok 1
                      </span>
                    )}
                    {hasCard && (
                      <div style={{marginTop:5,fontSize:10,color:T.muted,background:"#f8fafc",
                        borderRadius:7,padding:"4px 8px",border:"1px solid "+T.border,
                        lineHeight:1.5}}>
                        <span style={{fontWeight:700,color:"#7c3aed"}}>🔐 URL zaszyfrowany</span>
                        <span style={{marginLeft:6,fontFamily:"monospace",color:"#9ca3af"}}>
                          {baseUrl}?sc=<span style={{letterSpacing:0}}>{"●".repeat(24)}</span>
                        </span>
                        <div style={{fontSize:9,color:"#6b7280",marginTop:2}}>
                          Token generowany przy każdym programowaniu — unikalny i nieodwracalny
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Kolumna akcji ── */}
                <div style={{display:"flex",flexDirection:"column",gap:5,flexShrink:0,minWidth:128}}>

                  {/* KROK 1 — Przypisz/Zmień kartę */}
                  <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",
                    letterSpacing:0.5,marginBottom:1}}>Krok 1</div>
                  {isAssign ? (
                    <button onClick={stopAction}
                      style={{padding:"6px 12px",background:"#ef4444",color:"#fff",
                        border:"none",borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:700}}>
                      ✕ Anuluj skan
                    </button>
                  ) : (
                    <button onClick={()=>startAssign(u.id)}
                      style={{padding:"6px 12px",
                        background: hasCard?"#3b82f6":"linear-gradient(135deg,#2563eb,#1d4ed8)",
                        color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:700,
                        boxShadow:"0 2px 6px rgba(37,99,235,0.25)"}}>
                      {hasCard ? "🔄 Zmień kartę" : "📡 Przypisz kartę"}
                    </button>
                  )}

                  {/* ── Ręczne ID ── */}
                  {!isAssign && (
                    <button onClick={()=>{
                      const id = window.prompt("ID karty NFC (hex). Np. A1B2C3D4", u.nfcCard||"");
                      if (id === null) return;
                      const clean = id.replace(/[^a-fA-F0-9]/g,"").toUpperCase();
                      if (!clean) return;
                      const conflict = users.find(x=>x.nfcCard===clean&&x.id!==u.id);
                      if (conflict) { window.alert("Ta karta należy do: "+(conflict.name||conflict.login)); return; }
                      setUsers(p=>p.map(x=>x.id===u.id?{...x,nfcCard:clean}:x));
                    }}
                      style={{padding:"5px 12px",background:T.bg,color:T.muted,
                        border:"1px solid "+T.border,borderRadius:8,cursor:"pointer",fontSize:10}}>
                      ⌨ Wpisz ID ręcznie
                    </button>
                  )}

                  {/* KROK 2 — Zaprogramuj tag */}
                  <div style={{fontSize:9,fontWeight:700,color:T.muted,textTransform:"uppercase",
                    letterSpacing:0.5,marginTop:4,marginBottom:1}}>Krok 2</div>
                  {isProgram ? (
                    <button onClick={stopAction}
                      style={{padding:"6px 12px",background:"#ef4444",color:"#fff",
                        border:"none",borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:700}}>
                      ✕ Anuluj zapis
                    </button>
                  ) : (
                    <button onClick={()=>startProgram(u)}
                      disabled={!hasCard}
                      title={!hasCard?"Najpierw przypisz kartę (Krok 1)":"Zapisz URL logowania na tag NFC"}
                      style={{padding:"6px 12px",
                        background: !hasCard?"#e5e7eb":isProgram?"#7c3aed":"linear-gradient(135deg,#7c3aed,#5b21b6)",
                        color: !hasCard?T.muted:"#fff",border:"none",borderRadius:8,
                        cursor:!hasCard?"not-allowed":"pointer",fontSize:11,fontWeight:700,
                        boxShadow:!hasCard?"none":"0 2px 6px rgba(124,58,237,0.3)"}}>
                      📲 Zaprogramuj tag
                    </button>
                  )}

                  {/* Usuń kartę */}
                  {hasCard && !isActive && (
                    <button onClick={()=>removeCard(u.id)}
                      style={{padding:"5px 12px",background:"#fef2f2",color:"#dc2626",
                        border:"1px solid #fecaca",borderRadius:8,cursor:"pointer",fontSize:10,fontWeight:600}}>
                      🗑 Usuń kartę
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════════ FIREBASE SYNC PANEL ═══════════════════════════════════ */
function FirebaseSyncPanel({ fbConfig, setFbConfig, settings, setSettings }) {
  const [form, setForm] = useState({
    databaseURL:   fbConfig?.databaseURL   || "",
    storageBucket: fbConfig?.storageBucket || "",
    secret:        fbConfig?.secret        || "",
  });
  const [status,    setStatus]    = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const isConfigured   = !!(fbConfig?.databaseURL);
  const hasStorage     = !!(fbConfig?.storageBucket);

  const test = async () => {
    if (!form.databaseURL) { setStatus("error"); setStatusMsg("Podaj adres bazy danych"); return; }
    setStatus("testing"); setStatusMsg("Testuję połączenie...");
    try {
      // Testuj przez /api/db — wymaga tokenu, potwierdza że API i Firebase działają
      const res = await apiFetch(API_BASE);
      if (res.ok) {
        if (form.storageBucket) {
          const bucket = form.storageBucket.replace("gs://","").replace(/\/$/,"");
          const testUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o`;
          const sRes = await fetch(testUrl);
          if (sRes.ok || sRes.status === 401) {
            setStatus("ok"); setStatusMsg("✅ RTDB + Storage — oba połączenia działają!");
          } else {
            setStatus("ok"); setStatusMsg("✅ RTDB działa · ⚠ Storage: sprawdź storageBucket");
          }
        } else {
          setStatus("ok"); setStatusMsg("✅ Połączenie RTDB działa! Dodaj Storage Bucket dla zdjęć.");
        }
      } else {
        const text = await res.text();
        setStatus("error"); setStatusMsg("Błąd: " + (text || res.status));
      }
    } catch(e) {
      setStatus("error"); setStatusMsg("Błąd połączenia: " + e.message);
    }
  };

  const save = () => {
    if (!form.databaseURL) { setStatus("error"); setStatusMsg("Podaj adres bazy danych"); return; }
    const secretVal = form.secret.trim();
    // Jeśli pole secret jest puste, zachowaj stary secret z fbConfig (nie nadpisuj pustym)
    const finalSecret = secretVal || (fbConfig?.secret || "");
    const newCfg = {
      databaseURL:   form.databaseURL.trim(),
      storageBucket: form.storageBucket.trim(),
      secret:        finalSecret,
    };
    setFbConfig(newCfg);
    // Zapisz też bezpośrednio do localStorage żeby mieć pewność
    try { localStorage.setItem("smakidomu_firebase_config", JSON.stringify(newCfg)); } catch(e) {}
    try { localStorage.setItem("smakidomu_fbConfig", JSON.stringify(newCfg)); } catch(e) {}
    setStatus("ok"); setStatusMsg("Konfiguracja zapisana! Dane synchronizowane w czasie rzeczywistym.");
  };

  const clear = () => {
    setFbConfig(null);
    setForm({ databaseURL: "", storageBucket: "", secret: "" });
    setStatus(null); setStatusMsg("");
  };

  return (
    <div>
      {/* Status banner */}
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderRadius:10,marginBottom:12,
        background: isConfigured ? "#f0fdf4" : "#fff8e1",
        border: "1.5px solid " + (isConfigured ? "#86efac" : "#f0c040")}}>
        <span style={{fontSize:20}}>{isConfigured ? "🔴" : "⚠"}</span>
        <div style={{flex:1}}>
          <div style={{fontSize:12,fontWeight:700,color: isConfigured ? "#15803d" : "#856404"}}>
            {isConfigured ? "🟢 Live sync aktywny — WebSocket" : "Synchronizacja wyłączona"}
          </div>
          <div style={{fontSize:10,color:T.muted,marginTop:1}}>
            {isConfigured
              ? `RTDB: real-time push · Storage: ${hasStorage ? "✅ zdjęcia w chmurze" : "⚠ nieskonfigurowane (zdjęcia tylko lokalnie)"}`
              : "Dane zapisywane tylko lokalnie — nie widoczne na innych urządzeniach"}
          </div>
        </div>
        {isConfigured && (
          <button onClick={clear}
            style={{padding:"4px 10px",background:"#ffebee",color:"#c0392b",border:"none",borderRadius:7,fontWeight:700,cursor:"pointer",fontSize:11}}>
            Wyłącz
          </button>
        )}
      </div>

      {/* Instructions */}
      <details style={{marginBottom:12}}>
        <summary style={{fontSize:12,fontWeight:700,color:T.muted,cursor:"pointer",padding:"6px 0"}}>
          📖 Jak skonfigurować Firebase? (kliknij)
        </summary>
        <div style={{background:T.bg,borderRadius:10,padding:"12px 14px",marginTop:8,fontSize:11,color:T.muted,lineHeight:1.8,border:"1px solid "+T.border}}>
          <div style={{fontWeight:800,color:T.text,marginBottom:6}}>🗄 Realtime Database (zamówienia, menu, live sync):</div>
          <div>1. Wejdź na <strong>firebase.google.com</strong> → zaloguj się</div>
          <div>2. <strong>Create a project</strong> → nadaj nazwę</div>
          <div>3. Menu lewe → <strong>Build → Realtime Database → Create Database</strong></div>
          <div>4. Wybierz lokalizację <strong>Europe West</strong> → <em>Start in test mode</em></div>
          <div>5. Skopiuj URL bazy: <em style={{color:T.accent}}>https://nazwa-default-rtdb.europe-west1.firebasedatabase.app</em></div>
          <div style={{marginTop:10,fontWeight:800,color:T.text,marginBottom:6}}>🖼 Storage (zdjęcia dostępne na wszystkich urządzeniach):</div>
          <div>6. Menu lewe → <strong>Build → Storage → Get started</strong></div>
          <div>7. <em>Start in test mode</em> → wybierz lokalizację</div>
          <div>8. Skopiuj bucket: <em style={{color:T.accent}}>nazwa-projekt.appspot.com</em> (z górnego paska Storage)</div>
          <div style={{marginTop:10,color:"#c0392b",fontWeight:600}}>⚠ Test mode wygasa po 30 dniach — ustaw reguły lub dodaj Database Secret</div>
        </div>
      </details>

      {/* Form fields */}
      <div style={{marginBottom:8}}>
        <label style={S.label}>Adres bazy danych (Realtime Database URL) *</label>
        <input style={{...S.inp, fontFamily:"monospace", fontSize:12}}
          placeholder="https://twoja-baza-default-rtdb.europe-west1.firebasedatabase.app"
          value={form.databaseURL}
          onChange={e=>setForm(p=>({...p,databaseURL:e.target.value}))}
        />
      </div>

      <div style={{marginBottom:8}}>
        <label style={S.label}>
          Klucz API Imgbb — zdjęcia w chmurze&nbsp;
          <span style={{background:settings?.imgbbApiKey?"#dcfce7":"#fff3cd",color:settings?.imgbbApiKey?"#15803d":"#856404",
            borderRadius:10,padding:"1px 7px",fontWeight:700,fontSize:10}}>
            {settings?.imgbbApiKey ? "✅ aktywne" : "opcjonalnie"}
          </span>
        </label>
        <input style={{...S.inp, fontFamily:"monospace", fontSize:12}} type="password"
          placeholder="Klucz z api.imgbb.com"
          value={settings?.imgbbApiKey||""}
          onChange={e=>setSettings(p=>({...p,imgbbApiKey:e.target.value.trim()}))}
        />
        <div style={{fontSize:10,color:T.muted,marginTop:3}}>
          Bez tego zdjęcia są widoczne tylko na urządzeniu gdzie zostały wgrane. Zarejestruj się na imgbb.com → api.imgbb.com → skopiuj klucz.
        </div>
      </div>

      <div style={{marginBottom:12}}>
        <label style={S.label}>Database Secret (opcjonalnie — dla ochrony danych)</label>
        <input style={{...S.inp, fontFamily:"monospace", fontSize:12}} type="password"
          placeholder="Project Settings → Service Accounts → Database secrets"
          value={form.secret}
          onChange={e=>setForm(p=>({...p,secret:e.target.value}))}
        />
      </div>

      {status && (
        <div style={{marginBottom:10,padding:"7px 12px",borderRadius:9,fontSize:12,fontWeight:600,
          background: status==="ok"?"#f0fdf4":status==="testing"?"#fff8e1":"#ffebee",
          color: status==="ok"?"#15803d":status==="testing"?"#856404":"#c0392b",
          border: "1px solid "+(status==="ok"?"#86efac":status==="testing"?"#f0c040":"#fca5a5")}}>
          {status==="testing"?"⏳":status==="ok"?"✅":"❌"} {statusMsg}
        </div>
      )}

      <div style={{display:"flex",gap:7}}>
        <button style={{...S.darkBtn,flex:1}} onClick={save}>💾 Zapisz i aktywuj</button>
        <button style={S.cancelBtn} onClick={test}>🔌 Testuj</button>
      </div>
    </div>
  );
}

/* ════════════════════ SALES REPORT PDF ══════════════════════════════════════ */
function generateSalesReport(orders, settings, period) {
  const fmt2 = n => Number(n).toFixed(2);
  const name  = settings.restaurantName || "Restauracja";
  const now   = new Date();

  // Wyznacz zakres dat
  let dateFrom, dateTo, periodLabel;
  if (period === "day") {
    dateFrom = dateTo = now.toISOString().split("T")[0];
    periodLabel = "Raport dzienny — " + now.toLocaleDateString("pl-PL", {weekday:"long", year:"numeric", month:"long", day:"numeric"});
  } else if (period === "week") {
    const day = now.getDay() === 0 ? 6 : now.getDay() - 1;
    const mon = new Date(now); mon.setDate(now.getDate() - day);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    dateFrom = mon.toISOString().split("T")[0];
    dateTo   = sun.toISOString().split("T")[0];
    periodLabel = "Raport tygodniowy — " + mon.toLocaleDateString("pl-PL") + " – " + sun.toLocaleDateString("pl-PL");
  } else {
    const y = now.getFullYear(), m = now.getMonth();
    dateFrom = new Date(y, m, 1).toISOString().split("T")[0];
    dateTo   = new Date(y, m+1, 0).toISOString().split("T")[0];
    periodLabel = "Raport miesięczny — " + now.toLocaleDateString("pl-PL", {year:"numeric", month:"long"});
  }

  const filtered = orders.filter(o => o.date >= dateFrom && o.date <= dateTo);
  const totalRev = filtered.reduce((s,o) => s+o.total, 0);
  const totalQty = filtered.reduce((s,o) => s+o.cart.reduce((a,c)=>a+c.qty,0), 0);
  const avgOrder = filtered.length ? totalRev / filtered.length : 0;

  // Top produkty
  const prodMap = {};
  filtered.forEach(o => o.cart.forEach(c => {
    if (!prodMap[c.name]) prodMap[c.name] = { name:c.name, qty:0, rev:0 };
    prodMap[c.name].qty += c.qty;
    prodMap[c.name].rev += (c.promoPrice!=null?c.promoPrice:c.price) * c.qty;
  }));
  const topProds = Object.values(prodMap).sort((a,b)=>b.rev-a.rev).slice(0,10);

  // Przychód wg metody płatności
  const payMethods = [
    {key:"blik",   label:"BLIK",    color:"#e94560"},
    {key:"card",   label:"Karta",   color:"#0984e3"},
    {key:"cash",   label:"Gotówka", color:"#27ae60"},
    {key:"paypal", label:"PayPal",  color:"#003087"},
  ];
  const payRows = payMethods.map(pm => {
    const cnt = filtered.filter(o=>o.paymentMethod===pm.key).length;
    const rev = filtered.filter(o=>o.paymentMethod===pm.key).reduce((s,o)=>s+o.total,0);
    return cnt > 0 ? `<tr><td>${pm.label}</td><td style="text-align:center;">${cnt}</td><td style="text-align:right;font-weight:700;color:${pm.color};">${fmt2(rev)} zl</td></tr>` : "";
  }).join("");

  // Przychód dzienny (dla tygodnia/miesiąca)
  const dayMap = {};
  filtered.forEach(o => {
    dayMap[o.date] = dayMap[o.date] || { cnt:0, rev:0 };
    dayMap[o.date].cnt++;
    dayMap[o.date].rev += o.total;
  });
  const dayRows = Object.entries(dayMap).sort(([a],[b])=>a.localeCompare(b)).map(([date, d]) => {
    const dObj = new Date(date + "T12:00:00");
    const label = dObj.toLocaleDateString("pl-PL", {weekday:"short", day:"numeric", month:"short"});
    return `<tr><td>${label}</td><td style="text-align:center;">${d.cnt}</td><td style="text-align:right;font-weight:700;">${fmt2(d.rev)} zl</td></tr>`;
  }).join("");

  const topProdRows = topProds.map((p,i) =>
    `<tr><td style="color:#6B3F1E;font-weight:700;">#${i+1}</td><td>${p.name}</td><td style="text-align:center;">${p.qty}</td><td style="text-align:right;font-weight:700;">${fmt2(p.rev)} zl</td></tr>`
  ).join("");

  const genDate = now.toLocaleDateString("pl-PL") + " " + now.toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"});
  const fileName = `raport_${period}_${dateFrom}.pdf`;

  const html = `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8"/>
  <title>Raport sprzedaży — ${name}</title>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
  <style>
    * { box-sizing:border-box; margin:0; padding:0; }
    body { font-family:'Segoe UI',system-ui,sans-serif; background:#f0f2f5; color:#222; padding:24px; display:flex; flex-direction:column; align-items:center; }
    .report { width:680px; background:#fff; border-radius:14px; overflow:hidden; box-shadow:0 4px 24px rgba(0,0,0,0.10); }
    .header { background:linear-gradient(135deg,#3B1F0A,#6B3F1E); color:#fff; padding:28px 32px; }
    .header h1 { font-size:22px; font-weight:900; letter-spacing:0.5px; margin-bottom:4px; }
    .header .sub { font-size:13px; opacity:0.8; }
    .header .period { font-size:14px; font-weight:700; margin-top:8px; background:rgba(255,255,255,0.15); display:inline-block; padding:4px 14px; border-radius:20px; }
    .body { padding:28px 32px; }
    .kpi-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:28px; }
    .kpi { background:#FDF6EC; border-radius:10px; padding:16px 12px; text-align:center; border-top:3px solid #C8621A; }
    .kpi .val { font-size:20px; font-weight:900; color:#3B1F0A; margin-bottom:4px; }
    .kpi .lbl { font-size:10px; color:#8B6A4A; text-transform:uppercase; letter-spacing:0.5px; }
    h2 { font-size:13px; font-weight:800; color:#3B1F0A; margin-bottom:10px; padding-bottom:5px; border-bottom:2px solid #E8D5BA; }
    table { width:100%; border-collapse:collapse; font-size:12px; margin-bottom:24px; }
    th { background:#FDF6EC; color:#8B6A4A; font-size:10px; text-transform:uppercase; letter-spacing:0.5px; padding:7px 10px; text-align:left; }
    td { padding:7px 10px; border-bottom:1px solid #F0E8DC; }
    tr:last-child td { border-bottom:none; }
    .footer-bar { background:#FDF6EC; border-top:1px solid #E8D5BA; padding:12px 32px; font-size:10px; color:#8B6A4A; display:flex; justify-content:space-between; }
    .btn-bar { margin-top:20px; display:flex; gap:10px; }
    .btn-dl { padding:11px 30px; background:#6B3F1E; color:#fff; border:none; border-radius:8px; font-size:14px; font-weight:700; cursor:pointer; }
    .btn-cl { padding:11px 20px; background:#eee; color:#555; border:none; border-radius:8px; font-size:14px; cursor:pointer; }
    .empty { text-align:center; padding:20px; color:#bbb; font-size:12px; }
  </style>
</head>
<body>
  <div class="report" id="report-content">
    <div class="header">
      <h1>${name}</h1>
      <div class="sub">Raport sprzedaży</div>
      <div class="period">${periodLabel}</div>
    </div>
    <div class="body">
      <div class="kpi-grid">
        <div class="kpi"><div class="val">${filtered.length}</div><div class="lbl">Zamówień</div></div>
        <div class="kpi"><div class="val">${fmt2(totalRev)} zl</div><div class="lbl">Przychód</div></div>
        <div class="kpi"><div class="val">${totalQty}</div><div class="lbl">Sprzedanych poz.</div></div>
        <div class="kpi"><div class="val">${fmt2(avgOrder)} zl</div><div class="lbl">Śr. zamówienie</div></div>
      </div>

      ${period !== "day" && dayRows ? `
      <h2>Sprzedaż dzień po dniu</h2>
      <table>
        <thead><tr><th>Dzień</th><th style="text-align:center;">Zamówień</th><th style="text-align:right;">Przychód</th></tr></thead>
        <tbody>${dayRows}</tbody>
      </table>` : ""}

      <h2>Metody płatności</h2>
      ${payRows ? `<table><thead><tr><th>Metoda</th><th style="text-align:center;">Zamówień</th><th style="text-align:right;">Przychód</th></tr></thead><tbody>${payRows}</tbody></table>`
               : `<div class="empty">Brak danych</div>`}

      <h2>Top produkty</h2>
      ${topProdRows ? `<table><thead><tr><th>#</th><th>Produkt</th><th style="text-align:center;">Szt.</th><th style="text-align:right;">Przychód</th></tr></thead><tbody>${topProdRows}</tbody></table>`
                   : `<div class="empty">Brak danych</div>`}
    </div>
    <div class="footer-bar">
      <span>Wygenerowano: ${genDate}</span>
      <span>${name}</span>
    </div>
  </div>
  <div class="btn-bar">
    <button class="btn-dl" id="btn-pdf">&#11015; Pobierz PDF</button>
    <button class="btn-cl" onclick="window.close()">&#10005; Zamknij</button>
  </div>
  <script>
    document.getElementById('btn-pdf').addEventListener('click', function() {
      var btn = this;
      btn.disabled = true;
      btn.textContent = 'Generowanie...';
      html2pdf().set({
        margin: [8,8,8,8],
        filename: '${fileName}',
        image: { type:'jpeg', quality:0.98 },
        html2canvas: { scale:2, useCORS:true },
        jsPDF: { unit:'mm', format:'a4', orientation:'portrait' }
      }).from(document.getElementById('report-content')).save().then(function() {
        btn.disabled = false;
        btn.textContent = '\u2713 Pobrano!';
        setTimeout(function(){ btn.textContent = '\u2B07 Pobierz PDF'; }, 2500);
      });
    });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank", "width=780,height=750,scrollbars=yes");
  if (w) { w.document.write(html); w.document.close(); }
}

/* ════════════════════ MANUAL ORDER MODAL ════════════════════════════════════
   Ręczne dodawanie zamówień przez sprzedawcę / admina.
   Obsługuje: stolik wewnętrzny, stolik zewnętrzny, wynos, dojazd, rezerwacja.
   Zamówienie trafia do kolejki identycznie jak zamówienie od klienta.
══════════════════════════════════════════════════════════════════════════════ */
function ManualOrderModal({ menu, cats, tables, orders, settings, onPlace, onClose, clients, preselectedClient, onlineMenu, onlineCats, setLogs, currentUser }) {
  const ac = settings.accentColor;
  const bp = useBreakpoint();

  /* ── typ zamówienia ── */
  // "indoor" | "outdoor" | "takeaway" | "delivery" | "reservation"
  const [orderType,   setOrderType]   = useState(null);

  /* ── stolik ── */
  const [selTable,    setSelTable]    = useState(null);
  const [tableSection,setTableSection]= useState("indoor"); // "indoor" | "outdoor"

  /* ── dane klienta (wynos / dojazd) ── */
  const [guestForm,   setGuestForm]   = useState({ firstName:"", lastName:"", phone:"", address:"", zoneId:"" });

  /* ── rezerwacja ── */
  const [resForm,     setResForm]     = useState({ name:"", phone:"", date:"", time:"", guests:"2", note:"" });

  /* ── koszyk ── */
  const [cart,        setCart]        = useState([]);
  const [orderNote,   setOrderNote]   = useState("");
  const [payment,     setPayment]     = useState("cash");
  const [search,      setSearch]      = useState("");

  /* ── kontrahent ── */
  const [selClient,   setSelClient]   = useState(preselectedClient || null);
  const [clientSearch,setClientSearch]= useState("");
  const [showClients, setShowClients] = useState(false);

  /* ── faktura ── */
  const [wantInvoice,  setWantInvoice]  = useState(false);
  const [sendKSeFNow,  setSendKSeFNow]  = useState(false);
  const [invForm,      setInvForm]      = useState({
    buyerName:"", buyerNip:"", buyerAddress:"", vatRate:"8"
  });
  /* Auto-wypełnij dane faktury gdy wybrano kontrahenta */
  useEffect(() => {
    if (!selClient) return;
    setInvForm(p => ({
      ...p,
      buyerName:    selClient.company || selClient.name || p.buyerName,
      buyerNip:     selClient.nip     || p.buyerNip,
      buyerAddress: selClient.address || p.buyerAddress,
    }));
  }, [selClient]);

  /* Autouzupełnianie formularzy po wybraniu / zmianie kontrahenta (działa też dla preselectedClient) */
  useEffect(() => {
    if (!selClient) return;
    const nameParts = (selClient.name || "").trim().split(" ");
    const autoFirst = nameParts[0] || "";
    const autoLast  = nameParts.slice(1).join(" ") || "";
    // Buduj adres: najpierw pole skrócone, a jeśli puste — złóż z pól szczegółowych
    const autoAddress = selClient.address ||
      ([
        [selClient.street, selClient.buildingNo].filter(Boolean).join(" "),
        [selClient.postalCode, selClient.city].filter(Boolean).join(" "),
      ].filter(Boolean).join(", ")) || "";
    setGuestForm(p => ({
      ...p,
      firstName: autoFirst,
      lastName:  autoLast,
      phone:     selClient.phone   || p.phone,
      address:   autoAddress       || p.address,
    }));
    setResForm(p => ({
      ...p,
      name:  selClient.name  || p.name,
      phone: selClient.phone || p.phone,
    }));
  }, [selClient]);

  /* ── krok (1 = dane klienta, 2 = produkty/płatność) ── */
  const [step, setStep] = useState(1);

  /* ── źródło menu: "restaurant" | "online" | "bar" ── */
  const [menuSource, setMenuSource] = useState("restaurant");

  /* ── addon modal ── */
  const [addonModal,  setAddonModal]  = useState(null);

  const activeMenu        = menu.filter(i => i.active);
  const onlineMenuActive  = (onlineMenu||[]).filter(i => i.active !== false);
  /* Menu barowe: produkty oznaczone jako barowe lub z kategorii bar */
  const barMenu           = activeMenu.filter(i => {
    if (i.bar || i.isBar) return true;
    const cat = (cats||[]).find(c => c.id === i.catId);
    return cat && (cat.bar || cat.isBar || (cat.name||"").toLowerCase().includes("bar") || (cat.name||"").toLowerCase().includes("drink") || (cat.name||"").toLowerCase().includes("alkohol") || (cat.name||"").toLowerCase().includes("napoj") || (cat.name||"").toLowerCase().includes("napój"));
  });

  /* Aktywne menu wg wybranego źródła */
  const sourceMenu =
    menuSource === "online" ? onlineMenuActive :
    menuSource === "bar"    ? (barMenu.length > 0 ? barMenu : activeMenu) :
    activeMenu;

  const today        = todayStr();
  const activeOrders = orders.filter(o => o.date === today);
  const occupiedIds  = new Set(activeOrders.map(o => o.table));
  const activeTables = tables.filter(t => t.active);
  const indoorTables = activeTables.filter(t => t.type !== "outdoor");
  const outdoorTables= activeTables.filter(t => t.type === "outdoor");
  const zones        = settings?.deliveryZones || [];

  const needsTable  = orderType === "indoor" || orderType === "outdoor" || orderType === "reservation";
  const needsGuest  = orderType === "takeaway" || orderType === "delivery";
  const isReservation = orderType === "reservation";
    // eslint-disable-next-line no-unused-vars
  const currentTables = orderType === "outdoor" ? outdoorTables : indoorTables;

  const filtered = search.trim()
    ? sourceMenu.filter(i =>
        i.name.toLowerCase().includes(search.toLowerCase()) ||
        (i.desc||"").toLowerCase().includes(search.toLowerCase()))
    : sourceMenu;

  /* ── helpers koszyka ── */
  const addItem = item => {
    if (item.addons && item.addons.length > 0) {
      setAddonModal({ item, selectedAddons: {} });
      return;
    }
    setCart(p => {
      const ex = p.find(c => c.cartKey === item.id || c.id === item.id);
      return ex ? p.map(c => (c.cartKey||c.id)===item.id ? {...c, qty:c.qty+1} : c)
                : [...p, {...item, qty:1, note:"", cartKey:item.id}];
    });
  };

  const addItemWithAddons = (item, selectedAddons) => {
    const addonsArr = Object.entries(selectedAddons)
      .filter(([,qty])=>qty>0)
      .map(([id,qty])=>{
        const a = item.addons.find(x=>x.id===id);
        return a ? {...a, qty} : null;
      }).filter(Boolean);
    const addonsPrice = addonsArr.reduce((s,a)=>s+a.price*a.qty,0);
    const cartKey = item.id + (addonsArr.length ? "_"+addonsArr.map(a=>a.id+"x"+a.qty).join("_") : "");
    const effectivePrice = (item.promoPrice??item.price) + addonsPrice;
    setCart(p => {
      const ex = p.find(c => c.cartKey === cartKey);
      return ex ? p.map(c => c.cartKey===cartKey ? {...c, qty:c.qty+1} : c)
                : [...p, {...item, qty:1, note:"", cartKey, selectedAddons:addonsArr, addonsPrice, effectivePrice}];
    });
    setAddonModal(null);
  };

  const remItem = id => setCart(p => {
    const ex = p.find(c => (c.cartKey||c.id) === id);
    return ex?.qty === 1 ? p.filter(c => (c.cartKey||c.id) !== id)
                         : p.map(c => (c.cartKey||c.id)===id ? {...c, qty:c.qty-1} : c);
  });
  const setNote = (id, note) => setCart(p => p.map(c => (c.cartKey||c.id)===id ? {...c,note} : c));

  const totalPrice   = cart.reduce((s,c) => s + (c.effectivePrice ?? ep(c))*c.qty, 0);
  const deliveryCost = orderType==="delivery" && guestForm.zoneId
    ? (zones.find(z=>z.id===guestForm.zoneId)?.price||0) : 0;
  const finalTotal   = totalPrice + deliveryCost;
  const totalWait    = cart.length ? Math.max(...cart.map(c => c.wait||10)) : 0;
  const totalQty     = cart.reduce((s,c) => s+c.qty, 0);

  const canPlaceTable = needsTable && selTable && cart.length > 0;
  const canPlaceGuest = needsGuest && guestForm.firstName.trim() && guestForm.phone.trim() && cart.length > 0
    && (orderType!=="delivery" || guestForm.address.trim());
  const canPlaceRes = isReservation && selTable && resForm.name.trim() && resForm.time.trim();
  const canPlace = canPlaceTable || canPlaceGuest || canPlaceRes;

  /* ── place order ── */
  const place = () => {
    if (!canPlace) return;
    logActivity(setLogs, currentUser, "Złożył nowe zamówienie", orderType + " · " + fmt(finalTotal) + " zł");
    const tbl = activeTables.find(t => t.id === selTable);
    const now = new Date();
    const timeStr = now.toLocaleTimeString("pl-PL", {hour:"2-digit",minute:"2-digit"});

    /* helper — buduje fakturę z zamówienia i opcjonalnie wysyła do KSeF */
    const buildAndSendInvoice = async (order) => {
      if (!wantInvoice || !invForm.buyerName.trim()) return;
      const y = now.getFullYear(), m = now.getMonth()+1;
      const iso = now.toISOString().slice(0,10);
      const due = new Date(now); due.setDate(due.getDate()+14);

      // Pobierz aktualne faktury z localStorage do numeracji
      let existingInvs = [];
      try { existingInvs = JSON.parse(localStorage.getItem("smakidomu_ksef_invoices")||"[]"); } catch(e){}

      const invNumber = nextInvoiceNumber(existingInvs, y, m);
      const lines = (order.cart||[]).map(c=>({
        id: genId(),
        name: c.name || "Produkt",
        unit: "szt.",
        qty: String(c.qty||1),
        price: String((c.effectivePrice??c.price)||0),
        vat: invForm.vatRate,
      }));

      const newInv = {
        id: genId(),
        number: invNumber,
        issueDate: iso,
        saleDate: iso,
        dueDate: due.toISOString().slice(0,10),
        buyerName: invForm.buyerName.trim(),
        buyerNip: invForm.buyerNip,
        buyerAddress: invForm.buyerAddress,
        paymentMethod: payment==="cash"?"gotówka":payment==="card"?"karta":"blik",
        notes: `Zamówienie #${order.tableNumber||order.table||""} z dnia ${iso}`,
        status: "issued",
        lines,
        linkedOrderId: order.id,
      };

      // Zapisz fakturę w localStorage
      const updatedInvs = [...existingInvs, newInv];
      try { localStorage.setItem("smakidomu_ksef_invoices", JSON.stringify(updatedInvs)); } catch(e){}

      // Wyślij do KSeF jeśli opcja zaznaczona i API skonfigurowane
      if (sendKSeFNow && settings?.ksefSeller?.apiKeyId && settings?.ksefSeller?.apiKey && settings?.ksefSeller?.ksefToken) {
        try {
          const s = settings.ksefSeller;
          const base = s.apiEnv==="prod" ? "https://ksefapi.pl/api" : "https://ksefapi.pl/api-test";
          const auth = "Basic " + btoa(s.apiKeyId + ":" + s.apiKey);
          const xml = generateKSeFXml(newInv, settings);

          const sessRes = await fetch(base + "/session/open", {
            method:"POST", headers:{"Authorization":auth,"Content-Type":"application/json"},
            body: JSON.stringify({ksefToken: s.ksefToken})
          });
          const sessData = await sessRes.json().catch(()=>null);
          const sessionId = sessData?.sessionId || sessData?.SessionId;

          const sendRes = await fetch(base + "/invoice/send", {
            method:"POST",
            headers:{"Authorization":auth,"Content-Type":"application/xml",...(sessionId?{"X-Session-Id":sessionId}:{})},
            body: xml
          });
          const sendData = await sendRes.json().catch(()=>null);
          const ksefNumber = sendData?.ksefNumber || sendData?.KsefNumber || sendData?.referenceNumber || sendData?.invoiceId || "—";

          // Zaktualizuj fakturę w localStorage z numerem KSeF
          const withKSef = updatedInvs.map(x=>x.id===newInv.id?{...x,ksefNumber,ksefSentAt:new Date().toLocaleString("pl-PL"),ksefEnv:s.apiEnv||"test",ksefSessionId:sessionId}:x);
          try { localStorage.setItem("smakidomu_ksef_invoices", JSON.stringify(withKSef)); } catch(e){}
        } catch(e) {
          // eslint-disable-next-line no-console

          // eslint-disable-next-line no-console
          console.warn("KSeF send error:", e.message);
        }
      }

      // Oznacz zamówienie jako mające fakturę
      return newInv.id;
    };

    // Rezerwacja — zapisujemy jako rezerwację na stoliku, bez produktów w kolejce
    if (isReservation && tbl) {
      // Zapisujemy rezerwację bezpośrednio na stolicy — onPlace obsłuży aktualizację tables
      // Tworzymy też zamówienie-rezerwację dla widoczności w kolejce
      const order = {
        id: genId(),
        cart: cart.map(ci => ({
          id:ci.id, name:ci.name, emoji:ci.emoji, image:ci.image||null,
          price:ci.price, promoPrice:ci.promoPrice??null,
          effectivePrice:ci.effectivePrice,
          selectedAddons:ci.selectedAddons||[], addonsPrice:ci.addonsPrice||0,
          cartKey:ci.cartKey||ci.id, qty:ci.qty, wait:ci.wait||10, catId:ci.catId, note:ci.note||"",
        })),
        total: finalTotal, totalBeforeDiscount: finalTotal,
        discount:0, voucherCode:null,
        orderNote: orderNote.trim(),
        wait: totalWait,
        table: selTable, tableNumber: tbl.number, tableType: tbl.type||"indoor",
        date: today, time: timeStr,
        paymentMethod: payment, manualOrder:true, cookStatus:"new", issued:false,
        paid: payment !== "cash",
        isReservation: true,
        reservationName: resForm.name.trim(),
        reservationPhone: resForm.phone.trim(),
        reservationDate: resForm.date || today,
        reservationTime: resForm.time,
        reservationGuests: parseInt(resForm.guests)||2,
        reservationNote: resForm.note.trim(),
        ...(selClient?{clientId:selClient.id,clientName:selClient.name,clientPhone:selClient.phone||"",clientDiscount:selClient.discount||0}:{}),
        ...(wantInvoice?{invoiceRequest:{buyerName:invForm.buyerName.trim(),buyerNip:invForm.buyerNip,buyerAddress:invForm.buyerAddress,vatRate:invForm.vatRate}}:{}),
      };
      onPlace(order);
      buildAndSendInvoice(order);
      return;
    }

    // Wynos / dojazd
    if (needsGuest) {
      const selectedZone = zones.find(z=>z.id===guestForm.zoneId)||null;
      const order = {
        id:genId(), takeaway:true, delivery:orderType==="delivery",
        firstName:guestForm.firstName.trim(), lastName:guestForm.lastName.trim(),
        phone:guestForm.phone.trim(), address:guestForm.address.trim(),
        note:guestForm.note?.trim()||"",
        deliveryZone: selectedZone?selectedZone.name:null,
        deliveryCost,
        cart: cart.map(ci => ({
          id:ci.id, name:ci.name, emoji:ci.emoji, image:ci.image||null,
          price:ci.price, promoPrice:ci.promoPrice??null,
          effectivePrice:ci.effectivePrice,
          selectedAddons:ci.selectedAddons||[], addonsPrice:ci.addonsPrice||0,
          cartKey:ci.cartKey||ci.id, qty:ci.qty, wait:ci.wait||10, catId:ci.catId, note:ci.note||"",
        })),
        total: finalTotal, itemsTotal: totalPrice,
        totalBeforeDiscount: finalTotal, discount:0, voucherCode:null,
        orderNote: orderNote.trim(),
        wait: orderType==="delivery"?45:20,
        table: orderType==="delivery"?"dowóz":"wynos",
        tableNumber: orderType==="delivery"?"dowóz":"wynos",
        date: today, time: timeStr,
        paymentMethod: payment, manualOrder:true, cookStatus:"new", issued:false,
        paid: payment !== "cash",
        ...(selClient?{clientId:selClient.id,clientName:selClient.name,clientPhone:selClient.phone||"",clientDiscount:selClient.discount||0}:{}),
        ...(wantInvoice?{invoiceRequest:{buyerName:invForm.buyerName.trim(),buyerNip:invForm.buyerNip,buyerAddress:invForm.buyerAddress,vatRate:invForm.vatRate}}:{}),
      };
      onPlace(order);
      buildAndSendInvoice(order);
      return;
    }

    // Stolik wewnętrzny / zewnętrzny
    const order = {
      id: genId(),
      cart: cart.map(ci => ({
        id:ci.id, name:ci.name, emoji:ci.emoji, image:ci.image||null,
        price:ci.price, promoPrice:ci.promoPrice??null,
        effectivePrice:ci.effectivePrice,
        selectedAddons:ci.selectedAddons||[], addonsPrice:ci.addonsPrice||0,
        cartKey:ci.cartKey||ci.id, qty:ci.qty, wait:ci.wait||10, catId:ci.catId, note:ci.note||"",
      })),
      total: finalTotal, totalBeforeDiscount: finalTotal,
      discount:0, voucherCode:null,
      orderNote: orderNote.trim(),
      wait: totalWait,
      table: selTable, tableNumber: (tbl?.number ?? selTable), tableType: (tbl?.type||"indoor"),
      date: today, time: timeStr,
      paymentMethod: payment, manualOrder:true, cookStatus:"new", issued:false,
      paid: payment !== "cash",
      ...(selClient?{clientId:selClient.id,clientName:selClient.name,clientPhone:selClient.phone||"",clientDiscount:selClient.discount||0}:{}),
      ...(wantInvoice?{invoiceRequest:{buyerName:invForm.buyerName.trim(),buyerNip:invForm.buyerNip,buyerAddress:invForm.buyerAddress,vatRate:invForm.vatRate}}:{}),
    };
    onPlace(order);
    buildAndSendInvoice(order);
  };

  /* ── order type definitions ── */
  const ORDER_TYPES = [
    { key:"indoor",      icon:"🏠", label:"Stolik wewnętrzny",  color:"#2563eb", bg:"#eff6ff" },
    { key:"outdoor",     icon:"🌿", label:"Stolik zewnętrzny",  color:"#15803d", bg:"#f0fdf4" },
    { key:"takeaway",    icon:"🏃", label:"Na wynos",           color:"#d97706", bg:"#fef9c3" },
    { key:"delivery",    icon:"🚗", label:"Dojazd/Dostawa",     color:"#0369a1", bg:"#e0f2fe" },
    { key:"reservation", icon:"🔒", label:"Rezerwacja",         color:"#7c3aed", bg:"#f5f3ff" },
  ];

  /* ── table grid renderer ── */
  const renderTableGrid = (tableList) => {
    const cols = bp.xs ? 4 : bp.sm ? 5 : 6;
    return (
      <div style={{display:"grid",gridTemplateColumns:`repeat(${cols},1fr)`,gap:6}}>
        {tableList.map(t => {
          const busy = t.manuallyFreed ? false : (t.occupied || occupiedIds.has(t.id) || occupiedIds.has(t.number));
          const isSel = selTable === t.id;
          return (
            <button key={t.id}
              onClick={() => setSelTable(t.id)}
              style={{
                padding:"9px 4px", borderRadius:10, border:"2px solid",
                fontWeight:700, fontSize:12, cursor:"pointer", transition:"all .12s",
                position:"relative",
                borderColor: isSel ? ac : busy ? "#e94560" : "#ddd",
                background:  isSel ? ac  : busy ? "#fde8ec" : T.bg,
                color:       isSel ? "#fff" : busy ? "#e94560" : T.text,
                boxShadow:   isSel ? `0 2px 8px ${ac}55` : "none",
              }}>
              {t.number}
              {busy && !isSel && <span style={{position:"absolute",top:2,right:2,fontSize:7}}>🍽</span>}
              {t.reservation && !busy && !isSel && <span style={{position:"absolute",top:2,right:2,fontSize:7}}>🔒</span>}
            </button>
          );
        })}
        {tableList.length === 0 && (
          <div style={{gridColumn:`span ${cols}`,textAlign:"center",padding:"16px 0",color:T.muted,fontSize:12}}>
            Brak stolików
          </div>
        )}
      </div>
    );
  };

  const selTableObj = activeTables.find(t => t.id === selTable);

  /* ── walidacja kroku 1 ── */
  const canGoStep2 = (() => {
    if (!orderType) return false;
    if (needsTable && !selTable) return false;
    if (needsGuest && (!guestForm.firstName.trim() || !guestForm.phone.trim())) return false;
    if (isReservation && (!selTable || !resForm.name.trim() || !resForm.time.trim())) return false;
    return true;
  })();

  /* ── hint in footer ── */
  const getHint = () => {
    if (step === 1) {
      if (!orderType) return "⬆ Wybierz typ zamówienia";
      if (needsTable && !selTable) return "⬆ Wybierz stolik";
      if (needsGuest && (!guestForm.firstName.trim()||!guestForm.phone.trim())) return "⬆ Uzupełnij dane klienta";
      if (isReservation && (!selTable||!resForm.name.trim()||!resForm.time.trim())) return "⬆ Wybierz stolik i uzupełnij dane";
      return null;
    }
    if (step === 2) {
      if (!cart.length && !isReservation) return "⬆ Dodaj przynajmniej jeden produkt";
      return null;
    }
    return null;
  };
  const hint = getHint();

  /* ── place button label ── */
  const canGoStep3 = step === 2 && (cart.length > 0 || isReservation);

  const placeBtnLabel = () => {
    if (step === 1) return canGoStep2 ? "Dalej → Wybierz produkty" : "Uzupełnij dane";
    if (step === 2) return canGoStep3 ? "Dalej → Płatność i uwagi" : "Dodaj produkty";
    if (isReservation && canPlaceRes) return `🔒 Dodaj rezerwację · Stolik #${selTableObj?.number}`;
    if (needsGuest && canPlaceGuest) return `✅ Złóż zamówienie · ${fmt(finalTotal)} zł`;
    if (canPlaceTable) return `✅ Złóż zamówienie · ${fmt(finalTotal)} zł · Stolik #${selTableObj?.number}`;
    return "Uzupełnij zamówienie";
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(20,10,0,0.88)",zIndex:600,
      display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto"}}>

      {/* ADDON MODAL */}
      {addonModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.72)",zIndex:700,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:"20px 20px 0 0",width:"100%",maxWidth:520,maxHeight:"82vh",overflowY:"auto",padding:"20px 20px 32px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div>
                <div style={{fontWeight:800,fontSize:16,color:"#111"}}>{addonModal.item.emoji} {addonModal.item.name}</div>
                <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>Cena bazowa: {Number(addonModal.item.promoPrice??addonModal.item.price).toFixed(2)} zł</div>
              </div>
              <button onClick={()=>setAddonModal(null)} style={{background:"#f3f4f6",border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>
            <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:8}}>Wybierz dodatki:</div>
            {addonModal.item.addons.map(addon=>{
              const qty = addonModal.selectedAddons[addon.id]||0;
              return (
                <div key={addon.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+(qty>0?ac:"#e5e7eb"),background:qty>0?"#fff8f0":"#f9fafb",marginBottom:6,transition:"all .15s"}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:13}}>{addon.name}</div>
                    <div style={{fontSize:11,color:qty>0?ac:"#6b7280",fontWeight:qty>0?700:400}}>{addon.price===0?"Gratis":"+"+Number(addon.price).toFixed(2)+" zł"}{addon.max>1&&<span style={{color:"#9ca3af",marginLeft:6}}>max {addon.max} szt.</span>}</div>
                  </div>
                  {addon.max===1?(
                    <button onClick={()=>setAddonModal(p=>({...p,selectedAddons:{...p.selectedAddons,[addon.id]:qty?0:1}}))}
                      style={{width:30,height:30,borderRadius:7,border:"2px solid "+(qty?ac:"#d1d5db"),background:qty?ac:"#fff",cursor:"pointer",color:qty?"#fff":"#6b7280",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {qty?"✓":""}
                    </button>
                  ):(
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <button onClick={()=>setAddonModal(p=>({...p,selectedAddons:{...p.selectedAddons,[addon.id]:Math.max(0,qty-1)}}))}
                        style={{width:28,height:28,borderRadius:6,border:"1.5px solid #d1d5db",background:"#fff",cursor:"pointer",fontWeight:700,fontSize:15,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                      <span style={{fontWeight:700,fontSize:14,minWidth:18,textAlign:"center",color:qty>0?ac:"#111"}}>{qty}</span>
                      <button onClick={()=>setAddonModal(p=>({...p,selectedAddons:{...p.selectedAddons,[addon.id]:Math.min(addon.max,qty+1)}}))}
                        style={{width:28,height:28,borderRadius:6,border:"1.5px solid "+ac,background:ac,cursor:"pointer",fontWeight:700,fontSize:15,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{marginTop:14,padding:"10px 12px",background:"#f9fafb",borderRadius:10,border:"1px solid #e5e7eb",marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",fontWeight:800,fontSize:14,color:"#111"}}>
                <span>Razem:</span>
                <span style={{color:ac}}>{(Number(addonModal.item.promoPrice??addonModal.item.price)+Object.entries(addonModal.selectedAddons).reduce((s,[id,q])=>{const a=addonModal.item.addons.find(x=>x.id===id);return s+(a?a.price*q:0);},0)).toFixed(2)} zł</span>
              </div>
            </div>
            <button onClick={()=>addItemWithAddons(addonModal.item, addonModal.selectedAddons)}
              style={{width:"100%",padding:"12px",background:ac,color:"#fff",border:"none",borderRadius:10,fontWeight:800,fontSize:15,cursor:"pointer"}}>
              Dodaj do zamówienia
            </button>
          </div>
        </div>
      )}

      <div style={{background:T.surface,width:"100%",maxWidth:bp.isDesktop?640:520,minHeight:"100vh",
        display:"flex",flexDirection:"column",boxShadow:"0 0 60px rgba(0,0,0,0.6)"}}>

        {/* ── Header ── */}
        <div style={{background:T.navBg,padding:"13px 16px",display:"flex",alignItems:"center",gap:10,flexShrink:0,
          borderBottom:"2px solid rgba(255,255,255,0.07)"}}>
          <button onClick={step===3 ? ()=>setStep(2) : step===2 ? ()=>setStep(1) : onClose}
            style={{background:"rgba(255,255,255,0.14)",border:"none",borderRadius:8,color:"#fff",
              fontSize:18,cursor:"pointer",width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {step > 1 ? "←" : "✕"}
          </button>
          <div style={{flex:1,minWidth:0}}>
            <div style={{color:T.gold,fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",display:"flex",alignItems:"center",gap:8}}>
              Panel obsługi
              <span style={{display:"flex",alignItems:"center",gap:4}}>
                <span style={{width:18,height:18,borderRadius:"50%",background:step===1?"rgba(255,255,255,0.9)":"rgba(255,255,255,0.35)",color:step===1?T.primary:"rgba(255,255,255,0.7)",fontSize:9,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center"}}>1</span>
                <span style={{width:16,height:1.5,background:"rgba(255,255,255,0.3)"}}/>
                <span style={{width:18,height:18,borderRadius:"50%",background:step===2?"rgba(255,255,255,0.9)":"rgba(255,255,255,0.2)",color:step===2?T.primary:"rgba(255,255,255,0.4)",fontSize:9,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center"}}>2</span>
                <span style={{width:16,height:1.5,background:"rgba(255,255,255,0.3)"}}/>
                <span style={{width:18,height:18,borderRadius:"50%",background:step===3?"rgba(255,255,255,0.9)":"rgba(255,255,255,0.2)",color:step===3?T.primary:"rgba(255,255,255,0.4)",fontSize:9,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center"}}>3</span>
              </span>
            </div>
            <div style={{color:"#fff",fontWeight:900,fontSize:15,fontFamily:"'Playfair Display',serif",display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
              {step===1 ? "➕ Nowe zamówienie" : step===2 ? "🛒 Wybierz produkty" : "💳 Płatność i uwagi"}
              {orderType && (
                <span style={{
                  background:"rgba(255,255,255,0.15)",borderRadius:8,padding:"2px 8px",
                  fontSize:11,fontWeight:700,
                  color: ORDER_TYPES.find(t=>t.key===orderType)?.color || "#fff"
                }}>
                  {ORDER_TYPES.find(t=>t.key===orderType)?.icon} {ORDER_TYPES.find(t=>t.key===orderType)?.label}
                </span>
              )}
            </div>
          </div>
          {step > 1 && cart.length > 0 && (
            <div style={{flexShrink:0,background:ac,borderRadius:20,padding:"4px 11px",color:"#fff",fontWeight:800,fontSize:12}}>
              🛒 {totalQty} · {fmt(finalTotal)} zł
            </div>
          )}
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"14px 14px 0"}}>

          {/* ══════ KROK 1: Typ / Kontrahent / Dane klienta ══════ */}
          {step === 1 && <>

          {/* ── 0. Typ zamówienia ── */}
          <div style={{...S.formCard,marginBottom:12}}>
            <div style={{...S.formTitle,marginBottom:10}}>1. Typ zamówienia</div>
            <div style={{display:"grid",gridTemplateColumns:bp.xs?"1fr 1fr":"repeat(3,1fr)",gap:7}}>
              {ORDER_TYPES.map(ot => (
                <button key={ot.key}
                  onClick={()=>{setOrderType(ot.key); setSelTable(null); if(ot.key==="outdoor") setTableSection("outdoor"); else if(ot.key==="indoor"||ot.key==="reservation") setTableSection("indoor"); logActivity(setLogs, currentUser, "Wybrał typ zamówienia", ot.label);}}
                  style={{
                    padding:"10px 6px", borderRadius:11, border:"2px solid",
                    fontWeight:700, fontSize:11, cursor:"pointer", textAlign:"center",
                    transition:"all .13s",
                    borderColor: orderType===ot.key ? ot.color : T.border,
                    background:  orderType===ot.key ? ot.bg     : T.bg,
                    color:       orderType===ot.key ? ot.color  : T.muted,
                    boxShadow:   orderType===ot.key ? `0 2px 8px ${ot.color}33` : "none",
                  }}>
                  <div style={{fontSize:20,marginBottom:4}}>{ot.icon}</div>
                  {ot.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Kontrahent ── */}
          {(clients&&clients.length>0)&&(
            <div style={{...S.formCard,marginBottom:12}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{...S.formTitle,margin:0}}>👥 Kontrahent (opcjonalnie)</div>
                {selClient&&<button onClick={()=>setSelClient(null)} style={{background:"#ffebee",color:"#e94560",border:"none",borderRadius:8,padding:"4px 10px",cursor:"pointer",fontWeight:700,fontSize:12}}>✕ Usuń</button>}
              </div>
              {selClient ? (
                <div style={{background:"#f0fdf4",borderRadius:10,padding:"8px 12px",border:"1.5px solid #86efac"}}>
                  <div style={{fontWeight:800,fontSize:14,color:T.text}}>{selClient.name}</div>
                  {selClient.company&&<div style={{fontSize:12,color:T.muted}}>{selClient.company}</div>}
                  {selClient.phone&&<div style={{fontSize:12,color:T.muted}}>📞 {selClient.phone}</div>}
                  {selClient.discount>0&&<div style={{fontSize:12,color:"#d97706",fontWeight:700}}>-{selClient.discount}% rabat stały</div>}
                </div>
              ) : (
                <div>
                  <input style={{...S.inp,marginBottom:8}} placeholder="🔍 Szukaj kontrahenta..."
                    value={clientSearch} onChange={e=>setClientSearch(e.target.value)}
                    onFocus={()=>setShowClients(true)}/>
                  {(showClients||clientSearch)&&(
                    <div style={{maxHeight:160,overflowY:"auto",border:"1.5px solid "+T.border,borderRadius:10,background:T.bg}}>
                      {(clients||[]).filter(c=>!clientSearch||c.name.toLowerCase().includes(clientSearch.toLowerCase())||(c.phone&&c.phone.includes(clientSearch))).map(c=>(
                        <div key={c.id} onClick={()=>{
                          setSelClient(c);
                          setShowClients(false);
                          setClientSearch("");
                        }}
                          style={{padding:"8px 12px",cursor:"pointer",borderBottom:"1px solid "+T.border+"44",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <div>
                            <div style={{fontWeight:700,fontSize:13}}>{c.name}</div>
                            {c.phone&&<div style={{fontSize:11,color:T.muted}}>📞 {c.phone}</div>}
                          </div>
                          {c.discount>0&&<span style={{background:"#fff8e1",color:"#d97706",borderRadius:6,padding:"2px 7px",fontSize:11,fontWeight:800}}>-{c.discount}%</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Wybór stolika (indoor / outdoor / rezerwacja) ── */}
          {(orderType==="indoor"||orderType==="outdoor"||orderType==="reservation")&&(
            <div style={{...S.formCard,marginBottom:12}}>
              <div style={{...S.formTitle,marginBottom:10}}>
                {orderType==="reservation"?"2. Wybierz stolik pod rezerwację":"2. Wybierz stolik"}
              </div>

              {/* Tabs indoor / outdoor */}
              {activeTables.some(t=>t.type==="outdoor") && (
                <div style={{display:"flex",gap:6,marginBottom:10}}>
                  {[{k:"indoor",icon:"🏠",label:"Wewnętrzne"},{k:"outdoor",icon:"🌿",label:"Zewnętrzne"}].map(s=>(
                    <button key={s.k}
                      onClick={()=>{setTableSection(s.k); setSelTable(null);}}
                      style={{...S.pill, ...(tableSection===s.k?S.pillOn:{}), flex:1, fontSize:11, padding:"6px 4px"}}>
                      {s.icon} {s.label}
                      <span style={{marginLeft:4,opacity:0.7,fontSize:10}}>
                        ({s.k==="indoor"?indoorTables.length:outdoorTables.length})
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Indoor label if no outdoor tables */}
              {!activeTables.some(t=>t.type==="outdoor") && (
                <div style={{fontSize:11,color:T.muted,marginBottom:8}}>🏠 Stoły wewnętrzne</div>
              )}

              {/* Table grid */}
              {renderTableGrid(tableSection==="outdoor" ? outdoorTables : indoorTables)}

              {selTableObj && (
                <div style={{marginTop:9,padding:"7px 10px",background:"#f0fdf4",borderRadius:9,border:"1px solid #86efac",
                  display:"flex",alignItems:"center",gap:8,fontSize:12,color:"#15803d",fontWeight:700}}>
                  ✅ Stolik #{selTableObj.number}
                  {selTableObj.type==="outdoor"&&<span style={{background:"#dcfce7",borderRadius:6,padding:"1px 7px",fontSize:10}}>🌿 zewnętrzny</span>}
                  {selTableObj.seats&&<span style={{fontWeight:400,color:T.muted}}>· {selTableObj.seats} miejsc</span>}
                  {selTableObj.reservation&&<span style={{background:"#fef9c3",borderRadius:6,padding:"1px 7px",fontSize:10,color:"#854d0e"}}>🔒 zarezerwowany</span>}
                </div>
              )}
            </div>
          )}

          {/* ── Dane klienta (wynos / dojazd) ── */}
          {needsGuest&&(
            <div style={{...S.formCard,marginBottom:12}}>
              <div style={{...S.formTitle,marginBottom:10}}>
                {orderType==="delivery"?"2. Dane dostawy":"2. Dane klienta"}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9,marginBottom:0}}>
                <div>
                  <label style={S.label}>Imię *</label>
                  <input style={S.inp} placeholder="Jan" value={guestForm.firstName}
                    onChange={e=>setGuestForm(p=>({...p,firstName:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Nazwisko</label>
                  <input style={S.inp} placeholder="Kowalski" value={guestForm.lastName}
                    onChange={e=>setGuestForm(p=>({...p,lastName:e.target.value}))}/>
                </div>
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>Telefon *</label>
                  <input style={S.inp} type="tel" placeholder="+48 600 000 000" value={guestForm.phone}
                    onChange={e=>setGuestForm(p=>({...p,phone:e.target.value}))}/>
                </div>
                {orderType==="delivery"&&(<>
                  <div style={{gridColumn:"span 2"}}>
                    <label style={S.label}>Adres dostawy *</label>
                    <input style={S.inp} placeholder="ul. Przykładowa 12, Warszawa" value={guestForm.address}
                      onChange={e=>setGuestForm(p=>({...p,address:e.target.value}))}/>
                  </div>
                  {zones.length>0&&(
                    <div style={{gridColumn:"span 2"}}>
                      <label style={S.label}>Strefa dowozu</label>
                      <select style={S.inp} value={guestForm.zoneId}
                        onChange={e=>setGuestForm(p=>({...p,zoneId:e.target.value}))}>
                        <option value="">Wybierz strefę...</option>
                        {zones.map(z=>(
                          <option key={z.id} value={z.id}>{z.name} — {z.price===0?"Bezpłatnie":z.price.toFixed(2)+" zł"}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>)}
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>Uwagi</label>
                  <input style={S.inp} placeholder="Np. domofon, piętro..." value={guestForm.note||""}
                    onChange={e=>setGuestForm(p=>({...p,note:e.target.value}))}/>
                </div>
              </div>
            </div>
          )}

          {/* ── Dane rezerwacji ── */}
          {isReservation&&(
            <div style={{...S.formCard,marginBottom:12,background:"#faf5ff",border:"1.5px solid #c4b5fd"}}>
              <div style={{...S.formTitle,marginBottom:10,color:"#7c3aed"}}>🔒 Dane rezerwacji</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:9}}>
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>Nazwisko / Nazwa *</label>
                  <input style={S.inp} placeholder="Jan Kowalski" value={resForm.name}
                    onChange={e=>setResForm(p=>({...p,name:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Telefon</label>
                  <input style={S.inp} type="tel" placeholder="+48 600..." value={resForm.phone}
                    onChange={e=>setResForm(p=>({...p,phone:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Liczba gości</label>
                  <input style={S.inp} type="number" min="1" max="50" value={resForm.guests}
                    onChange={e=>setResForm(p=>({...p,guests:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Data</label>
                  <input style={S.inp} type="date" value={resForm.date||today}
                    onChange={e=>setResForm(p=>({...p,date:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Godzina *</label>
                  <input style={S.inp} type="time" value={resForm.time}
                    onChange={e=>setResForm(p=>({...p,time:e.target.value}))}/>
                </div>
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>Uwagi</label>
                  <input style={S.inp} placeholder="Np. urodziny, menu specjalne..." value={resForm.note}
                    onChange={e=>setResForm(p=>({...p,note:e.target.value}))}/>
                </div>
              </div>
            </div>
          )}

          </> /* koniec kroku 1 */}

          {/* ══════ KROK 2: Produkty ══════ */}
          {step === 2 && <>

          {/* ── Wybór produktów ── */}
          {orderType && !isReservation && (
            <div style={{...S.formCard,marginBottom:12}}>
              <div style={{...S.formTitle,marginBottom:8}}>
                {needsGuest?"3. Wybierz produkty":"3. Wybierz produkty"}
              </div>
              {/* ── Przełącznik źródła menu ── */}
              <div style={{display:"flex",gap:6,marginBottom:10,padding:"6px 8px",background:"#f0f4ff",borderRadius:10,border:"1.5px solid #dbe4ff"}}>
                {[
                  {key:"restaurant", icon:"🍽", label:"Menu"},
                  {key:"bar",        icon:"🍸",       label:"BAR"},
                  {key:"online",     icon:"🛒",       label:"Online"},
                ].map(src => (
                  <button
                    key={src.key}
                    onClick={() => { setMenuSource(src.key); setSearch(""); logActivity(setLogs, currentUser, "Przełączył zakładkę produktów", src.label); }}
                    style={{
                      flex:1, padding:"7px 4px", borderRadius:8, fontWeight:700, fontSize:12,
                      border: menuSource===src.key ? "2px solid "+ac : "2px solid transparent",
                      background: menuSource===src.key ? ac : "rgba(255,255,255,0.7)",
                      color: menuSource===src.key ? "#fff" : T.muted,
                      cursor:"pointer", transition:"all .15s",
                      boxShadow: menuSource===src.key ? "0 2px 8px rgba(37,99,235,0.2)" : "none",
                    }}>
                    <span style={{fontSize:15,display:"block",marginBottom:1}}>{src.icon}</span>
                    {src.label}
                    {src.key==="bar" && barMenu.length>0 && <span style={{display:"block",fontSize:9,opacity:0.85,marginTop:1}}>{barMenu.length} poz.</span>}
                    {src.key==="online" && onlineMenuActive.length>0 && <span style={{display:"block",fontSize:9,opacity:0.85,marginTop:1}}>{onlineMenuActive.length} poz.</span>}
                    {src.key==="restaurant" && <span style={{display:"block",fontSize:9,opacity:0.85,marginTop:1}}>{activeMenu.length} poz.</span>}
                  </button>
                ))}
              </div>
              {menuSource==="online" && onlineMenuActive.length===0 && (
                <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"10px 12px",marginBottom:10,fontSize:12,color:"#92400e"}}>
                  ⚠ Menu online jest puste — dodaj produkty w zakładce <strong>Zamówienia online</strong>.
                </div>
              )}
              {menuSource==="bar" && barMenu.length===0 && (
                <div style={{background:"#f0f9ff",border:"1px solid #bae6fd",borderRadius:8,padding:"10px 12px",marginBottom:10,fontSize:12,color:"#075985"}}>
                  ℹ Brak kategorii oznaczonych jako barowe — wyświetlono pełne menu.
                </div>
              )}
              <input
                style={{...S.inp,marginBottom:10}}
                placeholder={"🔍 Szukaj w "+(menuSource==="bar"?"barze":menuSource==="online"?"menu online":"menu")+"..."}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:bp.isDesktop?400:300,overflowY:"auto"}}>
                {filtered.length === 0 && (
                  <div style={{textAlign:"center",padding:20,color:T.muted,fontSize:13}}>Brak produktów</div>
                )}
                {filtered.map(item => {
                  const ci = cart.find(c => c.id === item.id);
                  const hasAddons = item.addons && item.addons.length > 0;
                  const isUnavailable = item.available === false;
                  return (
                    <div key={item.id} style={{display:"flex",alignItems:"center",gap:10,
                      background:isUnavailable?"#fff5f5":ci?ac+"0d":T.bg,borderRadius:10,padding:"8px 10px",
                      border:"1.5px solid "+(isUnavailable?"#fecaca":ci?ac+"44":T.border),
                      opacity:isUnavailable?0.7:1}}>
                      <div style={{width:38,height:38,borderRadius:8,flexShrink:0,overflow:"hidden",
                        background:T.surface,display:"flex",alignItems:"center",justifyContent:"center",
                        border:"1px solid "+T.border+"66"}}>
                        {item.image
                          ? <img src={item.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                          : <span style={{fontSize:20}}>{item.emoji}</span>}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:5}}>
                          <div style={{fontWeight:700,fontSize:13,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</div>
                          {hasAddons&&!isUnavailable&&<span style={{background:ac,color:"#fff",borderRadius:6,padding:"1px 5px",fontSize:9,fontWeight:700,flexShrink:0}}>+dod.</span>}
                          {isUnavailable&&<span style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:6,padding:"1px 5px",fontSize:9,fontWeight:800,flexShrink:0}}>NIEDOSTĘPNY</span>}
                        </div>
                        <div style={{fontSize:11,color:T.muted}}>
                          {item.promoPrice
                            ? <><span style={{textDecoration:"line-through",marginRight:4}}>{fmt(item.price)} zł</span>
                                <span style={{color:ac,fontWeight:700}}>{fmt(item.promoPrice)} zł</span></>
                            : <span style={{fontWeight:700,color:T.primary}}>{fmt(item.price)} zł</span>
                          }
                          <span style={{marginLeft:5,opacity:0.6}}>⏱ {item.wait} min</span>
                        </div>
                        {ci && !isUnavailable && (
                          <input
                            value={ci.note||""}
                            onChange={e=>setNote(item.id,e.target.value)}
                            placeholder="Uwaga (np. bez cebuli...)"
                            style={{marginTop:4,width:"100%",padding:"3px 7px",fontSize:10,
                              borderRadius:6,border:"1px solid "+T.border,outline:"none",
                              background:T.surface,color:T.text}}
                          />
                        )}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                        {isUnavailable ? (
                          <span style={{fontSize:11,color:"#dc2626",fontWeight:700}}>—</span>
                        ) : ci ? (
                          <>
                            <button style={{...S.qBtn,borderColor:ac,color:ac,width:26,height:26}}
                              onClick={()=>remItem(ci.cartKey||ci.id)}>−</button>
                            <span style={{fontWeight:800,fontSize:14,minWidth:16,textAlign:"center",color:T.text}}>
                              {ci.qty}
                            </span>
                            <button style={{...S.qBtn,borderColor:ac,color:ac,width:26,height:26}}
                              onClick={()=>addItem(item)}>+</button>
                          </>
                        ) : (
                          <button style={{...S.addBtn,background:ac,padding:"5px 10px",fontSize:11}}
                            onClick={()=>addItem(item)}>
                            {hasAddons?"Wybierz":"+ Dodaj"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}


          {/* ── Podsumowanie koszyka (krok 2) ── */}
          {cart.length > 0 && (
            <div style={{...S.formCard,marginBottom:12,background:"#f8fff8",border:"1.5px solid #27ae6044"}}>
              <div style={{...S.formTitle,color:"#27ae60",marginBottom:8}}>🧾 Podsumowanie</div>
              {cart.map(ci => (
                <div key={ci.cartKey||ci.id} style={{padding:"6px 0",borderBottom:"1px solid "+T.border+"44"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12}}>
                    <span style={{fontWeight:600,color:T.text,flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginRight:8}}>
                      {ci.emoji} {ci.name}{ci.note?<em style={{color:ac}}> · {ci.note}</em>:""}
                    </span>
                    <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                      <button style={{...S.qBtn,width:22,height:22,borderColor:ac,color:ac,fontSize:13}}
                        onClick={()=>remItem(ci.cartKey||ci.id)}>−</button>
                      <span style={{fontWeight:800,fontSize:13,minWidth:16,textAlign:"center",color:T.text}}>{ci.qty}</span>
                      <button style={{...S.qBtn,width:22,height:22,background:ac,color:"#fff",borderColor:ac,fontSize:13}}
                        onClick={()=>addItem(ci)}>+</button>
                      <span style={{fontWeight:700,color:T.text,minWidth:52,textAlign:"right",fontSize:12}}>
                        {fmt((ci.effectivePrice??ep(ci))*ci.qty)} zł
                      </span>
                    </div>
                  </div>
                  {(ci.selectedAddons||[]).map((a,ai)=>(
                    <div key={ai} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#C8621A",paddingLeft:14}}>
                      <span>+ {a.name} ×{a.qty}</span>
                      <span>+{(a.price*a.qty).toFixed(2)} zł</span>
                    </div>
                  ))}
                </div>
              ))}
              {deliveryCost > 0 && (
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#0369a1",paddingTop:4,borderTop:"1px dashed #ddd",marginTop:4}}>
                  <span>🚗 Dostawa</span>
                  <span style={{fontWeight:700}}>+{fmt(deliveryCost)} zł</span>
                </div>
              )}
              <div style={{display:"flex",justifyContent:"space-between",marginTop:8,fontWeight:800,fontSize:14,color:T.primary}}>
                <span>Razem</span>
                <span>{fmt(finalTotal)} zł</span>
              </div>
              {!isReservation&&<div style={{fontSize:11,color:T.muted,marginTop:3}}>⏱ Czas oczekiwania: ~{orderType==="delivery"?45:totalWait} min</div>}
            </div>
          )}

          </> /* koniec kroku 2 */}

          {/* ══════ KROK 3: Płatność / Uwagi / Podsumowanie ══════ */}
          {step === 3 && <>

          {/* ── Metoda płatności ── */}
          {orderType && !isReservation && (
            <div style={{...S.formCard,marginBottom:12}}>
              <div style={{...S.formTitle,marginBottom:8}}>4. Płatność</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                {[
                  {k:"cash",   icon:"💵", label:"Gotówka"},
                  {k:"card",   icon:"💳", label:"Karta"},
                  {k:"blik",   icon:"📱", label:"BLIK"},
                ].map(m => (
                  <button key={m.k}
                    onClick={()=>setPayment(m.k)}
                    style={{padding:"9px 4px",borderRadius:10,border:"2px solid",fontWeight:700,
                      fontSize:12,cursor:"pointer",textAlign:"center",transition:"all .12s",
                      borderColor:payment===m.k?ac:"#ddd",
                      background:payment===m.k?ac+"15":T.bg,
                      color:payment===m.k?ac:T.muted}}>
                    <div style={{fontSize:18,marginBottom:3}}>{m.icon}</div>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Uwagi ── */}
          {orderType && (
            <div style={{...S.formCard,marginBottom:12}}>
              <div style={{...S.formTitle,marginBottom:6}}>
                {isReservation?"3. Dodatkowe uwagi (opcjonalnie)":"5. Uwagi (opcjonalnie)"}
              </div>
              <input
                style={S.inp}
                placeholder="Np. alergie, specjalne życzenia..."
                value={orderNote}
                onChange={e=>setOrderNote(e.target.value)}
              />
            </div>
          )}

          {/* ── Faktura VAT ── */}
          {orderType && !isReservation && (
            <div style={{...S.formCard,marginBottom:12,
              border: wantInvoice ? "2px solid #6366f1" : "1.5px solid "+T.border,
              background: wantInvoice ? "#f5f3ff" : T.bg,
              transition:"all .2s"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:wantInvoice?12:0}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{...S.formTitle,margin:0,color:wantInvoice?"#6b21a8":T.text}}>🧾 Faktura VAT</div>
                  {wantInvoice&&<span style={{background:"#ede9fe",color:"#6b21a8",borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700}}>aktywna</span>}
                </div>
                <button onClick={()=>setWantInvoice(p=>!p)}
                  style={{padding:"6px 14px",borderRadius:9,border:"2px solid",fontWeight:800,fontSize:12,cursor:"pointer",
                    borderColor: wantInvoice?"#6366f1":"#d1d5db",
                    background:  wantInvoice?"#6366f1":"#fff",
                    color:       wantInvoice?"#fff":"#6b7280",
                    transition:"all .15s"}}>
                  {wantInvoice?"✓ Tak, chcę fakturę":"Wystaw fakturę"}
                </button>
              </div>

              {wantInvoice && (
                <div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                    <div style={{gridColumn:"span 2"}}>
                      <label style={S.label}>Nazwa nabywcy *</label>
                      <input style={S.inp} placeholder="Firma Sp. z o.o. / Jan Kowalski"
                        value={invForm.buyerName}
                        onChange={e=>setInvForm(p=>({...p,buyerName:e.target.value}))}/>
                    </div>
                    <div>
                      <label style={S.label}>NIP nabywcy</label>
                      <input style={S.inp} placeholder="0000000000"
                        value={invForm.buyerNip}
                        onChange={e=>setInvForm(p=>({...p,buyerNip:e.target.value.replace(/\D/g,"").slice(0,10)}))}/>
                    </div>
                    <div>
                      <label style={S.label}>Stawka VAT pozycji</label>
                      <select style={S.inp} value={invForm.vatRate}
                        onChange={e=>setInvForm(p=>({...p,vatRate:e.target.value}))}>
                        <option value="23">23%</option>
                        <option value="8">8%</option>
                        <option value="5">5%</option>
                        <option value="0">0%</option>
                        <option value="zw">ZW</option>
                      </select>
                    </div>
                    <div style={{gridColumn:"span 2"}}>
                      <label style={S.label}>Adres nabywcy</label>
                      <input style={S.inp} placeholder="ul. Przykładowa 1, 00-001 Warszawa"
                        value={invForm.buyerAddress}
                        onChange={e=>setInvForm(p=>({...p,buyerAddress:e.target.value}))}/>
                    </div>
                  </div>

                  {/* KSeF auto-send toggle */}
                  {settings?.ksefSeller?.apiKeyId && settings?.ksefSeller?.apiKey && (
                    <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",
                      borderRadius:9,border:"1.5px solid "+(sendKSeFNow?"#a78bfa":"#e5e7eb"),
                      background:sendKSeFNow?"#ede9fe":"#fafafa",marginBottom:4,cursor:"pointer"}}
                      onClick={()=>setSendKSeFNow(p=>!p)}>
                      <div style={{width:20,height:20,borderRadius:5,border:"2px solid "+(sendKSeFNow?"#6366f1":"#d1d5db"),
                        background:sendKSeFNow?"#6366f1":"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                        {sendKSeFNow&&<span style={{color:"#fff",fontSize:12,fontWeight:900}}>✓</span>}
                      </div>
                      <div>
                        <div style={{fontWeight:700,fontSize:12,color:sendKSeFNow?"#6b21a8":"#374151"}}>🚀 Automatycznie wyślij do KSeF</div>
                        <div style={{fontSize:10,color:"#6b7280"}}>Faktura zostanie wysłana do Krajowego Systemu e-Faktur po złożeniu zamówienia</div>
                      </div>
                      <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,
                        color:settings?.ksefSeller?.apiEnv==="prod"?"#dc2626":"#15803d"}}>
                        {settings?.ksefSeller?.apiEnv==="prod"?"🚀 PROD":"🧪 TEST"}
                      </span>
                    </div>
                  )}
                  {!(settings?.ksefSeller?.apiKeyId && settings?.ksefSeller?.apiKey) && (
                    <div style={{fontSize:11,color:"#9a3412",background:"#fff7ed",padding:"7px 10px",borderRadius:7,border:"1px solid #fed7aa"}}>
                      ⚠ Brak konfiguracji API KSeF — faktura zostanie zapisana lokalnie. Skonfiguruj klucze w panelu 🧾 KSeF → ⚙ Dane sprzedawcy.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          </> /* koniec kroku 3 */}
        </div>

        {/* ── Sticky footer ── */}
        <div style={{padding:"12px 14px",background:T.surface,
          borderTop:"1px solid "+T.border,flexShrink:0,
          boxShadow:"0 -4px 16px rgba(59,31,10,0.08)"}}>
          {hint && (
            <div style={{fontSize:11,color:T.muted,textAlign:"center",marginBottom:8}}>{hint}</div>
          )}
          {(() => {
            const btnColor = isReservation ? "linear-gradient(135deg,#7c3aed,#a78bfa)"
              : orderType==="delivery" ? "linear-gradient(135deg,#0369a1,#0ea5e9)"
              : orderType==="outdoor"  ? "linear-gradient(135deg,#15803d,#22c55e)"
              : `linear-gradient(135deg,${ac},${ac}dd)`;
            if (step === 1) {
              return (
                <button onClick={()=>{ if(canGoStep2) setStep(2); }} disabled={!canGoStep2}
                  style={{width:"100%",padding:14,color:"#fff",border:"none",borderRadius:12,fontWeight:900,fontSize:15,
                    cursor:canGoStep2?"pointer":"not-allowed",
                    background:canGoStep2?btnColor:"#d1d5db",
                    boxShadow:canGoStep2?"0 4px 14px rgba(0,0,0,0.2)":"none",transition:"all .15s"}}>
                  {placeBtnLabel()}
                </button>
              );
            }
            if (step === 2) {
              return (
                <button onClick={()=>{ if(canGoStep3) setStep(3); }} disabled={!canGoStep3}
                  style={{width:"100%",padding:14,color:"#fff",border:"none",borderRadius:12,fontWeight:900,fontSize:15,
                    cursor:canGoStep3?"pointer":"not-allowed",
                    background:canGoStep3?btnColor:"#d1d5db",
                    boxShadow:canGoStep3?"0 4px 14px rgba(0,0,0,0.2)":"none",transition:"all .15s"}}>
                  {placeBtnLabel()}
                </button>
              );
            }
            return (
              <button onClick={place} disabled={!canPlace}
                style={{width:"100%",padding:14,color:"#fff",border:"none",borderRadius:12,fontWeight:900,fontSize:15,
                  cursor:canPlace?"pointer":"not-allowed",
                  background:canPlace?btnColor:"#d1d5db",
                  boxShadow:canPlace?"0 4px 14px rgba(0,0,0,0.2)":"none",transition:"all .15s"}}>
                {placeBtnLabel()}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════ SELLER ROUTING PANEL ══════════════════════════════════ */
/* ════════════════════ TAKEAWAY PANEL ═══════════════════════════════════════ */
/* ════════════════════ BACKUP PANEL ══════════════════════════════════════════ */
/* ════════════════════ CLIENTS PANEL ════════════════════════════════════════ */
const BLANK_CLIENT = {
  id:null, name:"", company:"", phone:"", email:"",
  address:"", nip:"", note:"", discount:0,
  street:"", buildingNo:"", postalCode:"", city:"", gmina:"", powiat:"", wojewodztwo:""
};

/* ════════════════════ MESSAGES PANEL ═══════════════════════════════════════ */
const EMAIL_TEMPLATES = {
  order_received: {
    id: "order_received",
    label: "✅ Potwierdzenie zamówienia",
    subject: "Twoje zamówienie zostało przyjęte – {{restaurant_name}}",
    body: `Cześć {{customer_name}}!

Twoje zamówienie w {{restaurant_name}} zostało przyjęte.

📋 Szczegóły zamówienia:
{{order_items}}

💰 Łączna kwota: {{order_total}} zł
🕐 Szacowany czas: {{wait_time}} min
📍 {{order_type}}

Dziękujemy za zamówienie!
{{restaurant_name}}`,
  },
  order_ready: {
    id: "order_ready",
    label: "🔔 Zamówienie gotowe",
    subject: "Twoje zamówienie jest gotowe! – {{restaurant_name}}",
    body: `Cześć {{customer_name}}!

Twoje zamówienie jest gotowe do odbioru! 🎉

📍 {{order_type}}
💰 Kwota: {{order_total}} zł

Do zobaczenia!
{{restaurant_name}}`,
  },
  promo: {
    id: "promo",
    label: "📣 Wiadomość promocyjna",
    subject: "{{promo_title}} – {{restaurant_name}}",
    body: `Cześć!

{{promo_message}}

Zapraszamy do {{restaurant_name}}!

Pozdrawiamy,
Zespół {{restaurant_name}}`,
  },
  custom: {
    id: "custom",
    label: "✏ Własna wiadomość",
    subject: "",
    body: "",
  },
};

function MessagesPanel({ clients, orders, settings, setSettings, ac }) {
  const [selTemplate, setSelTemplate] = useState("order_received");
  const [subject,     setSubject]     = useState(EMAIL_TEMPLATES.order_received.subject);
  const [body,        setBody]        = useState(EMAIL_TEMPLATES.order_received.body);
  const [recipients,  setRecipients]  = useState("all_clients"); // all_clients | manual | select
  const [manualEmail, setManualEmail] = useState("");
  const [selClients,  setSelClients]  = useState([]);
  const [sending,     setSending]     = useState(false);
  const [sentMsg,     setSentMsg]     = useState(null);
  const [tab2,        setTab2]        = useState("compose"); // compose | auto | history | config
  const [history,     setHistory]     = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  // EmailJS config from settings
  const ejsServiceId  = settings.emailjsServiceId  || "";
  const ejsTemplateId = settings.emailjsTemplateId || "";
  const ejsPublicKey  = settings.emailjsPublicKey  || "";
  const senderEmail   = settings.senderEmail       || "";

  const autoSettings = settings.emailAuto || {
    onOrderReceived: false,
    onOrderReady:    false,
    fromEmail:       "",
  };

  const fillTemplate = (tmpl, vars={}) => {
    const rn = settings.restaurantName || "Restauracja";
    let s = tmpl;
    const defaultVars = {
      restaurant_name: rn,
      customer_name: vars.customer_name || "Kliencie",
      order_items:   vars.order_items   || "(lista pozycji)",
      order_total:   vars.order_total   || "0.00",
      wait_time:     vars.wait_time     || "20",
      order_type:    vars.order_type    || "Odbiór w restauracji",
      promo_title:   vars.promo_title   || "Specjalna oferta",
      promo_message: vars.promo_message || "(treść promocji)",
      ...vars,
    };
    Object.entries(defaultVars).forEach(([k,v]) => {
      s = s.split(`{{${k}}}`).join(v);
    });
    return s;
  };

  const sendViaEmailJS = async (to, subj, bodyText) => {
    if (!ejsServiceId || !ejsTemplateId || !ejsPublicKey) return false;
    try {
      const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
        method: "POST",
        headers: {"Content-Type":"application/json"},
        body: JSON.stringify({
          service_id:  ejsServiceId,
          template_id: ejsTemplateId,
          user_id:     ejsPublicKey,
          template_params: {
            to_email: to,
            to_name:  to,
            subject:  subj,
            message:  bodyText,
            from_name: settings.restaurantName || "Restauracja",
            reply_to: senderEmail || "",
          }
        })
      });
      return res.ok;
    } catch { return false; }
  };

  const sendViaMailto = (to, subj, bodyText) => {
    const mailto = `mailto:${to}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(bodyText)}`;
    window.open(mailto, "_blank");
    return true;
  };

  const getRecipientList = () => {
    if (recipients === "manual") {
      return manualEmail.split(",").map(e=>e.trim()).filter(Boolean).map(e=>({email:e, name:""}));
    }
    if (recipients === "select") {
      return selClients.map(id=>{
        const c = (clients||[]).find(x=>x.id===id);
        return c ? {email:c.email, name:c.name} : null;
      }).filter(Boolean);
    }
    // all_clients
    return (clients||[]).filter(c=>c.email).map(c=>({email:c.email, name:c.name}));
  };

  const handleSend = async () => {
    const list = getRecipientList();
    if (!list.length) { setSentMsg({ok:false, text:"Brak odbiorców z adresem email."}); return; }
    setSending(true);
    setSentMsg(null);
    let ok = 0, fail = 0;
    const hasEmailJS = ejsServiceId && ejsTemplateId && ejsPublicKey;

    for (const r of list) {
      const filled_body = fillTemplate(body, {customer_name: r.name});
      const filled_subj = fillTemplate(subject, {customer_name: r.name});
      let sent = false;
      if (hasEmailJS) {
        sent = await sendViaEmailJS(r.email, filled_subj, filled_body);
      } else {
        sent = sendViaMailto(r.email, filled_subj, filled_body);
        break; // mailto opens one at a time
      }
      if (sent) ok++; else fail++;
    }

    const entry = {
      id: genId(),
      date: new Date().toLocaleDateString("pl-PL"),
      time: new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),
      subject: fillTemplate(subject),
      recipients: list.length,
      ok, fail,
      template: selTemplate,
    };
    setHistory(p=>[entry,...p].slice(0,50));

    if (!hasEmailJS) {
      setSentMsg({ok:true, text:`Otwarto klienta email dla ${list.length} odbiorców. Sprawdź okno przeglądarki.`});
    } else {
      setSentMsg({ok: fail===0, text: fail===0 ? `✅ Wysłano do ${ok} odbiorców!` : `⚠ Wysłano: ${ok}, błąd: ${fail}`});
    }
    setSending(false);
  };

  const hasEmailJS = ejsServiceId && ejsTemplateId && ejsPublicKey;
  const recipientList = getRecipientList();

  return (
    <div style={{paddingBottom:40}}>
      {/* Tabs */}
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:"2px solid "+TA.border,paddingBottom:0}}>
        {[["compose","✏ Utwórz"],["auto","⚡ Automatyczne"],["history","📋 Historia"],["config","⚙ Konfiguracja"]].map(([k,l])=>(
          <button key={k} onClick={()=>setTab2(k)} style={{
            padding:"9px 16px",border:"none",background:"none",cursor:"pointer",
            fontWeight:tab2===k?700:400,fontSize:13,
            borderBottom:tab2===k?`2px solid ${TA.accent}`:"2px solid transparent",
            color:tab2===k?TA.accent:TA.muted,marginBottom:-2
          }}>{l}</button>
        ))}
      </div>

      {/* COMPOSE */}
      {tab2==="compose"&&(
        <div style={{display:"grid",gridTemplateColumns:TA&&"1fr",gap:16}}>
          {/* Template picker */}
          <div style={S.formCard}>
            <div style={S.formTitle}>Wybierz szablon</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
              {Object.values(EMAIL_TEMPLATES).map(t=>(
                <div key={t.id} onClick={()=>{
                  setSelTemplate(t.id);
                  if(t.id!=="custom"){ setSubject(t.subject); setBody(t.body); }
                }} style={{
                  padding:"10px 12px",borderRadius:9,cursor:"pointer",
                  border:`2px solid ${selTemplate===t.id?TA.accent:TA.border}`,
                  background:selTemplate===t.id?TA.accentSoft:"#fff",
                  transition:"all .15s"
                }}>
                  <div style={{fontWeight:600,fontSize:13,color:selTemplate===t.id?TA.accent:TA.text}}>{t.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Editor */}
          <div style={S.formCard}>
            <div style={S.formTitle}>Treść wiadomości</div>
            <div style={{marginBottom:10}}>
              <label style={S.label}>Temat</label>
              <input style={S.inp} value={subject} onChange={e=>setSubject(e.target.value)} placeholder="Temat emaila"/>
            </div>
            <div style={{marginBottom:10}}>
              <label style={S.label}>Treść</label>
              <textarea style={{...S.inp,minHeight:200,resize:"vertical",fontFamily:"monospace",fontSize:12}}
                value={body} onChange={e=>setBody(e.target.value)}/>
            </div>
            <div style={{background:"#f9fafb",borderRadius:8,padding:"8px 12px",fontSize:11,color:TA.muted}}>
              <strong>Zmienne:</strong> {"{{restaurant_name}}"} {"{{customer_name}}"} {"{{order_items}}"} {"{{order_total}}"} {"{{wait_time}}"} {"{{order_type}}"}
            </div>
          </div>

          {/* Recipients */}
          <div style={S.formCard}>
            <div style={S.formTitle}>Odbiorcy</div>
            <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
              {[["all_clients","👥 Wszyscy klienci"],["select","☑ Wybrani klienci"],["manual","✏ Ręczny adres"]].map(([v,l])=>(
                <button key={v} onClick={()=>setRecipients(v)} style={{
                  padding:"7px 14px",borderRadius:7,border:`1.5px solid ${recipients===v?TA.accent:TA.border}`,
                  background:recipients===v?TA.accentSoft:"#fff",color:recipients===v?TA.accent:TA.muted,
                  cursor:"pointer",fontWeight:recipients===v?600:400,fontSize:12
                }}>{l}</button>
              ))}
            </div>

            {recipients==="manual"&&(
              <div style={{marginBottom:10}}>
                <label style={S.label}>Adresy email (oddzielone przecinkami)</label>
                <input style={S.inp} value={manualEmail} onChange={e=>setManualEmail(e.target.value)}
                  placeholder="jan@example.com, anna@example.com"/>
              </div>
            )}

            {recipients==="select"&&(
              <div style={{maxHeight:200,overflowY:"auto",border:"1px solid "+TA.border,borderRadius:8}}>
                {(clients||[]).filter(c=>c.email).length===0
                  ? <div style={{padding:12,color:TA.muted,fontSize:12,textAlign:"center"}}>Brak klientów z adresem email</div>
                  : (clients||[]).filter(c=>c.email).map(c=>(
                    <div key={c.id} onClick={()=>setSelClients(p=>p.includes(c.id)?p.filter(x=>x!==c.id):[...p,c.id])}
                      style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",
                        borderBottom:"1px solid "+TA.border+"44",cursor:"pointer",
                        background:selClients.includes(c.id)?TA.accentSoft:"#fff"}}>
                      <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${selClients.includes(c.id)?TA.accent:TA.border}`,
                        background:selClients.includes(c.id)?TA.accent:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {selClients.includes(c.id)&&<span style={{color:"#fff",fontSize:11,fontWeight:900}}>✓</span>}
                      </div>
                      <div>
                        <div style={{fontWeight:600,fontSize:13}}>{c.name}</div>
                        <div style={{fontSize:11,color:TA.muted}}>{c.email}</div>
                      </div>
                    </div>
                  ))
                }
              </div>
            )}

            {recipients==="all_clients"&&(
              <div style={{fontSize:12,color:TA.muted,padding:"8px 0"}}>
                Zostanie wysłane do {(clients||[]).filter(c=>c.email).length} klientów z adresem email.
              </div>
            )}
          </div>

          {/* Preview + Send */}
          <div style={S.formCard}>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <button onClick={()=>setPreviewOpen(p=>!p)} style={{...S.cancelBtn,flex:1}}>
                {previewOpen?"Ukryj podgląd":"👁 Podgląd"}
              </button>
              <button onClick={handleSend} disabled={sending||!recipientList.length} style={{
                ...S.darkBtn,flex:2,
                opacity:sending||!recipientList.length?0.6:1,
                background:hasEmailJS?"#2563eb":"#059669"
              }}>
                {sending?"Wysyłanie..."
                  : hasEmailJS
                    ? `📤 Wyślij do ${recipientList.length} odbiorców`
                    : `📧 Otwórz w kliencie email (${recipientList.length})`}
              </button>
            </div>

            {!hasEmailJS&&(
              <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"9px 12px",fontSize:12,color:"#92400e",marginBottom:10}}>
                ⚠ EmailJS nie jest skonfigurowany — wiadomości otworzą się w domyślnym kliencie email (mailto).
                Skonfiguruj EmailJS w zakładce ⚙ Konfiguracja aby wysyłać automatycznie.
              </div>
            )}

            {sentMsg&&(
              <div style={{background:sentMsg.ok?"#dcfce7":"#fee2e2",color:sentMsg.ok?"#16a34a":"#dc2626",
                borderRadius:8,padding:"9px 12px",fontSize:13,fontWeight:600}}>
                {sentMsg.text}
              </div>
            )}

            {previewOpen&&(
              <div style={{border:"1px solid "+TA.border,borderRadius:9,overflow:"hidden",marginTop:10}}>
                <div style={{background:"#f9fafb",padding:"8px 14px",fontSize:12,fontWeight:700,color:TA.muted,borderBottom:"1px solid "+TA.border}}>
                  Podgląd: {fillTemplate(subject)}
                </div>
                <pre style={{margin:0,padding:"14px",fontSize:12,fontFamily:"inherit",whiteSpace:"pre-wrap",color:TA.text,maxHeight:300,overflowY:"auto"}}>
                  {fillTemplate(body)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AUTO */}
      {tab2==="auto"&&(
        <div>
          <div style={S.formCard}>
            <div style={S.formTitle}>⚡ Automatyczne powiadomienia email</div>
            <div style={{fontSize:12,color:TA.muted,marginBottom:16}}>
              Automatycznie wysyłaj email do klientów którzy podali adres email przy zamówieniu online.
              Wymaga skonfigurowanego EmailJS.
            </div>
            {[
              {key:"onOrderReceived", label:"✅ Potwierdzenie zamówienia", desc:"Wysłaj email gdy klient złoży zamówienie online"},
              {key:"onOrderReady",    label:"🔔 Zamówienie gotowe",        desc:"Wysłaj email gdy kuchnia oznaczy zamówienie jako gotowe"},
            ].map(item=>{
              const enabled = autoSettings[item.key];
              return (
                <div key={item.key} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",
                  borderRadius:10,border:`1.5px solid ${enabled?TA.accent:TA.border}`,
                  background:enabled?TA.accentSoft:"#fff",marginBottom:10,transition:"all .2s"}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13,color:enabled?TA.accent:TA.text}}>{item.label}</div>
                    <div style={{fontSize:11,color:TA.muted,marginTop:2}}>{item.desc}</div>
                  </div>
                  <div onClick={()=>setSettings(p=>({...p,emailAuto:{...p.emailAuto,[item.key]:!enabled}}))}
                    style={{width:44,height:24,borderRadius:12,background:enabled?TA.accent:"#d1d5db",
                      cursor:"pointer",position:"relative",transition:"all .2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:2,left:enabled?22:2,width:20,height:20,
                      borderRadius:"50%",background:"#fff",transition:"all .2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
                  </div>
                </div>
              );
            })}
            {!hasEmailJS&&(
              <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"9px 12px",fontSize:12,color:"#92400e",marginTop:10}}>
                ⚠ Skonfiguruj EmailJS w zakładce ⚙ Konfiguracja aby włączyć automatyczne wysyłanie.
              </div>
            )}
          </div>
        </div>
      )}

      {/* HISTORY */}
      {tab2==="history"&&(
        <div style={S.formCard}>
          <div style={S.formTitle}>📋 Historia wysłanych wiadomości</div>
          {history.length===0
            ? <div style={S.empty}><div style={{fontSize:40}}>📭</div><p>Brak wysłanych wiadomości</p></div>
            : history.map(h=>(
              <div key={h.id} style={{padding:"10px 0",borderBottom:"1px solid "+TA.border+"44",display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:36,height:36,borderRadius:8,background:h.fail>0?"#fee2e2":"#dcfce7",
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                  {h.fail>0?"⚠":"✅"}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:13,color:TA.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.subject}</div>
                  <div style={{fontSize:11,color:TA.muted,marginTop:2}}>
                    {h.date} {h.time} · {h.recipients} odbiorców · wysłano: {h.ok}{h.fail>0&&`, błąd: ${h.fail}`}
                  </div>
                </div>
              </div>
            ))
          }
        </div>
      )}

      {/* CONFIG */}
      {tab2==="config"&&(
        <div>
          <div style={S.formCard}>
            <div style={S.formTitle}>⚙ Konfiguracja EmailJS</div>
            <div style={{fontSize:12,color:TA.muted,marginBottom:14,lineHeight:1.6}}>
              <strong>EmailJS</strong> umożliwia wysyłanie emaili bezpośrednio z przeglądarki bez backendu.
              Zarejestruj się bezpłatnie na <a href="https://emailjs.com" target="_blank" rel="noreferrer" style={{color:TA.accent}}>emailjs.com</a>,
              stwórz serwis email (Gmail, SMTP itp.) i szablon wiadomości.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr",gap:12}}>
              <div>
                <label style={S.label}>Service ID</label>
                <input style={S.inp} value={ejsServiceId}
                  onChange={e=>setSettings(p=>({...p,emailjsServiceId:e.target.value}))}
                  placeholder="np. service_xxxxxxx"/>
              </div>
              <div>
                <label style={S.label}>Template ID</label>
                <input style={S.inp} value={ejsTemplateId}
                  onChange={e=>setSettings(p=>({...p,emailjsTemplateId:e.target.value}))}
                  placeholder="np. template_xxxxxxx"/>
              </div>
              <div>
                <label style={S.label}>Public Key</label>
                <input style={S.inp} value={ejsPublicKey}
                  onChange={e=>setSettings(p=>({...p,emailjsPublicKey:e.target.value}))}
                  placeholder="np. xxxxxxxxxxxxxxxxx"/>
              </div>
              <div>
                <label style={S.label}>Adres nadawcy (reply-to)</label>
                <input style={S.inp} type="email" value={senderEmail}
                  onChange={e=>setSettings(p=>({...p,senderEmail:e.target.value}))}
                  placeholder="restauracja@example.com"/>
              </div>
            </div>
            <div style={{marginTop:14,padding:"12px",background:"#f0fdf4",borderRadius:8,border:"1px solid #bbf7d0",fontSize:12}}>
              <strong>Jak skonfigurować EmailJS:</strong>
              <ol style={{margin:"6px 0 0",paddingLeft:18,lineHeight:1.8}}>
                <li>Wejdź na <a href="https://emailjs.com" target="_blank" rel="noreferrer" style={{color:TA.accent}}>emailjs.com</a> i załóż konto (bezpłatne — 200 emaili/miesiąc)</li>
                <li>Dodaj Email Service (Gmail, Outlook, SMTP)</li>
                <li>Stwórz Email Template z polami: <code>to_email</code>, <code>subject</code>, <code>message</code>, <code>from_name</code></li>
                <li>Skopiuj Service ID, Template ID i Public Key powyżej</li>
              </ol>
            </div>
          </div>

          <div style={S.formCard}>
            <div style={S.formTitle}>🧪 Test połączenia</div>
            <div style={{marginBottom:12}}>
              <label style={S.label}>Wyślij testowego emaila na adres:</label>
              <div style={{display:"flex",gap:8}}>
                <input style={{...S.inp,flex:1}} type="email" id="test-email-input"
                  placeholder="twoj@email.com"/>
                <button onClick={async ()=>{
                  const el = document.getElementById("test-email-input");
                  const email = el?.value?.trim();
                  if (!email) return alert("Wpisz adres email");
                  if (!hasEmailJS) return alert("Najpierw skonfiguruj EmailJS");
                  const ok = await sendViaEmailJS(email, "Test – "+settings.restaurantName, "To jest testowa wiadomość z systemu POS.");
                  alert(ok ? "✅ Test wysłany pomyślnie!" : "❌ Błąd wysyłania. Sprawdź konfigurację EmailJS.");
                }} style={{...S.darkBtn,flexShrink:0}}>
                  Wyślij test
                </button>
              </div>
            </div>
            {hasEmailJS
              ? <div style={{background:"#dcfce7",color:"#16a34a",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:600}}>✅ EmailJS skonfigurowany poprawnie</div>
              : <div style={{background:"#fee2e2",color:"#dc2626",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:600}}>❌ EmailJS nie skonfigurowany — uzupełnij powyższe pola</div>
            }
          </div>
        </div>
      )}
    </div>
  );
}

function ClientsPanel({ clients, setClients, orders, setOrders, menu, cats, tables, settings, ac, isAdmin, onlineMenu, onlineCats, setLogs, currentUser }) {
  const [search,         setSearch]         = useState("");
  const [editId,         setEditId]         = useState(null);
  const [showForm,       setShowForm]       = useState(false);
  const [form,           setForm]           = useState(BLANK_CLIENT);
  const [orderClient,    setOrderClient]    = useState(null); // kontrahent dla nowego zamówienia

  const filtered = (clients||[]).filter(c=>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.company&&c.company.toLowerCase().includes(search.toLowerCase())) ||
    (c.phone&&c.phone.includes(search))
  );

  const openNew = () => { setForm({...BLANK_CLIENT,id:genId()}); setEditId(null); setShowForm(true); };
  const openEdit = c => { setForm({...c}); setEditId(c.id); setShowForm(true); };

  const save = () => {
    if (!form.name.trim()) { alert("Wpisz imię lub nazwę klienta"); return; }
    if (editId) {
      setClients(p=>p.map(c=>c.id===editId?{...form}:c));
    } else {
      setClients(p=>[...p,{...form}]);
    }
    setShowForm(false);
  };

  const del = id => {
    if (!window.confirm("Usunąć kontrahenta?")) return;
    setClients(p=>p.filter(c=>c.id!==id));
  };

  // Historia zamówień klienta (po telefonie lub nazwisku)
  const clientOrders = (c) => (orders||[]).filter(o=>
    (o.firstName&&o.lastName&&`${o.firstName} ${o.lastName}`.toLowerCase()===c.name.toLowerCase()) ||
    (o.phone&&c.phone&&o.phone===c.phone)
  );

  const handlePlaceOrder = (o) => {
    if (setOrders) setOrders(p => [...p, o].slice(-500));
    setOrderClient(null);
  };

  return (
    <div style={{paddingBottom:40}}>
      {/* Modal nowego zamówienia dla kontrahenta */}
      {orderClient && menu && (
        <ManualOrderModal
          menu={menu} cats={cats||[]} tables={tables||[]} orders={orders||[]}
          settings={settings||{}} clients={clients||[]}
          preselectedClient={orderClient}
          onlineMenu={onlineMenu||[]} onlineCats={onlineCats||[]}
          setLogs={setLogs} currentUser={currentUser}
          onPlace={handlePlaceOrder}
          onClose={() => setOrderClient(null)}
        />
      )}

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
        <div>
          <div style={{fontWeight:800,fontSize:18,color:T.text}}>👥 Kontrahenci</div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>{(clients||[]).length} klientów w bazie</div>
        </div>
        <button onClick={openNew} style={{padding:"10px 16px",background:ac,color:"#fff",border:"none",borderRadius:12,fontWeight:800,cursor:"pointer",fontSize:14,boxShadow:"0 2px 8px rgba(0,0,0,0.15)"}}>
          + Dodaj klienta
        </button>
      </div>

      <input style={{...S.inp,marginBottom:14}} placeholder="🔍 Szukaj po nazwie, firmie lub telefonie..."
        value={search} onChange={e=>setSearch(e.target.value)}/>

      {/* Formularz dodawania/edycji */}
      {showForm&&(
        <div style={{background:T.surface,borderRadius:16,border:"2px solid "+ac,padding:16,marginBottom:16}}>
          <div style={{fontWeight:800,fontSize:15,color:T.text,marginBottom:14}}>
            {editId?"✏ Edytuj kontrahenta":"+ Nowy kontrahent"}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div style={{gridColumn:"span 2"}}>
              <label style={S.label}>Imię i nazwisko / Nazwa *</label>
              <input style={S.inp} placeholder="Jan Kowalski" value={form.name}
                onChange={e=>setForm(p=>({...p,name:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Firma (opcjonalnie)</label>
              <input style={S.inp} placeholder="Kowalski Sp. z o.o." value={form.company}
                onChange={e=>setForm(p=>({...p,company:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>NIP</label>
              <input style={S.inp} placeholder="000-000-00-00" value={form.nip}
                onChange={e=>setForm(p=>({...p,nip:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Telefon</label>
              <input style={S.inp} placeholder="+48 600 000 000" type="tel" value={form.phone}
                onChange={e=>setForm(p=>({...p,phone:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>E-mail</label>
              <input style={S.inp} placeholder="jan@example.com" type="email" value={form.email}
                onChange={e=>setForm(p=>({...p,email:e.target.value}))}/>
            </div>
            <div style={{gridColumn:"span 2"}}>
              <label style={S.label}>Adres — ulica</label>
              <input style={S.inp} placeholder="ul. Przykładowa" value={form.street||""}
                onChange={e=>setForm(p=>({...p,street:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Numer budynku</label>
              <input style={S.inp} placeholder="12A" value={form.buildingNo||""}
                onChange={e=>setForm(p=>({...p,buildingNo:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Kod pocztowy</label>
              <input style={S.inp} placeholder="00-000" value={form.postalCode||""}
                onChange={e=>setForm(p=>({...p,postalCode:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Miejscowość</label>
              <input style={S.inp} placeholder="Warszawa" value={form.city||""}
                onChange={e=>setForm(p=>({...p,city:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Gmina</label>
              <input style={S.inp} placeholder="Warszawa" value={form.gmina||""}
                onChange={e=>setForm(p=>({...p,gmina:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Powiat</label>
              <input style={S.inp} placeholder="Warszawa" value={form.powiat||""}
                onChange={e=>setForm(p=>({...p,powiat:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Województwo</label>
              <input style={S.inp} placeholder="mazowieckie" value={form.wojewodztwo||""}
                onChange={e=>setForm(p=>({...p,wojewodztwo:e.target.value}))}/>
            </div>
            <div style={{gridColumn:"span 2"}}>
              <label style={S.label}>Adres (skrócony — do faktur i zamówień)</label>
              <input style={S.inp} placeholder="ul. Przykładowa 12, 00-000 Miasto" value={form.address}
                onChange={e=>setForm(p=>({...p,address:e.target.value}))}/>
              {(form.street||form.buildingNo||form.postalCode||form.city)&&(
                <button type="button" onClick={()=>setForm(p=>({...p,
                  address:[p.street,p.buildingNo].filter(Boolean).join(" ")+
                    (p.postalCode||p.city ? ", "+[p.postalCode,p.city].filter(Boolean).join(" ") : "")
                }))} style={{marginTop:4,fontSize:11,color:TA.accent,background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:600}}>
                  ↑ Uzupełnij z pól powyżej
                </button>
              )}
            </div>
            <div>
              <label style={S.label}>Stały rabat (%)</label>
              <input style={S.inp} type="number" min="0" max="100" placeholder="0" value={form.discount}
                onChange={e=>setForm(p=>({...p,discount:Number(e.target.value)}))}/>
            </div>
            <div>
              <label style={S.label}>Uwagi</label>
              <input style={S.inp} placeholder="Alergie, preferencje..." value={form.note}
                onChange={e=>setForm(p=>({...p,note:e.target.value}))}/>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={save} style={{...S.darkBtn,flex:2,background:ac}}>
              {editId?"✅ Zapisz zmiany":"✅ Dodaj kontrahenta"}
            </button>
            <button onClick={()=>setShowForm(false)} style={S.cancelBtn}>Anuluj</button>
          </div>
        </div>
      )}

      {/* Lista kontrahentów */}
      {filtered.length===0 ? (
        <div style={{textAlign:"center",padding:"40px 0",color:T.border}}>
          <div style={{fontSize:48}}>👥</div>
          <div style={{fontSize:14,fontWeight:700,color:T.muted,marginTop:8}}>
            {search?"Brak wyników":"Brak kontrahentów — dodaj pierwszego"}
          </div>
        </div>
      ) : filtered.map(c=>{
        const hist = clientOrders(c);
        const total = hist.reduce((s,o)=>s+Number(o.total||0),0);
        return (
          <div key={c.id} style={{background:T.surface,borderRadius:14,padding:14,marginBottom:10,border:"1.5px solid "+T.border,boxShadow:T.cardShadow}}>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                  <span style={{fontWeight:800,fontSize:15,color:T.text}}>{c.name}</span>
                  {c.company&&<span style={{background:"#f0f4ff",color:"#3b5bdb",borderRadius:8,padding:"2px 8px",fontSize:11,fontWeight:700}}>{c.company}</span>}
                  {c.discount>0&&<span style={{background:"#fff8e1",color:"#d97706",borderRadius:8,padding:"2px 8px",fontSize:11,fontWeight:800}}>-{c.discount}% rabat</span>}
                </div>
                <div style={{display:"flex",gap:12,marginTop:5,flexWrap:"wrap"}}>
                  {c.phone&&<span style={{fontSize:12,color:T.muted}}>📞 {c.phone}</span>}
                  {c.email&&<span style={{fontSize:12,color:T.muted}}>✉ {c.email}</span>}
                  {c.nip&&<span style={{fontSize:12,color:T.muted}}>🧾 {c.nip}</span>}
                </div>
                {c.address&&<div style={{fontSize:12,color:T.muted,marginTop:3}}>📍 {c.address}</div>}
                {c.note&&<div style={{fontSize:11,color:T.muted,marginTop:3,fontStyle:"italic"}}>💬 {c.note}</div>}
                {hist.length>0&&(
                  <div style={{marginTop:6,display:"flex",gap:8}}>
                    <span style={{background:"#f0fdf4",color:"#15803d",borderRadius:8,padding:"2px 8px",fontSize:11,fontWeight:700}}>
                      📋 {hist.length} zamówień
                    </span>
                    <span style={{background:"#f0fdf4",color:"#15803d",borderRadius:8,padding:"2px 8px",fontSize:11,fontWeight:700}}>
                      💰 {total.toFixed(2)} zł łącznie
                    </span>
                  </div>
                )}
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
                <button
                  onClick={()=>setOrderClient(c)}
                  style={{padding:"7px 12px",background:ac,border:"none",borderRadius:9,cursor:"pointer",fontWeight:700,fontSize:12,color:"#fff"}}
                  title="Nowe zamówienie dla kontrahenta">
                  🛒 Zamówienie
                </button>
                <button onClick={()=>openEdit(c)} style={{padding:"7px 12px",background:T.bg,border:"1.5px solid "+T.border,borderRadius:9,cursor:"pointer",fontWeight:700,fontSize:12,color:T.text}}>✏</button>
                {isAdmin&&<button onClick={()=>del(c.id)} style={{padding:"7px 12px",background:"#ffebee",border:"1.5px solid #fca5a5",borderRadius:9,cursor:"pointer",fontWeight:700,fontSize:12,color:"#e94560"}}>🗑</button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════ BACKUP PANEL — v8 (Pantry Cloud + schedule) ══════════ */

/* Klucz localStorage dla ustawień kopii */
const BACKUP_SETTINGS_KEY = "backupSettings_v2";
const BACKUP_HISTORY_KEY  = "backupHistory_v1";

function loadBackupSettings(settingsObj) {
  try {
    const local = JSON.parse(localStorage.getItem(BACKUP_SETTINGS_KEY)||"null") || {};
    if (settingsObj) {
      if (settingsObj._backupPantryId)   local.pantryId         = settingsObj._backupPantryId;
      if (settingsObj._backupBasket)     local.lastPantryBasket = settingsObj._backupBasket;
    }
    return local;
  } catch { return {}; }
}
function saveBackupSettings(s) {
  try { localStorage.setItem(BACKUP_SETTINGS_KEY, JSON.stringify(s)); } catch {}
}
function loadBackupHistory() {
  try { return JSON.parse(localStorage.getItem(BACKUP_HISTORY_KEY)||"[]"); } catch { return []; }
}
function saveBackupHistory(h) {
  try { localStorage.setItem(BACKUP_HISTORY_KEY, JSON.stringify(h.slice(0,50))); } catch {}
}

/* ─── Pantry Cloud API ───────────────────────────────────────────────────────
   Dokumentacja: https://documenter.getpostman.com/view/3281832/SzmZeMLC
   Endpointy:
     PUT  /apiv1/pantry/{pantryId}/basket/{basketName}  → zapisz JSON
     GET  /apiv1/pantry/{pantryId}/basket/{basketName}  → wczytaj JSON
     GET  /apiv1/pantry/{pantryId}                      → info o koncie (% zajęcia)
   Limit: 1 MB całego konta, dane usuwane po 30 dniach nieaktywności.
   Rejestracja: https://getpantry.cloud  (podajesz email → dostajesz Pantry ID)
─────────────────────────────────────────────────────────────────────────────*/
const PANTRY_BASE = "https://getpantry.cloud/apiv1/pantry";

/* Zapisz backup do Pantry Cloud (jako JSON w koszyku) */
async function uploadToPantry(pantryId, basketName, jsonObj) {
  if (!pantryId) throw new Error("Brak Pantry ID — uzupełnij w ustawieniach");
  // Pantry przyjmuje max 1 MB i wyłącznie obiekty JSON (nie tablice na top-level)
  const payload = typeof jsonObj === "object" && !Array.isArray(jsonObj)
    ? jsonObj
    : { data: jsonObj };
  const res = await fetch(`${PANTRY_BASE}/${pantryId}/basket/${encodeURIComponent(basketName)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Pantry HTTP ${res.status}: ${txt.slice(0,120)}`);
  }
  return await res.text();
}

/* Pobierz backup z Pantry Cloud */
async function downloadFromPantry(pantryId, basketName) {
  if (!pantryId) throw new Error("Brak Pantry ID");
  const res = await fetch(`${PANTRY_BASE}/${pantryId}/basket/${encodeURIComponent(basketName)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Pantry HTTP ${res.status}: ${txt.slice(0,120)}`);
  }
  return res.json();
}

/* Pobierz informacje o koncie Pantry (% zajęcia) */
async function getPantryInfo(pantryId) {
  if (!pantryId) throw new Error("Brak Pantry ID");
  const res = await fetch(`${PANTRY_BASE}/${pantryId}`, {
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`Pantry HTTP ${res.status}`);
  return res.json();
}

/* Pomocnik: pobierz dane Firebase Realtime Database */
async function fetchFirebaseData(databaseURL, secret) {
  if (!databaseURL) throw new Error("Brak URL bazy Firebase");
  if (!secret) throw new Error("Brak klucza Firebase (secret) — uzupełnij go w zakładce Ustawienia → Firebase");
  const url = databaseURL.replace(/\/$/, "") + `/.json?auth=${secret}`;
  const res = await fetch(url);
  if (res.status === 401) throw new Error("Firebase zwrócił błąd HTTP 401 — nieprawidłowy lub brakujący klucz autoryzacji (secret). Sprawdź konfigurację Firebase w Ustawieniach.");
  if (!res.ok) throw new Error(`Firebase zwrócił błąd HTTP ${res.status}`);
  return res.json();
}

/* Skróć JSON do limitu 1 MB Pantry usuwając zdjęcia z kategorii */
function compressBackupForPantry(data) {
  const SIZE_LIMIT = 900 * 1024; // 900 KB — bufor bezpieczeństwa
  let json = JSON.stringify(data);
  if (json.length <= SIZE_LIMIT) return data;
  // Usuń zdjęcia z kategorii (base64 — największy rozmiar)
  const stripped = {
    ...data,
    cats: (data.cats||[]).map(c => ({ ...c, image: c.image ? "[OBRAZ_USUNIĘTY]" : undefined })),
    settings: { ...data.settings, logo: data.settings?.logo ? "[LOGO_USUNIĘTE]" : undefined, customerBgImage: undefined },
  };
  json = JSON.stringify(stripped);
  if (json.length <= SIZE_LIMIT) return stripped;
  // Jeśli nadal za duże — usuń historię zamówień (zostaw ostatnie 50)
  return { ...stripped, orders: (stripped.orders||[]).slice(-50) };
}

/* ─── OneDrive (Microsoft Graph) — PKCE OAuth ─────────────────────────────
   Nie wymaga client secret ani backendu — cały flow działa w przeglądarce.
   Rejestracja: portal.azure.com → Azure Active Directory → App registrations
     → New registration → typ "Single-page application" → Redirect URI = URL tej strony
   Scopes: Files.ReadWrite (zapis/odczyt pliku), offline_access (refresh token)
   Plik jest zapisywany w: /Aplikacje/<nazwaRestauracji>/backup_<data>.json
──────────────────────────────────────────────────────────────────────────────*/
const OD_GRAPH = "https://graph.microsoft.com/v1.0";
const OD_AUTH  = "https://login.microsoftonline.com/common/oauth2/v2.0";
const OD_SCOPES = "files.readwrite offline_access user.read";

/* Generuj losowy string do PKCE/state */
function odRandString(len=32) {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr, b=>b.toString(16).padStart(2,"0")).join("").slice(0,len);
}

/* PKCE: generuj code_verifier i code_challenge */
async function odGenPKCE() {
  const verifier = odRandString(64);
  const enc = new TextEncoder();
  const hash = await crypto.subtle.digest("SHA-256", enc.encode(verifier));
  const challenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g,"-").replace(/\//g,"_").replace(/=/g,"");
  return { verifier, challenge };
}

/* Zapisz/wczytaj token OneDrive z localStorage */
function odSaveToken(t) { try { localStorage.setItem("od_token_v1", JSON.stringify(t)); } catch {} }
function odLoadToken()  { try { return JSON.parse(localStorage.getItem("od_token_v1")||"null"); } catch { return null; } }
function odClearToken() { try { localStorage.removeItem("od_token_v1"); } catch {} }

/* Sprawdź czy token jest ważny (z buforem 60 s) */
function odTokenValid(t) { return t && t.access_token && t.expires_at && Date.now() < t.expires_at - 60000; }

/* Odśwież token przez refresh_token */
async function odRefreshToken(clientId, refreshToken) {
  const params = new URLSearchParams({
    client_id: clientId,
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    scope: OD_SCOPES,
  });
  const res = await fetch(`${OD_AUTH}/token`, { method:"POST", headers:{"Content-Type":"application/x-www-form-urlencoded"}, body:params });
  if (!res.ok) { const e=await res.text(); throw new Error("Refresh token error: "+e.slice(0,120)); }
  const data = await res.json();
  const token = { ...data, expires_at: Date.now() + data.expires_in*1000 };
  odSaveToken(token);
  return token;
}

/* Pobierz ważny token (odśwież jeśli trzeba) */
async function odGetValidToken(clientId) {
  const t = odLoadToken();
  if (!t) throw new Error("Brak tokenu — zaloguj się do OneDrive");
  if (odTokenValid(t)) return t.access_token;
  if (t.refresh_token) {
    const refreshed = await odRefreshToken(clientId, t.refresh_token);
    return refreshed.access_token;
  }
  odClearToken();
  throw new Error("Token wygasł — zaloguj się ponownie do OneDrive");
}

/* Wyślij plik JSON do OneDrive /Aplikacje/<folder>/<filename> */
async function odUploadFile(accessToken, folderName, filename, jsonObj) {
  const json = JSON.stringify(jsonObj, null, 2);
  const path = encodeURIComponent(`Aplikacje/${folderName}/${filename}`);
  const url = `${OD_GRAPH}/me/drive/root:/${path}:/content`;
  const res = await fetch(url, {
    method: "PUT",
    headers: { Authorization:`Bearer ${accessToken}`, "Content-Type":"application/json" },
    body: json,
  });
  if (!res.ok) { const e=await res.text(); throw new Error(`OneDrive upload HTTP ${res.status}: ${e.slice(0,120)}`); }
  return res.json();
}

/* Listuj pliki w folderze /Aplikacje/<folder> */
async function odListFiles(accessToken, folderName) {
  const path = encodeURIComponent(`Aplikacje/${folderName}`);
  const url = `${OD_GRAPH}/me/drive/root:/${path}:/children?$orderby=lastModifiedDateTime desc&$top=20`;
  const res = await fetch(url, { headers:{ Authorization:`Bearer ${accessToken}` } });
  if (res.status===404) return [];
  if (!res.ok) { const e=await res.text(); throw new Error(`OneDrive list HTTP ${res.status}: ${e.slice(0,120)}`); }
  const data = await res.json();
  return data.value || [];
}

/* Pobierz zawartość pliku z OneDrive po download URL */
async function odDownloadFile(accessToken, fileId) {
  const url = `${OD_GRAPH}/me/drive/items/${fileId}/content`;
  const res = await fetch(url, { headers:{ Authorization:`Bearer ${accessToken}` } });
  if (!res.ok) throw new Error(`OneDrive download HTTP ${res.status}`);
  return res.json();
}

/* Pobierz podstawowe info o zalogowanym użytkowniku */
async function odGetMe(accessToken) {
  const res = await fetch(`${OD_GRAPH}/me`, { headers:{ Authorization:`Bearer ${accessToken}` } });
  if (!res.ok) throw new Error(`Graph /me HTTP ${res.status}`);
  return res.json();
}

/* ─── Box.com API — OAuth 2.0 (Authorization Code) ───────────────────────────
   Box nie obsługuje PKCE dla SPA (stan na 2025), dlatego flow wymaga
   client_id + client_secret (rejestracja w developer.box.com → My Apps).
   Scopes: root_readwrite (odczyt/zapis plików)
   Pliki zapisywane w: /Aplikacje/<nazwaRestauracji>/backup_<data>.json
   Dokumentacja: https://developer.box.com/guides/authentication/oauth2/
──────────────────────────────────────────────────────────────────────────────*/
const BOX_AUTH_URL  = "https://account.box.com/api/oauth2/authorize";
const BOX_TOKEN_URL = "https://api.box.com/oauth2/token";
const BOX_API       = "https://api.box.com/2.0";
const BOX_UPLOAD    = "https://upload.box.com/api/2.0";

/* Zapisz/wczytaj token Box z localStorage */
function boxSaveToken(t) { try { localStorage.setItem("box_token_v1", JSON.stringify(t)); } catch {} }
function boxLoadToken()  { try { return JSON.parse(localStorage.getItem("box_token_v1")||"null"); } catch { return null; } }
function boxClearToken() { try { localStorage.removeItem("box_token_v1"); } catch {} }

/* Sprawdź czy token jest ważny (z buforem 60 s) */
function boxTokenValid(t) { return t && t.access_token && t.expires_at && Date.now() < t.expires_at - 60000; }

/* Odśwież token przez refresh_token */
async function boxRefreshToken(clientId, clientSecret, refreshToken) {
  const params = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: clientId,
    client_secret: clientSecret,
  });
  const res = await fetch(BOX_TOKEN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: params,
  });
  if (!res.ok) { const e = await res.text(); throw new Error("Box refresh token error: " + e.slice(0, 120)); }
  const data = await res.json();
  const token = { ...data, expires_at: Date.now() + (data.expires_in || 3600) * 1000 };
  boxSaveToken(token);
  return token;
}

/* Pobierz ważny token Box (odśwież jeśli trzeba) */
async function boxGetValidToken(clientId, clientSecret) {
  const t = boxLoadToken();
  if (!t) throw new Error("Brak tokenu Box — zaloguj się do Box.com");
  if (boxTokenValid(t)) return t.access_token;
  if (t.refresh_token && clientId && clientSecret) {
    const refreshed = await boxRefreshToken(clientId, clientSecret, t.refresh_token);
    return refreshed.access_token;
  }
  boxClearToken();
  throw new Error("Token Box wygasł — zaloguj się ponownie");
}

/* Znajdź lub utwórz folder w Box (zwraca folder_id) */
async function boxEnsureFolder(accessToken, folderName, parentId = "0") {
  // Próbujemy bezpośrednio utworzyć folder (metoda bardziej odporna na błędy 403 przy listowaniu)
  const createRes = await fetch(`${BOX_API}/folders`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ name: folderName, parent: { id: parentId } }),
  });

  if (createRes.ok) {
    const folderData = await createRes.json();
    return folderData.id;
  }

  const errText = await createRes.text();
  let errJson = {};
  try { errJson = JSON.parse(errText); } catch {}

  // Jeśli folder już istnieje (kod błędu 'item_name_in_use') — pobierz ID z odpowiedzi lub przez listowanie
  if (createRes.status === 409) {
    // Spróbuj wyciągnąć ID z context_info
    const conflictId = errJson?.context_info?.conflicts?.[0]?.id;
    if (conflictId) return conflictId;
    // Fallback: wylistuj i znajdź po nazwie
    const listRes = await fetch(`${BOX_API}/folders/${parentId}/items?fields=id,name,type&limit=1000`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (listRes.ok) {
      const listData = await listRes.json();
      const existing = (listData.entries || []).find(e => e.type === "folder" && e.name === folderName);
      if (existing) return existing.id;
    }
    throw new Error(`Box API: Folder '${folderName}' już istnieje ale nie można pobrać jego ID`);
  }

  // Jeśli inny błąd — spróbujmy jednak wylistować (fallback)
  const listRes = await fetch(`${BOX_API}/folders/${parentId}/items?fields=id,name,type&limit=1000`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!listRes.ok) {
    throw new Error(`Box API Error: Nie można utworzyć ani wylistować folderu '${folderName}' (HTTP ${createRes.status}). Upewnij się, że masz uprawnienia do zapisu w Box.`);
  }
  const listData = await listRes.json();
  const existing = (listData.entries || []).find(e => e.type === "folder" && e.name === folderName);
  if (existing) return existing.id;

  throw new Error(`Box API Error: ${errJson.message || errText}`);
}

/* Wyślij plik JSON do Box /Aplikacje/<folder>/<filename> */
async function boxUploadFile(accessToken, folderName, filename, jsonObj) {
  const json = JSON.stringify(jsonObj, null, 2);
  const blob = new Blob([json], { type: "application/json" });

  // Upewnij się, że folder istnieje
  const appFolderId = await boxEnsureFolder(accessToken, "Aplikacje", "0");
  const subFolderId = await boxEnsureFolder(accessToken, folderName, appFolderId);

  // Sprawdź czy plik o tej nazwie już istnieje (aby użyć wersjonowania)
  const listRes = await fetch(`${BOX_API}/folders/${subFolderId}/items?fields=id,name,type&limit=200`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const listData = listRes.ok ? await listRes.json() : { entries: [] };
  const existingFile = (listData.entries || []).find(e => e.type === "file" && e.name === filename);

  const formData = new FormData();
  formData.append("attributes", JSON.stringify({
    name: filename,
    parent: { id: subFolderId },
  }));
  formData.append("file", blob, filename);

  let uploadUrl, method;
  if (existingFile) {
    // Nowa wersja istniejącego pliku
    uploadUrl = `${BOX_UPLOAD}/files/${existingFile.id}/content`;
    method = "POST";
  } else {
    // Nowy plik
    uploadUrl = `${BOX_UPLOAD}/files/content`;
    method = "POST";
  }

  const res = await fetch(uploadUrl, {
    method,
    headers: { Authorization: `Bearer ${accessToken}` },
    body: formData,
  });
  if (!res.ok) { const e = await res.text(); throw new Error(`Box upload HTTP ${res.status}: ${e.slice(0, 120)}`); }
  return res.json();
}

/* Listuj pliki w folderze Box /Aplikacje/<folder> */
async function boxListFiles(accessToken, folderName) {
  try {
    const appFolderId = await boxEnsureFolder(accessToken, "Aplikacje", "0");
    const subFolderId = await boxEnsureFolder(accessToken, folderName, appFolderId);
    const res = await fetch(`${BOX_API}/folders/${subFolderId}/items?fields=id,name,size,modified_at,type&limit=50`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) { const e = await res.text(); throw new Error(`Box list HTTP ${res.status}: ${e.slice(0, 120)}`); }
    const data = await res.json();
    return (data.entries || []).filter(e => e.type === "file").sort((a, b) => new Date(b.modified_at) - new Date(a.modified_at));
  } catch (e) {
    if (e.message && e.message.includes("404")) return [];
    throw e;
  }
}

/* Pobierz zawartość pliku z Box po file_id */
async function boxDownloadFile(accessToken, fileId) {
  // Pobierz URL do pobrania
  const res = await fetch(`${BOX_API}/files/${fileId}/content`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    redirect: "follow",
  });
  if (!res.ok) throw new Error(`Box download HTTP ${res.status}`);
  return res.json();
}

/* Pobierz info o zalogowanym użytkowniku Box */
async function boxGetMe(accessToken) {
  const res = await fetch(`${BOX_API}/users/me`, { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!res.ok) throw new Error(`Box /users/me HTTP ${res.status}`);
  return res.json();
}

function BackupPanel({ menu, cats, users, settings, tables, orders, campaigns, vouchers, usedCodes, staffCalls, clients,
  setMenu, setCats, setUsers, setSettings, setTables, setOrders, setCampaigns, setVouchers, setUsedCodes, setClients,
  fbConfig }) {

  const [importing,       setImporting]       = useState(false);
  const [msg,             setMsg]             = useState(null);
  const [tab,             setTab]             = useState("manual"); // manual | pantry | onedrive | schedule | history
  const [bkSettings,     setBkSettings]      = useState(() => loadBackupSettings(settings));
  const [history,        setHistory]         = useState(() => loadBackupHistory());
  const [pantryStatus,   setPantryStatus]    = useState(null); // null | "sending" | "ok" | "error"
  const [fbPantryStatus, setFbPantryStatus]  = useState(null);
  const [pantryMsg,      setPantryMsg]       = useState(null);
  const [pantryInfo,     setPantryInfo]      = useState(null);
  const [pantryInfoLoading, setPantryInfoLoading] = useState(false);
  const [scheduleMsg,    setScheduleMsg]     = useState(null);
  const [restoreLoading, setRestoreLoading]  = useState(false);
  /* OneDrive */
  const [odMsg,          setOdMsg]           = useState(null);
  const [odStatus,       setOdStatus]        = useState(null); // null | "loading" | "uploading" | "listing" | "restoring" | "ok" | "error"
  const [odToken,        setOdTokenState]    = useState(() => odLoadToken());
  const [odMe,           setOdMe]            = useState(null);
  const [odFiles,        setOdFiles]         = useState(null);
  const [odClientId,     setOdClientId]      = useState(() => { try { return settings?._backupOdClientId || localStorage.getItem("od_client_id")||""; } catch { return ""; } });
  const [odShowHelp,     setOdShowHelp]      = useState(false);
  /* Box.com */
  const [boxMsg,         setBoxMsg]          = useState(null);
  const [boxStatus,      setBoxStatus]       = useState(null); // null | "uploading" | "listing" | "restoring" | "ok" | "error"
  const [boxToken,       setBoxTokenState]   = useState(() => boxLoadToken());
  const [boxMe,          setBoxMe]           = useState(null);
  const [boxFiles,       setBoxFiles]        = useState(null);
  const [boxClientId,    setBoxClientId]     = useState(() => { try { return settings?._backupBoxClientId || localStorage.getItem("box_client_id")||"" } catch { return ""; } });
  const [boxClientSecret,setBoxClientSecret] = useState(() => { try { return settings?._backupBoxSecret || localStorage.getItem("box_client_secret")||"" } catch { return ""; } });
  const [boxShowHelp,    setBoxShowHelp]     = useState(false);
  const fileRef = useRef();
  const schedTimerRef = useRef(null);
  const boxSchedTimerRef = useRef(null);
  const odPkceRef = useRef(null);

  /* Zachowaj ustawienia przy każdej zmianie */
  useEffect(() => { saveBackupSettings(bkSettings); }, [bkSettings]);

  /* Buduj obiekt danych do kopii zapasowej */
  const buildBackupData = () => ({
    version: 8,
    exportedAt: new Date().toISOString(),
    appName: settings?.restaurantName || "SmakiDomu",
    menu, cats, users, settings, tables, orders,
    campaigns, vouchers, usedCodes, staffCalls, clients
  });

  const buildBasketName = (prefix="backup") => {
    const name = (settings?.restaurantName || "app").replace(/[^a-zA-Z0-9_-]/g, "_");
    return `${prefix}_${name}`;
  };

  /* Dodaj wpis do historii */
  const addHistory = (entry) => {
    const h = [{ id: Date.now(), ...entry }, ...loadBackupHistory()].slice(0, 50);
    saveBackupHistory(h);
    setHistory(h);
  };

  /* ── POBIERZ (ręcznie) ── */
  const downloadBackup = () => {
    const data  = buildBackupData();
    const json  = JSON.stringify(data, null, 2);
    const blob  = new Blob([json], { type: "application/json" });
    const url   = URL.createObjectURL(blob);
    const a     = document.createElement("a");
    const name  = (settings?.restaurantName || "app").replace(/\s+/g, "-");
    const fname = `backup-${name}-v8-${new Date().toISOString().slice(0,10)}.json`;
    a.href = url; a.download = fname;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    const catWithImg = (cats||[]).filter(c=>c.image).length;
    setMsg({ type:"ok", text:`✅ Pobrano (${(json.length/1024).toFixed(1)} KB)${catWithImg>0?" · "+catWithImg+" kategorii ze zdjęciem":""}` });
    addHistory({ type:"download", label:"Ręczne pobranie", filename:fname, ok:true, size:`${(json.length/1024).toFixed(1)} KB` });
    setTimeout(()=>setMsg(null), 5000);
  };

  /* ── IMPORT Z PLIKU ── */
  const handleImport = async e => {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = "";
    setImporting(true); setMsg(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.version || (!data.menu && !data.settings)) {
        setMsg({ type:"err", text:"❌ Nieprawidłowy plik — to nie jest kopia zapasowa tej aplikacji" });
        setImporting(false); return;
      }
      const date = data.exportedAt ? new Date(data.exportedAt).toLocaleString("pl-PL") : "nieznana data";
      const addonsCount = (data.menu||[]).reduce((s,i)=>s+(i.addons?.length||0),0);
      const confirmed = window.confirm(
        `Przywrócić kopię zapasową?\n\n` +
        `Aplikacja: ${data.appName||"?"}\nData: ${date}\nWersja: ${data.version||"?"}\n` +
        `Produkty: ${data.menu?.length||0} (dodatki: ${addonsCount})\n` +
        `Kategorie: ${data.cats?.length||0}${data.version>=6?" (ze zdjęciami)":""}\n` +
        `Zamówienia: ${data.orders?.length||0} · Kontrahenci: ${data.clients?.length||0}\n\n` +
        `⚠ WSZYSTKIE OBECNE DANE ZOSTANĄ ZASTĄPIONE!`
      );
      if (!confirmed) { setImporting(false); return; }
      if (Array.isArray(data.menu))      setMenu(data.menu);
      if (Array.isArray(data.cats))      setCats(data.cats);
      if (data.settings)                 setSettings(data.settings);
      if (Array.isArray(data.tables))    setTables(data.tables);
      if (Array.isArray(data.orders))    setOrders(data.orders);
      if (Array.isArray(data.campaigns)) setCampaigns(data.campaigns);
      if (Array.isArray(data.vouchers))  setVouchers(data.vouchers);
      if (Array.isArray(data.usedCodes)) setUsedCodes(data.usedCodes);
      if (Array.isArray(data.clients))   setClients && setClients(data.clients);
      if (Array.isArray(data.users) && data.users.every(u=>u.passwordHash)) setUsers(data.users);
      addHistory({ type:"import", label:"Import z pliku", filename:file.name, ok:true });
      setMsg({ type:"ok", text:"✅ Dane przywrócone pomyślnie! Strona odświeży się za 3 sekundy..." });
      setTimeout(()=>window.location.reload(), 3000);
    } catch(err) {
      setMsg({ type:"err", text:"❌ Błąd importu: " + err.message });
    }
    setImporting(false);
  };

  /* ── WYŚLIJ NA PANTRY CLOUD ── */
  const sendToPantry = async (isAuto=false) => {
    const pantryId = bkSettings.pantryId || "";
    setPantryStatus("sending"); setPantryMsg(null);
    try {
      const raw     = buildBackupData();
      const data    = compressBackupForPantry(raw);
      const basket  = buildBasketName(isAuto ? "auto_backup" : "backup");
      const rawSize = JSON.stringify(raw).length;
      const cmpSize = JSON.stringify(data).length;
      const compressed = cmpSize < rawSize;
      await uploadToPantry(pantryId, basket, data);
      setPantryStatus("ok");
      setPantryMsg({ ok:true, text:`✅ Kopia wysłana do Pantry Cloud! Koszyk: "${basket}" (${(cmpSize/1024).toFixed(1)} KB${compressed?" — skompresowana, zdjęcia pominięte":""})` });
      addHistory({ type:"pantry", label: isAuto?"Auto → Pantry Cloud":"Ręcznie → Pantry Cloud", basket, ok:true, size:`${(cmpSize/1024).toFixed(1)} KB` });
      setBkSettings(p=>({...p, lastPantryBackup: new Date().toISOString(), lastPantryBasket: basket}));
    } catch(err) {
      setPantryStatus("error");
      setPantryMsg({ ok:false, text:"❌ Błąd wysyłania: " + err.message });
      addHistory({ type:"pantry", label: isAuto?"Auto → Pantry (błąd)":"Pantry (błąd)", ok:false, error: err.message });
    }
  };

  /* ── PRZYWRÓĆ Z PANTRY CLOUD ── */
  const restoreFromPantry = async () => {
    const pantryId = bkSettings.pantryId || "";
    const basket   = bkSettings.lastPantryBasket || buildBasketName("backup");
    setRestoreLoading(true); setPantryMsg(null);
    try {
      const data = await downloadFromPantry(pantryId, basket);
      if (!data.version || (!data.menu && !data.settings)) {
        setPantryMsg({ ok:false, text:`❌ Koszyk "${basket}" nie zawiera prawidłowej kopii zapasowej` });
        setRestoreLoading(false); return;
      }
      const date = data.exportedAt ? new Date(data.exportedAt).toLocaleString("pl-PL") : "nieznana data";
      const confirmed = window.confirm(
        `Przywrócić kopię z Pantry Cloud?\n\nKoszyk: ${basket}\nData: ${date}\nWersja: ${data.version||"?"}\n` +
        `Produkty: ${data.menu?.length||0} · Zamówienia: ${data.orders?.length||0}\n\n` +
        `⚠ WSZYSTKIE OBECNE DANE ZOSTANĄ ZASTĄPIONE!`
      );
      if (!confirmed) { setRestoreLoading(false); return; }
      if (Array.isArray(data.menu))      setMenu(data.menu);
      if (Array.isArray(data.cats))      setCats(data.cats);
      if (data.settings)                 setSettings(data.settings);
      if (Array.isArray(data.tables))    setTables(data.tables);
      if (Array.isArray(data.orders))    setOrders(data.orders);
      if (Array.isArray(data.campaigns)) setCampaigns(data.campaigns);
      if (Array.isArray(data.vouchers))  setVouchers(data.vouchers);
      if (Array.isArray(data.usedCodes)) setUsedCodes(data.usedCodes);
      if (Array.isArray(data.clients))   setClients && setClients(data.clients);
      if (Array.isArray(data.users) && data.users.every(u=>u.passwordHash)) setUsers(data.users);
      addHistory({ type:"restore", label:"Przywrócono z Pantry Cloud", basket, ok:true });
      setPantryMsg({ ok:true, text:"✅ Dane przywrócone! Strona odświeży się za 3 sekundy..." });
      setTimeout(()=>window.location.reload(), 3000);
    } catch(err) {
      setPantryMsg({ ok:false, text:"❌ Błąd przywracania: " + err.message });
    }
    setRestoreLoading(false);
  };

  /* ── KOPIA FIREBASE → PANTRY ── */
  const sendFirebaseToPantry = async () => {
    const pantryId = bkSettings.pantryId || "";
    setFbPantryStatus("sending"); setPantryMsg(null);
    try {
      const fbData  = await fetchFirebaseData(fbConfig?.databaseURL, fbConfig?.secret);
      const payload = { version:8, exportedAt: new Date().toISOString(), source:"firebase", appName: settings?.restaurantName||"SmakiDomu", data: fbData };
      const basket  = buildBasketName("firebase_backup");
      // Firebase dump może być duży — spróbuj z pełnym, jeśli za duże to przytnij
      let toSend = payload;
      if (JSON.stringify(toSend).length > 900*1024) {
        toSend = { ...payload, data: null, note:"Dane Firebase zbyt duże dla Pantry (>1MB). Użyj ręcznej kopii." };
      }
      await uploadToPantry(pantryId, basket, toSend);
      const size = JSON.stringify(toSend).length;
      setFbPantryStatus("ok");
      setPantryMsg({ ok:true, text:`✅ Kopia Firebase wysłana! Koszyk: "${basket}" (${(size/1024).toFixed(1)} KB)` });
      addHistory({ type:"firebase", label:"Firebase → Pantry Cloud", basket, ok:true, size:`${(size/1024).toFixed(1)} KB` });
      setBkSettings(p=>({...p, lastFirebaseBackup: new Date().toISOString()}));
    } catch(err) {
      setFbPantryStatus("error");
      setPantryMsg({ ok:false, text:"❌ Błąd kopii Firebase: " + err.message });
      addHistory({ type:"firebase", label:"Firebase → Pantry (błąd)", ok:false, error: err.message });
    }
  };

  /* ── INFO O KONCIE PANTRY ── */
  const loadPantryInfo = async () => {
    const pantryId = bkSettings.pantryId || "";
    if (!pantryId) return;
    setPantryInfoLoading(true);
    try {
      const info = await getPantryInfo(pantryId);
      setPantryInfo(info);
    } catch(err) {
      setPantryInfo({ error: err.message });
    }
    setPantryInfoLoading(false);
  };

  /* ══════════════════ OneDrive ══════════════════════════════════════════════ */

  /* Zapisz Client ID i zainicjuj logowanie PKCE */
  const odSaveClientId = (id) => {
    setOdClientId(id);
    try { localStorage.setItem("od_client_id", id); } catch {}
    if (setSettings) setSettings(p => ({ ...p, _backupOdClientId: id }));
  };

  /* Krok 1: otwórz okno autoryzacji Microsoft */
  const odLogin = async () => {
    if (!odClientId.trim()) { setOdMsg({ ok:false, text:"❌ Najpierw wpisz Client ID aplikacji Azure." }); return; }
    setOdMsg(null);
    const { verifier, challenge } = await odGenPKCE();
    const state = odRandString(16);
    odPkceRef.current = { verifier, state };
    sessionStorage.setItem("od_pkce_verifier", verifier);
    sessionStorage.setItem("od_pkce_state", state);
    sessionStorage.setItem("od_client_id", odClientId.trim());
    const redirectUri = window.location.origin + window.location.pathname;
    const params = new URLSearchParams({
      client_id: odClientId.trim(),
      response_type: "code",
      redirect_uri: redirectUri,
      scope: OD_SCOPES,
      code_challenge: challenge,
      code_challenge_method: "S256",
      state,
    });
    window.location.href = `${OD_AUTH}/authorize?${params}`;
  };

  /* Krok 2: po powrocie z OAuth pobierz token (wywołaj raz przy montowaniu) */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const stateParam = urlParams.get("state");
    const verifier = sessionStorage.getItem("od_pkce_verifier");
    const savedState = sessionStorage.getItem("od_pkce_state");
    const savedClientId = sessionStorage.getItem("od_client_id");
    if (!code || !verifier || stateParam !== savedState) return;
    sessionStorage.removeItem("od_pkce_verifier");
    sessionStorage.removeItem("od_pkce_state");
    sessionStorage.removeItem("od_client_id");
    // Wyczyść parametry z URL
    const clean = window.location.origin + window.location.pathname;
    window.history.replaceState({}, "", clean);
    const redirectUri = window.location.origin + window.location.pathname;
    (async () => {
      try {
        const params = new URLSearchParams({
          client_id: savedClientId,
          grant_type: "authorization_code",
          code,
          redirect_uri: redirectUri,
          code_verifier: verifier,
        });
        const res = await fetch(`${OD_AUTH}/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params,
        });
        if (!res.ok) { const e=await res.text(); throw new Error("Token error: "+e.slice(0,120)); }
        const data = await res.json();
        const token = { ...data, expires_at: Date.now() + data.expires_in*1000 };
        odSaveToken(token);
        odSaveClientId(savedClientId);
        setOdTokenState(token);
        setTab("onedrive");
        // Pobierz profil
        const me = await odGetMe(token.access_token).catch(()=>null);
        setOdMe(me);
        setOdMsg({ ok:true, text:`✅ Zalogowano do OneDrive${me?" jako "+me.displayName:""}!` });
      } catch(err) {
        setOdMsg({ ok:false, text:"❌ Błąd logowania: "+err.message });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Pobierz profil jeśli mamy token */
  useEffect(() => {
    if (!odToken || !odClientId) return;
    (async () => {
      try {
        const at = await odGetValidToken(odClientId);
        const me = await odGetMe(at).catch(()=>null);
        setOdMe(me);
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Wyślij kopię na OneDrive */
  const odUploadBackup = async () => {
    setOdStatus("uploading"); setOdMsg(null);
    try {
      const at = await odGetValidToken(odClientId);
      const data = buildBackupData();
      const name = (settings?.restaurantName||"app").replace(/[^a-zA-Z0-9_-]/g,"_");
      const fname = `backup-${name}-v8-${new Date().toISOString().slice(0,10)}.json`;
      await odUploadFile(at, name, fname, data);
      const size = (JSON.stringify(data).length/1024).toFixed(1);
      setOdStatus("ok");
      setOdMsg({ ok:true, text:`✅ Kopia zapisana w OneDrive! Plik: ${fname} (${size} KB)` });
      addHistory({ type:"onedrive", label:"Kopia → OneDrive", filename:fname, ok:true, size:`${size} KB` });
      // Odśwież listę plików
      const files = await odListFiles(at, name).catch(()=>[]);
      setOdFiles(files);
    } catch(err) {
      setOdStatus("error");
      setOdMsg({ ok:false, text:"❌ Błąd OneDrive: "+err.message });
      addHistory({ type:"onedrive", label:"OneDrive (błąd)", ok:false, error: err.message });
    }
  };

  /* Listuj pliki kopii w OneDrive */
  const odLoadFiles = async () => {
    setOdStatus("listing"); setOdMsg(null);
    try {
      const at = await odGetValidToken(odClientId);
      const name = (settings?.restaurantName||"app").replace(/[^a-zA-Z0-9_-]/g,"_");
      const files = await odListFiles(at, name);
      setOdFiles(files);
      setOdStatus(null);
      if (files.length===0) setOdMsg({ ok:true, text:"ℹ Brak plików kopii w folderze OneDrive." });
    } catch(err) {
      setOdStatus("error");
      setOdMsg({ ok:false, text:"❌ Błąd listowania: "+err.message });
    }
  };

  /* Przywróć z OneDrive (po wybraniu pliku) */
  const odRestore = async (fileId, fileName) => {
    const confirmed = window.confirm(`Przywrócić kopię z pliku:\n${fileName}\n\n⚠ WSZYSTKIE OBECNE DANE ZOSTANĄ ZASTĄPIONE!`);
    if (!confirmed) return;
    setOdStatus("restoring"); setOdMsg(null);
    try {
      const at = await odGetValidToken(odClientId);
      const data = await odDownloadFile(at, fileId);
      if (!data.version || (!data.menu && !data.settings)) throw new Error("Plik nie jest prawidłową kopią zapasową");
      if (Array.isArray(data.menu))      setMenu(data.menu);
      if (Array.isArray(data.cats))      setCats(data.cats);
      if (data.settings)                 setSettings(data.settings);
      if (Array.isArray(data.tables))    setTables(data.tables);
      if (Array.isArray(data.orders))    setOrders(data.orders);
      if (Array.isArray(data.campaigns)) setCampaigns(data.campaigns);
      if (Array.isArray(data.vouchers))  setVouchers(data.vouchers);
      if (Array.isArray(data.usedCodes)) setUsedCodes(data.usedCodes);
      if (Array.isArray(data.clients))   setClients && setClients(data.clients);
      if (Array.isArray(data.users) && data.users.every(u=>u.passwordHash)) setUsers(data.users);
      addHistory({ type:"restore", label:"Przywrócono z OneDrive", filename:fileName, ok:true });
      setOdMsg({ ok:true, text:"✅ Dane przywrócone! Strona odświeży się za 3 sekundy..." });
      setTimeout(()=>window.location.reload(), 3000);
    } catch(err) {
      setOdStatus("error");
      setOdMsg({ ok:false, text:"❌ Błąd przywracania: "+err.message });
    }
    setOdStatus(null);
  };

  /* Wyloguj z OneDrive */
  const odLogout = () => {
    odClearToken();
    setOdTokenState(null);
    setOdMe(null);
    setOdFiles(null);
    setOdMsg({ ok:true, text:"ℹ Wylogowano z OneDrive." });
  };

  /* ══════════════════ Box.com ═══════════════════════════════════════════════════ */

  /* Zapisz Client ID/Secret i odśwież stan */
  const boxSaveClientId = (id) => {
    setBoxClientId(id);
    try { localStorage.setItem("box_client_id", id); } catch {}
    if (setSettings) setSettings(p => ({ ...p, _backupBoxClientId: id }));
  };
  const boxSaveClientSecret = (s) => {
    setBoxClientSecret(s);
    try { localStorage.setItem("box_client_secret", s); } catch {}
    if (setSettings) setSettings(p => ({ ...p, _backupBoxSecret: s }));
  };

  /* Krok 1: przekieruj do Box OAuth */
  const boxLogin = () => {
    if (!boxClientId.trim()) { setBoxMsg({ ok:false, text:"❌ Najpierw wpisz Client ID aplikacji Box." }); return; }
    setBoxMsg(null);
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem("box_oauth_state", state);
    sessionStorage.setItem("box_client_id", boxClientId.trim());
    sessionStorage.setItem("box_client_secret", boxClientSecret.trim());
    const redirectUri = window.location.origin + window.location.pathname;
    const params = new URLSearchParams({
      client_id: boxClientId.trim(),
      response_type: "code",
      redirect_uri: redirectUri,
      state,
    });
    window.location.href = `${BOX_AUTH_URL}?${params}`;
  };

  /* Krok 2: po powrocie z OAuth wymień code na token */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const stateParam = urlParams.get("state");
    const savedState = sessionStorage.getItem("box_oauth_state");
    const savedClientId = sessionStorage.getItem("box_client_id");
    const savedClientSecret = sessionStorage.getItem("box_client_secret");
    if (!code || !savedClientId || stateParam !== savedState) return;
    sessionStorage.removeItem("box_oauth_state");
    sessionStorage.removeItem("box_client_id");
    sessionStorage.removeItem("box_client_secret");
    const clean = window.location.origin + window.location.pathname;
    window.history.replaceState({}, "", clean);
    const redirectUri = window.location.origin + window.location.pathname;
    (async () => {
      try {
        const params = new URLSearchParams({
          grant_type: "authorization_code",
          code,
          client_id: savedClientId,
          client_secret: savedClientSecret,
          redirect_uri: redirectUri,
        });
        const res = await fetch(BOX_TOKEN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: params,
        });
        if (!res.ok) { const e = await res.text(); throw new Error("Token error: " + e.slice(0, 120)); }
        const data = await res.json();
        const token = { ...data, expires_at: Date.now() + (data.expires_in || 3600) * 1000 };
        boxSaveToken(token);
        boxSaveClientId(savedClientId);
        boxSaveClientSecret(savedClientSecret);
        setBoxTokenState(token);
        setTab("box");
        const me = await boxGetMe(token.access_token).catch(() => null);
        setBoxMe(me);
        setBoxMsg({ ok:true, text:`✅ Zalogowano do Box.com${me ? " jako " + (me.name || me.login) : ""}!` });
      } catch(err) {
        setBoxMsg({ ok:false, text:"❌ Błąd logowania Box: " + err.message });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Pobierz profil jeśli mamy token */
  useEffect(() => {
    if (!boxToken || !boxClientId || !boxClientSecret) return;
    (async () => {
      try {
        const at = await boxGetValidToken(boxClientId, boxClientSecret);
        setBoxTokenState(boxLoadToken());
        const me = await boxGetMe(at).catch(() => null);
        setBoxMe(me);
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Wyślij kopię na Box.com */
  const boxUploadBackup = async (isAuto = false) => {
    setBoxStatus("uploading"); if (!isAuto) setBoxMsg(null);
    try {
      const at = await boxGetValidToken(boxClientId, boxClientSecret);
      const data = buildBackupData();
      const name = (settings?.restaurantName || "app").replace(/[^a-zA-Z0-9_-]/g, "_");
      const fname = `backup-${name}-v8-${new Date().toISOString().slice(0, 10)}.json`;
      await boxUploadFile(at, name, fname, data);
      const size = (JSON.stringify(data).length / 1024).toFixed(1);
      setBoxStatus("ok");
      const nowIso = new Date().toISOString();
      setBkSettings(p => ({ ...p, lastBoxBackup: nowIso }));
      if (!isAuto) setBoxMsg({ ok:true, text:`✅ Kopia zapisana w Box.com! Plik: ${fname} (${size} KB)` });
      addHistory({ type:"box", label: isAuto ? "Auto → Box.com" : "Kopia → Box.com", filename:fname, ok:true, size:`${size} KB` });
      const files = await boxListFiles(at, name).catch(() => []);
      setBoxFiles(files);
    } catch(err) {
      setBoxStatus("error");
      if (!isAuto) setBoxMsg({ ok:false, text:"❌ Błąd Box.com: " + err.message });
      addHistory({ type:"box", label:"Box.com (błąd)", ok:false, error: err.message });
    }
  };

  /* Listuj pliki kopii w Box.com */
  const boxLoadFiles = async () => {
    setBoxStatus("listing"); setBoxMsg(null);
    try {
      const at = await boxGetValidToken(boxClientId, boxClientSecret);
      const name = (settings?.restaurantName || "app").replace(/[^a-zA-Z0-9_-]/g, "_");
      const files = await boxListFiles(at, name);
      setBoxFiles(files);
      setBoxStatus(null);
      if (files.length === 0) setBoxMsg({ ok:true, text:"ℹ Brak plików kopii w folderze Box.com." });
    } catch(err) {
      setBoxStatus("error");
      setBoxMsg({ ok:false, text:"❌ Błąd listowania Box: " + err.message });
    }
  };

  /* Przywróć z Box.com */
  const boxRestore = async (fileId, fileName) => {
    const confirmed = window.confirm(`Przywrócić kopię z pliku:\n${fileName}\n\n⚠ WSZYSTKIE OBECNE DANE ZOSTANĄ ZASTĄPIONE!`);
    if (!confirmed) return;
    setBoxStatus("restoring"); setBoxMsg(null);
    try {
      const at = await boxGetValidToken(boxClientId, boxClientSecret);
      const data = await boxDownloadFile(at, fileId);
      if (!data.version || (!data.menu && !data.settings)) throw new Error("Plik nie jest prawidłową kopią zapasową");
      if (Array.isArray(data.menu))      setMenu(data.menu);
      if (Array.isArray(data.cats))      setCats(data.cats);
      if (data.settings)                 setSettings(data.settings);
      if (Array.isArray(data.tables))    setTables(data.tables);
      if (Array.isArray(data.orders))    setOrders(data.orders);
      if (Array.isArray(data.campaigns)) setCampaigns(data.campaigns);
      if (Array.isArray(data.vouchers))  setVouchers(data.vouchers);
      if (Array.isArray(data.usedCodes)) setUsedCodes(data.usedCodes);
      if (Array.isArray(data.clients))   setClients && setClients(data.clients);
      if (Array.isArray(data.users) && data.users.every(u => u.passwordHash)) setUsers(data.users);
      addHistory({ type:"restore", label:"Przywrócono z Box.com", filename:fileName, ok:true });
      setBoxMsg({ ok:true, text:"✅ Dane przywrócone z Box.com! Strona odświeży się za 3 sekundy..." });
      setTimeout(() => window.location.reload(), 3000);
    } catch(err) {
      setBoxStatus("error");
      setBoxMsg({ ok:false, text:"❌ Błąd przywrócenia: " + err.message });
    }
    setBoxStatus(null);
  };

  /* Wyloguj z Box.com */
  const boxLogout = () => {
    boxClearToken();
    setBoxTokenState(null);
    setBoxMe(null);
    setBoxFiles(null);
    setBoxMsg({ ok:true, text:"ℹ Wylogowano z Box.com." });
  };

  /* ── HARMONOGRAM Box.com — sprawdzaj co minutę ── */
  useEffect(() => {
    if (boxSchedTimerRef.current) clearInterval(boxSchedTimerRef.current);
    if (!bkSettings.boxSchedEnabled || !boxToken || !boxClientId || !boxClientSecret) return;
    // Nie uruchamiaj harmonogramu jeśli token wygasł i nie ma refresh_token
    if (!boxTokenValid(boxToken) && !boxToken.refresh_token) return;
    const check = async () => {
      // Dodatkowe sprawdzenie ważności tokenu przed każdą próbą
      const currentToken = boxLoadToken();
      if (!currentToken || (!boxTokenValid(currentToken) && !currentToken.refresh_token)) return;
      const now     = new Date();
      const lastRaw = bkSettings.lastBoxBackup;
      const last    = lastRaw ? new Date(lastRaw) : null;
      const freq    = bkSettings.boxSchedFreq || "daily";
      const schedTime = bkSettings.boxSchedTime || "04:00";
      const [hh, mm]  = schedTime.split(":").map(Number);
      let due = false;
      if (freq === "daily") {
        const todayAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm);
        due = now >= todayAt && (!last || last < todayAt);
      } else if (freq === "weekly") {
        const day = bkSettings.boxSchedDay ?? 1;
        const todayAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm);
        due = now.getDay() === day && now >= todayAt && (!last || last < todayAt);
      } else if (freq === "hourly") {
        due = !last || (now - last) >= 3600000;
      }
      if (due) {
        setScheduleMsg("🔄 Trwa automatyczna kopia zapasowa na Box.com...");
        await boxUploadBackup(true).catch(() => {});
        setScheduleMsg(null);
      }
    };
    boxSchedTimerRef.current = setInterval(check, 60000);
    check();
    return () => clearInterval(boxSchedTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bkSettings.boxSchedEnabled, bkSettings.lastBoxBackup, bkSettings.boxSchedFreq, bkSettings.boxSchedTime, bkSettings.boxSchedDay, boxToken, boxClientId, boxClientSecret]);

  /* ── HARMONOGRAM — sprawdzaj co minutę ── */
  useEffect(() => {
    if (schedTimerRef.current) clearInterval(schedTimerRef.current);
    if (!bkSettings.schedEnabled || !bkSettings.pantryId) return;
    const check = async () => {
      const now       = new Date();
      const lastRaw   = bkSettings.lastPantryBackup;
      const last      = lastRaw ? new Date(lastRaw) : null;
      const freq      = bkSettings.schedFreq || "daily";
      const schedTime = bkSettings.schedTime || "03:00";
      const [hh, mm]  = schedTime.split(":").map(Number);
      let due = false;
      if (freq === "daily") {
        const todayAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm);
        due = now >= todayAt && (!last || last < todayAt);
      } else if (freq === "weekly") {
        const day = bkSettings.schedDay ?? 1;
        const todayAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hh, mm);
        due = now.getDay() === day && now >= todayAt && (!last || last < todayAt);
      } else if (freq === "hourly") {
        due = !last || (now - last) >= 3600000;
      }
      if (due) {
        setScheduleMsg("🔄 Trwa automatyczna kopia zapasowa na Pantry Cloud...");
        await sendToPantry(true).catch(()=>{});
        setScheduleMsg(null);
      }
    };
    schedTimerRef.current = setInterval(check, 60000);
    check();
    return () => clearInterval(schedTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bkSettings.schedEnabled, bkSettings.pantryId, bkSettings.schedFreq, bkSettings.schedTime, bkSettings.schedDay, bkSettings.lastPantryBackup]);

  /* ── ARCHIWIZACJA ARCHIWUM ── */
  const downloadArchive = () => {
    const archivedOrders = orders.filter(o => o.archivedAt);
    const data = {
      version: 8,
      type: "archive_export",
      exportedAt: new Date().toISOString(),
      appName: settings?.restaurantName || "SmakiDomu",
      orders: archivedOrders
    };
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const name = (settings?.restaurantName || "app").replace(/\s+/g, "-");
    const fname = `archiwum-${name}-${new Date().toISOString().slice(0, 10)}.json`;
    a.href = url; a.download = fname;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    setMsg({ type: "ok", text: `✅ Wyeksportowano ${archivedOrders.length} zarchiwizowanych zamówień (${(json.length / 1024).toFixed(1)} KB)` });
    addHistory({ type: "archive_export", label: "Eksport archiwum", filename: fname, ok: true, size: `${(json.length / 1024).toFixed(1)} KB` });
    setTimeout(() => setMsg(null), 5000);
  };

  /* ── KOPIA ARCHIWUM NA BOX.COM ── */
  const boxUploadArchive = async () => {
    setBoxStatus("uploading"); setBoxMsg(null);
    try {
      const at = await boxGetValidToken(boxClientId, boxClientSecret);
      const archivedOrders = orders.filter(o => o.archivedAt);
      const data = {
        version: 8,
        type: "archive_export",
        exportedAt: new Date().toISOString(),
        appName: settings?.restaurantName || "SmakiDomu",
        orders: archivedOrders
      };
      const name = (settings?.restaurantName || "app").replace(/[^a-zA-Z0-9_-]/g, "_");
      const fname = `archiwum-${name}-${new Date().toISOString().slice(0, 10)}.json`;
      await boxUploadFile(at, name, fname, data);
      const size = (JSON.stringify(data).length / 1024).toFixed(1);
      setBoxStatus("ok");
      setBoxMsg({ ok: true, text: `✅ Archiwum (${archivedOrders.length} zamówień) zapisane w Box.com! Plik: ${fname}` });
      addHistory({ type: "box_archive", label: "Archiwum → Box.com", filename: fname, ok: true, size: `${size} KB` });
      const files = await boxListFiles(at, name).catch(() => []);
      setBoxFiles(files);
    } catch (err) {
      setBoxStatus("error");
      setBoxMsg({ ok: false, text: "❌ Błąd Box.com: " + err.message });
      addHistory({ type: "box_archive", label: "Archiwum → Box.com (błąd)", ok: false, error: err.message });
    }
  };

  const totalAddons = (menu||[]).reduce((s,i)=>s+(i.addons?.length||0),0);
  const stats = [
    ["🍽 Produkty",    menu?.length||0],
    ["🧩 Dodatki",     totalAddons],
    ["📂 Kategorie",   cats?.length||0],
    ["🪑 Stoliki",     tables?.length||0],
    ["📋 Zamówienia",  orders?.length||0],
    ["👥 Użytkownicy", users?.length||0],
    ["👤 Kontrahenci", clients?.length||0],
    ["📣 Kampanie",    campaigns?.length||0],
    ["🎟 Vouchery",    vouchers?.length||0],
    ["✅ Użyte kody",  usedCodes?.length||0],
  ];

  const tabBtns = [
    { k:"manual",   label:"⬇ Ręczna kopia" },
    { k:"pantry",   label:"🧵 Pantry Cloud" },
    { k:"onedrive", label:"☁ OneDrive" },
    { k:"box",      label:"📦 Box.com" },
    { k:"schedule", label:"⏰ Harmonogram" },
    { k:"history",  label:"📋 Historia" },
  ];

  const msgBox = (m) => m ? (
    <div style={{ padding:"12px 16px", borderRadius:12, marginBottom:16,
      background: m.ok?"#f0fdf4":"#ffebee",
      color: m.ok?"#15803d":"#c0392b",
      border:"1.5px solid "+(m.ok?"#86efac":"#fca5a5"),
      fontWeight:700, fontSize:13 }}>
      {m.text}
    </div>
  ) : null;

  return (
    <div style={{padding:"0 0 40px"}}>
      <div style={{fontWeight:800,fontSize:18,color:T.text,marginBottom:4}}>💾 Kopie zapasowe</div>
      <div style={{fontSize:12,color:T.muted,marginBottom:16}}>
        Ręczne pobieranie, chmura Pantry Cloud, OneDrive, Box.com (z synchronizacją API), harmonogram automatyczny i kopia Firebase.
      </div>

      {scheduleMsg && (
        <div style={{background:"#eff6ff",border:"1.5px solid #bfdbfe",borderRadius:12,padding:"10px 16px",
          marginBottom:12,fontSize:13,color:"#1d4ed8",fontWeight:600}}>{scheduleMsg}</div>
      )}

      {/* Taby */}
      <div style={{display:"flex",gap:2,marginBottom:20,borderBottom:"2px solid "+T.border,flexWrap:"wrap"}}>
        {tabBtns.map(b=>(
          <button key={b.k} onClick={()=>setTab(b.k)} style={{
            padding:"9px 14px", border:"none", background:"transparent", cursor:"pointer", fontSize:13,
            fontWeight: tab===b.k?700:500, color: tab===b.k?TA.accent:T.muted,
            borderBottom: tab===b.k?`2px solid ${TA.accent}`:"2px solid transparent",
            marginBottom:-2, whiteSpace:"nowrap"
          }}>{b.label}</button>
        ))}
      </div>

      {/* ── TAB: RĘCZNA ── */}
      {tab==="manual" && (
        <div>
          {msg && msgBox(msg)}
          <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
            <button onClick={downloadBackup} style={{
              flex:"1 1 200px", padding:"18px 16px",
              background:"linear-gradient(135deg,#0984e3,#38bdf8)",
              color:"#fff", border:"none", borderRadius:16, fontWeight:900, cursor:"pointer", fontSize:15,
              boxShadow:"0 4px 14px rgba(9,132,227,0.35)",
              display:"flex", alignItems:"center", justifyContent:"center", gap:10
            }}>
              <span style={{fontSize:24}}>⬇</span>
              <div style={{textAlign:"left"}}>
                <div>Pobierz kopię zapasową</div>
                <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Zapisz plik .json na urządzeniu</div>
              </div>
            </button>
            <button onClick={()=>fileRef.current.click()} disabled={importing} style={{
              flex:"1 1 200px", padding:"18px 16px",
              background: importing?"#f0f2f5":"linear-gradient(135deg,#27ae60,#2ecc71)",
              color: importing?T.muted:"#fff",
              border:"none", borderRadius:16, fontWeight:900, cursor:importing?"wait":"pointer", fontSize:15,
              boxShadow: importing?"none":"0 4px 14px rgba(39,174,96,0.35)",
              display:"flex", alignItems:"center", justifyContent:"center", gap:10
            }}>
              <span style={{fontSize:24}}>{importing?"⏳":"⬆"}</span>
              <div style={{textAlign:"left"}}>
                <div>{importing?"Importowanie...":"Przywróć z kopii"}</div>
                <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Wczytaj plik .json z kopią</div>
              </div>
            </button>
            <input ref={fileRef} type="file" accept=".json" style={{display:"none"}} onChange={handleImport}/>
          </div>

          <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap"}}>
            <button onClick={downloadArchive} style={{
              flex:"1 1 200px", padding:"14px 16px",
              background:"linear-gradient(135deg,#7c3aed,#a855f7)",
              color:"#fff", border:"none", borderRadius:16, fontWeight:800, cursor:"pointer", fontSize:14,
              boxShadow:"0 4px 12px rgba(124,58,237,0.25)",
              display:"flex", alignItems:"center", justifyContent:"center", gap:10
            }}>
              <span style={{fontSize:20}}>📦</span>
              <div style={{textAlign:"left"}}>
                <div>Archiwizuj archiwum</div>
                <div style={{fontSize:10,fontWeight:600,opacity:0.85}}>Eksportuj wszystkie zarchiwizowane zamówienia</div>
              </div>
            </button>
            {boxToken && (
              <button onClick={boxUploadArchive} disabled={boxStatus==="uploading"} style={{
                flex:"1 1 200px", padding:"14px 16px",
                background:"linear-gradient(135deg,#0061ff,#007aff)",
                color:"#fff", border:"none", borderRadius:16, fontWeight:800, cursor:boxStatus==="uploading"?"wait":"pointer", fontSize:14,
                boxShadow:"0 4px 12px rgba(0,97,255,0.25)",
                display:"flex", alignItems:"center", justifyContent:"center", gap:10
              }}>
                <span style={{fontSize:20}}>📦</span>
                <div style={{textAlign:"left"}}>
                  <div>Kopia archiwum do Box.com</div>
                  <div style={{fontSize:10,fontWeight:600,opacity:0.85}}>Wyślij archiwum zamówień do chmury</div>
                </div>
              </button>
            )}
          </div>

          <div style={{background:T.surface,borderRadius:16,border:"1.5px solid "+T.border,padding:16,marginBottom:16}}>
            <div style={{fontWeight:800,fontSize:14,color:T.text,marginBottom:12}}>📦 Aktualna zawartość bazy danych</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:8}}>
              {stats.map(([label,count])=>(
                <div key={label} style={{background:T.bg,borderRadius:10,padding:"10px 14px",border:"1px solid "+T.border,textAlign:"center"}}>
                  <div style={{fontSize:20,marginBottom:4}}>{label.split(" ")[0]}</div>
                  <div style={{fontWeight:900,fontSize:20,color:T.primary,lineHeight:1}}>{count}</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>{label.split(" ").slice(1).join(" ")}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{background:"#fffbeb",borderRadius:12,border:"1px solid #fde68a",padding:"12px 16px"}}>
            <div style={{fontWeight:700,fontSize:12,color:"#92400e",marginBottom:6}}>💡 Wskazówki</div>
            <div style={{fontSize:12,color:"#78350f",lineHeight:1.7}}>
              <div>• Przed każdą większą zmianą w menu lub ustawieniach</div>
              <div>• Kopia zawiera <strong>wszystkie dane</strong> — menu, zamówienia, ustawienia, użytkowników, stoliki, kampanie</div>
              <div>• Przy przywracaniu <strong>wszystkie obecne dane zostaną zastąpione</strong> danymi z kopii</div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: PANTRY CLOUD ── */}
      {tab==="pantry" && (
        <div>
          {pantryMsg && msgBox(pantryMsg)}

          {/* Czym jest Pantry */}
          <div style={{background:"linear-gradient(135deg,#fff7ed,#fef3c7)",borderRadius:16,border:"1.5px solid #fed7aa",padding:16,marginBottom:16}}>
            <div style={{fontWeight:800,fontSize:14,color:"#92400e",marginBottom:6}}>🧺 Czym jest Pantry Cloud?</div>
            <div style={{fontSize:12,color:"#78350f",lineHeight:1.75}}>
              <strong>Pantry Cloud</strong> to <strong>bezpłatny serwis</strong> do przechowywania danych JSON w chmurze.
              Nie wymaga logowania przez OAuth, konfiguracji Google Cloud ani żadnych kluczy API w konsoli deweloperskiej.
              Wystarczy <strong>jeden email → dostajesz Pantry ID</strong> i możesz zapisywać/odczytywać dane przez REST API.
              Limit: <strong>1 MB na konto</strong> (kopia typowo zajmuje 50–300 KB). Dane aktywne 30 dni od ostatniego użycia.
            </div>
            <a href="https://getpantry.cloud" target="_blank" rel="noopener noreferrer"
              style={{display:"inline-block",marginTop:8,fontSize:12,fontWeight:700,color:"#c2410c",textDecoration:"underline"}}>
              Utwórz darmowe konto na getpantry.cloud →
            </a>
          </div>

          {/* Konfiguracja Pantry ID */}
          <div style={{background:T.surface,borderRadius:16,border:"1.5px solid "+T.border,padding:16,marginBottom:16}}>
            <div style={{fontWeight:800,fontSize:14,color:T.text,marginBottom:4}}>🔑 Twój Pantry ID</div>
            <div style={{fontSize:12,color:T.muted,marginBottom:10}}>
              Skopiuj Pantry ID ze strony getpantry.cloud po rejestracji i wklej poniżej. Możesz też wpisać nazwę konkretnego koszyka (basket), z którego chcesz przywrócić kopię.
            </div>
            <label style={{fontSize:12,fontWeight:700,color:T.text,display:"block",marginBottom:4}}>Pantry ID</label>
            <input
              style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid "+T.border,fontSize:13,fontFamily:"monospace",boxSizing:"border-box",marginBottom:10}}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={bkSettings.pantryId||""}
              onChange={e=>{ const v=e.target.value.trim(); setBkSettings(p=>({...p, pantryId: v})); setPantryInfo(null); if(setSettings) setSettings(p=>({...p, _backupPantryId: v})); }}
            />
            <label style={{fontSize:12,fontWeight:700,color:T.text,display:"block",marginBottom:4}}>Nazwa koszyka do przywracania (opcjonalnie)</label>
            <input
              style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid "+T.border,fontSize:13,fontFamily:"monospace",boxSizing:"border-box",marginBottom:10}}
              placeholder={buildBasketName("backup")}
              value={bkSettings.lastPantryBasket||""}
              onChange={e=>{ const v=e.target.value.trim(); setBkSettings(p=>({...p, lastPantryBasket: v})); if(setSettings) setSettings(p=>({...p, _backupBasket: v})); }}
            />

            {/* Info o koncie Pantry */}
            <button onClick={loadPantryInfo} disabled={!bkSettings.pantryId||pantryInfoLoading}
              style={{padding:"7px 16px",borderRadius:9,border:"1.5px solid "+T.border,background:"#f8fafc",cursor:pantryInfoLoading?"wait":"pointer",fontSize:12,fontWeight:600,color:T.muted}}>
              {pantryInfoLoading?"⏳ Ładowanie...":"ℹ Sprawdź stan konta Pantry"}
            </button>
            {pantryInfo && !pantryInfo.error && (
              <div style={{marginTop:10,background:"#f0fdf4",borderRadius:10,padding:"10px 14px",fontSize:12}}>
                <div style={{fontWeight:700,color:"#15803d",marginBottom:4}}>📊 Stan konta Pantry</div>
                <div style={{color:"#166534"}}>Nazwa: <strong>{pantryInfo.name || "—"}</strong></div>
                {pantryInfo.percentFull !== undefined && (
                  <div style={{marginTop:6}}>
                    <div style={{color:"#166534",marginBottom:4}}>Zajętość: <strong>{pantryInfo.percentFull}%</strong></div>
                    <div style={{height:8,borderRadius:4,background:"#dcfce7",overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${pantryInfo.percentFull}%`,background:pantryInfo.percentFull>80?"#ef4444":"#22c55e",borderRadius:4,transition:"width .3s"}}/>
                    </div>
                  </div>
                )}
                {Array.isArray(pantryInfo.baskets) && (
                  <div style={{marginTop:6,color:"#166534"}}>
                    Koszyki: {pantryInfo.baskets.map(b=>
                      <span key={b.name} style={{display:"inline-block",background:"#dcfce7",borderRadius:6,padding:"2px 8px",margin:"2px",fontSize:11}}>{b.name}</span>
                    )}
                  </div>
                )}
              </div>
            )}
            {pantryInfo?.error && (
              <div style={{marginTop:10,background:"#ffebee",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#c0392b",fontWeight:600}}>
                ❌ {pantryInfo.error}
              </div>
            )}
          </div>

          {/* Przyciski zapisu i przywracania */}
          <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
            <button onClick={()=>sendToPantry(false)} disabled={!bkSettings.pantryId||pantryStatus==="sending"}
              style={{
                flex:"1 1 200px", padding:"16px",
                background:!bkSettings.pantryId||pantryStatus==="sending"?"#f0f2f5":"linear-gradient(135deg,#f97316,#fb923c)",
                color:!bkSettings.pantryId||pantryStatus==="sending"?T.muted:"#fff",
                border:"none", borderRadius:14, fontWeight:900, cursor:!bkSettings.pantryId||pantryStatus==="sending"?"not-allowed":"pointer",
                fontSize:15, boxShadow:bkSettings.pantryId?"0 4px 14px rgba(249,115,22,0.35)":"none",
                display:"flex", alignItems:"center", justifyContent:"center", gap:10
              }}>
              <span style={{fontSize:22}}>{pantryStatus==="sending"?"⏳":"🧺"}</span>
              <div style={{textAlign:"left"}}>
                <div>{pantryStatus==="sending"?"Wysyłanie...":"Zapisz kopię w Pantry Cloud"}</div>
                <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Bezpłatna chmura JSON</div>
              </div>
            </button>

            <button onClick={restoreFromPantry} disabled={!bkSettings.pantryId||restoreLoading}
              style={{
                flex:"1 1 200px", padding:"16px",
                background:!bkSettings.pantryId||restoreLoading?"#f0f2f5":"linear-gradient(135deg,#8b5cf6,#a78bfa)",
                color:!bkSettings.pantryId||restoreLoading?T.muted:"#fff",
                border:"none", borderRadius:14, fontWeight:900, cursor:!bkSettings.pantryId||restoreLoading?"not-allowed":"pointer",
                fontSize:15, boxShadow:bkSettings.pantryId?"0 4px 14px rgba(139,92,246,0.35)":"none",
                display:"flex", alignItems:"center", justifyContent:"center", gap:10
              }}>
              <span style={{fontSize:22}}>{restoreLoading?"⏳":"🔄"}</span>
              <div style={{textAlign:"left"}}>
                <div>{restoreLoading?"Przywracanie...":"Przywróć z Pantry Cloud"}</div>
                <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Wczyta dane z koszyka</div>
              </div>
            </button>
          </div>

          {/* Kopia Firebase → Pantry */}
          <div style={{background:T.surface,borderRadius:16,border:"1.5px solid "+T.border,padding:16}}>
            <div style={{fontWeight:800,fontSize:14,color:T.text,marginBottom:4}}>🔥 Kopia Firebase Realtime Database → Pantry Cloud</div>
            <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.6}}>
              Pobierze <strong>cały snapshot bazy Firebase</strong> i wyśle jako JSON do Pantry Cloud.
              Jeśli dane Firebase przekroczą 1 MB, zostanie zapisana informacja o błędzie — pobierz wtedy kopię ręcznie.
              {bkSettings.lastFirebaseBackup && (
                <span style={{display:"block",marginTop:4,color:"#15803d",fontWeight:600}}>
                  Ostatnia kopia Firebase: {new Date(bkSettings.lastFirebaseBackup).toLocaleString("pl-PL")}
                </span>
              )}
            </div>
            {!fbConfig?.databaseURL ? (
              <div style={{background:"#fff8e1",border:"1px solid #fde68a",borderRadius:10,padding:"10px 14px",fontSize:12,color:"#92400e"}}>
                ⚠ Firebase nie jest skonfigurowane. Przejdź do Ustawienia → Firebase i uzupełnij Database URL.
              </div>
            ) : (
              <button onClick={sendFirebaseToPantry} disabled={!bkSettings.pantryId||fbPantryStatus==="sending"}
                style={{
                  width:"100%", padding:"14px",
                  background:!bkSettings.pantryId||fbPantryStatus==="sending"?"#f0f2f5":"linear-gradient(135deg,#ff6d00,#ff9100)",
                  color:!bkSettings.pantryId||fbPantryStatus==="sending"?T.muted:"#fff",
                  border:"none", borderRadius:14, fontWeight:900, cursor:!bkSettings.pantryId?"not-allowed":"pointer",
                  fontSize:14, boxShadow:bkSettings.pantryId?"0 4px 14px rgba(255,109,0,0.3)":"none",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:10
                }}>
                <span style={{fontSize:20}}>{fbPantryStatus==="sending"?"⏳":"🔥"}</span>
                {fbPantryStatus==="sending" ? "Pobieranie z Firebase..." : "Wyślij kopię Firebase do Pantry Cloud"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: ONEDRIVE ── */}
      {tab==="onedrive" && (
        <div>
          {odMsg && (
            <div style={{ padding:"12px 16px", borderRadius:12, marginBottom:16,
              background: odMsg.ok?"#f0fdf4":"#ffebee",
              color: odMsg.ok?"#15803d":"#c0392b",
              border:"1.5px solid "+(odMsg.ok?"#86efac":"#fca5a5"),
              fontWeight:700, fontSize:13 }}>
              {odMsg.text}
            </div>
          )}

          {/* Info o OneDrive */}
          <div style={{background:"linear-gradient(135deg,#eff6ff,#e0f2fe)",borderRadius:16,border:"1.5px solid #bfdbfe",padding:16,marginBottom:16}}>
            <div style={{fontWeight:800,fontSize:14,color:"#1e40af",marginBottom:6}}>☁ Kopia na OneDrive</div>
            <div style={{fontSize:12,color:"#1e3a8a",lineHeight:1.75}}>
              Zapisuje kopię JSON bezpośrednio na Twoim <strong>Microsoft OneDrive</strong> w folderze{" "}
              <code style={{background:"rgba(0,0,0,0.08)",borderRadius:4,padding:"1px 5px"}}>Aplikacje/{(settings?.restaurantName||"app").replace(/[^a-zA-Z0-9_-]/g,"_")}/</code>.{" "}
              Wymaga jednorazowej rejestracji aplikacji w Azure (darmowe) i zalogowania przez Microsoft.
            </div>
            <button onClick={()=>setOdShowHelp(h=>!h)} style={{
              marginTop:8,fontSize:12,color:"#2563eb",background:"none",border:"none",cursor:"pointer",
              textDecoration:"underline",padding:0,fontWeight:600
            }}>
              {odShowHelp?"▲ Ukryj instrukcję":"📖 Jak skonfigurować? (kliknij)"}
            </button>
            {odShowHelp && (
              <div style={{marginTop:10,fontSize:12,color:"#1e3a8a",lineHeight:1.8,background:"rgba(255,255,255,0.6)",borderRadius:10,padding:12}}>
                <div style={{fontWeight:800,marginBottom:4}}>🔧 Jednorazowa konfiguracja Azure (5 min):</div>
                <div>1. Wejdź na <strong>portal.azure.com</strong> → zaloguj się kontem Microsoft</div>
                <div>2. Wyszukaj <strong>„App registrations"</strong> → kliknij <strong>„New registration"</strong></div>
                <div>3. Nazwa: dowolna (np. „MojaPiekarnia Backup")</div>
                <div>4. Supported account types: <strong>„Accounts in any organizational directory and personal Microsoft accounts"</strong></div>
                <div>5. Redirect URI: wybierz <strong>„Single-page application (SPA)"</strong> i wpisz URL tej strony:<br/>
                  <code style={{background:"rgba(0,0,0,0.08)",borderRadius:4,padding:"1px 5px",wordBreak:"break-all"}}>{window.location.origin+window.location.pathname}</code></div>
                <div>6. Kliknij <strong>Register</strong> → skopiuj <strong>Application (client) ID</strong></div>
                <div>7. Wklej Client ID poniżej i kliknij <strong>„Zaloguj przez Microsoft"</strong></div>
                <div style={{marginTop:6,color:"#166534",fontWeight:600}}>✅ Nie potrzeba client secret — flow PKCE jest bezpieczny w przeglądarce</div>
              </div>
            )}
          </div>

          {/* Client ID */}
          <div style={{background:"#fff",borderRadius:14,border:"1.5px solid "+T.border,padding:16,marginBottom:16}}>
            <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:8}}>🔑 Azure Application (Client) ID</div>
            <input
              type="text"
              style={{...S.inp, fontFamily:"monospace", fontSize:12}}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={odClientId}
              onChange={e=>odSaveClientId(e.target.value.trim())}
            />
            <div style={{fontSize:11,color:T.muted,marginTop:6}}>
              Skopiuj z Azure Portal → App registrations → Twoja aplikacja → Overview → Application (client) ID
            </div>
          </div>

          {/* Status zalogowania */}
          {odToken && odTokenValid(odToken) ? (
            <div>
              <div style={{background:"#f0fdf4",borderRadius:14,border:"1.5px solid #86efac",padding:"14px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:"#15803d"}}>✅ Zalogowano do OneDrive</div>
                  {odMe && <div style={{fontSize:12,color:"#166534",marginTop:2}}>
                    {odMe.displayName}{odMe.mail?" · "+odMe.mail:""}
                  </div>}
                </div>
                <button onClick={odLogout} style={{fontSize:12,padding:"6px 12px",borderRadius:8,border:"1px solid #86efac",background:"#fff",color:"#15803d",cursor:"pointer",fontWeight:600}}>
                  Wyloguj
                </button>
              </div>

              {/* Przyciski akcji */}
              <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                <button onClick={odUploadBackup} disabled={odStatus==="uploading"} style={{
                  flex:"1 1 200px", padding:"18px 16px",
                  background:odStatus==="uploading"?"#f0f2f5":"linear-gradient(135deg,#0078d4,#40a9ff)",
                  color:odStatus==="uploading"?T.muted:"#fff",
                  border:"none", borderRadius:16, fontWeight:900, cursor:odStatus==="uploading"?"wait":"pointer", fontSize:15,
                  boxShadow:odStatus==="uploading"?"none":"0 4px 14px rgba(0,120,212,0.35)",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:10
                }}>
                  <span style={{fontSize:24}}>{odStatus==="uploading"?"⏳":"☁"}</span>
                  <div style={{textAlign:"left"}}>
                    <div>{odStatus==="uploading"?"Wysyłanie...":"Zapisz kopię na OneDrive"}</div>
                    <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Plik .json w folderze Aplikacje/</div>
                  </div>
                </button>
                <button onClick={odLoadFiles} disabled={odStatus==="listing"} style={{
                  flex:"1 1 200px", padding:"18px 16px",
                  background:odStatus==="listing"?"#f0f2f5":"linear-gradient(135deg,#7c3aed,#a78bfa)",
                  color:odStatus==="listing"?T.muted:"#fff",
                  border:"none", borderRadius:16, fontWeight:900, cursor:odStatus==="listing"?"wait":"pointer", fontSize:15,
                  boxShadow:odStatus==="listing"?"none":"0 4px 14px rgba(124,58,237,0.35)",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:10
                }}>
                  <span style={{fontSize:24}}>{odStatus==="listing"?"⏳":"📂"}</span>
                  <div style={{textAlign:"left"}}>
                    <div>{odStatus==="listing"?"Ładowanie...":"Przeglądaj kopie"}</div>
                    <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Lista plików na OneDrive</div>
                  </div>
                </button>
              </div>

              {/* Lista plików */}
              {odFiles !== null && (
                <div style={{background:T.surface,borderRadius:14,border:"1.5px solid "+T.border,padding:14,marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:10}}>
                    📁 Pliki kopii na OneDrive ({odFiles.length})
                  </div>
                  {odFiles.length===0 ? (
                    <div style={{textAlign:"center",padding:"24px 14px",color:T.muted,fontSize:13}}>
                      Brak plików kopii w folderze
                    </div>
                  ) : odFiles.map(f=>(
                    <div key={f.id} style={{
                      display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,
                      padding:"10px 12px",borderRadius:10,marginBottom:6,
                      background:"#f9fafb",border:"1px solid "+T.border,flexWrap:"wrap"
                    }}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:12,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          📄 {f.name}
                        </div>
                        <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                          {f.lastModifiedDateTime ? new Date(f.lastModifiedDateTime).toLocaleString("pl-PL") : ""}
                          {f.size ? ` · ${(f.size/1024).toFixed(1)} KB` : ""}
                        </div>
                      </div>
                      <button
                        onClick={()=>odRestore(f.id, f.name)}
                        disabled={odStatus==="restoring"}
                        style={{padding:"6px 12px",borderRadius:8,border:"1.5px solid #7c3aed",background:"#fff",
                          color:"#7c3aed",cursor:odStatus==="restoring"?"wait":"pointer",fontSize:12,fontWeight:700,flexShrink:0}}
                      >
                        {odStatus==="restoring"?"⏳":"↩ Przywróć"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button onClick={odLogin} disabled={!odClientId.trim()} style={{
              width:"100%", padding:"18px 16px",
              background:!odClientId.trim()?"#f0f2f5":"linear-gradient(135deg,#0078d4,#40a9ff)",
              color:!odClientId.trim()?T.muted:"#fff",
              border:"none", borderRadius:16, fontWeight:900,
              cursor:!odClientId.trim()?"not-allowed":"pointer",
              fontSize:15, boxShadow:odClientId.trim()?"0 4px 14px rgba(0,120,212,0.35)":"none",
              display:"flex", alignItems:"center", justifyContent:"center", gap:12, marginBottom:16
            }}>
              <span style={{fontSize:26}}>🔐</span>
              <div style={{textAlign:"left"}}>
                <div>Zaloguj przez Microsoft</div>
                <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Autoryzacja OAuth 2.0 + PKCE — bezpieczna, bez hasła</div>
              </div>
            </button>
          )}

          {/* Info box */}
          <div style={{background:"#fffbeb",borderRadius:12,border:"1px solid #fde68a",padding:"12px 16px"}}>
            <div style={{fontWeight:700,fontSize:12,color:"#92400e",marginBottom:6}}>💡 Informacje</div>
            <div style={{fontSize:12,color:"#78350f",lineHeight:1.7}}>
              <div>• Pliki zapisywane w: <strong>OneDrive / Aplikacje / {(settings?.restaurantName||"app").replace(/[^a-zA-Z0-9_-]/g,"_")}/</strong></div>
              <div>• Token jest przechowywany w localStorage przeglądarki i automatycznie odświeżany</div>
              <div>• Nie wymaga płatnej subskrypcji — działa z bezpłatnym kontem Microsoft (5 GB)</div>
              <div>• Przywrócenie kopii <strong>zastąpi wszystkie obecne dane</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: BOX.COM ── */}
      {tab==="box" && (
        <div>
          {boxMsg && (
            <div style={{ padding:"12px 16px", borderRadius:12, marginBottom:16,
              background: boxMsg.ok?"#f0fdf4":"#ffebee",
              color: boxMsg.ok?"#15803d":"#c0392b",
              border:"1.5px solid "+(boxMsg.ok?"#86efac":"#fca5a5"),
              fontWeight:700, fontSize:13 }}>
              {boxMsg.text}
            </div>
          )}

          {/* Info o Box.com */}
          <div style={{background:"linear-gradient(135deg,#fff7ed,#ffedd5)",borderRadius:16,border:"1.5px solid #fed7aa",padding:16,marginBottom:16}}>
            <div style={{fontWeight:800,fontSize:14,color:"#9a3412",marginBottom:6}}>📦 Kopia na Box.com</div>
            <div style={{fontSize:12,color:"#7c2d12",lineHeight:1.75}}>
              Zapisuje kopię JSON bezpośrednio na Twoim koncie <strong>Box.com</strong> w folderze{" "}
              <code style={{background:"rgba(0,0,0,0.08)",borderRadius:4,padding:"1px 5px"}}>Aplikacje/{(settings?.restaurantName||"app").replace(/[^a-zA-Z0-9_-]/g,"_")}/</code>.{" "}
              Obsługuje automatyczne wersjonowanie plików i harmonogram synchronizacji.
            </div>
            <button onClick={()=>setBoxShowHelp(h=>!h)} style={{
              marginTop:8,fontSize:12,color:"#c2410c",background:"none",border:"none",cursor:"pointer",
              textDecoration:"underline",padding:0,fontWeight:600
            }}>
              {boxShowHelp?"\u25b2 Ukryj instrukcję":"\ud83d\udcd6 Jak skonfigurować? (kliknij)"}
            </button>
            {boxShowHelp && (
              <div style={{marginTop:10,fontSize:12,color:"#7c2d12",lineHeight:1.8,background:"rgba(255,255,255,0.6)",borderRadius:10,padding:12}}>
                <div style={{fontWeight:800,marginBottom:4}}>🔧 Jednorazowa konfiguracja Box Developer (5 min):</div>
                <div>1. Wejdź na <strong>developer.box.com</strong> → zaloguj się kontem Box</div>
                <div>2. Kliknij <strong>"My Apps"</strong> → <strong>"Create New App"</strong></div>
                <div>3. Wybierz <strong>"Custom App"</strong> → <strong>"User Authentication (OAuth 2.0)"</strong></div>
                <div>4. Nazwa: dowolna (np. "Backup Restauracji")</div>
                <div>5. W konfiguracji aplikacji dodaj <strong>Redirect URI</strong>:<br/>
                  <code style={{background:"rgba(0,0,0,0.08)",borderRadius:4,padding:"1px 5px",wordBreak:"break-all"}}>{window.location.origin+window.location.pathname}</code></div>
                <div>6. Skopiuj <strong>Client ID</strong> i <strong>Client Secret</strong> z zakładki Configuration</div>
                <div>7. Wklej oba poniżej i kliknij <strong>"Zaloguj przez Box.com"</strong></div>
                <div style={{marginTop:6,color:"#166534",fontWeight:600}}>✅ Box oferuje 10 GB bezpłatnie. Token jest automatycznie odświeżany.</div>
              </div>
            )}
          </div>

          {/* Client ID i Secret */}
          <div style={{background:"#fff",borderRadius:14,border:"1.5px solid "+T.border,padding:16,marginBottom:16}}>
            <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:8}}>🔑 Dane aplikacji Box</div>
            <div style={{marginBottom:10}}>
              <label style={{...S.label}}>Client ID</label>
              <input
                type="text"
                style={{...S.inp, fontFamily:"monospace", fontSize:12}}
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={boxClientId}
                onChange={e=>boxSaveClientId(e.target.value.trim())}
              />
              <div style={{fontSize:11,color:T.muted,marginTop:4}}>Skopiuj z developer.box.com → My Apps → Twoja aplikacja → Configuration → Client ID</div>
            </div>
            <div>
              <label style={{...S.label}}>Client Secret</label>
              <input
                type="password"
                style={{...S.inp, fontFamily:"monospace", fontSize:12}}
                placeholder="••••••••••••••••••••••••••••••••"
                value={boxClientSecret}
                onChange={e=>boxSaveClientSecret(e.target.value.trim())}
              />
              <div style={{fontSize:11,color:T.muted,marginTop:4}}>Skopiuj z developer.box.com → My Apps → Twoja aplikacja → Configuration → Client Secret</div>
            </div>
          </div>

          {/* Status zalogowania */}
          {boxToken && boxTokenValid(boxToken) ? (
            <div>
              <div style={{background:"#f0fdf4",borderRadius:14,border:"1.5px solid #86efac",padding:"14px 16px",marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:"#15803d"}}>✅ Zalogowano do Box.com</div>
                  {boxMe && <div style={{fontSize:12,color:"#166534",marginTop:2}}>
                    {boxMe.name}{boxMe.login?" · "+boxMe.login:""}
                    {boxMe.space_used && boxMe.space_amount ? ` · ${(boxMe.space_used/1024/1024/1024).toFixed(2)} GB / ${(boxMe.space_amount/1024/1024/1024).toFixed(0)} GB` : ""}
                  </div>}
                </div>
                <button onClick={boxLogout} style={{fontSize:12,padding:"6px 12px",borderRadius:8,border:"1px solid #86efac",background:"#fff",color:"#15803d",cursor:"pointer",fontWeight:600}}>
                  Wyloguj
                </button>
              </div>

              {/* Przyciski akcji */}
              <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                <button onClick={()=>boxUploadBackup(false)} disabled={boxStatus==="uploading"} style={{
                  flex:"1 1 200px", padding:"18px 16px",
                  background:boxStatus==="uploading"?"#f0f2f5":"linear-gradient(135deg,#ea580c,#f97316)",
                  color:boxStatus==="uploading"?T.muted:"#fff",
                  border:"none", borderRadius:16, fontWeight:900, cursor:boxStatus==="uploading"?"wait":"pointer", fontSize:15,
                  boxShadow:boxStatus==="uploading"?"none":"0 4px 14px rgba(234,88,12,0.35)",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:10
                }}>
                  <span style={{fontSize:24}}>{boxStatus==="uploading"?"⏳":"📦"}</span>
                  <div style={{textAlign:"left"}}>
                    <div>{boxStatus==="uploading"?"Wysyłanie...":"Zapisz kopię na Box.com"}</div>
                    <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Plik .json w folderze Aplikacje/</div>
                  </div>
                </button>
                <button onClick={boxLoadFiles} disabled={boxStatus==="listing"} style={{
                  flex:"1 1 200px", padding:"18px 16px",
                  background:boxStatus==="listing"?"#f0f2f5":"linear-gradient(135deg,#7c3aed,#a78bfa)",
                  color:boxStatus==="listing"?T.muted:"#fff",
                  border:"none", borderRadius:16, fontWeight:900, cursor:boxStatus==="listing"?"wait":"pointer", fontSize:15,
                  boxShadow:boxStatus==="listing"?"none":"0 4px 14px rgba(124,58,237,0.35)",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:10
                }}>
                  <span style={{fontSize:24}}>{boxStatus==="listing"?"⏳":"📂"}</span>
                  <div style={{textAlign:"left"}}>
                    <div>{boxStatus==="listing"?"Wczytywanie...":"Przeglądaj kopie"}</div>
                    <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Lista plików na Box.com</div>
                  </div>
                </button>
              </div>

              {/* Lista plików */}
              {boxFiles !== null && (
                <div style={{background:T.surface,borderRadius:14,border:"1.5px solid "+T.border,padding:14,marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:10}}>
                    📁 Pliki kopii na Box.com ({boxFiles.length})
                  </div>
                  {boxFiles.length===0 ? (
                    <div style={{textAlign:"center",padding:"24px 14px",color:T.muted,fontSize:13}}>
                      Brak plików kopii w folderze
                    </div>
                  ) : boxFiles.map(f=>(
                    <div key={f.id} style={{
                      display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,
                      padding:"10px 12px",borderRadius:10,marginBottom:6,
                      background:"#f9fafb",border:"1px solid "+T.border,flexWrap:"wrap"
                    }}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:12,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          📄 {f.name}
                        </div>
                        <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                          {f.modified_at ? new Date(f.modified_at).toLocaleString("pl-PL") : ""}
                          {f.size ? ` · ${(f.size/1024).toFixed(1)} KB` : ""}
                        </div>
                      </div>
                      <button
                        onClick={()=>boxRestore(f.id, f.name)}
                        disabled={boxStatus==="restoring"}
                        style={{padding:"6px 12px",borderRadius:8,border:"1.5px solid #ea580c",background:"#fff",
                          color:"#ea580c",cursor:boxStatus==="restoring"?"wait":"pointer",fontSize:12,fontWeight:700,flexShrink:0}}
                      >
                        {boxStatus==="restoring"?"⏳":"↩ Przywróć"}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Harmonogram Box.com */}
              <div style={{background:T.surface,borderRadius:14,border:"1.5px solid "+T.border,padding:16,marginBottom:16}}>
                <div style={{fontWeight:700,fontSize:14,color:T.text,marginBottom:10}}>⏰ Harmonogram automatyczny Box.com</div>
                <div style={{display:"flex",alignItems:"center",gap:14,padding:"12px 14px",borderRadius:12,
                  background:bkSettings.boxSchedEnabled?"#fff7ed":"#f8f8f8",
                  border:"1.5px solid "+(bkSettings.boxSchedEnabled?"#fed7aa":T.border),marginBottom:12,transition:"all .2s"}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13,color:T.text}}>Automatyczna kopia na Box.com</div>
                    <div style={{fontSize:12,color:T.muted,marginTop:2}}>
                      {bkSettings.boxSchedEnabled ? "🟢 Aktywne — kopia wysłana zgodnie z harmonogramem" : "🔴 Nieaktywne"}
                    </div>
                  </div>
                  <div onClick={()=>setBkSettings(p=>({...p,boxSchedEnabled:!p.boxSchedEnabled}))}
                    style={{width:48,height:26,borderRadius:13,background:bkSettings.boxSchedEnabled?"#f97316":"#d1d5db",
                      cursor:"pointer",position:"relative",transition:"all .2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:3,left:bkSettings.boxSchedEnabled?23:3,width:20,height:20,
                      borderRadius:"50%",background:"#fff",transition:"all .2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}} />
                  </div>
                </div>
                <div style={{marginBottom:10}}>
                  <label style={{fontSize:12,fontWeight:700,color:T.text,display:"block",marginBottom:6}}>Częstotliwość</label>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {[["hourly","Co godzinę"],["daily","Codziennie"],["weekly","Co tydzień"]].map(([v,l])=>(
                      <button key={v} onClick={()=>setBkSettings(p=>({...p,boxSchedFreq:v}))}
                        style={{flex:"1 1 110px",padding:"9px 12px",borderRadius:10,cursor:"pointer",fontWeight:600,fontSize:13,
                          border:"2px solid "+(bkSettings.boxSchedFreq===v||(!bkSettings.boxSchedFreq&&v==="daily")?"#f97316":T.border),
                          background:bkSettings.boxSchedFreq===v||(!bkSettings.boxSchedFreq&&v==="daily")?"#fff7ed":"#fff",
                          color:bkSettings.boxSchedFreq===v||(!bkSettings.boxSchedFreq&&v==="daily")?"#ea580c":T.muted}}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                {(bkSettings.boxSchedFreq||"daily") !== "hourly" && (
                  <div style={{marginBottom:10}}>
                    <label style={{fontSize:12,fontWeight:700,color:T.text,display:"block",marginBottom:6}}>Godzina wykonania kopii</label>
                    <input type="time" value={bkSettings.boxSchedTime||"04:00"}
                      onChange={e=>setBkSettings(p=>({...p,boxSchedTime:e.target.value}))}
                      style={{padding:"9px 12px",borderRadius:9,border:"1.5px solid "+T.border,fontSize:14,fontFamily:"monospace"}}/>
                  </div>
                )}
                {bkSettings.boxSchedFreq === "weekly" && (
                  <div style={{marginBottom:10}}>
                    <label style={{fontSize:12,fontWeight:700,color:T.text,display:"block",marginBottom:6}}>Dzień tygodnia</label>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {["Nie","Pon","Wt","Śr","Czw","Pt","Sob"].map((d,i)=>(
                        <button key={i} onClick={()=>setBkSettings(p=>({...p,boxSchedDay:i}))}
                          style={{padding:"8px 12px",borderRadius:9,cursor:"pointer",fontWeight:600,fontSize:13,
                            border:"2px solid "+((bkSettings.boxSchedDay??1)===i?"#f97316":T.border),
                            background:(bkSettings.boxSchedDay??1)===i?"#fff7ed":"#fff",
                            color:(bkSettings.boxSchedDay??1)===i?"#ea580c":T.muted}}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{background:"#f8fafc",borderRadius:10,padding:"10px 14px",fontSize:12,color:T.muted,lineHeight:1.8,marginBottom:10}}>
                  {bkSettings.lastBoxBackup && (
                    <div><strong>Ostatnia kopia Box.com:</strong> {new Date(bkSettings.lastBoxBackup).toLocaleString("pl-PL")}</div>
                  )}
                  {bkSettings.boxSchedEnabled && (
                    <div style={{color:"#ea580c",fontWeight:600,marginTop:4}}>
                      ℹ Harmonogram sprawdzany co minutę — aplikacja musi być otwarta w przeglądarce.
                    </div>
                  )}
                </div>
                <button onClick={()=>boxUploadBackup(true)} disabled={boxStatus==="uploading"}
                  style={{
                    width:"100%",padding:"13px",borderRadius:12,fontWeight:800,fontSize:14,cursor:"pointer",
                    background:boxStatus==="uploading"?"#f0f2f5":"linear-gradient(135deg,#ea580c,#f97316)",
                    color:boxStatus==="uploading"?T.muted:"#fff",border:"none",
                    boxShadow:boxStatus==="uploading"?"none":"0 4px 14px rgba(234,88,12,0.3)"
                  }}>
                  🔄 Wykonaj kopię teraz (Box.com)
                </button>
              </div>
            </div>
          ) : (
            <button onClick={boxLogin} disabled={!boxClientId.trim()||!boxClientSecret.trim()} style={{
              width:"100%", padding:"18px 16px",
              background:(!boxClientId.trim()||!boxClientSecret.trim())?"#f0f2f5":"linear-gradient(135deg,#ea580c,#f97316)",
              color:(!boxClientId.trim()||!boxClientSecret.trim())?T.muted:"#fff",
              border:"none", borderRadius:16, fontWeight:900,
              cursor:(!boxClientId.trim()||!boxClientSecret.trim())?"not-allowed":"pointer",
              fontSize:15, boxShadow:(!boxClientId.trim()||!boxClientSecret.trim())?"none":"0 4px 14px rgba(234,88,12,0.35)",
              display:"flex", alignItems:"center", justifyContent:"center", gap:12, marginBottom:16
            }}>
              <span style={{fontSize:26}}>🔐</span>
              <div style={{textAlign:"left"}}>
                <div>Zaloguj przez Box.com</div>
                <div style={{fontSize:11,fontWeight:600,opacity:0.85}}>Autoryzacja OAuth 2.0 — bezpieczna, token zapisany lokalnie</div>
              </div>
            </button>
          )}

          {/* Info box */}
          <div style={{background:"#fffbeb",borderRadius:12,border:"1px solid #fde68a",padding:"12px 16px"}}>
            <div style={{fontWeight:700,fontSize:12,color:"#92400e",marginBottom:6}}>💡 Informacje</div>
            <div style={{fontSize:12,color:"#78350f",lineHeight:1.7}}>
              <div>• Pliki zapisywane w: <strong>Box.com / Aplikacje / {(settings?.restaurantName||"app").replace(/[^a-zA-Z0-9_-]/g,"_")}/</strong></div>
              <div>• Automatyczne wersjonowanie — nadpisanie pliku tworzy nową wersję w Box</div>
              <div>• Token zapisany w localStorage i automatycznie odświeżany (refresh_token)</div>
              <div>• Bezpłatne konto Box: 10 GB przestrzeni</div>
              <div>• Przywrócenie kopii <strong>zastąpi wszystkie obecne dane</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: HARMONOGRAM ── */}
      {tab==="schedule" && (
        <div>
          <div style={{background:T.surface,borderRadius:16,border:"1.5px solid "+T.border,padding:16,marginBottom:16}}>
            <div style={{fontWeight:800,fontSize:14,color:T.text,marginBottom:12}}>⏰ Automatyczne kopie do Pantry Cloud</div>

            {!bkSettings.pantryId && (
              <div style={{background:"#fff8e1",border:"1px solid #fde68a",borderRadius:10,padding:"10px 14px",
                fontSize:12,color:"#92400e",marginBottom:14}}>
                ⚠ Najpierw uzupełnij <strong>Pantry ID</strong> w zakładce <strong>🧺 Pantry Cloud</strong>.
              </div>
            )}

            {/* Toggle aktywacji */}
            <div style={{display:"flex",alignItems:"center",gap:14,padding:"12px 14px",borderRadius:12,
              background:bkSettings.schedEnabled?"#f0fdf4":"#f8f8f8",
              border:"1.5px solid "+(bkSettings.schedEnabled?"#86efac":T.border),marginBottom:14,transition:"all .2s"}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:14,color:T.text}}>Automatyczne kopie zapasowe</div>
                <div style={{fontSize:12,color:T.muted,marginTop:2}}>
                  {bkSettings.schedEnabled ? "🟢 Aktywne — kopia wysyłana zgodnie z harmonogramem" : "🔴 Nieaktywne"}
                </div>
              </div>
              <div onClick={()=>setBkSettings(p=>({...p,schedEnabled:!p.schedEnabled}))}
                style={{width:48,height:26,borderRadius:13,background:bkSettings.schedEnabled?"#22c55e":"#d1d5db",
                  cursor:"pointer",position:"relative",transition:"all .2s",flexShrink:0}}>
                <div style={{position:"absolute",top:3,left:bkSettings.schedEnabled?23:3,width:20,height:20,
                  borderRadius:"50%",background:"#fff",transition:"all .2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
              </div>
            </div>

            {/* Częstotliwość */}
            <div style={{marginBottom:14}}>
              <label style={{fontSize:12,fontWeight:700,color:T.text,display:"block",marginBottom:6}}>Częstotliwość</label>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {[["hourly","Co godzinę"],["daily","Codziennie"],["weekly","Co tydzień"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setBkSettings(p=>({...p,schedFreq:v}))}
                    style={{flex:"1 1 110px",padding:"10px 14px",borderRadius:10,cursor:"pointer",fontWeight:600,fontSize:13,
                      border:"2px solid "+(bkSettings.schedFreq===v||(!bkSettings.schedFreq&&v==="daily")?TA.accent:T.border),
                      background:bkSettings.schedFreq===v||(!bkSettings.schedFreq&&v==="daily")?"#eff6ff":"#fff",
                      color:bkSettings.schedFreq===v||(!bkSettings.schedFreq&&v==="daily")?TA.accent:T.muted}}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Godzina */}
            {(bkSettings.schedFreq||"daily") !== "hourly" && (
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:700,color:T.text,display:"block",marginBottom:6}}>Godzina wykonania kopii</label>
                <input type="time" value={bkSettings.schedTime||"03:00"}
                  onChange={e=>setBkSettings(p=>({...p,schedTime:e.target.value}))}
                  style={{padding:"9px 12px",borderRadius:9,border:"1.5px solid "+T.border,fontSize:14,fontFamily:"monospace"}}/>
              </div>
            )}

            {/* Dzień tygodnia */}
            {bkSettings.schedFreq === "weekly" && (
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:700,color:T.text,display:"block",marginBottom:6}}>Dzień tygodnia</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {["Nie","Pon","Wt","Śr","Czw","Pt","Sob"].map((d,i)=>(
                    <button key={i} onClick={()=>setBkSettings(p=>({...p,schedDay:i}))}
                      style={{padding:"8px 12px",borderRadius:9,cursor:"pointer",fontWeight:600,fontSize:13,
                        border:"2px solid "+((bkSettings.schedDay??1)===i?TA.accent:T.border),
                        background:(bkSettings.schedDay??1)===i?"#eff6ff":"#fff",
                        color:(bkSettings.schedDay??1)===i?TA.accent:T.muted}}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Kopia Firebase w harmonogramie */}
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:11,
              background:"#fff8f1",border:"1.5px solid #fed7aa",marginBottom:14}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:13,color:T.text}}>🔥 Dołącz też kopię Firebase</div>
                <div style={{fontSize:12,color:T.muted,marginTop:1}}>Automatycznie wyśle też snapshot Firebase do Pantry</div>
              </div>
              <div onClick={()=>setBkSettings(p=>({...p,schedFirebase:!p.schedFirebase}))}
                style={{width:44,height:24,borderRadius:12,background:bkSettings.schedFirebase?"#f97316":"#d1d5db",
                  cursor:"pointer",position:"relative",transition:"all .2s",flexShrink:0}}>
                <div style={{position:"absolute",top:2,left:bkSettings.schedFirebase?21:2,width:20,height:20,
                  borderRadius:"50%",background:"#fff",transition:"all .2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
              </div>
            </div>

            {/* Status */}
            <div style={{background:"#f8fafc",borderRadius:12,padding:"12px 14px",fontSize:12,color:T.muted,lineHeight:1.8}}>
              {bkSettings.lastPantryBackup && (
                <div><strong>Ostatnia kopia:</strong> {new Date(bkSettings.lastPantryBackup).toLocaleString("pl-PL")}</div>
              )}
              {bkSettings.schedEnabled && (
                <div style={{color:TA.accent,fontWeight:600,marginTop:4}}>
                  ℹ Harmonogram sprawdzany co minutę — aplikacja musi być otwarta w przeglądarce.
                </div>
              )}
            </div>
          </div>

          {/* Ręczne wyzwolenie */}
          <button onClick={()=>sendToPantry(true)} disabled={!bkSettings.pantryId||pantryStatus==="sending"}
            style={{
              width:"100%",padding:"14px",borderRadius:14,fontWeight:800,fontSize:14,cursor:"pointer",
              background:!bkSettings.pantryId?"#f0f2f5":"linear-gradient(135deg,#f97316,#fb923c)",
              color:!bkSettings.pantryId?T.muted:"#fff",border:"none",
              boxShadow:bkSettings.pantryId?"0 4px 14px rgba(249,115,22,0.3)":"none"
            }}>
            🔄 Wykonaj kopię teraz (Pantry Cloud)
          </button>
        </div>
      )}

      {/* ── TAB: HISTORIA ── */}
      {tab==="history" && (
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontWeight:800,fontSize:14,color:T.text}}>📋 Historia kopii zapasowych</div>
            {history.length>0 && (
              <button onClick={()=>{saveBackupHistory([]);setHistory([]);}}
                style={{fontSize:11,color:"#ef4444",background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>
                Wyczyść
              </button>
            )}
          </div>
          {history.length===0 ? (
            <div style={{textAlign:"center",padding:40,color:T.muted}}>
              <div style={{fontSize:40,marginBottom:8}}>📋</div>
              <div>Brak historii kopii zapasowych</div>
            </div>
          ) : history.map(h=>{
            const typeIcon = h.type==="pantry"?"🧺":h.type==="firebase"?"🔥":h.type==="restore"?"🔄":h.type==="import"?"⬆":h.type==="onedrive"?"☁":h.type==="box"?"📦":"⬇";
            return (
              <div key={h.id} style={{
                background:T.surface, borderRadius:12, padding:"12px 14px", marginBottom:8,
                border:"1.5px solid "+(h.ok?"#e5e7eb":"#fca5a5"),
                borderLeft:"4px solid "+(h.ok?"#22c55e":"#ef4444")
              }}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13,color:T.text}}>{typeIcon} {h.label}</div>
                    {h.basket   && <div style={{fontSize:11,color:T.muted,marginTop:2,fontFamily:"monospace"}}>Koszyk: {h.basket}</div>}
                    {h.filename && <div style={{fontSize:11,color:T.muted,marginTop:2,fontFamily:"monospace"}}>{h.filename}</div>}
                    {h.size     && <div style={{fontSize:11,color:T.muted}}>Rozmiar: {h.size}</div>}
                    {h.error    && <div style={{fontSize:11,color:"#ef4444",marginTop:2}}>{h.error}</div>}
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:10,color:T.muted}}>{new Date(h.id).toLocaleString("pl-PL")}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   PANEL ADMINA — ZAMÓWIENIA ONLINE (osobny moduł, niezależny od Na wynos)
══════════════════════════════════════════════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════════════════════
   SYSTEM ZAMÓWIEŃ ONLINE — SKLEP DLA KLIENTA
   Osobny, niezależny system. Nie korzysta z menu restauracji.
══════════════════════════════════════════════════════════════════════════════ */

/* ─── Sklep klienta ─── */
/* ══════════════════════════════════════════════════════════════════════════════
   SKLEP ONLINE — widok klienta, dopasowany do stylu aplikacji
══════════════════════════════════════════════════════════════════════════════ */
function OnlineShop({ onlineMenu, onlineCats, menu, cats, settings, ac, onBack, onPlace, onSaveClient }) {
  const bp        = useBreakpoint();
  const isPhone   = bp.xs || (bp.isMobile && !bp.isTablet);
  const isTablet  = bp.isTablet;
  const isDesktop = bp.isDesktop;

  /* ── Motyw — taki sam jak w TableSelector ── */
  const bgStyle   = settings?.customerBgStyle || "bakery";
  const bgImage   = settings?.customerBgImage || "";
  const opacity   = settings?.cardOpacity ?? 0.97;
  const acBakery  = bgStyle === "bakery" ? "#C8621A" : ac;
  const primaryColor = acBakery;
  const pageBg    = bgStyle === "custom" && bgImage
    ? `url(${bgImage}) center/cover fixed`
    : bgStyle === "plain" ? T.bg
    : "linear-gradient(160deg,#fdf6e8 0%,#f5e6c8 50%,#ede0c4 100%)";
  const cardBase  = bgStyle === "bakery" ? "253,246,236" : "255,255,255";
  const cardBg    = `rgba(${cardBase},${opacity})`;
  const cardBorder= bgStyle === "bakery" ? "#E8D5BA" : T.border;
  const headBg    = bgStyle === "bakery"
    ? "linear-gradient(135deg,#3B1F0A 0%,#7B4F2E 100%)"
    : `linear-gradient(135deg,${ac} 0%,${ac}cc 100%)`;
  const textDark  = bgStyle === "bakery" ? "#3B1F0A" : T.text;
  const textMid   = bgStyle === "bakery" ? "#8B6A4A" : T.muted;

  /* ── Aktywne metody płatności z ustawień ── */
  const enabledPayments = settings?.onlinePayments || {cash:true,blik:true,card:true,paypal:true};
  const ALL_PAYMENTS = [
    {id:"cash",   emoji:"💵", label:"Gotówka",   desc:"Płatność przy odbiorze"},
    {id:"blik",   emoji:"📱", label:"BLIK",      desc:"Podaj kod przy odbiorze"},
    {id:"card",   emoji:"💳", label:"Karta",     desc:"Płatność kartą przy odbiorze"},
    {id:"paypal", emoji:"🅿", label:"PayPal",    desc:"Opłać online przez PayPal"},
  ];
  const PAYMENTS = ALL_PAYMENTS.filter(p => enabledPayments[p.id] !== false);

  /* ── Układ responsywny ── */
  const contentMax   = isDesktop ? 900 : isTablet ? 700 : "100%";
  const contentPad   = isDesktop ? "20px 0 100px" : isTablet ? "16px 20px 100px" : "12px 12px 100px";
  const gridCols     = isDesktop ? "1fr 1fr 1fr" : isTablet ? "1fr 1fr 1fr" : "1fr 1fr";
  const imgH         = isDesktop ? 140 : isTablet ? 130 : 105;
  const headerMax    = isDesktop ? 900 : isTablet ? 700 : "100%";
  const formGrid     = isPhone ? "1fr" : "1fr 1fr";

  const [step,      setStep]      = useState("shop");
  const [shopTab,   setShopTab]   = useState("online"); // "online" | "menu"
  const [cart,      setCart]      = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [activeMenuCat, setActiveMenuCat] = useState("all");
  const [menuSearch, setMenuSearch] = useState("");
  const [search,    setSearch]    = useState("");
  const [detail,    setDetail]    = useState(null);
  const [success,   setSuccess]   = useState(null);
  const [form,      setForm]      = useState({
    firstName:"", lastName:"", phone:"", email:"",
    address:"", note:"", readyAt:"", payment:"cash", zoneId:null
  });

  const [deliveryType, setDeliveryType] = useState("pickup"); // "pickup" | "delivery"
  const [itemImages,   setItemImages]   = useState({});        // { itemId: dataUrl }

  const handleItemImage = (itemId, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => setItemImages(prev => ({...prev, [itemId]: e.target.result}));
    reader.readAsDataURL(file);
  };

  const zones        = settings?.deliveryZones || [];
  const selectedZone = zones.find(z=>z.id===form.zoneId) || null;
  const deliveryCost = (deliveryType==="delivery" && selectedZone) ? (selectedZone.price||0) : 0;
  const itemsTotal   = cart.reduce((s,c)=>s+(Number(c.price)||0)*(c.qty||1),0);
  const total        = itemsTotal + deliveryCost;
  const cartQty      = cart.reduce((s,c)=>s+(c.qty||1),0);
  const canInfo      = form.firstName.trim()&&form.lastName.trim()&&form.phone.trim()
    && (deliveryType==="pickup" || (deliveryType==="delivery" && form.address.trim()));

  const allCats = [
    {id:"all", name:"🍽 Wszystkie"},
    ...(onlineCats||[]).filter(c=>c.active!==false),
  ];
  const visibleItems = (onlineMenu||[]).filter(m=>{
    const mc = activeCat==="all" || m.catId===activeCat;
    const ms = !search || m.name.toLowerCase().includes(search.toLowerCase());
    return mc && ms;
  });

  /* ── Menu restauracji (wspólny koszyk) ── */
  const menuCatsAll = [
    {id:"all", name:"🍽 Wszystkie"},
    ...(cats||[]).filter(c=>c.active!==false),
  ];
  const restaurantItems = (menu||[]).filter(m => {
    const mc = activeMenuCat==="all" || m.catId===activeMenuCat;
    const ms = !menuSearch || m.name.toLowerCase().includes(menuSearch.toLowerCase());
    return mc && ms && m.available !== false;
  });
  const cartQtyOf = id => (cart.find(c=>c.id===id)||{qty:0}).qty;
  const addToCart = item => setCart(p=>{
    const ex=p.find(c=>c.id===item.id);
    if(ex) return p.map(c=>c.id===item.id?{...c,qty:(c.qty||1)+1}:c);
    return [...p,{...item,qty:1}];
  });
  const removeFromCart = id => setCart(p=>p.map(c=>c.id===id?{...c,qty:(c.qty||1)-1}:c).filter(c=>c.qty>0));

  const handlePlace = () => {
    if(!canInfo){alert("Wypełnij imię, nazwisko i telefon");return;}
    if(!cart.length){alert("Dodaj produkty do koszyka");return;}
    const now=new Date();
    if(onSaveClient) onSaveClient({
      id:genId(), name:(form.firstName+" "+form.lastName).trim(),
      phone:form.phone, email:form.email, address:form.address,
      note:"Zamówienie online", discount:0, createdAt:now.toISOString(), source:"online",
    });
    const order={
      id:genId(), source:"online",
      firstName:form.firstName.trim(), lastName:form.lastName.trim(),
      phone:form.phone.trim(), email:form.email.trim(),
      address:form.address.trim(), note:form.note.trim(),
      readyAt:form.readyAt, payment:form.payment,
      deliveryType,
      deliveryZone: deliveryType==="delivery" ? (selectedZone?.name||null) : null,
      deliveryCost: deliveryType==="delivery" ? deliveryCost : 0,
      cart:cart.map(c=>({...c, customImage: itemImages[c.id]||null})),
      total, itemsTotal,
      date:now.toISOString().slice(0,10),
      time:now.toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),
      table:"online", tableNumber:"online",
      orderStatus:"new", takeaway:true,
    };
    onPlace(order);
    setSuccess(order);
  };

  const inp = {width:"100%",padding:"11px 13px",borderRadius:10,border:"1.5px solid "+cardBorder,fontSize:14,background:cardBg,color:textDark,boxSizing:"border-box",fontFamily:"inherit"};

  /* ── Ekran sukcesu ── */
  if(success) return (
    <div style={{minHeight:"100vh",background:pageBg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,textAlign:"center"}}>
      {bgStyle==="bakery"&&<>
        <div style={{position:"fixed",top:-80,right:-80,width:320,height:320,borderRadius:"50%",background:"rgba(200,98,26,0.10)",pointerEvents:"none"}}/>
        <div style={{position:"fixed",bottom:-100,left:-60,width:380,height:380,borderRadius:"50%",background:"rgba(59,31,10,0.12)",pointerEvents:"none"}}/>
      </>}
      <div style={{background:cardBg,borderRadius:24,padding:isPhone?"28px 20px":"40px 36px",border:"1.5px solid "+cardBorder,boxShadow:"0 16px 48px rgba(0,0,0,0.18)",maxWidth:400,width:"100%",position:"relative",zIndex:1}}>
        <div style={{fontSize:64,marginBottom:12}}>✅</div>
        <div style={{fontWeight:900,fontSize:22,color:textDark,marginBottom:8,fontFamily:"'Playfair Display',serif"}}>Zamówienie przyjęte!</div>
        <div style={{fontSize:13,color:textMid,marginBottom:6}}>Nr: #{String(success.id||"").slice(-6)}</div>
        <div style={{fontSize:14,color:textDark,marginBottom:4}}>{success.firstName} {success.lastName}</div>
        <div style={{fontSize:13,color:textMid,marginBottom:4}}>📞 {success.phone}</div>
        {success.readyAt&&<div style={{fontSize:13,color:primaryColor,fontWeight:700,marginBottom:4}}>⏰ Na: {new Date(success.readyAt).toLocaleString("pl-PL",{dateStyle:"short",timeStyle:"short"})}</div>}
        <div style={{fontWeight:900,fontSize:20,color:primaryColor,marginBottom:24}}>Łącznie: {(success.total||0).toFixed(2)} zł</div>
        <button onClick={()=>{setSuccess(null);setStep("shop");setCart([]);setForm({firstName:"",lastName:"",phone:"",email:"",address:"",note:"",readyAt:"",payment:"cash",zoneId:null});}}
          style={{width:"100%",padding:"13px",background:primaryColor,color:"#fff",border:"none",borderRadius:14,fontWeight:800,fontSize:15,cursor:"pointer",marginBottom:10,boxShadow:`0 4px 14px ${primaryColor}55`}}>
          🛒 Złóż kolejne zamówienie
        </button>
        <button onClick={onBack}
          style={{width:"100%",padding:"11px",background:"transparent",color:textMid,border:"1.5px solid "+cardBorder,borderRadius:12,fontWeight:700,fontSize:14,cursor:"pointer"}}>
          ← Wróć do stolików
        </button>
      </div>
    </div>
  );

  /* ── Modal szczegółów produktu ── */
  const DetailModal = () => {
    if(!detail) return null;
    const inCart = cartQtyOf(detail.id);
    return (
      <div onClick={()=>setDetail(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:800,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
        <div onClick={e=>e.stopPropagation()} style={{background:cardBg,borderRadius:"22px 22px 0 0",width:"100%",maxWidth:520,maxHeight:"88vh",overflowY:"auto",border:"1.5px solid "+cardBorder,borderBottom:"none"}}>
          {detail.image
            ?<img src={detail.image} alt={detail.name} style={{width:"100%",height:220,objectFit:"cover",borderRadius:"20px 20px 0 0"}}/>
            :<div style={{width:"100%",height:140,background:`linear-gradient(135deg,${primaryColor}22,${primaryColor}11)`,borderRadius:"20px 20px 0 0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:72}}>{detail.emoji||"🛒"}</div>
          }
          <div style={{padding:"20px 20px 32px"}}>
            {detail.catId&&<div style={{fontSize:11,fontWeight:700,color:primaryColor,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{(onlineCats||[]).find(c=>c.id===detail.catId)?.name||""}</div>}
            <div style={{fontWeight:900,fontSize:20,color:textDark,marginBottom:6,fontFamily:"'Playfair Display',serif"}}>{detail.name}</div>
            <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:10}}>
              <span style={{fontWeight:900,fontSize:22,color:primaryColor}}>{Number(detail.price||0).toFixed(2)} zł</span>
              <span style={{fontSize:13,color:textMid}}>/ {detail.unit||"szt"}</span>
              {detail.weight&&<span style={{fontSize:12,color:textMid}}>· {detail.weight}</span>}
            </div>
            {detail.desc&&<p style={{fontSize:14,color:textMid,lineHeight:1.7,marginBottom:12}}>{detail.desc}</p>}
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:12}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:detail.available!==false?"#16a34a":"#dc2626"}}/>
              <span style={{fontSize:12,fontWeight:700,color:detail.available!==false?"#16a34a":"#dc2626"}}>{detail.available!==false?"Dostępny":"Niedostępny"}</span>
            </div>
            {(detail.allergens||[]).length>0&&(
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:"#92400e",marginBottom:6}}>⚠ Alergeny</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {detail.allergens.map(a=><span key={a} style={{background:"#fff7ed",border:"1px solid #fed7aa",color:"#92400e",borderRadius:6,padding:"3px 8px",fontSize:11,fontWeight:700}}>{a}</span>)}
                </div>
              </div>
            )}
            {detail.available!==false&&(
              inCart>0
                ?<div style={{display:"flex",alignItems:"center",gap:14,justifyContent:"center",marginTop:8}}>
                  <button onClick={()=>removeFromCart(detail.id)} style={{width:46,height:46,borderRadius:12,background:"#fee2e2",border:"none",fontSize:22,cursor:"pointer",fontWeight:900,color:"#dc2626",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                  <span style={{fontWeight:900,fontSize:22,minWidth:36,textAlign:"center",color:textDark}}>{inCart}</span>
                  <button onClick={()=>addToCart(detail)} style={{width:46,height:46,borderRadius:12,background:primaryColor,border:"none",fontSize:22,cursor:"pointer",fontWeight:900,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 4px 12px ${primaryColor}55`}}>+</button>
                </div>
                :<button onClick={()=>{addToCart(detail);setDetail(null);}} style={{width:"100%",padding:"14px",background:primaryColor,color:"#fff",border:"none",borderRadius:14,fontWeight:900,fontSize:16,cursor:"pointer",boxShadow:`0 4px 14px ${primaryColor}55`}}>
                  🛒 Dodaj do koszyka
                </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ── Kroki ── */
  const STEPS = [["shop","1. Sklep"],["info","2. Dane"],["payment","3. Płatność"],["confirm","4. Podsumowanie"]];
  const stepIdx = STEPS.findIndex(([s])=>s===step);

  return (
    <div style={{minHeight:"100vh",background:pageBg,position:"relative"}}>
      {bgStyle==="bakery"&&<>
        <div style={{position:"fixed",top:-80,right:-80,width:320,height:320,borderRadius:"50%",background:"rgba(200,98,26,0.10)",pointerEvents:"none",zIndex:0}}/>
        <div style={{position:"fixed",bottom:-100,left:-60,width:380,height:380,borderRadius:"50%",background:"rgba(59,31,10,0.12)",pointerEvents:"none",zIndex:0}}/>
      </>}
      <DetailModal/>

      {/* ── Nagłówek ── */}
      <div style={{background:headBg,padding:isPhone?"13px 14px 11px":isTablet?"14px 20px":"16px 24px",position:"sticky",top:0,zIndex:100,boxShadow:"0 4px 18px rgba(0,0,0,0.22)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,maxWidth:headerMax,margin:"0 auto"}}>
          <button onClick={onBack} style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",borderRadius:9,padding:"7px 12px",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:13,flexShrink:0}}>← Wróć</button>
          <div style={{flex:1,minWidth:0}}>
            <div style={{color:"#fff",fontWeight:900,fontSize:isPhone?15:isTablet?17:19,fontFamily:"'Playfair Display',serif",lineHeight:1.2,display:"flex",alignItems:"center",gap:8}}>
              {settings?.logo
                ? <img src={settings.logo} alt="" style={{width:isPhone?28:34,height:isPhone?28:34,borderRadius:8,objectFit:"cover",flexShrink:0,border:"1.5px solid rgba(255,255,255,0.3)"}}/>
                : <span>🛒</span>
              }
              Sklep online
            </div>
            <div style={{color:"rgba(255,255,255,0.7)",fontSize:isPhone?11:12,marginTop:1}}>{settings?.restaurantName||"Restauracja"}</div>
          </div>
          {cartQty>0&&step==="shop"&&(
            <button onClick={()=>setStep("info")} style={{background:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.35)",borderRadius:10,padding:"8px 14px",color:"#fff",fontWeight:900,cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:6,backdropFilter:"blur(4px)"}}>
              🛒 {cartQty} · {total.toFixed(2)} zł
            </button>
          )}
        </div>
      </div>

      <div style={{maxWidth:contentMax,width:"100%",margin:"0 auto",padding:contentPad,position:"relative",zIndex:1}}>

        {/* Kroki */}
        <div style={{display:"flex",gap:4,marginBottom:14}}>
          {STEPS.map(([s,l],i)=>(
            <div key={s} onClick={()=>{
              if(i<stepIdx) setStep(s);
              if(s==="info"&&cart.length>0) setStep(s);
              if(s==="payment"&&canInfo&&cart.length>0) setStep(s);
              if(s==="confirm"&&canInfo&&cart.length>0) setStep(s);
            }} style={{flex:1,textAlign:"center",padding:"7px 2px",borderRadius:9,fontSize:10,fontWeight:700,cursor:"pointer",transition:"all .15s",
              background:s===step?primaryColor:i<stepIdx?primaryColor+"22":cardBg,
              color:s===step?"#fff":i<stepIdx?primaryColor:textMid,
              border:"1.5px solid "+(s===step?primaryColor:i<stepIdx?primaryColor+"55":cardBorder)}}>
              {l}
            </div>
          ))}
        </div>

        {/* ══ KROK 1: SKLEP ══ */}
        {step==="shop"&&(<>
          {/* ── Zakładki: Sklep / Menu restauracji ── */}
          <div style={{display:"flex",gap:8,marginBottom:14,background:cardBg,borderRadius:14,padding:5,border:"1.5px solid "+cardBorder}}>
            {[
              {id:"online", icon:"🛒", label:"Sklep online"},
              {id:"menu",   icon:"🍽", label:"Menu restauracji"},
            ].map(t=>(
              <button key={t.id} onClick={()=>setShopTab(t.id)}
                style={{flex:1,padding:"10px 8px",borderRadius:10,border:"none",cursor:"pointer",
                  background:shopTab===t.id?primaryColor:cardBg,
                  color:shopTab===t.id?"#fff":textMid,
                  fontWeight:shopTab===t.id?800:600,fontSize:13,
                  boxShadow:shopTab===t.id?`0 3px 10px ${primaryColor}44`:"none",
                  transition:"all .15s",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                <span>{t.icon}</span><span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* ══ ZAKŁADKA: SKLEP ONLINE ══ */}
          {shopTab==="online"&&(<>
          {/* Kategorie */}
          <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:10,marginBottom:8,scrollbarWidth:"none"}}>
            {allCats.map(c=>(
              <button key={c.id} onClick={()=>setActiveCat(c.id)}
                style={{padding:"7px 14px",borderRadius:20,border:"1.5px solid "+(activeCat===c.id?primaryColor:cardBorder),
                  background:activeCat===c.id?primaryColor:cardBg,
                  color:activeCat===c.id?"#fff":textDark,
                  fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,
                  boxShadow:activeCat===c.id?`0 2px 8px ${primaryColor}44`:"none",transition:"all .15s"}}>
                {c.name}
              </button>
            ))}
          </div>
          {/* Szukaj */}
          <div style={{position:"relative",marginBottom:12}}>
            <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:16,color:textMid}}>🔍</span>
            <input style={{...inp,paddingLeft:38}} placeholder="Szukaj produktów..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>

          {/* Siatka produktów */}
          {visibleItems.length===0&&(
            <div style={{textAlign:"center",padding:"48px 20px",color:textMid}}>
              <div style={{fontSize:48,marginBottom:12}}>🛒</div>
              <div style={{fontWeight:700,fontSize:15,color:textDark}}>Brak produktów</div>
              <div style={{fontSize:13,marginTop:4}}>Zmień kategorię lub wyszukiwanie</div>
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:gridCols,gap:isPhone?10:isTablet?12:16}}>
            {visibleItems.map(item=>{
              const qty    = cartQtyOf(item.id);
              const unavail= item.available===false;
              return (
                <div key={item.id} onClick={()=>!unavail&&setDetail(item)}
                  style={{background:cardBg,borderRadius:16,overflow:"hidden",
                    border:"2px solid "+(qty>0?primaryColor:unavail?"#fecaca":cardBorder),
                    cursor:unavail?"default":"pointer",transition:"all .15s",position:"relative",
                    boxShadow:qty>0?`0 4px 14px ${primaryColor}33`:"0 2px 8px rgba(0,0,0,0.07)"}}>
                  {/* Zdjęcie */}
                  <div style={{height:imgH,background:`linear-gradient(135deg,${primaryColor}11,${primaryColor}06)`,position:"relative",overflow:"hidden"}}>
                    {item.image
                      ?<img src={item.image} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      :<div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:isPhone?40:52}}>{item.emoji||"🛒"}</div>
                    }
                    {unavail&&<div style={{position:"absolute",inset:0,background:"rgba(255,255,255,0.72)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <span style={{background:"#dc2626",color:"#fff",borderRadius:8,padding:"3px 10px",fontSize:10,fontWeight:800}}>NIEDOSTĘPNY</span>
                    </div>}
                    {qty>0&&<div style={{position:"absolute",top:6,right:6,background:primaryColor,color:"#fff",borderRadius:"50%",width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:12,boxShadow:`0 2px 6px ${primaryColor}66`}}>{qty}</div>}
                  </div>
                  <div style={{padding:isPhone?"10px 10px 12px":"12px 12px 14px"}}>
                    <div style={{fontWeight:700,fontSize:isPhone?11:isTablet?13:14,color:textDark,marginBottom:3,lineHeight:1.3}}>{item.name}</div>
                    {item.weight&&<div style={{fontSize:isPhone?10:11,color:textMid,marginBottom:3}}>{item.weight}</div>}
                    {(item.allergens||[]).length>0&&<div style={{fontSize:9,color:"#92400e",marginBottom:3}}>⚠ {item.allergens.slice(0,2).join(", ")}{item.allergens.length>2?"...":""}</div>}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:4}}>
                      <div>
                        <span style={{fontWeight:900,fontSize:isPhone?13:isTablet?15:16,color:unavail?textMid:primaryColor}}>{Number(item.price||0).toFixed(2)} zł</span>
                        <span style={{fontSize:9,color:textMid,marginLeft:2}}>/{item.unit||"szt"}</span>
                      </div>
                      {!unavail&&(qty>0
                        ?<div style={{display:"flex",alignItems:"center",gap:4}} onClick={e=>e.stopPropagation()}>
                          <button onClick={()=>removeFromCart(item.id)} style={{width:isPhone?22:26,height:isPhone?22:26,borderRadius:6,background:"#fee2e2",border:"none",fontSize:isPhone?13:15,cursor:"pointer",fontWeight:900,color:"#dc2626",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                          <span style={{fontWeight:900,fontSize:isPhone?12:14,color:textDark,minWidth:16,textAlign:"center"}}>{qty}</span>
                          <button onClick={e=>{e.stopPropagation();addToCart(item);}} style={{width:isPhone?22:26,height:isPhone?22:26,borderRadius:6,background:primaryColor,border:"none",fontSize:isPhone?13:15,cursor:"pointer",fontWeight:900,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 2px 5px ${primaryColor}55`}}>+</button>
                        </div>
                        :<button onClick={e=>{e.stopPropagation();addToCart(item);}} style={{width:isPhone?26:30,height:isPhone?26:30,borderRadius:8,background:primaryColor,border:"none",fontSize:isPhone?14:16,cursor:"pointer",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 2px 6px ${primaryColor}55`}}>+</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sticky koszyk */}
          {cartQty>0&&(
            <div style={{position:"fixed",bottom:16,left:"50%",transform:"translateX(-50%)",width:"calc(100% - 28px)",maxWidth:492,zIndex:200}}>
              <button onClick={()=>setStep("info")} style={{width:"100%",padding:"15px 18px",background:headBg,color:"#fff",border:"none",borderRadius:16,fontWeight:900,fontSize:15,boxShadow:"0 6px 24px rgba(0,0,0,0.28)",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
                <span style={{background:"rgba(255,255,255,0.22)",borderRadius:8,padding:"3px 11px",fontSize:14,fontWeight:900}}>{cartQty}</span>
                <span style={{flex:1,textAlign:"left"}}>Przejdź do zamówienia</span>
                <span style={{fontWeight:900}}>{total.toFixed(2)} zł →</span>
              </button>
            </div>
          )}
          </>)} {/* koniec shopTab==="online" */}

          {/* ══ ZAKŁADKA: MENU RESTAURACJI ══ */}
          {shopTab==="menu"&&(<>
            {/* Kategorie menu */}
            <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:10,marginBottom:8,scrollbarWidth:"none"}}>
              {menuCatsAll.map(c=>(
                <button key={c.id} onClick={()=>setActiveMenuCat(c.id)}
                  style={{padding:"7px 14px",borderRadius:20,border:"1.5px solid "+(activeMenuCat===c.id?primaryColor:cardBorder),
                    background:activeMenuCat===c.id?primaryColor:cardBg,
                    color:activeMenuCat===c.id?"#fff":textDark,
                    fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0,
                    boxShadow:activeMenuCat===c.id?`0 2px 8px ${primaryColor}44`:"none",transition:"all .15s"}}>
                  {c.emoji||""} {c.name}
                </button>
              ))}
            </div>

            {/* Szukaj */}
            <div style={{position:"relative",marginBottom:12}}>
              <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:16,color:textMid}}>🔍</span>
              <input style={{...inp,paddingLeft:38}} placeholder="Szukaj w menu..." value={menuSearch} onChange={e=>setMenuSearch(e.target.value)}/>
            </div>

            {restaurantItems.length===0&&(
              <div style={{textAlign:"center",padding:"48px 20px",color:textMid}}>
                <div style={{fontSize:48,marginBottom:12}}>🍽</div>
                <div style={{fontWeight:700,fontSize:15,color:textDark}}>Brak pozycji w menu</div>
                <div style={{fontSize:13,marginTop:4}}>Zmień kategorię lub wyszukiwanie</div>
              </div>
            )}

            <div style={{display:"grid",gridTemplateColumns:gridCols,gap:isPhone?10:isTablet?12:16}}>
              {restaurantItems.map(item=>{
                const qty = cartQtyOf(item.id+"_menu");
                return (
                  <div key={item.id}
                    style={{background:cardBg,borderRadius:16,overflow:"hidden",
                      border:"2px solid "+(qty>0?primaryColor:cardBorder),
                      cursor:"pointer",transition:"all .15s",position:"relative",
                      boxShadow:qty>0?`0 4px 14px ${primaryColor}33`:"0 2px 8px rgba(0,0,0,0.07)"}}>
                    {/* Zdjęcie */}
                    <div style={{height:imgH,background:`linear-gradient(135deg,${primaryColor}11,${primaryColor}06)`,position:"relative",overflow:"hidden"}}>
                      {item.image
                        ?<img src={item.image} alt={item.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                        :<div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:isPhone?40:52}}>{item.emoji||"🍽"}</div>
                      }
                      {qty>0&&<div style={{position:"absolute",top:6,right:6,background:primaryColor,color:"#fff",borderRadius:"50%",width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:900,fontSize:12,boxShadow:`0 2px 6px ${primaryColor}66`}}>{qty}</div>}
                    </div>
                    <div style={{padding:isPhone?"10px 10px 12px":"12px 12px 14px"}}>
                      <div style={{fontWeight:700,fontSize:isPhone?11:isTablet?13:14,color:textDark,marginBottom:3,lineHeight:1.3}}>{item.name}</div>
                      {item.description&&<div style={{fontSize:10,color:textMid,marginBottom:3,lineHeight:1.4}}>{item.description.slice(0,60)}{item.description.length>60?"...":""}</div>}
                      {(() => {
                        const alg = item.allergens;
                        if (!alg || (Array.isArray(alg) && alg.length===0) || alg==="") return null;
                        const arr = Array.isArray(alg) ? alg : String(alg).split(/,\s*/);
                        return <div style={{fontSize:9,color:"#92400e",marginBottom:3}}>⚠ {arr.slice(0,2).join(", ")}{arr.length>2?"...":""}</div>;
                      })()}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:4}}>
                        <div>
                          <span style={{fontWeight:900,fontSize:isPhone?13:isTablet?15:16,color:primaryColor}}>{Number(item.price||0).toFixed(2)} zł</span>
                          <span style={{fontSize:9,color:textMid,marginLeft:2}}>/{item.unit||"szt"}</span>
                        </div>
                        {qty>0
                          ?<div style={{display:"flex",alignItems:"center",gap:4}}>
                            <button onClick={()=>removeFromCart(item.id+"_menu")} style={{width:isPhone?22:26,height:isPhone?22:26,borderRadius:6,background:"#fee2e2",border:"none",fontSize:isPhone?13:15,cursor:"pointer",fontWeight:900,color:"#dc2626",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                            <span style={{fontWeight:900,fontSize:isPhone?12:14,color:textDark,minWidth:16,textAlign:"center"}}>{qty}</span>
                            <button onClick={()=>addToCart({...item,id:item.id+"_menu",_origId:item.id,_source:"menu"})} style={{width:isPhone?22:26,height:isPhone?22:26,borderRadius:6,background:primaryColor,border:"none",fontSize:isPhone?13:15,cursor:"pointer",fontWeight:900,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 2px 5px ${primaryColor}55`}}>+</button>
                          </div>
                          :<button onClick={()=>addToCart({...item,id:item.id+"_menu",_origId:item.id,_source:"menu"})} style={{width:isPhone?26:30,height:isPhone?26:30,borderRadius:8,background:primaryColor,border:"none",fontSize:isPhone?14:16,cursor:"pointer",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 2px 6px ${primaryColor}55`}}>+</button>
                        }
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sticky koszyk */}
            {cartQty>0&&(
              <div style={{position:"fixed",bottom:16,left:"50%",transform:"translateX(-50%)",width:"calc(100% - 28px)",maxWidth:492,zIndex:200}}>
                <button onClick={()=>setStep("info")} style={{width:"100%",padding:"15px 18px",background:headBg,color:"#fff",border:"none",borderRadius:16,fontWeight:900,fontSize:15,boxShadow:"0 6px 24px rgba(0,0,0,0.28)",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
                  <span style={{background:"rgba(255,255,255,0.22)",borderRadius:8,padding:"3px 11px",fontSize:14,fontWeight:900}}>{cartQty}</span>
                  <span style={{flex:1,textAlign:"left"}}>Przejdź do zamówienia</span>
                  <span style={{fontWeight:900}}>{total.toFixed(2)} zł →</span>
                </button>
              </div>
            )}
          </>)} {/* koniec shopTab==="menu" */}
        </>)}

        {/* ══ KROK 2: DANE ══ */}
        {step==="info"&&(
          <div style={{background:cardBg,borderRadius:18,padding:isPhone?16:20,border:"1.5px solid "+cardBorder,boxShadow:"0 4px 18px rgba(0,0,0,0.09)"}}>
            <div style={{fontWeight:900,fontSize:16,color:textDark,marginBottom:16,fontFamily:"'Playfair Display',serif"}}>📝 Twoje dane</div>

            {/* ── Wybór: odbiór / dostawa ── */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18}}>
              {[
                {k:"pickup",   icon:"🏃", label:"Odbiór osobisty", desc:"Odbierasz w lokalu"},
                {k:"delivery", icon:"🚗", label:"Dostawa",         desc:"Dowozimy pod adres"},
              ].map(({k,icon,label,desc})=>(
                <div key={k} onClick={()=>setDeliveryType(k)}
                  style={{padding:"13px 14px",borderRadius:14,cursor:"pointer",textAlign:"center",
                    border:"2px solid "+(deliveryType===k?primaryColor:cardBorder),
                    background:deliveryType===k?primaryColor+"11":cardBg,
                    transition:"all .15s",userSelect:"none"}}>
                  <div style={{fontSize:26,marginBottom:4}}>{icon}</div>
                  <div style={{fontWeight:800,fontSize:13,color:deliveryType===k?primaryColor:textDark}}>{label}</div>
                  <div style={{fontSize:11,color:textMid,marginTop:2}}>{desc}</div>
                  {deliveryType===k&&<div style={{width:20,height:20,borderRadius:"50%",background:primaryColor,color:"#fff",fontSize:12,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center",margin:"6px auto 0"}}>✓</div>}
                </div>
              ))}
            </div>

            <div style={{display:"grid",gridTemplateColumns:formGrid,gap:10}}>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:textMid,display:"block",marginBottom:4}}>Imię *</label>
                <input style={inp} placeholder="Jan" value={form.firstName} onChange={e=>setForm(p=>({...p,firstName:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:textMid,display:"block",marginBottom:4}}>Nazwisko *</label>
                <input style={inp} placeholder="Kowalski" value={form.lastName} onChange={e=>setForm(p=>({...p,lastName:e.target.value}))}/>
              </div>
              <div style={{gridColumn:"span "+(isPhone?"1":"2")}}>
                <label style={{fontSize:11,fontWeight:700,color:textMid,display:"block",marginBottom:4}}>Telefon *</label>
                <input style={inp} type="tel" placeholder="+48 600 000 000" value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))}/>
              </div>
              <div style={{gridColumn:"span "+(isPhone?"1":"2")}}>
                <label style={{fontSize:11,fontWeight:700,color:textMid,display:"block",marginBottom:4}}>E-mail</label>
                <input style={inp} type="email" placeholder="jan@example.com" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))}/>
              </div>

              {/* Adres i strefa — tylko przy dostawie */}
              {deliveryType==="delivery"&&(<>
                <div style={{gridColumn:"span "+(isPhone?"1":"2")}}>
                  <label style={{fontSize:11,fontWeight:700,color:textMid,display:"block",marginBottom:4}}>Adres dostawy *</label>
                  <input style={inp} placeholder="ul. Przykładowa 1, 00-001 Warszawa" value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))}/>
                </div>
                {zones.length>0&&(
                  <div style={{gridColumn:"span "+(isPhone?"1":"2")}}>
                    <label style={{fontSize:11,fontWeight:700,color:textMid,display:"block",marginBottom:6}}>Strefa dowozu</label>
                    {zones.map(z=>(
                      <div key={z.id} onClick={()=>setForm(p=>({...p,zoneId:z.id}))}
                        style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:12,cursor:"pointer",marginBottom:6,
                          border:"2px solid "+(form.zoneId===z.id?primaryColor:cardBorder),
                          background:form.zoneId===z.id?primaryColor+"11":cardBg,transition:"all .15s"}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:13,color:textDark}}>{z.name}</div>
                          <div style={{fontSize:11,color:textMid}}>{z.kmFrom||0}–{z.kmTo||0} km</div>
                        </div>
                        <div style={{fontWeight:900,fontSize:15,color:form.zoneId===z.id?primaryColor:textMid}}>{z.price===0?"Gratis":(z.price||0).toFixed(2)+" zł"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>)}

              <div style={{gridColumn:"span "+(isPhone?"1":"2")}}>
                <label style={{fontSize:11,fontWeight:700,color:textMid,display:"block",marginBottom:4}}>Na kiedy ma być gotowe</label>
                <input style={inp} type="datetime-local" value={form.readyAt} onChange={e=>setForm(p=>({...p,readyAt:e.target.value}))}/>
              </div>
              <div style={{gridColumn:"span "+(isPhone?"1":"2")}}>
                <label style={{fontSize:11,fontWeight:700,color:textMid,display:"block",marginBottom:4}}>Uwagi do zamówienia</label>
                <textarea style={{...inp,minHeight:68,resize:"vertical"}} placeholder="np. bez cebuli, kod do domofonu..." value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))}/>
              </div>

              {/* Upload zdjęcia do produktu (np. grafika na torcie) */}
              {cart.some(c=>c.allowCustomImage)&&(
                <div style={{gridColumn:"span "+(isPhone?"1":"2")}}>
                  <div style={{marginBottom:10,borderRadius:14,overflow:"hidden",border:"1.5px solid "+primaryColor+"44"}}>
                    {/* Nagłówek */}
                    <div style={{background:primaryColor,padding:"11px 16px",display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:22}}>🎨</span>
                      <div>
                        <div style={{color:"#fff",fontWeight:800,fontSize:14,lineHeight:1.2}}>Własna grafika lub zdjęcie jako wzór</div>
                        <div style={{color:"rgba(255,255,255,0.8)",fontSize:11,marginTop:2}}>Spersonalizuj swoje zamówienie</div>
                      </div>
                    </div>
                    {/* Opis */}
                    <div style={{background:primaryColor+"0d",padding:"12px 16px",borderBottom:"1px solid "+primaryColor+"22"}}>
                      <p style={{fontSize:12,color:textDark,lineHeight:1.75,margin:0}}>
                        Możesz w prosty sposób spersonalizować wygląd swojego zamówienia. Prześlij nam dowolny plik
                        (np. zdjęcie solenizanta, logo firmy lub postać z bajki), a my wydrukujemy go na jadalnym opłatku.
                      </p>
                      <p style={{fontSize:12,color:textMid,lineHeight:1.75,margin:"8px 0 0"}}>
                        Możesz też pokazać nam zdjęcie tortu znalezione w sieci, które najbardziej Ci się podoba,
                        a nasi cukiernicy odtworzą wskazany styl, układ owoców czy sposób tynkowania.
                      </p>
                    </div>
                    {/* Produkty z opcją zdjęcia */}
                    <div style={{padding:"12px 16px",background:cardBg}}>
                      {cart.filter(c=>c.allowCustomImage).map(c=>(
                        <div key={c.id} style={{marginBottom:10}}>
                          <div style={{fontWeight:700,fontSize:12,color:textDark,marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                            <span>{c.emoji||"🎂"}</span> {c.name}
                          </div>
                          {itemImages[c.id]
                            ? <div style={{display:"flex",alignItems:"center",gap:12}}>
                                <div style={{position:"relative",display:"inline-block"}}>
                                  <img src={itemImages[c.id]} alt="podgląd" style={{height:80,borderRadius:8,objectFit:"cover",border:"2px solid "+primaryColor}}/>
                                  <button onClick={()=>setItemImages(p=>{const n={...p};delete n[c.id];return n;})}
                                    style={{position:"absolute",top:-6,right:-6,width:22,height:22,borderRadius:"50%",background:"#dc2626",color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                                </div>
                                <div style={{fontSize:12,color:"#059669",fontWeight:700}}>✅ Grafika dodana — dziękujemy!</div>
                              </div>
                            : <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"11px 14px",borderRadius:10,background:primaryColor+"11",border:"1.5px dashed "+primaryColor,color:primaryColor,fontWeight:700,fontSize:13}}>
                                <span style={{fontSize:22}}>📁</span>
                                <div>
                                  <div style={{fontWeight:800}}>Wgraj zdjęcie / grafikę</div>
                                  <div style={{fontSize:10,fontWeight:400,color:textMid,marginTop:1}}>JPG, PNG, PDF — maks. 10 MB</div>
                                </div>
                                <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleItemImage(c.id,e.target.files[0])}/>
                              </label>
                          }
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button onClick={()=>{
              if(!canInfo){
                alert(deliveryType==="delivery"
                  ?"Wypełnij imię, nazwisko, telefon i adres dostawy"
                  :"Wypełnij imię, nazwisko i telefon");
                return;
              }
              setStep("payment");
            }}
              style={{width:"100%",marginTop:14,padding:"14px",background:primaryColor,color:"#fff",border:"none",borderRadius:12,fontWeight:900,cursor:"pointer",fontSize:15,boxShadow:`0 4px 14px ${primaryColor}44`}}>
              Dalej — wybierz płatność →
            </button>
            {/* Edytowalny koszyk */}
            <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid "+cardBorder}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:13,color:textMid}}>🛒 Koszyk ({cartQty} poz.)</div>
                <button onClick={()=>setStep("shop")}
                  style={{fontSize:11,fontWeight:700,color:primaryColor,background:primaryColor+"15",border:"1px solid "+primaryColor+"44",borderRadius:7,padding:"4px 10px",cursor:"pointer"}}>
                  + Dodaj produkty
                </button>
              </div>
              {cart.map(c=>(
                <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid "+cardBorder+"66"}}>
                  <span style={{fontSize:16,flexShrink:0}}>{c.emoji||"🛒"}</span>
                  <span style={{flex:1,fontSize:13,color:textDark,fontWeight:600}}>{c.name}</span>
                  <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                    <button onClick={()=>removeFromCart(c.id)}
                      style={{width:22,height:22,borderRadius:6,background:"#fee2e2",border:"none",fontSize:13,cursor:"pointer",fontWeight:900,color:"#dc2626",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                    <span style={{fontWeight:800,fontSize:13,minWidth:18,textAlign:"center",color:textDark}}>{c.qty}</span>
                    <button onClick={()=>addToCart(c)}
                      style={{width:22,height:22,borderRadius:6,background:primaryColor,border:"none",fontSize:13,cursor:"pointer",fontWeight:900,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                  </div>
                  <span style={{fontWeight:700,fontSize:13,color:primaryColor,minWidth:58,textAlign:"right"}}>{(Number(c.price||0)*c.qty).toFixed(2)} zł</span>
                  <button onClick={()=>setCart(p=>p.filter(x=>x.id!==c.id))}
                    style={{width:20,height:20,borderRadius:5,background:"#f3f4f6",border:"none",fontSize:11,cursor:"pointer",color:"#9ca3af",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                </div>
              ))}
              {deliveryType==="delivery"&&deliveryCost>0&&(
                <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"6px 0",color:textMid}}>
                  <span>🚗 Dostawa ({selectedZone?.name||""})</span>
                  <span style={{fontWeight:700}}>{deliveryCost.toFixed(2)} zł</span>
                </div>
              )}
              <div style={{textAlign:"right",marginTop:8,fontWeight:900,fontSize:15,color:primaryColor}}>Razem: {total.toFixed(2)} zł</div>
            </div>
          </div>
        )}

        {/* ══ KROK 3: PŁATNOŚĆ ══ */}
        {step==="payment"&&(
          <div style={{background:cardBg,borderRadius:18,padding:isPhone?16:20,border:"1.5px solid "+cardBorder,boxShadow:"0 4px 18px rgba(0,0,0,0.09)"}}>
            <div style={{fontWeight:900,fontSize:16,color:textDark,marginBottom:16,fontFamily:"'Playfair Display',serif"}}>💳 Metoda płatności</div>
            {PAYMENTS.map(p=>(
              <div key={p.id} onClick={()=>setForm(f=>({...f,payment:p.id}))}
                style={{display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderRadius:14,cursor:"pointer",marginBottom:10,
                  border:"2px solid "+(form.payment===p.id?primaryColor:cardBorder),
                  background:form.payment===p.id?primaryColor+"11":cardBg,transition:"all .15s"}}>
                <div style={{fontSize:26,width:36,textAlign:"center"}}>{p.emoji}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:800,fontSize:14,color:textDark}}>{p.label}</div>
                  <div style={{fontSize:12,color:textMid,marginTop:1}}>{p.desc}</div>
                </div>
                <div style={{width:22,height:22,borderRadius:"50%",border:"2px solid "+(form.payment===p.id?primaryColor:cardBorder),background:form.payment===p.id?primaryColor:"transparent",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12,transition:"all .15s",flexShrink:0}}>
                  {form.payment===p.id?"✓":""}
                </div>
              </div>
            ))}
            <button onClick={()=>setStep("confirm")}
              style={{width:"100%",marginTop:4,padding:"14px",background:primaryColor,color:"#fff",border:"none",borderRadius:12,fontWeight:900,cursor:"pointer",fontSize:15,boxShadow:`0 4px 14px ${primaryColor}44`}}>
              Dalej — podsumowanie →
            </button>
          </div>
        )}

        {/* ══ KROK 4: PODSUMOWANIE ══ */}
        {step==="confirm"&&(
          <div>
            <div style={{background:cardBg,borderRadius:18,padding:16,marginBottom:12,border:"1.5px solid "+cardBorder,boxShadow:"0 2px 10px rgba(0,0,0,0.07)"}}>
              <div style={{fontWeight:700,fontSize:13,color:textMid,marginBottom:10}}>👤 Dane zamawiającego</div>
              <div style={{fontSize:13,color:textMid,lineHeight:1.9}}>
                <div style={{color:textDark,fontWeight:800,fontSize:14}}>{form.firstName} {form.lastName}</div>
                <div>📞 {form.phone}</div>
                {form.email&&<div>✉ {form.email}</div>}
                {form.address&&<div>🏠 {form.address}</div>}
                {form.readyAt&&<div style={{color:primaryColor,fontWeight:700}}>⏰ Na: {new Date(form.readyAt).toLocaleString("pl-PL",{dateStyle:"short",timeStyle:"short"})}</div>}
                {form.note&&<div>💬 {form.note}</div>}
                <div>💳 {PAYMENTS.find(p=>p.id===form.payment)?.emoji} {PAYMENTS.find(p=>p.id===form.payment)?.label}</div>
              </div>
            </div>
            <div style={{background:cardBg,borderRadius:18,padding:16,marginBottom:14,border:"1.5px solid "+cardBorder,boxShadow:"0 2px 10px rgba(0,0,0,0.07)"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontWeight:700,fontSize:13,color:textMid}}>🛒 Zamówienie</div>
                <button onClick={()=>setStep("shop")}
                  style={{fontSize:11,fontWeight:700,color:primaryColor,background:primaryColor+"15",border:"1px solid "+primaryColor+"44",borderRadius:7,padding:"4px 10px",cursor:"pointer"}}>
                  ✏️ Edytuj
                </button>
              </div>
              {cart.map(c=>(
                <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",borderBottom:"1px solid "+cardBorder+"55"}}>
                  <span style={{fontSize:15,flexShrink:0}}>{c.emoji||"🛒"}</span>
                  <span style={{flex:1,fontSize:13,color:textDark,fontWeight:600}}>{c.name}</span>
                  <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                    <button onClick={()=>removeFromCart(c.id)}
                      style={{width:20,height:20,borderRadius:5,background:"#fee2e2",border:"none",fontSize:12,cursor:"pointer",fontWeight:900,color:"#dc2626",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                    <span style={{fontWeight:800,fontSize:13,minWidth:18,textAlign:"center"}}>{c.qty}</span>
                    <button onClick={()=>addToCart(c)}
                      style={{width:20,height:20,borderRadius:5,background:primaryColor,border:"none",fontSize:12,cursor:"pointer",fontWeight:900,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                  </div>
                  <span style={{fontWeight:700,fontSize:13,color:primaryColor,minWidth:56,textAlign:"right"}}>{(Number(c.price||0)*c.qty).toFixed(2)} zł</span>
                  <button onClick={()=>setCart(p=>p.filter(x=>x.id!==c.id))}
                    style={{width:18,height:18,borderRadius:4,background:"#f3f4f6",border:"none",fontSize:10,cursor:"pointer",color:"#9ca3af",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
                </div>
              ))}
              {deliveryCost>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"5px 0",color:"#0369a1"}}>
                <span>🚗 Dostawa ({selectedZone?.name})</span>
                <span style={{fontWeight:700}}>{deliveryCost.toFixed(2)} zł</span>
              </div>}
              <div style={{display:"flex",justifyContent:"flex-end",marginTop:10,fontWeight:900,fontSize:17,color:primaryColor}}>
                Łącznie: {total.toFixed(2)} zł
              </div>
            </div>
            <button onClick={handlePlace}
              style={{width:"100%",padding:"16px",background:"linear-gradient(135deg,#16a34a,#22c55e)",color:"#fff",border:"none",borderRadius:16,fontWeight:900,fontSize:16,boxShadow:"0 6px 20px rgba(22,163,74,0.38)",cursor:"pointer"}}>
              ✅ Złóż zamówienie
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Dokument druku — poza komponentem żeby nie resetował przy re-renderze ── */
function OnlineDocModal({doc, onClose, settings, ac}) {
  if (!doc) return null;
  const PAYMENTS = {cash:"💵 Gotówka",blik:"📱 BLIK",card:"💳 Karta",paypal:"🅿 PayPal"};
  const DOC_TYPES = [
    {id:"invoice",label:"🧾 Faktura"},{id:"wz",label:"📋 Dok. WZ"},
    {id:"delivery",label:"🚚 Dowód dostawy"},{id:"release",label:"✅ Potw. wydania"},
  ];
  const {type,order} = doc;
  const DS = (settings?.docSettings||{})[type] || {};
  const rName  = DS.companyName  || settings?.restaurantName || "Restauracja";
  const rAddr  = DS.companyAddr  || settings?.address || "";
  const rNip   = DS.companyNip   || "";
  const rPhone = DS.companyPhone || settings?.phone || "";
  const rEmail = DS.companyEmail || settings?.email || "";
  const rBank  = DS.bankAccount  || "";
  const footer = DS.footer || "";
  const accentCol = DS.accentColor || ac || "#6d28d9";
  const showSign  = DS.showSignatures !== false;
  const showStamp = DS.showStamp !== false;
  const TITLES = {invoice:"FAKTURA VAT",wz:"DOKUMENT WZ",delivery:"DOWÓD DOSTAWY",release:"POTWIERDZENIE WYDANIA"};
  const now = new Date().toLocaleString("pl-PL");
  const lines = (order.cart||[]).map((c,i)=>`
    <tr style="background:${i%2===0?"#fafafa":"#fff"}">
      <td style="padding:7px 10px">${c.emoji||""} ${c.name}</td>
      <td style="padding:7px 10px;text-align:center">${c.qty||1} ${c.unit||"szt"}</td>
      <td style="padding:7px 10px;text-align:right;font-weight:700">${(Number(c.price||0)*(c.qty||1)).toFixed(2)} zł</td>
    </tr>`).join("");

  const html = `<!DOCTYPE html><html lang="pl"><head><meta charset="UTF-8"/>
  <title>${TITLES[type]} — ${rName}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;font-size:13px;color:#111;background:#fff;padding:28px 32px}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:16px;border-bottom:3px solid ${accentCol}}
    .company h1{font-size:20px;font-weight:900;color:${accentCol};margin-bottom:4px}
    .company p{font-size:11px;color:#555;line-height:1.7}
    .doc-title{text-align:right}.doc-title h2{font-size:22px;font-weight:900;color:${accentCol}}
    .doc-title p{font-size:11px;color:#555;margin-top:4px}
    .parties{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px}
    .box{background:#f9fafb;border-radius:8px;padding:12px 14px;border:1px solid #e5e7eb}
    .box h3{font-size:11px;font-weight:700;color:${accentCol};text-transform:uppercase;letter-spacing:1px;margin-bottom:6px}
    .box p{font-size:12px;line-height:1.8;color:#222}
    table{width:100%;border-collapse:collapse;margin-bottom:12px}
    thead tr{background:${accentCol};color:#fff}
    thead th{padding:8px 10px;text-align:left;font-size:12px;font-weight:700}
    thead th:last-child{text-align:right}
    .total-row{background:${accentCol}22;font-weight:900;font-size:14px}
    .total-row td{padding:10px;border-top:2px solid ${accentCol}}
    .footer{margin-top:16px;font-size:10px;color:#888;border-top:1px solid #e5e7eb;padding-top:10px;text-align:center}
    .signs{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:24px}
    .sign-box{border-top:1.5px solid #333;padding-top:6px;text-align:center;font-size:11px;color:#555}
    .stamp-box{border:2px dashed #aaa;border-radius:50%;width:90px;height:90px;margin:0 auto;display:flex;align-items:center;justify-content:center;font-size:10px;color:#aaa}
    @media print{body{padding:12px}button{display:none!important}}
  </style></head><body>
  <div class="header">
    <div class="company">
      ${DS.logo?`<img src="${DS.logo}" style="height:56px;object-fit:contain;margin-bottom:4px"/>`:`<div style="font-size:28px;margin-bottom:4px">🏪</div>`}
      <h1>${rName}</h1>
      <p>${rAddr}${rNip?`<br/>NIP: ${rNip}`:""}${rPhone?`<br/>Tel: ${rPhone}`:""}${rEmail?`<br/>E-mail: ${rEmail}`:""}${rBank?`<br/>Konto: ${rBank}`:""}</p>
    </div>
    <div class="doc-title">
      <h2>${TITLES[type]}</h2>
      <p>Nr: ${type.toUpperCase()}-${String(order.id||"").slice(-6)||"000001"}<br/>Data: ${now}${order.date?`<br/>Zamówienie: ${order.date} ${order.time||""}`:""}</p>
    </div>
  </div>
  <div class="parties">
    <div class="box"><h3>Sprzedawca</h3><p>${rName}${rAddr?`<br/>${rAddr}`:""}${rNip?`<br/>NIP: ${rNip}`:""}</p></div>
    <div class="box"><h3>Nabywca</h3><p>${order.firstName||""} ${order.lastName||""}<br/>Tel: ${order.phone||""}${order.email?`<br/>${order.email}`:""}${order.address?`<br/>${order.address}`:""}</p></div>
  </div>
  ${type==="delivery"&&order.address?`<div class="box" style="margin-bottom:14px"><h3>Adres dostawy</h3><p>${order.address}${order.deliveryZone?`<br/>Strefa: ${order.deliveryZone}`:""}</p></div>`:""}
  <table>
    <thead><tr><th>Pozycja</th><th style="text-align:center">Ilość</th><th style="text-align:right">Wartość</th></tr></thead>
    <tbody>${lines}</tbody>
    ${order.deliveryCost>0?`<tr><td colspan="2" style="padding:6px 10px;color:#555">🚗 Dostawa (${order.deliveryZone||""})</td><td style="padding:6px 10px;text-align:right;font-weight:700">${Number(order.deliveryCost).toFixed(2)} zł</td></tr>`:""}
    <tr class="total-row"><td colspan="2" style="padding:10px">RAZEM DO ZAPŁATY</td><td style="padding:10px;text-align:right">${Number(order.total||0).toFixed(2)} zł</td></tr>
  </table>
  <div class="box" style="margin-bottom:12px"><p>Forma płatności: <strong>${PAYMENTS[order.payment]||order.payment||"—"}</strong>${type==="invoice"&&rBank?`<br/>Konto bankowe: ${rBank}`:""}</p></div>
  ${DS.customNote?`<div class="box" style="margin-bottom:12px;border-color:${accentCol}55"><p style="color:#555">${DS.customNote}</p></div>`:""}
  ${showSign?`<div class="signs"><div class="sign-box">Wystawił(a):<br/><br/><br/>___________________________<br/>${rName}</div><div class="sign-box">${type==="release"?"Odebrał(a)":type==="wz"?"Wydający":type==="delivery"?"Dostarczył(a)":"Nabywca"}:<br/><br/><br/>___________________________<br/>${order.firstName||""} ${order.lastName||""}</div></div>`:""}
  ${showStamp&&type==="invoice"?`<div style="display:flex;justify-content:flex-end;margin-top:12px"><div class="stamp-box">Pieczęć firmowa</div></div>`:""}
  ${footer?`<div class="footer">${footer}</div>`:""}
  <div style="text-align:center;margin-top:14px"><button onclick="window.print()" style="padding:10px 28px;background:${accentCol};color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:14px;font-weight:700">🖨 Drukuj / Zapisz PDF</button></div>
  </body></html>`;

  const openDoc = () => { const w=window.open("","_blank"); w.document.write(html); w.document.close(); };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#fff",borderRadius:16,padding:24,maxWidth:480,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontWeight:800,fontSize:16}}>{DOC_TYPES.find(d=>d.id===type)?.label}</div>
          <button onClick={onClose} style={{background:"#f3f4f6",border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:16}}>✕</button>
        </div>
        {/* Miniaturka podglądu */}
        <div style={{border:"1.5px solid #e5e7eb",borderRadius:12,overflow:"hidden",marginBottom:14}}>
          <div style={{background:accentCol,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{color:"#fff",fontWeight:800,fontSize:13}}>{rName}</div>
            <div style={{color:"rgba(255,255,255,0.85)",fontWeight:700,fontSize:11}}>{TITLES[type]}</div>
          </div>
          <div style={{padding:"10px 14px",fontSize:12}}>
            <div style={{marginBottom:6,color:"#555"}}>👤 {order.firstName} {order.lastName} · 📞 {order.phone}</div>
            {(order.cart||[]).slice(0,3).map((c,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"2px 0",borderBottom:"1px solid #f3f4f6",fontSize:11}}>
                <span>{c.emoji||"🛒"} {c.name} × {c.qty||1}</span>
                <span style={{fontWeight:700}}>{(Number(c.price||0)*(c.qty||1)).toFixed(2)} zł</span>
              </div>
            ))}
            {(order.cart||[]).length>3&&<div style={{fontSize:10,color:"#9ca3af",padding:"2px 0"}}>...i {(order.cart||[]).length-3} więcej</div>}
            <div style={{marginTop:6,fontWeight:900,textAlign:"right",color:accentCol,fontSize:14}}>
              RAZEM: {Number(order.total||0).toFixed(2)} zł
            </div>
          </div>
        </div>
        <button onClick={openDoc}
          style={{width:"100%",padding:"13px",background:accentCol,color:"#fff",border:"none",borderRadius:10,fontWeight:800,cursor:"pointer",fontSize:15}}>
          🖨 Otwórz dokument / Drukuj PDF
        </button>
        <div style={{marginTop:8,fontSize:11,color:"#9ca3af",textAlign:"center"}}>
          Wygląd dokumentu możesz edytować w Super Admin → Szablony dokumentów
        </div>
      </div>
    </div>
  );
}

/* ── Formularz produktu — poza komponentem żeby nie resetował przy re-renderze ── */
const ONLINE_ALLERGENS = ["gluten","laktoza","orzechy","jaja","soja","sezam","ryby","mięczaki","seler","gorczyca","łubin","dwutlenek siarki"];
const ONLINE_UNITS     = ["szt","kg","g","dag","l","ml","opak","porcja"];

function OnlineItemForm({item, setItem, onSave, onCancel, title, onlineCats, ac, imgbbKey}) {
  const [uploading, setUploading] = useState(false);

  const uploadImg = async (file) => {
    if (!imgbbKey) { alert("Podaj klucz API Imgbb w Ustawieniach → Zdjęcia"); return; }
    const fd = new FormData();
    fd.append("image", file);
    setUploading(true);
    try {
      const r = await fetch(`https://api.imgbb.com/1/upload?key=${imgbbKey}`,{method:"POST",body:fd});
      const d = await r.json();
      if (d.data?.url) setItem(p=>({...p,image:d.data.url}));
      else alert("Błąd przesyłania zdjęcia");
    } catch { alert("Błąd połączenia z Imgbb"); }
    setUploading(false);
  };

  return (
    <div style={{background:"#fff",borderRadius:14,border:"2px solid "+ac,padding:18,marginBottom:16,boxShadow:"0 4px 16px rgba(0,0,0,0.08)"}}>
      <div style={{fontWeight:800,fontSize:15,marginBottom:14,color:"#111"}}>{title}</div>
      {/* Zdjęcie */}
      <div style={{marginBottom:12}}>
        <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:6}}>Zdjęcie produktu</label>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {item.image&&<img src={item.image} alt="" style={{width:64,height:64,objectFit:"cover",borderRadius:10,border:"1px solid #e5e7eb"}}/>}
          <div style={{flex:1}}>
            <input type="file" accept="image/*" style={{display:"none"}} id="online-item-img"
              onChange={e=>{const f=e.target.files[0];if(f)uploadImg(f);}}/>
            <label htmlFor="online-item-img" style={{display:"inline-block",padding:"8px 14px",background:"#f3f4f6",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700,color:"#374151",border:"1.5px dashed #d1d5db"}}>
              {uploading?"⏳ Przesyłanie...":"📷 Wybierz zdjęcie"}
            </label>
            {item.image&&<button type="button" onClick={()=>setItem(p=>({...p,image:""}))} style={{marginLeft:8,background:"#fee2e2",border:"none",borderRadius:7,padding:"6px 10px",color:"#dc2626",cursor:"pointer",fontSize:11,fontWeight:700}}>Usuń</button>}
          </div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <div style={{gridColumn:"span 2"}}>
          <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Nazwa produktu *</label>
          <input style={{width:"100%",padding:"10px 12px",borderRadius:9,border:"1.5px solid #e5e7eb",fontSize:14,boxSizing:"border-box"}}
            value={item.name} placeholder="np. Chleb żytni" onChange={e=>setItem(p=>({...p,name:e.target.value}))}/>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Cena (zł) *</label>
          <input style={{width:"100%",padding:"10px 12px",borderRadius:9,border:"1.5px solid #e5e7eb",fontSize:14,boxSizing:"border-box"}}
            type="number" min="0" step="0.01" placeholder="0.00" value={item.price} onChange={e=>setItem(p=>({...p,price:e.target.value}))}/>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Jednostka</label>
          <select style={{width:"100%",padding:"10px 12px",borderRadius:9,border:"1.5px solid #e5e7eb",fontSize:14}}
            value={item.unit} onChange={e=>setItem(p=>({...p,unit:e.target.value}))}>
            {ONLINE_UNITS.map(u=><option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Waga / ilość</label>
          <input style={{width:"100%",padding:"10px 12px",borderRadius:9,border:"1.5px solid #e5e7eb",fontSize:14,boxSizing:"border-box"}}
            placeholder="np. 500g, 1kg, 6 szt" value={item.weight||""} onChange={e=>setItem(p=>({...p,weight:e.target.value}))}/>
        </div>
        <div>
          <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Emoji</label>
          <input style={{width:"100%",padding:"10px 12px",borderRadius:9,border:"1.5px solid #e5e7eb",fontSize:20,textAlign:"center",boxSizing:"border-box"}}
            value={item.emoji||""} placeholder="🛒" onChange={e=>setItem(p=>({...p,emoji:e.target.value}))}/>
        </div>
        <div style={{gridColumn:"span 2"}}>
          <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Kategoria</label>
          <select style={{width:"100%",padding:"10px 12px",borderRadius:9,border:"1.5px solid #e5e7eb",fontSize:14}}
            value={item.catId||""} onChange={e=>setItem(p=>({...p,catId:e.target.value}))}>
            <option value="">— brak —</option>
            {(onlineCats||[]).map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div style={{gridColumn:"span 2"}}>
          <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Opis produktu</label>
          <textarea style={{width:"100%",padding:"10px 12px",borderRadius:9,border:"1.5px solid #e5e7eb",fontSize:13,boxSizing:"border-box",minHeight:60,resize:"vertical"}}
            placeholder="Krótki opis, skład, smak..." value={item.desc||""} onChange={e=>setItem(p=>({...p,desc:e.target.value}))}/>
        </div>
        <div style={{gridColumn:"span 2"}}>
          <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:6}}>⚠ Alergeny</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {ONLINE_ALLERGENS.map(a=>{
              const active=(item.allergens||[]).includes(a);
              return (
                <button type="button" key={a} onClick={()=>setItem(p=>({...p,allergens:active?(p.allergens||[]).filter(x=>x!==a):[...(p.allergens||[]),a]}))}
                  style={{padding:"4px 10px",borderRadius:7,border:"1.5px solid "+(active?"#f59e0b":"#e5e7eb"),background:active?"#fff7ed":"#f9fafb",color:active?"#92400e":"#6b7280",fontSize:11,fontWeight:700,cursor:"pointer"}}>
                  {a}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{gridColumn:"span 2",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:10,background:"#f9fafb",border:"1px solid #e5e7eb"}}>
          <div>
            <div style={{fontWeight:700,fontSize:13}}>Dostępność</div>
            <div style={{fontSize:11,color:"#6b7280",marginTop:1}}>{item.available!==false?"Produkt widoczny i dostępny do zamówienia":"Produkt niedostępny — wyświetlany jako wyczerpany"}</div>
          </div>
          <button type="button" onClick={()=>setItem(p=>({...p,available:p.available===false?true:false}))}
            style={{padding:"7px 16px",borderRadius:9,border:"none",background:item.available!==false?ac:"#e5e7eb",color:item.available!==false?"#fff":"#6b7280",fontWeight:700,cursor:"pointer",fontSize:13}}>
            {item.available!==false?"✓ Dostępny":"✗ Niedostępny"}
          </button>
        </div>
        <div style={{gridColumn:"span 2",borderRadius:12,overflow:"hidden",border:"1px solid #e9d5ff"}}>
          <div style={{background:"#7c3aed",padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:20}}>🎨</span>
              <div>
                <div style={{color:"#fff",fontWeight:800,fontSize:13}}>Własna grafika lub zdjęcie jako wzór</div>
                <div style={{color:"rgba(255,255,255,0.75)",fontSize:10,marginTop:1}}>Klient może przesłać własny plik przy zamówieniu</div>
              </div>
            </div>
            <button type="button" onClick={()=>setItem(p=>({...p,allowCustomImage:!p.allowCustomImage}))}
              style={{padding:"7px 16px",borderRadius:9,border:"none",background:item.allowCustomImage?"#fff":"rgba(255,255,255,0.2)",color:item.allowCustomImage?"#7c3aed":"#fff",fontWeight:700,cursor:"pointer",fontSize:13,flexShrink:0}}>
              {item.allowCustomImage?"✓ Włączone":"Wyłączone"}
            </button>
          </div>
          {item.allowCustomImage&&(
            <div style={{background:"#fdf4ff",padding:"12px 14px",borderTop:"1px solid #e9d5ff"}}>
              <p style={{fontSize:11,color:"#6d28d9",lineHeight:1.75,margin:"0 0 6px"}}>
                <strong>Tekst widoczny dla klienta w formularzu zamówienia:</strong>
              </p>
              <p style={{fontSize:11,color:"#555",lineHeight:1.75,margin:0,background:"#fff",borderRadius:8,padding:"8px 12px",border:"1px solid #e9d5ff"}}>
                Możesz w prosty sposób spersonalizować wygląd swojego zamówienia. Prześlij nam dowolny plik
                (np. zdjęcie solenizanta, logo firmy lub postać z bajki), a my wydrukujemy go na jadalnym opłatku.
                Możesz też pokazać nam zdjęcie tortu znalezione w sieci, które najbardziej Ci się podoba,
                a nasi cukiernicy odtworzą wskazany styl, układ owoców czy sposób tynkowania.
              </p>
            </div>
          )}
        </div>
      </div>
      <div style={{display:"flex",gap:8,marginTop:14}}>
        <button type="button" onClick={()=>{if(!item.name||!item.price){alert("Podaj nazwę i cenę");return;}onSave();}}
          style={{flex:1,padding:"11px",background:ac,color:"#fff",border:"none",borderRadius:10,fontWeight:800,cursor:"pointer",fontSize:14}}>💾 Zapisz</button>
        <button type="button" onClick={onCancel}
          style={{padding:"11px 18px",background:"#f3f4f6",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer",fontSize:14,color:"#6b7280"}}>Anuluj</button>
      </div>
    </div>
  );
}

function OnlineOrdersAdminPanel({ onlineOrders, setOnlineOrders, onlineMenu, setOnlineMenu, onlineCats, setOnlineCats, settings, setSettings, ac }) {
  const bp      = useBreakpoint();
  const isPhone = bp.xs || (bp.isMobile && !bp.isTablet);
  // eslint-disable-next-line no-unused-vars
  const ALLERGENS  = ["gluten","laktoza","orzechy","jaja","soja","sezam","ryby","mięczaki","seler","gorczyca","łubin","dwutlenek siarki"];
  // eslint-disable-next-line no-unused-vars
  const UNITS      = ["szt","kg","g","dag","l","ml","opak","porcja"];
  const PAYMENTS   = {cash:"💵 Gotówka",blik:"📱 BLIK",card:"💳 Karta",paypal:"🅿 PayPal"};
  const STATUS_CFG = {
    new:       {label:"🆕 Nowe",       color:"#f59e0b", bg:"#fffbeb"},
    accepted:  {label:"✅ Przyjęte",   color:"#2563eb", bg:"#eff6ff"},
    ready:     {label:"📦 Gotowe",     color:"#16a34a", bg:"#f0fdf4"},
    issued:    {label:"🎉 Wydane",     color:"#6b7280", bg:"#f9fafb"},
    cancelled: {label:"❌ Anulowane",  color:"#dc2626", bg:"#fff1f2"},
  };
  const DOC_TYPES  = [
    {id:"invoice", label:"🧾 Faktura"},
    {id:"wz",      label:"📋 Dok. WZ"},
    {id:"delivery",label:"🚚 Dowód dostawy"},
    {id:"release", label:"✅ Potw. wydania"},
  ];

  const [subTab,      setSubTab]     = useState("orders");
  const [filterSt,    setFilterSt]   = useState("all");
  const [selOrder,    setSelOrder]   = useState(null);
  const [showDoc,     setShowDoc]    = useState(null);
  const [editItem,    setEditItem]   = useState(null);
  const [showNewItem, setShowNewItem]= useState(false);
  const [newItem,     setNewItem]    = useState({name:"",price:"",unit:"szt",catId:"",emoji:"🛒",weight:"",desc:"",available:true,allergens:[],image:""});
  const [showNewCat,  setShowNewCat] = useState(false);
  const [newCat,      setNewCat]     = useState({name:"",emoji:""});
  // eslint-disable-next-line no-unused-vars
  const [imgbbKey,    setImgbbKey]   = useState(()=>{ try{return JSON.parse(localStorage.getItem("smakidomu_settings")||"{}").imgbbApiKey||"";}catch{return "";} });

  const filteredOrders = (onlineOrders||[])
    .filter(o=>filterSt==="all"||(o.orderStatus||"new")===filterSt)
    .slice().reverse();

  const setOrderStatus = (id,status) =>
    setOnlineOrders(p=>p.map(o=>o.id===id?{...o,orderStatus:status,issued:status==="issued"}:o));

  /* ── Upload zdjęcia ── */

  return (
    <div style={{padding:"0 0 40px"}}>
      <OnlineDocModal doc={showDoc} onClose={()=>setShowDoc(null)} settings={settings} ac={ac}/>

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18}}>
        <div>
          <div style={{fontWeight:900,fontSize:20,color:"#111"}}>🌐 Zamówienia online</div>
          <div style={{fontSize:12,color:"#6b7280",marginTop:3}}>
            {(onlineOrders||[]).filter(o=>(o.orderStatus||"new")==="new").length} nowych ·{" "}
            {(onlineMenu||[]).length} produktów ·{" "}
            {(onlineCats||[]).length} kategorii
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{display:"flex",gap:8,marginBottom:20,background:"#f3f4f6",borderRadius:12,padding:4}}>
        {[["orders","📋 Zamówienia"],["menu","🍽 Menu online"],["cats","📂 Kategorie"],["payments","💳 Płatności"]].map(([k,l])=>(
          <button key={k} onClick={()=>setSubTab(k)}
            style={{flex:1,padding:"9px 8px",borderRadius:9,border:"none",fontWeight:700,fontSize:isPhone?11:13,cursor:"pointer",
              background:subTab===k?"#fff":"transparent",color:subTab===k?"#111":"#6b7280",
              boxShadow:subTab===k?"0 2px 8px rgba(0,0,0,0.1)":"none",transition:"all .15s"}}>
            {l}
          </button>
        ))}
      </div>

      {/* ════ ZAMÓWIENIA ════ */}
      {subTab==="orders"&&(<div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
          {[["all","Wszystkie"],["new","🆕 Nowe"],["accepted","✅ Przyjęte"],["ready","📦 Gotowe"],["issued","🎉 Wydane"],["cancelled","❌ Anulowane"]].map(([k,l])=>(
            <button key={k} onClick={()=>setFilterSt(k)}
              style={{padding:"5px 12px",borderRadius:20,border:"1.5px solid "+(filterSt===k?ac:"#e5e7eb"),
                background:filterSt===k?ac:"#fff",color:filterSt===k?"#fff":"#374151",
                fontWeight:700,fontSize:11,cursor:"pointer"}}>
              {l}
            </button>
          ))}
        </div>

        {filteredOrders.length===0&&(
          <div style={{textAlign:"center",padding:"48px 20px",color:"#9ca3af"}}>
            <div style={{fontSize:40,marginBottom:12}}>📋</div>
            <div style={{fontWeight:700}}>Brak zamówień</div>
          </div>
        )}

        {filteredOrders.map(order=>{
          const st = STATUS_CFG[order.orderStatus||"new"]||STATUS_CFG.new;
          const expanded = selOrder===order.id;
          return (
            <div key={order.id} style={{background:"#fff",borderRadius:16,border:"2px solid "+(expanded?ac:"#f3f4f6"),padding:16,marginBottom:10,boxShadow:"0 2px 10px rgba(0,0,0,0.06)",cursor:"pointer"}}
              onClick={()=>setSelOrder(expanded?null:order.id)}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:4}}>
                    <span style={{fontWeight:800,fontSize:14,color:"#111"}}>{order.firstName} {order.lastName}</span>
                    <span style={{background:st.bg,color:st.color,borderRadius:7,padding:"2px 9px",fontSize:11,fontWeight:700}}>{st.label}</span>
                    {order.payment&&<span style={{background:"#f3f4f6",borderRadius:7,padding:"2px 8px",fontSize:11,color:"#6b7280"}}>{PAYMENTS[order.payment]||order.payment}</span>}
                  </div>
                  <div style={{fontSize:12,color:"#6b7280"}}>
                    📞 {order.phone}{order.email&&<span> · ✉ {order.email}</span>}
                  </div>
                  {order.address&&<div style={{fontSize:12,color:"#6b7280",marginTop:1}}>🏠 {order.address}</div>}
                  {order.readyAt&&<div style={{fontSize:12,color:"#f59e0b",fontWeight:700,marginTop:2}}>⏰ Na: {new Date(order.readyAt).toLocaleString("pl-PL",{dateStyle:"short",timeStyle:"short"})}</div>}
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontWeight:900,fontSize:17,color:ac}}>{Number(order.total||0).toFixed(2)} zł</div>
                  <div style={{fontSize:11,color:"#9ca3af"}}>{order.date} {order.time}</div>
                </div>
              </div>

              {expanded&&(
                <div onClick={e=>e.stopPropagation()}>
                  {/* Produkty */}
                  <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid #f3f4f6"}}>
                    {(order.cart||[]).map((c,i)=>(
                      <div key={i} style={{padding:"6px 0",borderBottom:"1px solid #f9fafb"}}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"#374151"}}>
                          <span>{c.emoji||"🛒"} {c.name} × {c.qty||1} {c.unit||"szt"}</span>
                          <span style={{fontWeight:700}}>{(Number(c.price||0)*(c.qty||1)).toFixed(2)} zł</span>
                        </div>
                        {c.customImage&&(
                          <div style={{marginTop:6,display:"flex",alignItems:"center",gap:10,background:"#f5f3ff",borderRadius:9,padding:"8px 10px",border:"1.5px solid #c4b5fd"}}>
                            <img src={c.customImage} alt="zdjęcie klienta"
                              style={{width:56,height:56,objectFit:"cover",borderRadius:8,border:"1.5px solid #a78bfa",flexShrink:0,cursor:"pointer"}}
                              onClick={()=>window.open(c.customImage,"_blank")}
                            />
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontWeight:700,fontSize:11,color:"#6d28d9",marginBottom:3}}>📸 Zdjęcie od klienta</div>
                              <div style={{fontSize:10,color:"#7c3aed",marginBottom:6}}>Grafika / wzór dla: {c.name}</div>
                              <a href={c.customImage} download={`grafika_${c.name.replace(/\s+/g,"_")}_${order.id.slice(-6)}.jpg`}
                                style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 11px",background:"#6d28d9",color:"#fff",borderRadius:7,fontSize:11,fontWeight:700,textDecoration:"none"}}>
                                ⬇ Pobierz zdjęcie
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {order.deliveryCost>0&&<div style={{fontSize:13,color:"#0369a1",padding:"4px 0"}}>🚗 Dostawa: {Number(order.deliveryCost).toFixed(2)} zł</div>}
                    {order.note&&<div style={{background:"#fff7ed",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#92400e",marginTop:8}}>💬 {order.note}</div>}
                  </div>
                  {/* Status */}
                  <div style={{marginTop:12,display:"flex",gap:6,flexWrap:"wrap"}}>
                    {Object.entries(STATUS_CFG).map(([k,v])=>(
                      <button key={k} onClick={()=>setOrderStatus(order.id,k)}
                        style={{padding:"6px 12px",borderRadius:8,border:"1.5px solid "+(order.orderStatus===k?v.color:"#e5e7eb"),
                          background:order.orderStatus===k?v.color:"#fff",color:order.orderStatus===k?"#fff":"#374151",fontWeight:700,fontSize:11,cursor:"pointer"}}>
                        {v.label}
                      </button>
                    ))}
                  </div>
                  {/* Dokumenty */}
                  <div style={{marginTop:8,display:"flex",gap:6,flexWrap:"wrap"}}>
                    {DOC_TYPES.map(d=>(
                      <button key={d.id} onClick={()=>setShowDoc({type:d.id,order})}
                        style={{padding:"6px 12px",borderRadius:8,border:"1px solid #e5e7eb",background:"#f9fafb",color:"#374151",fontWeight:600,fontSize:11,cursor:"pointer"}}>
                        {d.label}
                      </button>
                    ))}
                    <button onClick={()=>{if(window.confirm("Usunąć zamówienie?"))setOnlineOrders(p=>p.filter(o=>o.id!==order.id));}}
                      style={{padding:"6px 12px",borderRadius:8,border:"1px solid #fecaca",background:"#fff",color:"#dc2626",fontWeight:700,fontSize:11,cursor:"pointer",marginLeft:"auto"}}>
                      🗑 Usuń
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>)}

      {/* ════ MENU ONLINE ════ */}
      {subTab==="menu"&&(<div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{fontSize:13,color:"#6b7280"}}>{(onlineMenu||[]).length} produktów w menu online</div>
          <button onClick={()=>{setShowNewItem(p=>!p);setEditItem(null);}}
            style={{padding:"9px 16px",background:showNewItem?"#f3f4f6":ac,color:showNewItem?"#6b7280":"#fff",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer",fontSize:13}}>
            {showNewItem?"✕ Anuluj":"+ Dodaj produkt"}
          </button>
        </div>

        {showNewItem&&!editItem&&(
          <OnlineItemForm item={newItem} setItem={setNewItem} title="+ Nowy produkt"
            onlineCats={onlineCats} ac={ac} imgbbKey={imgbbKey}
            onSave={()=>{
              setOnlineMenu(p=>[...(p||[]),{...newItem,id:genId(),price:Number(newItem.price)}]);
              setNewItem({name:"",price:"",unit:"szt",catId:"",emoji:"🛒",weight:"",desc:"",available:true,allergens:[],image:""});
              setShowNewItem(false);
            }}
            onCancel={()=>setShowNewItem(false)}/>
        )}

        {editItem&&(
          <OnlineItemForm item={editItem} setItem={setEditItem} title="✏ Edytuj produkt"
            onlineCats={onlineCats} ac={ac} imgbbKey={imgbbKey}
            onSave={()=>{
              setOnlineMenu(p=>p.map(m=>m.id===editItem.id?{...editItem,price:Number(editItem.price)}:m));
              setEditItem(null);
            }}
            onCancel={()=>setEditItem(null)}/>
        )}

        {(onlineMenu||[]).length===0&&!showNewItem&&(
          <div style={{textAlign:"center",padding:"48px 20px",color:"#9ca3af"}}>
            <div style={{fontSize:40,marginBottom:12}}>🍽</div>
            <div style={{fontWeight:700}}>Brak produktów</div>
            <div style={{fontSize:13,marginTop:4}}>Kliknij „Dodaj produkt" aby zacząć</div>
          </div>
        )}

        {(onlineMenu||[]).map(item=>(
          <div key={item.id} style={{display:"flex",alignItems:"center",gap:12,background:"#fff",borderRadius:14,border:"1px solid #f3f4f6",padding:"12px 14px",marginBottom:8,boxShadow:"0 1px 6px rgba(0,0,0,0.05)"}}>
            {item.image
              ?<img src={item.image} alt={item.name} style={{width:52,height:52,objectFit:"cover",borderRadius:10,flexShrink:0}}/>
              :<div style={{width:52,height:52,background:"#f3f4f6",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,flexShrink:0}}>{item.emoji||"🛒"}</div>
            }
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontWeight:700,fontSize:14,color:"#111"}}>{item.name}</div>
              <div style={{fontSize:12,color:"#6b7280",marginTop:1}}>
                {Number(item.price||0).toFixed(2)} zł / {item.unit||"szt"}
                {item.weight&&<span style={{marginLeft:6,color:"#9ca3af"}}>· {item.weight}</span>}
                {item.catId&&<span style={{marginLeft:8,background:"#e0e7ff",color:"#3730a3",borderRadius:5,padding:"1px 6px",fontSize:10,fontWeight:700}}>
                  {(onlineCats||[]).find(c=>c.id===item.catId)?.name||item.catId}
                </span>}
              </div>
              {(item.allergens||[]).length>0&&(
                <div style={{fontSize:10,color:"#92400e",marginTop:2}}>⚠ {item.allergens.join(", ")}</div>
              )}
            </div>
            <div style={{display:"flex",gap:6,flexShrink:0}}>
              <button onClick={()=>{setEditItem({...item,price:String(item.price||"")});setShowNewItem(false);}}
                style={{padding:"6px 10px",borderRadius:8,border:"1px solid #e5e7eb",background:"#f9fafb",cursor:"pointer",fontSize:12}}>✏</button>
              <button onClick={()=>setOnlineMenu(p=>p.map(m=>m.id===item.id?{...m,available:m.available===false?true:false}:m))}
                style={{padding:"6px 10px",borderRadius:8,border:"1px solid "+(item.available!==false?"#bbf7d0":"#fecaca"),
                  background:item.available!==false?"#f0fdf4":"#fff1f2",color:item.available!==false?"#16a34a":"#dc2626",cursor:"pointer",fontSize:12,fontWeight:700}}>
                {item.available!==false?"✓":"✗"}
              </button>
              <button onClick={()=>{if(window.confirm("Usunąć "+item.name+"?"))setOnlineMenu(p=>p.filter(m=>m.id!==item.id));}}
                style={{padding:"6px 10px",borderRadius:8,border:"1px solid #fecaca",background:"#fff",color:"#dc2626",cursor:"pointer",fontSize:12}}>🗑</button>
            </div>
          </div>
        ))}
      </div>)}

      {/* ════ KATEGORIE ════ */}
      {subTab==="cats"&&(<div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{fontSize:13,color:"#6b7280"}}>{(onlineCats||[]).length} kategorii</div>
          <button onClick={()=>setShowNewCat(p=>!p)}
            style={{padding:"9px 16px",background:showNewCat?"#f3f4f6":ac,color:showNewCat?"#6b7280":"#fff",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer",fontSize:13}}>
            {showNewCat?"✕ Anuluj":"+ Nowa kategoria"}
          </button>
        </div>

        {showNewCat&&(
          <div style={{background:"#fff",borderRadius:14,border:"2px solid "+ac,padding:16,marginBottom:14}}>
            <div style={{display:"grid",gridTemplateColumns:"60px 1fr",gap:10,marginBottom:10}}>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Emoji</label>
                <input style={{width:"100%",padding:"10px",borderRadius:9,border:"1.5px solid #e5e7eb",fontSize:20,textAlign:"center",boxSizing:"border-box"}}
                  value={newCat.emoji} placeholder="🥖" onChange={e=>setNewCat(p=>({...p,emoji:e.target.value}))}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:700,color:"#6b7280",display:"block",marginBottom:4}}>Nazwa kategorii *</label>
                <input style={{width:"100%",padding:"10px 12px",borderRadius:9,border:"1.5px solid #e5e7eb",fontSize:14,boxSizing:"border-box"}}
                  value={newCat.name} placeholder="np. Piekarnicze" onChange={e=>setNewCat(p=>({...p,name:e.target.value}))}/>
              </div>
            </div>
            <button onClick={()=>{
              if(!newCat.name.trim()){alert("Podaj nazwę");return;}
              setOnlineCats(p=>[...(p||[]),{id:genId(),name:(newCat.emoji+" "+newCat.name).trim(),active:true}]);
              setNewCat({name:"",emoji:""});setShowNewCat(false);
            }} style={{width:"100%",padding:"11px",background:ac,color:"#fff",border:"none",borderRadius:10,fontWeight:800,cursor:"pointer",fontSize:14}}>
              + Dodaj kategorię
            </button>
          </div>
        )}

        {(onlineCats||[]).length===0&&!showNewCat&&(
          <div style={{textAlign:"center",padding:"48px 20px",color:"#9ca3af"}}>
            <div style={{fontSize:40,marginBottom:12}}>📂</div>
            <div style={{fontWeight:700}}>Brak kategorii</div>
            <div style={{fontSize:13,marginTop:4}}>Dodaj kategorie: Piekarnicze, Cukiernicze, Własne...</div>
          </div>
        )}

        {(onlineCats||[]).map((cat,idx)=>(
          <div key={cat.id} style={{display:"flex",alignItems:"center",gap:10,background:"#fff",borderRadius:12,border:"1px solid #f3f4f6",padding:"12px 16px",marginBottom:8,boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
            <div style={{flex:1,fontWeight:700,fontSize:14,color:"#111"}}>{cat.name}</div>
            <div style={{fontSize:12,color:"#9ca3af"}}>{(onlineMenu||[]).filter(m=>m.catId===cat.id).length} prod.</div>
            <button onClick={()=>setOnlineCats(p=>p.map(c=>c.id===cat.id?{...c,active:!c.active}:c))}
              style={{padding:"5px 12px",borderRadius:8,border:"1px solid "+(cat.active!==false?"#bbf7d0":"#fecaca"),
                background:cat.active!==false?"#f0fdf4":"#fff1f2",color:cat.active!==false?"#16a34a":"#dc2626",cursor:"pointer",fontWeight:700,fontSize:12}}>
              {cat.active!==false?"Aktywna":"Ukryta"}
            </button>
            <button onClick={()=>{
              const up=[...(onlineCats||[])];const i=up.findIndex(c=>c.id===cat.id);
              if(idx>0){[up[i],up[i-1]]=[up[i-1],up[i]];setOnlineCats(up);}
            }} style={{padding:"5px 8px",borderRadius:8,border:"1px solid #e5e7eb",background:"#f9fafb",cursor:"pointer",fontSize:12}}>↑</button>
            <button onClick={()=>{
              const up=[...(onlineCats||[])];const i=up.findIndex(c=>c.id===cat.id);
              if(i<up.length-1){[up[i],up[i+1]]=[up[i+1],up[i]];setOnlineCats(up);}
            }} style={{padding:"5px 8px",borderRadius:8,border:"1px solid #e5e7eb",background:"#f9fafb",cursor:"pointer",fontSize:12}}>↓</button>
            <button onClick={()=>{if(window.confirm("Usunąć kategorię?"))setOnlineCats(p=>p.filter(c=>c.id!==cat.id));}}
              style={{padding:"5px 10px",borderRadius:8,border:"1px solid #fecaca",background:"#fff",color:"#dc2626",cursor:"pointer",fontSize:12}}>🗑</button>
          </div>
        ))}
      </div>)}
      {/* ════ PŁATNOŚCI ════ */}
      {subTab==="payments"&&(<div>
        <div style={{background:"#fff",borderRadius:16,padding:20,border:"1px solid #f3f4f6",boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <div style={{fontWeight:800,fontSize:15,color:"#111",marginBottom:6}}>💳 Metody płatności w sklepie online</div>
          <div style={{fontSize:13,color:"#6b7280",marginBottom:18}}>Wybierz które metody płatności będą dostępne dla klientów.</div>
          {[
            {id:"cash",   emoji:"💵", label:"Gotówka",   desc:"Płatność gotówką przy odbiorze lub dostawie"},
            {id:"blik",   emoji:"📱", label:"BLIK",      desc:"Klient podaje kod BLIK przy odbiorze"},
            {id:"card",   emoji:"💳", label:"Karta",     desc:"Płatność kartą przy odbiorze lub dostawie"},
            {id:"paypal", emoji:"🅿", label:"PayPal",    desc:"Płatność online przez PayPal przed odbiorem"},
          ].map(p=>{
            const enabled = (settings?.onlinePayments||{})[p.id] !== false;
            return (
              <div key={p.id} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderRadius:14,marginBottom:10,
                border:"2px solid "+(enabled?ac:"#e5e7eb"),background:enabled?"#fff8f0":"#fafafa",transition:"all .15s"}}>
                <div style={{fontSize:26,width:36,textAlign:"center"}}>{p.emoji}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:800,fontSize:14,color:"#111"}}>{p.label}</div>
                  <div style={{fontSize:12,color:"#6b7280",marginTop:1}}>{p.desc}</div>
                </div>
                <button type="button" onClick={()=>{
                  const cur = settings?.onlinePayments || {cash:true,blik:true,card:true,paypal:true};
                  // Sprawdź czy nie wyłączamy ostatniej aktywnej metody
                  const active = Object.entries({...cur,[p.id]:!enabled}).filter(([,v])=>v!==false).length;
                  if(active===0){alert("Musi być włączona przynajmniej jedna metoda płatności");return;}
                  // Zapisz do settings przez callback — używamy setSettings jeśli dostępne
                  if(typeof setSettings==="function"){
                    setSettings(s=>({...s,onlinePayments:{...(s.onlinePayments||{cash:true,blik:true,card:true,paypal:true}),[p.id]:!enabled}}));
                  }
                }}
                  style={{padding:"8px 18px",borderRadius:10,border:"none",
                    background:enabled?ac:"#e5e7eb",color:enabled?"#fff":"#6b7280",
                    fontWeight:800,cursor:"pointer",fontSize:13,transition:"all .15s",minWidth:90}}>
                  {enabled?"✓ Włączona":"✗ Wyłączona"}
                </button>
              </div>
            );
          })}
          <div style={{marginTop:16,padding:"12px 16px",borderRadius:12,background:"#f0fdf4",border:"1px solid #bbf7d0",fontSize:12,color:"#166534"}}>
            ✅ Zmiany są zapisywane natychmiast i widoczne dla klientów w sklepie online.
          </div>
        </div>
      </div>)}
    </div>
  );
}

function TakeawayPanel({ orders, setOrders, menu, cats, settings, ac, isSeller, isAdmin }) {
  // eslint-disable-next-line no-unused-vars
  const tr = useTr();
  const BLANK = {
    firstName:"", lastName:"", phone:"", note:"",
    pickupTime:"", cart:[], total:0,
    delivery: false, address:"", zoneId:""
  };
  const [form,    setForm]    = useState(BLANK);
  const [showNew, setShowNew] = useState(false);
  const [filter,  setFilter]  = useState("pending"); // pending | done | all
  const [cartSearch, setCartSearch] = useState("");

  const takeawayOrders = orders.filter(o=>o.takeaway);
  const filtered = takeawayOrders.filter(o=>{
    if (filter==="pending")  return !o.issued;
    if (filter==="done")     return o.issued;
    if (filter==="delivery") return o.delivery;
    if (filter==="pickup")   return !o.delivery;
    return true;
  }).slice().reverse();

  const [addonModal, setAddonModal] = useState(null);

  const addToCart = (item) => {
    if (item.addons && item.addons.length > 0) {
      setAddonModal({ item, selectedAddons: {} });
      return;
    }
    setForm(p=>{
      const existing = p.cart.find(c=>(c.cartKey||c.id)===item.id);
      const newCart = existing
        ? p.cart.map(c=>(c.cartKey||c.id)===item.id?{...c,qty:c.qty+1}:c)
        : [...p.cart, {id:item.id,name:item.name,emoji:item.emoji,image:item.image,price:Number(item.price),qty:1,allergens:item.allergens,cartKey:item.id}];
      const total = newCart.reduce((s,c)=>s+(c.effectivePrice??c.price)*c.qty,0);
      return {...p, cart:newCart, total};
    });
  };

  const addToCartWithAddons = (item, selectedAddons) => {
    const addonsArr = Object.entries(selectedAddons)
      .filter(([,qty])=>qty>0)
      .map(([id,qty])=>{ const a=item.addons.find(x=>x.id===id); return a?{...a,qty}:null; })
      .filter(Boolean);
    const addonsPrice = addonsArr.reduce((s,a)=>s+a.price*a.qty,0);
    const cartKey = item.id+(addonsArr.length?"_"+addonsArr.map(a=>a.id+"x"+a.qty).join("_"):"");
    const effectivePrice = Number(item.price)+addonsPrice;
    setForm(p=>{
      const existing = p.cart.find(c=>c.cartKey===cartKey);
      const newCart = existing
        ? p.cart.map(c=>c.cartKey===cartKey?{...c,qty:c.qty+1}:c)
        : [...p.cart, {id:item.id,name:item.name,emoji:item.emoji,image:item.image,price:Number(item.price),qty:1,allergens:item.allergens,cartKey,selectedAddons:addonsArr,addonsPrice,effectivePrice}];
      const total = newCart.reduce((s,c)=>s+(c.effectivePrice??c.price)*c.qty,0);
      return {...p, cart:newCart, total};
    });
    setAddonModal(null);
  };

  const removeFromCart = (id) => {
    setForm(p=>{
      const newCart = p.cart.map(c=>(c.cartKey||c.id)===id?{...c,qty:c.qty-1}:c).filter(c=>c.qty>0);
      const total = newCart.reduce((s,c)=>s+(c.effectivePrice??c.price)*c.qty,0);
      return {...p, cart:newCart, total};
    });
  };

  const zones = settings?.deliveryZones || [];
  const selectedZone = zones.find(z=>String(z.id)===String(form.zoneId)) || null;
  const deliveryCost = form.delivery && selectedZone ? (selectedZone.price || 0) : 0;
  const orderTotal   = form.total + deliveryCost;

  const placeOrder = () => {
    if (!form.firstName.trim()||!form.lastName.trim()||!form.phone.trim()) {
      alert("Wypełnij imię, nazwisko i telefon"); return;
    }
    if (!form.cart.length) { alert("Dodaj przynajmniej jedną pozycję"); return; }
    if (!form.pickupTime) { alert("Podaj godzinę "+(form.delivery?"dowozu":"odbioru")); return; }
    if (form.delivery && !form.address.trim()) { alert("Podaj adres dowozu"); return; }
    if (form.delivery && zones.length>0 && !form.zoneId) { alert("Wybierz strefę dowozu"); return; }
    const now = new Date();
    const o = {
      id: Date.now(), takeaway: true,
      delivery: form.delivery,
      address: form.address.trim(),
      firstName: form.firstName.trim(), lastName: form.lastName.trim(),
      phone: form.phone.trim(), note: form.note.trim(),
      pickupTime: form.pickupTime,
      cart: form.cart, total: orderTotal,
      deliveryZone: selectedZone ? selectedZone.name : null,
      deliveryCost,
      date: now.toISOString().slice(0,10),
      time: now.toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),
      tableNumber: form.delivery?"dowóz":"wynos", table: form.delivery?"dowóz":"wynos",
      issued: false, sellerAccepted: true, cookStatus: "new",
      wait: form.delivery ? 45 : 20,
    };
    setOrders(p=>[...p, o]);
    setForm(BLANK);
    setShowNew(false);
  };

  const menuItems = menu.filter(m=>
    !cartSearch || m.name.toLowerCase().includes(cartSearch.toLowerCase())
  );

  return (
    <div style={{padding:"0 0 40px"}}>

      {/* MODAL DODATKOW */}
      {addonModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:700,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:"20px 20px 0 0",width:"100%",maxWidth:520,maxHeight:"80vh",overflowY:"auto",padding:"20px 20px 32px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <div>
                <div style={{fontWeight:800,fontSize:16}}>{addonModal.item.emoji} {addonModal.item.name}</div>
                <div style={{fontSize:12,color:"#6b7280",marginTop:2}}>Cena bazowa: {Number(addonModal.item.promoPrice??addonModal.item.price).toFixed(2)} zł</div>
              </div>
              <button onClick={()=>setAddonModal(null)} style={{background:"#f3f4f6",border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>
            <div style={{fontWeight:700,fontSize:13,color:"#374151",marginBottom:8}}>"Wybierz dodatki:"</div>
            {addonModal.item.addons.map(addon=>{
              const qty = addonModal.selectedAddons[addon.id]||0;
              return (
                <div key={addon.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+(qty>0?ac:"#e5e7eb"),background:qty>0?"#fff8f0":"#f9fafb",marginBottom:6}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:13}}>{addon.name}</div>
                    <div style={{fontSize:11,color:qty>0?ac:"#6b7280",fontWeight:qty>0?700:400}}>{addon.price===0?"Gratis":"+"+Number(addon.price).toFixed(2)+" zł"}</div>
                  </div>
                  {addon.max===1?(
                    <button onClick={()=>setAddonModal(p=>({...p,selectedAddons:{...p.selectedAddons,[addon.id]:qty?0:1}}))}
                      style={{width:30,height:30,borderRadius:7,border:"2px solid "+(qty?ac:"#d1d5db"),background:qty?ac:"#fff",cursor:"pointer",color:qty?"#fff":"#6b7280",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {qty?"✓":""}
                    </button>
                  ):(
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <button onClick={()=>setAddonModal(p=>({...p,selectedAddons:{...p.selectedAddons,[addon.id]:Math.max(0,qty-1)}}))}
                        style={{width:28,height:28,borderRadius:6,border:"1.5px solid #d1d5db",background:"#fff",cursor:"pointer",fontWeight:700,fontSize:15,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                      <span style={{fontWeight:700,fontSize:14,minWidth:18,textAlign:"center",color:qty>0?ac:"#111"}}>{qty}</span>
                      <button onClick={()=>setAddonModal(p=>({...p,selectedAddons:{...p.selectedAddons,[addon.id]:Math.min(addon.max,qty+1)}}))}
                        style={{width:28,height:28,borderRadius:6,border:"1.5px solid "+ac,background:ac,cursor:"pointer",fontWeight:700,fontSize:15,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{marginTop:14,marginBottom:12,padding:"10px 12px",background:"#f9fafb",borderRadius:10,border:"1px solid #e5e7eb"}}>
              <div style={{display:"flex",justifyContent:"space-between",fontWeight:800,fontSize:14}}>
                <span>Razem:</span>
                <span style={{color:ac}}>{(Number(addonModal.item.price)+Object.entries(addonModal.selectedAddons).reduce((s,[id,q])=>{const a=addonModal.item.addons.find(x=>x.id===id);return s+(a?a.price*q:0);},0)).toFixed(2)} zł</span>
              </div>
            </div>
            <button onClick={()=>addToCartWithAddons(addonModal.item, addonModal.selectedAddons)}
              style={{width:"100%",padding:"12px",background:ac,color:"#fff",border:"none",borderRadius:10,fontWeight:800,fontSize:15,cursor:"pointer"}}>
              Dodaj do zamówienia
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
        <div>
          <div style={{fontWeight:800,fontSize:18,color:T.text}}>🥡 Zamówienia na wynos</div>
          <div style={{fontSize:12,color:T.muted,marginTop:2}}>
            {takeawayOrders.filter(o=>!o.issued&&o.delivery).length} dowóz · {takeawayOrders.filter(o=>!o.issued&&!o.delivery).length} odbiór oczekujących
          </div>
        </div>
        <button onClick={()=>setShowNew(p=>!p)} style={{
          padding:"10px 16px",background:showNew?"#f5f5f5":ac,color:showNew?T.muted:"#fff",
          border:"none",borderRadius:12,fontWeight:800,cursor:"pointer",fontSize:14,
          boxShadow:showNew?"none":"0 2px 8px rgba(0,0,0,0.2)"
        }}>{showNew?"✕ Anuluj":"+ Nowe zamówienie"}</button>
      </div>

      {/* Nowe zamówienie */}
      {showNew&&(
        <div style={{background:T.surface,borderRadius:16,border:"2px solid "+ac,padding:16,marginBottom:20,boxShadow:"0 4px 16px rgba(0,0,0,0.1)"}}>
          <div style={S.formTitle}>📝 Nowe zamówienie na wynos</div>

          {/* Typ zamówienia — dostawa/odbiór */}
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            {[[false,"🏃 Odbiór osobisty"],[true,"🚗 Z dowozem"]].map(([v,l])=>(
              <button key={String(v)} type="button"
                onClick={()=>setForm(p=>({...p,delivery:v,address:v?p.address:""}))}
                style={{flex:1,padding:"11px 8px",borderRadius:12,border:"2px solid "+(form.delivery===v?ac:T.border),
                  background:form.delivery===v?ac:T.surface,color:form.delivery===v?"#fff":T.muted,
                  fontWeight:800,cursor:"pointer",fontSize:14,transition:"all .15s"}}>
                {l}
              </button>
            ))}
          </div>

          {/* Dane klienta */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
            <div>
              <label style={S.label}>Imię *</label>
              <input style={S.inp} placeholder="Jan" value={form.firstName}
                onChange={e=>setForm(p=>({...p,firstName:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Nazwisko *</label>
              <input style={S.inp} placeholder="Kowalski" value={form.lastName}
                onChange={e=>setForm(p=>({...p,lastName:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>Telefon *</label>
              <input style={S.inp} placeholder="+48 600 000 000" value={form.phone} type="tel"
                onChange={e=>setForm(p=>({...p,phone:e.target.value}))}/>
            </div>
            <div>
              <label style={S.label}>{form.delivery?"Czas dowozu *":"Godzina odbioru *"}</label>
              <input style={S.inp} type="time" value={form.pickupTime}
                onChange={e=>setForm(p=>({...p,pickupTime:e.target.value}))}/>
            </div>
            {form.delivery&&(
              <div style={{gridColumn:"span 2"}}>
                <label style={S.label}>Adres dowozu *</label>
                <input style={S.inp} placeholder={"ul. Przykładowa 12/3, 00-000 Miasto"}
                  value={form.address}
                  onChange={e=>setForm(p=>({...p,address:e.target.value}))}/>
              </div>
            )}
            {form.delivery && zones.length>0 && (
              <div style={{gridColumn:"span 2"}}>
                <label style={S.label}>Strefa dowozu *</label>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {zones.map(z=>{
                    const sel = String(form.zoneId)===String(z.id);
                    return (
                      <div key={z.id} onClick={()=>setForm(p=>({...p,zoneId:String(z.id)}))}
                        style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                          padding:"10px 14px",borderRadius:10,cursor:"pointer",transition:"all .15s",
                          border:"2px solid "+(sel?ac:T.border),
                          background:sel?"#fff8e1":T.bg}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:13,color:sel?ac:T.text}}>{z.name}</div>
                          <div style={{fontSize:11,color:T.muted}}>{z.kmFrom}–{z.kmTo} km od restauracji</div>
                        </div>
                        <div style={{fontWeight:900,fontSize:15,color:sel?ac:T.muted}}>
                          {z.price===0 ? "Bezpłatnie" : `${Number(z.price).toFixed(2)} zł`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{gridColumn:"span 2"}}>
              <label style={S.label}>Uwagi</label>
              <input style={S.inp} placeholder="np. bez cebuli, alergia na gluten..." value={form.note}
                onChange={e=>setForm(p=>({...p,note:e.target.value}))}/>
            </div>
          </div>

          {/* Wybór pozycji z menu */}
          <div style={S.formTitle}>🍽 Wybierz pozycje</div>
          <input style={{...S.inp,marginBottom:8}} placeholder="🔍 Szukaj w menu..."
            value={cartSearch} onChange={e=>setCartSearch(e.target.value)}/>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:7,marginBottom:12,maxHeight:220,overflowY:"auto"}}>
            {menuItems.map(item=>{
              const inCart = form.cart.find(c=>c.id===item.id);
              const hasAddons = item.addons && item.addons.length > 0;
              const isUnavailable = item.available === false;
              return (
                <div key={item.id} onClick={()=>!isUnavailable&&addToCart(item)} style={{
                  background: isUnavailable?"#fff5f5":inCart?"#fff8e1":T.bg,
                  border:"1.5px solid "+(isUnavailable?"#fecaca":inCart?ac:T.border),
                  borderRadius:10, padding:"8px 10px", cursor:isUnavailable?"default":"pointer",
                  display:"flex",alignItems:"center",gap:8,transition:"all .15s",
                  opacity:isUnavailable?0.65:1,position:"relative"
                }}>
                  <span style={{fontSize:20}}>{item.emoji}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.name}</div>
                    {isUnavailable
                      ? <div style={{fontSize:10,color:"#dc2626",fontWeight:700}}>Niedostępny</div>
                      : <div style={{fontSize:11,color:T.muted}}>{Number(item.price).toFixed(2)} zł</div>
                    }
                    {hasAddons&&!isUnavailable&&<div style={{fontSize:9,color:ac,fontWeight:700}}>+dodatki</div>}
                  </div>
                  {inCart&&!isUnavailable&&<span style={{background:ac,color:"#fff",borderRadius:6,padding:"1px 6px",fontSize:11,fontWeight:800,flexShrink:0}}>×{inCart.qty}</span>}
                </div>
              );
            })}
          </div>

          {/* Koszyk */}
          {form.cart.length>0&&(
            <div style={{background:T.bg,borderRadius:12,padding:"10px 14px",marginBottom:12,border:"1px solid "+T.border}}>
              <div style={{fontSize:12,fontWeight:800,color:T.muted,marginBottom:8}}>🛒 Zamówione pozycje:</div>
              {form.cart.map(c=>(
                <div key={c.cartKey||c.id} style={{padding:"4px 0",borderBottom:"1px solid "+T.border+"44"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:16}}>{c.emoji}</span>
                    <span style={{flex:1,fontSize:12,fontWeight:700}}>{c.name}</span>
                    <span style={{fontSize:11,color:T.muted}}>{((c.effectivePrice??c.price)*c.qty).toFixed(2)} zł</span>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>
                      <button onClick={e=>{e.stopPropagation();removeFromCart(c.cartKey||c.id);}}
                        style={{width:22,height:22,borderRadius:"50%",background:"#ffebee",color:"#c0392b",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                      <span style={{fontWeight:800,fontSize:13,minWidth:16,textAlign:"center"}}>{c.qty}</span>
                      <button onClick={e=>{e.stopPropagation();addToCart(menu.find(m=>m.id===c.id));}}
                        style={{width:22,height:22,borderRadius:"50%",background:"#e8f5e9",color:"#27ae60",border:"none",cursor:"pointer",fontWeight:900,fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                    </div>
                  </div>
                  {(c.selectedAddons||[]).map((a,ai)=>(
                    <div key={ai} style={{fontSize:10,color:"#C8621A",paddingLeft:28,marginTop:1}}>+ {a.name} ×{a.qty} (+{(a.price*a.qty).toFixed(2)} zł)</div>
                  ))}
                </div>
              ))}
              <div style={{fontWeight:800,fontSize:14,color:T.text,marginTop:8,textAlign:"right"}}>
                Suma produktów: {form.total.toFixed(2)} zł
                {deliveryCost>0&&(
                  <div style={{fontSize:12,color:"#0369a1",fontWeight:700,marginTop:2}}>
                    🚗 Dowóz ({selectedZone?.name}): +{deliveryCost.toFixed(2)} zł
                  </div>
                )}
                {deliveryCost>0&&(
                  <div style={{fontSize:15,color:T.text,marginTop:4,borderTop:"1px solid "+T.border,paddingTop:4}}>
                    Razem: {orderTotal.toFixed(2)} zł
                  </div>
                )}
              </div>
            </div>
          )}

          <button onClick={placeOrder} style={{
            width:"100%",padding:"13px",background:"linear-gradient(135deg,#27ae60,#2ecc71)",
            color:"#fff",border:"none",borderRadius:12,fontWeight:900,cursor:"pointer",fontSize:15,
            boxShadow:"0 3px 12px rgba(39,174,96,.35)"
          }}>
            ✅ Zarejestruj zamówienie na wynos
          </button>
        </div>
      )}

      {/* Filtr */}
      <div style={{display:"flex",gap:7,marginBottom:14,flexWrap:"wrap"}}>
        {[["pending","⏳ Oczekujące"],["done","✅ Odebrane/Dostarczone"],["delivery","🚗 Dowóz"],["pickup","🏃 Odbiór"],["all","Wszystkie"]].map(([v,l])=>(
          <button key={v} style={{...S.pill,...(filter===v?S.pillOn:{})}} onClick={()=>setFilter(v)}>{l}</button>
        ))}
      </div>

      {/* Lista zamówień */}
      {filtered.length===0
        ? <div style={{textAlign:"center",padding:"40px 0",color:T.border}}>
            <div style={{fontSize:48}}>🥡</div>
            <div style={{fontSize:14,fontWeight:700,color:T.muted,marginTop:8}}>Brak zamówień na wynos</div>
          </div>
        : filtered.map(o=>(
        <div key={o.id} style={{
          background:T.surface, borderRadius:16, marginBottom:14,
          border:"2px solid "+(o.issued?"#d1fae5":"#f39c12"),
          boxShadow:o.issued?"none":"0 4px 14px rgba(243,156,18,.15)",
          overflow:"hidden", opacity:o.issued?0.7:1
        }}>
          {/* Baner górny */}
          <div style={{
            background:o.issued?"linear-gradient(135deg,#27ae60,#2ecc71)":o.delivery?"linear-gradient(135deg,#0984e3,#38bdf8)":"linear-gradient(135deg,#e67e22,#f39c12)",
            padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"
          }}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{background:"rgba(255,255,255,0.2)",color:"#fff",borderRadius:8,padding:"2px 8px",fontSize:11,fontWeight:800}}>
                  {o.delivery?"🚗 DOWÓZ":"🏃 ODBIÓR"}
                </span>
              </div>
              <div style={{color:"#fff",fontWeight:900,fontSize:16,marginTop:4}}>{o.firstName} {o.lastName}</div>
              <div style={{color:"rgba(255,255,255,0.85)",fontSize:12}}>📞 {o.phone}</div>
              {o.delivery&&o.address&&<div style={{color:"rgba(255,255,255,0.9)",fontSize:11,marginTop:2}}>📍 {o.address}</div>}
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{background:"rgba(255,255,255,0.25)",borderRadius:10,padding:"4px 12px"}}>
                <div style={{color:"#fff",fontWeight:900,fontSize:15}}>🕐 {o.pickupTime}</div>
                <div style={{color:"rgba(255,255,255,0.8)",fontSize:10}}>{o.delivery?"czas dowozu":"godzina odbioru"}</div>
              </div>
            </div>
          </div>

          {/* Pozycje */}
          <div style={{padding:"10px 16px 4px"}}>
            <div style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Pozycje:</div>
            {o.cart.map((c,ci)=>(
              <div key={c.cartKey||c.id||ci} style={{padding:"4px 0",borderBottom:"1px solid "+T.border+"33"}}>
                <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.text}}>
                  <span style={{fontWeight:500}}>{c.emoji} {c.name} ×{c.qty}</span>
                  <span style={{fontWeight:600}}>{((c.effectivePrice??(c.promoPrice??c.price))*c.qty).toFixed(2)} zł</span>
                </div>
                {(c.selectedAddons||[]).map(a=>(
                  <div key={a.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#C8621A",paddingLeft:16,marginTop:1}}>
                    <span>+ {a.name} ×{a.qty}</span>
                    <span>{(a.price*a.qty).toFixed(2)} zł</span>
                  </div>
                ))}
              </div>
            ))}
            {o.note&&<div style={{marginTop:6,fontSize:11,color:"#C8621A",fontWeight:600}}>📝 {o.note}</div>}
            <div style={{fontWeight:800,fontSize:13,color:T.text,marginTop:8,textAlign:"right"}}>Suma: {Number(o.total).toFixed(2)} zł</div>
          </div>

          {/* Akcje */}
          <div style={{padding:"10px 16px 14px"}}>
            {o.issued ? (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span style={{color:"#27ae60",fontWeight:700,fontSize:13}}>
                  {o.delivery?"🚗 Dostarczone":"✅ Odebrane"}{o.issuedAt?` o ${o.issuedAt}`:""}
                </span>
                {isAdmin&&<button onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issued:false,issuedAt:null}:x))}
                  style={{padding:"5px 12px",background:T.bg,color:T.muted,border:"1.5px solid "+T.border,borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:11}}>
                  ↩ Cofnij
                </button>}
              </div>
            ) : (
              <button onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issued:true,issuedAt:new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"})}:x))}
                style={{width:"100%",padding:"12px",background:"linear-gradient(135deg,#27ae60,#2ecc71)",
                  color:"#fff",border:"none",borderRadius:12,fontWeight:900,cursor:"pointer",fontSize:15,
                  boxShadow:"0 3px 12px rgba(39,174,96,.35)"}}>
                {o.delivery?"🚗 Oznacz jako dostarczone":"✅ Zamówienie odebrane"}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function SellerRoutingPanel({ order, onRoute }) {
  const [routing, setRouting] = useState(() =>
    (order.cart||[]).reduce((acc, c) => ({ ...acc, [c.id]: c.serveAt || "kitchen" }), {})
  );
  const [open, setOpen] = useState(false);

  const toggle = (id) => setRouting(p => ({ ...p, [id]: p[id]==="kitchen" ? "bar" : "kitchen" }));

  const handleSend = () => {
    const cartWithRouting = (order.cart||[]).map(c => ({ ...c, serveAt: routing[c.id]||"kitchen" }));
    onRoute(cartWithRouting);
    setOpen(false);
  };

  const kitchenCount = Object.values(routing).filter(v=>v==="kitchen").length;
  const barCount     = Object.values(routing).filter(v=>v==="bar").length;

  return (
    <div>
      {!open ? (
        <button
          onClick={()=>setOpen(true)}
          style={{width:"100%",padding:"10px",background:"linear-gradient(135deg,#e67e22,#f39c12)",
            color:"#fff",border:"none",borderRadius:10,fontWeight:800,cursor:"pointer",fontSize:13,
            boxShadow:"0 2px 8px rgba(243,156,18,.35)"}}>
          👨‍🍳 Przypisz i wyślij do kuchni
        </button>
      ) : (
        <div style={{background:"#fffbeb",border:"2px solid #f39c12",borderRadius:12,padding:"12px 14px"}}>
          <div style={{fontSize:12,fontWeight:800,color:"#d97706",marginBottom:10}}>
            Kliknij pozycję aby zmienić gdzie trafi:
          </div>
          {(order.cart||[]).map(c=>(
            <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0",borderBottom:"1px solid #fde68a"}}>
              <div style={{fontSize:18}}>{c.emoji}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:"#3B1F0A"}}>{c.name} ×{c.qty}</div>
                {(c.selectedAddons||[]).map((a,ai)=>(
                  <div key={ai} style={{fontSize:10,color:"#C8621A",paddingLeft:4}}>+ {a.name} ×{a.qty}</div>
                ))}
              </div>
              <button
                onClick={()=>toggle(c.id)}
                style={{padding:"5px 11px",borderRadius:9,border:"2px solid",
                  borderColor:routing[c.id]==="kitchen"?"#f39c12":"#0984e3",
                  background:routing[c.id]==="kitchen"?"#fff8e1":"#e8f4fd",
                  color:routing[c.id]==="kitchen"?"#d97706":"#0984e3",
                  fontWeight:800,cursor:"pointer",fontSize:12,transition:"all .15s"}}>
                {routing[c.id]==="kitchen"?"👨‍🍳 Kuchnia":"🥤 Bar"}
              </button>
            </div>
          ))}
          <div style={{display:"flex",gap:8,marginTop:10}}>
            <div style={{flex:1,background:"#fff8e1",borderRadius:8,padding:"6px 10px",fontSize:11,color:"#d97706",fontWeight:700}}>
              👨‍🍳 {kitchenCount} poz. → kuchnia
            </div>
            <div style={{flex:1,background:"#e8f4fd",borderRadius:8,padding:"6px 10px",fontSize:11,color:"#0984e3",fontWeight:700}}>
              🥤 {barCount} poz. → bar
            </div>
          </div>
          <div style={{display:"flex",gap:8,marginTop:10}}>
            <button onClick={()=>setOpen(false)}
              style={{flex:1,padding:"9px",background:"#f5f5f5",color:"#666",border:"none",borderRadius:9,fontWeight:700,cursor:"pointer",fontSize:12}}>
              Anuluj
            </button>
            <button onClick={handleSend}
              style={{flex:2,padding:"9px",background:"linear-gradient(135deg,#27ae60,#2ecc71)",color:"#fff",border:"none",
                borderRadius:9,fontWeight:800,cursor:"pointer",fontSize:13,
                boxShadow:"0 2px 8px rgba(39,174,96,.35)"}}>
              ✅ Wyślij do kuchni
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════ TRANSLATIONS ADMIN PANEL ═════════════════════════════ */
const TRANSLATION_GROUPS = [
  {
    label: "🪑 Wybór stolika",
    keys: ["chooseTable","scanHint","qrLabel","qrSub","nfcLabel","nfcScanning","nfcHint","orManual",
           "legendFree","legendReserved","legendOccupied","occupiedTitle","occupiedDesc",
           "staffCalled","reservationWarn","confirmEmpty","guestsQuestion",
           "pickupBtn","deliveryBtn","orTakeaway","outdoor","indoor"]
  },
  {
    label: "🍽 Menu i kategorie",
    keys: ["catAll","catPromo","promoToday","noProducts","ingredients","allergens","addToCart",
           "itemNotePlaceholder","bannerSeeOffer","campaignSee","campaignCode","campaignCodeLabel",
           "campaignCopy","campaignCopied"]
  },
  {
    label: "🛒 Koszyk i zamówienie",
    keys: ["orderNotePlaceholder","placeOrder","voucherPlaceholder","voucherUse",
           "voucherNotFound","voucherInactive","voucherUsed","voucherMaxUses",
           "orderBack","orderSummary","deliveryFree","deliveryAddrHint","deliveryZoneLabel"]
  },
  {
    label: "➕ Dodatki",
    keys: ["addonsTitle","addonsBase","addonsTotal","addonsFree","addonsAdd",
           "onlineAddonFree"]
  },
  {
    label: "💳 Płatność",
    keys: ["payTitle","payBack","payMethodCard","payMethodCardSub","payMethodBlik","payMethodBlikSub",
           "payMethodCash","payMethodCashSub","payMethodPaypal","payMethodPaypalSub",
           "payBtnCard","payBtnBlik","payBtnCash","payBtnPaypal",
           "blikTitle","blikSubtitle","blikAmount","blikCodeLabel","blikConfirm","blikEnter",
           "blikBack","blikWaiting","blikWaitSub","blikAmount2","blikCancel","blikSuccess","blikSuccessSub"]
  },
  {
    label: "✅ Potwierdzenie zamówienia",
    keys: ["successTitle","successBlik","successCash","successCard","successTable","successTime",
           "successPayment","successCoupon","successDiscount","successTotal",
           "successPayBlik","successPayCard","successPayCash","successNote","successWaiter",
           "successReceipt","successNewOrder"]
  },
  {
    label: "🚗 Formularz online",
    keys: ["onlineDeliveryTitle","onlinePickupTitle","onlineStep1","onlineStep2","onlineStep3",
           "onlineYourData","onlineFirstName","onlineLastName","onlinePhone","onlineAddress",
           "onlineNoteOpt","onlineNoteHint","onlineZoneLabel","onlineNext","onlineFillRequired",
           "onlineFillDelivery","onlineSelectZone","onlineAddMin","onlineSearchMenu","onlineHasAddons",
           "onlineDataTitle","onlineOrderTitle","onlineFreeDelivery","onlineBackBtn"]
  },
  {
    label: "🛒 Koszyk (edycja)",
    keys: ["cartTitle","cartEmpty","cartItemNote","cartOrderNote","cartSubtotal","cartToPay"]
  },
  {
    label: "🔒 Polityka prywatności",
    keys: ["privacyReadFirst","privacyScrollHint","privacyNoConsent","privacyHowTitle",
           "privacyOpt1","privacyOpt2","privacyOpt3","privacyGoBack"]
  },
  {
    label: "🧭 Nawigacja",
    keys: ["navOrders","navAdmin","navEditCart"]
  },
];

function TranslationsAdminPanel({ settings, setSettings, T, S, ac }) {
  const [openGroup, setOpenGroup] = useState(null);
  const [searchTr, setSearchTr] = useState("");

  const custom = settings.customTranslations || {};
  const setCustom = (lang, key, val) => {
    setSettings(p => ({
      ...p,
      customTranslations: {
        ...((p.customTranslations) || {}),
        [lang]: {
          ...((p.customTranslations || {})[lang] || {}),
          [key]: val,
        }
      }
    }));
  };
  const resetKey = (lang, key) => {
    setSettings(p => {
      const next = { ...((p.customTranslations || {})[lang] || {}) };
      delete next[key];
      return { ...p, customTranslations: { ...(p.customTranslations || {}), [lang]: next } };
    });
  };
  const resetAll = () => {
    if (!window.confirm("Czy na pewno chcesz przywrócić wszystkie tłumaczenia do domyślnych?")) return;
    setSettings(p => ({ ...p, customTranslations: {} }));
  };

  const isModified = (lang, key) => {
    return (custom[lang] || {})[key] !== undefined && (custom[lang] || {})[key] !== "";
  };

  const getVal = (lang, key) => ((custom[lang] || {})[key] ?? LANG[lang]?.[key] ?? "");

  const totalModified = ["pl","en"].reduce((s, lang) =>
    s + Object.keys(custom[lang] || {}).filter(k => (custom[lang][k]||"") !== "").length, 0);

  const filteredGroups = TRANSLATION_GROUPS.map(g => ({
    ...g,
    keys: searchTr
      ? g.keys.filter(k => {
          const q = searchTr.toLowerCase();
          return k.toLowerCase().includes(q)
            || (LANG.pl[k]||"").toLowerCase().includes(q)
            || (LANG.en[k]||"").toLowerCase().includes(q);
        })
      : g.keys
  })).filter(g => g.keys.length > 0 || !searchTr);

  return (
    <div style={S.formCard}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div>
          <div style={S.formTitle}>🌐 Tłumaczenia interfejsu klienta</div>
          <div style={{fontSize:12,color:T.muted,marginTop:4,lineHeight:1.5}}>
            Dostosuj teksty wyświetlane klientom w obu językach. Puste pola używają domyślnych wartości.
            {totalModified > 0 && (
              <span style={{marginLeft:8,background:"#dbeafe",color:"#1d4ed8",borderRadius:20,padding:"1px 8px",fontSize:11,fontWeight:700}}>
                {totalModified} zmodyfikowanych
              </span>
            )}
          </div>
        </div>
        {totalModified > 0 && (
          <button onClick={resetAll}
            style={{...S.cancelBtn,fontSize:12,color:"#dc2626",borderColor:"#fecaca",background:"#fef2f2",flexShrink:0}}>
            ↩ Przywróć wszystkie domyślne
          </button>
        )}
      </div>

      {/* Search */}
      <input
        style={{...S.inp,marginBottom:14}}
        placeholder="🔍 Szukaj klucza lub tekstu..."
        value={searchTr}
        onChange={e=>setSearchTr(e.target.value)}
      />

      {/* Header */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:6,padding:"0 4px"}}>
        <div style={{fontSize:11,fontWeight:700,color:T.muted}}>Klucz</div>
        <div style={{fontSize:11,fontWeight:700,color:"#1d4ed8",display:"flex",alignItems:"center",gap:5}}>
          🇵🇱 Polski
        </div>
        <div style={{fontSize:11,fontWeight:700,color:"#15803d",display:"flex",alignItems:"center",gap:5}}>
          🇬🇧 English
        </div>
      </div>

      {filteredGroups.map((group, gi) => {
        const isOpen = searchTr ? true : openGroup === gi;
        const groupModified = group.keys.filter(k => isModified("pl",k)||isModified("en",k)).length;
        return (
          <div key={group.label} style={{marginBottom:8,borderRadius:12,border:"1.5px solid "+T.border,overflow:"hidden"}}>
            <div
              onClick={() => setOpenGroup(isOpen ? null : gi)}
              style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"10px 14px",cursor:"pointer",userSelect:"none",
                background: isOpen ? T.accentSoft : T.bg,
                transition:"background .15s"}}>
              <div style={{fontWeight:700,fontSize:13,color:isOpen ? ac : T.text}}>
                {group.label}
                {groupModified > 0 && (
                  <span style={{marginLeft:8,background:ac,color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:10,fontWeight:700}}>
                    {groupModified}
                  </span>
                )}
              </div>
              <span style={{color:T.muted,fontSize:16,fontWeight:700,lineHeight:1}}>
                {isOpen ? "▲" : "▼"}
              </span>
            </div>

            {isOpen && (
              <div style={{padding:"8px 12px 12px",display:"flex",flexDirection:"column",gap:8}}>
                {group.keys.map(key => {
                  const plMod = isModified("pl", key);
                  const enMod = isModified("en", key);
                  const anyMod = plMod || enMod;
                  // Only show string keys from LANG
                  if (typeof LANG.pl[key] !== "string" && typeof LANG.en[key] !== "string") return null;
                  return (
                    <div key={key} style={{
                      display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,alignItems:"start",
                      padding:"8px 10px",borderRadius:10,
                      background: anyMod ? "#fffbeb" : "transparent",
                      border: anyMod ? "1px solid #fde68a" : "1px solid transparent",
                      transition:"all .15s"
                    }}>
                      <div style={{paddingTop:6}}>
                        <div style={{fontFamily:"monospace",fontSize:11,color:anyMod ? "#92400e" : T.muted,wordBreak:"break-all",fontWeight:anyMod?700:400}}>
                          {key}
                        </div>
                        {anyMod && (
                          <button
                            onClick={()=>{resetKey("pl",key);resetKey("en",key);}}
                            style={{marginTop:4,fontSize:10,color:"#dc2626",background:"none",border:"none",cursor:"pointer",padding:0,textDecoration:"underline"}}>
                            ↩ domyślny
                          </button>
                        )}
                      </div>
                      <div>
                        <input
                          style={{
                            ...S.inp,fontSize:12,padding:"6px 10px",
                            borderColor: plMod ? "#f59e0b" : T.inputBorder,
                            background: plMod ? "#fffbeb" : T.bg,
                          }}
                          value={getVal("pl", key)}
                          placeholder={LANG.pl[key] || ""}
                          onChange={e => setCustom("pl", key, e.target.value)}
                        />
                        {plMod && (
                          <div style={{fontSize:9,color:"#9ca3af",marginTop:2,paddingLeft:2}}>
                            domyślnie: {LANG.pl[key]}
                          </div>
                        )}
                      </div>
                      <div>
                        <input
                          style={{
                            ...S.inp,fontSize:12,padding:"6px 10px",
                            borderColor: enMod ? "#f59e0b" : T.inputBorder,
                            background: enMod ? "#fffbeb" : T.bg,
                          }}
                          value={getVal("en", key)}
                          placeholder={LANG.en[key] || ""}
                          onChange={e => setCustom("en", key, e.target.value)}
                        />
                        {enMod && (
                          <div style={{fontSize:9,color:"#9ca3af",marginTop:2,paddingLeft:2}}>
                            default: {LANG.en[key]}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div style={{marginTop:12,padding:"10px 14px",background:T.bg,borderRadius:10,border:"1px solid "+T.border,fontSize:11,color:T.muted,lineHeight:1.5}}>
        💡 Zmiany są widoczne natychmiast po wpisaniu i zapisują się razem z resztą ustawień. Puste pole = tekst domyślny.
      </div>
    </div>
  );
}


/* ════════════════════════════ EXTRAS PANEL ═════════════════════════════════
   Dodatkowe opcje — narzędzia pomocnicze dla admina i superadmina.
══════════════════════════════════════════════════════════════════════════════ */
function ExtrasPanel({
  orders, setOrders, menu, setMenu, cats, setCats, users, setUsers,
  clients, setClients, tables, setTables, campaigns, setCampaigns,
  vouchers, setVouchers, settings, setSettings,
  usedCodes, setUsedCodes, isSuperAdmin, user, ac, rolePerm
}) {
  // Fallback: jeśli rolePerm nie przekazano (np. stary kod) — nie blokuj
  const perm = rolePerm || (() => true);
  const [msg, setMsg] = useState(null);
  const showMsg = (text, type="ok") => { setMsg({text,type}); setTimeout(()=>setMsg(null),4000); };
  const todayStr = () => new Date().toISOString().slice(0,10);

  const stats = [
    { icon:"📋", label:"Wszystkich zamówień",  val: orders.length,                                    color:"#2563eb" },
    { icon:"📦", label:"Zarchiwizowanych",      val: orders.filter(o=>o.archivedAt).length,            color:"#7c3aed" },
    { icon:"🍽", label:"Pozycji w menu",        val: menu.length,                                      color:"#d97706" },
    { icon:"📂", label:"Kategorii",             val: cats.length,                                      color:"#059669" },
    { icon:"👤", label:"Użytkowników",          val: users.length,                                     color:"#dc2626" },
    { icon:"👥", label:"Kontrahentów",          val: clients.length,                                   color:"#0284c7" },
    { icon:"🪑", label:"Stolików",              val: tables.length,                                    color:"#0891b2" },
    { icon:"🎟", label:"Bonów / voucherów",     val: vouchers.length,                                  color:"#16a34a" },
    { icon:"📣", label:"Kampanii",              val: campaigns.length,                                 color:"#e94560" },
    { icon:"🔖", label:"Użytych kodów",         val: Array.isArray(usedCodes)?usedCodes.length:Object.keys(usedCodes||{}).length, color:"#64748b" },
  ];

  const quickActions = [
    {
      icon:"🧹", label:"Wyczyść użyte kody bonów", color:"#d97706", bg:"#fff7ed",
      desc:"Usuwa historię wykorzystanych kodów rabatowych.",
      action: () => {
        if (!window.confirm("Wyczyścić wszystkie użyte kody bonów?")) return;
        // Usuń każdy kod przez API serwera
        const codes = Array.isArray(usedCodes) ? usedCodes : Object.keys(usedCodes || {});
        Promise.all(codes.map(code =>
          apiFetch(`/api/used-codes/${encodeURIComponent(code)}`, { method: "DELETE" }).catch(() => {})
        )).then(() => {
          setUsedCodes([]);
          showMsg("🧹 Użyte kody wyczyszczone");
        });
      }
    },
    {
      icon:"📦", label:"Archiwizuj dziś — zapłacone", color:"#7c3aed", bg:"#faf5ff",
      desc:"Przenosi dzisiejsze zapłacone zamówienia do archiwum.",
      action: () => {
        const today = todayStr(); let count = 0;
        setOrders(p => p.map(o => {
          if (o.date===today && o.paid && !o.archivedAt) { count++; return {...o, archivedAt: new Date().toISOString()}; }
          return o;
        }));
        showMsg(`📦 Zarchiwizowano ${count} zamówień`);
      }
    },
    {
      icon:"🔄", label:"Resetuj statusy stolików", color:"#0284c7", bg:"#eff6ff",
      desc:"Oznacza wszystkie stoliki jako wolne (pomocne po awarii).",
      action: () => {
        if (!window.confirm("Oznaczyć wszystkie stoliki jako wolne?")) return;
        setTables(p => p.map(t => ({...t, occupied:false, currentOrderId:null})));
        showMsg("🔄 Stoliki zresetowane");
      }
    },
    {
      icon:"🗑", label:"Usuń puste zamówienia", color:"#dc2626", bg:"#fef2f2",
      desc:"Usuwa zamówienia bez pozycji w koszyku.",
      action: () => {
        const before = orders.length;
        setOrders(p => p.filter(o => (o.cart?.length||0) > 0));
        showMsg(`🗑 Usunięto ${before - orders.length} pustych zamówień`);
      }
    },
    {
      icon:"📊", label:"Eksportuj zamówienia CSV", color:"#059669", bg:"#f0fdf4",
      desc:"Pobiera plik CSV ze wszystkimi zamówieniami z dzisiaj.",
      action: () => {
        const today = todayStr();
        const rows = orders.filter(o=>o.date===today);
        if (!rows.length) { showMsg("Brak zamówień z dzisiaj","err"); return; }
        const csv = ["ID,Stolik,Godzina,Suma,Zapłacone,Wydane,Pozycje",
          ...rows.map(o=>[o.id?.slice(0,8),o.tableNumber||o.table,o.time||"",o.total||"",
            o.paid?"TAK":"NIE",o.issued?"TAK":"NIE",
            (o.cart||[]).map(c=>`${c.name}x${c.qty}`).join("|")].join(","))
        ].join("\n");
        const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
        a.download = `zamowienia_${today}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        showMsg("📊 Plik CSV pobrany");
      }
    },
    {
      icon:"🔀", label:"Sortuj menu alfabetycznie", color:"#7c3aed", bg:"#faf5ff",
      desc:"Sortuje pozycje menu alfabetycznie według nazwy.",
      action: () => {
        setMenu(p => [...p].sort((a,b)=>(a.name||"").localeCompare(b.name||"","pl")));
        showMsg("🔀 Menu posortowane alfabetycznie");
      }
    },
  ];

  const lsSize = (() => {
    try { let t=0; for(let k in localStorage){if(k.startsWith("smakidomu_"))t+=(localStorage[k]||"").length;} return (t/1024).toFixed(1)+" KB"; }
    catch(e){ return "n/d"; }
  })();

  return (
    <div style={{paddingBottom:40}}>
      {msg&&<div style={{padding:"10px 14px",borderRadius:10,marginBottom:14,fontWeight:700,fontSize:13,
        background:msg.type==="ok"?"#f0fdf4":"#fef2f2",color:msg.type==="ok"?"#15803d":"#dc2626",
        border:"1.5px solid "+(msg.type==="ok"?"#86efac":"#fca5a5")}}>{msg.text}</div>}

      <div style={{fontWeight:800,fontSize:18,color:TA.text,marginBottom:4}}>🔩 Dodatkowe opcje</div>
      <div style={{fontSize:12,color:TA.muted,marginBottom:20}}>Narzędzia pomocnicze, statystyki i szybkie akcje administracyjne.</div>

      {/* ── Funkcje przyznane przez superadmina ── */}
      {(()=>{
        const perms = settings.adminPermissions || {};
        // Lista wszystkich możliwych funkcji z ich powiązaniami z settings
        const FEATURE_DEFS = [
          {
            key:"adminVouchers", icon:"🎟", label:"Bony / Vouchery",
            desc:"Pole kodu kuponu widoczne dla klientów przy kasie",
            settingKey:"vouchersEnabled", defaultOn:true,
            toggle: v => setSettings(p=>({...p, vouchersEnabled:v})),
            current: settings.vouchersEnabled !== false,
          },
          {
            key:"adminPrivacyPolicy", icon:"🔒", label:"Polityka prywatności (RODO)",
            desc:"Klient musi zaakceptować politykę przed złożeniem zamówienia",
            settingKey:"privacyPolicy.enabled", defaultOn:true,
            toggle: v => setSettings(p=>({...p, privacyPolicy:{...(p.privacyPolicy||{}), enabled:v}})),
            current: !!(settings.privacyPolicy?.enabled),
          },
          {
            key:"adminQrNfc", icon:"📷", label:"QR i NFC",
            desc:"Klienci mogą skanować kody QR i przykładać tagi NFC do zamawiania",
            settingKey:"qrEnabled+nfcEnabled", defaultOn:true,
            toggle: v => setSettings(p=>({...p, qrEnabled:v, nfcEnabled:v})),
            current: settings.qrEnabled !== false && settings.nfcEnabled !== false,
            subItems: [
              { icon:"📷", label:"Kod QR",  current: settings.qrEnabled  !== false, toggle: v=>setSettings(p=>({...p,qrEnabled:v}))  },
              { icon:"📡", label:"Tag NFC", current: settings.nfcEnabled !== false, toggle: v=>setSettings(p=>({...p,nfcEnabled:v})) },
            ],
          },
          {
            key:"adminTakeaway", icon:"🥡", label:"Zamówienia na wynos i dostawa",
            desc:"Klienci mogą zamawiać na wynos lub z dostawą ze strony głównej",
            settingKey:"takeawayEnabled+deliveryEnabled", defaultOn:true,
            toggle: v => setSettings(p=>({...p, takeawayEnabled:v, deliveryEnabled:v})),
            current: settings.takeawayEnabled !== false || settings.deliveryEnabled !== false,
            subItems: [
              { icon:"🥡", label:"Odbiór osobisty", current: settings.takeawayEnabled !== false, toggle: v=>setSettings(p=>({...p,takeawayEnabled:v})) },
              { icon:"🚗", label:"Dostawa",          current: settings.deliveryEnabled !== false, toggle: v=>setSettings(p=>({...p,deliveryEnabled:v})) },
            ],
          },
          {
            key:"adminReceipt", icon:"🧾", label:"Wygląd paragonu PDF",
            desc:"Edycja treści, logo i formatu paragonu",
            settingKey:null, defaultOn:true,
            toggle: null,
            current: null,
            infoOnly: false,
            customRender: (isGranted) => {
              const rc = settings.receipt || {};
              const showLogo = rc.showLogo !== false;
              return (
                <div style={{borderRadius:12,overflow:"hidden",
                  border:"1.5px solid "+(isGranted?"#6d28d9":"#d1d5db"),
                  background: isGranted?"#faf5ff":"#f9fafb",
                  opacity: isGranted ? 1 : 0.45}}>
                  {/* Nagłówek */}
                  <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",
                    borderBottom:"1px solid #ede9fe"}}>
                    <div style={{width:42,height:42,borderRadius:11,flexShrink:0,
                      background:"#ede9fe",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>🧾</div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:13,color:"#6d28d9"}}>
                        Wygląd paragonu PDF
                        {!isSuperAdmin && isGranted && (
                          <span style={{marginLeft:7,fontSize:9,background:"#ede9fe",color:"#6d28d9",
                            borderRadius:5,padding:"1px 6px",fontWeight:700,verticalAlign:"middle"}}>✓ PRZYZNANE</span>
                        )}
                      </div>
                      <div style={{fontSize:11,color:TA.muted,marginTop:2}}>Logo, nagłówek, stopka i podgląd paragonu</div>
                    </div>
                  </div>
                  {/* Edytor */}
                  <div style={{padding:"14px 16px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,alignItems:"start"}}>
                    {/* Lewa kolumna — pola */}
                    <div style={{display:"flex",flexDirection:"column",gap:10}}>
                      {/* Logo toggle */}
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                        padding:"9px 12px",borderRadius:9,background:"#fff",border:"1px solid #e5e7eb"}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:12,color:TA.text}}>Logo na paragonie</div>
                          <div style={{fontSize:10,color:TA.muted}}>Wyświetl logo restauracji u góry</div>
                        </div>
                        <div onClick={()=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),showLogo:!showLogo}}))}
                          style={{width:40,height:22,borderRadius:11,flexShrink:0,cursor:"pointer",
                            position:"relative",transition:"all .2s",background:showLogo?"#6d28d9":"#d1d5db"}}>
                          <div style={{position:"absolute",top:2,width:18,height:18,borderRadius:"50%",
                            background:"#fff",transition:"all .2s",
                            left:showLogo?20:2,boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
                        </div>
                      </div>
                      {/* Nazwa firmy */}
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:TA.muted,display:"block",marginBottom:4}}>
                          Nazwa firmy (nagłówek)
                        </label>
                        <input style={{...S.inp,fontSize:12}}
                          placeholder={settings.restaurantName||"Nazwa firmy"}
                          value={rc.companyLine||""}
                          onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),companyLine:e.target.value}}))}/>
                      </div>
                      {/* Adres */}
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:TA.muted,display:"block",marginBottom:4}}>Adres</label>
                        <input style={{...S.inp,fontSize:12}}
                          placeholder="ul. Przykładowa 1, 00-000 Miasto"
                          value={rc.addressLine||""}
                          onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),addressLine:e.target.value}}))}/>
                      </div>
                      {/* NIP */}
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:TA.muted,display:"block",marginBottom:4}}>NIP</label>
                        <input style={{...S.inp,fontSize:12}}
                          placeholder="NIP: 000-000-00-00"
                          value={rc.nipLine||""}
                          onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),nipLine:e.target.value}}))}/>
                      </div>
                      {/* Napis pod nazwą */}
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:TA.muted,display:"block",marginBottom:4}}>Napis pod nazwą</label>
                        <input style={{...S.inp,fontSize:12}}
                          placeholder="Potwierdzenie zamówienia"
                          value={rc.confirmLabel||""}
                          onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),confirmLabel:e.target.value}}))}/>
                      </div>
                      {/* Stopka */}
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:TA.muted,display:"block",marginBottom:4}}>Stopka (podziękowania)</label>
                        <textarea style={{...S.inp,fontSize:12,resize:"vertical",minHeight:60,fontFamily:"inherit",lineHeight:1.5}}
                          placeholder={"Dziękujemy za wizytę!\nSmacznego!"}
                          value={rc.footerLine||""}
                          onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),footerLine:e.target.value}}))}/>
                      </div>
                      {/* Drobny druk */}
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:TA.muted,display:"block",marginBottom:4}}>Drobny druk</label>
                        <input style={{...S.inp,fontSize:12}}
                          placeholder="np. Reklamacje do 7 dni"
                          value={rc.footerNote||""}
                          onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),footerNote:e.target.value}}))}/>
                      </div>
                    </div>
                    {/* Prawa kolumna — podgląd */}
                    <div style={{position:"sticky",top:0}}>
                      <div style={{fontSize:11,fontWeight:700,color:TA.muted,marginBottom:8,textAlign:"center"}}>
                        👁 Podgląd na żywo
                      </div>
                      <div style={{border:"1.5px dashed #d1d5db",borderRadius:10,padding:14,background:"#fff",
                        fontFamily:"'Courier New',monospace",fontSize:11,color:"#333",maxWidth:220,margin:"0 auto",
                        boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
                        <div style={{textAlign:"center",marginBottom:8}}>
                          {showLogo && settings.logo && (
                            <img src={settings.logo} alt="" style={{width:36,height:36,objectFit:"cover",
                              borderRadius:6,display:"block",margin:"0 auto 4px"}}/>
                          )}
                          <strong style={{fontSize:13,display:"block"}}>{rc.companyLine||settings.restaurantName||"Nazwa firmy"}</strong>
                          {rc.addressLine && <div style={{fontSize:10,color:"#777",marginTop:2}}>{rc.addressLine}</div>}
                          {rc.nipLine     && <div style={{fontSize:10,color:"#777"}}>{rc.nipLine}</div>}
                          <div style={{fontSize:10,color:"#999",marginTop:3}}>{rc.confirmLabel||"Potwierdzenie zamówienia"}</div>
                        </div>
                        <div style={{borderTop:"1px dashed #ccc",margin:"6px 0"}}/>
                        <div style={{fontSize:10,color:"#555",textAlign:"left"}}>
                          <div>Stolik: #1</div>
                          <div style={{display:"flex",justifyContent:"space-between"}}><span>Przykładowe danie ×1</span><span>25,00 zł</span></div>
                          <div style={{display:"flex",justifyContent:"space-between"}}><span>Napój ×2</span><span>16,00 zł</span></div>
                        </div>
                        <div style={{borderTop:"1px dashed #ccc",margin:"6px 0"}}/>
                        <div style={{display:"flex",justifyContent:"space-between",fontWeight:700,fontSize:11}}>
                          <span>SUMA</span><span>41,00 zł</span>
                        </div>
                        <div style={{borderTop:"1px dashed #ccc",margin:"6px 0"}}/>
                        <div style={{fontSize:10,color:"#777",textAlign:"center",whiteSpace:"pre-line",lineHeight:1.5}}>
                          {rc.footerLine||"Dziękujemy za wizytę!\nSmacznego!"}
                        </div>
                        {rc.footerNote && (
                          <div style={{fontSize:9,color:"#aaa",textAlign:"center",marginTop:4,fontStyle:"italic"}}>
                            {rc.footerNote}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            },
          },
        ];

        // Pokaż tylko te funkcje które superadmin przyznał adminowi
        const granted = isSuperAdmin
          ? FEATURE_DEFS  // superadmin widzi wszystkie
          : FEATURE_DEFS.filter(f => perms[f.key] !== false);

        if (granted.length === 0) return null;

        return (
          <div style={{...S.formCard, marginBottom:16, border:"2px solid #6d28d933"}}>
            <div style={{fontWeight:700,fontSize:13,color:"#6d28d9",marginBottom:4,display:"flex",alignItems:"center",gap:8}}>
              🎛 Funkcje do zarządzania
            </div>
            <div style={{fontSize:11,color:"#7c3aed",marginBottom:14,lineHeight:1.5}}>
              {isSuperAdmin
                ? "Wszystkie funkcje systemu — masz pełen dostęp jako Super Administrator."
                : "Funkcje udostępnione przez Super Administratora. Możesz je włączać i wyłączać."}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {granted.map(f => {
                const isGranted = isSuperAdmin || perms[f.key] !== false;
                // Własny renderer (np. edytor paragonu)
                if (f.customRender) return (
                  <div key={f.key} style={{opacity: isGranted ? 1 : 0.45}}>
                    {f.customRender(isGranted)}
                  </div>
                );
                return (
                  <div key={f.key} style={{
                    borderRadius:12, overflow:"hidden",
                    border:"1.5px solid "+(f.infoOnly?"#e5e7eb":f.current?"#6d28d9":"#d1d5db"),
                    background: f.infoOnly?"#f9fafb":f.current?"#faf5ff":"#f9fafb",
                    opacity: isGranted ? 1 : 0.45,
                    transition:"all .2s"
                  }}>
                    {/* Główny wiersz */}
                    <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px"}}>
                      <div style={{
                        width:42,height:42,borderRadius:11,flexShrink:0,
                        background:f.current&&!f.infoOnly?"#ede9fe":"#f3f4f6",
                        display:"flex",alignItems:"center",justifyContent:"center",fontSize:22
                      }}>{f.icon}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:f.current&&!f.infoOnly?"#6d28d9":"#374151"}}>
                          {f.label}
                          {!isSuperAdmin && isGranted && (
                            <span style={{marginLeft:7,fontSize:9,background:"#ede9fe",color:"#6d28d9",
                              borderRadius:5,padding:"1px 6px",fontWeight:700,verticalAlign:"middle"}}>
                              ✓ PRZYZNANE
                            </span>
                          )}
                        </div>
                        <div style={{fontSize:11,color:TA.muted,marginTop:2,lineHeight:1.4}}>{f.desc}</div>
                      </div>
                      {/* Toggle — tylko jeśli nie jest infoOnly */}
                      {!f.infoOnly && f.toggle && (
                        <div
                          onClick={()=>{ if(isGranted) f.toggle(!f.current); }}
                          style={{
                            width:46,height:26,borderRadius:13,flexShrink:0,
                            background:f.current?"#6d28d9":"#d1d5db",
                            cursor:isGranted?"pointer":"not-allowed",
                            position:"relative",transition:"background .2s"
                          }}>
                          <div style={{
                            position:"absolute",top:3,
                            left:f.current?21:3,
                            width:20,height:20,borderRadius:"50%",
                            background:"#fff",transition:"left .2s",
                            boxShadow:"0 1px 4px rgba(0,0,0,0.2)"
                          }}/>
                        </div>
                      )}
                      {f.infoOnly && (
                        <span style={{fontSize:11,color:TA.muted,fontStyle:"italic",flexShrink:0}}>
                          edycja w Ustawieniach →
                        </span>
                      )}
                    </div>
                    {/* Sub-przełączniki (np. QR osobno + NFC osobno) */}
                    {f.subItems && f.current && (
                      <div style={{
                        display:"flex",gap:8,padding:"8px 14px 12px",
                        borderTop:"1px solid #ede9fe",flexWrap:"wrap"
                      }}>
                        {f.subItems.map(sub=>(
                          <div key={sub.label}
                            onClick={()=>{ if(isGranted) sub.toggle(!sub.current); }}
                            style={{
                              display:"flex",alignItems:"center",gap:8,
                              padding:"6px 12px",borderRadius:9,cursor:isGranted?"pointer":"default",
                              background:sub.current?"#ede9fe":"#f3f4f6",
                              border:"1.5px solid "+(sub.current?"#a78bfa":"#e5e7eb"),
                              transition:"all .15s",userSelect:"none"
                            }}>
                            <span style={{fontSize:15}}>{sub.icon}</span>
                            <span style={{fontSize:11,fontWeight:700,color:sub.current?"#6d28d9":"#9ca3af"}}>
                              {sub.label}
                            </span>
                            <div style={{
                              width:30,height:17,borderRadius:9,
                              background:sub.current?"#6d28d9":"#d1d5db",
                              position:"relative",flexShrink:0,transition:"background .2s"
                            }}>
                              <div style={{
                                position:"absolute",top:2,
                                left:sub.current?14:2,
                                width:13,height:13,borderRadius:"50%",
                                background:"#fff",transition:"left .2s",
                                boxShadow:"0 1px 3px rgba(0,0,0,0.2)"
                              }}/>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })} {/* end granted.map */}
            </div>
          </div>
        );
      })()}

      {/* Statystyki */}
      <div style={{...S.formCard,marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:13,color:TA.text,marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
          📊 Statystyki systemu
          <span style={{fontSize:10,color:TA.muted,fontWeight:500,marginLeft:"auto"}}>localStorage: {lsSize}</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:10}}>
          {stats.map(s=>(
            <div key={s.label} style={{padding:"12px 14px",borderRadius:12,background:s.color+"12",border:"1.5px solid "+s.color+"33"}}>
              <div style={{fontSize:22}}>{s.icon}</div>
              <div style={{fontWeight:900,fontSize:24,color:s.color,lineHeight:1}}>{s.val}</div>
              <div style={{fontSize:10,color:TA.muted,fontWeight:600,lineHeight:1.3,marginTop:2}}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Status systemu */}
      <div style={{...S.formCard,marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:13,color:TA.text,marginBottom:12}}>🌐 Status systemu</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
          {[
            {icon:"🌍",label:"Tryb online",      val:navigator.onLine?"Online":"Offline",                        ok:navigator.onLine},
            {icon:"📱",label:"Urządzenie",        val:navigator.userAgent.includes("Mobile")?"Mobilne":"Desktop", ok:true},
            {icon:"💾",label:"localStorage",      val:lsSize,                                                     ok:true},
            {icon:"👤",label:"Zalogowany jako",   val:user?.name||user?.login||"?",                               ok:true},
            {icon:"🎭",label:"Rola",               val:user?.role||"?",                                            ok:true},
            {icon:"🔒",label:"Polityka RODO",      val:settings.privacyPolicy?.enabled?"Aktywna":"Nieaktywna",    ok:!!settings.privacyPolicy?.enabled},
            {icon:"🎟",label:"Bony",               val:settings.vouchersEnabled!==false?"Włączone":"Wyłączone",   ok:settings.vouchersEnabled!==false},
            {icon:"🥡",label:"Na wynos",           val:settings.takeawayEnabled!==false?"Włączony":"Wyłączony",   ok:settings.takeawayEnabled!==false},
          ].map(item=>(
            <div key={item.label} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",borderRadius:10,
              background:item.ok?"#f0fdf4":"#fef2f2",border:"1px solid "+(item.ok?"#86efac":"#fca5a5"),flex:"1 0 160px"}}>
              <span style={{fontSize:18}}>{item.icon}</span>
              <div>
                <div style={{fontSize:10,color:TA.muted,fontWeight:600}}>{item.label}</div>
                <div style={{fontSize:12,fontWeight:700,color:item.ok?"#15803d":"#dc2626"}}>{item.val}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Szybkie akcje */}
      <div style={{...S.formCard,marginBottom:16}}>
        <div style={{fontWeight:700,fontSize:13,color:TA.text,marginBottom:14}}>⚡ Szybkie akcje</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:10}}>
          {quickActions.map(a=>(
            <div key={a.label} onClick={a.action}
              style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",borderRadius:12,
                background:a.bg,border:"1.5px solid "+a.color+"33",cursor:"pointer",transition:"all .15s"}}
              onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 14px "+a.color+"33"}
              onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
              <div style={{width:44,height:44,borderRadius:12,flexShrink:0,background:a.color+"22",
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>{a.icon}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:13,color:a.color}}>{a.label}</div>
                <div style={{fontSize:11,color:TA.muted,marginTop:2,lineHeight:1.4}}>{a.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dane restauracji — podgląd */}
      <div style={S.formCard}>
        <div style={{fontWeight:700,fontSize:13,color:TA.text,marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
          🏪 Dane restauracji
          <span style={{fontSize:11,color:TA.muted,marginLeft:"auto",fontWeight:500}}>Edycja w zakładce ⚙ Ustawienia</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {[
            {label:"Nazwa",    val:settings.restaurantName||"—"},
            {label:"Adres",    val:settings.restaurantAddress||"—"},
            {label:"Telefon",  val:settings.restaurantPhone||"—"},
            {label:"NIP",      val:settings.restaurantNip||"—"},
            {label:"Waluta",   val:settings.currency||"PLN"},
            {label:"Język",    val:settings.lang||"pl"},
          ].map(item=>(
            <div key={item.label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              padding:"7px 12px",borderRadius:8,background:"#f9fafb",border:"1px solid #e5e7eb",gap:8}}>
              <span style={{fontSize:11,color:TA.muted,fontWeight:600,flexShrink:0}}>{item.label}</span>
              <span style={{fontSize:11,fontWeight:700,color:TA.text,textAlign:"right",overflow:"hidden",
                textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:160}}>{item.val}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ════════════════════════════ DATABASE PANEL ════════════════════════════════
   Panel bazy danych — tylko dla superadmina.
   Przeglądanie i edycja wszystkich kolekcji danych aplikacji.
══════════════════════════════════════════════════════════════════════════════ */
function DatabasePanel({
  orders, setOrders, menu, setMenu, cats, setCats, users, setUsers,
  clients, setClients, tables, setTables, campaigns, setCampaigns,
  vouchers, setVouchers, settings, setSettings, staffCalls, setStaffCalls,
  usedCodes, setUsedCodes, customRoles, setCustomRoles,
  onlineOrders, setOnlineOrders, onlineMenu, setOnlineMenu,
  onlineCats, setOnlineCats, logs, setLogs, ac
}) {
  const [activeCol, setActiveCol] = useState("orders");
  const [search, setSearch]       = useState("");
  const [editId, setEditId]       = useState(null);   // id edytowanego rekordu
  const [editJson, setEditJson]   = useState("");      // raw JSON string
  const [editErr, setEditErr]     = useState("");
  const [confirmDel, setConfirmDel] = useState(null); // id do potwierdzenia usunięcia
  const [addMode, setAddMode]     = useState(false);
  const [addJson, setAddJson]     = useState("");
  const [addErr, setAddErr]       = useState("");
  const [msg, setMsg]             = useState(null);
  const [jsonView, setJsonView]   = useState(false);  // widok całej kolekcji jako JSON

  const showMsg = (text, type="ok") => { setMsg({text,type}); setTimeout(()=>setMsg(null),4000); };

  /* ── definicja kolekcji ── */
  const COLS = [
    { key:"orders",      label:"📋 Zamówienia",    icon:"📋", data:orders,      set:setOrders,      idField:"id",     color:"#2563eb" },
    { key:"archive",     label:"📦 Archiwum",       icon:"📦", data:orders.filter(o=>o.archivedAt), set: fn => setOrders(p => {
        const archived = p.filter(o=>o.archivedAt);
        const active   = p.filter(o=>!o.archivedAt);
        const next = typeof fn==="function" ? fn(archived) : fn;
        return [...active, ...next];
      }), idField:"id", color:"#7c3aed" },
    { key:"menu",        label:"🍽 Menu",           icon:"🍽", data:menu,        set:setMenu,        idField:"id",     color:"#d97706" },
    { key:"cats",        label:"📂 Kategorie",      icon:"📂", data:cats,        set:setCats,        idField:"id",     color:"#7c3aed" },
    { key:"users",       label:"👤 Użytkownicy",    icon:"👤", data:users,       set:setUsers,       idField:"id",     color:"#dc2626" },
    { key:"clients",     label:"👥 Kontrahenci",    icon:"👥", data:clients,     set:setClients,     idField:"id",     color:"#059669" },
    { key:"tables",      label:"🪑 Stoliki",        icon:"🪑", data:tables,      set:setTables,      idField:"id",     color:"#0284c7" },
    { key:"campaigns",   label:"📣 Kampanie",       icon:"📣", data:campaigns,   set:setCampaigns,   idField:"id",     color:"#e94560" },
    { key:"vouchers",    label:"🎟 Bony",           icon:"🎟", data:vouchers,    set:setVouchers,    idField:"id",     color:"#15803d" },
    { key:"usedCodes",   label:"🔖 Użyte kody",     icon:"🔖",
      data: Array.isArray(usedCodes)
        ? usedCodes.map((c,i) => typeof c==="string" ? {id:String(i), code:c} : {...c, id:c.id||c.code||String(i)})
        : Object.entries(usedCodes||{}).map(([k,v]) => ({id:k, code:k, ...(typeof v==="object"?v:{usedAt:v})})),
      set: fn => {
        // Konwertuj z powrotem do oryginalnego formatu po edycji
        const next = typeof fn==="function" ? fn(
          Array.isArray(usedCodes)
            ? usedCodes.map((c,i)=>typeof c==="string"?{id:String(i),code:c}:{...c,id:c.id||c.code||String(i)})
            : Object.entries(usedCodes||{}).map(([k,v])=>({id:k,code:k,...(typeof v==="object"?v:{usedAt:v})}))
        ) : fn;
        if (Array.isArray(usedCodes)) {
          setUsedCodes(next.map(r=>r.code||r.id));
        } else {
          setUsedCodes(Object.fromEntries(next.map(r=>[r.code||r.id, r.usedAt||true])));
        }
      },
      idField:"id", color:"#64748b" },
    { key:"staffCalls",  label:"🔔 Wołania obsługi",icon:"🔔", data:staffCalls,  set:setStaffCalls,  idField:"id",     color:"#ea580c" },
    { key:"customRoles", label:"🎭 Role własne",    icon:"🎭", data:customRoles, set:setCustomRoles, idField:"id",     color:"#6d28d9" },
    { key:"onlineOrders",label:"🌐 Zamówienia online",icon:"🌐", data:onlineOrders||[], set:setOnlineOrders, idField:"id",  color:"#0891b2" },
    { key:"onlineMenu",  label:"🛒 Menu online",      icon:"🛒", data:onlineMenu||[],   set:setOnlineMenu,   idField:"id",  color:"#c2410c" },
    { key:"onlineCats",  label:"🗂 Kategorie online", icon:"🗂", data:onlineCats||[],   set:setOnlineCats,   idField:"id",  color:"#a21caf" },
    { key:"logs",        label:"📋 Logi",             icon:"📋", data:logs||[],         set:setLogs,         idField:"id",  color:"#475569" },
    { key:"settings",    label:"⚙ Ustawienia",     icon:"⚙", data:settings,    set:setSettings,    idField:null,     color:"#374151", isSingle:true },
  ];

  const col = COLS.find(c=>c.key===activeCol) || COLS[0];

  /* ── dane kolekcji jako tablica rekordów ── */
  const records = col.isSingle
    ? [{ __single: true, ...col.data }]
    : Array.isArray(col.data) ? col.data : [];

  /* ── filtrowanie ── */
  const filtered = search.trim()
    ? records.filter(r => JSON.stringify(r).toLowerCase().includes(search.toLowerCase()))
    : records;

  /* ── pomocnik: etykieta rekordu ── */
  const recLabel = (r) => {
    if (r.__single) return "Obiekt ustawień";
    if (col.key==="orders"||col.key==="archive") return `#${r.tableNumber||r.table||"?"} · ${r.date||""} · ${r.total||0} zł${r.archivedAt?" · 📦 "+new Date(r.archivedAt).toLocaleString("pl-PL",{hour:"2-digit",minute:"2-digit"}):""}`;
    if (col.key==="usedCodes") return `🔖 ${r.code||r.id}${r.usedAt?" · "+r.usedAt:""}`;    if (col.key==="menu")   return `${r.emoji||""} ${r.name||r.id}`;
    if (col.key==="users")  return `${r.name||r.login||r.id} (${r.role||""})`;
    if (col.key==="cats")   return `${r.emoji||""} ${r.name||r.id}`;
    if (col.key==="clients")    return `${r.company||r.name||r.id}`;
    if (col.key==="tables")      return `Stolik #${r.number||r.id}`;
    if (col.key==="onlineOrders")return `🌐 #${r.id||"?"} · ${r.date||r.createdAt||""} · ${r.total||0} zł`;
    if (col.key==="onlineMenu")  return `${r.emoji||""} ${r.name||r.id}`;
    if (col.key==="onlineCats")  return `${r.emoji||""} ${r.name||r.id}`;
    if (col.key==="logs")        return `📋 ${r.type||r.label||r.id||""} · ${r.date||r.ts||""}`;
    if (col.key==="staffCalls")  return `🔔 Stolik ${r.table||r.tableNumber||r.id} · ${r.ts||r.date||""}`;
    if (col.key==="customRoles") return `🎭 ${r.name||r.id}`;
    return r.name||r.code||r.id||JSON.stringify(r).slice(0,40);
  };

  /* ── edycja rekordu ── */
  const startEdit = (r) => {
    const id = col.idField ? r[col.idField] : "__single";
    setEditId(id);
    setEditJson(JSON.stringify(col.isSingle ? col.data : r, null, 2));
    setEditErr("");
    setAddMode(false);
  };

  const saveEdit = () => {
    try {
      const parsed = JSON.parse(editJson);
      if (col.isSingle) {
        col.set(parsed);
      } else {
        col.set(p => p.map(r => (col.idField && r[col.idField]===editId) ? parsed : r));
      }
      setEditId(null);
      showMsg("✅ Rekord zapisany");
    } catch(e) {
      setEditErr("Błąd JSON: " + e.message);
    }
  };

  /* ── usuwanie rekordu ── */
  const deleteRec = (r) => {
    if (!col.idField || col.isSingle) return;
    col.set(p => p.filter(x => x[col.idField] !== r[col.idField]));
    setConfirmDel(null);
    showMsg("🗑 Rekord usunięty");
  };

  /* ── dodawanie rekordu ── */
  const startAdd = () => {
    const template = records.length > 0 && !records[0].__single
      ? Object.fromEntries(Object.keys(records[0]).map(k=>[k, records[0][k] instanceof Array ? [] : typeof records[0][k] === "object" && records[0][k] !== null ? {} : ""]))
      : {};
    if (col.idField) template[col.idField] = genId();
    setAddJson(JSON.stringify(template, null, 2));
    setAddErr("");
    setAddMode(true);
    setEditId(null);
  };

  const saveAdd = () => {
    try {
      const parsed = JSON.parse(addJson);
      col.set(p => Array.isArray(p) ? [...p, parsed] : p);
      setAddMode(false);
      showMsg("✅ Rekord dodany");
    } catch(e) {
      setAddErr("Błąd JSON: " + e.message);
    }
  };

  /* ── wyczyść kolekcję ── */
  const clearCollection = () => {
    if (!window.confirm(`⚠ UWAGA: Czy na pewno wyczyścić całą kolekcję "${col.label}" (${records.length} rekordów)?\n\nTa operacja jest NIEODWRACALNA.`)) return;
    col.set(col.isSingle ? {} : []);
    showMsg(`🗑 Kolekcja ${col.label} wyczyszczona`);
  };

  /* ── import JSON ── */
  const importJson = () => {
    const text = prompt("Wklej JSON tablicy lub obiektu:");
    if (!text) return;
    try {
      const parsed = JSON.parse(text);
      col.set(parsed);
      showMsg("✅ Dane zaimportowane");
    } catch(e) {
      alert("Błąd JSON: " + e.message);
    }
  };

  /* ── eksport JSON ── */
  const exportJson = () => {
    const json = JSON.stringify(col.data, null, 2);
    const blob = new Blob([json], {type:"application/json"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `smakidomu_${col.key}_${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const totalSize = JSON.stringify(col.data).length;

  return (
    <div style={{paddingBottom:40}}>
      {msg&&<div style={{padding:"10px 14px",borderRadius:10,marginBottom:12,fontWeight:700,fontSize:13,
        background:msg.type==="ok"?"#f0fdf4":"#fef2f2",
        color:msg.type==="ok"?"#15803d":"#dc2626",
        border:"1.5px solid "+(msg.type==="ok"?"#86efac":"#fca5a5")}}>
        {msg.text}
      </div>}

      {/* Nagłówek */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontWeight:800,fontSize:18,color:TA.text}}>🗄 Baza danych — edytor</div>
          <div style={{fontSize:12,color:TA.muted,marginTop:2}}>Przeglądaj i edytuj wszystkie kolekcje danych aplikacji</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={exportJson} style={{...S.cancelBtn,fontSize:12}}>📥 Eksport JSON</button>
          <button onClick={importJson} style={{...S.cancelBtn,fontSize:12}}>📤 Import JSON</button>
          {!col.isSingle&&<button onClick={startAdd} style={{...S.darkBtn,fontSize:12}}>+ Dodaj rekord</button>}
        </div>
      </div>

      {/* Selektor kolekcji */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
        {COLS.map(c=>(
          <button key={c.key}
            onClick={()=>{setActiveCol(c.key);setEditId(null);setAddMode(false);setSearch("");}}
            style={{
              padding:"7px 12px",borderRadius:9,fontSize:12,fontWeight:600,cursor:"pointer",
              border:"1.5px solid "+(activeCol===c.key?c.color:TA.border),
              background:activeCol===c.key?c.color:"#fff",
              color:activeCol===c.key?"#fff":TA.muted,
              transition:"all .15s",position:"relative"
            }}>
            {c.icon} {c.label.split(" ").slice(1).join(" ")}
            <span style={{
              marginLeft:5,background:activeCol===c.key?"rgba(255,255,255,0.3)":TA.border,
              color:activeCol===c.key?"#fff":TA.muted,
              borderRadius:10,padding:"0 5px",fontSize:10,fontWeight:800
            }}>
              {c.isSingle ? "obj" : Array.isArray(c.data) ? c.data.length : "?"}
            </span>
          </button>
        ))}
      </div>

      {/* Info bar */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"8px 14px",marginBottom:12,
        background:"#f9fafb",borderRadius:10,border:"1px solid "+TA.border,flexWrap:"wrap"}}>
        <span style={{fontWeight:700,fontSize:13,color:col.color}}>{col.icon} {col.label}</span>
        <span style={{fontSize:11,color:TA.muted}}>·</span>
        <span style={{fontSize:11,color:TA.muted}}>{col.isSingle?"obiekt":records.length+" rekordów"}</span>
        <span style={{fontSize:11,color:TA.muted}}>·</span>
        <span style={{fontSize:11,color:TA.muted}}>{(totalSize/1024).toFixed(1)} KB</span>
        <div style={{marginLeft:"auto",display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>setJsonView(p=>!p)}
            style={{...S.smBtn,background:jsonView?"#eff6ff":"#f9fafb",color:jsonView?TA.accent:TA.muted,fontSize:11}}>
            {jsonView?"📋 Lista":"{ } JSON"}
          </button>
          {!col.isSingle&&(
            <button onClick={clearCollection}
              style={{...S.smBtn,background:"#fef2f2",color:"#dc2626",borderColor:"#fca5a5",fontSize:11}}>
              🗑 Wyczyść
            </button>
          )}
        </div>
      </div>

      {/* Widok JSON całej kolekcji */}
      {jsonView&&(
        <div style={{...S.formCard,border:"2px solid "+col.color}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontWeight:700,fontSize:13,color:col.color}}>{col.icon} {col.label} — pełny JSON</div>
            <button onClick={()=>setJsonView(false)} style={S.closeBtn}>✕</button>
          </div>
          <textarea
            style={{...S.inp,fontFamily:"monospace",fontSize:11,height:420,resize:"vertical",
              background:"#1e1e1e",color:"#d4d4d4",border:"1px solid #374151",lineHeight:1.5}}
            value={JSON.stringify(col.data, null, 2)}
            onChange={e=>{
              try {
                const parsed = JSON.parse(e.target.value);
                col.set(parsed);
              } catch(_) {}
            }}
          />
          <div style={{fontSize:10,color:TA.muted,marginTop:6}}>
            ⚡ Edycja na żywo — zmiany aplikowane natychmiast po poprawnym JSON
          </div>
        </div>
      )}

      {/* Formularz dodawania */}
      {addMode&&!col.isSingle&&(
        <div style={{...S.formCard,border:"2px solid #16a34a",background:"#f0fdf4",marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontWeight:700,fontSize:14,color:"#15803d"}}>➕ Nowy rekord — {col.label}</div>
            <button onClick={()=>setAddMode(false)} style={S.closeBtn}>✕</button>
          </div>
          <textarea
            style={{...S.inp,fontFamily:"monospace",fontSize:12,height:260,resize:"vertical",
              background:"#fff",border:"1.5px solid "+(addErr?"#dc2626":"#86efac"),lineHeight:1.5}}
            value={addJson}
            onChange={e=>{setAddJson(e.target.value);setAddErr("");}}
          />
          {addErr&&<div style={{color:"#dc2626",fontSize:12,marginTop:6,fontWeight:600}}>{addErr}</div>}
          <div style={{display:"flex",gap:10,marginTop:12}}>
            <button onClick={saveAdd} style={{...S.darkBtn,background:"#16a34a"}}>✅ Dodaj rekord</button>
            <button onClick={()=>setAddMode(false)} style={S.cancelBtn}>Anuluj</button>
          </div>
        </div>
      )}

      {/* Szukaj */}
      {!jsonView&&(
        <div style={{marginBottom:12}}>
          <input
            style={S.inp}
            placeholder={`🔍 Szukaj w ${col.label}...`}
            value={search}
            onChange={e=>setSearch(e.target.value)}
          />
        </div>
      )}

      {/* Lista rekordów */}
      {!jsonView&&(
        filtered.length===0
          ? <div style={S.empty}><div style={{fontSize:40}}>{col.icon}</div><p>Brak rekordów{search?" pasujących do szukania":""}</p></div>
          : filtered.map((r, idx)=>{
            const recId = col.idField ? r[col.idField] : (r.__single ? "__single" : idx);
            const isEditing = editId === recId;
            return (
              <div key={recId||idx} style={{
                ...S.card,
                borderLeft:"4px solid "+col.color,
                marginBottom:8,
                background: isEditing ? "#fafeff" : "#fff"
              }}>
                {isEditing ? (
                  /* ── Formularz edycji ── */
                  <div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                      <div style={{fontWeight:700,fontSize:13,color:col.color}}>✏ Edycja rekordu</div>
                      <button onClick={()=>setEditId(null)} style={S.closeBtn}>✕</button>
                    </div>
                    <textarea
                      style={{...S.inp,fontFamily:"monospace",fontSize:12,height:340,resize:"vertical",
                        background:"#fff",border:"1.5px solid "+(editErr?"#dc2626":col.color+"66"),lineHeight:1.5}}
                      value={editJson}
                      onChange={e=>{setEditJson(e.target.value);setEditErr("");}}
                    />
                    {editErr&&<div style={{color:"#dc2626",fontSize:12,marginTop:6,fontWeight:600}}>{editErr}</div>}
                    <div style={{display:"flex",gap:10,marginTop:12}}>
                      <button onClick={saveEdit} style={{...S.darkBtn,background:col.color}}>💾 Zapisz zmiany</button>
                      <button onClick={()=>setEditId(null)} style={S.cancelBtn}>Anuluj</button>
                    </div>
                  </div>
                ) : confirmDel===recId ? (
                  /* ── Potwierdzenie usunięcia ── */
                  <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                    <span style={{flex:1,fontSize:13,fontWeight:700,color:"#dc2626"}}>
                      ⚠ Usunąć rekord: {recLabel(r)}?
                    </span>
                    <button onClick={()=>deleteRec(r)}
                      style={{padding:"6px 14px",background:"#dc2626",color:"#fff",border:"none",borderRadius:8,fontWeight:800,cursor:"pointer",fontSize:12}}>
                      🗑 Usuń
                    </button>
                    <button onClick={()=>setConfirmDel(null)} style={S.cancelBtn}>Anuluj</button>
                  </div>
                ) : (
                  /* ── Widok rekordu ── */
                  <div style={{display:"flex",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13,color:TA.text,marginBottom:4}}>
                        {recLabel(r)}
                      </div>
                      {/* Podgląd kluczowych pól */}
                      <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                        {Object.entries(r).filter(([k])=>k!=="__single").slice(0,8).map(([k,v])=>(
                          <span key={k} style={{
                            background:"#f3f4f6",borderRadius:6,padding:"2px 8px",
                            fontSize:10,color:TA.muted,fontFamily:"monospace"
                          }}>
                            <span style={{color:col.color,fontWeight:700}}>{k}:</span>{" "}
                            {Array.isArray(v)?`[${v.length}]`:typeof v==="object"&&v!==null?`{…}`
                              :String(v).slice(0,30)+(String(v).length>30?"…":"")}
                          </span>
                        ))}
                        {Object.keys(r).filter(k=>k!=="__single").length>8&&(
                          <span style={{fontSize:10,color:TA.muted}}>
                            +{Object.keys(r).length-8} więcej pól
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:5,flexShrink:0}}>
                      <button onClick={()=>startEdit(r)}
                        style={{...S.actBtn,color:col.color,fontWeight:700}}>
                        ✏ Edytuj
                      </button>
                      {col.idField&&!col.isSingle&&(
                        <button onClick={()=>setConfirmDel(recId)}
                          style={{...S.actBtn,background:"#ffebee",color:"#dc2626"}}>
                          🗑
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
      )}
    </div>
  );
}

/* ════════════════════════════ LOGS PANEL — REAL-TIME ════════════════════════ */
function useRelativeTime(tick) {
  // tick changes every second — use externally provided ticker
  return (ts) => {
    if (!ts) return "—";
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff <   5) return "przed chwilą";
    if (diff <  60) return `${diff} sek. temu`;
    if (diff < 3600) return `${Math.floor(diff / 60)} min temu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} godz. temu`;
    return new Date(ts).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };
}

function LogsPanel({ logs, setLogs, orders, setOrders }) {
  const [subTab,    setSubTab]    = useState("logins");
  const [search,    setSearch]    = useState("");
  const [paused,    setPaused]    = useState(false);
  const [tick,      setTick]      = useState(0);           // 1-second heartbeat
  const [newIds,    setNewIds]    = useState(new Set());   // IDs of freshly arrived entries
  const prevLoginCount = useRef(0);
  const prevOrderCount = useRef(0);
  const listTopRef     = useRef(null);
  const relTime        = useRelativeTime(tick);

  /* ── 1-second ticker for relative timestamps ── */
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  /* ── Detect new entries and flash them ── */
  const loginLogs = useMemo(() =>
    (logs || []).filter(l => l.type === "login" || l.type === "login_fail").slice().reverse(),
  [logs]);

  const orderLogs = useMemo(() =>
    (orders || []).filter(o => o.ip || o.location).slice().reverse(),
  [orders]);

  const activityLogs = useMemo(() =>
    (logs || []).filter(l => l.type === "activity").slice().reverse(),
  [logs]);

  useEffect(() => {
    const cur = loginLogs.length;
    if (cur > prevLoginCount.current) {
      const fresh = loginLogs.slice(0, cur - prevLoginCount.current).map(l => l.id).filter(Boolean);
      if (fresh.length) {
        setNewIds(prev => { const s = new Set(prev); fresh.forEach(id => s.add(id)); return s; });
        setTimeout(() => setNewIds(prev => { const s = new Set(prev); fresh.forEach(id => s.delete(id)); return s; }), 4000);
        if (!paused && listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
    prevLoginCount.current = cur;
  }, [loginLogs.length]); // eslint-disable-line

  useEffect(() => {
    const cur = orderLogs.length;
    if (cur > prevOrderCount.current) {
      const fresh = orderLogs.slice(0, cur - prevOrderCount.current).map(o => o.id).filter(Boolean);
      if (fresh.length) {
        setNewIds(prev => { const s = new Set(prev); fresh.forEach(id => s.add(id)); return s; });
        setTimeout(() => setNewIds(prev => { const s = new Set(prev); fresh.forEach(id => s.delete(id)); return s; }), 4000);
        if (!paused && subTab === "orders" && listTopRef.current)
          listTopRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
    prevOrderCount.current = cur;
  }, [orderLogs.length]); // eslint-disable-line

  /* ── Filtering ── */
  const filteredLogin = useMemo(() => {
    if (!search.trim()) return loginLogs;
    const q = search.toLowerCase();
    return loginLogs.filter(l =>
      (l.login   || "").toLowerCase().includes(q) ||
      (l.name    || "").toLowerCase().includes(q) ||
      (l.ip      || "").toLowerCase().includes(q) ||
      (l.city    || "").toLowerCase().includes(q) ||
      (l.country || "").toLowerCase().includes(q)
    );
  }, [loginLogs, search]);

  const filteredOrder = useMemo(() => {
    if (!search.trim()) return orderLogs;
    const q = search.toLowerCase();
    return orderLogs.filter(o =>
      (o.ip       || "").toLowerCase().includes(q) ||
      (o.city     || "").toLowerCase().includes(q) ||
      (o.country  || "").toLowerCase().includes(q) ||
      String(o.tableNumber || "").toLowerCase().includes(q)
    );
  }, [orderLogs, search]);

  const filteredActivity = useMemo(() => {
    if (!search.trim()) return activityLogs;
    const q = search.toLowerCase();
    return activityLogs.filter(l =>
      (l.login   || "").toLowerCase().includes(q) ||
      (l.name    || "").toLowerCase().includes(q) ||
      (l.action  || "").toLowerCase().includes(q) ||
      (l.details || "").toLowerCase().includes(q)
    );
  }, [activityLogs, search]);

  /* ── Helpers ── */
  const fmt = ts => {
    if (!ts) return "—";
    try { return new Date(ts).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
    catch { return ts; }
  };

  const badgeStyle = (color, bg) => ({
    display: "inline-block", padding: "2px 9px", borderRadius: 20,
    fontSize: 11, fontWeight: 700, background: bg, color: color,
    border: `1.5px solid ${color}33`,
  });

  const isNew = id => id && newIds.has(id);

  const cardStyle = (id, accentColor) => ({
    ...S.card,
    borderLeft: `4px solid ${accentColor}`,
    transition: "background .5s, box-shadow .5s",
    background: isNew(id) ? "#fffbea" : "#fff",
    boxShadow: isNew(id) ? `0 0 0 2px ${accentColor}55, ${TA.cardShadow}` : TA.cardShadow,
    animation: isNew(id) ? "fadeSlide .35s ease" : "none",
  });

  const failsToday = useMemo(() =>
    (logs||[]).filter(l => l.type === "login_fail" && l.ts && new Date(l.ts).toDateString() === new Date().toDateString()).length,
  [logs]);

  /* ── Field grid row ── */
  const FieldGrid = ({ pairs }) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 5, fontSize: 12, marginTop: 6 }}>
      {pairs.filter(([,v]) => v && v !== "—").map(([k, v]) => (
        <div key={k} style={{ background: "#f9fafb", borderRadius: 7, padding: "4px 9px", display: "flex", gap: 4, alignItems: "baseline", minWidth: 0 }}>
          <span style={{ fontWeight: 700, color: TA.muted, flexShrink: 0 }}>{k}</span>
          <span style={{ color: TA.text, wordBreak: "break-all" }}>{v}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      {/* ── Injected CSS for pulse animation ── */}
      <style>{`
        @keyframes livePulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(0.7)} }
        @keyframes newFlash  { 0%{background:#fffbea} 100%{background:#fff} }
        .log-live-dot { animation: livePulse 1.4s ease-in-out infinite; }
      `}</style>

      {/* ── LIVE header bar ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
        background: "linear-gradient(135deg,#16213e,#1e3a6e)",
        borderRadius: 12, padding: "10px 16px", flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <div className="log-live-dot" style={{
            width: 10, height: 10, borderRadius: "50%",
            background: paused ? "#9ca3af" : "#22c55e",
            boxShadow: paused ? "none" : "0 0 8px #22c55e",
          }}/>
          <span style={{ fontWeight: 800, fontSize: 13, color: "#fff", letterSpacing: 1 }}>
            {paused ? "WSTRZYMANO" : "● LIVE"}
          </span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
          Logi aktualizują się automatycznie w czasie rzeczywistym
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            onClick={() => setPaused(p => !p)}
            style={{
              padding: "5px 13px", borderRadius: 8, border: "1.5px solid rgba(255,255,255,0.25)",
              background: paused ? "#22c55e22" : "rgba(255,255,255,0.1)",
              color: paused ? "#22c55e" : "rgba(255,255,255,0.75)", fontWeight: 700, fontSize: 12, cursor: "pointer",
            }}>
            {paused ? "▶ Wznów" : "⏸ Wstrzymaj"}
          </button>
          <button
            onClick={() => { if (window.confirm("Wyczyścić wszystkie logi?")) setLogs([]); }}
            style={{
              padding: "5px 13px", borderRadius: 8, border: "1.5px solid rgba(239,68,68,0.4)",
              background: "rgba(239,68,68,0.15)", color: "#fca5a5", fontWeight: 700, fontSize: 12, cursor: "pointer",
            }}>
            🗑 Wyczyść logi
          </button>
        </div>
      </div>

      {/* ── Stats cards ── */}
      <div className="resp-grid-3" style={{ marginBottom: 14 }}>
        {[
          { icon: "🔐", label: "Logowania łącznie",  val: loginLogs.length,   color: "#2563eb", bg: "#eff6ff" },
          { icon: "📦", label: "Zamówienia z IP",     val: orderLogs.length,   color: "#059669", bg: "#f0fdf4" },
          { icon: "⚠", label: "Nieudane dziś",       val: failsToday,         color: "#dc2626", bg: "#fef2f2" },
        ].map(s => (
          <div key={s.label} style={{
            background: s.bg, border: `1.5px solid ${s.color}33`,
            borderRadius: 12, padding: "13px 16px",
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <div style={{ fontSize: 26 }}>{s.icon}</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 24, color: s.color, lineHeight: 1 }}>{s.val}</div>
              <div style={{ fontSize: 11, color: TA.muted, fontWeight: 600, marginTop: 2 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Sub-tabs + search ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        {[
          { key: "logins", icon: "🔐", label: `Logowania (${loginLogs.length})` },
          { key: "orders", icon: "📦", label: `Zamówienia (${orderLogs.length})` },
          { key: "activity", icon: "🖱", label: `Aktywność (${activityLogs.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            style={{ ...S.pill, ...(subTab === t.key ? S.pillOn : {}), fontSize: 13, padding: "8px 18px" }}>
            {t.icon} {t.label}
            {/* Badge for new entries */}
            {t.key === "logins" && newIds.size > 0 && subTab !== "logins" && (
              <span style={{ background: "#ef4444", color: "#fff", borderRadius: 10, padding: "0 5px", fontSize: 10, fontWeight: 700, marginLeft: 5 }}>NEW</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ position: "relative", marginBottom: 14 }}>
        <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: TA.muted, fontSize: 14 }}>🔍</span>
        <input style={{ ...S.inp, paddingLeft: 32 }} placeholder="Szukaj po loginie, IP, mieście, kraju…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {search && (
          <button onClick={() => setSearch("")}
            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: TA.muted, fontSize: 16 }}>✕</button>
        )}
      </div>

      {/* ── Scroll anchor ── */}
      <div ref={listTopRef}/>

      {/* ══════════ LOGINS ══════════ */}
      {subTab === "logins" && (
        filteredLogin.length === 0
          ? <div style={S.empty}><div style={{ fontSize: 48, marginBottom: 8 }}>🔐</div><div>Brak logów logowań</div></div>
          : filteredLogin.map((l) => {
            const accent = l.type === "login_fail" ? "#dc2626" : "#2563eb";
            const roleColor = l.role === "superadmin" ? "#6d28d9" : l.role === "admin" ? "#856404" : "#15803d";
            const roleBg    = l.role === "superadmin" ? "#ede9fe" : l.role === "admin" ? "#fff3cd" : "#dcfce7";
            return (
              <div key={l.id} style={cardStyle(l.id, accent)}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 11, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 26, flexShrink: 0, lineHeight: 1, marginTop: 2 }}>
                    {l.type === "login_fail" ? "❌" : "✅"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Header row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 3 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: TA.text }}>{l.name || l.login}</span>
                      {l.role && <span style={badgeStyle(roleColor, roleBg)}>{l.role}</span>}
                      <span style={badgeStyle(l.type === "login_fail" ? "#dc2626" : "#059669", l.type === "login_fail" ? "#fef2f2" : "#f0fdf4")}>
                        {l.type === "login_fail" ? "Nieudane" : "Udane"}
                      </span>
                      {isNew(l.id) && (
                        <span style={{ background: "#fbbf24", color: "#78350f", borderRadius: 8, padding: "1px 7px", fontSize: 10, fontWeight: 800, animation: "popIn .3s ease" }}>
                          NOWE
                        </span>
                      )}
                      {/* Relative time — updates every second */}
                      <span style={{ marginLeft: "auto", fontSize: 11, color: TA.muted, whiteSpace: "nowrap" }}>
                        🕐 {relTime(l.ts)}
                      </span>
                    </div>
                    {/* Detail grid */}
                    <FieldGrid pairs={[
                      ["📅",  fmt(l.ts)],
                      ["👤",  l.login],
                      ["🌐 IP",    l.ip],
                      ["📍",  l.city],
                      ["🌍",  l.country],
                      ["🗺", l.region],
                      ["📡",  l.isp],
                      ["🖥", l.ua ? l.ua.slice(0, 60) + (l.ua.length > 60 ? "…" : "") : null],
                    ]}/>
                  </div>
                </div>
              </div>
            );
          })
      )}

      {/* ══════════ ORDERS ══════════ */}
      {subTab === "orders" && (
        filteredOrder.length === 0
          ? <div style={S.empty}><div style={{ fontSize: 48, marginBottom: 8 }}>📦</div><div>Brak danych IP w zamówieniach</div></div>
          : filteredOrder.map((o) => (
            <div key={o.id} style={cardStyle(o.id, "#059669")}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 11, flexWrap: "wrap" }}>
                <div style={{ fontSize: 26, flexShrink: 0, lineHeight: 1, marginTop: 2 }}>📦</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: TA.text }}>
                      {o.takeaway ? "🥡 Na wynos" : `Stolik #${o.tableNumber || o.table}`}
                      {" · "}{o.total != null ? Number(o.total).toFixed(2) + " zł" : "—"}
                    </span>
                    <span style={badgeStyle("#059669", "#f0fdf4")}>{o.date} {o.time}</span>
                    {o.takeaway && <span style={badgeStyle("#d97706", "#fff7ed")}>Na wynos</span>}
                    {isNew(o.id) && (
                      <span style={{ background: "#fbbf24", color: "#78350f", borderRadius: 8, padding: "1px 7px", fontSize: 10, fontWeight: 800, animation: "popIn .3s ease" }}>
                        NOWE
                      </span>
                    )}
                    <span style={{ marginLeft: "auto", fontSize: 11, color: TA.muted, whiteSpace: "nowrap" }}>
                      🕐 {relTime(o.ts || (o.date + "T" + (o.time || "00:00")))}
                    </span>
                  </div>
                  <FieldGrid pairs={[
                    ["🌐 IP",    o.ip],
                    ["📍",  o.city || o.location],
                    ["🌍",  o.country],
                    ["🗺", o.region],
                    ["📡",  o.isp],
                    ["🖥", o.ua ? o.ua.slice(0, 60) + (o.ua.length > 60 ? "…" : "") : null],
                    ["📋 Pozycji", String((o.cart || []).length)],
                  ]}/>
                  <button onClick={()=>{ if(window.confirm("Usunąć ten wpis z historii zamówień?")) setOrders(p=>(p||[]).filter(x=>x.id!==o.id)); }}
                    style={{marginTop:8,padding:"4px 10px",borderRadius:7,border:"1px solid #fecaca",background:"#fff",color:"#dc2626",cursor:"pointer",fontSize:11,fontWeight:700}}>
                    🗑 Usuń
                  </button>
                </div>
              </div>
            </div>
          ))
      )}
      {/* ══════════ ACTIVITY (kliknięcia / akcje pracowników) ══════════ */}
      {subTab === "activity" && (
        filteredActivity.length === 0
          ? <div style={S.empty}><div style={{ fontSize: 48, marginBottom: 8 }}>🖱</div><div>Brak logów aktywności</div></div>
          : filteredActivity.map((l) => {
            const roleColor = l.role === "superadmin" ? "#6d28d9" : l.role === "admin" ? "#856404" : l.role === "cook" ? "#0369a1" : "#15803d";
            const roleBg    = l.role === "superadmin" ? "#ede9fe" : l.role === "admin" ? "#fff3cd" : l.role === "cook" ? "#e0f2fe" : "#dcfce7";
            return (
              <div key={l.id} style={cardStyle(l.id, "#7c3aed")}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 11, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 26, flexShrink: 0, lineHeight: 1, marginTop: 2 }}>🖱</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 3 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: TA.text }}>{l.action}</span>
                      <span style={badgeStyle(roleColor, roleBg)}>{l.name || l.login}</span>
                      {isNew(l.id) && (
                        <span style={{ background: "#fbbf24", color: "#78350f", borderRadius: 8, padding: "1px 7px", fontSize: 10, fontWeight: 800, animation: "popIn .3s ease" }}>
                          NOWE
                        </span>
                      )}
                      <span style={{ marginLeft: "auto", fontSize: 11, color: TA.muted, whiteSpace: "nowrap" }}>
                        🕐 {relTime(l.ts)}
                      </span>
                    </div>
                    {l.details && (
                      <div style={{ fontSize: 12, color: TA.muted, marginTop: 2 }}>{l.details}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
      )}
    </div>
  );
}

function AdminPanel({ user, users, setUsers, menu, setMenu, cats, setCats, settings, setSettings, campaigns, setCampaigns, vouchers, setVouchers, tables, setTables, usedCodes, setUsedCodes, fbConfig, setFbConfig, orders, setOrders, onLogout, staffCalls, setStaffCalls, clients, setClients, customRoles, setCustomRoles, logs, setLogs, onlineMenu, setOnlineMenu, onlineCats, setOnlineCats, onlineOrders, setOnlineOrders }) {
  const bp = useBreakpoint();
  const isSuperAdmin = user.role==="superadmin";
  const isAdmin  = user.role==="admin" || user.role==="superadmin";
  const isCook   = user.role==="cook";
  const isSeller = user.role==="seller";

  // ── Staff alerts state ──
  const [incomingAlert, setIncomingAlert] = React.useState(null);
  const alertTimerRef = React.useRef(null);

  // ── Notification bar state ──
  const [notifBarMode,    setNotifBarMode]    = React.useState(null);   // null | "bell" | "msg"
  const [notifTarget,     setNotifTarget]     = React.useState("all");  // "all"|"seller"|"cook"|"admin"
  const [notifMsgText,    setNotifMsgText]    = React.useState("");
  const [notifSending,    setNotifSending]    = React.useState(false);

  // ── Listen for incoming alerts ──
  useStaffAlerts({
    fbConfig,
    user,
    onAlert: (data) => {
      playSirenSound();
      setIncomingAlert(data);
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
      alertTimerRef.current = setTimeout(() => setIncomingAlert(null), 10000);
    },
  });

  const handleSendBell = async () => {
    if (notifSending) return;
    setNotifSending(true);
    await sendStaffAlert(fbConfig, "reminder", user.name || user.login, user.role, "", notifTarget);
    setNotifSending(false);
    setNotifBarMode(null);
    setNotifTarget("all");
  };

  const handleSendMsg = async () => {
    if (!notifMsgText.trim() || notifSending) return;
    setNotifSending(true);
    await sendStaffAlert(fbConfig, "message", user.name || user.login, user.role, notifMsgText.trim(), notifTarget);
    setNotifSending(false);
    setNotifMsgText("");
    setNotifBarMode(null);
    setNotifTarget("all");
  };

  // Helper: czy admin (nie superadmin) ma uprawnienie do danej funkcji (nie superadmin) ma uprawnienie do danej funkcji
  // superadmin zawsze ma dostęp; dla admin sprawdzamy settings.adminPermissions
  // Sprawdź uprawnienie dla bieżącego użytkownika (rola + nadpisania indywidualne)
  const rolePerm = (key) => {
    if (isSuperAdmin) return true;
    if (!key) return true; // brak klucza = nie blokuj
    const role = user?.role || "seller";
    const uid  = user?.id;
    // 1. Sprawdź nadpisanie indywidualne (najwyższy priorytet)
    const userPerms = uid ? ((settings.userPermissions||{})[uid]||{}) : {};
    if (key in userPerms) return userPerms[key] === true;
    // 2. Sprawdź uprawnienia roli (nowy system rolePermissions) — priorytet nad starym
    const rp = (settings.rolePermissions||{})[role]||{};
    if (key in rp) return rp[key] === true;
    // 3. Stary system adminPermissions (wsteczna kompatybilność) —
    //    TYLKO gdy klucz nie istnieje w rolePermissions (żeby nowy system mógł wyłączać)
    const ap = settings.adminPermissions||{};
    if (key in ap) return ap[key] !== false;
    // 4. Domyślnie: włączone
    return true;
  };
  // Alias wstecznej kompatybilności
  const adminPerm = (key) => rolePerm(key);
  const ac = settings.accentColor;

  // Główny administrator NIE widzi kont Super Administratora na liście użytkowników
  // (konta superadmin są w pełni aktywne — tylko niewidoczne dla admina)
  const visibleUsers = isSuperAdmin ? users : users.filter(u => u.role !== "superadmin");

  // sound notifications
  const { soundEnabled, setSoundEnabled, latestNewOrder, latestStaffCall, setLatestStaffCall } = useOrderNotification(orders, user.notifications, staffCalls);


  // alert nowego zamowienia (tylko sprzedawca i kucharz) - latestNewOrder pochodzi z hooka
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  useEffect(() => {
    if (latestNewOrder && (isSeller || isCook)) {
      setNewOrderAlert(latestNewOrder);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestNewOrder]);
  // tabs
  const [tab, setTab] = useState("orders");
  const [saSubTab, setSaSubTab] = useState("users");  // podkategoria Super Admin
  const [selDoc,   setSelDoc]   = useState("invoice"); // edytor szablonów dokumentów
  const [settSec,  setSettSec]  = useState("restaurant"); // sekcja ustawień
  const SETT_SECTIONS = [
    { id:"restaurant", icon:"🏢", label:"Dane restauracji" },
    { id:"appearance", icon:"🎨", label:"Wygląd" },
    { id:"orders",     icon:"🖥", label:"Panel klienta" },
    { id:"payments",   icon:"💳", label:"Platnosci" },
    { id:"integrations",icon:"⚙", label:"Zaawansowane" },
  ];
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  // bottom nav drag-to-scroll refs
  const bottomNavRef = useRef(null);
  const bottomNavDragging = useRef(false);
  const bottomNavStartX = useRef(0);
  const bottomNavScrollLeft = useRef(0);

  // image picker
  const [pickerCtx, setPickerCtx] = useState(null);

  // menu form
  const BLANK_ITEM = { name:"", catId:"", price:"", promoPrice:"", wait:"", emoji:"🍽", desc:"", ingredients:"", allergens:"", image:null, available:true };
  const [showAdd,  setShowAdd]  = useState(false);
  const [newItem,  setNewItem]  = useState(BLANK_ITEM);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});

  // menu search / category filter
  const [menuSearch, setMenuSearch] = useState("");
  const [menuCatFilter, setMenuCatFilter] = useState("all");

  // cat form
  const BLANK_CAT = { name:"", emoji:"🍽", parentId:"", image:null };
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCat,     setNewCat]     = useState(BLANK_CAT);
  const [editCat,    setEditCat]    = useState(null);
  const [editCForm,  setEditCForm]  = useState({});

  // user form
  const BLANK_USER = { login:"", passwordHash:"", name:"", role:"seller",
    notifications: ROLE_DEFS.seller.notifications };
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser,     setNewUser]     = useState(BLANK_USER);
  const [editUser,    setEditUser]    = useState(null);
  const [editUForm,   setEditUForm]   = useState({});

  // custom roles management
  const BLANK_ROLE = {
    id: "", label: "", icon: "👤", color: "#2563eb", bg: "#eff6ff",
    canChangePassword: false,
    notifications: { newOrder: true, orderIssued: false, newReservation: false, lowStock: false }
  };
  // eslint-disable-next-line no-unused-vars
  const [showRolesManager, setShowRolesManager] = useState(false);
  const [editRoleId,       setEditRoleId]       = useState(null);
  const [editRoleForm,     setEditRoleForm]      = useState({});
  const [newRoleForm,      setNewRoleForm]       = useState(BLANK_ROLE);
  const [showAddRole,      setShowAddRole]       = useState(false);

  // Merge built-in roles with custom roles for lookup
  const allRoleDefs = {
    ...ROLE_DEFS,
    ...Object.fromEntries((customRoles||[]).map(r => [r.id, r]))
  };

  // orders filter
  const [orderFilter, setOrderFilter] = useState("cookPending");
  const [archiveDate, setArchiveDate] = useState(todayStr());
  // manual order modal
  const [showManualOrder, setShowManualOrder] = useState(false);

  /* stats */
  const todayOrders  = useMemo(()=>orders.filter(o=>o.date===todayStr()),[orders]);
  const todayRevenue = useMemo(()=>todayOrders.reduce((s,o)=>s+o.total,0),[todayOrders]);
  const todayQty     = useMemo(()=>todayOrders.reduce((s,o)=>s+o.cart.reduce((a,c)=>a+c.qty,0),0),[todayOrders]);
  const topProducts  = useMemo(()=>{
    const m={};
    todayOrders.forEach(o=>o.cart.forEach(c=>{
      if(!m[c.name]) m[c.name]={name:c.name,emoji:c.emoji,image:c.image,qty:0,rev:0};
      m[c.name].qty+=c.qty; m[c.name].rev+=ep(c)*c.qty;
    }));
    return Object.values(m).sort((a,b)=>b.qty-a.qty).slice(0,5);
  },[todayOrders]);
  /* ── Drukowanie zamówienia ── */
  const printOrder = (o) => {
    const rName  = settings?.restaurantName || "Restauracja";
    const rAddr  = settings?.address || "";
    const ac2    = ac || "#e67e22";
    const now    = new Date().toLocaleString("pl-PL");
    const PAYS   = {cash:"💵 Gotówka",blik:"📱 BLIK",card:"💳 Karta",paypal:"🅿 PayPal",cod:"💵 Przy odbiorze"};
    const lines  = (o.cart||[]).map((c,i) => `
      <tr style="background:${i%2===0?"#fafafa":"#fff"}">
        <td style="padding:7px 10px">${c.emoji||""} ${c.name}${c.selectedAddons?.length?`<br/><small style="color:#888">+ ${c.selectedAddons.map(a=>a.name).join(", ")}</small>`:""}</td>
        <td style="padding:7px 10px;text-align:center">${c.qty||1} ${c.unit||"szt"}</td>
        <td style="padding:7px 10px;text-align:right;font-weight:700">${(((c.promoPrice??c.price)??0)*(c.qty||1)).toFixed(2)} zł</td>
      </tr>`).join("");

    const sourceLabel = o.source==="online"?"🌐 Sklep online":o.tableNumber==="takeaway"?"🥡 Na wynos":`🪑 Stolik #${o.tableNumber||"?"}`;
    const html = `<!DOCTYPE html><html lang="pl"><head><meta charset="UTF-8"/>
    <title>Zamówienie #${String(o.id||"").slice(-6)}</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:Arial,sans-serif;font-size:13px;color:#111;padding:20px 24px}
      .header{text-align:center;margin-bottom:16px;padding-bottom:12px;border-bottom:3px solid ${ac2}}
      .header h1{font-size:22px;font-weight:900;color:${ac2};margin-bottom:2px}
      .header p{font-size:11px;color:#666;line-height:1.6}
      .badge{display:inline-block;padding:3px 10px;border-radius:20px;font-weight:800;font-size:11px;color:#fff;background:${ac2};margin-bottom:8px}
      .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px}
      .box{background:#f9fafb;border-radius:8px;padding:10px 12px;border:1px solid #e5e7eb}
      .box h3{font-size:10px;font-weight:700;color:${ac2};text-transform:uppercase;letter-spacing:1px;margin-bottom:5px}
      .box p{font-size:12px;line-height:1.8;color:#222}
      table{width:100%;border-collapse:collapse;margin-bottom:10px}
      thead tr{background:${ac2};color:#fff}
      thead th{padding:7px 10px;text-align:left;font-size:12px;font-weight:700}
      thead th:last-child{text-align:right}
      .total-row{background:${ac2}22;font-weight:900}
      .total-row td{padding:9px 10px;border-top:2px solid ${ac2}}
      .footer{text-align:center;font-size:10px;color:#999;border-top:1px solid #e5e7eb;padding-top:10px;margin-top:14px}
      @media print{button{display:none!important}body{padding:10px}}
    </style></head><body>
    <div class="header">
      <h1>${rName}</h1>
      ${rAddr?`<p>${rAddr}</p>`:""}
      <p>Wydrukowano: ${now}</p>
    </div>
    <div class="badge">${sourceLabel}</div>
    <div class="info-grid">
      <div class="box">
        <h3>Zamówienie</h3>
        <p>Nr: <strong>${String(o.id||"").slice(-8)}</strong><br/>
        Data: ${o.date||""} ${o.time||""}<br/>
        Status: ${o.paid?"✅ Opłacone":"💸 Nieopłacone"}</p>
      </div>
      <div class="box">
        <h3>${o.firstName?"Klient":"Stolik"}</h3>
        <p>${o.firstName?`${o.firstName} ${o.lastName||""}<br/>${o.phone||""}${o.email?`<br/>${o.email}`:""}`:
          `Stolik ${o.tableNumber||"?"}`}</p>
      </div>
      ${o.address?`<div class="box" style="grid-column:span 2"><h3>Adres dostawy</h3><p>${o.address}${o.deliveryZone?`<br/>Strefa: ${o.deliveryZone}`:""}</p></div>`:""}
      ${o.note?`<div class="box" style="grid-column:span 2;border-color:#fed7aa;background:#fff7ed"><h3>Uwagi klienta</h3><p style="color:#92400e">${o.note}</p></div>`:""}
    </div>
    <table>
      <thead><tr><th>Pozycja</th><th style="text-align:center">Ilość</th><th style="text-align:right">Wartość</th></tr></thead>
      <tbody>${lines}</tbody>
      ${(o.deliveryCost||0)>0?`<tr style="background:#f3f4f6"><td colspan="2" style="padding:6px 10px;color:#555">🚗 Dostawa</td><td style="padding:6px 10px;text-align:right;font-weight:700">${Number(o.deliveryCost).toFixed(2)} zł</td></tr>`:""}
      <tr class="total-row"><td colspan="2" style="padding:9px 10px">RAZEM DO ZAPŁATY</td><td style="padding:9px 10px;text-align:right">${Number(o.total||0).toFixed(2)} zł</td></tr>
    </table>
    <div class="box">
      <p>Forma płatności: <strong>${PAYS[o.payment]||o.payment||"—"}</strong>${o.readyAt?`<br/>Na kiedy: <strong>${new Date(o.readyAt).toLocaleString("pl-PL",{dateStyle:"short",timeStyle:"short"})}</strong>`:""}</p>
    </div>
    <div class="footer">${rName} · Dziękujemy za zamówienie!</div>
    <div style="text-align:center;margin-top:14px">
      <button onclick="window.print()" style="padding:10px 28px;background:${ac2};color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:14px;font-weight:700">🖨 Drukuj / Zapisz PDF</button>
    </div>
    </body></html>`;
    const w = window.open("","_blank","width=700,height=800");
    w.document.write(html);
    w.document.close();
  };

  const filteredOrders = useMemo(()=>{
    // Archiwum — zamówienia oznaczone jako zarchiwizowane z wybranej daty
    if (orderFilter==="archive") {
      return orders
        .filter(o=>o.date===archiveDate && o.archivedAt)
        .slice().reverse();
    }
    let base = orders;
    // Dla sprzedawcy i kucharza w normalnych widokach — ukryj zarchiwizowane
    // Admin też nie widzi archiwum w normalnej liście (tylko w filtrze "Archiwum")
    if (isSeller || isCook || isAdmin) base = orders.filter(o=>!o.archivedAt);
    if (["today","pending","issued","cooking","ready","unpaid","cookPending","cookHistory"].includes(orderFilter))
      base = base.filter(o=>o.date===todayStr());
    if (orderFilter==="cookPending")  base = base.filter(o=>!o.issuedToSeller); // kucharz: do zrobienia
    if (orderFilter==="cookHistory")  base = base.filter(o=>o.issuedToSeller);  // kucharz: już wydane
    if (orderFilter==="pending")  base = base.filter(o=>!o.issued);
    if (orderFilter==="issued")   base = base.filter(o=>o.issued);
    if (orderFilter==="cooking")  base = base.filter(o=>o.cookStatus==="cooking");
    if (orderFilter==="ready")    base = base.filter(o=>o.cookStatus==="ready");
    if (orderFilter==="toSeller") base = base.filter(o=>o.issuedToSeller&&!o.issued);
    if (orderFilter==="bar")      base = base.filter(o=>o.issuedToSeller&&!o.issued&&(o.cart||[]).some(c=>c.serveAt==="bar"&&!c.issuedByBar));
    if (orderFilter==="unpaid")   base = base.filter(o=>!o.paid);
    return base.slice().reverse();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[orders,orderFilter,isSeller,isCook]);

  /* image picker logic */
  const pickerImg = () => {
    if (!pickerCtx) return null;
    if (pickerCtx.type==="newItem")   return newItem.image;
    if (pickerCtx.type==="editItem")  return editForm.image;
    if (pickerCtx.type==="quickImg")  return (menu.find(i=>i.id===pickerCtx.id)||{}).image||null;
    if (pickerCtx.type==="logo")      return settings.logo;
    if (pickerCtx.type==="customerBg") return settings.customerBgImage||null;
    if (pickerCtx.type==="specialsBg") return settings.dailySpecials?.bgImage||null;
    if (pickerCtx.type==="specialsHeader") return settings.dailySpecials?.headerImage||null;
    if (pickerCtx.type==="banner")    return ((settings[pickerCtx.key]||[]).find(b=>b.id===pickerCtx.id)||{}).image||null;
    if (pickerCtx.type==="campaign")  return ((campaigns||[]).find(cp=>cp.id===pickerCtx.id)||{}).image||null;
    if (pickerCtx.type==="cat")       return (cats.find(c=>c.id===pickerCtx.id)||{}).image||null;
    if (pickerCtx.type==="newCat")    return newCat.image||null;
    return null;
  };
  const onImgSave = img => {
    if (!pickerCtx) return;
    if (pickerCtx.type==="newItem")   setNewItem(p=>({...p,image:img}));
    if (pickerCtx.type==="editItem")  setEditForm(p=>({...p,image:img}));
    if (pickerCtx.type==="quickImg")  setMenu(p=>p.map(i=>i.id===pickerCtx.id?{...i,image:img}:i));
    if (pickerCtx.type==="logo")      setSettings(p=>({...p,logo:img}));
    if (pickerCtx.type==="customerBg") setSettings(p=>({...p,customerBgImage:img}));
    if (pickerCtx.type==="specialsBg") setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),bgImage:img}}));
    if (pickerCtx.type==="specialsHeader") setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),headerImage:img}}));
    if (pickerCtx.type==="banner")    setSettings(p=>({...p,[pickerCtx.key]:p[pickerCtx.key].map(b=>b.id===pickerCtx.id?{...b,image:img}:b)}));
    if (pickerCtx.type==="campaign")  setCampaigns(p=>p.map(cp=>cp.id===pickerCtx.id?{...cp,image:img}:cp));
    if (pickerCtx.type==="cat")       setCats(p=>p.map(c=>c.id===pickerCtx.id?{...c,image:img}:c));
    if (pickerCtx.type==="newCat")    setNewCat(p=>({...p,image:img}));
    setPickerCtx(null);
  };

  /* menu helpers */
  const addMenuItem = () => {
    if (!newItem.name.trim()) { alert("Wpisz nazwę produktu"); return; }
    if (!newItem.price || parseFloat(newItem.price) <= 0) { alert("Wpisz prawidłową cenę"); return; }
    setMenu(p=>[...p,{...newItem,id:genId(),active:true,available:newItem.available!==false,
      price:parseFloat(newItem.price)||0,wait:parseInt(newItem.wait)||10,
      promoPrice:newItem.promoPrice?parseFloat(newItem.promoPrice):null}]);
    logActivity(setLogs, user, "Dodał produkt do menu", newItem.name+" · "+fmt(parseFloat(newItem.price)||0)+" zł");
    setNewItem(BLANK_ITEM); setShowAdd(false);
  };
  const saveEditMenu = () => {
    const orig = menu.find(i=>i.id===editItem);
    setMenu(p=>p.map(i=>i.id===editItem?{...editForm,available:editForm.available!==false,
      price:parseFloat(editForm.price)||0,wait:parseInt(editForm.wait)||5,
      promoPrice:editForm.promoPrice?parseFloat(editForm.promoPrice):null}:i));
    logActivity(setLogs, user, "Edytował produkt menu", (orig?.name||editForm.name)+" · "+fmt(parseFloat(editForm.price)||0)+" zł");
    setEditItem(null);
  };
  const deleteMenuItem = (id) => {
    const item = menu.find(i => i.id === id);
    if (!item) return;
    if (!window.confirm(`Czy na pewno chcesz usunąć "${item.name}"?\nTej operacji nie można cofnąć.`)) return;
    setMenu(p => p.filter(i => i.id !== id));
    logActivity(setLogs, user, "Usunął produkt z menu", item.name);
  };

  /* cat helpers */
  const addCat = () => {
    if (!newCat.name) return;
    const id = newCat.name.toLowerCase().replace(/\s+/g,"-")+"-"+genId();
    setCats(p=>[...p,{...newCat,id,parentId:newCat.parentId||null}]);
    logActivity(setLogs, user, "Dodał kategorię menu", newCat.name);
    setNewCat(BLANK_CAT); setShowAddCat(false);
  };
  const saveEditCat = () => {
    const orig = cats.find(c=>c.id===editCat);
    setCats(p=>p.map(c=>c.id===editCat?{...editCForm,parentId:editCForm.parentId||null}:c));
    logActivity(setLogs, user, "Edytował kategorię menu", orig?.name||editCForm.name);
    setEditCat(null);
  };
  const deleteCat = id => {
    const cat = cats.find(c => c.id === id);
    const count = menu.filter(i => getDescendantIds(cats,id).includes(i.catId)).length;
    const msg = count > 0
      ? `Czy na pewno usunąć kategorię "${cat?.name}"?\nZawiera ${count} produktów — zostaną bez kategorii.`
      : `Czy na pewno usunąć kategorię "${cat?.name}"?`;
    if (!window.confirm(msg)) return;
    const toRemove=[id,...cats.filter(c=>c.parentId===id).map(c=>c.id)];
    setCats(p=>p.filter(c=>!toRemove.includes(c.id)));
    setMenu(p=>p.map(i=>toRemove.includes(i.catId)?{...i,catId:""}:i));
    logActivity(setLogs, user, "Usunął kategorię menu", cat?.name||id);
  };

  /* user helpers */
  const addUser = () => {
    if (!newUser.login.trim()) { alert("Wpisz login"); return; }
    if (!newUser.passwordHash) { alert("Wpisz hasło dla nowego użytkownika"); return; }
    if (users.find(u => u.login === newUser.login.trim())) { alert("Użytkownik o tym loginie już istnieje"); return; }
    // Tylko superadmin może tworzyć konta superadmin
    if (newUser.role === "superadmin" && !isSuperAdmin) {
      alert("🔒 Tylko Super Administrator może tworzyć konta Super Administratora.");
      return;
    }
    const userToAdd = {...newUser, login: newUser.login.trim(), id: genId()};
    delete userToAdd.password;
    setUsers(p => [...p, userToAdd]);
    setNewUser(BLANK_USER); setShowAddUser(false);
  };
  const saveEditUser = () => {
    // Obrona głęboka: główny administrator nie może zapisać zmian na koncie superadmina
    const target = users.find(u => u.id === editUser);
    if (target && target.role === "superadmin" && !isSuperAdmin) {
      alert("🔒 Tylko Super Administrator może edytować konto Super Administratora.");
      setEditUser(null);
      return;
    }
    // Obrona przed eskalacją roli: admin nie może nadać roli superadmin
    if (editUForm.role === "superadmin" && !isSuperAdmin) {
      alert("🔒 Tylko Super Administrator może nadawać rolę Super Administratora.");
      setEditUser(null);
      return;
    }
    const updated = {...editUForm};
    delete updated.password; // usuń ewentualny plain-text przy edycji
    setUsers(p => p.map(u => u.id === editUser ? updated : u));
    setEditUser(null);
  };

  const allCatOpts = cats.map(c=>({...c,label:c.parentId?`  ↳ ${c.name}`:c.name}));

  // Helper: czy dana zakładka jest widoczna dla roli użytkownika
  // superadmin zawsze widzi wszystko; dla pozostałych sprawdzamy settings.menuVisibility
  const isTabVisible = (key, role) => {
    if (isSuperAdmin) return true;
    const uid = user?.id;
    // 1. Nadpisanie indywidualne użytkownika
    const userPerms = uid ? ((settings.userPermissions||{})[uid]||{}) : {};
    // Mapowanie zakładek → klucze uprawnień
    const TAB_TO_PERM = {
      menu:"adminMenu", promo:"adminPromo", cats:"adminCats",
      tables:"adminTables", takeaway:"adminTakeaway", online:"adminOnlineOrders",
      clients:"adminClients", campaigns:"adminCampaigns", vouchers:"adminVouchers",
      sales:"adminSales", messages:"adminMessages", settings:"adminSettings",
      extras:"adminExtras", ksef:"adminKsef", nfccards:"adminNfcCards",
      warehouse:"adminWarehouse", specials:"adminSpecials",
      // seller
      "tables-s":"sellerOrders", "online-s":"sellerOnlineOrders",
    };
    const permKey = TAB_TO_PERM[key];
    if (permKey && uid && permKey in userPerms) {
      if (!userPerms[permKey]) return false;
    } else if (permKey) {
      const rp = (settings.rolePermissions||{})[role]||{};
      if (permKey in rp && !rp[permKey]) return false;
    }
    // 2. menuVisibility (zakładki widoczne dla roli)
    const mv = settings.menuVisibility || {};
    if (mv[role]?.[key] === true) return true;
    // 3. Dodatkowe role użytkownika
    const extraRoles = user?.extraRoles || [];
    for (const er of extraRoles) {
      if (mv[er]?.[key] === true) return true;
    }
    return false;
  };

  // Wszystkie możliwe zakładki dla roli admin i seller
  const ADMIN_TABS_ALL = [
    {key:"menu",     label:"🍽 Menu"},
    {key:"promo",    label:"🔥 Promocje"},
    {key:"cats",     label:"📂 Kategorie"},
    {key:"tables",   label:"🪑 Stoliki"},
    {key:"takeaway", label:"🥡 Na wynos"},
    {key:"online",   label:"🌐 Zamówienia online"},
    {key:"clients",  label:"👥 Kontrahenci"},
    {key:"campaigns",label:"📣 Kampanie"},
    {key:"vouchers", label:"🎟 Bony"},
    {key:"sales",    label:"📊 Sprzedaż"},
    {key:"nfccards", label:"💳 Karty logowania"},
    {key:"messages", label:"📧 Wiadomości"},
    {key:"specials", label:"⭐ Polecamy"},
    {key:"settings", label:"⚙ Ustawienia"},
    {key:"extras",   label:"🔩 Dodatkowe opcje"},
    {key:"ksef",     label:"🧾 KSeF"},
  ];
  const SELLER_TABS_ALL = [
    {key:"tables",   label:"🪑 Stoliki"},
    {key:"takeaway", label:"🥡 Na wynos"},
    {key:"online",   label:"🌐 Zamówienia online"},
    {key:"clients",  label:"👥 Kontrahenci"},
    {key:"specials", label:"⭐ Polecamy"},
  ];

  // Mapa klucza zakładki → klucz uprawnienia
  const TAB_PERM_MAP = {
    orders:"adminOrders", menu:"adminMenu", promo:"adminPromo", cats:"adminCats",
    tables:"adminTables", takeaway:"adminTakeaway", online:"adminOnlineOrders",
    clients:"adminClients", campaigns:"adminCampaigns", vouchers:"adminVouchers",
    sales:"adminSales", messages:"adminMessages", settings:"adminSettings",
    extras:"adminExtras", ksef:"adminKsef", warehouse:"adminWarehouse",
    specials:"adminSpecials", nfccards:"adminNfcCards",
  };
  const SELLER_PERM_MAP = {
    orders:"sellerOrders", tables:"sellerOrders", takeaway:"sellerTakeaway",
    online:"sellerOnlineOrders", clients:"sellerClients", specials:"adminSpecials",
  };
  const COOK_PERM_MAP = {
    orders:"cookOrders", tables:"cookOrders", takeaway:"cookOrders",
    clients:"cookClients",
  };

  const tabs = [
    {key:"orders",    label:"📋 Zamówienia"},
    ...(isSeller && !isAdmin
      ? SELLER_TABS_ALL.filter(t => isTabVisible(t.key, "seller") && rolePerm(SELLER_PERM_MAP[t.key]||null))
      : []),
    ...(isCook && !isAdmin && !isSeller
      ? ADMIN_TABS_ALL.filter(t => isTabVisible(t.key, "cook") && rolePerm(COOK_PERM_MAP[t.key]||null))
      : []),
    ...(isAdmin && !isSuperAdmin
      ? ADMIN_TABS_ALL.filter(t => isTabVisible(t.key, "admin") && rolePerm(TAB_PERM_MAP[t.key]||null))
      : []),
    ...(isSuperAdmin
      ? ADMIN_TABS_ALL
      : []),
    ...(isSuperAdmin?[
      {key:"superadmin", label:"🛡 Super Admin"},
    ]:[]),
  ];

  /* ── helpers for inline photo button in menu list ── */
  const PhotoBtn = ({ item }) => (
    <button onClick={()=>setPickerCtx({type:"quickImg",id:item.id})}
      style={{position:"absolute",bottom:-4,right:-4,background:item.image?ac:"#1a1a2e",color:"#fff",
        border:"2px solid #fff",borderRadius:"50%",width:21,height:21,fontSize:10,cursor:"pointer",
        display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>📷</button>
  );

  return (
    <>
      {newOrderAlert && (
        <NewOrderAlert
          order={newOrderAlert}
          isSeller={isSeller||isAdmin}
          onClose={() => setNewOrderAlert(null)}
          onAccept={(cartWithRouting) => {
            setOrders(p => p.map(o => o.id === newOrderAlert.id
              ? { ...o, cart: cartWithRouting, sellerAccepted: true,
                  sellerAcceptedAt: new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),
                  cookStatus: "new" }
              : o
            ));
            setNewOrderAlert(null);
          }}
        />
      )}
      {latestStaffCall && (
        <StaffCallAlert call={latestStaffCall} onClose={() => setLatestStaffCall(null)} />
      )}
      {showManualOrder && (
        <ManualOrderModal
          menu={menu} cats={cats} tables={tables} orders={orders}
          settings={settings} clients={clients||[]}
          onlineMenu={onlineMenu||[]} onlineCats={onlineCats||[]}
          setLogs={setLogs} currentUser={user}
          onPlace={o => {
            setOrders(p => [...p, o].slice(-500));
            if (o.isReservation && o.table) {
              setTables(p => p.map(t => t.id === o.table ? {
                ...t,
                reservation: {
                  name:   o.reservationName   || '',
                  phone:  o.reservationPhone  || '',
                  time:   o.reservationTime   || '',
                  guests: o.reservationGuests || 2,
                  note:   o.reservationNote   || '',
                  date:   o.reservationDate   || '',
                }
              } : t));
            }
            setShowManualOrder(false);
          }}
          onClose={() => setShowManualOrder(false)}
        />
      )}
    <div className="admin-panel" style={{display:"flex",height:"100vh",overflow:"hidden",background:TA.bg,fontFamily:"'Inter',system-ui,sans-serif"}}>
      {pickerCtx!==null&&(
        <ImagePickerModal
          title={pickerCtx.type==="logo"?"Logo restauracji":pickerCtx.type==="customerBg"?"Zdjecie tla":pickerCtx.type==="specialsBg"?"🖼 Tło modalu Polecamy":pickerCtx.type==="specialsHeader"?"🖼 Obrazek nagłówka Polecamy":pickerCtx.type==="banner"?"Zdjecie baneru":pickerCtx.type==="campaign"?"Zdjecie kampanii":pickerCtx.type==="cat"?"📂 Zdjęcie kategorii":pickerCtx.type==="newCat"?"📂 Zdjęcie kategorii":"Zdjecie produktu"}
          current={pickerImg()} onSave={onImgSave} onClose={()=>setPickerCtx(null)}
          imgbbApiKey={settings.imgbbApiKey||""}/>
      )}

      {/* SIDEBAR — na mobile: overlay drawer, na desktop: stały panel boczny */}
      {/* Backdrop na mobile gdy sidebar otwarty */}
      {!sidebarCollapsed && bp.isMobile && (
        <div
          onClick={() => setSidebarCollapsed(true)}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:49}}
        />
      )}
      <div className="admin-sidebar" style={{
        width: sidebarCollapsed ? (bp.isMobile ? 0 : 48) : 220,
        minWidth: sidebarCollapsed ? (bp.isMobile ? 0 : 48) : 220,
        background:"#16213e", display:"flex", flexDirection:"column",
        transition:"width .22s ease, min-width .22s ease",
        overflow:"hidden", flexShrink:0,
        zIndex: bp.isMobile ? 50 : 10,
        position: bp.isMobile ? "fixed" : "relative",
        top: bp.isMobile ? 0 : "auto",
        left: bp.isMobile ? 0 : "auto",
        bottom: bp.isMobile ? 0 : "auto",
        boxShadow: bp.isMobile && !sidebarCollapsed ? "4px 0 24px rgba(0,0,0,0.4)" : "none",
        borderRight:"1px solid rgba(255,255,255,0.06)"
      }}>
        {/* Logo area */}
        <div style={{height:56,borderBottom:"1px solid rgba(255,255,255,0.07)",flexShrink:0,display:"flex",alignItems:"center",padding:"0 8px",gap:8}}>
          <div style={{width:32,height:32,borderRadius:7,background:"#2563eb",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:700,color:"#fff",flexShrink:0}}>
            {settings.logo ? <img src={settings.logo} alt="" style={{width:32,height:32,borderRadius:7,objectFit:"cover"}}/> : "G"}
          </div>
          {!sidebarCollapsed&&(
            <div style={{flex:1,overflow:"hidden",minWidth:0}}>
              <div style={{color:"#fff",fontWeight:700,fontSize:13,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{settings.restaurantName||"System POS"}</div>
              <div style={{color:"rgba(255,255,255,0.35)",fontSize:10}}>Panel zarządzania</div>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav style={{flex:1,overflowY:"auto",overflowX:"hidden",padding:"8px 0"}}>
          {[
            {key:"orders",    icon:"📋", label:"Zamówienia"},
            {key:"tables",    icon:"🪑", label:"Stoliki"},
            {key:"takeaway",  icon:"🥡", label:"Na wynos"},
            {key:"online",    icon:"🌐", label:"Zamówienia online"},
            {key:"clients",   icon:"👥", label:"Klienci"},
            {key:"specials",  icon:"⭐", label:"Polecamy"},
            {key:"menu",      icon:"🍽", label:"Menu"},
            {key:"cats",      icon:"📂", label:"Kategorie"},
            {key:"promo",     icon:"🔥", label:"Promocje"},
            {key:"campaigns", icon:"📣", label:"Kampanie"},
            {key:"vouchers",  icon:"🎟", label:"Bony"},
            {key:"sales",     icon:"📊", label:"Sprzedaz"},
            {key:"nfccards",  icon:"💳", label:"Karty NFC"},
            {key:"messages",  icon:"📧", label:"Wiadomości"},
            {key:"settings",  icon:"⚙", label:"Ustawienia"},
            {key:"extras",    icon:"🔩", label:"Dodatkowe"},
            {key:"ksef",      icon:"🧾", label:"KSeF"},
            {key:"superadmin", icon:"🛡", label:"Super Admin"},
          ].filter(item => {
            if (item.key === "orders") return true;
            if (item.key === "superadmin") return isSuperAdmin;
            if (!isSuperAdmin) {
              // Sprawdź czy zakładka jest widoczna (menuVisibility) I czy uprawnienie jest włączone
              if (!isTabVisible(item.key, user.role)) return false;
              return rolePerm(({menu:"adminMenu",promo:"adminPromo",cats:"adminCats",tables:"adminTables",takeaway:"adminTakeaway",online:"adminOnlineOrders",clients:"adminClients",campaigns:"adminCampaigns",vouchers:"adminVouchers",sales:"adminSales",messages:"adminMessages",settings:"adminSettings",extras:"adminExtras",ksef:"adminKsef",warehouse:"adminWarehouse",specials:"adminSpecials"}[item.key])||null);
            }
            // superadmin — pokaż tylko zakładki z ADMIN_TABS_ALL (bez tych przeniesionych do Super Admin)
            const superAdminHidden = ["users","hardware","backup","logs","database"];
            if (superAdminHidden.includes(item.key)) return false;
            return true;
          }).map(item=>{
            const isAct = tab===item.key;
            let cnt = 0;
            if(item.key==="orders"){
              const today=orders.filter(o=>o.date===todayStr());
              cnt=isCook?today.filter(o=>o.cookStatus==="ready"&&!o.issued).length
                :today.filter(o=>!o.issued).length;
              cnt+=(staffCalls||[]).filter(c=>!c.dismissed).length;
            }
            return (
              <button key={item.key} onClick={()=>{setTab(item.key); logActivity(setLogs, user, "Wejście w zakładkę", item.label);}}
                title={sidebarCollapsed?item.label:""}
                style={{
                  display:"flex",alignItems:"center",
                  width:"100%",padding: sidebarCollapsed?"10px 0":"9px 16px",
                  justifyContent: sidebarCollapsed?"center":"flex-start",
                  gap:10,border:"none",cursor:"pointer",
                  background: isAct?"#2563eb":"transparent",
                  color: isAct?"#fff":"rgba(255,255,255,0.6)",
                  fontWeight: isAct?600:400,fontSize:13,
                  textAlign:"left",transition:"all .12s",
                  borderLeft: isAct&&!sidebarCollapsed?"3px solid rgba(255,255,255,0.3)":"3px solid transparent",
                  position:"relative"
                }}>
                <span style={{fontSize:16,flexShrink:0,lineHeight:1}}>{item.icon}</span>
                {!sidebarCollapsed&&<span style={{flex:1,whiteSpace:"nowrap"}}>{item.label}</span>}
                {cnt>0&&!sidebarCollapsed&&<span style={{background:isAct?"rgba(255,255,255,0.25)":"#ef4444",color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:10,fontWeight:700}}>{cnt}</span>}
                {cnt>0&&sidebarCollapsed&&<span style={{position:"absolute",top:5,right:8,width:8,height:8,borderRadius:"50%",background:"#ef4444"}}/>}
              </button>
            );
          })}
        </nav>

        {/* User + actions */}
        <div style={{padding:"8px 8px 12px",borderTop:"1px solid rgba(255,255,255,0.07)",flexShrink:0}}>
          {/* Toggle button — zawsze widoczny */}
          <button
            onClick={()=>setSidebarCollapsed(p=>!p)}
            style={{
              width:"100%", padding:"8px 0",
              background:"rgba(255,255,255,0.08)",
              border:"1px solid rgba(255,255,255,0.15)",
              borderRadius:8, cursor:"pointer",
              color:"#fff", fontSize:16, fontWeight:700,
              display:"flex", alignItems:"center",
              justifyContent: sidebarCollapsed ? "center" : "flex-end",
              paddingRight: sidebarCollapsed ? 0 : 12,
              transition:"all .15s", marginBottom:8, userSelect:"none"
            }}>
            {!sidebarCollapsed && <span style={{flex:1,paddingLeft:12,fontSize:12,color:"rgba(255,255,255,0.5)",fontWeight:400}}>Zwiń menu</span>}
            <span style={{fontSize:18}}>{sidebarCollapsed ? "›" : "‹"}</span>
          </button>

          {!sidebarCollapsed&&(
            <div style={{display:"flex",alignItems:"center",gap:9,padding:"4px 4px 8px",overflow:"hidden"}}>
              <div style={{width:30,height:30,borderRadius:"50%",background:"#2563eb",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#fff",flexShrink:0}}>
                {user.name?user.name[0].toUpperCase():"U"}
              </div>
              <div style={{minWidth:0}}>
                <div style={{color:"#fff",fontWeight:600,fontSize:12,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{user.name}</div>
                <div style={{color:"rgba(255,255,255,0.35)",fontSize:10}}>{(ROLE_DEFS[user.role]||ROLE_DEFS.seller).label}</div>
              </div>
            </div>
          )}
          <div style={{display:"flex",gap:5,justifyContent:sidebarCollapsed?"center":"stretch"}}>
            <button onClick={()=>setSoundEnabled(p=>!p)} title={soundEnabled?"Wycisz":"Dzwiek"} style={{
              padding:"6px",background:"rgba(255,255,255,0.06)",border:"none",borderRadius:6,
              cursor:"pointer",color:soundEnabled?"rgba(255,255,255,0.7)":"rgba(255,255,255,0.2)",
              fontSize:14,width:sidebarCollapsed?36:"auto",flexShrink:0
            }}>{soundEnabled?"🔔":"🔕"}</button>
            {!sidebarCollapsed&&<button onClick={onLogout} style={{
              flex:1,padding:"6px 8px",background:"rgba(239,68,68,0.15)",border:"none",
              borderRadius:6,cursor:"pointer",color:"#fca5a5",fontSize:11,fontWeight:500
            }}>Wyloguj</button>}
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minWidth:0}}>
        {/* ════ PAGE HEADER ════ */}
        <div style={{background:"#fff",borderBottom:"1px solid "+TA.border,padding:"0 16px",
          height:56,display:"flex",alignItems:"center",justifyContent:"space-between",
          flexShrink:0,boxShadow:"0 1px 3px rgba(0,0,0,0.04)",gap:10}}>

          {/* Lewa: hamburger + tytuł */}
          <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0,flex:1}}>
            {bp.isMobile && (
              <button onClick={()=>setSidebarCollapsed(p=>!p)}
                style={{width:38,height:38,flexShrink:0,background:"#f0f2f5",
                  border:"1px solid "+TA.border,borderRadius:8,cursor:"pointer",fontSize:18,
                  display:"flex",alignItems:"center",justifyContent:"center",color:TA.text}}
                aria-label="Otwórz menu">☰</button>
            )}
            <h2 style={{margin:0,fontSize:17,fontWeight:700,color:TA.text,letterSpacing:-0.2,
              whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {tabs.find(t=>t.key===tab)?.label||""}
            </h2>
          </div>

          {/* Prawa: tylko "Nowe zamówienie" */}
          <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
            {(isSeller||isAdmin)&&tab==="orders"&&rolePerm(isCook?"cookOrders":isSeller?"sellerOrders":"adminOrders")&&(
              <button onClick={()=>setShowManualOrder(true)} style={{
                padding:"8px 16px",background:TA.accent,color:"#fff",border:"none",
                borderRadius:8,fontWeight:600,cursor:"pointer",fontSize:13,flexShrink:0,
                display:"flex",alignItems:"center",gap:7,
                boxShadow:"0 2px 8px rgba(37,99,235,0.3)"}}>
                + Nowe zamówienie
              </button>
            )}
          </div>
        </div>

        {/* ════ NOTIFICATION BAR — pod headerem, styl jak bottom-nav ════ */}
        {(()=>{
          const todayOrd   = orders.filter(o=>o.date===todayStr());
          const newOrdCnt  = isCook
            ? todayOrd.filter(o=>o.cookStatus==="ready"&&!o.issued).length
            : todayOrd.filter(o=>!o.issued).length;
          const staffCnt   = (staffCalls||[]).filter(c=>!c.dismissed).length;
          const occupiedCnt= (tables||[]).filter(t=>
            t.active&&!t.manuallyFreed&&
            (t.occupied||todayOrd.some(o=>o.table===t.id||o.table===t.number))
          ).length;

          const TARGET_OPTS = [
            { key:"all",    label:"Wszyscy",    icon:"👥" },
            { key:"seller", label:"Sprzedawca", icon:"🏷" },
            { key:"cook",   label:"Kucharz",    icon:"👨‍🍳" },
            { key:"admin",  label:"Admin",      icon:"👑" },
          ];

          return (
            <>
              {/* ── Pasek powiadomień ── */}
              <div style={{
                background:TA.primary, borderBottom:"1px solid rgba(255,255,255,0.07)",
                display:"flex", alignItems:"stretch", flexShrink:0,
                overflowX:"auto", scrollbarWidth:"none",
              }}>

                {/* Badges — zamówienia */}
                {newOrdCnt > 0 && (
                  <button onClick={()=>setTab("orders")}
                    style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                      padding:"5px 12px",border:"none",cursor:"pointer",background:"transparent",
                      borderTop:"3px solid #ef4444",color:"#fff",gap:1,flexShrink:0}}>
                    <span style={{fontSize:15}}>🛍</span>
                    <span style={{fontSize:8,fontWeight:700,whiteSpace:"nowrap",
                      background:"#ef4444",borderRadius:8,padding:"0 5px",lineHeight:"13px"}}>
                      {newOrdCnt} zam.
                    </span>
                  </button>
                )}

                {/* Badges — wezwania obsługi */}
                {staffCnt > 0 && (
                  <button onClick={()=>setTab("orders")}
                    style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                      padding:"5px 12px",border:"none",cursor:"pointer",background:"transparent",
                      borderTop:"3px solid #f59e0b",color:"#fff",gap:1,flexShrink:0}}>
                    <span style={{fontSize:15}}>🔔</span>
                    <span style={{fontSize:8,fontWeight:700,whiteSpace:"nowrap",
                      background:"#f59e0b",borderRadius:8,padding:"0 5px",lineHeight:"13px"}}>
                      {staffCnt} wezw.
                    </span>
                  </button>
                )}

                {/* Separator */}
                {(newOrdCnt>0||staffCnt>0) && (
                  <div style={{width:1,background:"rgba(255,255,255,0.1)",margin:"6px 4px",flexShrink:0}}/>
                )}

                {/* 🪑 Stoliki — przycisk otwierający panel (ukryty dla kucharza) */}
                {!isCook && (
                  <>
                    <button
                      onClick={()=>setNotifBarMode(p=>p==="tables"?null:"tables")}
                      style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                        padding:"5px 14px",border:"none",cursor:"pointer",flexShrink:0,
                        background: notifBarMode==="tables"?"rgba(230,126,34,0.25)":"transparent",
                        borderTop: notifBarMode==="tables"?"3px solid #e67e22":"3px solid transparent",
                        color: notifBarMode==="tables"?"#fb923c":"rgba(255,255,255,0.65)",
                        transition:"all .12s",gap:1,position:"relative"}}>
                      <span style={{fontSize:15}}>🪑</span>
                      <span style={{fontSize:8,fontWeight:600,whiteSpace:"nowrap"}}>Stoliki</span>
                      {occupiedCnt > 0 && (
                        <span style={{position:"absolute",top:3,right:4,background:"#e67e22",
                          color:"#fff",borderRadius:8,padding:"0 4px",fontSize:7,fontWeight:800,lineHeight:"12px"}}>
                          {occupiedCnt}
                        </span>
                      )}
                    </button>
                    <div style={{width:1,background:"rgba(255,255,255,0.1)",margin:"6px 4px",flexShrink:0}}/>
                  </>
                )}

                {/* 🔔 Dzwonek — wyślij przypomnienie */}
                <button
                  onClick={()=>setNotifBarMode(p=>p==="bell"?null:"bell")}
                  style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                    padding:"5px 14px",border:"none",cursor:"pointer",flexShrink:0,
                    background: notifBarMode==="bell"?"rgba(217,119,6,0.25)":"transparent",
                    borderTop: notifBarMode==="bell"?"3px solid #d97706":"3px solid transparent",
                    color: notifBarMode==="bell"?"#fbbf24":"rgba(255,255,255,0.65)",
                    transition:"all .12s",gap:1}}>
                  <span style={{fontSize:15}}>🔔</span>
                  <span style={{fontSize:8,fontWeight:600,whiteSpace:"nowrap"}}>Przypomnij</span>
                </button>

                {/* ✉ Wiadomość */}
                <button
                  onClick={()=>setNotifBarMode(p=>p==="msg"?null:"msg")}
                  style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                    padding:"5px 14px",border:"none",cursor:"pointer",flexShrink:0,
                    background: notifBarMode==="msg"?"rgba(37,99,235,0.25)":"transparent",
                    borderTop: notifBarMode==="msg"?"3px solid #2563eb":"3px solid transparent",
                    color: notifBarMode==="msg"?"#93c5fd":"rgba(255,255,255,0.65)",
                    transition:"all .12s",gap:1}}>
                  <span style={{fontSize:15}}>✉</span>
                  <span style={{fontSize:8,fontWeight:600,whiteSpace:"nowrap"}}>Wiadomość</span>
                </button>
              </div>

              {/* ── Panel wyboru odbiorcy (dzwonek) ── */}
              {notifBarMode==="bell" && (
                <div style={{background:"#1e293b",borderBottom:"1px solid rgba(255,255,255,0.08)",
                  padding:"10px 16px",display:"flex",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>
                  <span style={{fontSize:12,color:"rgba(255,255,255,0.6)",fontWeight:600,flexShrink:0}}>
                    🔔 Powiadom:
                  </span>
                  <div style={{display:"flex",gap:6,flex:1,flexWrap:"wrap"}}>
                    {TARGET_OPTS.map(o=>(
                      <button key={o.key} onClick={()=>setNotifTarget(o.key)}
                        style={{display:"flex",alignItems:"center",gap:5,
                          padding:"5px 12px",borderRadius:20,border:"2px solid",
                          cursor:"pointer",fontSize:12,fontWeight:700,transition:"all .12s",
                          borderColor: notifTarget===o.key?"#d97706":"rgba(255,255,255,0.15)",
                          background:  notifTarget===o.key?"rgba(217,119,6,0.3)":"rgba(255,255,255,0.06)",
                          color:       notifTarget===o.key?"#fbbf24":"rgba(255,255,255,0.55)"}}>
                        <span>{o.icon}</span><span>{o.label}</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={handleSendBell} disabled={notifSending}
                    style={{padding:"7px 18px",borderRadius:8,border:"none",cursor:"pointer",
                      background: notifSending?"#6b7280":"linear-gradient(135deg,#d97706,#b45309)",
                      color:"#fff",fontWeight:700,fontSize:12,flexShrink:0,
                      boxShadow:"0 2px 8px rgba(180,83,9,0.4)"}}>
                    {notifSending?"⏳":"🔔 Wyślij"}
                  </button>
                  <button onClick={()=>setNotifBarMode(null)}
                    style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",
                      borderRadius:6,padding:"5px 10px",cursor:"pointer",color:"rgba(255,255,255,0.5)",fontSize:12}}>
                    ✕
                  </button>
                </div>
              )}

              {/* ── Panel stolików — podgląd i szybkie zwalnianie/zajmowanie ── */}
              {notifBarMode==="tables" && (
                <div style={{background:"#1a2540",borderBottom:"1px solid rgba(255,255,255,0.08)",
                  padding:"10px 16px",flexShrink:0}}>

                  {/* Nagłówek + legenda */}
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10,flexWrap:"wrap"}}>
                    <span style={{fontSize:12,color:"rgba(255,255,255,0.7)",fontWeight:700}}>🪑 Stan stolików</span>
                    <div style={{display:"flex",gap:8,alignItems:"center",fontSize:10,color:"rgba(255,255,255,0.45)"}}>
                      <span style={{display:"flex",alignItems:"center",gap:3}}>
                        <span style={{width:10,height:10,borderRadius:3,background:"#ef4444",display:"inline-block"}}/>
                        Zajęty
                      </span>
                      <span style={{display:"flex",alignItems:"center",gap:3}}>
                        <span style={{width:10,height:10,borderRadius:3,background:"#22c55e",display:"inline-block"}}/>
                        Wolny
                      </span>
                      <span style={{display:"flex",alignItems:"center",gap:3}}>
                        <span style={{width:10,height:10,borderRadius:3,background:"#f59e0b",display:"inline-block"}}/>
                        Rezerwacja
                      </span>
                    </div>
                    <button onClick={()=>setNotifBarMode(null)}
                      style={{marginLeft:"auto",background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",
                        borderRadius:6,padding:"4px 10px",cursor:"pointer",color:"rgba(255,255,255,0.5)",fontSize:11}}>
                      ✕
                    </button>
                  </div>

                  {/* Siatka stolików */}
                  {(()=>{
                    const todayO = orders.filter(o=>o.date===todayStr());
                    const isOcc  = t => t.manuallyFreed ? false
                      : (t.occupied || todayO.some(o=>o.table===t.id||o.table===t.number));
                    const activeTbls = (tables||[]).filter(t=>t.active)
                      .sort((a,b)=>a.number-b.number);

                    // Pogrupuj po typie (indoor / outdoor)
                    const indoor  = activeTbls.filter(t=>t.type!=="outdoor");
                    const outdoor = activeTbls.filter(t=>t.type==="outdoor");

                    const renderGroup = (list, label) => list.length === 0 ? null : (
                      <div style={{marginBottom: outdoor.length>0?8:0}}>
                        {outdoor.length>0 && indoor.length>0 && (
                          <div style={{fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.35)",
                            textTransform:"uppercase",letterSpacing:1,marginBottom:5}}>
                            {label}
                          </div>
                        )}
                        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                          {list.map(t=>{
                            const occ  = isOcc(t);
                            const res  = !!t.reservation && !occ;
          // eslint-disable-next-line no-unused-vars

          // eslint-disable-next-line no-unused-vars
                            const bg    = occ ? "#ef4444" : res ? "#f59e0b" : "#22c55e";
          // eslint-disable-next-line no-unused-vars
                            const icon  = occ ? "🍽" : res ? "📅" : "✓";
          // eslint-disable-next-line no-unused-vars

                            return (
                              <div key={t.id} style={{
                                display:"flex",flexDirection:"column",alignItems:"center",
                                gap:3, minWidth:52,
                              }}>
                                {/* Kafelek stolika */}
                                <div style={{
                                  width:52,height:48,borderRadius:10,
                                  background: occ
                                    ? "linear-gradient(135deg,#ef4444,#b91c1c)"
                                    : res
                                      ? "linear-gradient(135deg,#f59e0b,#d97706)"
                                      : "linear-gradient(135deg,#22c55e,#16a34a)",
                                  display:"flex",flexDirection:"column",alignItems:"center",
                                  justifyContent:"center",gap:2,
                                  boxShadow: occ
                                    ? "0 2px 8px rgba(239,68,68,0.4)"
                                    : res
                                      ? "0 2px 8px rgba(245,158,11,0.4)"
                                      : "0 2px 8px rgba(34,197,94,0.3)",
                                  border:"1.5px solid rgba(255,255,255,0.15)",
                                  animation: occ ? "none" : "none",
                                }}>
                                  <span style={{fontSize:11,lineHeight:1}}>{icon}</span>
                                  <span style={{fontSize:11,fontWeight:900,color:"#fff",lineHeight:1}}>
                                    #{t.number}
                                  </span>
                                  <span style={{fontSize:7,color:"rgba(255,255,255,0.85)",lineHeight:1}}>
                                    {t.seats||4} os.
                                  </span>
                                </div>

                                {/* Przycisk akcji */}
                                <button
                                  onClick={()=> occ
                                    ? setTables(p=>p.map(x=>x.id===t.id?{...x,occupied:false,manuallyFreed:true}:x))
                                    : setTables(p=>p.map(x=>x.id===t.id?{...x,occupied:true,manuallyFreed:false}:x))
                                  }
                                  title={occ ? "Zwolnij stolik #"+t.number : "Zajmij stolik #"+t.number}
                                  style={{
                                    width:52,padding:"2px 0",border:"none",borderRadius:6,cursor:"pointer",
                                    background: occ ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)",
                                    color: occ ? "#4ade80" : "#fca5a5",
                                    fontSize:9,fontWeight:700,lineHeight:"14px",
                                    transition:"all .12s",
                                  }}
                                  onMouseEnter={e=>{e.currentTarget.style.background=occ?"rgba(34,197,94,0.35)":"rgba(239,68,68,0.35)";}}
                                  onMouseLeave={e=>{e.currentTarget.style.background=occ?"rgba(34,197,94,0.2)":"rgba(239,68,68,0.2)";}}
                                >
                                  {occ ? "✓ Zwolnij" : "＋ Zajmij"}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );

                    return (
                      <div>
                        {renderGroup(indoor,  "🏠 Wewnętrzne")}
                        {renderGroup(outdoor, "🌿 Zewnętrzne")}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* ── Panel wiadomości ── */}
              {notifBarMode==="msg" && (
                <div style={{background:"#1e293b",borderBottom:"1px solid rgba(255,255,255,0.08)",
                  padding:"10px 16px",display:"flex",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>
                  <span style={{fontSize:12,color:"rgba(255,255,255,0.6)",fontWeight:600,flexShrink:0}}>
                    ✉ Do:
                  </span>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {TARGET_OPTS.map(o=>(
                      <button key={o.key} onClick={()=>setNotifTarget(o.key)}
                        style={{display:"flex",alignItems:"center",gap:5,
                          padding:"5px 12px",borderRadius:20,border:"2px solid",
                          cursor:"pointer",fontSize:12,fontWeight:700,transition:"all .12s",
                          borderColor: notifTarget===o.key?"#2563eb":"rgba(255,255,255,0.15)",
                          background:  notifTarget===o.key?"rgba(37,99,235,0.3)":"rgba(255,255,255,0.06)",
                          color:       notifTarget===o.key?"#93c5fd":"rgba(255,255,255,0.55)"}}>
                        <span>{o.icon}</span><span>{o.label}</span>
                      </button>
                    ))}
                  </div>
                  <input
                    value={notifMsgText}
                    onChange={e=>setNotifMsgText(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&handleSendMsg()}
                    placeholder="Wpisz wiadomość... (Enter = wyślij)"
                    style={{flex:1,minWidth:160,padding:"7px 12px",borderRadius:8,
                      border:"1.5px solid rgba(255,255,255,0.2)",background:"rgba(255,255,255,0.08)",
                      color:"#fff",fontSize:13,outline:"none",
                      fontFamily:"'Inter',system-ui,sans-serif"}}
                    autoFocus
                  />
                  <button onClick={handleSendMsg} disabled={!notifMsgText.trim()||notifSending}
                    style={{padding:"7px 18px",borderRadius:8,border:"none",cursor:"pointer",
                      background: (!notifMsgText.trim()||notifSending)?"#6b7280":"linear-gradient(135deg,#2563eb,#1d4ed8)",
                      color:"#fff",fontWeight:700,fontSize:12,flexShrink:0,
                      boxShadow:(!notifMsgText.trim()||notifSending)?"none":"0 2px 8px rgba(37,99,235,0.4)"}}>
                    {notifSending?"⏳":"✉ Wyślij"}
                  </button>
                  <button onClick={()=>{setNotifBarMode(null);setNotifMsgText("");}}
                    style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.15)",
                      borderRadius:6,padding:"5px 10px",cursor:"pointer",color:"rgba(255,255,255,0.5)",fontSize:12}}>
                    ✕
                  </button>
                </div>
              )}

              {/* ── Toast — przychodzące powiadomienie ── */}
              {incomingAlert && (
                <div style={{position:"fixed",top:70,right:16,zIndex:9000,
                  background: incomingAlert.type==="reminder"
                    ? "linear-gradient(135deg,#92400e,#b45309)"
                    : "linear-gradient(135deg,#1e3a8a,#2563eb)",
                  color:"#fff",borderRadius:14,padding:"14px 18px",maxWidth:340,
                  boxShadow:"0 8px 32px rgba(0,0,0,0.4)",animation:"fadeSlide .3s ease",
                  display:"flex",flexDirection:"column",gap:8,
                  border:"1.5px solid rgba(255,255,255,0.15)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:26}}>
                      {incomingAlert.type==="reminder"?"🔔":"✉"}
                    </span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,fontSize:13}}>
                        {incomingAlert.type==="reminder"?"Przypomnienie!":"Wiadomość od obsługi!"}
                      </div>
                      <div style={{fontSize:11,opacity:0.8,marginTop:2}}>
                        od: <strong>{incomingAlert.from}</strong>
                      </div>
                    </div>
                    <button onClick={()=>setIncomingAlert(null)}
                      style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:"50%",
                        width:26,height:26,cursor:"pointer",color:"#fff",fontSize:14,
                        display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
                  </div>
                  {incomingAlert.msg && (
                    <div style={{background:"rgba(255,255,255,0.15)",borderRadius:9,
                      padding:"9px 12px",fontSize:13,lineHeight:1.5,fontWeight:500}}>
                      {incomingAlert.msg}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}

        {/* Tab content */}
        <div className="admin-content-pad" style={{flex:1,overflowY:"auto",padding: bp.isDesktop?24:14,background:TA.bg}}>

        {/* ── ORDERS ── */}
        {tab==="orders"&&rolePerm(isCook?"cookOrders":isSeller&&!isAdmin?"sellerOrders":"adminOrders")&&(
          <div>
            {/* ── Wezwania obsługi ── */}
            {(staffCalls||[]).filter(c=>!c.dismissed).map(call=>(
              <div key={call.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",
                background:"linear-gradient(135deg,#fff8e1,#fff3cd)",border:"2px solid #f39c12",
                borderRadius:14,marginBottom:10,boxShadow:"0 2px 10px rgba(243,156,18,0.2)",animation:"fadeSlide .3s ease"}}>
                <div style={{fontSize:26,flexShrink:0,animation:"popIn .4s ease"}}>🔔</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:800,fontSize:13,color:"#7d5a00"}}>Klient wzywa obsługę!</div>
                  <div style={{fontSize:12,color:"#a07010",marginTop:2}}>
                    Stolik <strong style={{fontSize:14}}>#{call.tableNumber}</strong> · godz. {call.time}
                  </div>
                </div>
                <button
                  onClick={()=>setStaffCalls(p=>p.map(c=>c.id===call.id?{...c,dismissed:true}:c))}
                  style={{padding:"8px 14px",background:"#f39c12",color:"#fff",border:"none",
                    borderRadius:9,fontWeight:800,cursor:"pointer",fontSize:12,flexShrink:0,
                    boxShadow:"0 2px 6px rgba(243,156,18,0.4)"}}>
                  ✓ Przyjęto
                </button>
              </div>
            ))}
            {/* Sound status indicator */}
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,padding:"8px 12px",
              background:soundEnabled?"#f0fdf4":T.bg,borderRadius:10,border:"1px solid "+(soundEnabled?"#86efac":T.border)}}>
              <span style={{fontSize:16}}>{soundEnabled?"🔔":"🔕"}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:soundEnabled?"#15803d":T.muted}}>
                  {soundEnabled?"Powiadomienia dźwiękowe włączone":"Powiadomienia dźwiękowe wyłączone"}
                </div>
                <div style={{fontSize:10,color:T.muted}}>
                  {soundEnabled?"Usłyszysz dźwięk gdy klient złoży zamówienie":"Kliknij 🔔 w nagłówku aby włączyć"}
                </div>
              </div>
              <button onClick={()=>setSoundEnabled(p=>!p)}
                style={{padding:"5px 12px",background:soundEnabled?"#dcfce7":"#f0f2f5",color:soundEnabled?"#15803d":T.muted,
                  border:"none",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:11}}>
                {soundEnabled?"Wycisz":"Włącz"}
              </button>
            </div>

            {/* Przycisk ręcznego zamówienia */}
            {(isAdmin || isSeller) && (
              <button
                onClick={() => setShowManualOrder(true)}
                style={{width:"100%",padding:"11px 16px",marginBottom:12,
                  background:"linear-gradient(135deg,"+T.primary+","+ac+")",
                  color:"#fff",border:"none",borderRadius:12,fontWeight:800,fontSize:14,
                  cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                  gap:8,boxShadow:"0 3px 12px rgba(107,63,30,0.3)",letterSpacing:0.2}}>
                ➕ Nowe zamówienie od obsługi
              </button>
            )}

            {/* ══════════ COOK VIEW — duże karty kuchenne (2 kolumny) ══════════ */}
            {isCook ? (
              <div>
                {(()=>{
                  const todayBase = orders.filter(o=>o.date===todayStr()&&!o.archivedAt);
                  const pending = todayBase.filter(o=>!o.issuedToSeller).length;
                  const issued  = todayBase.filter(o=>o.issuedToSeller).length;
                  return (
                    <>
                {/* Liczniki */}
                    <div style={{display:"flex",gap:10,marginBottom:18}}>
                      <div style={{flex:1,background:pending>0?"#fff3cd":"#f0fdf4",borderRadius:14,padding:"14px 16px",
                        border:"2px solid "+(pending>0?"#f39c12":"#86efac"),textAlign:"center",cursor:"pointer"}}
                        onClick={()=>setOrderFilter("cookPending")}>
                        <div style={{fontSize:28,lineHeight:1}}>{pending>0?"⏳":"✅"}</div>
                        <div style={{fontWeight:900,fontSize:28,color:pending>0?"#f39c12":"#27ae60",marginTop:2}}>{pending}</div>
                        <div style={{fontSize:12,color:T.muted,fontWeight:700}}>do przygotowania</div>
                      </div>
                      <div style={{flex:1,background:"#e8f4fd",borderRadius:14,padding:"14px 16px",
                        border:"2px solid #bae6fd",textAlign:"center",cursor:"pointer"}}
                        onClick={()=>setOrderFilter("cookHistory")}>
                        <div style={{fontSize:28,lineHeight:1}}>🍽</div>
                        <div style={{fontWeight:900,fontSize:28,color:"#0984e3",marginTop:2}}>{issued}</div>
                        <div style={{fontSize:12,color:T.muted,fontWeight:700}}>wydanych dziś</div>
                      </div>
                    </div>

                {/* Filtr uproszczony — zakładki kucharza */}
                <div style={{display:"flex",gap:8,marginBottom:16}}>
                  {[
                    ["cookPending", "⏳ Do zrobienia", pending],
                    ["cookHistory", "📋 Historia dziś", issued],
                  ].map(([v,l,cnt])=>(
                    <button key={v}
                      style={{flex:1,padding:"11px 8px",borderRadius:12,
                        border:"2px solid "+(orderFilter===v?ac:T.border),
                        background:orderFilter===v?ac:T.surface,
                        color:orderFilter===v?"#fff":T.muted,
                        fontWeight:700,cursor:"pointer",fontSize:13,transition:"all .15s",
                        position:"relative"}}
                      onClick={()=>setOrderFilter(v)}>
                      {l}
                      {cnt>0&&(
                        <span style={{
                          position:"absolute",top:6,right:8,
                          background:orderFilter===v?"rgba(255,255,255,0.35)":"#ef4444",
                          color:"#fff",borderRadius:10,padding:"1px 6px",
                          fontSize:10,fontWeight:800}}>
                          {cnt}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* ── HISTORIA DZIŚ — zestawienie wydanych pozycji ── */}
                {orderFilter==="cookHistory" && (()=>{
                  const doneOrders = filteredOrders; // już filtrowane przez cookHistory
                  if (doneOrders.length === 0) return (
                    <div style={{textAlign:"center",padding:"60px 14px",color:T.border}}>
                      <div style={{fontSize:60}}>📋</div>
                      <div style={{fontSize:16,fontWeight:700,marginTop:12,color:T.muted}}>Brak wydanych zamówień dziś</div>
                    </div>
                  );

                  // Zbierz statystyki — ile porcji każdej pozycji przygotowano
                  const itemStats = {};
                  doneOrders.forEach(o=>{
                    (o.cart||[]).filter(c=>(c.serveAt||"kitchen")==="kitchen").forEach(c=>{
                      const k = c.name;
                      if (!itemStats[k]) itemStats[k] = {name:c.name, emoji:c.emoji||"🍽", count:0, image:c.image||null};
                      itemStats[k].count += (c.qty||1);
                    });
                  });
                  const statsArr = Object.values(itemStats).sort((a,b)=>b.count-a.count);
                  const totalDishes = statsArr.reduce((s,x)=>s+x.count,0);

                  return (
                    <div>
                      {/* Podsumowanie dnia */}
                      <div style={{
                        background:"linear-gradient(135deg,#059669,#10b981)",
                        borderRadius:16,padding:"16px 18px",marginBottom:16,color:"#fff",
                        display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"
                      }}>
                        <div style={{fontSize:36}}>👨‍🍳</div>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:900,fontSize:18,marginBottom:2}}>Dziś przygotowano</div>
                          <div style={{fontSize:13,opacity:0.9}}>
                            <strong>{doneOrders.length}</strong> zamówień · <strong>{totalDishes}</strong> porcji · <strong>{statsArr.length}</strong> różnych pozycji
                          </div>
                        </div>
                      </div>

                      {/* Ranking porcji */}
                      {statsArr.length>0&&(
                        <div style={{background:T.surface,borderRadius:14,padding:"14px 16px",marginBottom:16,
                          border:"1.5px solid "+T.border}}>
                          <div style={{fontWeight:800,fontSize:13,color:T.text,marginBottom:10,
                            display:"flex",alignItems:"center",gap:6}}>
                            🏆 Przygotowane pozycje dziś
                          </div>
                          <div style={{display:"flex",flexDirection:"column",gap:7}}>
                            {statsArr.map((item,i)=>(
                              <div key={item.name} style={{
                                display:"flex",alignItems:"center",gap:10,
                                padding:"8px 10px",borderRadius:10,
                                background: i===0?"#f0fdf4":i===1?"#f9fafb":i===2?"#fff8f0":"#fff",
                                border:"1px solid "+(i===0?"#86efac":i===1?"#e5e7eb":i===2?"#fed7aa":"#f0f0f0")
                              }}>
                                <div style={{
                                  width:36,height:36,borderRadius:9,overflow:"hidden",
                                  background:"#f0f0f0",flexShrink:0,
                                  display:"flex",alignItems:"center",justifyContent:"center"
                                }}>
                                  {item.image
                                    ?<img src={item.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                                    :<span style={{fontSize:20}}>{item.emoji}</span>}
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontWeight:700,fontSize:13,color:T.text,
                                    whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                                    {item.name}
                                  </div>
                                  <div style={{height:5,borderRadius:3,background:"#e5e7eb",marginTop:4,overflow:"hidden"}}>
                                    <div style={{
                                      height:"100%",borderRadius:3,
                                      background:i===0?"#10b981":i===1?"#3b82f6":"#f59e0b",
                                      width:`${Math.round(item.count/statsArr[0].count*100)}%`,
                                      transition:"width .4s"
                                    }}/>
                                  </div>
                                </div>
                                <div style={{
                                  fontWeight:900,fontSize:18,
                                  color:i===0?"#059669":i===1?"#3b82f6":i===2?"#d97706":T.muted,
                                  flexShrink:0,minWidth:32,textAlign:"right"
                                }}>
                                  ×{item.count}
                                </div>
                                {i<3&&<div style={{fontSize:16,flexShrink:0}}>{["🥇","🥈","🥉"][i]}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Lista wydanych zamówień */}
                      <div style={{fontWeight:800,fontSize:13,color:T.muted,
                        marginBottom:10,paddingLeft:4}}>
                        📦 Wydane zamówienia ({doneOrders.length})
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:10}}>
                        {doneOrders.map(o=>(
                          <div key={o.id} style={{
                            background:T.surface,borderRadius:14,overflow:"hidden",
                            border:"2px solid #bae6fd",opacity:0.9
                          }}>
                            <div style={{
                              background:"linear-gradient(90deg,#0984e3,#38bdf8)",
                              padding:"9px 14px",
                              display:"flex",alignItems:"center",justifyContent:"space-between"
                            }}>
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <span style={{background:"rgba(255,255,255,0.25)",borderRadius:8,
                                  padding:"3px 10px",color:"#fff",fontWeight:900,fontSize:15}}>
                                  Stolik #{o.tableNumber||o.table}
                                </span>
                                <span style={{color:"rgba(255,255,255,0.85)",fontSize:12}}>{o.time}</span>
                              </div>
                              <span style={{background:"rgba(255,255,255,0.25)",borderRadius:7,
                                padding:"3px 10px",color:"#fff",fontWeight:700,fontSize:11}}>
                                🍽 Wydano{o.issuedToSellerAt?" o "+o.issuedToSellerAt:""}
                              </span>
                            </div>
                            <div style={{padding:"8px 12px"}}>
                              {(o.cart||[]).filter(c=>(c.serveAt||"kitchen")==="kitchen").map((c,i)=>(
                                <div key={i} style={{
                                  display:"flex",alignItems:"center",gap:8,
                                  padding:"5px 0",
                                  borderBottom:"1px solid "+T.border+"44"
                                }}>
                                  <span style={{fontSize:18}}>{c.issuedByCook?"✅":c.emoji}</span>
                                  <span style={{
                                    flex:1,fontSize:13,fontWeight:600,
                                    color:c.issuedByCook?"#059669":T.text,
                                    textDecoration:c.issuedByCook?"line-through":"none"
                                  }}>{c.name}</span>
                                  <span style={{fontWeight:800,fontSize:13,color:T.muted}}>×{c.qty}</span>
                                  {c.issuedByCookAt&&(
                                    <span style={{fontSize:10,color:"#059669",fontWeight:600}}>
                                      {c.issuedByCookAt}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {orderFilter!=="cookHistory" && (filteredOrders.length===0
                  ? <div style={{textAlign:"center",padding:"60px 14px",color:T.border}}>
                      <div style={{fontSize:72}}>👨‍🍳</div>
                      <div style={{fontSize:18,fontWeight:700,marginTop:12,color:T.muted}}>Brak zamówień do przygotowania</div>
                    </div>
                  : <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16}}>
                    {filteredOrders.map(o=>(
                    <div key={o.id} className="bread-card" style={{
                      background: o.issuedToSeller ? "#f9fafb" : T.surface,
                      borderRadius:20,
                      border: o.issuedToSeller ? "2px solid #bae6fd" : "3px solid #f39c12",
                      boxShadow: o.issuedToSeller ? "none" : "0 6px 24px rgba(243,156,18,0.18)",
                      overflow:"hidden",
                      opacity: o.issuedToSeller ? 0.65 : 1,
                      transition:"all .25s",
                      display:"flex",flexDirection:"column"
                    }}>
                      {/* Górny baner */}
                      <div style={{
                        background: o.issuedToSeller
                          ? "linear-gradient(90deg,#0984e3,#38bdf8)"
                          : "linear-gradient(90deg,#e67e22,#f39c12)",
                        padding:"12px 16px",
                        display:"flex",alignItems:"center",justifyContent:"space-between"
                      }}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{background:"rgba(255,255,255,0.25)",borderRadius:10,padding:"5px 12px"}}>
                            <span style={{color:"#fff",fontWeight:900,fontSize:18}}>Stolik #{o.tableNumber||o.table}</span>
                          </div>
                          <span style={{color:"rgba(255,255,255,0.9)",fontSize:12,fontWeight:600}}>{o.time}</span>
                        </div>
                        <div style={{background:"rgba(255,255,255,0.25)",borderRadius:8,padding:"4px 10px"}}>
                          <span style={{color:"#fff",fontWeight:800,fontSize:12}}>
                            {o.issuedToSeller ? "🍽 U sprzedawcy" : `⏱ ~${o.wait}min`}
                          </span>
                        </div>
                      </div>

                      {/* Lista pozycji — tylko kuchnia */}
                      <div style={{padding:"12px 14px 6px",flex:1}}>
                        {(()=>{
                          const kitchenItems = o.cart.filter(c=>(c.serveAt||"kitchen")==="kitchen");
                          const barItems     = o.cart.filter(c=>c.serveAt==="bar");
                          return (<>
                            {kitchenItems.length===0
                              ? <div style={{textAlign:"center",padding:"10px 0",color:T.muted,fontSize:12}}>Brak pozycji dla kuchni</div>
                              : kitchenItems.map(c=>(
                          <div key={c.id} style={{
                            display:"flex",alignItems:"center",gap:10,
                            padding:"9px 0",
                            borderBottom:"1px solid "+T.border+"55",
                            opacity: c.issuedByCook ? 0.6 : 1,
                            transition:"opacity .2s"
                          }}>
                            <div style={{
                              width:42,height:42,borderRadius:10,overflow:"hidden",
                              background: c.issuedByCook ? "#d1fae5" : T.bg,
                              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                              border:"1.5px solid "+(c.issuedByCook?"#6ee7b7":T.border),
                              transition:"all .2s"
                            }}>
                              {c.image
                                ? <img src={c.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                                : <span style={{fontSize:22}}>{c.issuedByCook?"✅":c.emoji}</span>}
                            </div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{
                                fontWeight:800,fontSize:14,
                                color: c.issuedByCook ? "#059669" : T.text,
                                lineHeight:1.2,
                                textDecoration: c.issuedByCook ? "line-through" : "none",
                                whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"
                              }}>{c.name}</div>
                              {(c.selectedAddons||[]).map((a,ai)=>(
                                <div key={ai} style={{fontSize:11,color:"#C8621A",fontWeight:600,marginTop:1}}>+ {a.name} ×{a.qty}</div>
                              ))}
                              {c.allergens&&<div style={{fontSize:10,color:"#c0392b",fontWeight:600,marginTop:2}}>⚠ {c.allergens}</div>}
                            </div>
                            {/* ilość + przycisk wydaj */}
                            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,flexShrink:0}}>
                              <div style={{
                                background: c.issuedByCook ? "#d1fae5" : o.issuedToSeller ? "#e0f2fe" : "#fef3c7",
                                border:"2px solid "+(c.issuedByCook?"#6ee7b7":o.issuedToSeller?"#7dd3fc":"#fcd34d"),
                                borderRadius:10,minWidth:42,height:34,
                                display:"flex",alignItems:"center",justifyContent:"center",
                                transition:"all .2s"
                              }}>
                                <span style={{fontWeight:900,fontSize:16,color:c.issuedByCook?"#059669":o.issuedToSeller?"#0369a1":"#d97706"}}>×{c.qty}</span>
                              </div>
                              {!o.issuedToSeller&&(
                                c.issuedByCook ? (
                                  <button
                                    onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,cart:x.cart.map(ci=>(ci.cartKey||ci.id)===(c.cartKey||c.id)?{...ci,issuedByCook:false,issuedByCookAt:null}:ci)}:x))}
                                    style={{padding:"3px 8px",background:"#f0fdf4",color:"#059669",border:"1.5px solid #6ee7b7",borderRadius:7,fontWeight:700,cursor:"pointer",fontSize:10,whiteSpace:"nowrap"}}>
                                    ↩ Cofnij
                                  </button>
                                ) : (
                                  <button
                                    onClick={()=>{
                                      const now = new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"});
                                      setOrders(p=>p.map(x=>{
                                        if (x.id !== o.id) return x;
                                        const updatedCart = x.cart.map(ci=>
                                          (ci.cartKey||ci.id)===(c.cartKey||c.id)
                                            ? {...ci, issuedByCook:true, issuedByCookAt:now}
                                            : ci
                                        );
                                        // Sprawdź czy to ostatnia nieodznaczona pozycja kuchenna
                                        const kItems = updatedCart.filter(ci=>(ci.serveAt||"kitchen")==="kitchen");
                                        const allDoneNow = kItems.length>0 && kItems.every(ci=>ci.issuedByCook);
                                        return {
                                          ...x,
                                          cart: updatedCart,
                                          cookStatus: allDoneNow ? "ready" : (x.cookStatus==="new"?"cooking":x.cookStatus),
                                          ...(allDoneNow && !x.issuedToSeller ? {
                                            issuedToSeller: true,
                                            issuedToSellerAt: now,
                                          } : {}),
                                        };
                                      }));
                                    }}
                                    style={{padding:"5px 10px",background:"linear-gradient(135deg,#f59e0b,#f97316)",color:"#fff",border:"none",borderRadius:8,fontWeight:800,cursor:"pointer",fontSize:12,whiteSpace:"nowrap",boxShadow:"0 2px 8px rgba(245,158,11,.45)",transition:"transform .1s"}}
                                    onMouseDown={e=>e.currentTarget.style.transform="scale(0.94)"}
                                    onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}
                                    onTouchStart={e=>e.currentTarget.style.transform="scale(0.94)"}
                                    onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}
                                  >
                                    🍳 Wydaj
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        ))}
                            {barItems.length>0&&(
                              <div style={{marginTop:8,padding:"7px 10px",background:"#f0f9ff",borderRadius:10,border:"1px solid #bae6fd"}}>
                                <div style={{fontSize:10,fontWeight:800,color:"#0369a1",marginBottom:4}}>🥤 WYDAJE SPRZEDAWCA:</div>
                                {barItems.map(c=>(
                                  <div key={c.id} style={{fontSize:12,color:"#0369a1",fontWeight:600}}>
                                    {c.emoji} {c.name} ×{c.qty}
                                  </div>
                                ))}
                              </div>
                            )}
                          </>);
                        })()}
                      </div>

                      {/* Przycisk WYDANE */}
                      <div style={{padding:"10px 14px 14px"}}>
                        {o.issuedToSeller ? (
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                            <span style={{color:"#0984e3",fontWeight:700,fontSize:12}}>
                              🍽 Wydano{o.issuedToSellerAt ? ` o ${o.issuedToSellerAt}` : ""}
                            </span>
                            {isAdmin && (
                              <button
                                onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issuedToSeller:false,issuedToSellerAt:null,cookStatus:"cooking",cart:x.cart.map(ci=>({...ci,issuedByCook:false,issuedByCookAt:null}))}:x))}
                                style={{padding:"6px 14px",background:"#fff",color:T.muted,border:"1.5px solid "+T.border,
                                  borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:11}}>
                                ↩ Cofnij
                              </button>
                            )}
                          </div>
                        ) : (()=>{
                          const kItems = o.cart.filter(c=>(c.serveAt||"kitchen")==="kitchen");
                          const doneCount = kItems.filter(c=>c.issuedByCook).length;
                          const allDone = kItems.length>0 && doneCount===kItems.length;
                          return (<>
                            {kItems.length>1 && (
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                                <div style={{flex:1,height:6,borderRadius:3,background:"#f0f0f0",overflow:"hidden"}}>
                                  <div style={{
                                    height:"100%",borderRadius:3,
                                    width:`${Math.round(doneCount/kItems.length*100)}%`,
                                    background: allDone ? "#059669" : "#f59e0b",
                                    transition:"width .3s ease"
                                  }}/>
                                </div>
                                <span style={{fontSize:11,fontWeight:800,color:allDone?"#059669":"#f59e0b",flexShrink:0}}>
                                  {doneCount}/{kItems.length}
                                </span>
                              </div>
                            )}
                            <button
                              onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issuedToSeller:true,issuedToSellerAt:new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),cookStatus:"ready",cart:x.cart.map(ci=>({...ci,issuedByCook:true,issuedByCookAt:ci.issuedByCookAt||new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"})}))}:x))}
                              style={{
                                width:"100%",padding:"14px",
                                background: allDone
                                  ? "linear-gradient(135deg,#059669,#10b981)"
                                  : "linear-gradient(135deg,#94a3b8,#64748b)",
                                color:"#fff",border:"none",borderRadius:12,
                                fontWeight:900,cursor:"pointer",fontSize:17,
                                boxShadow: allDone
                                  ? "0 4px 18px rgba(5,150,105,0.55)"
                                  : "0 2px 8px rgba(100,116,139,0.3)",
                                letterSpacing:0.3,transition:"background .3s, box-shadow .3s, transform .1s"
                              }}
                              onMouseDown={e=>e.currentTarget.style.transform="scale(0.97)"}
                              onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}
                              onTouchStart={e=>e.currentTarget.style.transform="scale(0.97)"}
                              onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}
                            >
                              {allDone ? "🚀 WYDAJ DO SPRZEDAWCY" : kItems.length===0||doneCount===0 ? "🍽 WYDANE" : `🍳 Wydaj pozostałe (${kItems.length-doneCount})`}
                            </button>
                          </>);
                        })()}
                      </div>
                    </div>
                  ))}
                  </div>
                )}
                    </>
                  ); // end return
                })(/* end cook IIFE */)}
              </div>
            ) : (
              /* ══════════ WIDOK DOMYŚLNY (admin / sprzedawca) ══════════ */
              <div>
                {/* Filter pills */}
                <div style={{display:"flex",gap:6,marginBottom:11,flexWrap:"wrap"}}>
                  {(()=>{
                    const unaccepted = orders.filter(o=>o.date===todayStr()&&!o.sellerAccepted&&!o.archivedAt).length;
                    const ready      = orders.filter(o=>o.date===todayStr()&&o.cookStatus==="ready"&&!o.issued&&!o.archivedAt).length;
                    const barPendingCount = orders.filter(o=>o.date===todayStr()&&o.issuedToSeller&&!o.issued&&(o.cart||[]).some(c=>c.serveAt==="bar"&&!c.issuedByBar)).length;
                    const archiveCount = orders.filter(o=>o.date===archiveDate&&o.archivedAt).length;
                    return [
                      ...(isSeller?[["unaccepted",unaccepted>0?`🆕 Nowe (${unaccepted})`:"🆕 Nowe"]]:[] ),
                      ...(isSeller?[["ready",      ready>0?`🔔 Gotowe (${ready})`:"🔔 Gotowe"]]:[] ),
                      ...((isSeller||isAdmin)&&barPendingCount>0?[["bar", `🥤 Bar (${barPendingCount})`]]:[]),
                      ["all","Wszystkie"],["today","Dzisiaj"],
                      ["pending","⏳ Oczekujące"],["issued","✅ Wydane"],
                      ...(isAdmin?[["cooking","👨‍🍳 W przygotowaniu"],["ready","🔔 Gotowe"],["toSeller","🍽 U sprzedawcy"]]:[]),
                      ...(isSeller||isAdmin?[["unpaid","💸 Niezapłacone"]]:[]),
                      ...(isSeller||isCook||isAdmin?[["archive", archiveCount>0?`📦 Archiwum (${archiveCount})`:"📦 Archiwum"]]:[]),
                    ].map(([v,l])=>(
                      <button key={v}
                        style={{...S.pill,...(orderFilter===v?S.pillOn:{}),
                          ...(v==="unaccepted"&&unaccepted>0&&orderFilter!==v?{borderColor:"#e94560",color:"#e94560",fontWeight:800}:{}),
                          ...(v==="ready"&&ready>0&&orderFilter!==v&&isSeller?{borderColor:"#059669",color:"#059669",fontWeight:800}:{}),
                          ...(v==="bar"&&barPendingCount>0&&orderFilter!==v?{borderColor:"#0369a1",color:"#0369a1",fontWeight:800,background:"#e0f2fe"}:{}),
                          ...(v==="archive"&&archiveCount>0&&orderFilter!==v?{borderColor:"#7c3aed",color:"#7c3aed"}:{})
                        }}
                        onClick={()=>setOrderFilter(v)}>{l}</button>
                    ));
                  })()}
                </div>

                {/* Summary bar */}
                {(()=>{
                  // Liczniki zawsze pokazują TYLKO dzisiejsze, nieusunięte zamówienia
                  const base = orders.filter(o => o.date===todayStr() && !o.archivedAt);
                  const pending  = base.filter(o=>!o.issued).length;
                  const issued   = base.filter(o=>o.issued).length;
                  const cooking  = base.filter(o=>o.cookStatus==="cooking").length;
                  const ready    = base.filter(o=>o.cookStatus==="ready").length;
                  const unpaid   = base.filter(o=>!o.paid).length;
                  const barPending = base.filter(o=>o.issuedToSeller&&!o.issued&&(o.cart||[]).some(c=>c.serveAt==="bar"&&!c.issuedByBar)).length;
                  const tiles = [
                    {show: pending>0,  bg:"#fff8e1", color:"#f39c12", icon:"⏳", val:pending, label:"oczekujące"},
                    {show: issued>0,   bg:"#e8f5e9", color:"#27ae60", icon:"✅", val:issued,  label:"wydane"},
                    {show: isSeller&&ready>0, bg:"#f0fdf4", color:"#059669", icon:"🔔", val:ready, label:"gotowe — wydaj!"},
                    {show: (isSeller||isAdmin)&&barPending>0, bg:"#e0f2fe", color:"#0369a1", icon:"🥤", val:barPending, label:"bar — wydaj!"},
                    {show: (isCook||isAdmin)&&cooking>0, bg:"#e8f4fd", color:"#0984e3", icon:"👨‍🍳", val:cooking, label:"w przygot."},
                    {show: (isCook||isAdmin)&&ready>0,   bg:"#f0fdf4", color:"#059669", icon:"🔔", val:ready, label:"gotowe"},
                    {show: (isCook||isAdmin)&&base.filter(o=>o.issuedToSeller&&!o.issued).length>0, bg:"#e8f4fd", color:"#0984e3", icon:"🍽", val:base.filter(o=>o.issuedToSeller&&!o.issued).length, label:"u sprzedawcy"},
                    {show: (isSeller||isAdmin)&&unpaid>0,bg:"#fff0f0", color:"#e94560", icon:"💸", val:unpaid, label:"niezapłac."},
                  ].filter(t=>t.show);
                  return tiles.length>0?(
                    <div style={{display:"flex",gap:7,marginBottom:11,flexWrap:"wrap"}}>
                      {tiles.map((t,i)=>(
                        <div key={i} style={{flex:"1 1 120px",background:t.bg,borderRadius:10,padding:"8px 11px",display:"flex",alignItems:"center",gap:7}}>
                          <span style={{fontSize:17}}>{t.icon}</span>
                          <div>
                            <div style={{fontWeight:800,fontSize:12,color:t.color}}>{t.val} {t.label}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ):null;
                })()}

                {/* ── WIDOK ARCHIWUM ── */}
                {orderFilter==="archive" && (
                  <div>
                    <div style={{background:"linear-gradient(135deg,#7c3aed,#a855f7)",borderRadius:14,padding:"14px 16px",marginBottom:13,color:"#fff",display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:22}}>📦</span>
                      <div>
                        <div style={{fontWeight:800,fontSize:14}}>Archiwum — {archiveDate === todayStr() ? "dzisiaj" : archiveDate}</div>
                        <div style={{fontSize:11,opacity:0.85}}>Wydane zamówienia z wybranego dnia. Tylko administrator może je usuwać.</div>
                      </div>
                      <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:11,fontWeight:700}}>Wybierz datę:</span>
                        <input
                          type="date"
                          value={archiveDate}
                          onChange={e => setArchiveDate(e.target.value)}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 8,
                            border: "none",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#7c3aed",
                            outline: "none"
                          }}
                        />
                      </div>
                    </div>
                    {filteredOrders.length===0
                      ? <div style={S.empty}><div style={{fontSize:46}}>🗂</div><p>Brak zarchiwizowanych zamówień w dniu {archiveDate}</p></div>
                      : filteredOrders.map(o=>(
                        <div key={o.id} style={{...S.card,borderLeft:"4px solid #7c3aed",opacity:0.85,background:"#faf5ff"}}>
                          <div style={{display:"flex",gap:7,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
                            <span style={{background:"#7c3aed",color:"#fff",borderRadius:8,padding:"3px 8px",fontWeight:700,fontSize:12}}>Stolik #{o.tableNumber||o.table}</span>
                            {o.takeaway&&<span style={{background:"#ede9fe",color:"#7c3aed",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>{o.delivery?"🚗 Dowóz":"🏃 Wynos"}</span>}
                            <span style={{background:"#ede9fe",color:"#7c3aed",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:800}}>📦 Archiwum</span>
                            {o.paid&&<span style={{background:"#d1fae5",color:"#059669",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>✅ Zapłacone</span>}
                            <span style={{marginLeft:"auto",fontSize:11,color:"#7c3aed",fontWeight:700}}>
                              {o.archivedAt ? new Date(o.archivedAt).toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}) : ""}
                            </span>
                          </div>
                          <div style={{marginBottom:7}}>
                            {o.cart.map((c,i)=>(
                              <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.textSoft,padding:"2px 0"}}>
                                <span>{c.emoji||"🍽"} {c.name} ×{c.qty}</span>
                                <span style={{fontWeight:600}}>{fmt((c.promoPrice||c.price)*c.qty)} zł</span>
                              </div>
                            ))}
                          </div>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:"1px solid #e9d5ff",paddingTop:7}}>
                            <div style={{fontWeight:800,fontSize:13,color:"#7c3aed"}}>Suma: {fmt(o.total)} zł</div>
                            <div style={{display:"flex",gap:6,alignItems:"center"}}>
                              {o.issuedAt&&<span style={{fontSize:10,color:"#7c3aed",fontWeight:600}}>Wydano o {o.issuedAt}</span>}
                              {isAdmin&&(
                                <button
                                  onClick={()=>{setOrders(p=>p.filter(x=>x.id!==o.id)); logActivity(setLogs, user, "Usunął zamówienie", "#"+String(o.id).slice(-6)+" · "+fmt(o.total)+" zł");}}
                                  style={{padding:"5px 11px",background:"#ffebee",color:"#c0392b",border:"1px solid #fca5a5",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:12}}>
                                  🗑 Usuń
                                </button>
                              )}
                            </div>
                          </div>
                          {!isAdmin&&(
                            <div style={{fontSize:10,color:"#a78bfa",marginTop:5,textAlign:"center"}}>
                              Usuwanie dostępne wyłącznie dla administratora
                            </div>
                          )}
                        </div>
                      ))
                    }
                  </div>
                )}

                {/* ── NORMALNA LISTA ZAMÓWIEŃ ── */}
                {orderFilter!=="archive" && (filteredOrders.length===0
                  ?<div style={S.empty}><div style={{fontSize:46}}>📋</div><p>Brak zamówień</p></div>
                  :filteredOrders.map(o=>{
                  const hasBarPending = o.issuedToSeller && !o.issued && (o.cart||[]).some(c=>c.serveAt==="bar"&&!c.issuedByBar);
                  return (
                  <div key={o.id} style={{
                    ...S.card,
                    borderLeft: "4px solid "+(o.issued?"#27ae60":hasBarPending?"#0369a1":o.issuedToSeller?"#0984e3":(COOK_STATUS[o.cookStatus||"new"]||COOK_STATUS.new).border),
                    background: hasBarPending ? "#f0f9ff" : "#fff",
                    opacity: o.issued ? 0.75 : 1,
                    transition: "all .2s",
                    boxShadow: hasBarPending ? "0 0 0 2px #bae6fd, "+S.card.boxShadow : S.card.boxShadow,
                  }}>
                    {/* Order header */}
                    <div style={{display:"flex",gap:7,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
                      <span style={{background:T.primary,color:"#fff",borderRadius:8,padding:"3px 8px",fontWeight:700,fontSize:12}}>Stolik #{o.tableNumber||o.table}</span>
                      {!o.takeaway && o.tableType && (
                        o.tableType==="outdoor"
                          ? <span style={{background:"#f0fdf4",color:"#15803d",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:800}}>🌿 Zewnętrzny</span>
                          : <span style={{background:"#eff6ff",color:"#1d4ed8",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>🏠 Wewnętrzny</span>
                      )}
                      {o.takeaway&&<span style={{background:o.delivery?"#e8f4fd":"#fffbeb",color:o.delivery?"#0369a1":"#d97706",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:800}}>{o.delivery?"🚗 Dowóz":"🏃 Wynos"}</span>}
                      {o.manualOrder && <span style={{background:"#f0f4ff",color:"#3b5bdb",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>👨‍💼 Obsługa</span>}
                      {o.clientName && <span style={{background:"#f5f0ff",color:"#7c3aed",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>👥 {o.clientName}</span>}
                      {o.guests && <span style={{background:"#f0f4ff",color:"#3b5bdb",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>👥 {o.guests} os.</span>}
                      {o.invoiceRequest && (
                        <span style={{background:"#f5f3ff",color:"#6b21a8",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:800,border:"1px solid #c4b5fd"}}>
                          🧾 FV · {o.invoiceRequest.buyerName||"—"}
                          {o.invoiceRequest.buyerNip?<span style={{opacity:0.7}}> · NIP {o.invoiceRequest.buyerNip}</span>:""}
                        </span>
                      )}
                      {o.invoiceLinkedId && (
                        <span style={{background:"#f0fdf4",color:"#15803d",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:800,border:"1px solid #86efac"}}>
                          ✅ FV: {o.invoiceNumber||o.invoiceLinkedId.slice(0,8)}
                        </span>
                      )}
                      <span style={{color:T.textSoft,fontSize:11}}>{o.time}</span>
                      <span style={{background:"#fff0f0",color:ac,borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:600}}>⏱ ~{o.wait} min</span>
                      {o.scanSource==="qr"  && <span style={{background:"#f0fdf4",color:"#15803d",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>📷 QR</span>}
                      {o.scanSource==="nfc" && <span style={{background:"#faf0ff",color:"#7c3aed",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>📡 NFC</span>}
                      {o.paymentMethod&&<span style={{
                        background:o.paymentMethod==="blik"?"#fde8ec":o.paymentMethod==="card"?"#e8f0fe":"#e8f5e9",
                        color:o.paymentMethod==="blik"?"#e94560":o.paymentMethod==="card"?"#0984e3":"#27ae60",
                        borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700
                      }}>{o.paymentMethod==="blik"?"📱 BLIK":o.paymentMethod==="card"?"💳 Karta":"💵 Gotówka"}</span>}
                      {(isCook||isAdmin)&&(
                        <span style={{
                          background: (COOK_STATUS[o.cookStatus||"new"]||COOK_STATUS.new).bg,
                          color:      (COOK_STATUS[o.cookStatus||"new"]||COOK_STATUS.new).color,
                          borderRadius:20, padding:"3px 9px", fontSize:11, fontWeight:800
                        }}>
                          {(COOK_STATUS[o.cookStatus||"new"]||COOK_STATUS.new).icon} {(COOK_STATUS[o.cookStatus||"new"]||COOK_STATUS.new).label}
                        </span>
                      )}
                      {(isSeller||isAdmin)&&(
                        <span style={{
                          background: o.paid?"#e8f5e9":"#fff0f0",
                          color:      o.paid?"#27ae60":"#e94560",
                          borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:800
                        }}>
                          {o.paid?"💳 Zapłacone":"💸 Niezapłacone"}
                        </span>
                      )}
                      <span style={{
                        marginLeft:"auto",
                        background: o.issued ? "#e8f5e9" : o.issuedToSeller ? "#e8f4fd" : "#fff8e1",
                        color:      o.issued ? "#27ae60" : o.issuedToSeller ? "#0984e3" : "#f39c12",
                        borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:800
                      }}>
                        {o.issued ? "✅ Wydane klientowi" : o.issuedToSeller ? "🍽 U sprzedawcy" : "⏳ Oczekuje"}
                      </span>
                    </div>

                    {/* Dane klienta — tylko dla zamówień na wynos/dostawę */}
                    {o.takeaway&&(o.firstName||o.phone)&&(
                      <div style={{background:o.delivery?"#e8f4fd":"#fffbeb",border:"1.5px solid "+(o.delivery?"#bae6fd":"#fde68a"),borderRadius:10,padding:"8px 12px",marginBottom:8}}>
                        <div style={{fontWeight:800,fontSize:11,color:o.delivery?"#0369a1":"#92400e",marginBottom:4}}>{o.delivery?"🚗 Dane dostawy":"🏃 Odbiór osobisty"}</div>
                        {(o.firstName||o.lastName)&&<div style={{fontSize:13,fontWeight:700,color:T.text}}>👤 {o.firstName} {o.lastName}</div>}
                        {o.phone&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>📞 {o.phone}</div>}
                        {o.delivery&&o.address&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>📍 {o.address}</div>}
                        {o.delivery&&o.deliveryZone&&(
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4,background:"rgba(255,255,255,0.6)",borderRadius:7,padding:"3px 8px"}}>
                            <span style={{fontSize:11,color:"#0369a1",fontWeight:700}}>🗺 {o.deliveryZone}</span>
                            <span style={{fontSize:12,fontWeight:900,color:"#0369a1"}}>
                              {!o.deliveryCost||o.deliveryCost===0?"Bezpłatnie":`+${Number(o.deliveryCost).toFixed(2)} zł`}
                            </span>
                          </div>
                        )}
                        {o.note&&<div style={{fontSize:11,color:T.muted,marginTop:2,fontStyle:"italic"}}>💬 {o.note}</div>}
                      </div>
                    )}

                    {/* Paid badge */}
                    {(isSeller||isAdmin)&&(
                      <span style={{
                        background: o.paid?"#e8f5e9":"#fff0f0",
                        color:      o.paid?"#27ae60":"#e94560",
                        borderRadius:20, padding:"3px 9px", fontSize:11, fontWeight:800
                      }}>
                        {o.paid?"💳 Zapłacone":"💸 Niezapłacone"}
                      </span>
                    )}

                    {/* Items — kuchnia i bar */}
                    {(()=>{
                      const kitchenItems = o.cart.filter(c=>(c.serveAt||"kitchen")==="kitchen");
                      const barItems     = o.cart.filter(c=>c.serveAt==="bar");
                      return (<>
                        {(()=>{
        // eslint-disable-next-line no-unused-vars
                          return kitchenItems.map(c=>(
                          <div key={c.cartKey||c.id} style={{
                            padding:"6px 8px",
                            borderRadius:8,
                            marginBottom:4,
                            background: c.issuedByCook ? "#f0fdf4" : "#fff",
                            border: "1.5px solid " + (c.issuedByCook ? "#86efac" : T.border+"66"),
                            opacity: c.issuedByCook ? 0.75 : 1,
                            transition:"all .2s"
                          }}>
                            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:T.muted,alignItems:"center",gap:7}}>
                              <div style={{display:"flex",alignItems:"center",gap:7,flex:1,minWidth:0}}>
                                <div style={{width:24,height:24,borderRadius:5,overflow:"hidden",background:"#f0f0f0",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                                  {c.image?<img src={c.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<span style={{fontSize:13}}>{c.emoji}</span>}
                                </div>
                                <span style={{fontWeight:700,color:c.issuedByCook?"#059669":T.text,textDecoration:c.issuedByCook?"line-through":"none"}}>{c.name} ×{c.qty}</span>
                                <span style={{background:"#fff3cd",color:"#856404",borderRadius:5,padding:"1px 4px",fontSize:9,fontWeight:800,flexShrink:0}}>Kuchnia</span>
                                {c.promoPrice&&<span style={{background:ac,color:"#fff",borderRadius:10,padding:"0 4px",fontSize:9,fontWeight:800}}>P</span>}
                              </div>
                              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                                <span style={{fontWeight:600}}>{fmt((c.effectivePrice??(c.promoPrice??c.price))*c.qty)} zł</span>
                                {isCook&&(
                                  c.issuedByCook ? (
                                    <span style={{background:"#d1fae5",color:"#059669",borderRadius:7,padding:"3px 8px",fontSize:10,fontWeight:800,whiteSpace:"nowrap"}}>
                                      ✅ Gotowe{c.issuedByCookAt?" "+c.issuedByCookAt:""}
                                    </span>
                                  ) : (
                                    <button
                                      onClick={()=>{
                                        const now = new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"});
                                        setOrders(p=>p.map(x=>{
                                          if (x.id !== o.id) return x;
                                          const updatedCart = x.cart.map(ci=>
                                            (ci.cartKey||ci.id)===(c.cartKey||c.id)
                                              ? {...ci, issuedByCook:true, issuedByCookAt:now}
                                              : ci
                                          );
                                          const kItems = updatedCart.filter(ci=>(ci.serveAt||"kitchen")==="kitchen");
                                          const allDoneNow = kItems.length>0 && kItems.every(ci=>ci.issuedByCook);
                                          return {
                                            ...x,
                                            cart: updatedCart,
                                            cookStatus: allDoneNow ? "ready" : (x.cookStatus==="new"?"cooking":x.cookStatus),
                                            ...(allDoneNow && !x.issuedToSeller ? {
                                              issuedToSeller: true,
                                              issuedToSellerAt: now,
                                            } : {}),
                                          };
                                        }));
                                      }}
                                      style={{padding:"4px 10px",background:"linear-gradient(135deg,#f39c12,#e67e22)",color:"#fff",border:"none",borderRadius:7,fontWeight:800,cursor:"pointer",fontSize:11,whiteSpace:"nowrap",boxShadow:"0 2px 6px rgba(243,156,18,.4)",transition:"all .15s"}}>
                                      🍳 Wydaj
                                    </button>
                                  )
                                )}
                                {/* SPRZEDAWCA / ADMIN — przycisk wydania per pozycja gdy kucharz już wydał */}
                                {(isSeller||isAdmin)&&c.issuedByCook&&(
                                  c.issuedToClient ? (
                                    <span style={{background:"#d1fae5",color:"#059669",borderRadius:7,padding:"3px 9px",fontSize:10,fontWeight:800,whiteSpace:"nowrap"}}>
                                      ✅ Wydano{c.issuedToClientAt?" "+c.issuedToClientAt:""}
                                    </span>
                                  ) : (
                                    <button
                                      onClick={()=>{
                                        const now = new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"});
                                        setOrders(p=>p.map(x=>{
                                          if (x.id !== o.id) return x;
                                          const updatedCart = x.cart.map(ci=>
                                            (ci.cartKey||ci.id)===(c.cartKey||c.id)
                                              ? {...ci, issuedToClient:true, issuedToClientAt:now}
                                              : ci
                                          );
                                          const kItems = updatedCart.filter(ci=>(ci.serveAt||"kitchen")==="kitchen");
                                          const allDoneNow = kItems.length>0 && kItems.every(ci=>ci.issuedToClient);
                                          if (allDoneNow) {
                                            setTables(t=>t.map(tb=>tb.id===x.table||tb.number===(x.tableNumber||x.table)?{...tb,occupied:false}:tb));
                                          }
                                          return {
                                            ...x,
                                            cart: updatedCart,
                                            ...(allDoneNow && !x.issued ? {
                                              issued: true,
                                              issuedAt: now,
                                            } : {}),
                                          };
                                        }));
                                      }}
                                      style={{
                                        padding:"5px 12px",
                                        background:"linear-gradient(135deg,#27ae60,#2ecc71)",
                                        color:"#fff",border:"none",borderRadius:8,fontWeight:800,
                                        cursor:"pointer",fontSize:11,whiteSpace:"nowrap",
                                        boxShadow:"0 2px 8px rgba(39,174,96,.4)",
                                        animation:"popIn .2s ease",
                                        transition:"all .15s"
                                      }}
                                      onMouseDown={e=>e.currentTarget.style.transform="scale(0.94)"}
                                      onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}
                                    >
                                      🍽 Wydaj
                                    </button>
                                  )
                                )}
                                {/* Pozycja kuchenna — kucharz jeszcze nie wydał → info dla sprzedawcy */}
                                {(isSeller||isAdmin)&&!c.issuedByCook&&(
                                  <span style={{background:"#fff8e1",color:"#d97706",borderRadius:6,padding:"2px 7px",fontSize:10,fontWeight:700,whiteSpace:"nowrap"}}>
                                    ⏳ w kuchni
                                  </span>
                                )}
                              </div>
                            </div>
                            {(c.selectedAddons||[]).map((a,ai)=>(
                              <div key={ai} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"#C8621A",fontWeight:600,paddingLeft:31,marginTop:1}}>
                                <span>+ {a.name} ×{a.qty}</span>
                                <span>+{(a.price*a.qty).toFixed(2)} zł</span>
                              </div>
                            ))}
                            {c.customImage&&(
                              <div style={{marginTop:6,display:"flex",alignItems:"center",gap:8,background:"#f5f3ff",borderRadius:8,padding:"6px 9px",border:"1.5px solid #c4b5fd"}}>
                                <img src={c.customImage} alt="grafika klienta"
                                  style={{width:44,height:44,objectFit:"cover",borderRadius:7,border:"1.5px solid #a78bfa",flexShrink:0,cursor:"pointer"}}
                                  onClick={()=>window.open(c.customImage,"_blank")}
                                />
                                <div>
                                  <div style={{fontWeight:700,fontSize:10,color:"#6d28d9",marginBottom:3}}>📸 Grafika klienta</div>
                                  <a href={c.customImage} download={`grafika_${(c.name||"produkt").replace(/\s+/g,"_")}.jpg`}
                                    style={{display:"inline-flex",alignItems:"center",gap:4,padding:"4px 9px",background:"#6d28d9",color:"#fff",borderRadius:6,fontSize:10,fontWeight:700,textDecoration:"none"}}>
                                    ⬇ Pobierz
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        ));})()}
                        {barItems.length>0&&(
                          <div style={{marginTop:6,padding:"10px 12px",
                            background: o.issuedToSeller && barItems.some(c=>!c.issuedByBar) ? "linear-gradient(135deg,#0369a1,#0284c7)" : "#f0f9ff",
                            borderRadius:10,
                            border: o.issuedToSeller && barItems.some(c=>!c.issuedByBar) ? "2px solid #0369a1" : "1px solid #bae6fd",
                            boxShadow: o.issuedToSeller && barItems.some(c=>!c.issuedByBar) ? "0 3px 12px rgba(3,105,161,0.35)" : "none",
                            animation: o.issuedToSeller && barItems.some(c=>!c.issuedByBar) ? "popIn .3s ease" : "none",
                          }}>
                            <div style={{fontSize:11,fontWeight:800,
                              color: o.issuedToSeller && barItems.some(c=>!c.issuedByBar) ? "#fff" : "#0369a1",
                              marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
                              🥤 DO WYDANIA Z BARU:
                              {o.issuedToSeller && barItems.some(c=>!c.issuedByBar) && (
                                <span style={{background:"#fff",color:"#0369a1",borderRadius:20,padding:"1px 8px",fontSize:10,fontWeight:900,animation:"blikSpin 0s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}>
                                  ← WYDAJ TERAZ
                                </span>
                              )}
                            </div>
                            {barItems.map(c=>(
                              <div key={c.cartKey||c.id} style={{padding:"4px 0"}}>
                                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                  <div style={{display:"flex",alignItems:"center",gap:6,fontSize:13,
                                    color: o.issuedToSeller && !c.issuedByBar ? "#fff" : c.issuedByBar ? "#059669" : "#0369a1",
                                    fontWeight:700}}>
                                    <span style={{fontSize:16}}>{c.emoji}</span>
                                    <span style={{textDecoration:c.issuedByBar?"line-through":"none"}}>{c.name} ×{c.qty}</span>
                                  </div>
                                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                                    <span style={{fontSize:11,color:o.issuedToSeller&&!c.issuedByBar?"rgba(255,255,255,0.8)":T.muted}}>
                                      {fmt((c.effectivePrice??(c.promoPrice??c.price))*c.qty)} zł
                                    </span>
                                    {(isSeller||isAdmin)&&(
                                      c.issuedByBar ? (
                                        <span style={{background:"#d1fae5",color:"#059669",borderRadius:6,padding:"3px 9px",fontSize:11,fontWeight:800}}>✅ Wydane</span>
                                      ) : (
                                        <button
                                          onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,cart:x.cart.map(ci=>(ci.cartKey||ci.id)===(c.cartKey||c.id)?{...ci,issuedByBar:true,issuedByBarAt:new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"})}:ci)}:x))}
                                          style={{padding:"5px 14px",
                                            background: o.issuedToSeller ? "#fff" : "#0984e3",
                                            color: o.issuedToSeller ? "#0369a1" : "#fff",
                                            border:"none",borderRadius:8,fontWeight:800,cursor:"pointer",fontSize:12,
                                            boxShadow:"0 2px 6px rgba(0,0,0,0.2)",transition:"all .15s"}}>
                                          Wydaj
                                        </button>
                                      )
                                    )}
                                  </div>
                                </div>
                                {(c.selectedAddons||[]).map((a,ai)=>(
                                  <div key={ai} style={{fontSize:10,color:o.issuedToSeller&&!c.issuedByBar?"rgba(255,255,255,0.75)":"#0369a1",paddingLeft:22,marginTop:1}}>+ {a.name} ×{a.qty}</div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </>);
                    })()}

                    {/* Footer: total + actions */}
                    <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:7}}>

                      {/* Sprzedawca — przypisz do kuchni/baru jeśli zamówienie nie zostało jeszcze wysłane */}
                      {(isSeller||isAdmin)&&!o.sellerAccepted&&(
                        <SellerRoutingPanel order={o} onRoute={cartWithRouting=>{
                          setOrders(p=>p.map(x=>x.id===o.id
                            ?{...x,cart:cartWithRouting,sellerAccepted:true,
                              sellerAcceptedAt:new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),
                              cookStatus:"new"}
                            :x));
                        }}/>
                      )}
                      {(isSeller||isAdmin)&&o.sellerAccepted&&(
                        <div style={{fontSize:11,color:"#f39c12",fontWeight:700,padding:"2px 0"}}>
                          👨‍🍳 Wysłano do kuchni{o.sellerAcceptedAt?` o ${o.sellerAcceptedAt}`:""}
                        </div>
                      )}

                      {/* GOTOWE Z KUCHNI — duży przycisk wydania dla sprzedawcy */}
                      {isSeller&&o.cookStatus==="ready"&&!o.issued&&(()=>{
                        const kItems = (o.cart||[]).filter(c=>(c.serveAt||"kitchen")==="kitchen");
                        const issuedCount = kItems.filter(c=>c.issuedToClient).length;
                        const allClientDone = kItems.length>0 && kItems.every(c=>c.issuedToClient);
                        return (
                          <>
                            {kItems.length>1&&(
                              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                                <div style={{flex:1,height:5,borderRadius:3,background:"#e0e0e0",overflow:"hidden"}}>
                                  <div style={{height:"100%",borderRadius:3,
                                    width:`${kItems.length?Math.round(issuedCount/kItems.length*100):0}%`,
                                    background:"#27ae60",transition:"width .3s"}}/>
                                </div>
                                <span style={{fontSize:10,fontWeight:800,color:"#27ae60",flexShrink:0}}>{issuedCount}/{kItems.length} wydanych</span>
                              </div>
                            )}
                            <button
                              onClick={()=>{
                                const now = new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"});
                                setOrders(p=>p.map(x=>x.id===o.id?{
                                  ...x,
                                  issued:true,
                                  issuedAt:now,
                                  cart:x.cart.map(ci=>({...ci,
                                    issuedToClient:true,
                                    issuedToClientAt:ci.issuedToClientAt||now
                                  }))
                                }:x));
                                setTables(p=>p.map(t=>t.id===o.table||t.number===(o.tableNumber||o.table)?{...t,occupied:false}:t));
                              }}
                              style={{
                                width:"100%",padding:"12px",
                                background:"linear-gradient(135deg,#27ae60,#2ecc71)",
                                color:"#fff",border:"none",borderRadius:12,
                                fontWeight:900,cursor:"pointer",fontSize:15,
                                boxShadow:"0 4px 14px rgba(39,174,96,0.45)",
                                animation:"popIn .3s ease"
                              }}>
                              🍽 {allClientDone ? "GOTOWE — Zamknij zamówienie" : `GOTOWE — Wydaj klientowi${issuedCount>0?" (pozostałe "+( kItems.length-issuedCount)+")":""}`}
                            </button>
                          </>
                        );
                      })()}

                      {/* Cook status buttons — visible to cook & admin */}
                      {(isCook||isAdmin) && (
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          {[["new","🆕 Nowe","#f39c12","#fff8e1"],["cooking","👨‍🍳 W przygotowaniu","#0984e3","#e8f4fd"],["ready","🔔 Gotowe do wydania","#059669","#f0fdf4"]].map(([s,l,col,bg])=>(
                            <button key={s}
                              disabled={isCook && (o.issuedToSeller || orderFilter === "today")}
                              onClick={()=>{setOrders(p=>p.map(x=>x.id===o.id?{...x,cookStatus:s,...(s==="ready"?{readyAt:new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"})}:{})}:x)); logActivity(setLogs, user, "Zmienił status zamówienia", l+" (#"+String(o.id).slice(-6)+")");}}
                              style={{flex:"1 1 auto",padding:"6px 10px",background:(o.cookStatus||"new")===s?col:bg,
                                color:(o.cookStatus||"new")===s?"#fff":col,
                                border:`2px solid ${col}`,borderRadius:9,fontWeight:700,
                                cursor:(isCook && (o.issuedToSeller || orderFilter === "today")) ? "default" : "pointer",
                                fontSize:11,
                                opacity:(o.cookStatus||"new")===s?1:0.7,transition:"all .15s"}}>
                              {l}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Paid toggle — visible to seller & admin */}
                      {(isSeller||isAdmin)&&(
                        o.paid ? (
                          <div style={{width:"100%",padding:"7px 12px",
                            background:"#e8f5e9",color:"#27ae60",
                            border:"2px solid #27ae60",borderRadius:9,
                            fontWeight:800,fontSize:12,textAlign:"center"}}>
                            ✅ Zapłacono{o.paidAt?" o "+o.paidAt:""}
                            {isAdmin&&(
                              <button
                                onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,paid:false,paidAt:null,archivedAt:null}:x))}
                                style={{marginLeft:10,padding:"2px 8px",background:"#fff",color:"#e94560",
                                  border:"1px solid #e94560",borderRadius:6,fontWeight:700,cursor:"pointer",fontSize:10}}>
                                ↩ Cofnij
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,paid:true,paidAt:new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),archivedAt:new Date().toISOString()}:x))}
                            style={{width:"100%",padding:"7px 12px",
                              background:"#fff0f0",color:"#e94560",
                              border:"2px solid #e94560",
                              borderRadius:9,fontWeight:800,cursor:"pointer",fontSize:12,transition:"all .15s"}}>
                            💸 Oznacz jako zapłacone
                          </button>
                        )
                      )}

                      {/* Issue row: cook→issuedToSeller, seller→issued (to client), admin→both */}
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:7}}>
                        <div style={{fontWeight:800,color:T.text,fontSize:13,flex:1}}>Suma: {fmt(o.total)} zł</div>
                        {(isAdmin||isSeller) && (
                          <button
                            onClick={()=>printOrder(o)}
                            style={{padding:"5px 10px",background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:12,flexShrink:0}}>
                            🖨 Drukuj
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={()=>{setOrders(p=>p.filter(x=>x.id!==o.id)); logActivity(setLogs, user, "Usunął zamówienie", "#"+String(o.id).slice(-6)+" · "+fmt(o.total)+" zł");}}
                            style={{padding:"5px 10px",background:"#ffebee",color:"#c0392b",border:"none",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:12,flexShrink:0}}>
                            🗑 Usuń
                          </button>
                        )}

                        {/* KUCHARZ — wydaje dla sprzedawcy */}
                        {isCook&&(()=>{
                          const kItems = o.cart.filter(c=>(c.serveAt||"kitchen")==="kitchen");
                          const issuedCount = kItems.filter(c=>c.issuedByCook).length;
                          const allDone = kItems.length>0 && issuedCount===kItems.length;
                          return o.issuedToSeller ? (
                            <button
                              onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issuedToSeller:false,issuedToSellerAt:null,archivedAt:null,cart:x.cart.map(ci=>({...ci,issuedByCook:false,issuedByCookAt:null}))}:x))}
                              style={{padding:"5px 12px",background:T.bg,color:T.textSoft,border:"none",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:11,flexShrink:0}}>
                              ↩ Cofnij
                            </button>
                          ) : (
                            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                              {kItems.length>1&&(
                                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:10,color:allDone?"#059669":"#f39c12",fontWeight:700}}>
                                  <div style={{width:80,height:5,borderRadius:3,background:"#f0f0f0",overflow:"hidden"}}>
                                    <div style={{height:"100%",borderRadius:3,width:`${kItems.length?Math.round(issuedCount/kItems.length*100):0}%`,background:allDone?"#059669":"#f39c12",transition:"width .3s"}}/>
                                  </div>
                                  {issuedCount}/{kItems.length}
                                </div>
                              )}
                              <button
                                onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issuedToSeller:true,issuedToSellerAt:new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),cookStatus:"ready",cart:x.cart.map(ci=>({...ci,issuedByCook:true,issuedByCookAt:ci.issuedByCookAt||new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"})}))}:x))}
                                style={{
                                  padding:"10px 20px",
                                  background: allDone
                                    ? "linear-gradient(135deg,#059669,#10b981)"
                                    : "linear-gradient(135deg,#0984e3,#3498db)",
                                  color:"#fff",border:"none",borderRadius:10,fontWeight:900,cursor:"pointer",fontSize:13,
                                  boxShadow: allDone
                                    ? "0 4px 14px rgba(5,150,105,.5)"
                                    : "0 4px 12px rgba(9,132,227,.3)",
                                  flexShrink:0,
                                  animation: allDone ? "popIn .3s ease" : "none",
                                  transition:"all .25s"
                                }}>
                                {allDone ? "🚀 WYDAJ DO SPRZEDAWCY" : "📤 Wydaj do sprzedawcy"}
                              </button>
                            </div>
                          );
                        })()}

                        {/* SPRZEDAWCA — wydaje klientowi */}
                        {isSeller&&(
                          o.issued ? (
                            <button
                              onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issued:false,issuedAt:null}:x))}
                              style={{padding:"5px 12px",background:T.bg,color:T.textSoft,border:"none",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:11,flexShrink:0}}>
                              ↩ Cofnij
                            </button>
                          ) : (
                            <button
                              onClick={()=>{
                                const now = new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"});
                                setOrders(p=>p.map(x=>x.id===o.id?{
                                  ...x,issued:true,issuedAt:now,
                                  cart:x.cart.map(ci=>({...ci,issuedToClient:true,issuedToClientAt:ci.issuedToClientAt||now}))
                                }:x));
                                setTables(p=>p.map(t=>t.id===o.table||t.number===(o.tableNumber||o.table)?{...t,occupied:false}:t));
                              }}
                              style={{padding:"7px 16px",background:"#27ae60",color:"#fff",border:"none",borderRadius:8,fontWeight:800,cursor:"pointer",fontSize:12,boxShadow:"0 2px 6px rgba(39,174,96,.35)",flexShrink:0}}>
                              ✅ Wydaj klientowi
                            </button>
                          )
                        )}

                        {/* ADMIN — widzi oba */}
                        {isAdmin&&(
                          <div style={{display:"flex",gap:5,flexShrink:0}}>
                            {o.issuedToSeller ? (
                              <button
                                onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issuedToSeller:false,issuedToSellerAt:null}:x))}
                                style={{padding:"5px 9px",background:"#e8f4fd",color:"#0984e3",border:"1px solid #0984e3",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:10}}>
                                ↩ Cofnij →sprzed.
                              </button>
                            ) : (
                              <button
                                onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issuedToSeller:true,issuedToSellerAt:new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),cookStatus:"ready"}:x))}
                                style={{padding:"6px 10px",background:"#0984e3",color:"#fff",border:"none",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:11}}>
                                🍽 →Sprzedawca
                              </button>
                            )}
                            {o.issued ? (
                              <button
                                onClick={()=>setOrders(p=>p.map(x=>x.id===o.id?{...x,issued:false,issuedAt:null}:x))}
                                style={{padding:"5px 9px",background:T.bg,color:T.textSoft,border:"none",borderRadius:8,fontWeight:700,cursor:"pointer",fontSize:10}}>
                                ↩ Cofnij →klient
                              </button>
                            ) : (
                              <button
                                onClick={()=>{
                                  const now = new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"});
                                  setOrders(p=>p.map(x=>x.id===o.id?{
                                    ...x,issued:true,issuedAt:now,
                                    cart:x.cart.map(ci=>({...ci,issuedToClient:true,issuedToClientAt:ci.issuedToClientAt||now}))
                                  }:x));
                                  setTables(p=>p.map(t=>t.id===o.table||t.number===(o.tableNumber||o.table)?{...t,occupied:false}:t));
                                }}
                                style={{padding:"6px 10px",background:"#27ae60",color:"#fff",border:"none",borderRadius:8,fontWeight:800,cursor:"pointer",fontSize:11,boxShadow:"0 2px 6px rgba(39,174,96,.35)"}}>
                                ✅ →Klient
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Timestamps */}
                    <div style={{display:"flex",gap:12,marginTop:5,flexWrap:"wrap"}}>
                      {o.issuedToSeller&&o.issuedToSellerAt&&<div style={{fontSize:10,color:"#0984e3",fontWeight:600}}>🍽 Wydano sprzedawcy o {o.issuedToSellerAt}</div>}
                      {o.issued&&o.issuedAt&&<div style={{fontSize:10,color:"#27ae60",fontWeight:600}}>✅ Wydano klientowi o {o.issuedAt}</div>}
                      {o.cookStatus==="ready"&&o.readyAt&&<div style={{fontSize:10,color:"#059669",fontWeight:600}}>Gotowe o {o.readyAt}</div>}
                      {o.paid&&o.paidAt&&<div style={{fontSize:10,color:"#0984e3",fontWeight:600}}>Zapłacono o {o.paidAt}</div>}
                    </div>
                  </div>
                );}))}
              </div>
            )}
          </div>
        )}

        {/* ── MENU ── */}
        {tab==="menu" && (isSuperAdmin || (isTabVisible("menu", user.role) && rolePerm("adminMenu"))) && (
          <div>
            <button style={S.accentBtn(ac)} onClick={()=>setShowAdd(p=>!p)}>+ Dodaj produkt</button>
            {showAdd&&(
              <div style={S.formCard}>
                <div style={S.formTitle}>Nowy produkt</div>
                {/* photo */}
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{width:64,height:64,borderRadius:11,overflow:"hidden",border:newItem.image?`2px solid ${ac}`:"2px dashed #ccc",
                    background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}
                    onClick={()=>setPickerCtx({type:"newItem"})}>
                    {newItem.image?<img src={newItem.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      :<div style={{textAlign:"center",color:"#ccc"}}><div style={{fontSize:22}}>📷</div><div style={{fontSize:9}}>FOTO</div></div>}
                  </div>
                  <div>
                    <button style={S.smBtn} onClick={()=>setPickerCtx({type:"newItem"})}>{newItem.image?"Zmień foto":"Dodaj foto"}</button>
                    {newItem.image&&<button style={{...S.smBtn,background:"#ffebee",color:ac,marginTop:5}} onClick={()=>setNewItem(p=>({...p,image:null}))}>🗑 Usuń</button>}
                  </div>
                </div>
                <div style={S.fGrid}>
                  <input style={S.inp} placeholder="Emoji" value={newItem.emoji} onChange={e=>setNewItem({...newItem,emoji:e.target.value})}/>
                  <input style={S.inp} placeholder="Nazwa produktu" value={newItem.name} onChange={e=>setNewItem({...newItem,name:e.target.value})}/>
                  <select style={S.inp} value={newItem.catId} onChange={e=>setNewItem({...newItem,catId:e.target.value})}>
                    <option value="">-- Kategoria --</option>
                    {allCatOpts.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                  <input style={S.inp} placeholder="Opis (krótki)" value={newItem.desc} onChange={e=>setNewItem({...newItem,desc:e.target.value})}/>
                  <input style={S.inp} type="number" placeholder="Cena (zł)" value={newItem.price} onChange={e=>setNewItem({...newItem,price:e.target.value})}/>
                  <input style={S.inp} type="number" placeholder="Cena promo (zł)" value={newItem.promoPrice} onChange={e=>setNewItem({...newItem,promoPrice:e.target.value})}/>
                  <input style={{...S.inp,gridColumn:"span 2"}} type="number" placeholder="Czas oczekiwania (min)" value={newItem.wait} onChange={e=>setNewItem({...newItem,wait:e.target.value})}/>
                  <input style={{...S.inp,gridColumn:"span 2"}} placeholder="Skład / Składniki (np. wołowina, cheddar, sałata...)" value={newItem.ingredients||""} onChange={e=>setNewItem({...newItem,ingredients:e.target.value})}/>
                  <input style={{...S.inp,gridColumn:"span 2"}} placeholder="Alergeny (np. gluten, nabiał, jaja)" value={newItem.allergens||""} onChange={e=>setNewItem({...newItem,allergens:e.target.value})}/>
                </div>
                {/* DOSTĘPNOŚĆ */}
                <div style={{marginTop:10,padding:"10px 14px",borderRadius:9,border:"1.5px solid "+(newItem.available!==false?"#bbf7d0":"#fecaca"),background:newItem.available!==false?"#f0fdf4":"#fff5f5",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,color:newItem.available!==false?"#15803d":"#dc2626"}}>
                      {newItem.available!==false?"✅ Produkt dostępny":"❌ Produkt niedostępny"}
                    </div>
                    <div style={{fontSize:11,color:newItem.available!==false?"#166534":"#991b1b",marginTop:2}}>
                      {newItem.available!==false?"Klienci będą mogli zamawiać ten produkt":"Produkt będzie widoczny ale niedostępny do zamówienia"}
                    </div>
                  </div>
                  <div onClick={()=>setNewItem(p=>({...p,available:p.available===false}))}
                    style={{width:46,height:26,borderRadius:13,background:newItem.available!==false?"#22c55e":"#f87171",cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:3,left:newItem.available!==false?23:3,width:20,height:20,borderRadius:"50%",background:"#fff",boxShadow:"0 1px 3px rgba(0,0,0,0.2)",transition:"left .2s"}}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:7,marginTop:9}}>
                  <button style={S.darkBtn} onClick={addMenuItem}>Zapisz produkt</button>
                  <button style={S.cancelBtn} onClick={()=>setShowAdd(false)}>Anuluj</button>
                </div>
              </div>
            )}

            {/* ── WYSZUKIWARKA MENU + FILTR KATEGORII ── */}
            {(()=>{
              const rootCats = cats.filter(c=>!c.parentId);
              const getChain = (catId) => {
                const r=[]; let cur=cats.find(c=>c.id===catId);
                // eslint-disable-next-line no-loop-func
                while(cur){r.unshift(cur);cur=cur.parentId?cats.find(c=>c.id===cur.parentId):null;}
                return r;
              };
              const getRootCatId = (catId) => { const ch=getChain(catId); return ch.length>0?ch[0].id:null; };

              const filteredMenu = menu.filter(item => {
                const matchSearch = !menuSearch.trim() ||
                  item.name.toLowerCase().includes(menuSearch.toLowerCase()) ||
                  (item.desc||"").toLowerCase().includes(menuSearch.toLowerCase());
                const matchCat = menuCatFilter==="all" || getRootCatId(item.catId)===menuCatFilter ||
                  (menuCatFilter==="none"&&!item.catId);
                return matchSearch && matchCat;
              });

              const grouped = {};
              const uncategorized = [];
              filteredMenu.forEach(item => {
                const rootId = getRootCatId(item.catId);
                if(rootId){ if(!grouped[rootId]) grouped[rootId]=[]; grouped[rootId].push(item); }
                else uncategorized.push(item);
              });

              const renderItem = (item) => {
                const chain = getChain(item.catId);
                return (
                  <div key={item.id} style={{...S.card,opacity:item.active?1:0.52}}>
                    {editItem===item.id?(
                      <div>
                        <div style={S.formTitle}>Edytuj produkt</div>
                        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                          <div style={{width:64,height:64,borderRadius:11,overflow:"hidden",border:editForm.image?`2px solid ${ac}`:"2px dashed #ccc",
                            background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}
                            onClick={()=>setPickerCtx({type:"editItem"})}>
                            {editForm.image?<img src={editForm.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                              :<div style={{textAlign:"center",color:"#ccc"}}><div style={{fontSize:22}}>📷</div></div>}
                          </div>
                          <div>
                            <button style={S.smBtn} onClick={()=>setPickerCtx({type:"editItem"})}>{editForm.image?"Zmień foto":"Dodaj foto"}</button>
                            {editForm.image&&<button style={{...S.smBtn,background:"#ffebee",color:ac,marginTop:5}} onClick={()=>setEditForm(p=>({...p,image:null}))}>🗑 Usuń</button>}
                          </div>
                        </div>
                        <div style={S.fGrid}>
                          <input style={S.inp} placeholder="Emoji" value={editForm.emoji} onChange={e=>setEditForm({...editForm,emoji:e.target.value})}/>
                          <input style={S.inp} placeholder="Nazwa" value={editForm.name} onChange={e=>setEditForm({...editForm,name:e.target.value})}/>
                          <select style={S.inp} value={editForm.catId} onChange={e=>setEditForm({...editForm,catId:e.target.value})}>
                            <option value="">-- Kategoria --</option>
                            {allCatOpts.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                          </select>
                          <input style={S.inp} placeholder="Opis" value={editForm.desc} onChange={e=>setEditForm({...editForm,desc:e.target.value})}/>
                          <input style={S.inp} type="number" placeholder="Cena (zł)" value={editForm.price} onChange={e=>setEditForm({...editForm,price:e.target.value})}/>
                          <input style={S.inp} type="number" placeholder="Cena promo (zł)" value={editForm.promoPrice||""} onChange={e=>setEditForm({...editForm,promoPrice:e.target.value})}/>
                          <input style={{...S.inp,gridColumn:"span 2"}} type="number" placeholder="Czas (min)" value={editForm.wait} onChange={e=>setEditForm({...editForm,wait:e.target.value})}/>
                          <input style={{...S.inp,gridColumn:"span 2"}} placeholder="Skład / Składniki" value={editForm.ingredients||""} onChange={e=>setEditForm({...editForm,ingredients:e.target.value})}/>
                          <input style={{...S.inp,gridColumn:"span 2"}} placeholder="Alergeny" value={editForm.allergens||""} onChange={e=>setEditForm({...editForm,allergens:e.target.value})}/>
                        </div>
                        {/* DOSTĘPNOŚĆ */}
                        <div style={{marginTop:12,padding:"10px 14px",borderRadius:9,border:"1.5px solid "+(editForm.available!==false?"#bbf7d0":"#fecaca"),background:editForm.available!==false?"#f0fdf4":"#fff5f5",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <div>
                            <div style={{fontWeight:700,fontSize:13,color:editForm.available!==false?"#15803d":"#dc2626"}}>
                              {editForm.available!==false?"✅ Produkt dostępny":"❌ Produkt niedostępny"}
                            </div>
                            <div style={{fontSize:11,color:editForm.available!==false?"#166534":"#991b1b",marginTop:2}}>
                              {editForm.available!==false?"Klienci mogą zamawiać ten produkt":"Produkt widoczny w menu jako niedostępny, nie można go zamówić"}
                            </div>
                          </div>
                          <div onClick={()=>setEditForm(p=>({...p,available:p.available===false}))}
                            style={{width:46,height:26,borderRadius:13,background:editForm.available!==false?"#22c55e":"#f87171",cursor:"pointer",position:"relative",transition:"background .2s",flexShrink:0}}>
                            <div style={{position:"absolute",top:3,left:editForm.available!==false?23:3,width:20,height:20,borderRadius:"50%",background:"#fff",boxShadow:"0 1px 3px rgba(0,0,0,0.2)",transition:"left .2s"}}/>
                          </div>
                        </div>
                        {/* DODATKI */}
                        <div style={{marginTop:14,borderTop:"1px solid "+TA.border,paddingTop:12}}>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                            <div style={{fontWeight:700,fontSize:13,color:TA.text}}>🧩 Dodatki (opcjonalne)</div>
                            <button onClick={()=>{
                              const addons = editForm.addons||[];
                              setEditForm(p=>({...p,addons:[...addons,{id:genId(),name:"",price:0,max:1}]}));
                            }} style={{...S.darkBtn,padding:"5px 12px",fontSize:12}}>+ Dodaj dodatek</button>
                          </div>
                          <div style={{fontSize:11,color:TA.muted,marginBottom:10}}>
                            Klient będzie mógł zaznaczyć dodatki przy zamawianiu — cena zostanie automatycznie dolączona.
                          </div>
                          {(editForm.addons||[]).length===0&&(
                            <div style={{textAlign:"center",padding:"12px",color:TA.muted,fontSize:12,background:"#f9fafb",borderRadius:8,border:"1px dashed "+TA.border}}>
                              Brak dodatków — kliknij "+ Dodaj dodatek" aby dodać
                            </div>
                          )}
                          {(editForm.addons||[]).map((addon,idx)=>(
                            <div key={addon.id} style={{display:"grid",gridTemplateColumns:"1fr 90px 70px 32px",gap:6,marginBottom:6,alignItems:"center"}}>
                              <input style={S.inp} placeholder="Nazwa dodatku (np. Podwójny ser)" value={addon.name}
                                onChange={e=>setEditForm(p=>({...p,addons:p.addons.map((a,i)=>i===idx?{...a,name:e.target.value}:a)}))}/>
                              <input style={{...S.inp,textAlign:"center"}} type="number" min="0" step="0.5" placeholder="Cena zł" value={addon.price||""}
                                onChange={e=>setEditForm(p=>({...p,addons:p.addons.map((a,i)=>i===idx?{...a,price:parseFloat(e.target.value)||0}:a)}))}/>
                              <input style={{...S.inp,textAlign:"center"}} type="number" min="1" max="10" placeholder="Max szt." value={addon.max||1}
                                onChange={e=>setEditForm(p=>({...p,addons:p.addons.map((a,i)=>i===idx?{...a,max:parseInt(e.target.value)||1}:a)}))}/>
                              <button onClick={()=>setEditForm(p=>({...p,addons:p.addons.filter((_,i)=>i!==idx)}))}
                                style={{background:"#ffebee",color:"#e94560",border:"none",borderRadius:7,width:32,height:36,cursor:"pointer",fontWeight:700,fontSize:14}}>🗑</button>
                            </div>
                          ))}
                          {(editForm.addons||[]).length>0&&(
                            <div style={{display:"grid",gridTemplateColumns:"1fr 90px 70px 32px",gap:6,marginBottom:4}}>
                              <div style={{fontSize:10,color:TA.muted,paddingLeft:4}}>Nazwa dodatku</div>
                              <div style={{fontSize:10,color:TA.muted,textAlign:"center"}}>Cena (zł)</div>
                              <div style={{fontSize:10,color:TA.muted,textAlign:"center"}}>Maks. szt.</div>
                              <div/>
                            </div>
                          )}
                        </div>
                        <div style={{display:"flex",gap:7,marginTop:12}}>
                          <button style={S.darkBtn} onClick={saveEditMenu}>Zapisz</button>
                          <button style={S.cancelBtn} onClick={()=>setEditItem(null)}>Anuluj</button>
                        </div>
                      </div>
                    ):(
                      <div style={{display:"flex",gap:9,alignItems:"center"}}>
                        <div style={{position:"relative",flexShrink:0}}>
                          <Thumb item={item} size={62}/>
                          <PhotoBtn item={item}/>
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:"flex",alignItems:"center",gap:5}}>
                            <span style={{fontWeight:700,fontSize:13,color:T.text}}>{item.name}</span>
                            {item.promoPrice&&<span style={{background:ac,color:"#fff",borderRadius:10,padding:"1px 6px",fontSize:9,fontWeight:800}}>PROMO</span>}
                            {item.available===false&&<span style={{background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:10,padding:"1px 6px",fontSize:9,fontWeight:800}}>NIEDOSTĘPNY</span>}
                          </div>
                          <div style={{fontSize:10,color:T.muted,marginTop:1}}>{chain.map(c=>c.name).join(" › ")||"Bez kategorii"}</div>
                          <div style={{fontSize:11,marginTop:2}}>
                            {item.promoPrice
                              ?<><span style={{textDecoration:"line-through",color:T.border}}>{fmt(item.price)} zł</span><span style={{color:ac,fontWeight:700,marginLeft:5}}>{fmt(item.promoPrice)} zł</span></>
                              :<span style={{color:ac,fontWeight:700}}>{fmt(item.price)} zł</span>}
                            <span style={{color:T.border,marginLeft:7}}>⏱ {item.wait} min</span>
                          </div>
                        </div>
                        <div style={{display:"flex",gap:5,flexShrink:0}}>
                          <button style={{...S.actBtn,background:item.available!==false?"#e0f2fe":"#fee2e2",color:item.available!==false?"#0369a1":"#dc2626",fontWeight:700,fontSize:11,padding:"5px 9px"}}
                            title={item.available!==false?"Oznacz jako niedostępny":"Oznacz jako dostępny"}
                            onClick={()=>setMenu(p=>p.map(i=>i.id===item.id?{...i,available:i.available===false}:i))}>
                            {item.available!==false?"🟢":"🔴"}
                          </button>
                          <button style={{...S.actBtn,background:item.active?"#e8f5e9":"#fff3e0"}}
                            onClick={()=>setMenu(p=>p.map(i=>i.id===item.id?{...i,active:!i.active}:i))}>
                            {item.active?"✅":"⏸"}
                          </button>
                          <button style={S.actBtn} onClick={()=>{setEditItem(item.id);setEditForm({...item,promoPrice:item.promoPrice != null ? item.promoPrice : ""});}}>✏</button>
                          <button style={{...S.actBtn,background:"#ffebee"}} onClick={()=>deleteMenuItem(item.id)}>🗑</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div>
                  {/* Pasek wyszukiwarki */}
                  <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
                    <div style={{flex:1,position:"relative"}}>
                      <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:14,color:TA.muted,pointerEvents:"none"}}>🔍</span>
                      <input
                        style={{...S.inp,paddingLeft:32}}
                        placeholder="Szukaj produktu po nazwie lub opisie…"
                        value={menuSearch}
                        onChange={e=>setMenuSearch(e.target.value)}
                      />
                    </div>
                    {menuSearch&&(
                      <button onClick={()=>setMenuSearch("")}
                        style={{...S.cancelBtn,padding:"8px 12px",fontSize:12,whiteSpace:"nowrap"}}>✕ Wyczyść</button>
                    )}
                  </div>

                  {/* Filtry kategorii — przyciski pill */}
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14,paddingBottom:10,borderBottom:"1px solid "+TA.border}}>
                    <button onClick={()=>setMenuCatFilter("all")}
                      style={{...S.pill,...(menuCatFilter==="all"?S.pillOn:{})}}>
                      Wszystkie ({menu.length})
                    </button>
                    {rootCats.map(rc=>{
                      const count = menu.filter(i=>getRootCatId(i.catId)===rc.id).length;
                      return (
                        <button key={rc.id} onClick={()=>setMenuCatFilter(rc.id)}
                          style={{...S.pill,...(menuCatFilter===rc.id?S.pillOn:{})}}>
                          {rc.emoji} {rc.name} ({count})
                        </button>
                      );
                    })}
                    {menu.some(i=>!i.catId)&&(
                      <button onClick={()=>setMenuCatFilter("none")}
                        style={{...S.pill,...(menuCatFilter==="none"?S.pillOn:{})}}>
                        Bez kategorii ({menu.filter(i=>!i.catId).length})
                      </button>
                    )}
                  </div>

                  {/* Licznik przy wyszukiwaniu */}
                  {menuSearch&&(
                    <div style={{fontSize:12,color:TA.muted,marginBottom:10}}>
                      Znaleziono: <strong>{filteredMenu.length}</strong> {filteredMenu.length===1?"produkt":filteredMenu.length<5?"produkty":"produktów"}
                    </div>
                  )}

                  {filteredMenu.length===0&&(
                    <div style={S.empty}>
                      <div style={{fontSize:32,marginBottom:8}}>🔍</div>
                      <div>Brak produktów pasujących do wyszukiwania</div>
                    </div>
                  )}

                  {/* Lista produktów — płaska przy wyszukiwaniu, zgrupowana normalnie */}
                  {menuSearch.trim() ? (
                    filteredMenu.map(item=>renderItem(item))
                  ) : (
                    <>
                      {rootCats.filter(rc=>grouped[rc.id]&&grouped[rc.id].length>0).map(rc=>(
                        <div key={rc.id} style={{marginBottom:20}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,paddingBottom:6,borderBottom:"2px solid "+TA.border}}>
                            <span style={{fontSize:18}}>{rc.emoji}</span>
                            <span style={{fontWeight:700,fontSize:14,color:TA.text}}>{rc.name}</span>
                            <span style={{fontSize:11,color:TA.muted,marginLeft:"auto",background:"#f3f4f6",borderRadius:20,padding:"2px 10px",fontWeight:600}}>
                              {grouped[rc.id].length} produktów
                            </span>
                          </div>
                          {grouped[rc.id].map(item=>renderItem(item))}
                        </div>
                      ))}
                      {uncategorized.length>0&&(
                        <div style={{marginBottom:20}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,paddingBottom:6,borderBottom:"2px solid "+TA.border}}>
                            <span style={{fontSize:18}}>❓</span>
                            <span style={{fontWeight:700,fontSize:14,color:TA.text}}>Bez kategorii</span>
                            <span style={{fontSize:11,color:TA.muted,marginLeft:"auto",background:"#f3f4f6",borderRadius:20,padding:"2px 10px",fontWeight:600}}>
                              {uncategorized.length} produktów
                            </span>
                          </div>
                          {uncategorized.map(item=>renderItem(item))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── PROMO ── */}
        {tab==="promo" && (isSuperAdmin || (isTabVisible("promo", user.role) && rolePerm("adminPromo"))) && (
          <div>
            <div style={{background:`linear-gradient(135deg,${ac},#ff6b35)`,borderRadius:14,padding:"14px",marginBottom:13,color:"#fff"}}>
              <div style={{fontWeight:800,fontSize:15,marginBottom:3}}>🔥 Zarządzanie promocjami</div>
              <div style={{fontSize:12,opacity:0.85}}>Wpisz cenę promocyjną. Pozostaw puste, aby usunąć promocję.</div>
            </div>
            {menu.filter(i=>i.active).map(item=>(
              <div key={item.id} style={S.card}>
                <div style={{display:"flex",gap:9,alignItems:"center"}}>
                  <Thumb item={item} size={50}/>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13,color:T.text}}>{item.name}</div>
                    <div style={{fontSize:11,color:T.muted}}>Cena regularna: {fmt(item.price)} zł</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                    <input type="number" placeholder="Promo zł" value={item.promoPrice != null ? item.promoPrice : ""} onChange={e=>{
                      const v=e.target.value;
                      setMenu(p=>p.map(i=>i.id===item.id?{...i,promoPrice:v?parseFloat(v):null}:i));
                    }} style={{width:85,...S.inp,padding:"7px 9px",fontSize:12,borderColor:item.promoPrice?ac:"#ddd"}}/>
                    {item.promoPrice&&(
                      <div style={{background:ac,color:"#fff",borderRadius:7,padding:"3px 7px",fontSize:10,fontWeight:800,whiteSpace:"nowrap"}}>
                        -{Math.round((1-item.promoPrice/item.price)*100)}%
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}


        {/* ══════════════ TABLES TAB ══════════════════════════════════════════════ */}
        {tab==="tables" && (isSuperAdmin || (isTabVisible("tables", user.role) && rolePerm("adminTables"))) && (
          isAdmin 
            ? <TablesPanel tables={tables} setTables={setTables} orders={orders} ac={ac} settings={settings} setSettings={setSettings}/>
            : <SellerTablesPanel tables={tables} setTables={setTables} orders={orders} ac={ac} settings={settings}/>
        )}

        {/* ── CATEGORIES ── */}
        {tab==="cats" && (isSuperAdmin || (isTabVisible("cats", user.role) && rolePerm("adminCats"))) && (
          <div>
            <button style={S.accentBtn(ac)} onClick={()=>setShowAddCat(p=>!p)}>+ Dodaj kategorię</button>
            {showAddCat&&(
              <div style={S.formCard}>
                <div style={S.formTitle}>Nowa kategoria</div>
                <div style={S.fGrid}>
                  <input style={S.inp} placeholder="Emoji" value={newCat.emoji} onChange={e=>setNewCat({...newCat,emoji:e.target.value})}/>
                  <input style={S.inp} placeholder="Nazwa kategorii" value={newCat.name} onChange={e=>setNewCat({...newCat,name:e.target.value})}/>
                  <select style={{...S.inp,gridColumn:"span 2"}} value={newCat.parentId} onChange={e=>setNewCat({...newCat,parentId:e.target.value})}>
                    <option value="">Brak (główna kategoria)</option>
                    {cats.filter(c=>!c.parentId).map(c=><option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                  </select>
                </div>
                {/* Zdjęcie kategorii — nowa */}
                <div style={{marginTop:10,display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:52,height:52,borderRadius:10,overflow:"hidden",background:T.bg,
                    border:"1.5px solid "+T.border,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {newCat.image
                      ? <img src={newCat.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      : <span style={{fontSize:26}}>{newCat.emoji||"🍽"}</span>}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:600,color:T.muted,marginBottom:5}}>Zdjęcie kategorii (opcjonalne)</div>
                    <div style={{display:"flex",gap:6}}>
                      <button style={{...S.smBtn,background:ac,color:"#fff",border:"none"}}
                        onClick={()=>setPickerCtx({type:"newCat"})}>📷 Dodaj zdjęcie</button>
                      {newCat.image&&<button style={{...S.smBtn,color:"#e94560",borderColor:"#fca5a5"}}
                        onClick={()=>setNewCat(p=>({...p,image:null}))}>✕ Usuń</button>}
                    </div>
                  </div>
                </div>
                <div style={{display:"flex",gap:7,marginTop:9}}>
                  <button style={S.darkBtn} onClick={addCat}>Dodaj</button>
                  <button style={S.cancelBtn} onClick={()=>setShowAddCat(false)}>Anuluj</button>
                </div>
              </div>
            )}

            {getRoots(cats).map(root=>(
              <div key={root.id} style={{marginBottom:10}}>
                <div style={S.card}>
                  {editCat===root.id?(
                    <div>
                      <div style={S.fGrid}>
                        <input style={S.inp} placeholder="Emoji" value={editCForm.emoji} onChange={e=>setEditCForm({...editCForm,emoji:e.target.value})}/>
                        <input style={S.inp} placeholder="Nazwa" value={editCForm.name} onChange={e=>setEditCForm({...editCForm,name:e.target.value})}/>
                      </div>
                      {/* Zdjęcie kategorii — edycja */}
                      <div style={{marginTop:10,display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:52,height:52,borderRadius:10,overflow:"hidden",background:T.bg,
                          border:"1.5px solid "+T.border,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          {editCForm.image
                            ? <img src={editCForm.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                            : <span style={{fontSize:26}}>{editCForm.emoji||"🍽"}</span>}
                        </div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:11,fontWeight:600,color:T.muted,marginBottom:5}}>Zdjęcie kategorii</div>
                          <div style={{display:"flex",gap:6}}>
                            <button style={{...S.smBtn,background:ac,color:"#fff",border:"none"}}
                              onClick={()=>setPickerCtx({type:"cat",id:root.id})}>📷 {editCForm.image?"Zmień":"Dodaj"}</button>
                            {editCForm.image&&<button style={{...S.smBtn,color:"#e94560",borderColor:"#fca5a5"}}
                              onClick={()=>setEditCForm(p=>({...p,image:null}))}>✕ Usuń</button>}
                          </div>
                        </div>
                      </div>
                      <div style={{display:"flex",gap:7,marginTop:8}}>
                        <button style={S.darkBtn} onClick={saveEditCat}>Zapisz</button>
                        <button style={S.cancelBtn} onClick={()=>setEditCat(null)}>Anuluj</button>
                      </div>
                    </div>
                  ):(
                    <div style={{display:"flex",alignItems:"center",gap:9}}>
                      {/* Miniatura zdjęcia lub emoji */}
                      <div style={{width:44,height:44,borderRadius:10,overflow:"hidden",background:T.bg,
                        border:"1.5px solid "+T.border,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {root.image
                          ? <img src={root.image} alt={root.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                          : <span style={{fontSize:24}}>{root.emoji}</span>}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:800,fontSize:14,color:T.text}}>{root.name}</div>
                        <div style={{fontSize:11,color:T.muted}}>{menu.filter(i=>getDescendantIds(cats,root.id).includes(i.catId)).length} produktów{root.image?" · 📷":""}</div>
                      </div>
                      <div style={{display:"flex",gap:5}}>
                        <button style={S.actBtn} onClick={()=>{setEditCat(root.id);setEditCForm({...root});}}>✏</button>
                        <button style={{...S.actBtn,background:"#ffebee"}} onClick={()=>deleteCat(root.id)}>🗑</button>
                      </div>
                    </div>
                  )}
                </div>
                {getChildren(cats,root.id).map(sub=>(
                  <div key={sub.id} style={{...S.card,marginLeft:16,marginTop:-5,borderLeft:"3px solid "+ac+"33"}}>
                    {editCat===sub.id?(
                      <div>
                        <div style={S.fGrid}>
                          <input style={S.inp} placeholder="Emoji" value={editCForm.emoji} onChange={e=>setEditCForm({...editCForm,emoji:e.target.value})}/>
                          <input style={S.inp} placeholder="Nazwa" value={editCForm.name} onChange={e=>setEditCForm({...editCForm,name:e.target.value})}/>
                        </div>
                        {/* Zdjęcie podkategorii — edycja */}
                        <div style={{marginTop:10,display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:44,height:44,borderRadius:8,overflow:"hidden",background:T.bg,
                            border:"1.5px solid "+T.border,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                            {editCForm.image
                              ? <img src={editCForm.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                              : <span style={{fontSize:20}}>{editCForm.emoji||"🍽"}</span>}
                          </div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:11,fontWeight:600,color:T.muted,marginBottom:5}}>Zdjęcie podkategorii</div>
                            <div style={{display:"flex",gap:6}}>
                              <button style={{...S.smBtn,background:ac,color:"#fff",border:"none"}}
                                onClick={()=>setPickerCtx({type:"cat",id:sub.id})}>📷 {editCForm.image?"Zmień":"Dodaj"}</button>
                              {editCForm.image&&<button style={{...S.smBtn,color:"#e94560",borderColor:"#fca5a5"}}
                                onClick={()=>setEditCForm(p=>({...p,image:null}))}>✕ Usuń</button>}
                            </div>
                          </div>
                        </div>
                        <div style={{display:"flex",gap:7,marginTop:8}}>
                          <button style={S.darkBtn} onClick={saveEditCat}>Zapisz</button>
                          <button style={S.cancelBtn} onClick={()=>setEditCat(null)}>Anuluj</button>
                        </div>
                      </div>
                    ):(
                      <div style={{display:"flex",alignItems:"center",gap:9}}>
                        <div style={{width:34,height:34,borderRadius:8,overflow:"hidden",background:T.bg,
                          border:"1.5px solid "+T.border,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          {sub.image
                            ? <img src={sub.image} alt={sub.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                            : <span style={{fontSize:16,opacity:0.7}}>{sub.emoji}</span>}
                        </div>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:600,fontSize:12,color:T.muted}}>↳ {sub.name}</div>
                          <div style={{fontSize:10,color:T.muted}}>{menu.filter(i=>i.catId===sub.id).length} produktów{sub.image?" · 📷":""}</div>
                        </div>
                        <div style={{display:"flex",gap:5}}>
                          <button style={S.actBtn} onClick={()=>{setEditCat(sub.id);setEditCForm({...sub});}}>✏</button>
                          <button style={{...S.actBtn,background:"#ffebee"}} onClick={()=>deleteCat(sub.id)}>🗑</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}


        {/* ── CAMPAIGNS ── */}
        {tab==="campaigns" && (isSuperAdmin || (isTabVisible("campaigns", user.role) && rolePerm("adminCampaigns"))) && (
            <div>
              <div style={{background:"linear-gradient(135deg,#1a1a2e,#16213e)",borderRadius:14,padding:14,marginBottom:12,color:"#fff"}}>
                <div style={{fontWeight:800,fontSize:15,marginBottom:3}}>📣 Kampanie reklamowe</div>
                <div style={{fontSize:12,opacity:0.8}}>Twórz kampanie łączące baner reklamowy z kodem rabatowym. Klient widzi kampanię jako pasek i/lub modal z kuponem.</div>
              </div>
              <button style={S.accentBtn(ac)} onClick={()=>setCampaigns(p=>[...p,{id:genId(),name:"Nowa kampania "+(p.length+1),active:true,dateFrom:"",dateTo:"",image:null,accentColor:ac,headline:"",desc:"",voucherCode:"",ctaLabel:"Zobacz →",showOnTop:true,showModal:true}])}>+ Nowa kampania</button>
              {campaigns.length===0&&<div style={S.empty}><div style={{fontSize:40}}>📣</div><p>Brak kampanii</p></div>}
              {campaigns.map((cp,ci)=>(
                <div key={cp.id} style={{...S.card,borderLeft:"4px solid "+(cp.active?"#27ae60":"#bbb")}}>
                  {/* Header row */}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:800,fontSize:13,color:T.text}}>{cp.name||"Bez nazwy"}</div>
                      <div style={{fontSize:10,color:cp.active?"#27ae60":"#bbb",fontWeight:700,marginTop:2}}>{cp.active?"● Aktywna":"○ Nieaktywna"} · {cp.dateFrom||"∞"} → {cp.dateTo||"∞"}</div>
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button style={{...S.actBtn,background:cp.active?"#e8f5e9":"#fff3e0"}}
                        onClick={()=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,active:!x.active}:x))}>
                        {cp.active?"✅":"⏸"}
                      </button>
                      <button style={{...S.actBtn,background:"#ffebee"}} onClick={()=>setCampaigns(p=>p.filter(x=>x.id!==cp.id))}>🗑</button>
                    </div>
                  </div>

                  {/* Image preview + upload */}
                  <div style={{height:70,borderRadius:9,overflow:"hidden",background:cp.image?`url(${cp.image}) center/cover`:`linear-gradient(135deg,${cp.accentColor||ac},#1a1a2e)`,
                    marginBottom:10,cursor:"pointer",position:"relative",display:"flex",alignItems:"center",justifyContent:"center"}}
                    onClick={()=>setPickerCtx({type:"campaign",id:cp.id})}>
                    {cp.image&&<div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.35)"}}/>}
                    <div style={{position:"relative",color:"#fff",fontWeight:700,fontSize:12}}>{cp.image?"🖼 Zmień zdjęcie kampanii":"📷 Dodaj zdjęcie kampanii"}</div>
                  </div>

                  {/* Fields */}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7}}>
                    <input style={{...S.inp,gridColumn:"span 2"}} placeholder="Nazwa kampanii (wewnętrzna)" value={cp.name}
                      onChange={e=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,name:e.target.value}:x))}/>
                    <input style={S.inp} placeholder="Nagłówek dla klienta" value={cp.headline}
                      onChange={e=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,headline:e.target.value}:x))}/>
                    <input style={S.inp} placeholder="Tekst przycisku" value={cp.ctaLabel}
                      onChange={e=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,ctaLabel:e.target.value}:x))}/>
                    <input style={{...S.inp,gridColumn:"span 2"}} placeholder="Opis kampanii (widoczny w modalu)" value={cp.desc}
                      onChange={e=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,desc:e.target.value}:x))}/>
                    <input style={S.inp} type="date" placeholder="Data od" value={cp.dateFrom}
                      onChange={e=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,dateFrom:e.target.value}:x))}/>
                    <input style={S.inp} type="date" placeholder="Data do" value={cp.dateTo}
                      onChange={e=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,dateTo:e.target.value}:x))}/>
                    <select style={S.inp} value={cp.voucherCode}
                      onChange={e=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,voucherCode:e.target.value}:x))}>
                      <option value="">Brak kuponu</option>
                      {vouchers.filter(v=>v.active).map(v=><option key={v.id} value={v.code}>{v.code} — {v.desc}</option>)}
                    </select>
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"0 4px"}}>
                      <label style={{fontSize:11,color:T.muted,fontWeight:600}}>Kolor:</label>
                      <input type="color" value={cp.accentColor||ac} style={{width:32,height:32,border:"none",borderRadius:"50%",cursor:"pointer",padding:0}}
                        onChange={e=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,accentColor:e.target.value}:x))}/>
                    </div>
                  </div>

                  {/* Visibility toggles */}
                  <div style={{display:"flex",gap:8,marginTop:9}}>
                    <button style={{...S.smBtn,flex:1,background:cp.showOnTop?"#e8f5e9":"#f0f2f5",color:cp.showOnTop?"#27ae60":"#666",textAlign:"center"}}
                      onClick={()=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,showOnTop:!x.showOnTop}:x))}>
                      {cp.showOnTop?"✅":"○"} Pasek górny
                    </button>
                    <button style={{...S.smBtn,flex:1,background:cp.showModal?"#e8f5e9":"#f0f2f5",color:cp.showModal?"#27ae60":"#666",textAlign:"center"}}
                      onClick={()=>setCampaigns(p=>p.map(x=>x.id===cp.id?{...x,showModal:!x.showModal}:x))}>
                      {cp.showModal?"✅":"○"} Modal popup
                    </button>
                  </div>
                </div>
              ))}
            </div>
        )}

        {/* ── VOUCHERS ── */}
        {tab==="vouchers" && (isSuperAdmin || (isTabVisible("vouchers", user.role) && adminPerm("adminVouchers"))) && (
          <div>
            <div style={{background:"linear-gradient(135deg,#1a1a2e,#16213e)",borderRadius:14,padding:14,marginBottom:12,color:"#fff"}}>
              <div style={{fontWeight:800,fontSize:15,marginBottom:3}}>🎟 Bony zniżkowe</div>
              <div style={{fontSize:12,opacity:0.8}}>Twórz kody rabatowe: procentowe lub kwotowe. Klienci wpisują kod przy kasie. Możesz przypisać kod do kampanii reklamowej.</div>
            </div>
            <VoucherAdminPanel vouchers={vouchers} setVouchers={setVouchers} usedCodes={usedCodes} ac={ac}/>
          </div>
        )}
        {/* ── SALES ── */}
        {tab==="sales" && (isSuperAdmin || (isTabVisible("sales", user.role) && rolePerm("adminSales"))) && (
          <div>
            {/* ── RAPORTY PDF ── */}
            <div style={{...S.formCard,marginBottom:14}}>
              <div style={S.formTitle}>📥 Pobierz raport sprzedaży</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:12,lineHeight:1.5}}>
                Generuj szczegółowy raport PDF z podsumowaniem zamówień, przychodów, metod płatności i top produktów.
              </div>
              <div style={{display:"grid",gridTemplateColumns:bp.isMobile?"1fr":"1fr 1fr 1fr",gap:9}}>
                {[
                  {period:"day",   icon:"📅", label:"Raport dzienny",     sub:"Dzisiejsza sprzedaż"},
                  {period:"week",  icon:"📆", label:"Raport tygodniowy",  sub:"Bieżący tydzień (Pn–Nd)"},
                  {period:"month", icon:"🗓", label:"Raport miesięczny",  sub:"Bieżący miesiąc"},
                ].map(({period,icon,label,sub})=>(
                  <button key={period}
                    onClick={()=>generateSalesReport(orders, settings, period)}
                    style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5,padding:"14px 10px",
                      background:T.bg,border:"1.5px solid "+T.border,borderRadius:12,cursor:"pointer",
                      transition:"all .15s",fontFamily:"inherit"}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=ac;e.currentTarget.style.background=ac+"12";}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.background=T.bg;}}>
                    <span style={{fontSize:26}}>{icon}</span>
                    <span style={{fontWeight:800,fontSize:12,color:T.primary}}>{label}</span>
                    <span style={{fontSize:10,color:T.muted}}>{sub}</span>
                    <span style={{fontSize:10,color:"#fff",background:ac,borderRadius:20,padding:"2px 10px",marginTop:2,fontWeight:700}}>⬇ PDF</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={{display:"grid",gridTemplateColumns: bp.isDesktop?"repeat(4,1fr)": bp.isTablet?"repeat(4,1fr)":"1fr 1fr",gap:9,marginBottom:16}}>
              {[["📦",todayOrders.length,"Zamówień dziś"],["💰",`${fmt(todayRevenue)} zł`,"Przychód dziś"],
                ["🍽",todayQty,"Pozycji sprzed."],["📈",(todayOrders.length?fmt(todayRevenue/todayOrders.length):"0.00")+" zł","Śr. zamówienie"]
              ].map(([icon,val,lbl])=>(
                <div key={lbl} style={{background:T.surface,borderRadius:13,padding:"13px 10px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
                  <div style={{fontSize:24,marginBottom:5}}>{icon}</div>
                  <div style={{fontSize:17,fontWeight:900,color:T.text}}>{val}</div>
                  <div style={{fontSize:10,color:T.muted,marginTop:3}}>{lbl}</div>
                </div>
              ))}
            </div>
            {/* Payment method breakdown */}
            <h3 style={{margin:"0 0 9px",fontSize:13,fontWeight:800,color:T.text}}>💳 Metody płatności dziś</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
              {[
                ["📱","BLIK","blik","#e94560"],
                ["💳","Karta","card","#0984e3"],
                ["💵","Gotówka","cash","#27ae60"],
              ].map(([icon,label,key,col])=>{
                const cnt = todayOrders.filter(o=>o.paymentMethod===key).length;
                const rev = todayOrders.filter(o=>o.paymentMethod===key).reduce((s,o)=>s+o.total,0);
                return (
                  <div key={key} style={{background:T.surface,borderRadius:12,padding:"10px 8px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",borderTop:`3px solid ${col}`}}>
                    <div style={{fontSize:20}}>{icon}</div>
                    <div style={{fontWeight:800,fontSize:12,color:T.text,marginTop:4}}>{cnt}</div>
                    <div style={{fontSize:9,color:T.muted}}>{label}</div>
                    {cnt>0&&<div style={{fontSize:9,color:col,fontWeight:700,marginTop:2}}>{fmt(rev)} zł</div>}
                  </div>
                );
              })}
            </div>

            {/* Kitchen status breakdown */}
            <h3 style={{margin:"0 0 9px",fontSize:13,fontWeight:800,color:T.text}}>👨‍🍳 Status kuchenny dziś</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
              {Object.entries(COOK_STATUS).map(([key,cs])=>{
                const cnt = todayOrders.filter(o=>(o.cookStatus||"new")===key).length;
                return (
                  <div key={key} style={{background:cs.bg,borderRadius:12,padding:"10px 8px",textAlign:"center",border:`2px solid ${cs.border}`}}>
                    <div style={{fontSize:20}}>{cs.icon}</div>
                    <div style={{fontWeight:800,fontSize:15,color:cs.color,marginTop:4}}>{cnt}</div>
                    <div style={{fontSize:9,color:cs.color,fontWeight:700}}>{cs.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Payment status */}
            <h3 style={{margin:"0 0 9px",fontSize:13,fontWeight:800,color:T.text}}>💸 Status płatności dziś</h3>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
              {[
                {icon:"✅",label:"Zapłacone",   color:"#27ae60",bg:"#e8f5e9",filter:o=>o.paid},
                {icon:"💸",label:"Niezapłacone",color:"#e94560",bg:"#fff0f0",filter:o=>!o.paid},
              ].map(({icon,label,color,bg,filter})=>{
                const cnt = todayOrders.filter(filter).length;
                const rev = todayOrders.filter(filter).reduce((s,o)=>s+o.total,0);
                return (
                  <div key={label} style={{background:bg,borderRadius:12,padding:"12px 10px",textAlign:"center",border:`2px solid ${color}`}}>
                    <div style={{fontSize:22}}>{icon}</div>
                    <div style={{fontWeight:900,fontSize:16,color,marginTop:4}}>{cnt}</div>
                    <div style={{fontSize:9,color,fontWeight:700,marginBottom:3}}>{label}</div>
                    {cnt>0&&<div style={{fontSize:11,color,fontWeight:700}}>{fmt(rev)} zł</div>}
                  </div>
                );
              })}
            </div>

            <h3 style={{margin:"0 0 9px",fontSize:13,fontWeight:800,color:T.text}}>🏆 Top produkty dziś</h3>
            {topProducts.length===0
              ?<div style={S.empty}><div style={{fontSize:36}}>📊</div><p>Brak danych na dziś</p></div>
              :topProducts.map((p,i)=>(
              <div key={p.name} style={{...S.card,display:"flex",gap:8,alignItems:"center"}}>
                <span style={{fontWeight:900,fontSize:15,color:ac,minWidth:22}}>#{i+1}</span>
                <div style={{width:36,height:36,borderRadius:8,overflow:"hidden",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {p.image?<img src={p.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:<span style={{fontSize:20}}>{p.emoji}</span>}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:12,color:T.text}}>{p.name}</div>
                  <div style={{fontSize:10,color:T.muted}}>{p.qty} szt.</div>
                </div>
                <div style={{fontWeight:800,color:ac,fontSize:12}}>{fmt(p.rev)} zł</div>
              </div>
            ))}
          </div>
        )}

        {/* ── USERS ── */}


        {/* ── SUPER ADMIN PANEL ── */}
        {tab==="superadmin"&&isSuperAdmin&&(
          <div>
            {/* ── Nawigacja podkategorii Super Admin ── */}
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16,padding:"10px 14px",background:"#faf5ff",borderRadius:12,border:"2px solid #c4b5fd"}}>
              {[
                {k:"users",       label:"👥 Użytkownicy"},
                {k:"roles",       label:"🎭 Role"},
                {k:"permissions", label:"🔧 Uprawnienia"},
                {k:"visibility",  label:"👁 Widoczność menu"},
                {k:"documents",   label:"📄 Szablony dokumentów"},
                {k:"logs",        label:"📋 Logi"},
                {k:"hardware",    label:"🖨 Sprzęt"},
                {k:"backup",      label:"💾 Kopie"},
                {k:"database",    label:"🗄 Baza danych"},
              ].map(({k, label}) => (
                <button key={k} onClick={() => setSaSubTab(k)}
                  style={{padding:"7px 14px",borderRadius:8,border:"none",fontWeight:700,fontSize:12,cursor:"pointer",
                    background:saSubTab===k?"#6d28d9":"#ede9fe",
                    color:saSubTab===k?"#fff":"#6d28d9",
                    transition:"all .15s",
                  }}>
                  {label}
                </button>
              ))}
            </div>

            {/* ══ PODKATEGORIA: UŻYTKOWNICY ══ */}
            {saSubTab==="users" && (
              <div>
                <div style={{...S.formCard,border:"2px solid #6d28d9",background:"#faf5ff"}}>
                  <div style={{...S.formTitle,color:"#6d28d9",borderBottomColor:"#ddd6fe"}}>
                    🛡 Panel Super Administratora
                  </div>
                  <div style={{fontSize:12,color:"#7c3aed",lineHeight:1.7,marginBottom:14}}>
                    Ten panel jest dostępny wyłącznie dla Super Administratora. Możesz zarządzać wszystkimi użytkownikami,
                    w tym kontami Głównych Administratorów, zmieniać ich hasła oraz role.
                  </div>
                  <div style={{background:"#ede9fe",borderRadius:10,padding:"12px 14px",marginBottom:14,border:"1.5px solid #c4b5fd"}}>
                    <div style={{fontWeight:700,fontSize:12,color:"#6d28d9",marginBottom:6}}>🔐 Uprawnienia Super Administratora:</div>
                    <div style={{fontSize:11,color:"#5b21b6",lineHeight:2}}>
                      ✅ Zmiana hasła Głównego Administratora<br/>
                      ✅ Zmiana roli dowolnego użytkownika<br/>
                      ✅ Usuwanie kont Głównych Administratorów<br/>
                      ✅ Tworzenie nowych Super Administratorów<br/>
                      ✅ Pełny dostęp do wszystkich modułów systemu<br/>
                      🔒 Hasło Super Administratora może zmienić tylko sam Super Administrator
                    </div>
                  </div>

                  {/* ── Nagłówek z przyciskiem dodawania ── */}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                    <div style={{fontWeight:700,fontSize:13,color:"#6d28d9"}}>
                      Wszyscy użytkownicy systemu ({users.length}):
                    </div>
                    <button
                      onClick={()=>{setShowAddUser(p=>!p); setNewUser({...BLANK_USER}); setEditUser(null);}}
                      style={{
                        padding:"9px 18px",
                        background:showAddUser?"#f3f4f6":"#6d28d9",
                        color:showAddUser?"#6b7280":"#fff",
                        border:"none",borderRadius:10,fontWeight:700,
                        cursor:"pointer",fontSize:13,
                        display:"flex",alignItems:"center",gap:6,
                      }}>
                      {showAddUser?"✕ Anuluj":"+ Dodaj użytkownika"}
                    </button>
                  </div>

                  {/* ── Formularz nowego użytkownika ── */}
                  {showAddUser && (
                    <div style={{background:"#fff",borderRadius:12,border:"2px solid #6d28d9",padding:16,marginBottom:16,boxShadow:"0 4px 16px rgba(109,40,217,0.12)"}}>
                      <UserForm
                        title="👤 Nowy użytkownik"
                        form={newUser}
                        setForm={setNewUser}
                        onSave={addUser}
                        onCancel={()=>{ setShowAddUser(false); setNewUser({...BLANK_USER}); }}
                        currentUser={user}
                        customRoles={customRoles}
                      />
                    </div>
                  )}

                  {/* ── Lista użytkowników ── */}
                  {users.map(u=>{
                    const rd = allRoleDefs[u.role] || ROLE_DEFS.seller;
                    return (
                      <div key={u.id} style={{...S.card,border:"1.5px solid "+(u.role==="superadmin"?"#c4b5fd":T.border),marginBottom:8}}>
                        {editUser===u.id?(
                          <UserForm
                            title={"✏ Edytuj: "+(u.name||u.login)}
                            form={editUForm} setForm={setEditUForm}
                            onSave={saveEditUser} onCancel={()=>setEditUser(null)}
                            currentUser={user}
                            customRoles={customRoles}
                          />
                        ):(
                          <div style={{display:"flex",gap:10,alignItems:"center"}}>
                            <div style={{width:42,height:42,borderRadius:"50%",background:rd.bg,border:"2px solid "+rd.color+"44",
                              display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                              {rd.icon}
                            </div>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:700,fontSize:13,color:T.text}}>{u.name||u.login}</div>
                              <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:3}}>
                                <span style={{fontSize:10,fontWeight:700,borderRadius:20,padding:"2px 9px",background:rd.bg,color:rd.color}}>
                                  {rd.icon} {rd.label}
                                </span>
                                {/* Dodatkowe role */}
                                {(u.extraRoles||[]).map(erId=>{
                                  const er = allRoleDefs[erId]; if(!er) return null;
                                  return <span key={erId} style={{fontSize:10,fontWeight:700,borderRadius:20,padding:"2px 9px",background:er.bg,color:er.color}}>+{er.icon} {er.label}</span>;
                                })}
                              </div>
                              <span style={{fontSize:10,color:T.muted}}>@{u.login}</span>
                            </div>
                            <div style={{display:"flex",gap:5,flexShrink:0}}>
                              <button style={S.actBtn} onClick={()=>{
                                setShowAddUser(false);
                                setEditUser(u.id);
                                setEditUForm({...u,notifications:u.notifications||rd.notifications});
                              }}>✏ Edytuj</button>
                              {u.id!==user.id&&(
                                <button style={{...S.actBtn,background:"#ffebee",color:"#dc2626",fontWeight:700}} onClick={()=>{
                                  if(window.confirm(`Usunąć użytkownika "${u.name||u.login}"?\n\nTa operacja jest nieodwracalna.`))
                                    setUsers(p=>p.filter(uu=>uu.id!==u.id));
                                }}>🗑 Usuń</button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* ── Zarządzanie rolami dodatkowymi ── */}
                  <div style={{marginTop:20,paddingTop:16,borderTop:"2px dashed #ddd6fe"}}>
                    <div style={{fontWeight:700,fontSize:13,color:"#6d28d9",marginBottom:4}}>🎭 Dodatkowe role użytkowników</div>
                    <div style={{fontSize:11,color:"#7c3aed",marginBottom:12,lineHeight:1.6}}>
                      Przypisz użytkownikowi dodatkowe role obok głównej — np. Sprzedawca może mieć też dostęp jak Kucharz.
                    </div>
                    {users.filter(u=>u.id!==user.id).map(u=>{
                      const rd = allRoleDefs[u.role] || ROLE_DEFS.seller;
                      const extraRoles = u.extraRoles || [];
                      const availableRoles = Object.entries(allRoleDefs)
                        .filter(([k])=> k !== u.role && k !== "superadmin")
                        .map(([k,v])=>({id:k,...v}));
                      return (
                        <div key={u.id} style={{...S.card,marginBottom:8,border:"1.5px solid #ddd6fe"}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                            <div style={{width:32,height:32,borderRadius:"50%",background:rd.bg,border:"2px solid "+rd.color+"44",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>{rd.icon}</div>
                            <div>
                              <div style={{fontWeight:700,fontSize:12,color:T.text}}>{u.name||u.login}</div>
                              <div style={{fontSize:10,color:T.muted}}>Główna rola: {rd.icon} {rd.label}</div>
                            </div>
                          </div>
                          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                            {availableRoles.map(r=>{
                              const isActive = extraRoles.includes(r.id);
                              return (
                                <button key={r.id}
                                  onClick={()=>setUsers(p=>p.map(x=> x.id!==u.id ? x : {
                                    ...x,
                                    extraRoles: isActive
                                      ? (x.extraRoles||[]).filter(e=>e!==r.id)
                                      : [...(x.extraRoles||[]), r.id]
                                  }))}
                                  style={{
                                    padding:"5px 11px",borderRadius:20,
                                    border:"1.5px solid "+(isActive?r.color||"#6d28d9":"#ddd6fe"),
                                    background:isActive?r.bg||"#ede9fe":"#fff",
                                    color:isActive?r.color||"#6d28d9":"#9ca3af",
                                    fontWeight:700,fontSize:11,cursor:"pointer",
                                    transition:"all .15s",
                                  }}>
                                  {isActive?"✓ ":""}{r.icon} {r.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    {users.filter(u=>u.id!==user.id).length===0&&(
                      <div style={{fontSize:12,color:T.muted,textAlign:"center",padding:12}}>Brak innych użytkowników</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ══ PODKATEGORIA: ROLE ══ */}
            {saSubTab==="roles" && (
              <div style={{...S.formCard,border:"2px solid #6d28d9",background:"#faf5ff"}}>
                <div style={{...S.formTitle,color:"#6d28d9",borderBottomColor:"#ddd6fe"}}>
                  🎭 Zarządzanie rolami użytkowników
                </div>
                <div style={{fontSize:12,color:"#7c3aed",lineHeight:1.7,marginBottom:14}}>
                  Twórz własne role — Magazynier, Dostawca, Kelner, Kasjer itp. Każda rola może mieć własną nazwę, ikonę i kolor. Przypisujesz ją użytkownikowi w zakładce Użytkownicy.
                </div>

                {/* ── Role wbudowane (info) ── */}
                <div style={{marginBottom:16}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#6d28d9",marginBottom:8}}>🔒 Role systemowe (nieusuwalne):</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {Object.entries(ROLE_DEFS).map(([k,r])=>(
                      <span key={k} style={{padding:"4px 12px",borderRadius:20,background:r.bg,color:r.color,fontWeight:700,fontSize:11,border:"1.5px solid "+r.color+"44"}}>
                        {r.icon} {r.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* ── Przycisk dodaj ── */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <div style={{fontWeight:700,fontSize:13,color:"#6d28d9"}}>
                    Własne role ({(customRoles||[]).length}):
                  </div>
                  <button onClick={()=>{setShowAddRole(p=>!p); setNewRoleForm({...BLANK_ROLE}); setEditRoleId(null);}}
                    style={{padding:"9px 18px",background:showAddRole?"#f3f4f6":"#6d28d9",color:showAddRole?"#6b7280":"#fff",
                      border:"none",borderRadius:10,fontWeight:700,cursor:"pointer",fontSize:13}}>
                    {showAddRole?"✕ Anuluj":"+ Nowa rola"}
                  </button>
                </div>

                {/* ── Formularz nowej roli ── */}
                {showAddRole && (
                  <div style={{background:"#fff",borderRadius:12,border:"2px solid #6d28d9",padding:16,marginBottom:16,boxShadow:"0 4px 16px rgba(109,40,217,0.12)"}}>
                    <div style={{fontWeight:800,fontSize:13,color:"#6d28d9",marginBottom:12}}>+ Nowa rola</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Nazwa roli *</label>
                        <input value={newRoleForm.label} onChange={e=>setNewRoleForm(p=>({...p,label:e.target.value}))}
                          placeholder="np. Magazynier, Dostawca, Kelner"
                          style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid #ddd6fe",fontSize:13,boxSizing:"border-box"}}/>
                      </div>
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>ID (unikalne, bez spacji) *</label>
                        <input value={newRoleForm.id} onChange={e=>setNewRoleForm(p=>({...p,id:e.target.value.toLowerCase().replace(/\s+/g,"_").replace(/[^a-z0-9_]/g,"")}))}
                          placeholder="np. warehouseman, courier"
                          style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid #ddd6fe",fontSize:13,boxSizing:"border-box"}}/>
                      </div>
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Emoji / ikona</label>
                        <input value={newRoleForm.icon} onChange={e=>setNewRoleForm(p=>({...p,icon:e.target.value}))}
                          placeholder="np. 📦 🚚 🧾"
                          style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid #ddd6fe",fontSize:20,textAlign:"center",boxSizing:"border-box"}}/>
                      </div>
                      <div>
                        <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Kolor</label>
                        <div style={{display:"flex",gap:8,alignItems:"center"}}>
                          <input type="color" value={newRoleForm.color||"#2563eb"}
                            onChange={e=>{
                              const col=e.target.value;
                              const bg=col+"22";
                              setNewRoleForm(p=>({...p,color:col,bg}));
                            }}
                            style={{width:44,height:38,borderRadius:8,border:"1.5px solid #ddd6fe",cursor:"pointer",padding:2}}/>
                          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                            {[["#2563eb","#dbeafe"],["#059669","#d1fae5"],["#d97706","#fef3c7"],["#dc2626","#fee2e2"],["#7c3aed","#ede9fe"],["#0369a1","#e0f2fe"],["#92400e","#fef3c7"],["#be185d","#fce7f3"]].map(([col,bg])=>(
                              <div key={col} onClick={()=>setNewRoleForm(p=>({...p,color:col,bg}))}
                                style={{width:22,height:22,borderRadius:6,background:col,cursor:"pointer",border:newRoleForm.color===col?"3px solid #111":"2px solid transparent",flexShrink:0}}/>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Podgląd roli */}
                    <div style={{marginBottom:12,padding:"8px 14px",borderRadius:10,background:newRoleForm.bg||"#eff6ff",border:"1.5px solid "+(newRoleForm.color||"#2563eb")+"44",display:"inline-flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:20}}>{newRoleForm.icon||"👤"}</span>
                      <span style={{fontWeight:700,color:newRoleForm.color||"#2563eb",fontSize:13}}>{newRoleForm.label||"Podgląd roli"}</span>
                    </div>

                    {/* Uprawnienia roli */}
                    <div style={{marginBottom:12}}>
                      <div style={{fontWeight:700,fontSize:12,color:"#7c3aed",marginBottom:8}}>Uprawnienia tej roli:</div>
                      <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"8px 12px",borderRadius:9,background:"#fff",border:"1.5px solid #ddd6fe",marginBottom:6}}>
                        <input type="checkbox" checked={!!newRoleForm.canChangePassword}
                          onChange={e=>setNewRoleForm(p=>({...p,canChangePassword:e.target.checked}))}
                          style={{width:15,height:15,accentColor:"#6d28d9"}}/>
                        <span style={{fontSize:12,fontWeight:700,color:"#6d28d9"}}>🔑 Może zmieniać hasła innych użytkowników</span>
                      </label>
                    </div>

                    {/* Powiadomienia domyślne */}
                    <div style={{marginBottom:12}}>
                      <div style={{fontWeight:700,fontSize:12,color:"#7c3aed",marginBottom:8}}>Domyślne powiadomienia:</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                        {Object.entries(NOTIF_LABELS).map(([key,label])=>{
                          const on = (newRoleForm.notifications||{})[key];
                          return (
                            <label key={key} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer",padding:"7px 10px",borderRadius:9,background:on?"#faf5ff":"#fff",border:"1.5px solid "+(on?"#c4b5fd":"#e5e7eb")}}>
                              <input type="checkbox" checked={!!on}
                                onChange={()=>setNewRoleForm(p=>({...p,notifications:{...(p.notifications||{}), [key]:!on}}))}
                                style={{width:14,height:14,accentColor:"#6d28d9"}}/>
                              <span style={{fontSize:11,color:on?"#6d28d9":"#6b7280",fontWeight:on?700:400}}>{label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>{
                        if(!newRoleForm.label.trim()){alert("Podaj nazwę roli");return;}
                        if(!newRoleForm.id.trim()){alert("Podaj ID roli");return;}
                        if(customRoles.find(r=>r.id===newRoleForm.id)||ROLE_DEFS[newRoleForm.id]){alert("Rola o tym ID już istnieje");return;}
                        setCustomRoles(p=>[...(p||[]), {...newRoleForm}]);
                        setNewRoleForm({...BLANK_ROLE});
                        setShowAddRole(false);
                      }} style={{padding:"10px 22px",background:"#6d28d9",color:"#fff",border:"none",borderRadius:10,fontWeight:800,cursor:"pointer",fontSize:14}}>
                        💾 Zapisz rolę
                      </button>
                      <button onClick={()=>{setShowAddRole(false);setNewRoleForm({...BLANK_ROLE});}}
                        style={{padding:"10px 16px",background:"#f3f4f6",color:"#6b7280",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer"}}>
                        Anuluj
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Lista własnych ról ── */}
                {(customRoles||[]).length===0&&!showAddRole&&(
                  <div style={{textAlign:"center",padding:"32px 20px",color:"#a78bfa"}}>
                    <div style={{fontSize:40,marginBottom:10}}>🎭</div>
                    <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>Brak własnych ról</div>
                    <div style={{fontSize:12}}>Kliknij „+ Nowa rola" aby dodać np. Magazyniera, Dostawcę, Kelnera</div>
                  </div>
                )}

                {(customRoles||[]).map(r=>(
                  <div key={r.id} style={{...S.card,border:"1.5px solid "+r.color+"44",marginBottom:8}}>
                    {editRoleId===r.id?(
                      <div>
                        <div style={{fontWeight:700,fontSize:13,color:"#6d28d9",marginBottom:10}}>✏ Edytuj rolę: {r.label}</div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                          <div>
                            <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Nazwa</label>
                            <input value={editRoleForm.label||""} onChange={e=>setEditRoleForm(p=>({...p,label:e.target.value}))}
                              style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid #ddd6fe",fontSize:13,boxSizing:"border-box"}}/>
                          </div>
                          <div>
                            <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Ikona</label>
                            <input value={editRoleForm.icon||""} onChange={e=>setEditRoleForm(p=>({...p,icon:e.target.value}))}
                              style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid #ddd6fe",fontSize:20,textAlign:"center",boxSizing:"border-box"}}/>
                          </div>
                          <div style={{gridColumn:"span 2"}}>
                            <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Kolor</label>
                            <div style={{display:"flex",gap:6,alignItems:"center"}}>
                              <input type="color" value={editRoleForm.color||"#2563eb"}
                                onChange={e=>setEditRoleForm(p=>({...p,color:e.target.value,bg:e.target.value+"22"}))}
                                style={{width:44,height:36,borderRadius:8,border:"1.5px solid #ddd6fe",cursor:"pointer",padding:2}}/>
                              {[["#2563eb","#dbeafe"],["#059669","#d1fae5"],["#d97706","#fef3c7"],["#dc2626","#fee2e2"],["#7c3aed","#ede9fe"],["#0369a1","#e0f2fe"],["#92400e","#fef3c7"],["#be185d","#fce7f3"]].map(([col,bg])=>(
                                <div key={col} onClick={()=>setEditRoleForm(p=>({...p,color:col,bg}))}
                                  style={{width:22,height:22,borderRadius:6,background:col,cursor:"pointer",border:editRoleForm.color===col?"3px solid #111":"2px solid transparent"}}/>
                              ))}
                            </div>
                          </div>
                        </div>
                        <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"8px 12px",borderRadius:9,background:"#fff",border:"1.5px solid #ddd6fe",marginBottom:8}}>
                          <input type="checkbox" checked={!!editRoleForm.canChangePassword}
                            onChange={e=>setEditRoleForm(p=>({...p,canChangePassword:e.target.checked}))}
                            style={{width:15,height:15,accentColor:"#6d28d9"}}/>
                          <span style={{fontSize:12,fontWeight:700,color:"#6d28d9"}}>🔑 Może zmieniać hasła</span>
                        </label>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={()=>{
                            setCustomRoles(p=>p.map(x=>x.id===r.id?{...x,...editRoleForm}:x));
                            setEditRoleId(null);
                          }} style={{padding:"9px 20px",background:"#6d28d9",color:"#fff",border:"none",borderRadius:10,fontWeight:800,cursor:"pointer"}}>
                            💾 Zapisz
                          </button>
                          <button onClick={()=>setEditRoleId(null)}
                            style={{padding:"9px 14px",background:"#f3f4f6",color:"#6b7280",border:"none",borderRadius:10,fontWeight:700,cursor:"pointer"}}>
                            Anuluj
                          </button>
                        </div>
                      </div>
                    ):(
                      <div style={{display:"flex",alignItems:"center",gap:12}}>
                        <div style={{width:44,height:44,borderRadius:"50%",background:r.bg,border:"2px solid "+r.color+"44",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                          {r.icon||"👤"}
                        </div>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                            <span style={{fontWeight:800,fontSize:14,color:T.text}}>{r.label}</span>
                            <span style={{background:r.bg,color:r.color,borderRadius:20,padding:"2px 9px",fontSize:10,fontWeight:700}}>ID: {r.id}</span>
                          </div>
                          <div style={{fontSize:11,color:T.muted}}>
                            {r.canChangePassword&&<span style={{color:"#1d4ed8",fontWeight:600,marginRight:6}}>🔑 Zmiana haseł</span>}
                            {users.filter(u=>u.role===r.id).length>0&&(
                              <span>Przypisani: {users.filter(u=>u.role===r.id).map(u=>u.name||u.login).join(", ")}</span>
                            )}
                            {users.filter(u=>(u.extraRoles||[]).includes(r.id)).length>0&&(
                              <span style={{marginLeft:4}}>(+{users.filter(u=>(u.extraRoles||[]).includes(r.id)).length} dodatkowych)</span>
                            )}
                          </div>
                        </div>
                        <div style={{display:"flex",gap:5,flexShrink:0}}>
                          <button style={S.actBtn} onClick={()=>{setEditRoleId(r.id);setEditRoleForm({...r});}}>✏ Edytuj</button>
                          <button style={{...S.actBtn,background:"#ffebee",color:"#dc2626"}} onClick={()=>{
                            const count=users.filter(u=>u.role===r.id).length;
                            if(count>0){alert(`Nie można usunąć — ${count} użytkownik(ów) ma tę rolę.`);return;}
                            if(window.confirm(`Usunąć rolę "${r.label}"?`))
                              setCustomRoles(p=>p.filter(x=>x.id!==r.id));
                          }}>🗑 Usuń</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <div style={{marginTop:14,padding:"10px 14px",background:"#ede9fe",borderRadius:10,fontSize:11,color:"#5b21b6",lineHeight:1.7}}>
                  ℹ Własne role możesz przypisać użytkownikom w zakładce <strong>Użytkownicy</strong>. Każda rola pojawia się w selektorze przy tworzeniu lub edycji konta.
                </div>
              </div>
            )}

            {/* ══ PODKATEGORIA: UPRAWNIENIA ══ */}
            {saSubTab==="permissions" && (()=>{
              const PERM_ROLES = [
                { id:"admin",  label:"👑 Główny administrator", color:"#856404", bg:"#fefce8", border:"#ca8a04" },
                { id:"seller", label:"🏷 Sprzedawca",          color:"#15803d", bg:"#f0fdf4", border:"#16a34a" },
                { id:"cook",   label:"👨‍🍳 Kucharz",             color:"#9a3412", bg:"#fff7ed", border:"#ea580c" },
              ];
              const ALL_PERMS = {
                admin: [
                  { section:"📦 Zamówienia i sprzedaż" },
                  { key:"adminOrders",       label:"📋 Zarządzanie zamówieniami",    desc:"Pełny dostęp do zamówień, zmiana statusów, usuwanie" },
                  { key:"adminOnlineOrders", label:"🌐 Zamówienia online",           desc:"Podgląd i obsługa zamówień ze sklepu online" },
                  { key:"adminTakeaway",     label:"🥡 Zamówienia na wynos/dostawa", desc:"Zarządzanie zamówieniami na wynos, strefami dostawy" },
                  { key:"adminSales",        label:"📊 Raporty sprzedaży",           desc:"Dostęp do statystyk, wykresów i raportów" },
                  { key:"adminKsef",         label:"🧾 KSeF — faktury",              desc:"Wystawianie i wysyłanie faktur do KSeF" },
                  { section:"🍽 Menu i produkty" },
                  { key:"adminMenu",         label:"🍽 Zarządzanie menu",            desc:"Dodawanie, edycja, usuwanie pozycji menu" },
                  { key:"adminCats",         label:"📂 Kategorie menu",              desc:"Tworzenie i edycja kategorii menu" },
                  { key:"adminPromo",        label:"🔥 Promocje",                    desc:"Tworzenie promocji i rabatów" },
                  { key:"adminOnlineMenu",   label:"🛒 Menu sklepu online",          desc:"Zarządzanie produktami w sklepie online" },
                  { key:"adminSpecials",     label:"⭐ Polecamy",                    desc:"Edycja sekcji polecanych produktów" },
                  { section:"👥 Klienci i kampanie" },
                  { key:"adminClients",      label:"👥 Kontrahenci",                 desc:"Baza klientów, historia zamówień" },
                  { key:"adminCampaigns",    label:"📣 Kampanie marketingowe",       desc:"Tworzenie i zarządzanie kampaniami" },
                  { key:"adminVouchers",     label:"🎟 Bony i vouchery",             desc:"Tworzenie i edycja bonów zniżkowych" },
                  { section:"🏢 Lokale i stoły" },
                  { key:"adminTables",       label:"🪑 Zarządzanie stolikami",       desc:"Dodawanie, edycja stolików i sekcji" },
                  { key:"adminMessages",     label:"📧 Wiadomości",                  desc:"System wiadomości między pracownikami" },
                  { section:"⚙ Ustawienia systemu" },
                  { key:"adminSettings",     label:"⚙ Ustawienia restauracji",      desc:"Nazwa, logo, dane kontaktowe, integracje" },
                  { key:"adminExtras",       label:"🔩 Dodatkowe opcje",             desc:"Zaawansowane opcje konfiguracji" },
                  { key:"adminQrNfc",        label:"📷 QR i NFC — stoliki",          desc:"Konfiguracja kodów QR i tagów NFC" },
                  { key:"adminNfcCards",     label:"💳 Karty NFC pracowników",       desc:"Przypisywanie i programowanie kart NFC" },
                  { key:"adminReceipt",      label:"🧾 Wygląd paragonu PDF",         desc:"Edycja treści i formatu paragonu" },
                  { key:"adminPrivacyPolicy",label:"🔒 Polityka prywatności",        desc:"Edycja treści polityki prywatności" },
                  { section:"👤 Zarządzanie zespołem" },
                  { key:"adminUsers",        label:"👤 Użytkownicy systemu",          desc:"Dodawanie, edycja i usuwanie kont (poza superadmin)" },
                  { key:"adminWarehouse",    label:"📦 Moduł magazynowy",            desc:"Pełny dostęp do magazynu i dokumentów" },
                  { section:"📋 Logi i kontrola" },
                  { key:"adminLogs",         label:"📋 Logi systemu",                desc:"Dostęp do logów logowań i operacji" },
                ],
                seller: [
                  { section:"📦 Zamówienia" },
                  { key:"sellerOrders",      label:"📋 Zarządzanie zamówieniami",    desc:"Zmiana statusów, przypisywanie do kuchni" },
                  { key:"sellerOnlineOrders",label:"🌐 Zamówienia online",           desc:"Podgląd i obsługa zamówień online" },
                  { key:"sellerTakeaway",    label:"🥡 Zamówienia na wynos",         desc:"Obsługa zamówień na wynos i dostawę" },
                  { section:"👥 Klienci" },
                  { key:"sellerClients",     label:"👥 Baza klientów",              desc:"Podgląd i edycja danych klientów" },
                  { key:"sellerAddOrders",   label:"➕ Nowe zamówienie od obsługi",  desc:"Tworzenie zamówień ręcznie przy stoliku" },
                  { section:"🍽 Menu" },
                  { key:"sellerSpecials",    label:"⭐ Polecamy",                    desc:"Podgląd sekcji polecanych" },
                  { key:"sellerMenuView",    label:"🍽 Podgląd menu",               desc:"Wgląd w aktualne menu bez edycji" },
                  { section:"💰 Płatności" },
                  { key:"sellerPayments",    label:"💳 Obsługa płatności",           desc:"Oznaczanie zamówień jako opłacone" },
                  { key:"sellerDiscount",    label:"🎟 Stosowanie rabatów",          desc:"Możliwość stosowania bonów i rabatów" },
                  { section:"🔔 Powiadomienia" },
                  { key:"sellerAlerts",      label:"🔔 Powiadomienia o zamówieniach",desc:"Dźwiękowe alerty o nowych zamówieniach" },
                ],
                cook: [
                  { section:"🍳 Kuchnia" },
                  { key:"cookOrders",        label:"📋 Widok zamówień kuchennych",   desc:"Lista dań do przygotowania z kolejnością" },
                  { key:"cookStatusChange",  label:"✅ Zmiana statusu dań",          desc:"Oznaczanie dań jako gotowe / w przygotowaniu" },
                  { key:"cookTakeaway",      label:"🥡 Zamówienia na wynos",         desc:"Widok zamówień na wynos dla kuchni" },
                  { section:"📋 Podgląd" },
                  { key:"cookMenuView",      label:"🍽 Podgląd menu",               desc:"Wgląd w skład i alergeny potraw" },
                  { key:"cookAllergens",     label:"⚠ Alergeny i składniki",        desc:"Widok alergenów przy pozycjach menu" },
                  { key:"cookClients",       label:"👥 Notatki o kliencie",          desc:"Widok uwag klienta przy zamówieniu" },
                  { section:"🔔 Powiadomienia" },
                  { key:"cookAlerts",        label:"🔔 Alerty nowych zamówień",      desc:"Dźwiękowe powiadomienia o nowych daniach" },
                ],
              };

              // Uprawnienia per-użytkownik (własne)
  // eslint-disable-next-line no-unused-vars

              return (
                <div style={{...S.formCard,border:"2px solid #6d28d9",background:"#faf5ff"}}>
                  <div style={{...S.formTitle,color:"#6d28d9",borderBottomColor:"#ddd6fe"}}>
                    🔧 Uprawnienia funkcjonalne
                  </div>
                  <div style={{fontSize:11,color:"#7c3aed",marginBottom:16,lineHeight:1.6}}>
                    Włącz lub wyłącz dostęp do poszczególnych modułów dla każdej roli. Możesz też ustawić uprawnienia indywidualnie dla konkretnego użytkownika — nadpisują one ustawienia roli.
                  </div>

                  {/* Zakładki ról */}
                  {PERM_ROLES.map(role => {
                    const perms = (settings.rolePermissions || {})[role.id] || {};
                    const setPerm = (key, val) => setSettings(p => ({
                      ...p,
                      rolePermissions: {
                        ...(p.rolePermissions||{}),
                        [role.id]: { ...((p.rolePermissions||{})[role.id]||{}), [key]: val }
                      }
                    }));
                    const items = ALL_PERMS[role.id] || [];

                    return (
                      <div key={role.id} style={{marginBottom:16,border:"2px solid "+role.border,borderRadius:14,overflow:"hidden"}}>
                        {/* Nagłówek roli */}
                        <div style={{background:role.bg,padding:"10px 16px",borderBottom:"1.5px solid "+role.border+"44",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <div style={{fontWeight:800,fontSize:14,color:role.color}}>{role.label}</div>
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>{
                              const allKeys = items.filter(i=>i.key).map(i=>i.key);
                              const newPerms = Object.fromEntries(allKeys.map(k=>[k,true]));
                              setSettings(p=>({...p,rolePermissions:{...(p.rolePermissions||{}),[role.id]:newPerms}}));
                            }} style={{padding:"4px 12px",borderRadius:8,border:"1.5px solid "+role.color,background:"transparent",color:role.color,fontWeight:700,cursor:"pointer",fontSize:11}}>
                              ✅ Włącz wszystko
                            </button>
                            <button onClick={()=>{
                              const allKeys = items.filter(i=>i.key).map(i=>i.key);
                              const newPerms = Object.fromEntries(allKeys.map(k=>[k,false]));
                              setSettings(p=>({...p,rolePermissions:{...(p.rolePermissions||{}),[role.id]:newPerms}}));
                            }} style={{padding:"4px 12px",borderRadius:8,border:"1.5px solid #d1d5db",background:"transparent",color:"#9ca3af",fontWeight:700,cursor:"pointer",fontSize:11}}>
                              ❌ Wyłącz wszystko
                            </button>
                          </div>
                        </div>

                        {/* Lista uprawnień */}
                        <div style={{padding:"12px 14px",background:"#fff"}}>
                          {items.map((item, idx) => {
                            if (item.section) return (
                              <div key={idx} style={{fontSize:10,fontWeight:800,color:role.color,textTransform:"uppercase",letterSpacing:1,marginTop:idx>0?12:0,marginBottom:6,paddingBottom:4,borderBottom:"1.5px solid "+role.border+"33"}}>
                                {item.section}
                              </div>
                            );
                            const enabled = perms[item.key] !== false;
                            return (
                              <div key={item.key}
                                onClick={()=>setPerm(item.key,!enabled)}
                                style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:9,cursor:"pointer",
                                  background:enabled?role.bg:"transparent",
                                  border:"1.5px solid "+(enabled?role.border:"transparent"),
                                  marginBottom:4,transition:"all .15s",userSelect:"none"}}>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontWeight:700,fontSize:12,color:enabled?role.color:"#6b7280"}}>{item.label}</div>
                                  <div style={{fontSize:10,color:enabled?role.color+"aa":"#9ca3af",lineHeight:1.3}}>{item.desc}</div>
                                </div>
                                <div style={{width:34,height:19,borderRadius:10,flexShrink:0,
                                  background:enabled?role.color:"#d1d5db",position:"relative",transition:"background .2s"}}>
                                  <div style={{position:"absolute",top:2,left:enabled?16:2,
                                    width:15,height:15,borderRadius:"50%",background:"#fff",
                                    boxShadow:"0 1px 3px rgba(0,0,0,0.2)",transition:"left .2s"}}/>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* ── Uprawnienia indywidualne per użytkownik ── */}
                  <div style={{marginTop:8,border:"2px solid #c4b5fd",borderRadius:14,overflow:"hidden"}}>
                    <div style={{background:"#ede9fe",padding:"10px 16px",borderBottom:"1.5px solid #c4b5fd"}}>
                      <div style={{fontWeight:800,fontSize:14,color:"#6d28d9"}}>👤 Uprawnienia indywidualne użytkowników</div>
                      <div style={{fontSize:11,color:"#7c3aed",marginTop:2}}>Nadpisują ustawienia roli dla konkretnej osoby</div>
                    </div>
                    <div style={{padding:"12px 14px",background:"#fff"}}>
                      {users.filter(u=>u.role!=="superadmin").map(u=>{
                        const rd = allRoleDefs[u.role] || ROLE_DEFS.seller;
                        const roleItems = ALL_PERMS[u.role] || ALL_PERMS.seller;
                        const userPerms = ((settings.userPermissions||{})[u.id]) || {};
                        const rolePerms = (settings.rolePermissions||{})[u.role] || {};
                        const setUserPerm = (key,val) => setSettings(p=>({
                          ...p,
                          userPermissions:{
                            ...(p.userPermissions||{}),
                            [u.id]:{...((p.userPermissions||{})[u.id]||{}),[key]:val}
                          }
                        }));
                        const clearUserPerm = (key) => setSettings(p=>{
                          const up={...((p.userPermissions||{})[u.id]||{})};
                          delete up[key];
                          return {...p,userPermissions:{...(p.userPermissions||{}),[u.id]:up}};
                        });

                        return (
                          <details key={u.id} style={{marginBottom:8,border:"1.5px solid #e5e7eb",borderRadius:10,overflow:"hidden"}}>
                            <summary style={{padding:"9px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:8,background:"#f9fafb",listStyle:"none",userSelect:"none"}}>
                              <div style={{width:30,height:30,borderRadius:"50%",background:rd.bg,border:"2px solid "+rd.color+"44",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>{rd.icon}</div>
                              <div style={{flex:1}}>
                                <div style={{fontWeight:700,fontSize:12,color:"#111"}}>{u.name||u.login}</div>
                                <div style={{fontSize:10,color:"#6b7280"}}>{rd.label} · @{u.login}</div>
                              </div>
                              {Object.keys(userPerms).length>0&&(
                                <span style={{background:"#6d28d9",color:"#fff",borderRadius:12,padding:"2px 8px",fontSize:10,fontWeight:700}}>
                                  {Object.keys(userPerms).length} nadpisań
                                </span>
                              )}
                            </summary>
                            <div style={{padding:"12px 14px",borderTop:"1px solid #e5e7eb"}}>
                              <div style={{fontSize:11,color:"#6b7280",marginBottom:10}}>
                                Zostaw domyślnie = używa uprawnień roli. Zmień = nadpisuje dla tego użytkownika.
                              </div>
                              {roleItems.filter(i=>i.key).map(item=>{
                                const hasOverride = item.key in userPerms;
                                const roleEnabled = rolePerms[item.key] !== false;
                                const effectiveEnabled = hasOverride ? userPerms[item.key] : roleEnabled;
                                return (
                                  <div key={item.key} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:8,marginBottom:3,background:hasOverride?"#faf5ff":"transparent",border:"1.5px solid "+(hasOverride?"#c4b5fd":"transparent")}}>
                                    <div style={{flex:1,minWidth:0}}>
                                      <div style={{fontWeight:600,fontSize:11,color:effectiveEnabled?"#111":"#9ca3af"}}>{item.label}</div>
                                      {hasOverride&&<div style={{fontSize:9,color:"#7c3aed",fontWeight:700}}>✏ Nadpisano indywidualnie</div>}
                                    </div>
                                    <div style={{display:"flex",gap:5,flexShrink:0,alignItems:"center"}}>
                                      {/* Wartość roli (info) */}
                                      <span style={{fontSize:9,color:"#9ca3af",whiteSpace:"nowrap"}}>Rola: {roleEnabled?"✅":"❌"}</span>
                                      {/* Toggle indywidualny */}
                                      <div onClick={()=> hasOverride&&userPerms[item.key]===effectiveEnabled ? clearUserPerm(item.key) : setUserPerm(item.key,!effectiveEnabled)}
                                        style={{width:32,height:18,borderRadius:9,background:effectiveEnabled?(hasOverride?"#6d28d9":"#9ca3af"):"#d1d5db",position:"relative",cursor:"pointer",transition:"background .2s",opacity:hasOverride?1:0.6}}>
                                        <div style={{position:"absolute",top:2,left:effectiveEnabled?14:2,width:14,height:14,borderRadius:"50%",background:"#fff",boxShadow:"0 1px 3px rgba(0,0,0,0.2)",transition:"left .2s"}}/>
                                      </div>
                                      {hasOverride&&(
                                        <button onClick={()=>clearUserPerm(item.key)}
                                          style={{padding:"2px 6px",borderRadius:5,border:"1px solid #e5e7eb",background:"#fff",color:"#9ca3af",cursor:"pointer",fontSize:9,fontWeight:700}}>
                                          ↩ Przywróć
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              <button onClick={()=>setSettings(p=>{const up={...(p.userPermissions||{})};delete up[u.id];return {...p,userPermissions:up};})}
                                style={{marginTop:8,padding:"5px 12px",borderRadius:7,border:"1px solid #fecaca",background:"#fff",color:"#dc2626",cursor:"pointer",fontSize:11,fontWeight:700}}>
                                🗑 Usuń wszystkie nadpisania dla {u.name||u.login}
                              </button>
                            </div>
                          </details>
                        );
                      })}
                      {users.filter(u=>u.role!=="superadmin").length===0&&(
                        <div style={{textAlign:"center",color:"#9ca3af",fontSize:12,padding:12}}>Brak użytkowników</div>
                      )}
                    </div>
                  </div>

                  <div style={{fontSize:10,color:"#a78bfa",marginTop:10,padding:"8px 12px",background:"#f5f3ff",borderRadius:8,lineHeight:1.6}}>
                    ℹ Super Administrator zawsze ma dostęp do wszystkich funkcji niezależnie od powyższych ustawień.<br/>
                    Uprawnienia indywidualne użytkownika <strong>nadpisują</strong> ustawienia roli.
                  </div>
                </div>
              );
            })()}

            {/* ══ PODKATEGORIA: WIDOCZNOŚĆ MENU ══ */}
            {saSubTab==="visibility" && (
              <div style={{...S.formCard,border:"2px solid #6d28d9",background:"#faf5ff"}}>
                <div style={{...S.formTitle,color:"#6d28d9",borderBottomColor:"#ddd6fe"}}>
                  👁 Widoczność kategorii menu panelu admina
                </div>
                <div style={{fontSize:11,color:"#7c3aed",marginBottom:12,lineHeight:1.6}}>
                  Wybierz które zakładki są widoczne dla poszczególnych ról. Super Administrator zawsze widzi wszystko.
                </div>
                {[
                  { role:"admin",  label:"👑 Główny administrator", color:"#856404",
                    tabs: [{key:"menu",label:"🍽 Menu"},{key:"promo",label:"🔥 Promocje"},{key:"cats",label:"📂 Kategorie"},{key:"tables",label:"🪑 Stoliki"},{key:"takeaway",label:"🥡 Na wynos"},{key:"online",label:"🌐 Zamówienia online"},{key:"clients",label:"👥 Kontrahenci"},{key:"campaigns",label:"📣 Kampanie"},{key:"vouchers",label:"🎟 Bony"},{key:"sales",label:"📊 Sprzedaż"},{key:"users",label:"👤 Użytkownicy"},{key:"messages",label:"📧 Wiadomości"},{key:"settings",label:"⚙ Ustawienia"},{key:"extras",label:"🔩 Dodatkowe"},{key:"ksef",label:"🧾 KSeF"},{key:"hardware",label:"🖨 Sprzęt"},{key:"backup",label:"💾 Kopia"},{key:"logs",label:"📋 Logi"}] },
                  { role:"seller", label:"🏷 Sprzedawca", color:"#15803d",
                    tabs: [{key:"tables",label:"🪑 Stoliki"},{key:"takeaway",label:"🥡 Na wynos"},{key:"online",label:"🌐 Zamówienia online"},{key:"clients",label:"👥 Kontrahenci"},{key:"specials",label:"⭐ Polecamy"}] },
                  { role:"cook",   label:"👨‍🍳 Kucharz", color:"#9a3412",
                    tabs: [{key:"tables",label:"🪑 Stoliki"},{key:"takeaway",label:"🥡 Na wynos"},{key:"clients",label:"👥 Kontrahenci"}] },
                ].map(({ role, label, color, tabs: roleTabs }) => {
                  const mv = ((settings.menuVisibility || {})[role]) || {};
                  return (
                    <div key={role} style={{border:"1.5px solid "+color+"55",borderRadius:10,padding:"12px 14px",marginBottom:10,background:"#fafafa"}}>
                      <div style={{fontWeight:700,fontSize:12,color,marginBottom:10}}>{label}</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                        {roleTabs.map(t => {
                          const checked = mv[t.key] === true;
                          return (
                            <label key={t.key} style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,background:checked?color:"#f3f4f6",color:checked?"#fff":"#6b7280",border:"1.5px solid "+(checked?color:"#e5e7eb"),transition:"all .15s",userSelect:"none"}}>
                              <input type="checkbox" checked={checked} style={{display:"none"}}
                                onChange={() => {
                                  setSettings(p => {
                                    const cur = p.menuVisibility || {};
                                    const roleVis = { ...(cur[role] || {}) };
                                    if (checked) delete roleVis[t.key]; else roleVis[t.key] = true;
                                    return { ...p, menuVisibility: { ...cur, [role]: roleVis } };
                                  });
                                }}
                              />
                              {t.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ══ PODKATEGORIA: LOGI ══ */}
            {saSubTab==="logs" && (
              <LogsPanel logs={logs} setLogs={setLogs} orders={orders} setOrders={setOrders} />
            )}

            {/* ══ PODKATEGORIA: SPRZĘT ══ */}
            {saSubTab==="hardware" && (
              <HardwarePanel settings={settings} setSettings={setSettings}/>
            )}

            {/* ══ PODKATEGORIA: KOPIE ══ */}
            {saSubTab==="backup" && (
              <BackupPanel
                menu={menu} cats={cats} users={visibleUsers} settings={settings}
                tables={tables} orders={orders} campaigns={campaigns}
                vouchers={vouchers} usedCodes={usedCodes} staffCalls={staffCalls}
                clients={clients} fbConfig={fbConfig}
                setMenu={setMenu} setCats={setCats} setUsers={setUsers}
                setSettings={setSettings} setTables={setTables}
                setOrders={setOrders} setCampaigns={setCampaigns}
                setVouchers={setVouchers} setUsedCodes={setUsedCodes}
                setClients={setClients}
              />
            )}

            {/* ══ PODKATEGORIA: BAZA DANYCH ══ */}
            {/* ══ PODKATEGORIA: SZABLONY DOKUMENTÓW ══ */}
            {saSubTab==="documents" && (()=>{
              const DS = settings.docSettings || {};
              const setDS = patch => setSettings(p=>({...p, docSettings:{...(p.docSettings||{}), ...patch}}));
              const DOC_LIST = [
                {id:"invoice",  label:"🧾 Faktura VAT",            color:"#6d28d9"},
                {id:"wz",       label:"📋 Dokument WZ",            color:"#2563eb"},
                {id:"delivery", label:"🚚 Dowód dostawy",          color:"#059669"},
                {id:"release",  label:"✅ Potwierdzenie wydania",   color:"#d97706"},
              ];

              const cfg = DS[selDoc] || {};
              const setCfg = patch => setDS({[selDoc]: {...cfg, ...patch}});

              // Demo zamówienie do podglądu
              const demoOrder = {
                id:"DEMO123456", firstName:"Jan", lastName:"Kowalski",
                phone:"+48 600 000 000", email:"jan@example.com",
                address:"ul. Przykładowa 1, 00-001 Warszawa",
                readyAt:"", payment:"card", deliveryType:"delivery",
                deliveryZone:"Strefa 1", deliveryCost:10,
                cart:[
                  {emoji:"🥖",name:"Chleb żytni",qty:2,unit:"szt",price:9.00},
                  {emoji:"🍰",name:"Tort czekoladowy",qty:1,unit:"szt",price:120.00},
                ],
                total:148.00, itemsTotal:138.00,
                date:new Date().toISOString().slice(0,10),
                time:new Date().toLocaleTimeString("pl-PL",{hour:"2-digit",minute:"2-digit"}),
              };

              const TITLES = {invoice:"FAKTURA VAT",wz:"DOKUMENT WZ",delivery:"DOWÓD DOSTAWY",release:"POTWIERDZENIE WYDANIA"};
              const PAYMENTS = {cash:"Gotówka",blik:"BLIK",card:"Karta",paypal:"PayPal"};

              const buildDoc = (o, docId) => {
                const c = DS[docId] || {};
                const rName  = c.companyName  || settings?.restaurantName || "Restauracja";
                const rAddr  = c.companyAddr  || settings?.address || "";
                const rNip   = c.companyNip   || "";
                const rPhone = c.companyPhone || settings?.phone || "";
                const rEmail = c.companyEmail || settings?.email || "";
                const rBank  = c.bankAccount  || "";
                const footer = c.footer || "";
                const showSign = c.showSignatures !== false;
                const showStamp= c.showStamp !== false;
                const accentCol = c.accentColor || "#6d28d9";
                const lines = (o.cart||[]).map((ci,i)=>`
                  <tr style="background:${i%2===0?"#fafafa":"#fff"}">
                    <td style="padding:7px 10px">${ci.emoji||""} ${ci.name}</td>
                    <td style="padding:7px 10px;text-align:center">${ci.qty||1} ${ci.unit||"szt"}</td>
                    <td style="padding:7px 10px;text-align:right;font-weight:700">${(Number(ci.price||0)*(ci.qty||1)).toFixed(2)} zł</td>
                  </tr>`).join("");
                const now = new Date().toLocaleString("pl-PL");
                const logoHtml = c.logo ? `<img src="${c.logo}" style="height:56px;object-fit:contain;margin-bottom:4px"/>` : `<div style="font-size:28px;margin-bottom:4px">🏪</div>`;
                return `<!DOCTYPE html><html lang="pl"><head><meta charset="UTF-8"/>
                <title>${TITLES[docId]} — ${rName}</title>
                <style>
                  *{box-sizing:border-box;margin:0;padding:0}
                  body{font-family:Arial,sans-serif;font-size:13px;color:#111;background:#fff;padding:28px 32px}
                  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:16px;border-bottom:3px solid ${accentCol}}
                  .company{flex:1}.company h1{font-size:20px;font-weight:900;color:${accentCol};margin-bottom:4px}
                  .company p{font-size:11px;color:#555;line-height:1.7}
                  .doc-title{text-align:right}.doc-title h2{font-size:22px;font-weight:900;color:${accentCol}}
                  .doc-title p{font-size:11px;color:#555;margin-top:4px}
                  .parties{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px}
                  .box{background:#f9fafb;border-radius:8px;padding:12px 14px;border:1px solid #e5e7eb}
                  .box h3{font-size:11px;font-weight:700;color:${accentCol};text-transform:uppercase;letter-spacing:1px;margin-bottom:6px}
                  .box p{font-size:12px;line-height:1.8;color:#222}
                  table{width:100%;border-collapse:collapse;margin-bottom:12px}
                  thead tr{background:${accentCol};color:#fff}
                  thead th{padding:8px 10px;text-align:left;font-size:12px;font-weight:700}
                  thead th:last-child{text-align:right}
                  tbody tr:hover{background:#f0f0ff}
                  .total-row{background:${accentCol}15;font-weight:900;font-size:14px}
                  .total-row td{padding:10px;border-top:2px solid ${accentCol}}
                  .footer{margin-top:16px;font-size:10px;color:#888;border-top:1px solid #e5e7eb;padding-top:10px;text-align:center}
                  .signs{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:24px}
                  .sign-box{border-top:1.5px solid #333;padding-top:6px;text-align:center;font-size:11px;color:#555}
                  .stamp-box{border:2px dashed #aaa;border-radius:50%;width:90px;height:90px;margin:0 auto;display:flex;align-items:center;justify-content:center;font-size:10px;color:#aaa}
                  @media print{body{padding:12px}button{display:none!important}}
                </style></head><body>
                <div class="header">
                  <div class="company">
                    ${logoHtml}
                    <h1>${rName}</h1>
                    <p>${rAddr}${rNip?`<br/>NIP: ${rNip}`:""}${rPhone?`<br/>Tel: ${rPhone}`:""}${rEmail?`<br/>E-mail: ${rEmail}`:""}${rBank?`<br/>Konto: ${rBank}`:""}</p>
                  </div>
                  <div class="doc-title">
                    <h2>${TITLES[docId]}</h2>
                    <p>Nr: ${docId.toUpperCase()}-${String(o.id||"").slice(-6)||"000001"}<br/>
                    Data: ${now}${o.date?`<br/>Zamówienie: ${o.date}`:""}${o.time?` ${o.time}`:""}</p>
                  </div>
                </div>
                <div class="parties">
                  <div class="box"><h3>Sprzedawca</h3><p>${rName}${rAddr?`<br/>${rAddr}`:""}${rNip?`<br/>NIP: ${rNip}`:""}</p></div>
                  <div class="box"><h3>Nabywca</h3><p>${o.firstName||""} ${o.lastName||""}<br/>Tel: ${o.phone||""}${o.email?`<br/>${o.email}`:""}${o.address?`<br/>${o.address}`:""}</p></div>
                </div>
                ${docId==="delivery"&&o.address?`<div class="box" style="margin-bottom:14px"><h3>Adres dostawy</h3><p>${o.address}${o.deliveryZone?`<br/>Strefa: ${o.deliveryZone}`:""}</p></div>`:""}
                <table>
                  <thead><tr><th>Pozycja</th><th style="text-align:center">Ilość</th><th style="text-align:right">Wartość</th></tr></thead>
                  <tbody>${lines}</tbody>
                  ${o.deliveryCost>0?`<tr><td colspan="2" style="padding:6px 10px;color:#555">🚗 Dostawa (${o.deliveryZone||""})</td><td style="padding:6px 10px;text-align:right;font-weight:700">${Number(o.deliveryCost).toFixed(2)} zł</td></tr>`:""}
                  <tr class="total-row"><td colspan="2" style="padding:10px">RAZEM DO ZAPŁATY</td><td style="padding:10px;text-align:right">${Number(o.total||0).toFixed(2)} zł</td></tr>
                </table>
                <div class="box" style="margin-bottom:12px"><p>Forma płatności: <strong>${PAYMENTS[o.payment]||o.payment||"—"}</strong>${docId==="invoice"&&rBank?`<br/>Konto bankowe: ${rBank}`:""}</p></div>
                ${c.customNote?`<div class="box" style="margin-bottom:12px;border-color:${accentCol}55"><p style="color:#555">${c.customNote}</p></div>`:""}
                ${showSign?`<div class="signs"><div class="sign-box">Wystawił(a):<br/><br/><br/>___________________________<br/>${rName}</div><div class="sign-box">${docId==="release"?"Odebrał(a)":docId==="wz"?"Wydający":docId==="delivery"?"Dostarczył(a)":"Zatwierdził(a)"}:<br/><br/><br/>___________________________<br/>${o.firstName||""} ${o.lastName||""}</div></div>`:""}
                ${showStamp&&docId==="invoice"?`<div style="display:flex;justify-content:flex-end;margin-top:12px"><div class="stamp-box">Pieczęć firmowa</div></div>`:""}
                ${footer?`<div class="footer">${footer}</div>`:""}
                <div style="text-align:center;margin-top:14px"><button onclick="window.print()" style="padding:10px 28px;background:${accentCol};color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:14px;font-weight:700">🖨 Drukuj / Zapisz PDF</button></div>
                </body></html>`;
              };

              return (
                <div style={{...S.formCard,border:"2px solid #6d28d9",background:"#faf5ff"}}>
                  <div style={{...S.formTitle,color:"#6d28d9",borderBottomColor:"#ddd6fe"}}>
                    📄 Edytor szablonów dokumentów
                  </div>
                  <div style={{fontSize:12,color:"#7c3aed",marginBottom:14,lineHeight:1.6}}>
                    Dostosuj wygląd i treść każdego dokumentu. Zmiany są widoczne natychmiast w podglądzie.
                  </div>

                  {/* Wybór dokumentu */}
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
                    {DOC_LIST.map(d=>(
                      <button key={d.id} onClick={()=>setSelDoc(d.id)}
                        style={{padding:"8px 16px",borderRadius:10,border:"2px solid "+(selDoc===d.id?d.color:"#ddd6fe"),
                          background:selDoc===d.id?d.color+"15":"#fff",color:selDoc===d.id?d.color:"#6b7280",
                          fontWeight:700,fontSize:12,cursor:"pointer",transition:"all .15s"}}>
                        {d.label}
                      </button>
                    ))}
                  </div>

                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                    {/* Lewa kolumna — pola edycji */}
                    <div>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#6d28d9"}}>🏢 Dane firmy na dokumencie</div>
                        <button onClick={()=>{
                          // Synchronizacja z danych restauracji w ustawieniach
                          setCfg({
                            companyName:  settings?.restaurantName || cfg.companyName || "",
                            companyAddr:  settings?.address        || cfg.companyAddr  || "",
                            companyNip:   settings?.nip            || cfg.companyNip   || "",
                            companyPhone: settings?.phone          || cfg.companyPhone || "",
                            companyEmail: settings?.email          || cfg.companyEmail || "",
                          });
                        }} style={{padding:"5px 11px",borderRadius:8,border:"1.5px solid #c4b5fd",background:"#ede9fe",color:"#6d28d9",fontWeight:700,cursor:"pointer",fontSize:11}}>
                          ↙ Pobierz z ustawień
                        </button>
                      </div>
                      {/* Info jeśli dane są dostępne */}
                      {(settings?.restaurantName||settings?.address||settings?.nip)&&(
                        <div style={{marginBottom:10,padding:"8px 12px",borderRadius:9,background:"#f0fdf4",border:"1px solid #bbf7d0",fontSize:11,color:"#065f46"}}>
                          ✅ Dane restauracji dostępne — kliknij „Pobierz z ustawień" aby zastosować
                        </div>
                      )}
                      {[
                        {f:"companyName",  l:"Nazwa firmy",            ph:settings?.restaurantName||"Nazwa restauracji"},
                        {f:"companyAddr",  l:"Adres",                  ph:settings?.address||"ul. Przykładowa 1, 00-001 Warszawa"},
                        {f:"companyNip",   l:"NIP",                    ph:settings?.nip||"000-000-00-00"},
                        {f:"companyPhone", l:"Telefon",                ph:settings?.phone||"+48 000 000 000"},
                        {f:"companyEmail", l:"E-mail",                 ph:settings?.email||"kontakt@firma.pl"},
                        {f:"bankAccount",  l:"Numer konta bankowego",  ph:"00 0000 0000 0000 0000 0000 0000"},
                      ].map(({f,l,ph})=>(
                        <div key={f} style={{marginBottom:8}}>
                          <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>{l}</label>
                          <input value={cfg[f]||""} onChange={e=>setCfg({[f]:e.target.value})} placeholder={ph}
                            style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid #ddd6fe",fontSize:12,boxSizing:"border-box",background:"#fff"}}/>
                        </div>
                      ))}

                      <div style={{fontWeight:700,fontSize:13,color:"#6d28d9",marginBottom:10,marginTop:14}}>🎨 Wygląd</div>
                      <div style={{marginBottom:8}}>
                        <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Kolor akcentu dokumentu</label>
                        <div style={{display:"flex",gap:8,alignItems:"center"}}>
                          <input type="color" value={cfg.accentColor||"#6d28d9"} onChange={e=>setCfg({accentColor:e.target.value})}
                            style={{width:44,height:36,borderRadius:8,border:"1.5px solid #ddd6fe",cursor:"pointer",padding:2}}/>
                          <span style={{fontSize:12,color:"#7c3aed"}}>{cfg.accentColor||"#6d28d9"}</span>
                          {["#6d28d9","#2563eb","#059669","#d97706","#dc2626","#111827"].map(col=>(
                            <div key={col} onClick={()=>setCfg({accentColor:col})}
                              style={{width:24,height:24,borderRadius:6,background:col,cursor:"pointer",border:cfg.accentColor===col?"3px solid #111":"2px solid transparent"}}/>
                          ))}
                        </div>
                      </div>

                      {/* Logo */}
                      <div style={{marginBottom:8}}>
                        <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Logo firmy na dokumencie</label>
                        <div style={{display:"flex",gap:8,alignItems:"center"}}>
                          {cfg.logo&&<img src={cfg.logo} alt="logo" style={{height:40,objectFit:"contain",borderRadius:6,border:"1px solid #ddd6fe"}}/>}
                          <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"7px 13px",background:"#ede9fe",borderRadius:9,cursor:"pointer",fontSize:11,fontWeight:700,color:"#6d28d9",border:"1.5px dashed #c4b5fd"}}>
                            📁 Wgraj logo
                            <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                              const f=e.target.files[0]; if(!f) return;
                              const r=new FileReader(); r.onload=ev=>setCfg({logo:ev.target.result}); r.readAsDataURL(f);
                            }}/>
                          </label>
                          {cfg.logo&&<button onClick={()=>setCfg({logo:""})} style={{padding:"6px 10px",background:"#fee2e2",border:"none",borderRadius:7,color:"#dc2626",cursor:"pointer",fontWeight:700,fontSize:11}}>Usuń</button>}
                        </div>
                      </div>

                      <div style={{fontWeight:700,fontSize:13,color:"#6d28d9",marginBottom:10,marginTop:14}}>📝 Treść i opcje</div>
                      <div style={{marginBottom:8}}>
                        <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Dodatkowa notatka / warunki</label>
                        <textarea value={cfg.customNote||""} onChange={e=>setCfg({customNote:e.target.value})}
                          placeholder="np. Dziękujemy za zakup! Płatność w terminie 14 dni."
                          style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid #ddd6fe",fontSize:12,boxSizing:"border-box",minHeight:60,resize:"vertical",background:"#fff"}}/>
                      </div>
                      <div style={{marginBottom:8}}>
                        <label style={{fontSize:11,fontWeight:700,color:"#7c3aed",display:"block",marginBottom:3}}>Stopka dokumentu</label>
                        <textarea value={cfg.footer||""} onChange={e=>setCfg({footer:e.target.value})}
                          placeholder="np. Dokument wygenerowany przez System POS | NIP: ..."
                          style={{width:"100%",padding:"9px 12px",borderRadius:9,border:"1.5px solid #ddd6fe",fontSize:12,boxSizing:"border-box",minHeight:48,resize:"vertical",background:"#fff"}}/>
                      </div>

                      {/* Opcje */}
                      <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:10}}>
                        {[
                          {f:"showSignatures",l:"✍ Pokaż pola podpisów"},
                          {f:"showStamp",     l:"🔵 Pole na pieczęć (Faktura)"},
                        ].map(({f,l})=>(
                          <label key={f} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",padding:"8px 12px",borderRadius:9,background:"#fff",border:"1.5px solid #ddd6fe"}}>
                            <input type="checkbox" checked={cfg[f]!==false} onChange={e=>setCfg({[f]:e.target.checked})}
                              style={{width:16,height:16,accentColor:"#6d28d9"}}/>
                            <span style={{fontSize:12,fontWeight:700,color:"#6d28d9"}}>{l}</span>
                          </label>
                        ))}
                      </div>

                      <button onClick={()=>{
                        const w=window.open("","_blank");
                        w.document.write(buildDoc(demoOrder, selDoc));
                        w.document.close();
                      }} style={{marginTop:14,width:"100%",padding:"12px",background:"#6d28d9",color:"#fff",border:"none",borderRadius:10,fontWeight:800,cursor:"pointer",fontSize:14}}>
                        👁 Otwórz podgląd / drukuj testowy
                      </button>
                    </div>

                    {/* Prawa kolumna — podgląd mini */}
                    <div>
                      <div style={{fontWeight:700,fontSize:13,color:"#6d28d9",marginBottom:10}}>📋 Miniaturka podglądu</div>
                      <div style={{border:"2px solid #ddd6fe",borderRadius:12,overflow:"hidden",boxShadow:"0 4px 16px rgba(109,40,217,0.1)"}}>
                        <div style={{background:cfg.accentColor||"#6d28d9",padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                          {cfg.logo
                            ?<img src={cfg.logo} alt="" style={{height:32,objectFit:"contain",borderRadius:4}}/>
                            :<div style={{fontSize:20}}>🏪</div>
                          }
                          <div>
                            <div style={{color:"#fff",fontWeight:800,fontSize:13}}>{cfg.companyName||settings?.restaurantName||"Nazwa firmy"}</div>
                            <div style={{color:"rgba(255,255,255,0.75)",fontSize:10}}>{cfg.companyNip&&`NIP: ${cfg.companyNip}`}</div>
                          </div>
                          <div style={{marginLeft:"auto",textAlign:"right"}}>
                            <div style={{color:"#fff",fontWeight:900,fontSize:11}}>{TITLES[selDoc]}</div>
                            <div style={{color:"rgba(255,255,255,0.75)",fontSize:10}}>Nr: {selDoc.toUpperCase()}-000001</div>
                          </div>
                        </div>
                        <div style={{padding:"10px 14px",background:"#fff"}}>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:8}}>
                            <div style={{background:"#f9fafb",borderRadius:6,padding:"6px 8px",fontSize:10}}>
                              <div style={{fontWeight:700,color:cfg.accentColor||"#6d28d9",marginBottom:2}}>SPRZEDAWCA</div>
                              <div style={{color:"#333"}}>{cfg.companyName||settings?.restaurantName||"Firma"}</div>
                              {cfg.companyNip&&<div style={{color:"#666"}}>NIP: {cfg.companyNip}</div>}
                            </div>
                            <div style={{background:"#f9fafb",borderRadius:6,padding:"6px 8px",fontSize:10}}>
                              <div style={{fontWeight:700,color:cfg.accentColor||"#6d28d9",marginBottom:2}}>NABYWCA</div>
                              <div style={{color:"#333"}}>Jan Kowalski</div>
                              <div style={{color:"#666"}}>ul. Przykładowa 1</div>
                            </div>
                          </div>
                          <div style={{background:cfg.accentColor||"#6d28d9",borderRadius:"4px 4px 0 0",padding:"4px 8px",display:"grid",gridTemplateColumns:"1fr auto auto"}}>
                            <span style={{color:"#fff",fontSize:9,fontWeight:700}}>Pozycja</span>
                            <span style={{color:"#fff",fontSize:9,fontWeight:700,marginRight:8}}>Ilość</span>
                            <span style={{color:"#fff",fontSize:9,fontWeight:700}}>Wartość</span>
                          </div>
                          {demoOrder.cart.map((c,i)=>(
                            <div key={i} style={{padding:"3px 8px",display:"grid",gridTemplateColumns:"1fr auto auto",background:i%2===0?"#fafafa":"#fff",fontSize:9}}>
                              <span>{c.emoji} {c.name}</span>
                              <span style={{marginRight:8}}>{c.qty} {c.unit}</span>
                              <span style={{fontWeight:700}}>{(c.price*c.qty).toFixed(2)} zł</span>
                            </div>
                          ))}
                          <div style={{background:cfg.accentColor+"22"||"#ede9fe",padding:"5px 8px",display:"flex",justifyContent:"space-between",fontSize:10,fontWeight:900,marginTop:2,borderRadius:"0 0 4px 4px"}}>
                            <span>RAZEM</span><span>{demoOrder.total.toFixed(2)} zł</span>
                          </div>
                          {cfg.customNote&&<div style={{marginTop:6,padding:"4px 8px",background:"#faf5ff",borderRadius:5,fontSize:9,color:"#6d28d9",border:"1px solid #ddd6fe"}}>{cfg.customNote}</div>}
                          {cfg.showSignatures!==false&&(
                            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8}}>
                              <div style={{borderTop:"1px solid #333",paddingTop:3,fontSize:8,color:"#555",textAlign:"center"}}>Wystawił(a)</div>
                              <div style={{borderTop:"1px solid #333",paddingTop:3,fontSize:8,color:"#555",textAlign:"center"}}>{selDoc==="release"?"Odebrał(a)":selDoc==="delivery"?"Dostarczył(a)":"Nabywca"}</div>
                            </div>
                          )}
                          {cfg.footer&&<div style={{marginTop:6,fontSize:8,color:"#888",textAlign:"center",borderTop:"1px solid #e5e7eb",paddingTop:4}}>{cfg.footer}</div>}
                        </div>
                      </div>
                      <div style={{marginTop:8,fontSize:11,color:"#7c3aed",textAlign:"center"}}>
                        Kliknij „Otwórz podgląd" aby zobaczyć pełny dokument
                      </div>
                    </div>
                  </div>

                  {/* Info o zastosowaniu */}
                  <div style={{marginTop:14,padding:"10px 14px",background:"#ede9fe",borderRadius:10,fontSize:11,color:"#5b21b6",lineHeight:1.7}}>
                    ℹ Ustawienia są zapisywane automatycznie i stosowane do wszystkich nowo generowanych dokumentów tego typu. Zmiany widoczne są od razu w podglądzie.
                  </div>
                </div>
              );
            })()}

            {saSubTab==="database" && (
              <DatabasePanel
                orders={orders}         setOrders={setOrders}
                menu={menu}             setMenu={setMenu}
                cats={cats}             setCats={setCats}
                users={users}           setUsers={setUsers}
                clients={clients}       setClients={setClients}
                tables={tables}         setTables={setTables}
                campaigns={campaigns}   setCampaigns={setCampaigns}
                vouchers={vouchers}     setVouchers={setVouchers}
                settings={settings}     setSettings={setSettings}
                staffCalls={staffCalls} setStaffCalls={setStaffCalls}
                usedCodes={usedCodes}   setUsedCodes={setUsedCodes}
                customRoles={customRoles} setCustomRoles={setCustomRoles}
                onlineOrders={onlineOrders} setOnlineOrders={setOnlineOrders}
                onlineMenu={onlineMenu}     setOnlineMenu={setOnlineMenu}
                onlineCats={onlineCats}     setOnlineCats={setOnlineCats}
                logs={logs}             setLogs={setLogs}
                ac={ac}
              />
            )}

          </div>
        )}





        {tab==="nfccards" && (isSuperAdmin || (isAdmin && adminPerm("adminNfcCards"))) && (
          <NfcCardsPanel users={users} setUsers={setUsers} currentUser={user} />
        )}

        {tab==="messages" && (isSuperAdmin || (isTabVisible("messages", user.role) && rolePerm("adminMessages"))) && (
          <MessagesPanel
            clients={clients} orders={orders}
            settings={settings} setSettings={setSettings} ac={ac}/>
        )}

        {/* ── SPECIALS ── */}
        {tab==="specials" && (isSuperAdmin || (isTabVisible("specials", user.role) && rolePerm("adminSpecials"))) && (
          <div>
            <div style={S.formCard}>
              <div style={S.formTitle}>⭐ Polecamy dnia — modal powitalny</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.6}}>
                Pełnoekranowy modal wyświetlany klientom przed wyborem stolika — pokaż dzisiejsze polecenia: śniadania, obiady, desery i inne propozycje. Klient może go zamknąć i przejść do stolika.
              </div>

              {/* Toggle włącz/wyłącz */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"12px 14px",borderRadius:12,background:T.bg,border:"1.5px solid "+T.border,marginBottom:14}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:T.text}}>
                    {settings.dailySpecials?.enabled ? "🟢 Polecamy — aktywne" : "🔴 Polecamy — wyłączone"}
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                    {settings.dailySpecials?.enabled
                      ? "Klient widzi modal z dzisiejszymi poleceniami przed wyborem stolika."
                      : "Modal nie jest wyświetlany."}
                  </div>
                </div>
                <button
                  onClick={()=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),enabled:!(p.dailySpecials?.enabled)}}))}
                  style={{padding:"8px 16px",borderRadius:8,border:"none",fontWeight:700,cursor:"pointer",fontSize:12,
                    flexShrink:0,marginLeft:12,
                    background:settings.dailySpecials?.enabled?"#22c55e":"#ef4444",
                    color:"#fff",boxShadow:"0 2px 6px rgba(0,0,0,0.15)"}}>
                  {settings.dailySpecials?.enabled ? "✅ Włączone" : "❌ Wyłączone"}
                </button>
              </div>

              {settings.dailySpecials?.enabled && (<>
                {/* Tytuł i podtytuł */}
                <div style={{marginBottom:10}}>
                  <label style={S.label}>Tytuł modalu</label>
                  <div style={{display:"flex",gap:7,alignItems:"stretch"}}>
                    <input style={{...S.inp,flex:1}} placeholder="🌟 Polecamy dzisiaj"
                      value={settings.dailySpecials?.title||""}
                      onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),title:e.target.value}}))}/>
                  </div>
                  {/* Szybki wybór emoji do tytułu */}
                  <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:7}}>
                    {["⭐","🌟","✨","🔥","🍽","👨‍🍳","🥘","🍴","🎯","💫","🏆","🎉","🌈","❤","🤩","😋","🥗","🥩","🍕","🍣","🧁","🥐","☕","🍵"].map(em=>(
                      <button key={em}
                        title={em}
                        onClick={()=>{
                          const current = settings.dailySpecials?.title || "";
                          // Jeśli tytuł zaczyna się od emoji (Unicode ≥ 2 bajty) — zamień pierwsze; inaczej dodaj na początku
                          const stripped = current.replace(/^(\p{Emoji_Presentation}|\p{Extended_Pictographic})\s*/u, "");
                          setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),title: em + " " + stripped}}));
                        }}
                        style={{
                          width:34,height:34,borderRadius:8,border:"1.5px solid "+T.border,
                          background:"#fff",fontSize:18,cursor:"pointer",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          transition:"all .12s",flexShrink:0,
                          boxShadow:"0 1px 3px rgba(0,0,0,0.06)",
                        }}
                        onMouseOver={e=>e.currentTarget.style.borderColor=T.accent}
                        onMouseOut={e=>e.currentTarget.style.borderColor=T.border}
                      >{em}</button>
                    ))}
                  </div>
                </div>
                <div style={{...S.fGrid,marginBottom:10}} className="form-grid-2">
                  <div>
                    <label style={S.label}>Kolor akcentu (nagłówek, przyciski)</label>
                    {(settings.dailySpecials?.headerPreset||"custom")!=="custom" && (
                      <div style={{fontSize:10,color:"#d97706",background:"#fef9c3",borderRadius:5,padding:"2px 7px",marginBottom:4,display:"inline-block",fontWeight:600}}>
                        ⚡ Aktywny preset — kolor używany przez przyciski. Wybierz „Własny" żeby gradient używał tych kolorów.
                      </div>
                    )}
                    <div style={{display:"flex",gap:7,alignItems:"center"}}>
                      <input type="color"
                        value={settings.dailySpecials?.accentColor||"#C8621A"}
                        onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),accentColor:e.target.value}}))}
                        style={{width:42,height:34,border:"1.5px solid "+T.border,borderRadius:7,cursor:"pointer",padding:2}}/>
                      <input style={{...S.inp,flex:1}} value={settings.dailySpecials?.accentColor||"#C8621A"}
                        onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),accentColor:e.target.value}}))}/>
                    </div>
                    {/* Gotowe palety akcentu */}
                    <div style={{display:"flex",gap:5,marginTop:7,flexWrap:"wrap"}}>
                      {["#C8621A","#e94560","#2563eb","#16a34a","#7c3aed","#db2777","#0891b2","#d97706","#111827","#be123c"].map(c=>(
                        <button key={c} title={c} onClick={()=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),accentColor:c}}))}
                          style={{width:26,height:26,borderRadius:6,background:c,border:(settings.dailySpecials?.accentColor||"#C8621A")===c?"3px solid #111":"2px solid rgba(0,0,0,0.12)",cursor:"pointer",flexShrink:0}}/>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={S.label}>Drugi kolor gradientu nagłówka</label>
                    <div style={{display:"flex",gap:7,alignItems:"center"}}>
                      <input type="color"
                        value={settings.dailySpecials?.accentColor2||"#3B1F0A"}
                        onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),accentColor2:e.target.value}}))}
                        style={{width:42,height:34,border:"1.5px solid "+T.border,borderRadius:7,cursor:"pointer",padding:2}}/>
                      <input style={{...S.inp,flex:1}} value={settings.dailySpecials?.accentColor2||"#3B1F0A"}
                        onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),accentColor2:e.target.value}}))}/>
                    </div>
                    {/* Gotowe palety drugiego koloru */}
                    <div style={{display:"flex",gap:5,marginTop:7,flexWrap:"wrap"}}>
                      {["#3B1F0A","#111827","#1e1e2e","#0f172a","#1a1a2e","#14532d","#1e3a5f","#3b0764","#7f1d1d","#422006"].map(c=>(
                        <button key={c} title={c} onClick={()=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),accentColor2:c}}))}
                          style={{width:26,height:26,borderRadius:6,background:c,border:(settings.dailySpecials?.accentColor2||"#3B1F0A")===c?"3px solid "+T.accent:"2px solid rgba(0,0,0,0.12)",cursor:"pointer",flexShrink:0}}/>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Styl nagłówka — presety */}
                <div style={{marginBottom:10}}>
                  <label style={S.label}>🎨 Styl nagłówka — gotowe presety</label>
                  <div style={{display:"flex",gap:7,flexWrap:"wrap",marginTop:5}}>
                    {[
                      {key:"custom",   label:"Własny",    g:"linear-gradient(135deg,#C8621A,#3B1F0A)"},
                      {key:"granite",  label:"Granit",    g:"linear-gradient(135deg,#3d3d3d 0%,#1a1a1a 50%,#2e2e2e 100%)"},
                      {key:"slate",    label:"Łupek",     g:"linear-gradient(135deg,#334155 0%,#0f172a 100%)"},
                      {key:"gold",     label:"Złoto",     g:"linear-gradient(135deg,#b45309 0%,#78350f 100%)"},
                      {key:"emerald",  label:"Szmaragd",  g:"linear-gradient(135deg,#065f46 0%,#022c22 100%)"},
                      {key:"wine",     label:"Wino",      g:"linear-gradient(135deg,#881337 0%,#3b0764 100%)"},
                      {key:"midnight", label:"Noc",       g:"linear-gradient(135deg,#1e3a5f 0%,#0f172a 100%)"},
                      {key:"rose",     label:"Róż",       g:"linear-gradient(135deg,#be185d 0%,#881337 100%)"},
                    ].map(p=>{
                      const active = (settings.dailySpecials?.headerPreset||"custom")===p.key;
                      return (
                        <button key={p.key}
                          onClick={()=>{
                            setSettings(prev=>({...prev,dailySpecials:{
                              ...(prev.dailySpecials||{}),
                              headerPreset: p.key,
                            }}));
                          }}
                          style={{
                            borderRadius:10, border: active?"3px solid "+T.accent:"2px solid transparent",
                            cursor:"pointer", overflow:"hidden", flexShrink:0,
                            padding:0, width:68, boxShadow:"0 2px 6px rgba(0,0,0,0.18)",
                          }}>
                          <div style={{background:p.g,height:28,width:"100%"}}/>
                          <div style={{
                            fontSize:9,fontWeight:700,padding:"3px 2px",
                            background:active?T.accent+"22":"#f8fafc",
                            color:active?T.accent:T.muted,textAlign:"center",
                          }}>{p.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Obrazek nagłówka */}
                <div style={{marginBottom:10}}>
                  <label style={S.label}>🖼 Obrazek tła nagłówka (opcjonalny)</label>
                  <div style={{fontSize:11,color:T.muted,marginBottom:6,lineHeight:1.5}}>
                    Zastępuje gradient kolorem — możesz ustawić krycie, żeby gradient przebijał przez zdjęcie.
                  </div>
                  {settings.dailySpecials?.headerImage ? (
                    <div style={{position:"relative",borderRadius:10,overflow:"hidden",height:80,marginBottom:6}}>
                      <img src={settings.dailySpecials.headerImage} alt=""
                        style={{width:"100%",height:"100%",objectFit:"cover"}}
                        onError={e=>{e.target.style.display="none";}}/>
                      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.38)",display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                        <button onClick={()=>setPickerCtx({type:"specialsHeader"})}
                          style={{padding:"5px 12px",background:"rgba(255,255,255,0.92)",border:"none",borderRadius:7,fontWeight:700,fontSize:11,cursor:"pointer",color:"#111"}}>
                          🔄 Zmień
                        </button>
                        <button onClick={()=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),headerImage:null}}))}
                          style={{padding:"5px 12px",background:"rgba(239,68,68,0.9)",border:"none",borderRadius:7,fontWeight:700,fontSize:11,cursor:"pointer",color:"#fff"}}>
                          🗑 Usuń
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={()=>setPickerCtx({type:"specialsHeader"})}
                      style={{...S.darkBtn,width:"100%",background:"#f8fafc",border:"1.5px dashed "+T.border,color:T.muted,fontSize:12,marginBottom:6}}>
                      📁 Wgraj lub podaj URL obrazka nagłówka
                    </button>
                  )}
                  {settings.dailySpecials?.headerImage && (
                    <div>
                      <label style={S.label}>Krycie gradientu nad obrazkiem</label>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <input type="range" min={0} max={1} step={0.05}
                          value={settings.dailySpecials?.headerImageOverlay??0.55}
                          onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),headerImageOverlay:Number(e.target.value)}}))}
                          style={{flex:1}}/>
                        <span style={{fontSize:12,fontWeight:700,color:T.text,minWidth:34,textAlign:"right"}}>
                          {Math.round((settings.dailySpecials?.headerImageOverlay??0.55)*100)}%
                        </span>
                      </div>
                      <div style={{fontSize:10,color:T.muted,marginTop:3}}>0% = samo zdjęcie · 100% = sam gradient</div>
                    </div>
                  )}
                </div>

                <div style={{marginBottom:10}}>
                  <label style={S.label}>Kolor tła modalu</label>
                    <div style={{display:"flex",gap:7,alignItems:"center"}}>
                      <input type="color"
                        value={settings.dailySpecials?.bgColor||"#FDF6EC"}
                        onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),bgColor:e.target.value}}))}
                        style={{width:42,height:34,border:"1.5px solid "+T.border,borderRadius:7,cursor:"pointer",padding:2}}/>
                      <input style={{...S.inp,flex:1}} value={settings.dailySpecials?.bgColor||"#FDF6EC"}
                        onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),bgColor:e.target.value}}))}/>
                    </div>
                    {/* Gotowe palety tła */}
                    <div style={{display:"flex",gap:5,marginTop:7,flexWrap:"wrap"}}>
                      {["#FDF6EC","#ffffff","#f8fafc","#f0fdf4","#eff6ff","#fdf4ff","#fff7ed","#fef2f2","#f0f9ff","#1e1e2e"].map(c=>(
                        <button key={c} title={c} onClick={()=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),bgColor:c}}))}
                          style={{width:26,height:26,borderRadius:6,background:c,border:(settings.dailySpecials?.bgColor||"#FDF6EC")===c?"3px solid #111":"2px solid rgba(0,0,0,0.12)",cursor:"pointer",flexShrink:0}}/>
                      ))}
                    </div>
                    {/* Obrazek tła */}
                    <div style={{marginTop:10}}>
                      <label style={S.label}>🖼 Obrazek tła (opcjonalny)</label>
                      {settings.dailySpecials?.bgImage ? (
                        <div style={{position:"relative",borderRadius:10,overflow:"hidden",height:72,marginTop:4}}>
                          <img src={settings.dailySpecials.bgImage} alt=""
                            style={{width:"100%",height:"100%",objectFit:"cover"}}
                            onError={e=>{e.target.style.display="none";}}/>
                          <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
                            <button onClick={()=>setPickerCtx({type:"specialsBg"})}
                              style={{padding:"5px 11px",background:"rgba(255,255,255,0.92)",border:"none",borderRadius:7,
                                fontWeight:700,fontSize:11,cursor:"pointer",color:"#111"}}>
                              🔄 Zmień
                            </button>
                            <button onClick={()=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),bgImage:null}}))}
                              style={{padding:"5px 11px",background:"rgba(239,68,68,0.92)",border:"none",borderRadius:7,
                                fontWeight:700,fontSize:11,cursor:"pointer",color:"#fff"}}>
                              🗑 Usuń
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={()=>setPickerCtx({type:"specialsBg"})}
                          style={{...S.darkBtn,width:"100%",marginTop:4,background:"#f8fafc",
                            border:"1.5px dashed "+T.border,color:T.muted,fontSize:12}}>
                          📁 Wgraj lub podaj URL obrazka tła
                        </button>
                      )}
                      {settings.dailySpecials?.bgImage && (
                        <div style={{marginTop:5}}>
                          <label style={S.label}>Krycie obrazka tła</label>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <input type="range" min={0} max={1} step={0.05}
                              value={settings.dailySpecials?.bgImageOpacity??0.15}
                              onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),bgImageOpacity:Number(e.target.value)}}))}
                              style={{flex:1}}/>
                            <span style={{fontSize:12,fontWeight:700,color:T.text,minWidth:34,textAlign:"right"}}>
                              {Math.round((settings.dailySpecials?.bgImageOpacity??0.15)*100)}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                </div>

                {/* Podgląd nagłówka + tła */}
                <div style={{marginBottom:10,borderRadius:12,overflow:"hidden",border:"1.5px solid "+T.border}}>
                  <div style={{
                    position:"relative", overflow:"hidden",
                    padding:"10px 14px",display:"flex",alignItems:"center",gap:8,
                  }}>
                    {settings.dailySpecials?.headerImage && (
                      <img src={settings.dailySpecials.headerImage} alt=""
                        style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",pointerEvents:"none"}}
                        onError={e=>{e.target.style.display="none";}}/>
                    )}
                    <div style={{
                      position:"absolute",inset:0,
                      background:(()=>{
                        const ds=settings.dailySpecials||{};
                        const p=ds.headerPreset||"custom";
                        const presets={granite:"linear-gradient(135deg,#3d3d3d 0%,#1a1a1a 50%,#2e2e2e 100%)",slate:"linear-gradient(135deg,#334155 0%,#0f172a 100%)",gold:"linear-gradient(135deg,#b45309 0%,#78350f 100%)",emerald:"linear-gradient(135deg,#065f46 0%,#022c22 100%)",wine:"linear-gradient(135deg,#881337 0%,#3b0764 100%)",midnight:"linear-gradient(135deg,#1e3a5f 0%,#0f172a 100%)",rose:"linear-gradient(135deg,#be185d 0%,#881337 100%)"};
                        return presets[p]||`linear-gradient(135deg,${ds.accentColor||"#C8621A"} 0%,${ds.accentColor2||"#3B1F0A"} 100%)`;
                      })(),
                      opacity: settings.dailySpecials?.headerImage ? (settings.dailySpecials?.headerImageOverlay??0.55) : 1,
                    }}/>
                    <span style={{position:"relative",zIndex:1,fontSize:18}}>⭐</span>
                    <span style={{position:"relative",zIndex:1,color:"#fff",fontWeight:800,fontSize:13,textShadow:"0 1px 4px rgba(0,0,0,0.5)"}}>{settings.dailySpecials?.title||"Polecamy dzisiaj"}</span>
                  </div>
                  <div style={{
                    position:"relative",
                    background:settings.dailySpecials?.bgColor||"#FDF6EC",
                    padding:"10px 14px",fontSize:12,color:"#8B6A4A",fontWeight:500,
                    overflow:"hidden",
                  }}>
                    {settings.dailySpecials?.bgImage && (
                      <img src={settings.dailySpecials.bgImage} alt=""
                        style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",
                          opacity:settings.dailySpecials?.bgImageOpacity??0.15,pointerEvents:"none"}}
                        onError={e=>{e.target.style.display="none";}}/>
                    )}
                    <span style={{position:"relative",zIndex:1}}>Podgląd tła modalu</span>
                  </div>
                </div>

                <div style={{marginBottom:10}}>
                  <label style={S.label}>Podtytuł / opis pod tytułem</label>
                  <input style={S.inp} placeholder="Sprawdź nasze propozycje na dziś!"
                    value={settings.dailySpecials?.subtitle||""}
                    onChange={e=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),subtitle:e.target.value}}))}/>
                </div>

                {/* Opcje */}
                <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
                  {[
                    ["showDate","📅 Pokaż dzisiejszą datę"],
                    ["autoShowOnEntry","🚀 Pokaż automatycznie przy wejściu"],
                  ].map(([key,lbl])=>(
                    <button key={key}
                      onClick={()=>setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),[key]:!(p.dailySpecials?.[key]!==false)}}))}
                      style={{...S.pill,...(settings.dailySpecials?.[key]!==false?S.pillOn:{}),fontSize:11}}>
                      {lbl} {settings.dailySpecials?.[key]!==false?"✓":""}
                    </button>
                  ))}
                </div>

                {/* Lista pozycji */}
                <div style={{fontWeight:700,fontSize:12,color:T.text,marginBottom:8}}>Pozycje na dziś:</div>
                {(settings.dailySpecials?.items||[]).map((item,idx)=>{
                  const updateItem = (patch) => {
                    const arr=[...(settings.dailySpecials?.items||[])];
                    arr[idx]={...arr[idx],...patch};
                    setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),items:arr}}));
                  };
                  const linkedMenuItem = item.menuItemId ? menu.find(m=>m.id===item.menuItemId) : null;
                  return (
                  <div key={idx} style={{...S.card,padding:"12px 14px",marginBottom:8,border:"1.5px solid "+T.border}}>
                    {/* Nagłówek karty */}
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                      <div style={{fontWeight:700,fontSize:12,color:T.text}}>
                        {item.name||`Pozycja ${idx+1}`}
                        {linkedMenuItem&&<span style={{marginLeft:7,background:"#eff6ff",color:T.accent,borderRadius:6,padding:"2px 8px",fontSize:10,fontWeight:700}}>📌 z menu</span>}
                      </div>
                      <button
                        onClick={()=>{
                          const arr=(settings.dailySpecials?.items||[]).filter((_,i)=>i!==idx);
                          setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),items:arr}}));
                        }}
                        style={{...S.actBtn,color:"#ef4444"}}>🗑 Usuń</button>
                    </div>

                    {/* Wybór z menu */}
                    <div style={{marginBottom:10}}>
                      <label style={S.label}>📋 Wybierz z menu (opcjonalnie)</label>
                      <select
                        value={item.menuItemId||""}
                        onChange={e=>{
                          const mid = e.target.value ? Number(e.target.value) : null;
                          if (!mid) { updateItem({menuItemId:null}); return; }
                          const mi = menu.find(m=>m.id===mid);
                          if (mi) updateItem({
                            menuItemId: mi.id,
                            name: mi.name,
                            desc: mi.desc||"",
                            price: mi.promoPrice!=null ? `${mi.promoPrice} zł` : `${mi.price} zł`,
                            emoji: mi.emoji||item.emoji||"🍽",
                            image: mi.image||null,
                          });
                        }}
                        style={{...S.inp}}>
                        <option value="">— ręczne wprowadzenie —</option>
                        {(()=>{
                          const grouped = {};
                          menu.filter(m=>m.active).forEach(m=>{
                            const cat = cats.find(c=>c.id===m.catId);
                            const label = cat ? cat.name : "Inne";
                            if (!grouped[label]) grouped[label]=[];
                            grouped[label].push(m);
                          });
                          return Object.entries(grouped).map(([catName, items])=>(
                            <optgroup key={catName} label={catName}>
                              {items.map(m=>(
                                <option key={m.id} value={m.id}>
                                  {m.emoji||"🍽"} {m.name} — {m.promoPrice!=null?m.promoPrice:m.price} zł
                                </option>
                              ))}
                            </optgroup>
                          ));
                        })()}
                      </select>
                    </div>

                    {/* Podgląd zdjęcia jeśli jest */}
                    {(item.image || linkedMenuItem?.image) && (
                      <div style={{marginBottom:10,borderRadius:10,overflow:"hidden",height:90,background:"#f3f4f6",position:"relative"}}>
                        <img src={item.image||linkedMenuItem?.image} alt=""
                          style={{width:"100%",height:"100%",objectFit:"cover"}}
                          onError={e=>{e.target.style.display="none";}}/>
                        <div style={{position:"absolute",bottom:6,right:6}}>
                          <button onClick={()=>updateItem({image:null})}
                            style={{...S.actBtn,background:"rgba(0,0,0,0.55)",color:"#fff",border:"none",fontSize:11,padding:"3px 8px"}}>
                            🗑 Usuń zdjęcie
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Pola ręczne */}
                    <div style={{...S.fGrid,marginBottom:8}} className="form-grid-2">
                      <div>
                        <label style={S.label}>Emoji</label>
                        <input style={{...S.inp,fontSize:18,textAlign:"center"}} placeholder="🍳"
                          value={item.emoji||""}
                          onChange={e=>updateItem({emoji:e.target.value})}/>
                      </div>
                      <div>
                        <label style={S.label}>Kategoria (grupowanie)</label>
                        <input style={S.inp} placeholder="np. Śniadania"
                          value={item.category||""}
                          onChange={e=>updateItem({category:e.target.value})}/>
                      </div>
                    </div>
                    <div style={{marginBottom:8}}>
                      <label style={S.label}>Nazwa dania *</label>
                      <input style={S.inp} placeholder="np. Jajecznica z boczkiem"
                        value={item.name||""}
                        onChange={e=>updateItem({name:e.target.value})}/>
                    </div>
                    <div style={{...S.fGrid,marginBottom:8}} className="form-grid-2">
                      <div>
                        <label style={S.label}>Opis (opcjonalny)</label>
                        <input style={S.inp} placeholder="np. z pomidorami i szczypiorkiem"
                          value={item.desc||""}
                          onChange={e=>updateItem({desc:e.target.value})}/>
                      </div>
                      <div>
                        <label style={S.label}>Cena (opcjonalna)</label>
                        <input style={S.inp} placeholder="np. 28 zł"
                          value={item.price||""}
                          onChange={e=>updateItem({price:e.target.value})}/>
                      </div>
                    </div>
                    {!item.image && (
                      <div>
                        <label style={S.label}>URL zdjęcia (opcjonalny)</label>
                        <input style={S.inp} placeholder="https://..."
                          value={item.imageUrl||""}
                          onChange={e=>updateItem({imageUrl:e.target.value,image:e.target.value||null})}/>
                      </div>
                    )}
                  </div>
                  );
                })}

                <button
                  onClick={()=>{
                    const arr=[...(settings.dailySpecials?.items||[]),{emoji:"🍽",name:"",desc:"",price:"",category:""}];
                    setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),items:arr}}));
                  }}
                  style={{...S.darkBtn,width:"100%",marginBottom:14,background:"#f0fdf4",color:"#15803d",border:"1.5px dashed #86efac"}}>
                  ➕ Dodaj pozycję
                </button>

                {/* Szybkie szablony */}
                <div style={{background:"#f0f9ff",border:"1.5px solid #bae6fd",borderRadius:10,padding:"10px 12px"}}>
                  <div style={{fontWeight:700,fontSize:11,color:"#0284c7",marginBottom:8}}>⚡ Szybkie szablony kategorii:</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {[["🌅 Śniadania","Śniadania"],["🥗 Obiady","Obiady"],["🍰 Desery","Desery"],["🍵 Napoje","Napoje"],["🌮 Dania dnia","Danie dnia"]].map(([lbl,cat])=>(
                      <button key={cat}
                        onClick={()=>{
                          const arr=[...(settings.dailySpecials?.items||[]),{emoji:"🍽",name:"",desc:"",price:"",category:cat}];
                          setSettings(p=>({...p,dailySpecials:{...(p.dailySpecials||{}),items:arr}}));
                        }}
                        style={{...S.pill,fontSize:11,background:"#fff",color:"#0369a1",border:"1px solid #7dd3fc"}}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              </>)}
            </div>
          </div>
        )}

        {/* ── SETTINGS ── */}
        {tab==="settings" && (isSuperAdmin || (isTabVisible("settings", user.role) && rolePerm("adminSettings"))) && (()=>{
          return (
          <div>
            {/* ── Nawigacja sekcji ── */}
            <div style={{
              display:"flex",gap:6,flexWrap:"wrap",marginBottom:18,
              background:"#f3f4f6",borderRadius:14,padding:6,
            }}>
              {SETT_SECTIONS.map(s=>(
                <button key={s.id} onClick={()=>setSettSec(s.id)}
                  style={{
                    flex:"1 1 auto",display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                    padding:"10px 10px",borderRadius:10,border:"none",cursor:"pointer",
                    background:settSec===s.id?"#fff":"transparent",
                    color:settSec===s.id?T.primary:T.muted,
                    fontWeight:settSec===s.id?800:500,
                    fontSize:13,
                    boxShadow:settSec===s.id?"0 2px 8px rgba(0,0,0,0.1)":"none",
                    transition:"all .15s",
                  }}>
                  <span>{s.icon}</span>
                  <span style={{whiteSpace:"nowrap"}}>{s.label}</span>
                </button>
              ))}
            </div>

            {/* ══ SEKCJA: DANE RESTAURACJI ══ */}
            {settSec==="restaurant" && (<>
            <div style={S.formCard}>
              <div style={S.formTitle}>🏠 Logo restauracji</div>
              <div style={{display:"flex",alignItems:"center",gap:13,marginTop:10}}>
                <div style={{width:86,height:86,borderRadius:15,overflow:"hidden",border:settings.logo?`2.5px solid ${ac}`:"2px dashed #ccc",
                  background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0}}
                  onClick={()=>setPickerCtx({type:"logo"})}>
                  {settings.logo
                    ?<img src={settings.logo} alt="logo" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    :<div style={{textAlign:"center",color:"#ccc"}}><div style={{fontSize:30}}>🏠</div><div style={{fontSize:9,marginTop:3}}>LOGO</div></div>}
                </div>
                <div>
                  <p style={{fontSize:12,color:T.muted,margin:"0 0 9px"}}>Logo widoczne w nagłówku dla klientów i na ekranie logowania.</p>
                  <button style={S.darkBtn} onClick={()=>setPickerCtx({type:"logo"})}>{settings.logo?"Zmień logo":"Dodaj logo"}</button>
                  {settings.logo&&<button style={{...S.cancelBtn,display:"block",marginTop:7}} onClick={()=>setSettings(p=>({...p,logo:null}))}>🗑 Usuń logo</button>}
                </div>
              </div>
            </div>

            <div style={S.formCard}>
              <div style={S.formTitle}>✏ Dane restauracji / firmy</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:12}}>Dane używane w dokumentach (faktury, WZ, dowody dostawy) i szablonach.</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>Nazwa restauracji / firmy</label>
                  <input style={S.inp} value={settings.restaurantName}
                    onChange={e=>setSettings(p=>({...p,restaurantName:e.target.value}))} placeholder="Nazwa restauracji"/>
                </div>
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>Adres</label>
                  <input style={S.inp} value={settings.address||""}
                    onChange={e=>setSettings(p=>({...p,address:e.target.value}))} placeholder="ul. Przykładowa 1, 00-001 Warszawa"/>
                </div>
                <div>
                  <label style={S.label}>NIP</label>
                  <input style={S.inp} value={settings.nip||""}
                    onChange={e=>setSettings(p=>({...p,nip:e.target.value}))} placeholder="000-000-00-00"/>
                </div>
                <div>
                  <label style={S.label}>Telefon</label>
                  <input style={S.inp} value={settings.phone||""}
                    onChange={e=>setSettings(p=>({...p,phone:e.target.value}))} placeholder="+48 000 000 000"/>
                </div>
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>E-mail</label>
                  <input style={S.inp} type="email" value={settings.email||""}
                    onChange={e=>setSettings(p=>({...p,email:e.target.value}))} placeholder="kontakt@firma.pl"/>
                </div>
              </div>
              <div style={{marginTop:8,fontSize:11,color:"#059669",background:"#f0fdf4",borderRadius:8,padding:"6px 12px",border:"1px solid #bbf7d0"}}>
                ✅ Dane automatycznie stosowane w Szablonach dokumentów — kliknij „Pobierz z ustawień" w edytorze szablonów.
              </div>
            </div>

            {/* ── INFORMACJA DLA KLIENTÓW ── */}
            <div style={S.formCard}>
              <div style={S.formTitle}>📢 Informacja dla klientów — strona wyboru stolika</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:12,lineHeight:1.6}}>
                Tekst wyświetlany klientom na ekranie wyboru stolika — np. godziny otwarcia, promocje dnia, ważne komunikaty. Obsługuje wiele linii (Enter = nowa linia). Gdy pole jest puste, baner nie pojawia się.
              </div>
              <textarea
                style={{...S.inp,resize:"vertical",minHeight:88,fontFamily:"inherit",lineHeight:1.65}}
                placeholder={"np. Kuchnia czynna do 21:00 🕘\nHappy Hours 16:00–18:00 — napoje 20% taniej 🎉\nWiFi: nazwa_sieci | hasło: haslo123"}
                value={settings.tablePageInfo||""}
                onChange={e=>setSettings(p=>({...p,tablePageInfo:e.target.value}))}
              />
              <div style={{fontSize:10,color:T.muted,marginTop:5,marginBottom: settings.tablePageInfo?12:0}}>
                Tekst pojawi się w żółtej ramce nad listą stolików.
              </div>
              {settings.tablePageInfo&&(
                <div style={{background:"linear-gradient(135deg,#fff8e1,#fffef5)",border:"1.5px solid #E8A020",borderRadius:12,padding:"11px 14px"}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#a07010",textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>Podgląd:</div>
                  <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                    <span style={{fontSize:16,flexShrink:0}}>ℹ</span>
                    <div style={{fontSize:12,color:"#6B3F1E",lineHeight:1.65,whiteSpace:"pre-wrap"}}>{settings.tablePageInfo}</div>
                  </div>
                </div>
              )}
            </div>


            {/* ── POLITYKA PRYWATNOŚCI ── */}
            {adminPerm("adminPrivacyPolicy") && (
            <div style={S.formCard}>
              <div style={S.formTitle}>🔒 Polityka prywatności (RODO/GDPR)</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.6}}>
                Gdy włączona — klient musi zaakceptować politykę prywatności przed wyborem stolika. Odmowa przekierowuje do ekranu informującego o możliwości złożenia zamówienia przy kasie.
              </div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"12px 14px",borderRadius:12,background:T.bg,border:"1.5px solid "+T.border,marginBottom:14}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:T.text}}>
                    {(settings.privacyPolicy?.enabled) ? "🟢 Polityka prywatności aktywna" : "🔴 Polityka prywatności wyłączona"}
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                    {(settings.privacyPolicy?.enabled)
                      ? "Klient widzi modal z polityką prywatności przy wejściu."
                      : "Klient od razu przechodzi do wyboru stolika."}
                  </div>
                </div>
                <button
                  onClick={()=>setSettings(p=>({...p,privacyPolicy:{...(p.privacyPolicy||{}),enabled:!(p.privacyPolicy?.enabled)}}))}
                  style={{padding:"8px 16px",borderRadius:8,border:"none",fontWeight:700,cursor:"pointer",fontSize:12,
                    flexShrink:0,marginLeft:12,background:(settings.privacyPolicy?.enabled)?"#22c55e":"#ef4444",
                    color:"#fff",boxShadow:"0 2px 6px rgba(0,0,0,0.15)"}}>
                  {(settings.privacyPolicy?.enabled) ? "✅ Włączona" : "❌ Wyłączona"}
                </button>
              </div>
              <label style={S.label}>Tytuł okna polityki</label>
              <input style={{...S.inp,marginBottom:12}} placeholder="np. Polityka prywatności"
                value={settings.privacyPolicy?.title || ""}
                onChange={e=>setSettings(p=>({...p,privacyPolicy:{...(p.privacyPolicy||{}),title:e.target.value}}))}/>
              <label style={S.label}>Treść polityki prywatności</label>
              <textarea style={{...S.inp,resize:"vertical",minHeight:160,fontFamily:"inherit",lineHeight:1.65,marginBottom:12}}
                placeholder="Wpisz treść polityki prywatności (RODO)…"
                value={settings.privacyPolicy?.content || ""}
                onChange={e=>setSettings(p=>({...p,privacyPolicy:{...(p.privacyPolicy||{}),content:e.target.value}}))}/>
              <label style={S.label}>Etykieta przycisku AKCEPTUJ</label>
              <input style={{...S.inp,marginBottom:12}} placeholder="np. Akceptuję i przechodzę do zamówienia"
                value={settings.privacyPolicy?.acceptLabel || ""}
                onChange={e=>setSettings(p=>({...p,privacyPolicy:{...(p.privacyPolicy||{}),acceptLabel:e.target.value}}))}/>
              <label style={S.label}>Etykieta przycisku ODRZUĆ</label>
              <input style={{...S.inp,marginBottom:12}} placeholder="np. Nie akceptuję"
                value={settings.privacyPolicy?.declineLabel || ""}
                onChange={e=>setSettings(p=>({...p,privacyPolicy:{...(p.privacyPolicy||{}),declineLabel:e.target.value}}))}/>
              <label style={S.label}>Komunikat dla klienta po odrzuceniu</label>
              <textarea style={{...S.inp,resize:"vertical",minHeight:80,fontFamily:"inherit",lineHeight:1.65,marginBottom:10}}
                placeholder="np. Zapraszamy do złożenia zamówienia u kelnera lub przy kasie."
                value={settings.privacyPolicy?.declineMessage || ""}
                onChange={e=>setSettings(p=>({...p,privacyPolicy:{...(p.privacyPolicy||{}),declineMessage:e.target.value}}))}/>
              <div style={{fontSize:10,color:T.muted,lineHeight:1.5}}>
                Ten tekst pojawi się na ekranie dla klientów, którzy odrzucili politykę prywatności.
              </div>
            </div>
            )}

            </>)}

            {/* ══ SEKCJA: WYGLĄD ══ */}
            {settSec==="appearance" && (<>
            {/* TLO STRONY KLIENTA */}
            <div style={S.formCard}>
              <div style={S.formTitle}>🎨 Tło strony wyboru stolika</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14}}>Wybierz styl tła widoczny dla klienta na stronie głównej.</div>

              <label style={S.label}>Styl tła</label>
              <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
                {[
                  ["bakery", "🥐 Piekarniczo-cukierniczy"],
                  ["plain",  "⬜ Jednolity kolor"],
                  ["custom", "🖼 Własne zdjęcie"],
                ].map(([v,l])=>{
                  const active = (settings.customerBgStyle||"bakery")===v;
                  return (
                    <div key={v} onClick={()=>setSettings(p=>({...p,customerBgStyle:v}))}
                      style={{flex:"1 1 130px",padding:"10px 12px",borderRadius:8,cursor:"pointer",
                        border:"2px solid "+(active?T.accent:T.border),
                        background:active?T.accentLight:"#fff",transition:"all .15s"}}>
                      <div style={{fontWeight:600,fontSize:13,color:active?T.accent:T.text}}>{l}</div>
                    </div>
                  );
                })}
              </div>

              {/* Podglad */}
              <div style={{
                borderRadius:10,height:70,marginBottom:14,overflow:"hidden",
                background:(settings.customerBgStyle||"bakery")==="custom"&&settings.customerBgImage
                  ? `url(${settings.customerBgImage}) center/cover`
                  : (settings.customerBgStyle||"bakery")==="plain"
                    ? (settings.customerBgColor||"#FDF6EC")
                    : "linear-gradient(160deg,#3B1F0A 0%,#6B3F1E 45%,#C8621A 100%)",
                display:"flex",alignItems:"center",justifyContent:"center",
                border:"1px solid "+T.border
              }}>
                <span style={{background:"rgba(255,255,255,0.88)",borderRadius:8,padding:"5px 14px",fontSize:12,fontWeight:600,color:T.text}}>Podgląd tła</span>
              </div>

              {/* Kolor — plain */}
              {(settings.customerBgStyle||"bakery")==="plain"&&(
                <div style={{marginBottom:12}}>
                  <label style={S.label}>Kolor tła</label>
                  <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
                    <input type="color" value={settings.customerBgColor||"#FDF6EC"}
                      onChange={e=>setSettings(p=>({...p,customerBgColor:e.target.value}))}
                      style={{width:44,height:36,borderRadius:7,border:"1px solid "+T.border,cursor:"pointer",padding:2}}/>
                    <input style={{...S.inp,flex:1}} value={settings.customerBgColor||"#FDF6EC"}
                      onChange={e=>setSettings(p=>({...p,customerBgColor:e.target.value}))}
                      placeholder="#FDF6EC"/>
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {["#FDF6EC","#F0F8FF","#FFF0F5","#F0FFF0","#FFFFF0","#1a1a2e","#16213e","#f8f9fa"].map(c=>(
                      <div key={c} onClick={()=>setSettings(p=>({...p,customerBgColor:c}))}
                        style={{width:28,height:28,borderRadius:6,background:c,cursor:"pointer",
                          border:"2px solid "+(settings.customerBgColor===c?T.accent:T.border)}}/>
                    ))}
                  </div>
                </div>
              )}

              {/* Zdjecie — custom */}
              {(settings.customerBgStyle||"bakery")==="custom"&&(
                <div style={{marginBottom:4}}>
                  <label style={S.label}>Zdjęcie tła</label>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <button onClick={()=>setPickerCtx({type:"customerBg"})}
                      style={{...S.darkBtn,fontSize:12}}>📷 Wybierz zdjęcie</button>
                    {settings.customerBgImage&&(
                      <>
                        <img src={settings.customerBgImage} alt="" style={{height:48,borderRadius:7,border:"1px solid "+T.border,objectFit:"cover",maxWidth:100}}/>
                        <button onClick={()=>setSettings(p=>({...p,customerBgImage:null}))}
                          style={{...S.cancelBtn,fontSize:11,color:"#dc2626",borderColor:"#fecaca",background:"#fef2f2"}}>Usuń</button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Przezroczystość karty stolików */}
              <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid "+T.border}}>
                <label style={S.label}>Przezroczystość karty wyboru stolika</label>
                <div style={{display:"flex",alignItems:"center",gap:12,marginTop:6}}>
                  <span style={{fontSize:11,color:T.muted,minWidth:28,textAlign:"right"}}>0%</span>
                  <input
                    type="range" min="0" max="1" step="0.01"
                    value={settings.tableSelectorOpacity !== undefined ? settings.tableSelectorOpacity : 0.97}
                    onChange={e=>setSettings(p=>({...p,tableSelectorOpacity:Number(e.target.value)}))}
                    style={{flex:1,accentColor:T.accent,height:4,cursor:"pointer"}}
                  />
                  <span style={{fontSize:11,color:T.muted,minWidth:28}}>100%</span>
                  <span style={{
                    fontWeight:700,fontSize:13,color:T.accent,
                    minWidth:42,textAlign:"center",
                    background:T.accentSoft,borderRadius:7,padding:"3px 7px",
                  }}>
                    {Math.round((settings.tableSelectorOpacity !== undefined ? settings.tableSelectorOpacity : 0.97)*100)}%
                  </span>
                </div>
                {/* Podgląd przezroczystości */}
                <div style={{
                  marginTop:10,borderRadius:10,overflow:"hidden",border:"1px solid "+T.border,
                  background:(settings.customerBgStyle||"bakery")==="custom"&&settings.customerBgImage
                    ? `url(${settings.customerBgImage}) center/cover`
                    : (settings.customerBgStyle||"bakery")==="plain"
                      ? (settings.customerBgColor||"#FDF6EC")
                      : "linear-gradient(160deg,#3B1F0A 0%,#6B3F1E 45%,#C8621A 100%)",
                  padding:14, display:"flex", alignItems:"center", justifyContent:"center",
                }}>
                  <div style={{
                    background:`rgba(${(settings.customerBgStyle||"bakery")==="bakery"?"253,246,236":"255,255,255"},${settings.tableSelectorOpacity !== undefined ? settings.tableSelectorOpacity : 0.97})`,
                    borderRadius:12,padding:"10px 20px",
                    border:"1.5px solid "+(settings.customerBgStyle||"bakery")==="bakery"?"#E8D5BA":T.border,
                    fontSize:12,fontWeight:600,color:(settings.customerBgStyle||"bakery")==="bakery"?"#3B1F0A":T.text,
                    boxShadow:"0 4px 16px rgba(0,0,0,0.15)",
                  }}>
                    🪑 Podgląd karty ({Math.round((settings.tableSelectorOpacity !== undefined ? settings.tableSelectorOpacity : 0.97)*100)}% nieprzezroczystości)
                  </div>
                </div>
                <div style={{fontSize:10,color:T.muted,marginTop:6}}>
                  Niska wartość = bardziej przezroczysta karta (widać tło przez kartę). Domyślnie 97%.
                </div>
              </div>
            </div>

            {/* ═══ WYGLĄD KARTY "ZAMÓW ONLINE" ═══ */}
            <div style={S.formCard}>
              <div style={S.formTitle}>🛒 Karta „Zamów online" — wygląd</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:16}}>
                Dostosuj wygląd przycisku sklepu online widocznego dla klienta na stronie głównej.
              </div>

              {/* Podgląd live */}
              <div style={{marginBottom:16,borderRadius:14,overflow:"hidden",boxShadow:"0 4px 18px rgba(0,0,0,0.15)"}}>
                <div style={{
                  background: settings.onlineCardBgImage
                    ? `url(${settings.onlineCardBgImage}) center/cover`
                    : `linear-gradient(135deg,${settings.onlineCardColor||"#7B4F2E"},${settings.onlineCardGradient2||"#A0522D"})`,
                  opacity: settings.onlineCardHeaderOpacity !== undefined ? settings.onlineCardHeaderOpacity : 1,
                  padding:"14px 18px",display:"flex",alignItems:"center",gap:12,
                }}>
                  <div style={{width:44,height:44,borderRadius:12,background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0,overflow:"hidden"}}>
                    {settings.onlineCardLogo
                      ? <img src={settings.onlineCardLogo} alt="" style={{width:44,height:44,objectFit:"cover",borderRadius:12}}/>
                      : settings.onlineCardIcon||"🌐"}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{color:"#fff",fontWeight:900,fontSize:16}}>{settings.onlineCardTitle||"Zamów online"}</div>
                    <div style={{color:"rgba(255,255,255,0.75)",fontSize:11,marginTop:2}}>{settings?.restaurantName||"Restauracja"} · dostawa lub odbiór</div>
                  </div>
                  <div style={{background:"rgba(255,255,255,0.2)",borderRadius:8,padding:"3px 9px",color:"#fff",fontSize:10,fontWeight:700}}>ONLINE</div>
                </div>
                <div style={{background:settings.onlineCardBtnBg||"#FFF8F0",padding:"12px 18px"}}>
                  <div style={{width:"100%",padding:"13px",borderRadius:12,background:settings.onlineCardBtnColor||"#A0522D",color:"#fff",fontWeight:900,fontSize:14,textAlign:"center"}}>
                    🛒 {settings.onlineCardBtnLabel||"Przejdź do sklepu online"}
                  </div>
                </div>
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div>
                  <label style={S.label}>🎨 Kolor nagłówka — lewy</label>
                  <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                    <input type="color" value={settings.onlineCardColor||"#7B4F2E"}
                      onChange={e=>setSettings(p=>({...p,onlineCardColor:e.target.value}))}
                      style={{width:44,height:36,borderRadius:7,border:"1px solid "+T.border,cursor:"pointer",padding:2}}/>
                    <input style={{...S.inp,flex:1,fontFamily:"monospace"}} value={settings.onlineCardColor||"#7B4F2E"}
                      onChange={e=>setSettings(p=>({...p,onlineCardColor:e.target.value}))} placeholder="#7B4F2E"/>
                  </div>
                  <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                    {["#7B4F2E","#1565C0","#6d28d9","#059669","#dc2626","#d97706","#111827","#be185d"].map(c=>(
                      <div key={c} onClick={()=>setSettings(p=>({...p,onlineCardColor:c}))}
                        style={{width:26,height:26,borderRadius:6,background:c,cursor:"pointer",boxShadow:"0 0 0 2px "+(settings.onlineCardColor===c?c:"transparent")}}/>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={S.label}>🎨 Kolor nagłówka — prawy</label>
                  <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6}}>
                    <input type="color" value={settings.onlineCardGradient2||"#A0522D"}
                      onChange={e=>setSettings(p=>({...p,onlineCardGradient2:e.target.value}))}
                      style={{width:44,height:36,borderRadius:7,border:"1px solid "+T.border,cursor:"pointer",padding:2}}/>
                    <input style={{...S.inp,flex:1,fontFamily:"monospace"}} value={settings.onlineCardGradient2||"#A0522D"}
                      onChange={e=>setSettings(p=>({...p,onlineCardGradient2:e.target.value}))} placeholder="#A0522D"/>
                  </div>
                  <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                    {["#A0522D","#0984e3","#7c3aed","#047857","#b91c1c","#b45309","#374151","#9d174d"].map(c=>(
                      <div key={c} onClick={()=>setSettings(p=>({...p,onlineCardGradient2:c}))}
                        style={{width:26,height:26,borderRadius:6,background:c,cursor:"pointer",boxShadow:"0 0 0 2px "+(settings.onlineCardGradient2===c?c:"transparent")}}/>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={S.label}>🖱 Kolor przycisku</label>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <input type="color" value={settings.onlineCardBtnColor||"#A0522D"}
                      onChange={e=>setSettings(p=>({...p,onlineCardBtnColor:e.target.value}))}
                      style={{width:44,height:36,borderRadius:7,border:"1px solid "+T.border,cursor:"pointer",padding:2}}/>
                    <input style={{...S.inp,flex:1,fontFamily:"monospace"}} value={settings.onlineCardBtnColor||"#A0522D"}
                      onChange={e=>setSettings(p=>({...p,onlineCardBtnColor:e.target.value}))} placeholder="#A0522D"/>
                  </div>
                </div>

                <div>
                  <label style={S.label}>🎨 Tło pod przyciskiem</label>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <input type="color" value={settings.onlineCardBtnBg||"#FFF8F0"}
                      onChange={e=>setSettings(p=>({...p,onlineCardBtnBg:e.target.value}))}
                      style={{width:44,height:36,borderRadius:7,border:"1px solid "+T.border,cursor:"pointer",padding:2}}/>
                    <input style={{...S.inp,flex:1,fontFamily:"monospace"}} value={settings.onlineCardBtnBg||"#FFF8F0"}
                      onChange={e=>setSettings(p=>({...p,onlineCardBtnBg:e.target.value}))} placeholder="#FFF8F0"/>
                  </div>
                </div>

                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>🖼 Logo / grafika w nagłówku</label>
                  <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",marginBottom:6}}>
                    {settings.onlineCardLogo&&<img src={settings.onlineCardLogo} alt="" style={{height:48,width:48,objectFit:"cover",borderRadius:10,border:"1px solid "+T.border}}/>}
                    <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 14px",background:"#f3f4f6",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700,color:"#374151",border:"1.5px dashed #d1d5db"}}>
                      📁 Wgraj logo
                      <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                        const f=e.target.files[0]; if(!f) return;
                        const r=new FileReader(); r.onload=ev=>setSettings(p=>({...p,onlineCardLogo:ev.target.result})); r.readAsDataURL(f);
                      }}/>
                    </label>
                    {settings.onlineCardLogo&&<button onClick={()=>setSettings(p=>({...p,onlineCardLogo:null}))}
                      style={{padding:"7px 12px",background:"#fee2e2",border:"none",borderRadius:8,color:"#dc2626",cursor:"pointer",fontWeight:700,fontSize:11}}>Usuń</button>}
                  </div>
                </div>

                <div>
                  <label style={S.label}>😀 Ikona emoji (gdy brak logo)</label>
                  <input style={{...S.inp,fontSize:20,textAlign:"center"}} value={settings.onlineCardIcon||"🌐"}
                    onChange={e=>setSettings(p=>({...p,onlineCardIcon:e.target.value}))} placeholder="🌐"/>
                </div>

                <div>
                  <label style={S.label}>✏ Tytuł karty</label>
                  <input style={S.inp} value={settings.onlineCardTitle||"Zamów online"}
                    onChange={e=>setSettings(p=>({...p,onlineCardTitle:e.target.value}))} placeholder="Zamów online"/>
                </div>

                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>✏ Tekst przycisku</label>
                  <input style={S.inp} value={settings.onlineCardBtnLabel||"Przejdź do sklepu online"}
                    onChange={e=>setSettings(p=>({...p,onlineCardBtnLabel:e.target.value}))} placeholder="Przejdź do sklepu online"/>
                </div>

                {/* Przezroczystość nagłówka */}
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>🔆 Przezroczystość nagłówka karty</label>
                  <div style={{display:"flex",alignItems:"center",gap:12,marginTop:4}}>
                    <span style={{fontSize:11,color:T.muted,minWidth:24}}>0%</span>
                    <input type="range" min="0" max="1" step="0.01"
                      value={settings.onlineCardHeaderOpacity !== undefined ? settings.onlineCardHeaderOpacity : 1}
                      onChange={e=>setSettings(p=>({...p,onlineCardHeaderOpacity:Number(e.target.value)}))}
                      style={{flex:1,accentColor:ac,cursor:"pointer"}}/>
                    <span style={{fontSize:11,color:T.muted,minWidth:24}}>100%</span>
                    <span style={{fontWeight:700,fontSize:12,color:ac,minWidth:36,textAlign:"center",
                      background:T.accentSoft,borderRadius:6,padding:"2px 6px"}}>
                      {Math.round((settings.onlineCardHeaderOpacity !== undefined ? settings.onlineCardHeaderOpacity : 1)*100)}%
                    </span>
                  </div>
                </div>

                {/* Zdjęcie tła nagłówka */}
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>🖼 Zdjęcie tła nagłówka (opcjonalne)</label>
                  <div style={{fontSize:11,color:T.muted,marginBottom:6}}>Jeśli ustawione, zastępuje gradient kolorów w nagłówku karty.</div>
                  <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                    {settings.onlineCardBgImage&&(
                      <img src={settings.onlineCardBgImage} alt="" style={{height:52,objectFit:"cover",borderRadius:8,border:"1px solid "+T.border,maxWidth:120}}/>
                    )}
                    <label style={{display:"inline-flex",alignItems:"center",gap:6,padding:"8px 14px",background:"#f3f4f6",borderRadius:9,cursor:"pointer",fontSize:12,fontWeight:700,color:"#374151",border:"1.5px dashed #d1d5db"}}>
                      📁 Wgraj zdjęcie tła
                      <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                        const f=e.target.files[0]; if(!f) return;
                        const r=new FileReader(); r.onload=ev=>setSettings(p=>({...p,onlineCardBgImage:ev.target.result})); r.readAsDataURL(f);
                      }}/>
                    </label>
                    {settings.onlineCardBgImage&&(
                      <button onClick={()=>setSettings(p=>({...p,onlineCardBgImage:null}))}
                        style={{padding:"7px 12px",background:"#fee2e2",border:"none",borderRadius:8,color:"#dc2626",cursor:"pointer",fontWeight:700,fontSize:11}}>Usuń</button>
                    )}
                  </div>
                </div>
              </div>

              <button onClick={()=>setSettings(p=>({...p,
                onlineCardColor:null,onlineCardGradient2:null,onlineCardBtnColor:null,
                onlineCardBtnBg:null,onlineCardLogo:null,onlineCardIcon:null,
                onlineCardTitle:null,onlineCardBtnLabel:null,onlineCardHeaderOpacity:undefined,
                onlineCardBgImage:null,
              }))}
                style={{marginTop:12,padding:"8px 16px",background:"#f3f4f6",color:T.muted,border:"1px solid "+T.border,borderRadius:9,fontWeight:700,cursor:"pointer",fontSize:12}}>
                🔄 Przywróć domyślne
              </button>
            </div>            <div style={S.formCard}>
              <div style={S.formTitle}>🍽 Menu z logo — pasek nawigacji klienta</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14}}>
                Wygląd górnego paska z logo i nazwą restauracji widocznego dla klientów.
              </div>

              {/* Toggle pokaż/ukryj */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                background: settings.navbarHidden ? "#fff8e1" : "#f0fdf4",
                border:"1.5px solid "+(settings.navbarHidden?"#f59e0b":"#22c55e"),
                borderRadius:10,padding:"10px 14px",marginBottom:16}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:settings.navbarHidden?"#92400e":"#15803d"}}>
                    {settings.navbarHidden ? "🙈 Pasek nawigacji — ukryty" : "👁 Pasek nawigacji — widoczny"}
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                    {settings.navbarHidden
                      ? "Klienci nie widzą górnego paska. Dostęp do admina: 5× kliknięcie logo w menu."
                      : "Klienci widzą górny pasek z przyciskiem Zamówienia."}
                  </div>
                </div>
                <button
                  onClick={()=>setSettings(p=>({...p,navbarHidden:!p.navbarHidden}))}
                  style={{
                    padding:"8px 16px",borderRadius:8,border:"none",fontWeight:700,cursor:"pointer",fontSize:12,
                    flexShrink:0,marginLeft:12,
                    background:settings.navbarHidden?"#22c55e":"#ef4444",
                    color:"#fff",
                    boxShadow:"0 2px 6px rgba(0,0,0,0.15)",
                  }}>
                  {settings.navbarHidden ? "👁 Pokaż" : "🙈 Ukryj"}
                </button>
              </div>

              {/* Podgląd na żywo */}
              <div style={{marginBottom:16,borderRadius:10,overflow:"hidden",boxShadow:"0 2px 10px rgba(0,0,0,0.15)"}}>
                {(()=>{
                  const hex = settings.navbarBgColor || "#2C1509";
                  const op  = settings.navbarOpacity !== undefined ? settings.navbarOpacity : 1.0;
                  let bg;
                  if (op >= 0.99) bg = hex;
                  else { const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16); bg=`rgba(${r},${g},${b},${op})`; }
                  // contrast: sprawdź czy kolor jasny czy ciemny
                  const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
                  const luminance = (0.299*r+0.587*g+0.114*b)/255;
                  const textCol = (luminance>0.5&&op>0.5) ? "rgba(0,0,0,0.85)" : "rgba(255,255,255,0.9)";
                  const borderCol = (luminance>0.5&&op>0.5) ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)";
                  return (
                    <div style={{
                      background:bg,
                      padding:"8px 14px",
                      display:"flex",alignItems:"center",gap:10,
                      backdropFilter: op<0.99?"blur(8px)":"none",
                    }}>
                      {settings.logo
                        ? <img src={settings.logo} alt="" style={{height:34,width:34,borderRadius:"50%",objectFit:"cover",border:`2px solid ${borderCol}`}}/>
                        : <span style={{fontSize:20}}>🥐</span>}
                      <span style={{fontWeight:700,fontSize:13,color:textCol,flex:1}}>{settings.restaurantName||"Restauracja"}</span>
                      <div style={{padding:"6px 12px",borderRadius:8,background:`rgba(${(luminance>0.5&&op>0.5)?"0,0,0":"255,255,255"},0.1)`,border:`1.5px solid ${borderCol}`,color:textCol,fontWeight:700,fontSize:12}}>
                        🛍 Zamówienia
                      </div>
                    </div>
                  );
                })()}
                <div style={{background:"#f3f4f6",padding:"6px 12px",fontSize:10,color:T.muted,textAlign:"center",borderTop:"1px solid "+T.border}}>
                  ↑ Podgląd paska nawigacji
                </div>
              </div>

              {/* Kolor tła */}
              <label style={S.label}>Kolor tła paska</label>
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
                {["#2C1509","#1a1a2e","#0f3460","#1e3a5f","#1b4332","#3d1a78","#000000","#ffffff"].map(c=>(
                  <div key={c} onClick={()=>setSettings(p=>({...p,navbarBgColor:c}))}
                    style={{width:30,height:30,borderRadius:8,background:c,cursor:"pointer",
                      border:(settings.navbarBgColor||"#2C1509")===c?"3px solid "+T.accent:"2px solid "+T.border,
                      boxShadow:(settings.navbarBgColor||"#2C1509")===c?`0 0 0 2px ${T.accent}`:"none",
                      flexShrink:0}}/>
                ))}
                <input type="color"
                  value={settings.navbarBgColor||"#2C1509"}
                  onChange={e=>setSettings(p=>({...p,navbarBgColor:e.target.value}))}
                  style={{width:30,height:30,borderRadius:8,border:"2px solid "+T.border,padding:0,cursor:"pointer",flexShrink:0}}/>
                <input style={{...S.inp,flex:1,minWidth:90,fontFamily:"monospace"}}
                  value={settings.navbarBgColor||"#2C1509"}
                  onChange={e=>setSettings(p=>({...p,navbarBgColor:e.target.value}))}
                  placeholder="#2C1509"/>
              </div>

              {/* Suwak przezroczystości */}
              <label style={S.label}>Przezroczystość paska</label>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
                <span style={{fontSize:11,color:T.muted,minWidth:36,textAlign:"right"}}>Przeźr.</span>
                <input type="range" min="0" max="1" step="0.01"
                  value={settings.navbarOpacity !== undefined ? settings.navbarOpacity : 1.0}
                  onChange={e=>setSettings(p=>({...p,navbarOpacity:Number(e.target.value)}))}
                  style={{flex:1,accentColor:T.accent,height:4,cursor:"pointer"}}/>
                <span style={{fontSize:11,color:T.muted,minWidth:36}}>Pełny</span>
                <span style={{
                  fontWeight:700,fontSize:13,color:T.accent,
                  minWidth:46,textAlign:"center",
                  background:T.accentSoft,borderRadius:7,padding:"3px 7px",
                }}>
                  {Math.round((settings.navbarOpacity !== undefined ? settings.navbarOpacity : 1.0)*100)}%
                </span>
              </div>
              <div style={{fontSize:10,color:T.muted,marginTop:4}}>
                0% = w pełni przezroczysty (szkło), 100% = pełny kolor. Przy przezroczystości aktywowany jest efekt rozmycia tła (blur).
              </div>

              {/* Przycisk przywrócenia domyślnych */}
              <button
                style={{...S.cancelBtn,marginTop:12,fontSize:12}}
                onClick={()=>setSettings(p=>({...p,navbarBgColor:"#2C1509",navbarOpacity:1.0}))}>
                ↩ Przywróć domyślny wygląd
              </button>
            </div>

            <div style={S.formCard}>
              <div style={S.formTitle}>🍽 Nagłówek menu klienta — pasek „Stolik / Nazwa"</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14}}>
                Wygląd paska z logo, numerem stolika i nazwą restauracji widocznego w menu klienta.
              </div>

              {/* Podgląd na żywo */}
              <div style={{marginBottom:16,borderRadius:10,overflow:"hidden",boxShadow:"0 2px 10px rgba(0,0,0,0.15)"}}>
                {(()=>{
                  const hex = settings.menuHeaderBgColor || "#16213e";
                  const op  = settings.menuHeaderOpacity !== undefined ? settings.menuHeaderOpacity : 1.0;
                  let bg;
                  if (op >= 0.99) bg = hex;
                  else { const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16); bg=`rgba(${r},${g},${b},${op})`; }
                  const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);
                  const luminance=(0.299*r+0.587*g+0.114*b)/255;
                  const textCol=(luminance>0.5&&op>0.5)?"rgba(0,0,0,0.85)":"#fff";
                  const goldCol=(luminance>0.5&&op>0.5)?"rgba(0,0,0,0.5)":"#E8A020";
                  return (
                    <div style={{background:bg,padding:"14px 16px",display:"flex",justifyContent:"space-between",alignItems:"center",backdropFilter:op<0.99?"blur(8px)":"none"}}>
                      <div style={{display:"flex",alignItems:"center",gap:11}}>
                        {settings.logo
                          ? <img src={settings.logo} alt="" style={{height:42,width:42,borderRadius:10,objectFit:"cover",border:"2px solid rgba(255,255,255,0.2)"}}/>
                          : <div style={{fontSize:34}}>🍽</div>}
                        <div>
                          <div style={{fontSize:9,color:goldCol,fontWeight:700,letterSpacing:1,textTransform:"uppercase"}}>Stolik #2</div>
                          <div style={{color:textCol,fontWeight:900,fontSize:19,fontFamily:"'Playfair Display',serif"}}>{settings.restaurantName||"Restauracja"}</div>
                        </div>
                      </div>
                      <div style={{background:"rgba(255,255,255,0.12)",borderRadius:20,padding:"6px 13px",fontSize:16,color:textCol}}>🛒</div>
                    </div>
                  );
                })()}
                <div style={{background:"#f3f4f6",padding:"6px 12px",fontSize:10,color:T.muted,textAlign:"center",borderTop:"1px solid "+T.border}}>
                  ↑ Podgląd nagłówka menu klienta
                </div>
              </div>

              {/* Kolor tła */}
              <label style={S.label}>Kolor tła nagłówka</label>
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
                {["#16213e","#0f3460","#2C1509","#1b4332","#3d1a78","#7c2d12","#000000","#ffffff"].map(c=>(
                  <div key={c} onClick={()=>setSettings(p=>({...p,menuHeaderBgColor:c}))}
                    style={{width:30,height:30,borderRadius:8,background:c,cursor:"pointer",
                      border:(settings.menuHeaderBgColor||"#16213e")===c?"3px solid "+T.accent:"2px solid "+T.border,
                      boxShadow:(settings.menuHeaderBgColor||"#16213e")===c?`0 0 0 2px ${T.accent}`:"none",
                      flexShrink:0}}/>
                ))}
                <input type="color"
                  value={settings.menuHeaderBgColor||"#16213e"}
                  onChange={e=>setSettings(p=>({...p,menuHeaderBgColor:e.target.value}))}
                  style={{width:30,height:30,borderRadius:8,border:"2px solid "+T.border,padding:0,cursor:"pointer",flexShrink:0}}/>
                <input style={{...S.inp,flex:1,minWidth:90,fontFamily:"monospace"}}
                  value={settings.menuHeaderBgColor||"#16213e"}
                  onChange={e=>setSettings(p=>({...p,menuHeaderBgColor:e.target.value}))}
                  placeholder="#16213e"/>
              </div>

              {/* Suwak przezroczystości */}
              <label style={S.label}>Przezroczystość nagłówka</label>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
                <span style={{fontSize:11,color:T.muted,minWidth:36,textAlign:"right"}}>Przeźr.</span>
                <input type="range" min="0" max="1" step="0.01"
                  value={settings.menuHeaderOpacity !== undefined ? settings.menuHeaderOpacity : 1.0}
                  onChange={e=>setSettings(p=>({...p,menuHeaderOpacity:Number(e.target.value)}))}
                  style={{flex:1,accentColor:T.accent,height:4,cursor:"pointer"}}/>
                <span style={{fontSize:11,color:T.muted,minWidth:36}}>Pełny</span>
                <span style={{fontWeight:700,fontSize:13,color:T.accent,minWidth:46,textAlign:"center",background:T.accentSoft,borderRadius:7,padding:"3px 7px"}}>
                  {Math.round((settings.menuHeaderOpacity !== undefined ? settings.menuHeaderOpacity : 1.0)*100)}%
                </span>
              </div>
              <div style={{fontSize:10,color:T.muted,marginTop:4}}>
                0% = w pełni przezroczysty (efekt szkła), 100% = pełny kolor.
              </div>

              <button style={{...S.cancelBtn,marginTop:12,fontSize:12}}
                onClick={()=>setSettings(p=>({...p,menuHeaderBgColor:"#16213e",menuHeaderOpacity:1.0}))}>
                ↩ Przywróć domyślny wygląd
              </button>
            </div>

            </>)}

            {/* ══ SEKCJA: WYGLĄD PANELU ══ */}
            {settSec==="orders" && (<>
            {/* ── PASEK ZAMÓW (CART BAR) ── */}
            <div style={S.formCard}>
              <div style={S.formTitle}>🛒 Pasek „Zamów" — kolor i bony zniżkowe</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14}}>
                Wygląd dolnego paska zamówienia widocznego dla klienta po dodaniu produktów do koszyka.
              </div>

              {/* Podgląd na żywo */}
              <div style={{marginBottom:16,borderRadius:10,overflow:"hidden",boxShadow:"0 2px 10px rgba(0,0,0,0.15)"}}>
                {(()=>{
                  const hex = settings.cartBarBgColor || "#16213e";
                  const op  = settings.cartBarOpacity !== undefined ? settings.cartBarOpacity : 1.0;
                  let bg;
                  if (op >= 0.99) bg = hex;
                  else { const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16); bg=`rgba(${r},${g},${b},${op})`; }
                  return (
                    <div style={{background:bg,padding:"10px 16px",backdropFilter:op<0.99?"blur(10px)":"none"}}>
                      <div style={{color:"rgba(255,255,255,0.5)",fontSize:11,background:"rgba(255,255,255,0.1)",borderRadius:8,padding:"6px 10px",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                        <span>📝</span><span style={{color:"rgba(255,255,255,0.4)",fontStyle:"italic"}}>Uwagi do zamówienia…</span>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                        <div>
                          <div style={{color:"#fff",fontWeight:700,fontSize:14}}>🛒 Koszyk · 3 szt. <span style={{fontSize:10,opacity:0.6,background:"rgba(255,255,255,0.15)",borderRadius:6,padding:"1px 6px"}}>✏ edytuj</span></div>
                          <div style={{color:"rgba(255,255,255,0.6)",fontSize:12,marginTop:2}}>⏱ ~15 min · <span style={{color:"rgba(255,255,255,0.85)",fontWeight:600}}>67.00 zł</span></div>
                        </div>
                        <div style={{background:settings.accentColor||ac,color:"#fff",borderRadius:12,padding:"11px 20px",fontWeight:800,fontSize:14,flexShrink:0}}>
                          Zamów
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div style={{background:"#f3f4f6",padding:"6px 12px",fontSize:10,color:T.muted,textAlign:"center",borderTop:"1px solid "+T.border}}>
                  ↑ Podgląd paska zamówienia
                </div>
              </div>

              {/* Kolor tła */}
              <label style={S.label}>Kolor tła paska zamów</label>
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:14,flexWrap:"wrap"}}>
                {["#16213e","#2C1509","#0f3460","#1b4332","#3d1a78","#7c2d12","#000000","#1f2937"].map(c=>(
                  <div key={c} onClick={()=>setSettings(p=>({...p,cartBarBgColor:c}))}
                    style={{width:30,height:30,borderRadius:8,background:c,cursor:"pointer",
                      border:(settings.cartBarBgColor||"#16213e")===c?"3px solid "+T.accent:"2px solid "+T.border,
                      boxShadow:(settings.cartBarBgColor||"#16213e")===c?`0 0 0 2px ${T.accent}`:"none",
                      flexShrink:0}}/>
                ))}
                <input type="color"
                  value={settings.cartBarBgColor||"#16213e"}
                  onChange={e=>setSettings(p=>({...p,cartBarBgColor:e.target.value}))}
                  style={{width:30,height:30,borderRadius:8,border:"2px solid "+T.border,padding:0,cursor:"pointer",flexShrink:0}}/>
                <input style={{...S.inp,flex:1,minWidth:90,fontFamily:"monospace"}}
                  value={settings.cartBarBgColor||"#16213e"}
                  onChange={e=>setSettings(p=>({...p,cartBarBgColor:e.target.value}))}
                  placeholder="#16213e"/>
              </div>

              {/* Suwak przezroczystości */}
              <label style={S.label}>Przezroczystość paska zamów</label>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:6}}>
                <span style={{fontSize:11,color:T.muted,minWidth:36,textAlign:"right"}}>Przeźr.</span>
                <input type="range" min="0" max="1" step="0.01"
                  value={settings.cartBarOpacity !== undefined ? settings.cartBarOpacity : 1.0}
                  onChange={e=>setSettings(p=>({...p,cartBarOpacity:Number(e.target.value)}))}
                  style={{flex:1,accentColor:T.accent,height:4,cursor:"pointer"}}/>
                <span style={{fontSize:11,color:T.muted,minWidth:36}}>Pełny</span>
                <span style={{fontWeight:700,fontSize:13,color:T.accent,minWidth:46,textAlign:"center",background:T.accentSoft,borderRadius:7,padding:"3px 7px"}}>
                  {Math.round((settings.cartBarOpacity !== undefined ? settings.cartBarOpacity : 1.0)*100)}%
                </span>
              </div>
              <div style={{fontSize:10,color:T.muted,marginTop:4,marginBottom:14}}>
                0% = w pełni przezroczysty (efekt szkła), 100% = pełny kolor.
              </div>

              {/* Bony zniżkowe toggle */}
              {adminPerm("adminVouchers") && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",
                borderRadius:12,background:T.bg,border:"1.5px solid "+T.border,marginBottom:10}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:T.text}}>🎟 Bony zniżkowe dla klienta</div>
                  <div style={{fontSize:11,color:T.muted,marginTop:2}}>
                    {settings.vouchersEnabled !== false
                      ? "Klienci widzą pole \"Użyj kodu kuponu\" przy kasie."
                      : "Pole kodu kuponu jest ukryte przed klientem."}
                  </div>
                </div>
                <button
                  onClick={()=>setSettings(p=>({...p,vouchersEnabled:p.vouchersEnabled===false?true:false}))}
                  style={{
                    padding:"8px 16px",borderRadius:8,border:"none",fontWeight:700,cursor:"pointer",fontSize:12,
                    flexShrink:0,marginLeft:12,
                    background:settings.vouchersEnabled!==false?"#22c55e":"#ef4444",
                    color:"#fff",
                    boxShadow:"0 2px 6px rgba(0,0,0,0.15)",
                  }}>
                  {settings.vouchersEnabled !== false ? "✅ Włączone" : "❌ Wyłączone"}
                </button>
              </div>
              )}

              <button style={{...S.cancelBtn,marginTop:4,fontSize:12}}
                onClick={()=>setSettings(p=>({...p,cartBarBgColor:"#16213e",cartBarOpacity:1.0}))}>
                ↩ Przywróć domyślny wygląd paska
              </button>
            </div>

            </>)}

            {/* ══ SEKCJA: ZAAWANSOWANE / INTEGRACJE ══ */}
            {settSec==="integrations" && (<>
            <div style={S.formCard}>
              <div style={S.formTitle}>🔄 Synchronizacja danych (Firebase)</div>
              <FirebaseSyncPanel fbConfig={fbConfig} setFbConfig={setFbConfig} settings={settings} setSettings={setSettings}/>
            </div>

            <div style={S.formCard}>
              <div style={S.formTitle}>🎨 Kolor akcentu</div>
              <div style={{display:"flex",gap:9,alignItems:"center",marginTop:10,flexWrap:"wrap"}}>
                {["#e94560","#ff6b35","#00b894","#0984e3","#6c5ce7","#fd79a8","#2d3436","#e17055"].map(c=>(
                  <div key={c} style={{width:34,height:34,borderRadius:"50%",background:c,cursor:"pointer",
                    border:settings.accentColor===c?"3px solid #fff":"3px solid transparent",
                    boxShadow:settings.accentColor===c?`0 0 0 2px ${c}`:"0 1px 4px rgba(0,0,0,0.15)"}}
                    onClick={()=>setSettings(p=>({...p,accentColor:c}))}/>
                ))}
                <input type="color" value={settings.accentColor} onChange={e=>setSettings(p=>({...p,accentColor:e.target.value}))}
                  style={{width:34,height:34,borderRadius:"50%",border:"none",padding:0,cursor:"pointer"}}/>
              </div>
              <div style={{marginTop:11,padding:9,background:ac,borderRadius:9,color:"#fff",textAlign:"center",fontWeight:700,fontSize:12}}>
                Podgląd koloru akcentu
              </div>
            </div>

            {/* ── STREFY DOWOZU ── */}
            {settings.deliveryEnabled!==false&&(
            <div style={S.formCard}>
              <div style={S.formTitle}>🗺 Strefy cenowe dowozu</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:12}}>
                Ustaw ceny dowozu zależne od odległości. Klient wybierze strefę ręcznie przy składaniu zamówienia.
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 80px 80px 80px 40px",gap:6,marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:700,color:T.muted}}>Nazwa</div>
                <div style={{fontSize:11,fontWeight:700,color:T.muted,textAlign:"center"}}>Od (km)</div>
                <div style={{fontSize:11,fontWeight:700,color:T.muted,textAlign:"center"}}>Do (km)</div>
                <div style={{fontSize:11,fontWeight:700,color:T.muted,textAlign:"center"}}>Cena (zł)</div>
                <div/>
              </div>
              {(settings.deliveryZones||[]).map((zone,idx)=>(
                <div key={zone.id} style={{display:"grid",gridTemplateColumns:"1fr 80px 80px 80px 40px",gap:6,marginBottom:6,alignItems:"center"}}>
                  <input style={{...S.inp,padding:"6px 10px",fontSize:13}} value={zone.name}
                    onChange={e=>setSettings(p=>({...p,deliveryZones:p.deliveryZones.map((z,i)=>i===idx?{...z,name:e.target.value}:z)}))}/>
                  <input style={{...S.inp,padding:"6px 8px",fontSize:13,textAlign:"center"}} type="number" min="0" value={zone.kmFrom}
                    onChange={e=>setSettings(p=>({...p,deliveryZones:p.deliveryZones.map((z,i)=>i===idx?{...z,kmFrom:Number(e.target.value)}:z)}))}/>
                  <input style={{...S.inp,padding:"6px 8px",fontSize:13,textAlign:"center"}} type="number" min="0" value={zone.kmTo}
                    onChange={e=>setSettings(p=>({...p,deliveryZones:p.deliveryZones.map((z,i)=>i===idx?{...z,kmTo:Number(e.target.value)}:z)}))}/>
                  <input style={{...S.inp,padding:"6px 8px",fontSize:13,textAlign:"center"}} type="number" min="0" step="0.5" value={zone.price}
                    onChange={e=>setSettings(p=>({...p,deliveryZones:p.deliveryZones.map((z,i)=>i===idx?{...z,price:Number(e.target.value)}:z)}))}/>
                  <button onClick={()=>setSettings(p=>({...p,deliveryZones:p.deliveryZones.filter((_,i)=>i!==idx)}))}
                    style={{background:"#ffebee",color:"#e94560",border:"none",borderRadius:8,width:34,height:34,cursor:"pointer",fontWeight:700,fontSize:14}}>🗑</button>
                </div>
              ))}
              <button
                onClick={()=>setSettings(p=>({...p,deliveryZones:[...(p.deliveryZones||[]),{id:Date.now(),name:`Strefa ${(p.deliveryZones||[]).length+1}`,kmFrom:0,kmTo:5,price:10}]}))}
                style={{...S.darkBtn,marginTop:6,fontSize:12,padding:"7px 14px"}}>
                + Dodaj strefę
              </button>
            </div>
            )}
            {adminPerm("adminQrNfc") && (
            <div style={S.formCard}>
              <div style={S.formTitle}>📷 QR i NFC — ustawienia globalne</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.5}}>
                Włącz lub wyłącz możliwość skanowania QR i NFC dla całej restauracji.
                Gdy wyłączone — klienci nie mogą automatycznie przypisać stolika przez skan.
              </div>
              {[
                { key:"qrEnabled",  icon:"📷", label:"Kod QR",   desc:"Klienci mogą skanować kody QR aparatem telefonu" },
                { key:"nfcEnabled", icon:"📡", label:"Tag NFC",  desc:"Klienci mogą przyłożyć telefon do tagu NFC" },
              ].map(item => {
                const enabled = settings[item.key] !== false;
                return (
                  <div key={item.key} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:12,
                    background:enabled?"#f0fdf4":T.bg, border:"1.5px solid "+(enabled?"#86efac":T.border),
                    marginBottom:10, transition:"all .2s"}}>
                    <div style={{width:42,height:42,borderRadius:10,background:enabled?"#dcfce7":"#f0f2f5",
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>
                      {item.icon}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:13,color:T.primary}}>{item.label}</div>
                      <div style={{fontSize:11,color:T.muted,marginTop:1}}>{item.desc}</div>
                    </div>
                    <div onClick={()=>setSettings(p=>({...p,[item.key]:!p[item.key]}))}
                      style={{width:46,height:26,borderRadius:13,background:enabled?"#22c55e":"#d1d5db",
                        cursor:"pointer",position:"relative",transition:"all .2s",flexShrink:0}}>
                      <div style={{position:"absolute",top:3,left:enabled?21:3,width:20,height:20,
                        borderRadius:"50%",background:"#fff",transition:"all .2s",
                        boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            {/* ── KARTY LOGOWANIA NFC ── */}
            {adminPerm("adminNfcCards") && (
            <div style={S.formCard}>
              <div style={S.formTitle}>💳 Karty logowania NFC</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.5}}>
                Przypisuj i programuj karty NFC dla pracowników. Po przyłożeniu zaprogramowanej karty
                do telefonu aplikacja otwiera się i loguje pracownika automatycznie — bez wpisywania hasła.
              </div>
              <NfcCardsPanel users={users} setUsers={setUsers} currentUser={user} />
            </div>
            )}

            {/* ── ZAMÓWIENIA NA WYNOS / DOSTAWA ── */}
            {adminPerm("adminTakeaway") && (
            <div style={S.formCard}>
              <div style={S.formTitle}>🥡 Zamówienia na wynos i dostawa</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.5}}>
                Włącz możliwość składania zamówień na wynos lub z dostawą przez klientów bezpośrednio ze strony głównej.
              </div>
              {[
                { key:"takeawayEnabled", icon:"🥡", label:"Odbiór osobisty (wynos)", desc:"Klienci mogą zamówić i odebrać osobiście" },
                { key:"deliveryEnabled", icon:"🚗", label:"Dostawa do adresu",       desc:"Klienci mogą zamówić z dostawą — podają adres" },
              ].map(item => {
                const enabled = settings[item.key] !== false;
                return (
                  <div key={item.key} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:12,
                    background:enabled?"#f0fdf4":T.bg, border:"1.5px solid "+(enabled?"#86efac":T.border),
                    marginBottom:10, transition:"all .2s"}}>
                    <div style={{width:42,height:42,borderRadius:10,background:enabled?"#dcfce7":"#f0f2f5",
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>
                      {item.icon}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:13,color:T.primary}}>{item.label}</div>
                      <div style={{fontSize:11,color:T.muted,marginTop:1}}>{item.desc}</div>
                    </div>
                    <div onClick={()=>setSettings(p=>({...p,[item.key]:!p[item.key]}))}
                      style={{width:46,height:26,borderRadius:13,background:enabled?"#22c55e":"#d1d5db",
                        cursor:"pointer",position:"relative",transition:"all .2s",flexShrink:0}}>
                      <div style={{position:"absolute",top:3,left:enabled?21:3,width:20,height:20,
                        borderRadius:"50%",background:"#fff",transition:"all .2s",
                        boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
                    </div>
                  </div>
                );
              })}
            </div>
            )} {/* end adminTakeaway */}

            </>)}

            {/* ══ SEKCJA: PŁATNOŚCI ══ */}
            {settSec==="payments" && (<>
            {/* ── PAYMENT METHODS ── */}
            <div style={S.formCard}>
              <div style={S.formTitle}>💳 Metody płatności</div>
              <div style={{fontSize:12,color:T.textSoft,marginBottom:12}}>
                Włącz lub wyłącz dostępne metody płatności. Tylko zaznaczone będą widoczne dla klientów przy składaniu zamówienia.
              </div>
              {Object.entries(settings.paymentMethods||{}).map(([key,pm])=>(
                <div key={key} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:11,
                  background:pm.enabled?"#f8fff8":"#f8f8f8",border:"1.5px solid "+(pm.enabled?"#27ae60":"#eee"),marginBottom:8,
                  transition:"all .2s"}}>
                  <div style={{width:40,height:40,borderRadius:10,background:pm.color+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>{pm.icon}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13,color:T.text}}>{pm.label}</div>
                    <div style={{fontSize:11,color:T.textSoft,marginTop:1}}>{pm.desc}</div>
                  </div>
                  <div style={{position:"relative",flexShrink:0}}>
                    <div onClick={()=>setSettings(p=>({...p,paymentMethods:{...p.paymentMethods,[key]:{...p.paymentMethods[key],enabled:!p.paymentMethods[key].enabled}}}))}
                      style={{width:44,height:24,borderRadius:12,background:pm.enabled?"#27ae60":"#ccc",cursor:"pointer",transition:"all .2s",position:"relative"}}>
                      <div style={{position:"absolute",top:2,left:pm.enabled?20:2,width:20,height:20,borderRadius:"50%",background:T.surface,transition:"all .2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── PAYMENT API KEYS ── */}
            <div style={S.formCard}>
              <div style={S.formTitle}>🔑 Klucze API płatności</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:12,lineHeight:1.5}}>
                Wpisz klucze API dla aktywnych metod płatności. Dane są szyfrowane i przechowywane lokalnie.
                Tryb sandbox służy do testowania bez prawdziwych transakcji.
              </div>
              {[
                { key:"blik",   label:"BLIK / Przelewy24", icon:"📱", fields:[
                  {k:"merchantId", label:"Merchant ID",  type:"text",    ph:"Np. 123456"},
                  {k:"apiKey",     label:"Klucz API",     type:"password",ph:"p24_..."},
                ]},
                { key:"card",   label:"Karta / Stripe",  icon:"💳", fields:[
                  {k:"merchantId", label:"Stripe Account ID", type:"text",    ph:"acct_..."},
                  {k:"apiKey",     label:"Secret Key",         type:"password",ph:"sk_live_..."},
                ]},
                { key:"paypal", label:"PayPal",           icon:"🅿", fields:[
                  {k:"clientId",   label:"Client ID",    type:"text",    ph:"AXxx..."},
                  {k:"secret",     label:"Secret",       type:"password",ph:"EXxx..."},
                ]},
              ].map(pm=>{
                const cfg = (settings.paymentApiKeys||{})[pm.key]||{};
                const enabled = (settings.paymentMethods||{})[pm.key] && (settings.paymentMethods||{})[pm.key].enabled;
                return (
                  <div key={pm.key} style={{borderRadius:12,border:"1.5px solid "+(enabled?T.border:"#eee"),padding:12,marginBottom:10,opacity:enabled?1:0.5}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <div style={{fontWeight:700,fontSize:13,color:T.primary}}>{pm.icon} {pm.label}</div>
                      {!enabled&&<span style={{fontSize:10,color:T.muted,background:T.bg,padding:"2px 8px",borderRadius:20}}>Wyłączona</span>}
                      {enabled&&(
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:11,color:T.muted}}>Sandbox</span>
                          <div onClick={()=>setSettings(p=>({...p,paymentApiKeys:{...p.paymentApiKeys,[pm.key]:{...cfg,sandbox:!cfg.sandbox}}}))}
                            style={{width:36,height:20,borderRadius:10,background:cfg.sandbox?"#E8A020":T.green,cursor:"pointer",position:"relative",transition:"all .2s"}}>
                            <div style={{position:"absolute",top:2,left:cfg.sandbox?2:16,width:16,height:16,borderRadius:"50%",background:T.surface,transition:"all .2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                          </div>
                          <span style={{fontSize:10,color:cfg.sandbox?"#E8A020":T.green,fontWeight:700}}>{cfg.sandbox?"TEST":"LIVE"}</span>
                        </div>
                      )}
                    </div>
                    {pm.fields.map(f=>(
                      <div key={f.k} style={{marginBottom:7}}>
                        <label style={S.label}>{f.label}</label>
                        <input
                          style={{...S.inp,fontFamily:f.type==="password"?"monospace":"inherit",letterSpacing:f.type==="password"?1:0,borderColor:cfg[f.k]?T.accent:T.inputBorder}}
                          type={f.type} placeholder={f.ph} value={cfg[f.k]||""}
                          onChange={e=>setSettings(p=>({...p,paymentApiKeys:{...p.paymentApiKeys,[pm.key]:{...cfg,[f.k]:e.target.value}}}))}
                        />
                      </div>
                    ))}
                    {((enabled&&cfg.apiKey)||(enabled&&cfg.clientId))?(
                      <div style={{background:T.bg,borderRadius:8,padding:"6px 10px",fontSize:11,color:T.muted,marginTop:4}}>
                        ✅ Klucz skonfigurowany · {cfg.sandbox?"Tryb testowy (sandbox)":"Tryb produkcyjny"}
                      </div>
                    ):enabled?(
                      <div style={{background:"#FFF8E1",borderRadius:8,padding:"6px 10px",fontSize:11,color:"#856404",marginTop:4}}>
                        ⚠ Uzupełnij klucz API, aby aktywować płatność
                      </div>
                    ):null}
                  </div>
                );
              })}
              <div style={{background:T.bg,borderRadius:10,padding:"10px 12px",fontSize:11,color:T.muted,marginTop:4,lineHeight:1.5,border:"1px solid "+T.border}}>
                🔒 Klucze API są przechowywane wyłącznie lokalnie w przeglądarce (localStorage). Nigdy nie są wysyłane na zewnętrzne serwery.
              </div>
            </div>

            {/* ── PARAGON / RECEIPT ── */}
            {adminPerm("adminReceipt") && (
            <div style={S.formCard}>
              <div style={S.formTitle}>🧾 Wygląd paragonu PDF</div>
              <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.5}}>
                Dostosuj nagłówek i stopkę paragonu pobieranego jako PDF przez klientów.
              </div>

              {/* Logo na paragonie */}
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                borderRadius:10,background:T.bg,border:"1px solid "+T.border,marginBottom:12}}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:12,color:T.primary,marginBottom:2}}>Logo na paragonie</div>
                  <div style={{fontSize:11,color:T.muted}}>Wyświetl logo restauracji u góry paragonu</div>
                </div>
                <div onClick={()=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),showLogo:!(p.receipt||{showLogo:true}).showLogo}}))}
                  style={{width:44,height:24,borderRadius:12,flexShrink:0,cursor:"pointer",transition:"all .2s",position:"relative",
                    background:(settings.receipt||{showLogo:true}).showLogo!==false?"#22c55e":"#d1d5db"}}>
                  <div style={{position:"absolute",top:2,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"all .2s",
                    left:(settings.receipt||{showLogo:true}).showLogo!==false?21:2,boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}}/>
                </div>
              </div>

              {/* Nazwa firmy */}
              <div style={{marginBottom:10}}>
                <label style={S.label}>Nazwa firmy (nagłówek paragonu)</label>
                <input style={S.inp} placeholder={settings.restaurantName||"Nazwa firmy"}
                  value={(settings.receipt||{}).companyLine||""}
                  onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),companyLine:e.target.value}}))}/>
              </div>

              {/* Adres */}
              <div style={{marginBottom:10}}>
                <label style={S.label}>Adres (opcjonalnie)</label>
                <input style={S.inp} placeholder="ul. Przykładowa 1, 00-000 Warszawa"
                  value={(settings.receipt||{}).addressLine||""}
                  onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),addressLine:e.target.value}}))}/>
              </div>

              {/* NIP */}
              <div style={{marginBottom:10}}>
                <label style={S.label}>NIP (opcjonalnie)</label>
                <input style={S.inp} placeholder="NIP: 000-000-00-00"
                  value={(settings.receipt||{}).nipLine||""}
                  onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),nipLine:e.target.value}}))}/>
              </div>

              {/* Napis pod nazwą firmy */}
              <div style={{marginBottom:10}}>
                <label style={S.label}>Napis pod nazwą firmy</label>
                <input style={S.inp} placeholder="Potwierdzenie zamówienia"
                  value={(settings.receipt||{}).confirmLabel||""}
                  onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),confirmLabel:e.target.value}}))}/>
              </div>

              {/* Stopka główna */}
              <div style={{marginBottom:10}}>
                <label style={S.label}>Stopka paragonu (tekst podziękowań)</label>
                <textarea style={{...S.inp,resize:"vertical",minHeight:64,fontFamily:"inherit"}}
                  placeholder={"Dziękujemy za wizytę!\nSmacznego!"}
                  value={(settings.receipt||{}).footerLine||""}
                  onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),footerLine:e.target.value}}))}/>
                <div style={{fontSize:10,color:T.muted,marginTop:3}}>Nowa linia = Enter. Tekst pojawi się na dole paragonu.</div>
              </div>

              {/* Dodatkowa notatka */}
              <div style={{marginBottom:6}}>
                <label style={S.label}>Dodatkowa notatka stopki (drobny druk)</label>
                <input style={S.inp} placeholder="np. Reklamacje do 7 dni · obsługa@restauracja.pl"
                  value={(settings.receipt||{}).footerNote||""}
                  onChange={e=>setSettings(p=>({...p,receipt:{...(p.receipt||{}),footerNote:e.target.value}}))}/>
              </div>

              {/* Podgląd miniparagon */}
              <div style={{marginTop:14,border:"1.5px dashed "+T.border,borderRadius:12,padding:14,background:"#fff",
                fontFamily:"'Courier New', monospace",fontSize:11,color:"#333",maxWidth:260,margin:"14px auto 0"}}>
                <div style={{textAlign:"center",marginBottom:6}}>
                  {(settings.receipt||{showLogo:true}).showLogo!==false && settings.logo && (
                    <img src={settings.logo} alt="" style={{width:40,height:40,objectFit:"cover",borderRadius:6,marginBottom:4,display:"block",margin:"0 auto 4px"}}/>
                  )}
                  <strong style={{fontSize:13}}>{(settings.receipt||{}).companyLine||settings.restaurantName||"Nazwa firmy"}</strong>
                  {(settings.receipt||{}).addressLine && <div style={{fontSize:10,color:"#777",marginTop:2}}>{(settings.receipt||{}).addressLine}</div>}
                  {(settings.receipt||{}).nipLine     && <div style={{fontSize:10,color:"#777"}}>{(settings.receipt||{}).nipLine}</div>}
                  <div style={{fontSize:10,color:"#999",marginTop:2}}>{(settings.receipt||{}).confirmLabel||"Potwierdzenie zamówienia"}</div>
                </div>
                <div style={{borderTop:"1px dashed #ccc",margin:"6px 0"}}/>
                <div style={{fontSize:10,color:"#777",textAlign:"center",whiteSpace:"pre-line",lineHeight:1.5}}>
                  {((settings.receipt||{}).footerLine||"Dziękujemy za wizytę!\nSmacznego!")}
                </div>
                {(settings.receipt||{}).footerNote && (
                  <div style={{fontSize:9,color:"#aaa",textAlign:"center",marginTop:4,fontStyle:"italic"}}>
                    {(settings.receipt||{}).footerNote}
                  </div>
                )}
              </div>
            </div>
            )} {/* end adminReceipt */}

            {/* ── BANNERS ── */}
            {["bannersTop","bannersBottom"].map(key=>(
              <div key={key} style={S.formCard}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div style={S.formTitle}>{key==="bannersTop"?"🖼 Banery górne":"📢 Banery dolne"}</div>
                  <button style={{...S.darkBtn,fontSize:11,padding:"5px 11px"}} onClick={()=>{
                    const newB={id:genId(),image:null,label:"Nowy baner",sub:"Podtytuł",link:""};
                    setSettings(p=>({...p,[key]:[...p[key],newB]}));
                  }}>+ Dodaj</button>
                </div>
                <div style={{fontSize:11,color:T.textSoft,marginBottom:10}}>
                  {key==="bannersTop"?"Duży baner pod nagłówkiem, wysokość 130px — idealny na główne promocje.":"Mały baner na dole listy produktów, wysokość 80px — dobry na info i happy hours."}
                </div>
                {(settings[key]||[]).map((b,bi)=>(
                  <div key={b.id} style={{border:"1.5px solid "+T.border,borderRadius:11,padding:11,marginBottom:9}}>
                    {/* Mini preview */}
                    <div style={{borderRadius:8,overflow:"hidden",height: key==="bannersTop"?70:50,
                      background:b.image?"transparent":"linear-gradient(135deg,"+ac+"cc,#1a1a2e)",
                      marginBottom:9,position:"relative",cursor:"pointer"}}
                      onClick={()=>setPickerCtx({type:"banner",key,id:b.id})}>
                      {b.image&&<img src={b.image} alt="" style={{width:"100%",height:"100%",objectFit:"cover",position:"absolute",inset:0}}/>}
                      <div style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.35)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <div style={{color:"#fff",fontWeight:700,fontSize:12}}>
                          {b.image?"🖼 Kliknij aby zmienić zdjęcie":"📷 Kliknij aby dodać zdjęcie"}
                        </div>
                      </div>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7}}>
                      <input style={S.inp} placeholder="Tytuł baneru" value={b.label}
                        onChange={e=>setSettings(p=>({...p,[key]:p[key].map((x,i)=>i===bi?{...x,label:e.target.value}:x)}))}/>
                      <input style={S.inp} placeholder="Podtytuł" value={b.sub}
                        onChange={e=>setSettings(p=>({...p,[key]:p[key].map((x,i)=>i===bi?{...x,sub:e.target.value}:x)}))}/>
                      <input style={{...S.inp,gridColumn:"span 2"}} placeholder="Link do kategorii (np. burgery, pizza, promo)" value={b.link}
                        onChange={e=>setSettings(p=>({...p,[key]:p[key].map((x,i)=>i===bi?{...x,link:e.target.value}:x)}))}/>
                    </div>
                    <div style={{display:"flex",gap:6,marginTop:8}}>
                      <button style={{...S.smBtn,background:"#e8f5e9",color:"#27ae60"}}
                        onClick={()=>setPickerCtx({type:"banner",key,id:b.id})}>🖼 Zdjęcie</button>
                      {b.image&&<button style={{...S.smBtn,background:"#fff0f0",color:ac}}
                        onClick={()=>setSettings(p=>({...p,[key]:p[key].map((x,i)=>i===bi?{...x,image:null}:x)}))}>🗑 Usuń foto</button>}
                      <button style={{...S.smBtn,background:"#ffebee",color:ac,marginLeft:"auto"}}
                        onClick={()=>setSettings(p=>({...p,[key]:p[key].filter((_,i)=>i!==bi)}))}>🗑 Usuń baner</button>
                    </div>
                  </div>
                ))}
                {(settings[key]||[]).length===0&&(
                  <div style={{textAlign:"center",padding:"20px 0",color:T.border,fontSize:13}}>Brak banerów — kliknij + Dodaj</div>
                )}
              </div>
            ))}
            </>)}

            {/* ══ TŁUMACZENIA w sekcji Integracje ══ */}
            {settSec==="integrations" && (
            <><TranslationsAdminPanel settings={settings} setSettings={setSettings} T={T} S={S} ac={ac}/></>
            )}

          </div>
          );
        })()}

        {/* ── ZAMÓWIENIA ONLINE ── */}
        {tab==="online" && (isSuperAdmin || (isTabVisible("online", user.role) && rolePerm("adminOnlineOrders"))) && (
          <OnlineOrdersAdminPanel
            onlineOrders={onlineOrders||[]} setOnlineOrders={setOnlineOrders}
            onlineMenu={onlineMenu||[]} setOnlineMenu={setOnlineMenu}
            onlineCats={onlineCats||[]} setOnlineCats={setOnlineCats}
            settings={settings} setSettings={setSettings} ac={ac}
          />
        )}

        {/* ── TAKEAWAY ── */}
        {tab==="takeaway" && (isSuperAdmin || (isTabVisible("takeaway", user.role) && adminPerm("adminTakeaway"))) && (
          <TakeawayPanel
            orders={orders} setOrders={setOrders}
            menu={menu} cats={cats} settings={settings} ac={ac}
            isSeller={isSeller} isAdmin={isAdmin}
          />
        )}

        {/* ── BACKUP ── */}
        {/* ── CLIENTS ── */}
        {tab==="clients" && (isSuperAdmin || (isTabVisible("clients", user.role) && rolePerm("adminClients"))) && (
          <ClientsPanel clients={clients} setClients={setClients} orders={orders} setOrders={setOrders}
            menu={menu} cats={cats} tables={tables} settings={settings} ac={ac} isAdmin={isAdmin}
            onlineMenu={onlineMenu} onlineCats={onlineCats} setLogs={setLogs} currentUser={user}/>
        )}

        {tab==="extras" && (isSuperAdmin || (isTabVisible("extras", user.role) && rolePerm("adminExtras"))) && (
          <ExtrasPanel
            orders={orders} setOrders={setOrders}
            menu={menu} setMenu={setMenu}
            cats={cats} setCats={setCats}
            users={users} setUsers={setUsers}
            clients={clients} setClients={setClients}
            tables={tables} setTables={setTables}
            campaigns={campaigns} setCampaigns={setCampaigns}
            vouchers={vouchers} setVouchers={setVouchers}
            settings={settings} setSettings={setSettings}
            usedCodes={usedCodes} setUsedCodes={setUsedCodes}
            isSuperAdmin={isSuperAdmin} user={user} ac={ac}
            rolePerm={rolePerm}
          />
        )}





        {tab==="ksef" && (isSuperAdmin || (isTabVisible("ksef", user.role) && rolePerm("adminKsef"))) && (
          <KSeFPanel orders={orders} setOrders={setOrders} settings={settings} setSettings={setSettings} clients={clients} menu={menu} user={user}/>
        )}



        </div>  {/* end tab content padding */}

        {/* BOTTOM NAV */}
        <div ref={bottomNavRef}
          onMouseDown={e=>{
            bottomNavDragging.current=true;
            bottomNavStartX.current=e.pageX-bottomNavRef.current.offsetLeft;
            bottomNavScrollLeft.current=bottomNavRef.current.scrollLeft;
            bottomNavRef.current.style.cursor="grabbing";
          }}
          onMouseMove={e=>{
            if(!bottomNavDragging.current) return;
            e.preventDefault();
            const x=e.pageX-bottomNavRef.current.offsetLeft;
            bottomNavRef.current.scrollLeft=bottomNavScrollLeft.current-(x-bottomNavStartX.current);
          }}
          onMouseUp={()=>{ bottomNavDragging.current=false; if(bottomNavRef.current) bottomNavRef.current.style.cursor="grab"; }}
          onMouseLeave={()=>{ bottomNavDragging.current=false; if(bottomNavRef.current) bottomNavRef.current.style.cursor="grab"; }}
          onWheel={e=>{ e.preventDefault(); bottomNavRef.current.scrollLeft+=e.deltaY+e.deltaX; }}
          className="bottom-nav"
          style={{
            background:TA.primary, flexShrink:0,
            borderTop:"1px solid rgba(255,255,255,0.07)",
            display:"flex", overflowX:"auto", overflowY:"hidden",
            padding:"0 2px", cursor:"grab",
            WebkitOverflowScrolling:"touch",
            scrollbarWidth:"none", msOverflowStyle:"none",
            userSelect:"none"
          }}>
          {[
            {key:"orders",    icon:"📋", label:"Zamówienia"},
            {key:"tables",    icon:"🪑", label:"Stoliki"},
            {key:"takeaway",  icon:"🥡", label:"Na wynos"},
            {key:"clients",   icon:"👥", label:"Klienci"},
            {key:"online",    icon:"🌐", label:"Zamówienia online"},
            {key:"specials",  icon:"⭐", label:"Polecamy"},
            {key:"menu",      icon:"🍽", label:"Menu"},
            {key:"cats",      icon:"📂", label:"Kategorie"},
            {key:"promo",     icon:"🔥", label:"Promocje"},
            {key:"campaigns", icon:"📣", label:"Kampanie"},
            {key:"vouchers",  icon:"🎟", label:"Bony"},
            {key:"sales",     icon:"📊", label:"Sprzedaz"},
            {key:"messages",  icon:"📧", label:"Wiadomości"},
            {key:"settings",  icon:"⚙", label:"Ustawienia"},
            {key:"extras",    icon:"🔩", label:"Dodatkowe"},
            {key:"ksef",      icon:"🧾", label:"KSeF"},
            {key:"superadmin", icon:"🛡", label:"Super Admin"},
          ].filter(item => {
            if (item.key === "orders") return true;
            if (item.key === "superadmin") return isSuperAdmin;
            const superAdminHidden = ["users","hardware","backup","logs","database"];
            if (superAdminHidden.includes(item.key)) return false;
            if (!isSuperAdmin) {
              if (!isTabVisible(item.key, user.role)) return false;
              return rolePerm(({menu:"adminMenu",promo:"adminPromo",cats:"adminCats",tables:"adminTables",takeaway:"adminTakeaway",online:"adminOnlineOrders",clients:"adminClients",campaigns:"adminCampaigns",vouchers:"adminVouchers",sales:"adminSales",messages:"adminMessages",settings:"adminSettings",extras:"adminExtras",ksef:"adminKsef",warehouse:"adminWarehouse",specials:"adminSpecials"}[item.key])||null);
            }
            return true;
          }).map(item=>{
            const isAct = tab===item.key;
            let cnt = 0;
            if(item.key==="orders"){
              const today=orders.filter(o=>o.date===todayStr());
              cnt=isCook?today.filter(o=>o.cookStatus==="ready"&&!o.issued).length
                :today.filter(o=>!o.issued).length;
              cnt+=(staffCalls||[]).filter(c=>!c.dismissed).length;
            }
            return (
              <button key={item.key}
                onMouseDown={e=>e.stopPropagation()}
                onClick={()=>{setTab(item.key); logActivity(setLogs, user, "Wejście w zakładkę", item.label);}}
                style={{
                  flex:"0 0 auto",
                  display:"flex", flexDirection:"column", alignItems:"center",
                  padding:"7px 10px 9px",
                  border:"none", cursor:"pointer",
                  background:"transparent",
                  color: isAct ? "#fff" : "rgba(255,255,255,0.45)",
                  borderTop: isAct ? "3px solid "+TA.accent : "3px solid transparent",
                  transition:"color .1s", position:"relative",
                  minWidth:54
                }}>
                <span style={{fontSize:16,lineHeight:1,marginBottom:2}}>{item.icon}</span>
                <span style={{fontSize:9,fontWeight:isAct?700:400,whiteSpace:"nowrap"}}>{item.label}</span>
                {cnt>0&&<span style={{position:"absolute",top:4,right:4,background:"#ef4444",color:"#fff",borderRadius:8,padding:"0 4px",fontSize:8,fontWeight:700,lineHeight:"13px"}}>{cnt}</span>}
              </button>
            );
          })}
        </div>

      </div>    {/* end CONTENT */}
    </div>      {/* end flex container */}
    </>
  );
}

/* ════════════════════════════ KSeF PANEL ════════════════════════════════════
   Krajowy System e-Faktur — generowanie i zarządzanie fakturami elektronicznymi.
   Moduł obsługuje wystawianie faktur VAT, eksport XML (FA(2)), podgląd i archiwum.
══════════════════════════════════════════════════════════════════════════════ */

const VAT_RATES = [
  { label: "23%", value: 23 },
  { label: "8%",  value: 8  },
  { label: "5%",  value: 5  },
  { label: "0%",  value: 0  },
  { label: "ZW",  value: "zw" },
];

function fmtPLN(n) {
  return Number(n||0).toFixed(2).replace(".",",") + " zł";
}

function fmtDate(iso) {
  if (!iso) return "—";
  const [y,m,d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/* Generuj numer faktury: FV/YYYY/MM/NNN */
function nextInvoiceNumber(invoices, year, month) {
  const prefix = `FV/${year}/${String(month).padStart(2,"0")}/`;
  const nums = invoices
    .filter(f=>f.number&&f.number.startsWith(prefix))
    .map(f=>parseInt(f.number.split("/")[3]||"0",10))
    .filter(n=>!isNaN(n));
  const next = nums.length ? Math.max(...nums)+1 : 1;
  return prefix + String(next).padStart(3,"0");
}

/* Oblicz pozycje + sumy VAT */
function calcInvoice(lines) {
  const vatGroups = {};
  let netTotal = 0, vatTotal = 0, grossTotal = 0;
  lines.forEach(ln => {
    const qty   = parseFloat(ln.qty)   || 0;
    const price = parseFloat(ln.price) || 0;
    const rate  = ln.vat === "zw" ? 0 : (parseFloat(ln.vat) || 0);
    const net   = qty * price;
    const vat   = net * rate / 100;
    const gross = net + vat;
    netTotal   += net;
    vatTotal   += vat;
    grossTotal += gross;
    const key = String(ln.vat);
    if (!vatGroups[key]) vatGroups[key] = { rate: ln.vat, net: 0, vat: 0, gross: 0 };
    vatGroups[key].net   += net;
    vatGroups[key].vat   += vat;
    vatGroups[key].gross += gross;
  });
  return { netTotal, vatTotal, grossTotal, vatGroups };
}

/* Generuj XML FA(2) — uproszczony schemat KSeF */
function generateKSeFXml(inv, settings) {
  const seller = settings.ksefSeller || {};
  const now = new Date().toISOString().slice(0,19);
  const { netTotal, vatTotal, grossTotal, vatGroups } = calcInvoice(inv.lines||[]);

  const lines = (inv.lines||[]).map((ln,i) => {
    const qty   = parseFloat(ln.qty)   || 0;
    const price = parseFloat(ln.price) || 0;
    const rate  = ln.vat === "zw" ? 0 : (parseFloat(ln.vat) || 0);
    const net   = qty * price;
    const vat   = net * rate / 100;
    return `    <fa:WierszFaktury>
      <fa:NrWiersza>${i+1}</fa:NrWiersza>
      <fa:P_7>${escXml(ln.name||"Usługa")}</fa:P_7>
      <fa:P_8A>${ln.unit||"szt."}</fa:P_8A>
      <fa:P_8B>${qty.toFixed(4)}</fa:P_8B>
      <fa:P_9A>${price.toFixed(2)}</fa:P_9A>
      <fa:P_11>${net.toFixed(2)}</fa:P_11>
      <fa:P_12>${ln.vat==="zw"?"ZW":String(parseFloat(ln.vat)||0)}</fa:P_12>
      <fa:P_14_2>${vat.toFixed(2)}</fa:P_14_2>
    </fa:WierszFaktury>`;
  }).join("\n");

  const vatRows = Object.values(vatGroups).map(g=>`    <fa:StawkiVat>
      <fa:P_12>${g.rate==="zw"?"ZW":String(parseFloat(g.rate)||0)}</fa:P_12>
      <fa:P_13_1>${g.net.toFixed(2)}</fa:P_13_1>
      <fa:P_14_1>${g.vat.toFixed(2)}</fa:P_14_1>
    </fa:StawkiVat>`).join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<fa:Faktura xmlns:fa="http://crd.gov.pl/wzor/2023/06/29/12648/" xmlns:etd="http://crd.gov.pl/xml/schematy/dziedzinowe/mf/2022/01/05/eD/DefinicjeTypy/">
  <fa:Naglowek>
    <fa:KodFormularza kodSystemowy="FA (2)" wersjaSchemy="1-0E">FA</fa:KodFormularza>
    <fa:WariantFormularza>2</fa:WariantFormularza>
    <fa:DataWytworzeniaFa>${now}</fa:DataWytworzeniaFa>
    <fa:SystemInfo>${escXml(settings.restaurantName||"System POS")}</fa:SystemInfo>
  </fa:Naglowek>
  <fa:Podmiot1>
    <fa:DaneIdentyfikacyjne>
      <fa:NIP>${escXml(seller.nip||"")}</fa:NIP>
      <fa:PelnaNazwa>${escXml(seller.name||settings.restaurantName||"")}</fa:PelnaNazwa>
    </fa:DaneIdentyfikacyjne>
    <fa:Adres>
      <fa:KodKraju>PL</fa:KodKraju>
      <fa:AdresL1>${escXml(seller.address||"")}</fa:AdresL1>
    </fa:Adres>
  </fa:Podmiot1>
  <fa:Podmiot2>
    <fa:DaneIdentyfikacyjne>
      <fa:NIP>${escXml(inv.buyerNip||"")}</fa:NIP>
      <fa:PelnaNazwa>${escXml(inv.buyerName||"")}</fa:PelnaNazwa>
    </fa:DaneIdentyfikacyjne>
    <fa:Adres>
      <fa:KodKraju>PL</fa:KodKraju>
      <fa:AdresL1>${escXml(inv.buyerAddress||"")}</fa:AdresL1>
    </fa:Adres>
  </fa:Podmiot2>
  <fa:Fa>
    <fa:KodWaluty>PLN</fa:KodWaluty>
    <fa:P_1>${inv.issueDate||""}</fa:P_1>
    <fa:P_2>${escXml(inv.number||"")}</fa:P_2>
    <fa:P_6>${inv.saleDate||inv.issueDate||""}</fa:P_6>
    <fa:P_15>${grossTotal.toFixed(2)}</fa:P_15>
    <fa:Adnotacje>
      <fa:P_16>2</fa:P_16>
      <fa:P_17>2</fa:P_17>
      <fa:P_18>2</fa:P_18>
      <fa:P_18A>2</fa:P_18A>
      <fa:P_19>2</fa:P_19>
      <fa:P_22>2</fa:P_22>
      <fa:P_23>2</fa:P_23>
      <fa:P_PMarzy>2</fa:P_PMarzy>
    </fa:Adnotacje>
    <fa:RodzajFaktury>VAT</fa:RodzajFaktury>
${lines}
${vatRows}
    <fa:P_13_1O>${netTotal.toFixed(2)}</fa:P_13_1O>
    <fa:P_14_1O>${vatTotal.toFixed(2)}</fa:P_14_1O>
    <fa:P_15O>${grossTotal.toFixed(2)}</fa:P_15O>
  </fa:Fa>
</fa:Faktura>`;
}

function escXml(s) {
  return String(s||"")
    .replace(/&/g,"&amp;")
    .replace(/</g,"&lt;")
    .replace(/>/g,"&gt;")
    .replace(/"/g,"&quot;")
    .replace(/'/g,"&apos;");
}

/* Generuj HTML do druku */
function invoiceHtml(inv, settings) {
  const seller = settings.ksefSeller || {};
  const { netTotal, vatTotal, grossTotal, vatGroups } = calcInvoice(inv.lines||[]);
  const rows = (inv.lines||[]).map((ln,i)=>{
    const qty   = parseFloat(ln.qty)   || 0;
    const price = parseFloat(ln.price) || 0;
    const rate  = ln.vat==="zw"?0:(parseFloat(ln.vat)||0);
    const net   = qty*price; const vat=net*rate/100; const gross=net+vat;
    return `<tr><td>${i+1}</td><td>${ln.name||""}</td><td>${ln.unit||"szt."}</td><td>${qty.toFixed(2)}</td>
      <td>${price.toFixed(2)}</td><td>${net.toFixed(2)}</td>
      <td>${ln.vat==="zw"?"ZW":ln.vat+"%"}</td><td>${vat.toFixed(2)}</td><td>${gross.toFixed(2)}</td></tr>`;
  }).join("");
  const vatRows = Object.values(vatGroups).map(g=>
    `<tr><td>${g.rate==="zw"?"ZW":g.rate+"%"}</td><td>${g.net.toFixed(2)}</td><td>${g.vat.toFixed(2)}</td><td>${g.gross.toFixed(2)}</td></tr>`
  ).join("");
  return `<!DOCTYPE html><html lang="pl"><head><meta charset="UTF-8">
<title>Faktura ${inv.number}</title>
<style>
  body{font-family:Arial,sans-serif;font-size:12px;margin:20px;color:#111}
  h1{font-size:20px;margin-bottom:4px}
  .header{display:flex;justify-content:space-between;margin-bottom:20px}
  .box{border:1px solid #ccc;padding:10px;border-radius:4px;min-width:200px}
  table{width:100%;border-collapse:collapse;margin-bottom:12px}
  th,td{border:1px solid #ddd;padding:5px 7px;text-align:left}
  th{background:#f5f5f5;font-size:11px}
  .total-row td{font-weight:bold;background:#f9f9f9}
  .sum-box{float:right;text-align:right;margin-top:8px}
  .sum-box td{border:none;padding:3px 8px}
  .big{font-size:18px;font-weight:bold}
  @media print{@page{margin:15mm}}
</style></head><body>
<h1>FAKTURA VAT</h1>
<div style="font-size:14px;margin-bottom:4px"><strong>${inv.number||""}</strong></div>
<div class="header">
  <div class="box"><strong>Sprzedawca:</strong><br>${seller.name||settings.restaurantName||""}<br>NIP: ${seller.nip||""}<br>${seller.address||""}<br>${seller.bank?"Konto: "+seller.bank:""}</div>
  <div class="box"><strong>Nabywca:</strong><br>${inv.buyerName||""}<br>${inv.buyerNip?"NIP: "+inv.buyerNip:""}<br>${inv.buyerAddress||""}</div>
  <div class="box"><strong>Data wystawienia:</strong> ${fmtDate(inv.issueDate)}<br><strong>Data sprzedaży:</strong> ${fmtDate(inv.saleDate||inv.issueDate)}<br><strong>Płatność:</strong> ${inv.paymentMethod||"przelew"}<br><strong>Termin:</strong> ${fmtDate(inv.dueDate)}</div>
</div>
<table><thead><tr><th>#</th><th>Nazwa</th><th>J.m.</th><th>Ilość</th><th>Cena netto</th><th>Netto</th><th>VAT</th><th>VAT kwota</th><th>Brutto</th></tr></thead><tbody>${rows}</tbody></table>
<table style="width:auto;float:left"><thead><tr><th>Stawka VAT</th><th>Netto</th><th>VAT</th><th>Brutto</th></tr></thead><tbody>${vatRows}</tbody></table>
<table class="sum-box"><tbody>
  <tr><td>Razem netto:</td><td>${netTotal.toFixed(2)} zł</td></tr>
  <tr><td>Razem VAT:</td><td>${vatTotal.toFixed(2)} zł</td></tr>
  <tr class="total-row"><td class="big">DO ZAPŁATY:</td><td class="big">${grossTotal.toFixed(2)} zł</td></tr>
</tbody></table>
<div style="clear:both;margin-top:30px;border-top:1px solid #eee;padding-top:12px;font-size:11px;color:#555">
  ${seller.bank?"Numer konta: "+seller.bank+"<br>":""}
  ${inv.notes?"Uwagi: "+inv.notes:""}
</div>
</body></html>`;
}

const BLANK_INV_LINE = { id: "", name: "", unit: "szt.", qty: "1", price: "", vat: "23" };
const BLANK_INVOICE  = {
  id: "", number: "", issueDate: "", saleDate: "", dueDate: "",
  buyerName: "", buyerNip: "", buyerAddress: "",
  paymentMethod: "przelew", notes: "", status: "draft",
  lines: [{ ...BLANK_INV_LINE, id: "l1" }],
  linkedOrderId: null,
};

function KSeFPanel({ orders, setOrders, settings, setSettings, clients, menu, user }) {
  const [tab2,        setTab2]        = useState("list");      // list | form | config
  const [invoices,    setInvoicesRaw] = useState(() => {
    try { return JSON.parse(localStorage.getItem("smakidomu_ksef_invoices")||"[]"); } catch(e){ return []; }
  });
  const setInvoices = fn => {
    setInvoicesRaw(prev => {
      const next = typeof fn==="function" ? fn(prev) : fn;
      try { localStorage.setItem("smakidomu_ksef_invoices", JSON.stringify(next)); } catch(e){}
      return next;
    });
  };

  const [form,        setForm]        = useState(null);        // null = nie edytujemy
  const [filterSt,    setFilterSt]    = useState("all");       // all | draft | issued | paid
  const [search,      setSearch]      = useState("");
  const [previewInv,  setPreviewInv]  = useState(null);
  const [msg,         setMsg]         = useState(null);
  const [sending,     setSending]     = useState({}); // invId -> "validating"|"sending"|"upo"
  const [selectedIds, setSelectedIds] = useState(new Set()); // do masowego usuwania

  /* ── Uprawnienia do usuwania ── */
  const userRole       = user?.role || "seller";
  const canDelete      = userRole === "admin" || userRole === "superadmin";
  const isSuperAdminKS = userRole === "superadmin";

  /* Usuń pojedynczą fakturę */
  const deleteInvoice = (inv) => {
    const hasKSef = inv.ksefNumber && inv.ksefNumber !== "—";
    const warn = hasKSef
      ? `⚠ Faktura ${inv.number} ma nadany numer KSeF (${inv.ksefNumber}).\n\nUsunięcie jej z systemu NIE cofnie faktury w KSeF — pozostanie ona w rejestrze Ministerstwa Finansów.\n\nCzy na pewno usunąć LOKALNY zapis tej faktury?`
      : `Czy na pewno usunąć fakturę ${inv.number}?\n\nTa operacja jest nieodwracalna.`;
    if (!window.confirm(warn)) return;
    setInvoices(p => p.filter(x => x.id !== inv.id));
    setSelectedIds(p => { const n = new Set(p); n.delete(inv.id); return n; });
    if (inv.linkedOrderId && setOrders) {
      setOrders(p => p.map(o => o.id === inv.linkedOrderId
        ? { ...o, invoiceLinkedId: undefined, invoiceNumber: undefined }
        : o
      ));
    }
    showMsg(`🗑 Faktura ${inv.number} została usunięta`);
  };

  /* Usuń zaznaczone */
  const deleteSelected = () => {
    if (selectedIds.size === 0) return;
    const toDelete = invoices.filter(i => selectedIds.has(i.id));
    const withKSef = toDelete.filter(i => i.ksefNumber && i.ksefNumber !== "—");
    let warn = `Usunąć ${selectedIds.size} ${selectedIds.size === 1 ? "fakturę" : selectedIds.size < 5 ? "faktury" : "faktur"}?\n\nTa operacja jest nieodwracalna.`;
    if (withKSef.length > 0)
      warn += `\n\n⚠ ${withKSef.length} z nich posiada numer KSeF — usunięcie dotyczy tylko lokalnego zapisu, faktury pozostają w systemie MF.`;
    if (!window.confirm(warn)) return;
    setInvoices(p => p.filter(i => !selectedIds.has(i.id)));
    toDelete.forEach(inv => {
      if (inv.linkedOrderId && setOrders) {
        setOrders(p => p.map(o => o.id === inv.linkedOrderId
          ? { ...o, invoiceLinkedId: undefined, invoiceNumber: undefined }
          : o
        ));
      }
    });
    showMsg(`🗑 Usunięto ${toDelete.length} faktur`);
    setSelectedIds(new Set());
  };

  /* Wyczyść całe archiwum (tylko superadmin) */
  const clearAll = () => {
    if (!window.confirm(`🛡 SUPERADMIN — Wyczyścić CAŁE archiwum faktur (${invoices.length})?\n\nTa operacja jest NIEODWRACALNA i usunie wszystkie faktury z pamięci przeglądarki.`)) return;
    setInvoices([]);
    setSelectedIds(new Set());
    showMsg("🗑 Archiwum faktur zostało wyczyszczone");
  };

  const ac = settings.accentColor || TA.accent;

  const showMsg = (text, type="ok") => { setMsg({text,type}); setTimeout(()=>setMsg(null),5000); };

  /* ── ksefapi.pl REST helpers ── */
  const ksefApi = () => {
    const s = settings.ksefSeller || {};
    const base = s.apiEnv==="prod" ? "https://ksefapi.pl/api" : "https://ksefapi.pl/api-test";
    const auth = (s.apiKeyId && s.apiKey) ? "Basic " + btoa(s.apiKeyId + ":" + s.apiKey) : null;
    return { base, auth, configured: !!(s.apiKeyId && s.apiKey) };
  };

  /* Walidacja faktury przez API */
  const validateInvoice = async (inv) => {
    const { base, auth, configured } = ksefApi();
    if (!configured) { alert("Skonfiguruj klucze API w ⚙ Dane sprzedawcy"); return false; }
    setSending(p=>({...p,[inv.id]:"validating"}));
    try {
      const xml = generateKSeFXml(inv, settings);
      const res = await fetch(base + "/invoice/validate", {
        method: "POST",
        headers: { "Authorization": auth, "Content-Type": "application/xml" },
        body: xml,
      });
      const data = await res.json().catch(()=>null);
      if (res.ok && (!data || !data.error)) {
        showMsg("✅ Faktura poprawna — gotowa do wysyłki");
        setSending(p=>({...p,[inv.id]:null}));
        return true;
      } else {
        showMsg("❌ Błąd walidacji: " + (data?.message || data?.error || res.statusText), "err");
        setSending(p=>({...p,[inv.id]:null}));
        return false;
      }
    } catch(e) {
      showMsg("❌ Błąd połączenia: " + e.message, "err");
      setSending(p=>({...p,[inv.id]:null}));
      return false;
    }
  };

  /* Wysyłka faktury do KSeF */
  const sendToKSeF = async (inv) => {
    const { base, auth, configured } = ksefApi();
    if (!configured) { alert("Skonfiguruj klucze API w ⚙ Dane sprzedawcy"); return; }
    const s = settings.ksefSeller || {};
    if (!s.ksefToken) { alert("Brak tokenu KSeF — uzupełnij go w ⚙ Dane sprzedawcy"); return; }
    if (!window.confirm(`Wysłać fakturę ${inv.number} do KSeF?\nTo działanie ma skutki prawne w środowisku produkcyjnym.`)) return;
    setSending(p=>({...p,[inv.id]:"sending"}));
    try {
      const xml = generateKSeFXml(inv, settings);
      // Krok 1: otwórz sesję
      const sessRes = await fetch(base + "/session/open", {
        method: "POST",
        headers: { "Authorization": auth, "Content-Type": "application/json" },
        body: JSON.stringify({ ksefToken: s.ksefToken }),
      });
      const sessData = await sessRes.json().catch(()=>null);
      if (!sessRes.ok) {
        showMsg("❌ Błąd otwarcia sesji: " + (sessData?.message || sessRes.statusText), "err");
        setSending(p=>({...p,[inv.id]:null})); return;
      }
      const sessionId = sessData?.sessionId || sessData?.SessionId;

      // Krok 2: wyślij fakturę
      const sendRes = await fetch(base + "/invoice/send", {
        method: "POST",
        headers: { "Authorization": auth, "Content-Type": "application/xml",
          ...(sessionId ? { "X-Session-Id": sessionId } : {}) },
        body: xml,
      });
      const sendData = await sendRes.json().catch(()=>null);
      if (!sendRes.ok) {
        showMsg("❌ Błąd wysyłki: " + (sendData?.message || sendRes.statusText), "err");
        setSending(p=>({...p,[inv.id]:null})); return;
      }
      const ksefNumber = sendData?.ksefNumber || sendData?.KsefNumber || sendData?.referenceNumber || sendData?.invoiceId || "—";

      // Krok 3: pobierz UPO (z opóźnieniem)
      setSending(p=>({...p,[inv.id]:"upo"}));
      const upoRef = sendData?.upoReferenceNumber || sendData?.referenceNumber;
      let upoUrl = null;
      if (upoRef && sessionId) {
        await new Promise(r=>setTimeout(r,2500));
        const upoRes = await fetch(base + "/session/upo/" + sessionId, {
          headers: { "Authorization": auth }
        });
        if (upoRes.ok) {
          const blob = await upoRes.blob();
          upoUrl = URL.createObjectURL(blob);
        }
      }

      // Zapisz wynik
      setInvoices(p=>p.map(x=>x.id===inv.id ? {
        ...x, status:"issued",
        ksefNumber,
        ksefSentAt: new Date().toLocaleString("pl-PL"),
        ksefEnv: s.apiEnv||"test",
        ksefSessionId: sessionId,
        upoUrl,
      } : x));

      setSending(p=>({...p,[inv.id]:null}));
      showMsg(`✅ Faktura wysłana do KSeF! Numer KSeF: ${ksefNumber}`);

      if (upoUrl) {
        const a = document.createElement("a");
        a.href = upoUrl; a.download = "UPO-" + (inv.number||"faktura").replace(/\//g,"-") + ".pdf";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
    } catch(e) {
      showMsg("❌ Błąd: " + e.message, "err");
      setSending(p=>({...p,[inv.id]:null}));
    }
  };

  /* Sprawdź status faktury w KSeF */
  const checkKSeFStatus = async (inv) => {
    const { base, auth, configured } = ksefApi();
    if (!configured || !inv.ksefNumber) return;
    setSending(p=>({...p,[inv.id]:"status"}));
    try {
      const res = await fetch(base + "/invoice/status/" + encodeURIComponent(inv.ksefNumber), {
        headers: { "Authorization": auth }
      });
      const data = await res.json().catch(()=>null);
      setSending(p=>({...p,[inv.id]:null}));
      if (res.ok) {
        showMsg("📊 Status KSeF: " + (data?.status || data?.Status || JSON.stringify(data)));
      } else {
        showMsg("❌ Błąd sprawdzania statusu: " + (data?.message || res.statusText), "err");
      }
    } catch(e) {
      showMsg("❌ " + e.message, "err");
      setSending(p=>({...p,[inv.id]:null}));
    }
  };

  /* ── filtrowanie ── */
  const filtered = invoices.filter(inv=>{
    if (filterSt!=="all" && inv.status!==filterSt) return false;
    if (search && !inv.number?.toLowerCase().includes(search.toLowerCase())
      && !inv.buyerName?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).slice().reverse();

  /* ── otwórz nową fakturę ── */
  const openNew = (fromOrder=null) => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth()+1;
    const iso = now.toISOString().slice(0,10);
    const due = new Date(now); due.setDate(due.getDate()+14);
    let lines = [{ ...BLANK_INV_LINE, id: genId() }];
    let buyerName="", buyerNip="", buyerAddress="";
    let linkedOrderId = null;
    if (fromOrder) {
      linkedOrderId = fromOrder.id;
      const cart = fromOrder.cart||[];
      lines = cart.map(c=>({
        id: genId(), name: c.name, unit: "szt.",
        qty: String(c.qty||1), price: String((c.effectivePrice??c.price)||0), vat: "8"
      }));
      if (fromOrder.clientId) {
        const cl = (clients||[]).find(c=>c.id===fromOrder.clientId);
        if (cl) { buyerName=cl.company||cl.name; buyerNip=cl.nip||""; buyerAddress=cl.address||""; }
      }
      buyerName = (buyerName||(fromOrder.firstName&&fromOrder.lastName))?`${fromOrder.firstName} ${fromOrder.lastName}`:buyerName;
    }
    setForm({
      ...BLANK_INVOICE,
      id: genId(),
      number: nextInvoiceNumber(invoices, y, m),
      issueDate: iso,
      saleDate:  iso,
      dueDate:   due.toISOString().slice(0,10),
      buyerName, buyerNip, buyerAddress,
      lines: lines.length ? lines : [{ ...BLANK_INV_LINE, id: genId() }],
      linkedOrderId,
      status: "draft",
    });
    setTab2("form");
  };

  const openEdit = (inv) => { setForm({...inv, lines:(inv.lines||[]).map(l=>({...l}))}); setTab2("form"); };

  const saveInvoice = (status="draft") => {
    if (!form) return;
    if (!form.buyerName.trim()) { alert("Wpisz nazwę nabywcy"); return; }
    if (!form.issueDate) { alert("Wybierz datę wystawienia"); return; }
    if (!form.lines.length) { alert("Dodaj co najmniej jedną pozycję"); return; }
    const updated = {...form, status};
    setInvoices(p=> p.find(x=>x.id===updated.id) ? p.map(x=>x.id===updated.id?updated:x) : [...p, updated]);
    // Oznacz powiązane zamówienie jako "ma fakturę"
    if (updated.linkedOrderId && setOrders) {
      setOrders(p=>p.map(o=>o.id===updated.linkedOrderId?{...o,invoiceLinkedId:updated.id,invoiceNumber:updated.number}:o));
    }
    setTab2("list");
    setForm(null);
    showMsg(status==="issued" ? "✅ Faktura wystawiona!" : "💾 Szkic zapisany");
  };

  /* ── linia faktury helpers ── */
  const setLine = (idx, field, val) => setForm(f=>({...f, lines: f.lines.map((l,i)=>i===idx?{...l,[field]:val}:l)}));
  const addLine = () => setForm(f=>({...f, lines:[...f.lines,{...BLANK_INV_LINE,id:genId()}]}));
  const delLine = (idx) => setForm(f=>({...f, lines: f.lines.filter((_,i)=>i!==idx)}));

  /* ── import z zamówienia ──
     Priorytetowo: zamówienia z invoiceRequest (klient zażyczył faktury),
     potem pozostałe opłacone bez faktury */
  const ordersWithInvReq   = (orders||[]).filter(o=>o.invoiceRequest && !o.invoiceLinkedId && o.paid);
  const ordersForInvoice   = (orders||[]).filter(o=>!o.invoiceLinkedId && o.paid && o.date && !o.invoiceRequest);

  /* ── eksport XML ── */
  const downloadXml = (inv) => {
    const xml = generateKSeFXml(inv, settings);
    const blob = new Blob([xml], {type:"application/xml"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = (inv.number||"faktura").replace(/\//g,"-")+".xml";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  /* ── drukuj ── */
  const printInvoice = (inv) => {
    const w = window.open("","_blank");
    if (!w) return;
    w.document.write(invoiceHtml(inv, settings));
    w.document.close();
    w.focus();
    setTimeout(()=>w.print(),600);
  };

  /* ── sumy dla dashboard ── */
  const totalIssued = invoices.filter(i=>i.status==="issued"||i.status==="paid")
    .reduce((s,i)=>s+calcInvoice(i.lines||[]).grossTotal,0);
  const totalPaid   = invoices.filter(i=>i.status==="paid")
    .reduce((s,i)=>s+calcInvoice(i.lines||[]).grossTotal,0);

  const STATUS_COL = { draft:"#6b7280", issued:"#2563eb", paid:"#16a34a", cancelled:"#dc2626" };
  const STATUS_LABEL = { draft:"Szkic", issued:"Wystawiona", paid:"Zapłacona", cancelled:"Anulowana" };

  const ksefSettings = settings.ksefSeller || {};

  return (
    <div style={{paddingBottom:40}}>
      {/* Komunikat */}
      {msg&&<div style={{padding:"10px 14px",borderRadius:10,marginBottom:14,fontWeight:700,fontSize:13,
        background:msg.type==="ok"?"#f0fdf4":"#ffebee",
        color:msg.type==="ok"?"#15803d":"#c0392b",
        border:"1.5px solid "+(msg.type==="ok"?"#86efac":"#fca5a5")}}>{msg.text}</div>}

      {/* Nagłówek */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontWeight:800,fontSize:18,color:TA.text}}>🧾 KSeF — e-Faktury</div>
          <div style={{fontSize:12,color:TA.muted,marginTop:2}}>Krajowy System e-Faktur · generowanie, eksport XML i archiwum</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          {tab2!=="config"&&<button onClick={()=>setTab2("config")} style={{...S.cancelBtn,fontSize:12}}>⚙ Dane sprzedawcy</button>}
          {tab2==="list"&&<button onClick={()=>openNew()} style={{padding:"9px 16px",background:ac,color:"#fff",border:"none",borderRadius:9,fontWeight:700,cursor:"pointer",fontSize:13}}>+ Nowa faktura</button>}
          {(tab2==="form"||tab2==="config")&&<button onClick={()=>{setTab2("list");setForm(null);}} style={S.cancelBtn}>← Powrót</button>}
        </div>
      </div>

      {/* ── KONFIGURACJA DANYCH SPRZEDAWCY ── */}
      {tab2==="config"&&(
        <div>
          <div style={{...S.formCard,border:"2px solid "+ac}}>
            <div style={S.formTitle}>🏢 Dane sprzedawcy (Twoja firma)</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div style={{gridColumn:"span 2"}}>
                <label style={S.label}>Pełna nazwa firmy *</label>
                <input style={S.inp} placeholder="Piekarnia Pokutyńskich Sp. z o.o." value={ksefSettings.name||""}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,name:e.target.value}}))}/>
              </div>
              <div>
                <label style={S.label}>NIP sprzedawcy *</label>
                <input style={S.inp} placeholder="0000000000" value={ksefSettings.nip||""}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,nip:e.target.value.replace(/\D/g,"").slice(0,10)}}))}/>
              </div>
              <div>
                <label style={S.label}>REGON</label>
                <input style={S.inp} placeholder="000000000" value={ksefSettings.regon||""}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,regon:e.target.value}}))}/>
              </div>
              <div style={{gridColumn:"span 2"}}>
                <label style={S.label}>Adres (ulica, nr, kod, miasto)</label>
                <input style={S.inp} placeholder="ul. Piekarska 1, 00-001 Warszawa" value={ksefSettings.address||""}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,address:e.target.value}}))}/>
              </div>
              <div style={{gridColumn:"span 2"}}>
                <label style={S.label}>Numer konta bankowego (IBAN)</label>
                <input style={S.inp} placeholder="PL00 0000 0000 0000 0000 0000 0000" value={ksefSettings.bank||""}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,bank:e.target.value}}))}/>
              </div>
              <div>
                <label style={S.label}>Domyślna stawka VAT</label>
                <select style={S.inp} value={ksefSettings.defaultVat||"23"}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,defaultVat:e.target.value}}))}>
                  {VAT_RATES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Domyślna forma płatności</label>
                <select style={S.inp} value={ksefSettings.defaultPayment||"przelew"}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,defaultPayment:e.target.value}}))}>
                  {["przelew","gotówka","karta","blik"].map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
            <div style={{marginTop:14,padding:"10px 14px",background:"#eff6ff",borderRadius:9,border:"1px solid #bfdbfe",fontSize:12,color:"#1d4ed8"}}>
              <strong>ℹ Dane sprzedawcy</strong> są wpisywane do każdej faktury i do eksportu XML (schemat FA(2) KSeF).
              NIP jest wymagany do wysyłki faktury do Krajowego Systemu e-Faktur.
            </div>
            <button onClick={()=>{setTab2("list");showMsg("✅ Dane sprzedawcy zapisane");}} style={{...S.darkBtn,marginTop:14}}>
              💾 Zapisz dane sprzedawcy
            </button>
          </div>

          <div style={{...S.formCard,border:"2px solid #6366f1"}}>
            <div style={S.formTitle}>🔗 Integracja REST API — ksefapi.pl</div>
            <div style={{fontSize:12,color:TA.muted,marginBottom:14,lineHeight:1.7}}>
              Wpisz klucze API z serwisu <a href="https://ksefapi.pl" target="_blank" rel="noreferrer" style={{color:"#6366f1",fontWeight:700}}>ksefapi.pl</a>, aby umożliwić automatyczną walidację i wysyłkę faktur do KSeF bez potrzeby ręcznego eksportu XML.
              Rejestracja bezpłatna — środowisko testowe nie wymaga zakupu pakietu.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div>
                <label style={S.label}>API Key ID</label>
                <input style={S.inp} placeholder="twój_key_id" value={ksefSettings.apiKeyId||""}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,apiKeyId:e.target.value.trim()}}))}/>
              </div>
              <div>
                <label style={S.label}>API Key (klucz prywatny)</label>
                <input style={{...S.inp,fontFamily:"monospace"}} type="password" placeholder="••••••••••••" value={ksefSettings.apiKey||""}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,apiKey:e.target.value.trim()}}))}/>
              </div>
              <div style={{gridColumn:"span 2"}}>
                <label style={S.label}>Token KSeF (z portalu podatki.gov.pl)</label>
                <input style={{...S.inp,fontFamily:"monospace",fontSize:11}} placeholder="token z systemu KSeF — zakładka API Keys and KSeF Tokens" value={ksefSettings.ksefToken||""}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,ksefToken:e.target.value.trim()}}))}/>
              </div>
              <div>
                <label style={S.label}>Środowisko</label>
                <select style={S.inp} value={ksefSettings.apiEnv||"test"}
                  onChange={e=>setSettings(p=>({...p,ksefSeller:{...p.ksefSeller,apiEnv:e.target.value}}))}>
                  <option value="test">🧪 Testowe (ksefapi.pl/api-test)</option>
                  <option value="prod">🚀 Produkcyjne (ksefapi.pl/api)</option>
                </select>
              </div>
              <div style={{display:"flex",alignItems:"flex-end"}}>
                <button onClick={async()=>{
                  const kid=ksefSettings.apiKeyId, key=ksefSettings.apiKey;
                  if(!kid||!key){alert("Wpisz API Key ID i API Key");return;}
                  const base=ksefSettings.apiEnv==="prod"?"https://ksefapi.pl/api":"https://ksefapi.pl/api-test";
                  const auth="Basic "+btoa(kid+":"+key);
                  showMsg("⏳ Testowanie połączenia...", "ok");
                  try{
                    const r=await fetch(base+"/invoice/generate",{method:"POST",headers:{"Authorization":auth,"Content-Type":"application/json"},body:JSON.stringify({test:true})});
                    if(r.status===401){showMsg("❌ Błąd autoryzacji — sprawdź klucze API","err");return;}
                    showMsg("✅ Połączenie z API działa poprawnie!","ok");
                  }catch(e){showMsg("❌ Błąd połączenia: "+e.message,"err");}
                }} style={{...S.darkBtn,width:"100%",background:"#6366f1"}}>🔌 Testuj połączenie</button>
              </div>
            </div>
            {(ksefSettings.apiKeyId&&ksefSettings.apiKey)
              ?<div style={{background:"#f0fdf4",border:"1px solid #86efac",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:700,color:"#15803d"}}>✅ Klucze API skonfigurowane · środowisko: {ksefSettings.apiEnv==="prod"?"produkcyjne 🚀":"testowe 🧪"}</div>
              :<div style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:8,padding:"8px 12px",fontSize:12,fontWeight:600,color:"#9a3412"}}>⚠ Klucze API nie skonfigurowane — wysyłka do KSeF niedostępna. Eksport XML działa zawsze.</div>
            }
            <div style={{marginTop:12,padding:"10px 14px",background:"#f5f3ff",borderRadius:9,border:"1px solid #c4b5fd",fontSize:12,color:"#6b21a8",lineHeight:1.8}}>
              <strong>Jak uzyskać klucze API:</strong>
              <ol style={{margin:"4px 0 0",paddingLeft:18}}>
                <li>Zarejestruj się na <a href="https://ksefapi.pl/portal-test/register/" target="_blank" rel="noreferrer" style={{color:"#6366f1"}}>ksefapi.pl/portal-test/register</a></li>
                <li>Po zalogowaniu otwórz zakładkę <strong>API Keys and KSeF Tokens</strong></li>
                <li>Skopiuj <strong>Key ID</strong> i <strong>Key</strong> — wklej powyżej</li>
                <li>Dodaj token KSeF wygenerowany w portalu podatki.gov.pl</li>
              </ol>
            </div>
            <button onClick={()=>{setTab2("list");showMsg("✅ Ustawienia API zapisane");}} style={{...S.darkBtn,marginTop:14}}>
              💾 Zapisz ustawienia API
            </button>
          </div>

          <div style={{...S.formCard}}>
            <div style={S.formTitle}>ℹ Informacje o środowiskach KSeF</div>
            <div style={{fontSize:12,color:TA.muted,lineHeight:1.8}}>
              <div>• <strong>Środowisko testowe ksefapi.pl:</strong> <code style={{background:"#f3f4f6",padding:"1px 5px",borderRadius:4}}>https://ksefapi.pl/api-test/</code> — bezpłatne, faktury bez skutków prawnych</div>
              <div>• <strong>Środowisko produkcyjne:</strong> <code style={{background:"#f3f4f6",padding:"1px 5px",borderRadius:4}}>https://ksefapi.pl/api/</code> — wymaga zakupu pakietu</div>
              <div>• <strong>Portal MF testowy:</strong> <a href="https://ksef-test.mf.gov.pl" target="_blank" rel="noreferrer" style={{color:TA.accent}}>ksef-test.mf.gov.pl</a></div>
              <div>• <strong>Portal MF produkcyjny:</strong> <a href="https://ksef.mf.gov.pl" target="_blank" rel="noreferrer" style={{color:TA.accent}}>ksef.mf.gov.pl</a></div>
              <div>• Schemat faktur: <strong>FA(3)</strong> obowiązuje od 1 lutego 2026 r.</div>
            </div>
          </div>
        </div>
      )}

      {/* ── LISTA FAKTUR ── */}
      {tab2==="list"&&(
        <div>
          {/* Dashboard */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10,marginBottom:16}}>
            {[
              ["🧾","Wszystkich faktur",invoices.length,"#1d4ed8","#eff6ff"],
              ["✅","Wystawionych",invoices.filter(i=>i.status==="issued"||i.status==="paid").length,"#15803d","#f0fdf4"],
              ["💰","Zapłacono",fmtPLN(totalPaid),"#92400e","#fffbeb"],
              ["📊","Suma brutto",fmtPLN(totalIssued),"#6b21a8","#f5f3ff"],
            ].map(([icon,label,val,col,bg])=>(
              <div key={label} style={{background:bg,border:"1.5px solid "+col+"33",borderRadius:12,padding:"12px 14px",textAlign:"center"}}>
                <div style={{fontSize:22,marginBottom:4}}>{icon}</div>
                <div style={{fontWeight:900,fontSize:typeof val==="string"?14:22,color:col,lineHeight:1}}>{val}</div>
                <div style={{fontSize:10,color:TA.muted,marginTop:3,fontWeight:600}}>{label}</div>
              </div>
            ))}
          </div>

          {/* Status API */}
          {(()=>{
            const { configured } = ksefApi();
            const s = settings.ksefSeller||{};
            return configured ? (
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"9px 14px",
                background:"#f5f3ff",borderRadius:10,border:"1px solid #c4b5fd",fontSize:12}}>
                <span style={{fontWeight:700,color:"#6b21a8"}}>🔗 API ksefapi.pl aktywne</span>
                <span style={{color:TA.muted}}>·</span>
                <span style={{color:s.apiEnv==="prod"?"#dc2626":"#15803d",fontWeight:600}}>{s.apiEnv==="prod"?"🚀 Produkcja":"🧪 Środowisko testowe"}</span>
                {s.ksefToken?<span style={{color:"#15803d",fontWeight:600}}>· ✅ Token KSeF</span>:<span style={{color:"#d97706",fontWeight:600}}>· ⚠ Brak tokenu KSeF</span>}
                <button onClick={()=>setTab2("config")} style={{...S.smBtn,marginLeft:"auto"}}>⚙ Konfiguruj</button>
              </div>
            ) : (
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,padding:"9px 14px",
                background:"#fff7ed",borderRadius:10,border:"1px solid #fed7aa",fontSize:12}}>
                <span style={{fontWeight:700,color:"#9a3412"}}>⚠ API nie skonfigurowane — tylko eksport XML</span>
                <button onClick={()=>setTab2("config")} style={{...S.smBtn,marginLeft:"auto",background:"#fff",color:"#9a3412",borderColor:"#fed7aa"}}>⚙ Dodaj klucze API</button>
              </div>
            );
          })()}

          {/* Import z zamówień — priorytet: zamówienia z żądaniem faktury */}
          {ordersWithInvReq.length>0&&(
            <div style={{...S.formCard,background:"#f5f3ff",borderColor:"#a78bfa",border:"2px solid #a78bfa"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <span style={{fontSize:20}}>🔔</span>
                <div>
                  <div style={{fontWeight:800,fontSize:13,color:"#6b21a8"}}>Zamówienia z żądaniem faktury ({ordersWithInvReq.length})</div>
                  <div style={{fontSize:11,color:"#7c3aed"}}>Klient/sprzedawca zaznaczył opcję "Wystaw fakturę" przy składaniu zamówienia</div>
                </div>
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {ordersWithInvReq.map(o=>(
                  <button key={o.id}
                    onClick={()=>{
                      // Pre-wypełnij formularz danymi z invoiceRequest
                      const req = o.invoiceRequest || {};
                      const now2 = new Date();
                      const y2=now2.getFullYear(), m2=now2.getMonth()+1;
                      const iso2=now2.toISOString().slice(0,10);
                      const due2=new Date(now2); due2.setDate(due2.getDate()+14);
                      let existingInvs2=[];
                      try{existingInvs2=JSON.parse(localStorage.getItem("smakidomu_ksef_invoices")||"[]");}catch(e){}
                      const lines=(o.cart||[]).map(c=>({
                        id:genId(), name:c.name||"Produkt", unit:"szt.",
                        qty:String(c.qty||1), price:String((c.effectivePrice??c.price)||0),
                        vat:req.vatRate||"8"
                      }));
                      setForm({
                        ...BLANK_INVOICE,
                        id:genId(),
                        number:nextInvoiceNumber(existingInvs2,y2,m2),
                        issueDate:iso2, saleDate:iso2,
                        dueDate:due2.toISOString().slice(0,10),
                        buyerName:req.buyerName||"",
                        buyerNip:req.buyerNip||"",
                        buyerAddress:req.buyerAddress||"",
                        paymentMethod:o.paymentMethod==="cash"?"gotówka":o.paymentMethod==="card"?"karta":"blik",
                        lines:lines.length?lines:[{...BLANK_INV_LINE,id:genId()}],
                        linkedOrderId:o.id,
                        status:"draft",
                        notes:`Zamówienie #${o.tableNumber||o.table||""} · ${o.date||""}`,
                      });
                      setTab2("form");
                    }}
                    style={{padding:"8px 14px",background:"#fff",border:"2px solid #a78bfa",borderRadius:10,
                      fontSize:12,cursor:"pointer",fontWeight:700,color:"#6b21a8",
                      display:"flex",flexDirection:"column",alignItems:"flex-start",gap:2}}>
                    <span>🧾 #{o.tableNumber||o.table} · {o.date} · <strong>{fmtPLN(o.total||0)}</strong></span>
                    {o.invoiceRequest?.buyerName&&<span style={{fontSize:10,color:"#7c3aed",fontWeight:600}}>Nabywca: {o.invoiceRequest.buyerName}{o.invoiceRequest.buyerNip?" · NIP "+o.invoiceRequest.buyerNip:""}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Import z zamówień — pozostałe opłacone */}
          {ordersForInvoice.length>0&&(
            <div style={{...S.formCard,background:"#f0fdf4",borderColor:"#86efac"}}>
              <div style={{fontWeight:700,fontSize:13,color:"#15803d",marginBottom:8}}>💡 Inne opłacone zamówienia bez faktury ({ordersForInvoice.length})</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {ordersForInvoice.slice(0,6).map(o=>(
                  <button key={o.id} onClick={()=>openNew(o)}
                    style={{padding:"7px 12px",background:"#fff",border:"1.5px solid #86efac",borderRadius:8,fontSize:12,cursor:"pointer",fontWeight:600,color:"#15803d"}}>
                    🧾 #{o.tableNumber||o.table} · {o.date} · {fmtPLN(o.total||0)}
                  </button>
                ))}
                {ordersForInvoice.length>6&&<span style={{fontSize:11,color:TA.muted,alignSelf:"center"}}>+{ordersForInvoice.length-6} więcej</span>}
              </div>
            </div>
          )}

          {/* Filtry */}
          <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
            {[["all","Wszystkie"],["draft","Szkice"],["issued","Wystawione"],["paid","Zapłacone"],["cancelled","Anulowane"]].map(([k,l])=>(
              <button key={k} onClick={()=>setFilterSt(k)}
                style={{...S.pill,...(filterSt===k?S.pillOn:{})}}>{l}</button>
            ))}
            <input style={{...S.inp,flex:1,minWidth:160,marginLeft:"auto"}}
              placeholder="🔍 Szukaj po numerze lub nazwie..."
              value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>

          {/* Pasek masowego usuwania — tylko admin/superadmin */}
          {canDelete && filtered.length > 0 && (
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",marginBottom:10,
              background:"#fafafa",borderRadius:9,border:"1px solid "+TA.border,flexWrap:"wrap"}}>
              <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontSize:12,fontWeight:600,color:TA.muted,userSelect:"none"}}>
                <input type="checkbox"
                  checked={filtered.length > 0 && filtered.every(i => selectedIds.has(i.id))}
                  onChange={e => {
                    if (e.target.checked) setSelectedIds(new Set(filtered.map(i=>i.id)));
                    else setSelectedIds(new Set());
                  }}
                  style={{width:15,height:15,cursor:"pointer"}}/>
                Zaznacz wszystkie ({filtered.length})
              </label>
              {selectedIds.size > 0 && (
                <>
                  <span style={{fontSize:12,color:TA.muted}}>·</span>
                  <span style={{fontSize:12,fontWeight:700,color:"#dc2626"}}>Zaznaczono: {selectedIds.size}</span>
                  <button onClick={deleteSelected}
                    style={{padding:"5px 14px",background:"#ffebee",color:"#dc2626",border:"1.5px solid #fca5a5",
                      borderRadius:7,fontWeight:700,cursor:"pointer",fontSize:12}}>
                    🗑 Usuń zaznaczone
                  </button>
                  <button onClick={()=>setSelectedIds(new Set())}
                    style={{...S.cancelBtn,padding:"5px 12px",fontSize:11}}>Odznacz</button>
                </>
              )}
              {isSuperAdminKS && invoices.length > 0 && (
                <button onClick={clearAll}
                  style={{marginLeft:"auto",padding:"5px 12px",background:"#fff0f0",color:"#991b1b",
                    border:"1.5px solid #fca5a5",borderRadius:7,fontWeight:700,cursor:"pointer",fontSize:11}}>
                  🛡 Wyczyść archiwum
                </button>
              )}
            </div>
          )}

          {filtered.length===0
            ? <div style={S.empty}><div style={{fontSize:40}}>🧾</div><p>Brak faktur w tym filtrze</p><button onClick={()=>openNew()} style={{...S.darkBtn,marginTop:10}}>+ Wystaw pierwszą fakturę</button></div>
            : filtered.map(inv=>{
              const { grossTotal } = calcInvoice(inv.lines||[]);
              const isSending = sending[inv.id];
              const hasApi = ksefApi().configured;
              return (
                <div key={inv.id} className="admin-card" style={{...S.card,borderLeft:"4px solid "+(STATUS_COL[inv.status]||TA.border)}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:10,flexWrap:"wrap"}}>
                    {/* Checkbox masowego usuwania — tylko admin/superadmin */}
                    {canDelete && (
                      <div style={{paddingTop:2,flexShrink:0}}>
                        <input type="checkbox"
                          checked={selectedIds.has(inv.id)}
                          onChange={e => setSelectedIds(p => {
                            const n = new Set(p);
                            e.target.checked ? n.add(inv.id) : n.delete(inv.id);
                            return n;
                          })}
                          style={{width:15,height:15,cursor:"pointer",marginTop:2}}/>
                      </div>
                    )}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <div style={{fontWeight:800,fontSize:14,color:TA.text}}>{inv.number||"—"}</div>
                        <span style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,
                          background:(STATUS_COL[inv.status]||"#aaa")+"22",color:STATUS_COL[inv.status]||TA.muted}}>
                          {STATUS_LABEL[inv.status]||inv.status}
                        </span>
                        {inv.ksefNumber&&inv.ksefNumber!=="—"&&(
                          <span style={{padding:"2px 8px",borderRadius:20,fontSize:10,fontWeight:700,background:"#f5f3ff",color:"#6b21a8",border:"1px solid #c4b5fd"}}>
                            KSeF: {inv.ksefNumber}
                          </span>
                        )}
                        {inv.ksefEnv&&(
                          <span style={{fontSize:10,color:TA.muted}}>{inv.ksefEnv==="prod"?"🚀 prod":"🧪 test"}</span>
                        )}
                      </div>
                      <div style={{fontSize:12,color:TA.muted,marginTop:3}}>
                        {inv.buyerName||"—"} · {fmtDate(inv.issueDate)} · <strong>{fmtPLN(grossTotal)}</strong>
                        {inv.ksefSentAt&&<span style={{marginLeft:8,color:"#6b21a8"}}>· wysłano {inv.ksefSentAt}</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:5,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
                      {inv.status==="draft"&&<button onClick={()=>openEdit(inv)} style={S.actBtn}>✏</button>}
                      <button onClick={()=>setPreviewInv(inv)} style={S.actBtn}>👁</button>
                      <button onClick={()=>printInvoice(inv)} style={S.actBtn} title="Drukuj">🖨</button>
                      <button onClick={()=>downloadXml(inv)} style={S.actBtn} title="Pobierz XML KSeF">📥 XML</button>
                      {/* Przyciski API */}
                      {hasApi&&(inv.status==="draft"||inv.status==="issued")&&!inv.ksefNumber&&(
                        <button
                          disabled={!!isSending}
                          onClick={()=>validateInvoice(inv)}
                          style={{...S.actBtn,background:"#f5f3ff",color:"#6b21a8",fontWeight:700,opacity:isSending?0.6:1}}>
                          {isSending==="validating"?"⏳ Walidacja...":"🔍 Waliduj"}
                        </button>
                      )}
                      {hasApi&&(inv.status==="draft"||inv.status==="issued")&&!inv.ksefNumber&&(
                        <button
                          disabled={!!isSending}
                          onClick={()=>sendToKSeF(inv)}
                          style={{...S.actBtn,background:"#ede9fe",color:"#5b21b6",fontWeight:800,border:"1.5px solid #a78bfa",opacity:isSending?0.6:1}}>
                          {isSending==="sending"?"⏳ Wysyłanie..."
                           :isSending==="upo"?"⏳ Pobieranie UPO..."
                           :"🚀 Wyślij do KSeF"}
                        </button>
                      )}
                      {inv.ksefNumber&&inv.ksefNumber!=="—"&&hasApi&&(
                        <button
                          disabled={!!isSending}
                          onClick={()=>checkKSeFStatus(inv)}
                          style={{...S.actBtn,background:"#f5f3ff",color:"#6b21a8",opacity:isSending?0.6:1}}>
                          {isSending==="status"?"⏳":"📡 Status"}
                        </button>
                      )}
                      {inv.upoUrl&&(
                        <a href={inv.upoUrl} download={"UPO-"+(inv.number||"").replace(/\//g,"-")+".pdf"}
                          style={{...S.actBtn,textDecoration:"none",background:"#f0fdf4",color:"#15803d",fontWeight:700}}>
                          📄 UPO
                        </a>
                      )}
                      {inv.status==="draft"&&<button onClick={()=>{setInvoices(p=>p.map(x=>x.id===inv.id?{...x,status:"issued"}:x));showMsg("✅ Faktura wystawiona!");}} style={{...S.actBtn,background:"#eff6ff",color:"#2563eb",fontWeight:700}}>Wystaw</button>}
                      {inv.status==="issued"&&<button onClick={()=>{setInvoices(p=>p.map(x=>x.id===inv.id?{...x,status:"paid"}:x));showMsg("💰 Oznaczono jako zapłaconą");}} style={{...S.actBtn,background:"#f0fdf4",color:"#16a34a",fontWeight:700}}>Zapłacona</button>}
                      {inv.status!=="cancelled"&&<button onClick={()=>{if(window.confirm("Anulować fakturę?"))setInvoices(p=>p.map(x=>x.id===inv.id?{...x,status:"cancelled"}:x));}} style={{...S.actBtn,background:"#ffebee",color:"#dc2626"}}>✕</button>}
                      {/* Przycisk trwałego usunięcia — tylko admin i superadmin */}
                      {canDelete&&(
                        <button
                          onClick={()=>deleteInvoice(inv)}
                          title={isSuperAdminKS?"Usuń fakturę (Superadmin)":"Usuń fakturę (Admin)"}
                          style={{...S.actBtn,background:"#dc2626",color:"#fff",fontWeight:700,
                            border:"none",padding:"6px 10px",
                            boxShadow:"0 1px 4px rgba(220,38,38,0.25)"}}>
                          🗑
                          {isSuperAdminKS&&<span style={{fontSize:9,verticalAlign:"super",marginLeft:1}}>🛡</span>}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      )}

      {/* ── FORMULARZ FAKTURY ── */}
      {tab2==="form"&&form&&(()=>{
        const { netTotal, vatTotal, grossTotal } = calcInvoice(form.lines||[]);
        return (
          <div>
            <div style={S.formCard}>
              <div style={S.formTitle}>📋 Nagłówek faktury</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <label style={S.label}>Numer faktury</label>
                  <input style={S.inp} value={form.number} onChange={e=>setForm(f=>({...f,number:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Data wystawienia</label>
                  <input style={S.inp} type="date" value={form.issueDate} onChange={e=>setForm(f=>({...f,issueDate:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Data sprzedaży</label>
                  <input style={S.inp} type="date" value={form.saleDate||form.issueDate} onChange={e=>setForm(f=>({...f,saleDate:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Termin płatności</label>
                  <input style={S.inp} type="date" value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>Forma płatności</label>
                  <select style={S.inp} value={form.paymentMethod} onChange={e=>setForm(f=>({...f,paymentMethod:e.target.value}))}>
                    {["przelew","gotówka","karta","blik"].map(v=><option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div style={S.formCard}>
              <div style={S.formTitle}>👤 Nabywca</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                {(clients||[]).length>0&&(
                  <div style={{gridColumn:"span 2"}}>
                    <label style={S.label}>Wybierz z kontrahentów</label>
                    <select style={S.inp} value="" onChange={e=>{
                      const cl=(clients||[]).find(c=>c.id===e.target.value);
                      if(cl) setForm(f=>({...f,buyerName:cl.company||cl.name,buyerNip:cl.nip||"",buyerAddress:cl.address||""}));
                    }}>
                      <option value="">— wybierz kontrahenta —</option>
                      {(clients||[]).map(c=><option key={c.id} value={c.id}>{c.company||c.name}{c.nip?" (NIP: "+c.nip+")":""}</option>)}
                    </select>
                  </div>
                )}
                <div style={{gridColumn:"span 2"}}>
                  <label style={S.label}>Nazwa nabywcy *</label>
                  <input style={S.inp} placeholder="Firma Sp. z o.o." value={form.buyerName} onChange={e=>setForm(f=>({...f,buyerName:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>NIP nabywcy</label>
                  <input style={S.inp} placeholder="0000000000" value={form.buyerNip} onChange={e=>setForm(f=>({...f,buyerNip:e.target.value.replace(/\D/g,"").slice(0,10)}))}/>
                </div>
                <div>
                  <label style={S.label}>Adres nabywcy</label>
                  <input style={S.inp} placeholder="ul. Przykładowa 1, 00-001 Warszawa" value={form.buyerAddress} onChange={e=>setForm(f=>({...f,buyerAddress:e.target.value}))}/>
                </div>
              </div>
            </div>

            <div style={S.formCard}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={S.formTitle}>🛒 Pozycje faktury</div>
                <button onClick={addLine} style={{...S.darkBtn,padding:"6px 12px",fontSize:12}}>+ Dodaj pozycję</button>
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead><tr style={{background:"#f9fafb"}}>
                    {["#","Nazwa / opis","J.m.","Ilość","Cena netto","Stawka VAT","Netto","VAT","Brutto",""].map(h=>(
                      <th key={h} style={{padding:"6px 8px",textAlign:"left",borderBottom:"2px solid "+TA.border,fontWeight:700,color:TA.muted,fontSize:11,whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {form.lines.map((ln,i)=>{
                      const qty=parseFloat(ln.qty)||0, price=parseFloat(ln.price)||0;
                      const rate=ln.vat==="zw"?0:(parseFloat(ln.vat)||0);
                      const net=qty*price, vat=net*rate/100, gross=net+vat;
                      return (
                        <tr key={ln.id} style={{borderBottom:"1px solid "+TA.border+"66"}}>
                          <td style={{padding:"5px 8px",color:TA.muted}}>{i+1}</td>
                          <td style={{padding:"4px 6px"}}><input style={{...S.inp,minWidth:140}} value={ln.name} onChange={e=>setLine(i,"name",e.target.value)} placeholder="Nazwa produktu/usługi"/></td>
                          <td style={{padding:"4px 6px"}}><input style={{...S.inp,width:52}} value={ln.unit} onChange={e=>setLine(i,"unit",e.target.value)}/></td>
                          <td style={{padding:"4px 6px"}}><input style={{...S.inp,width:60}} type="number" min="0" step="0.01" value={ln.qty} onChange={e=>setLine(i,"qty",e.target.value)}/></td>
                          <td style={{padding:"4px 6px"}}><input style={{...S.inp,width:80}} type="number" min="0" step="0.01" value={ln.price} onChange={e=>setLine(i,"price",e.target.value)} placeholder="0.00"/></td>
                          <td style={{padding:"4px 6px"}}>
                            <select style={{...S.inp,width:68}} value={ln.vat} onChange={e=>setLine(i,"vat",e.target.value)}>
                              {VAT_RATES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                          </td>
                          <td style={{padding:"5px 8px",fontWeight:600,whiteSpace:"nowrap"}}>{net.toFixed(2)}</td>
                          <td style={{padding:"5px 8px",color:TA.muted,whiteSpace:"nowrap"}}>{vat.toFixed(2)}</td>
                          <td style={{padding:"5px 8px",fontWeight:700,color:TA.accent,whiteSpace:"nowrap"}}>{gross.toFixed(2)}</td>
                          <td style={{padding:"4px 6px"}}>{form.lines.length>1&&<button onClick={()=>delLine(i)} style={{...S.actBtn,background:"#ffebee",color:"#dc2626",padding:"4px 8px"}}>✕</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Podsumowanie */}
              <div style={{marginTop:14,display:"flex",justifyContent:"flex-end"}}>
                <table style={{fontSize:13,borderCollapse:"collapse"}}>
                  <tbody>
                    <tr><td style={{padding:"4px 12px",color:TA.muted}}>Razem netto:</td><td style={{padding:"4px 12px",fontWeight:600,textAlign:"right"}}>{netTotal.toFixed(2)} zł</td></tr>
                    <tr><td style={{padding:"4px 12px",color:TA.muted}}>Razem VAT:</td><td style={{padding:"4px 12px",fontWeight:600,textAlign:"right"}}>{vatTotal.toFixed(2)} zł</td></tr>
                    <tr style={{background:ac+"11",borderTop:"2px solid "+ac}}>
                      <td style={{padding:"8px 12px",fontWeight:800,fontSize:15,color:ac}}>DO ZAPŁATY:</td>
                      <td style={{padding:"8px 12px",fontWeight:900,fontSize:17,color:ac,textAlign:"right"}}>{grossTotal.toFixed(2)} zł</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={S.formCard}>
              <div style={S.formTitle}>📝 Uwagi</div>
              <textarea style={{...S.inp,height:70,resize:"vertical"}} placeholder="Opcjonalne uwagi do faktury..."
                value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
            </div>

            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <button onClick={()=>saveInvoice("draft")} style={S.cancelBtn}>💾 Zapisz szkic</button>
              <button onClick={()=>saveInvoice("issued")} style={{...S.darkBtn,background:"#16a34a"}}>✅ Wystaw fakturę</button>
              {ksefApi().configured&&(
                <>
                  <button onClick={async()=>{
                    const ok=await validateInvoice(form);
                    if(ok) showMsg("✅ Faktura poprawna — możesz ją wysłać do KSeF");
                  }} disabled={!!sending[form.id]}
                  style={{...S.darkBtn,background:"#6366f1",opacity:sending[form.id]?0.6:1}}>
                    {sending[form.id]==="validating"?"⏳ Walidacja...":"🔍 Waliduj XML"}
                  </button>
                  <button onClick={async()=>{
                    saveInvoice("issued");
                    setTimeout(()=>sendToKSeF(form),300);
                  }} disabled={!!sending[form.id]}
                  style={{...S.darkBtn,background:"#5b21b6",opacity:sending[form.id]?0.6:1}}>
                    {sending[form.id]==="sending"?"⏳ Wysyłanie..."
                     :sending[form.id]==="upo"?"⏳ Pobieranie UPO..."
                     :"🚀 Wystaw i wyślij do KSeF"}
                  </button>
                </>
              )}
              <button onClick={()=>{setTab2("list");setForm(null);}} style={{...S.cancelBtn,marginLeft:"auto"}}>Anuluj</button>
            </div>
          </div>
        );
      })()}

      {/* ── PODGLĄD FAKTURY ── */}
      {previewInv&&(
        <div style={S.backdrop} onClick={()=>setPreviewInv(null)}>
          <div style={{...S.modalBox,maxWidth:720,maxHeight:"92vh"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
              <div style={{fontWeight:800,fontSize:15,color:TA.text}}>🧾 {previewInv.number}</div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>printInvoice(previewInv)} style={S.actBtn}>🖨 Drukuj</button>
                <button onClick={()=>downloadXml(previewInv)} style={{...S.actBtn,color:TA.accent,fontWeight:700}}>📥 XML</button>
                <button onClick={()=>setPreviewInv(null)} style={S.closeBtn}>✕</button>
              </div>
            </div>
            {/* Dane nagłówkowe */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
              <div style={{background:"#f9fafb",borderRadius:10,padding:"10px 12px",border:"1px solid "+TA.border}}>
                <div style={{fontSize:10,fontWeight:700,color:TA.muted,marginBottom:4}}>SPRZEDAWCA</div>
                <div style={{fontWeight:700,fontSize:13}}>{(settings.ksefSeller||{}).name||settings.restaurantName}</div>
                <div style={{fontSize:12,color:TA.muted}}>NIP: {(settings.ksefSeller||{}).nip||"—"}</div>
                <div style={{fontSize:12,color:TA.muted}}>{(settings.ksefSeller||{}).address||""}</div>
              </div>
              <div style={{background:"#f9fafb",borderRadius:10,padding:"10px 12px",border:"1px solid "+TA.border}}>
                <div style={{fontSize:10,fontWeight:700,color:TA.muted,marginBottom:4}}>NABYWCA</div>
                <div style={{fontWeight:700,fontSize:13}}>{previewInv.buyerName||"—"}</div>
                {previewInv.buyerNip&&<div style={{fontSize:12,color:TA.muted}}>NIP: {previewInv.buyerNip}</div>}
                <div style={{fontSize:12,color:TA.muted}}>{previewInv.buyerAddress}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
              {[["Data wystawienia",fmtDate(previewInv.issueDate)],["Data sprzedaży",fmtDate(previewInv.saleDate||previewInv.issueDate)],["Termin płatności",fmtDate(previewInv.dueDate)],["Płatność",previewInv.paymentMethod]].map(([l,v])=>(
                <div key={l} style={{background:"#eff6ff",borderRadius:8,padding:"6px 12px",fontSize:12}}>
                  <span style={{color:TA.muted}}>{l}: </span><strong>{v}</strong>
                </div>
              ))}
            </div>
            {/* Pozycje */}
            <div style={{overflowX:"auto",marginBottom:14}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead><tr style={{background:"#f3f4f6"}}>
                  {["#","Nazwa","J.m.","Ilość","Cena netto","Netto","VAT","VAT kwota","Brutto"].map(h=>(
                    <th key={h} style={{padding:"6px 8px",textAlign:"left",borderBottom:"2px solid "+TA.border,fontSize:11,color:TA.muted}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(previewInv.lines||[]).map((ln,i)=>{
                    const qty=parseFloat(ln.qty)||0,price=parseFloat(ln.price)||0;
                    const rate=ln.vat==="zw"?0:(parseFloat(ln.vat)||0);
                    const net=qty*price,vat=net*rate/100;
                    return (
                      <tr key={i} style={{borderBottom:"1px solid "+TA.border+"44"}}>
                        <td style={{padding:"6px 8px",color:TA.muted}}>{i+1}</td>
                        <td style={{padding:"6px 8px",fontWeight:600}}>{ln.name}</td>
                        <td style={{padding:"6px 8px",color:TA.muted}}>{ln.unit}</td>
                        <td style={{padding:"6px 8px"}}>{qty}</td>
                        <td style={{padding:"6px 8px"}}>{price.toFixed(2)}</td>
                        <td style={{padding:"6px 8px"}}>{net.toFixed(2)}</td>
                        <td style={{padding:"6px 8px"}}>{ln.vat==="zw"?"ZW":ln.vat+"%"}</td>
                        <td style={{padding:"6px 8px"}}>{vat.toFixed(2)}</td>
                        <td style={{padding:"6px 8px",fontWeight:700,color:TA.accent}}>{(net+vat).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Suma */}
            {(()=>{const {netTotal,vatTotal,grossTotal}=calcInvoice(previewInv.lines||[]);return(
              <div style={{display:"flex",justifyContent:"flex-end"}}>
                <div style={{background:"#f0fdf4",border:"2px solid #86efac",borderRadius:12,padding:"12px 20px",minWidth:200,textAlign:"right"}}>
                  <div style={{fontSize:12,color:TA.muted}}>Netto: {netTotal.toFixed(2)} zł · VAT: {vatTotal.toFixed(2)} zł</div>
                  <div style={{fontWeight:900,fontSize:20,color:"#16a34a",marginTop:4}}>DO ZAPŁATY: {grossTotal.toFixed(2)} zł</div>
                </div>
              </div>
            );})()}
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════ HARDWARE PANEL ═══════════════════════════════════════
   Konfiguracja kas fiskalnych, drukarek i sterowników.
══════════════════════════════════════════════════════════════════════════════ */

const FISCAL_DRIVERS = [
  { id:"novitus",   name:"Novitus",        models:["Next","HD E","Nano E","Link Online","System HD E"] },
  { id:"posnet",    name:"Posnet",         models:["Thermal HD FV EJ","Temo HS EJ","Mobile HS EJ","PS3000"] },
  { id:"elzab",     name:"ELZAB",          models:["Mera","Oscar","Omega F EJ","Mini LT","K10"] },
  { id:"thermal",   name:"Thermal / ESC/POS", models:["Generic ESC/POS","Star TSP100","Bixolon SRP-350","Epson TM-T88"] },
  { id:"brother",   name:"Brother",        models:["QL-800","QL-1100","TD-4420DN","TD-4550DNWB"] },
  { id:"zebra",     name:"Zebra",          models:["ZD421","ZD620","GK420d","ZT411"] },
  { id:"custom",    name:"Inny (ręczny)",  models:["Konfiguracja ręczna"] },
];

const PRINTER_TYPES = [
  { id:"receipt",   label:"🧾 Drukarka paragonów",   icon:"🧾" },
  { id:"kitchen",   label:"🍳 Drukarka kuchenna",     icon:"🍳" },
  { id:"label",     label:"🏷 Drukarka etykiet",      icon:"🏷" },
  { id:"fiscal",    label:"🏦 Kasa fiskalna",         icon:"🏦" },
];

const CONN_TYPES = [
  { id:"usb",       label:"USB",           icon:"🔌" },
  { id:"network",   label:"Sieć (TCP/IP)", icon:"🌐" },
  { id:"serial",    label:"COM / Serial",  icon:"⚡" },
  { id:"bluetooth", label:"Bluetooth",     icon:"📶" },
];

const BLANK_DEVICE = {
  id: null, name: "", type: "receipt", driver: "thermal", model: "",
  connection: "usb", ipAddress: "", port: "9100", comPort: "COM1",
  baudRate: "9600", paperWidth: "80", enabled: true, isDefault: false,
  cutPaper: true, openDrawer: false, printLogo: false, charEncoding: "cp1250",
  note: "",
};

const BLANK_FISCAL = {
  id: null, name: "", driver: "novitus", model: "", serialNumber: "",
  nipFirmy: "", connection: "usb", ipAddress: "", port: "51000", comPort: "COM1",
  baudRate: "57600", enabled: true, isDefault: true,
  printReceipt: true, printDuplicate: false, openDrawer: true,
  vatRates: { a:"23", b:"8", c:"5", d:"0" },
  note: "",
};

function HardwarePanel({ settings, setSettings }) {
  const hw = settings.hardware || { devices: [], fiscals: [] };
  const devices = hw.devices || [];
  const fiscals = hw.fiscals || [];

  const setHw = updater => setSettings(p => {
    const prev = p.hardware || { devices: [], fiscals: [] };
    return { ...p, hardware: typeof updater === "function" ? updater(prev) : updater };
  });

  const [tab, setTab] = useState("fiscal"); // "fiscal" | "printer" | "drivers"
  const [editDevice, setEditDevice] = useState(null);
  const [editFiscal, setEditFiscal] = useState(null);
  const [testMsg, setTestMsg] = useState({});
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [showAddFiscal, setShowAddFiscal] = useState(false);
  const [newDevice, setNewDevice] = useState({...BLANK_DEVICE});
  const [newFiscal, setNewFiscal] = useState({...BLANK_FISCAL});
  const [msg, setMsg] = useState(null);

  const showMsg = (text, type="ok") => { setMsg({text, type}); setTimeout(()=>setMsg(null), 3500); };

  const genId = () => Math.random().toString(36).slice(2, 10);

  const addFiscal = () => {
    if (!newFiscal.name.trim()) { alert("Wpisz nazwę kasy fiskalnej"); return; }
    const f = { ...newFiscal, id: genId() };
    setHw(p => ({ ...p, fiscals: [...(p.fiscals||[]), f] }));
    setNewFiscal({...BLANK_FISCAL});
    setShowAddFiscal(false);
    showMsg("✅ Kasa fiskalna dodana");
  };

  const saveFiscal = () => {
    setHw(p => ({ ...p, fiscals: (p.fiscals||[]).map(f => f.id === editFiscal.id ? editFiscal : f) }));
    setEditFiscal(null);
    showMsg("✅ Zmiany zapisane");
  };

  const deleteFiscal = id => {
    if (!window.confirm("Usunąć kasę fiskalną?")) return;
    setHw(p => ({ ...p, fiscals: (p.fiscals||[]).filter(f => f.id !== id) }));
    showMsg("🗑 Kasa usunięta");
  };

  const addDevice = () => {
    if (!newDevice.name.trim()) { alert("Wpisz nazwę drukarki"); return; }
    const d = { ...newDevice, id: genId() };
    setHw(p => ({ ...p, devices: [...(p.devices||[]), d] }));
    setNewDevice({...BLANK_DEVICE});
    setShowAddDevice(false);
    showMsg("✅ Drukarka dodana");
  };

  const saveDevice = () => {
    setHw(p => ({ ...p, devices: (p.devices||[]).map(d => d.id === editDevice.id ? editDevice : d) }));
    setEditDevice(null);
    showMsg("✅ Zmiany zapisane");
  };

  const deleteDevice = id => {
    if (!window.confirm("Usunąć drukarkę?")) return;
    setHw(p => ({ ...p, devices: (p.devices||[]).filter(d => d.id !== id) }));
  };

  const testConnection = (deviceId, type) => {
    setTestMsg(p => ({ ...p, [deviceId]: "⏳ Testowanie..." }));
    setTimeout(() => {
      // Simulate test
      const ok = Math.random() > 0.3;
      setTestMsg(p => ({ ...p, [deviceId]: ok ? "✅ Połączono pomyślnie!" : "❌ Brak odpowiedzi urządzenia" }));
      setTimeout(() => setTestMsg(p => { const n={...p}; delete n[deviceId]; return n; }), 3000);
    }, 1500);
  };

  const FiscalForm = ({ f, setF }) => {
    const drv = FISCAL_DRIVERS.find(d => d.id === f.driver) || FISCAL_DRIVERS[0];
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div style={{gridColumn:"span 2"}}>
            <label style={S.label}>Nazwa kasy *</label>
            <input style={S.inp} value={f.name} onChange={e=>setF(p=>({...p,name:e.target.value}))} placeholder="np. Kasa główna"/>
          </div>
          <div>
            <label style={S.label}>Producent / sterownik</label>
            <select style={S.inp} value={f.driver} onChange={e=>setF(p=>({...p,driver:e.target.value,model:""}))}>
              {FISCAL_DRIVERS.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Model</label>
            <select style={S.inp} value={f.model} onChange={e=>setF(p=>({...p,model:e.target.value}))}>
              <option value="">— wybierz model —</option>
              {drv.models.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Numer seryjny</label>
            <input style={S.inp} value={f.serialNumber} onChange={e=>setF(p=>({...p,serialNumber:e.target.value}))} placeholder="np. 123456789"/>
          </div>
          <div>
            <label style={S.label}>NIP firmy (kasa)</label>
            <input style={S.inp} value={f.nipFirmy} onChange={e=>setF(p=>({...p,nipFirmy:e.target.value}))} placeholder="000-000-00-00"/>
          </div>
        </div>

        <div style={{...S.formTitle,marginBottom:10}}>🔌 Połączenie</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <label style={S.label}>Typ połączenia</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {CONN_TYPES.map(c=>(
                <button key={c.id} onClick={()=>setF(p=>({...p,connection:c.id}))}
                  style={{padding:"6px 12px",borderRadius:7,border:`1.5px solid ${f.connection===c.id?TA.accent:TA.border}`,
                    background:f.connection===c.id?TA.accentSoft:"#fff",color:f.connection===c.id?TA.accent:TA.muted,
                    fontSize:12,cursor:"pointer",fontWeight:f.connection===c.id?700:400}}>
                  {c.icon} {c.label}
                </button>
              ))}
            </div>
          </div>
          {f.connection==="network" && (
            <>
              <div>
                <label style={S.label}>Adres IP</label>
                <input style={S.inp} value={f.ipAddress} onChange={e=>setF(p=>({...p,ipAddress:e.target.value}))} placeholder="192.168.1.100"/>
              </div>
              <div>
                <label style={S.label}>Port TCP</label>
                <input style={S.inp} value={f.port} onChange={e=>setF(p=>({...p,port:e.target.value}))} placeholder="51000"/>
              </div>
            </>
          )}
          {(f.connection==="serial" || f.connection==="usb") && (
            <>
              <div>
                <label style={S.label}>Port COM</label>
                <select style={S.inp} value={f.comPort} onChange={e=>setF(p=>({...p,comPort:e.target.value}))}>
                  {["COM1","COM2","COM3","COM4","COM5","COM6","USB","/dev/ttyUSB0","/dev/ttyS0"].map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label style={S.label}>Prędkość (baud)</label>
                <select style={S.inp} value={f.baudRate} onChange={e=>setF(p=>({...p,baudRate:e.target.value}))}>
                  {["9600","19200","38400","57600","115200"].map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </>
          )}
        </div>

        <div style={{...S.formTitle,marginBottom:10}}>⚙ Opcje drukowania</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
          {[
            {key:"enabled",         label:"Aktywna"},
            {key:"isDefault",       label:"Domyślna"},
            {key:"printReceipt",    label:"Drukuj paragon"},
            {key:"printDuplicate",  label:"Drukuj duplikat"},
            {key:"openDrawer",      label:"Otwieraj szufladę"},
          ].map(opt=>(
            <div key={opt.key} onClick={()=>setF(p=>({...p,[opt.key]:!p[opt.key]}))}
              style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:8,
                background:f[opt.key]?TA.accentSoft:"#f9fafb",border:`1.5px solid ${f[opt.key]?TA.accent:TA.border}`,
                cursor:"pointer",transition:"all .15s"}}>
              <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${f[opt.key]?TA.accent:TA.border}`,
                background:f[opt.key]?TA.accent:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {f[opt.key]&&<span style={{color:"#fff",fontSize:10,fontWeight:900}}>✓</span>}
              </div>
              <span style={{fontSize:12,fontWeight:f[opt.key]?700:400,color:f[opt.key]?TA.accent:TA.muted}}>{opt.label}</span>
            </div>
          ))}
        </div>

        <div style={{...S.formTitle,marginBottom:10}}>🔢 Stawki VAT</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:10}}>
          {["a","b","c","d"].map(r=>(
            <div key={r}>
              <label style={S.label}>Stawka {r.toUpperCase()}</label>
              <div style={{display:"flex",alignItems:"center",gap:4}}>
                <input style={{...S.inp,textAlign:"center"}} value={(f.vatRates||{})[r]||""}
                  onChange={e=>setF(p=>({...p,vatRates:{...(p.vatRates||{}), [r]:e.target.value}}))}
                  placeholder="np. 23"/>
                <span style={{fontSize:11,color:TA.muted,flexShrink:0}}>%</span>
              </div>
            </div>
          ))}
        </div>

        <div>
          <label style={S.label}>Uwagi</label>
          <textarea style={{...S.inp,height:56,resize:"none"}} value={f.note}
            onChange={e=>setF(p=>({...p,note:e.target.value}))} placeholder="Opcjonalne uwagi..."/>
        </div>
      </div>
    );
  };

  const PrinterForm = ({ d, setD }) => {
    const drv = FISCAL_DRIVERS.find(x => x.id === d.driver) || FISCAL_DRIVERS[0];
    return (
      <div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
          <div style={{gridColumn:"span 2"}}>
            <label style={S.label}>Nazwa drukarki *</label>
            <input style={S.inp} value={d.name} onChange={e=>setD(p=>({...p,name:e.target.value}))} placeholder="np. Drukarnia kuchenna"/>
          </div>
          <div>
            <label style={S.label}>Typ drukarki</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {PRINTER_TYPES.filter(t=>t.id!=="fiscal").map(t=>(
                <button key={t.id} onClick={()=>setD(p=>({...p,type:t.id}))}
                  style={{padding:"6px 10px",borderRadius:7,border:`1.5px solid ${d.type===t.id?TA.accent:TA.border}`,
                    background:d.type===t.id?TA.accentSoft:"#fff",color:d.type===t.id?TA.accent:TA.muted,
                    fontSize:11,cursor:"pointer",fontWeight:d.type===t.id?700:400}}>
                  {t.icon} {t.label.replace(/🧾|🍳|🏷|🏦/g,"").trim()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={S.label}>Producent / sterownik</label>
            <select style={S.inp} value={d.driver} onChange={e=>setD(p=>({...p,driver:e.target.value,model:""}))}>
              {FISCAL_DRIVERS.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Model</label>
            <select style={S.inp} value={d.model} onChange={e=>setD(p=>({...p,model:e.target.value}))}>
              <option value="">— wybierz model —</option>
              {drv.models.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Szerokość papieru</label>
            <select style={S.inp} value={d.paperWidth} onChange={e=>setD(p=>({...p,paperWidth:e.target.value}))}>
              <option value="58">58mm</option>
              <option value="80">80mm (standard)</option>
              <option value="112">112mm</option>
            </select>
          </div>
          <div>
            <label style={S.label}>Kodowanie znaków</label>
            <select style={S.inp} value={d.charEncoding} onChange={e=>setD(p=>({...p,charEncoding:e.target.value}))}>
              <option value="cp1250">CP1250 (Windows PL)</option>
              <option value="utf8">UTF-8</option>
              <option value="iso88592">ISO-8859-2</option>
              <option value="cp852">CP852 (DOS PL)</option>
            </select>
          </div>
        </div>

        <div style={{...S.formTitle,marginBottom:10}}>🔌 Połączenie</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
          {CONN_TYPES.map(c=>(
            <button key={c.id} onClick={()=>setD(p=>({...p,connection:c.id}))}
              style={{padding:"6px 12px",borderRadius:7,border:`1.5px solid ${d.connection===c.id?TA.accent:TA.border}`,
                background:d.connection===c.id?TA.accentSoft:"#fff",color:d.connection===c.id?TA.accent:TA.muted,
                fontSize:12,cursor:"pointer",fontWeight:d.connection===c.id?700:400}}>
              {c.icon} {c.label}
            </button>
          ))}
        </div>
        {d.connection==="network" && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <label style={S.label}>Adres IP</label>
              <input style={S.inp} value={d.ipAddress} onChange={e=>setD(p=>({...p,ipAddress:e.target.value}))} placeholder="192.168.1.200"/>
            </div>
            <div>
              <label style={S.label}>Port</label>
              <input style={S.inp} value={d.port} onChange={e=>setD(p=>({...p,port:e.target.value}))} placeholder="9100"/>
            </div>
          </div>
        )}
        {(d.connection==="serial"||d.connection==="usb") && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <label style={S.label}>Port COM / USB</label>
              <select style={S.inp} value={d.comPort} onChange={e=>setD(p=>({...p,comPort:e.target.value}))}>
                {["COM1","COM2","COM3","COM4","USB","LPT1","/dev/usb/lp0","/dev/ttyUSB0"].map(v=><option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Baud rate</label>
              <select style={S.inp} value={d.baudRate} onChange={e=>setD(p=>({...p,baudRate:e.target.value}))}>
                {["9600","19200","38400","57600","115200"].map(v=><option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
        )}

        <div style={{...S.formTitle,marginBottom:10}}>⚙ Opcje</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
          {[
            {key:"enabled",     label:"Aktywna"},
            {key:"isDefault",   label:"Domyślna"},
            {key:"cutPaper",    label:"Ucinaj papier"},
            {key:"openDrawer",  label:"Otwieraj szufladę"},
            {key:"printLogo",   label:"Drukuj logo"},
          ].map(opt=>(
            <div key={opt.key} onClick={()=>setD(p=>({...p,[opt.key]:!p[opt.key]}))}
              style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:8,
                background:d[opt.key]?TA.accentSoft:"#f9fafb",border:`1.5px solid ${d[opt.key]?TA.accent:TA.border}`,
                cursor:"pointer",transition:"all .15s"}}>
              <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${d[opt.key]?TA.accent:TA.border}`,
                background:d[opt.key]?TA.accent:"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {d[opt.key]&&<span style={{color:"#fff",fontSize:10,fontWeight:900}}>✓</span>}
              </div>
              <span style={{fontSize:12,fontWeight:d[opt.key]?700:400,color:d[opt.key]?TA.accent:TA.muted}}>{opt.label}</span>
            </div>
          ))}
        </div>
        <div>
          <label style={S.label}>Uwagi</label>
          <textarea style={{...S.inp,height:56,resize:"none"}} value={d.note}
            onChange={e=>setD(p=>({...p,note:e.target.value}))} placeholder="Opcjonalne uwagi..."/>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:8}}>
        <div>
          <h2 style={{margin:0,fontSize:18,fontWeight:800,color:TA.text}}>🖨 Sprzęt — kasy i drukarki</h2>
          <div style={{fontSize:12,color:TA.muted,marginTop:2}}>Zarządzaj kasami fiskalnymi, drukarkami i sterownikami</div>
        </div>
        {msg && (
          <div style={{padding:"8px 14px",borderRadius:8,background:msg.type==="ok"?"#dcfce7":"#fee2e2",
            color:msg.type==="ok"?"#16a34a":"#dc2626",fontWeight:700,fontSize:13}}>
            {msg.text}
          </div>
        )}
      </div>

      {/* Sub-tabs */}
      <div style={{display:"flex",gap:6,marginBottom:16,borderBottom:"2px solid "+TA.border,paddingBottom:0}}>
        {[["fiscal","🏦 Kasy fiskalne"],["printer","🖨 Drukarki"],["drivers","📦 Sterowniki"]].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} style={{
            padding:"9px 16px",border:"none",background:"none",cursor:"pointer",fontWeight:tab===k?700:400,
            fontSize:13,borderBottom:tab===k?`2px solid ${TA.accent}`:"2px solid transparent",
            color:tab===k?TA.accent:TA.muted,marginBottom:-2
          }}>{l}</button>
        ))}
      </div>

      {/* ── KASY FISKALNE ── */}
      {tab==="fiscal" && (
        <div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}>
            <button onClick={()=>setShowAddFiscal(p=>!p)} style={{...S.darkBtn,display:"flex",alignItems:"center",gap:6}}>
              + Dodaj kasę fiskalną
            </button>
          </div>

          {showAddFiscal && (
            <div style={{...S.formCard,border:"2px solid "+TA.accent,marginBottom:16}}>
              <div style={S.formTitle}>➕ Nowa kasa fiskalna</div>
              <FiscalForm f={newFiscal} setF={setNewFiscal}/>
              <div style={{display:"flex",gap:8,marginTop:14}}>
                <button onClick={addFiscal} style={S.darkBtn}>💾 Dodaj</button>
                <button onClick={()=>setShowAddFiscal(false)} style={S.cancelBtn}>Anuluj</button>
              </div>
            </div>
          )}

          {fiscals.length === 0 && !showAddFiscal && (
            <div style={S.empty}>
              <div style={{fontSize:48,marginBottom:8}}>🏦</div>
              <div style={{fontWeight:700,marginBottom:4}}>Brak skonfigurowanych kas fiskalnych</div>
              <div style={{fontSize:12}}>Kliknij „Dodaj kasę fiskalną" aby skonfigurować pierwszą kasę.</div>
            </div>
          )}

          {fiscals.map(f => (
            <div key={f.id} style={{...S.card,border:`1.5px solid ${f.enabled?TA.border:"#fca5a5"}`}}>
              {editFiscal?.id === f.id ? (
                <div>
                  <div style={S.formTitle}>✏ Edycja: {f.name}</div>
                  <FiscalForm f={editFiscal} setF={setEditFiscal}/>
                  <div style={{display:"flex",gap:8,marginTop:14}}>
                    <button onClick={saveFiscal} style={S.darkBtn}>💾 Zapisz</button>
                    <button onClick={()=>setEditFiscal(null)} style={S.cancelBtn}>Anuluj</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:22}}>🏦</span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:14,color:TA.text}}>{f.name}</div>
                      <div style={{fontSize:12,color:TA.muted}}>
                        {FISCAL_DRIVERS.find(d=>d.id===f.driver)?.name} {f.model && `· ${f.model}`}
                        {f.serialNumber && ` · SN: ${f.serialNumber}`}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                      {f.isDefault && <span style={{background:"#eff6ff",color:"#2563eb",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>⭐ Domyślna</span>}
                      {!f.enabled && <span style={{background:"#ffebee",color:"#dc2626",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>⛔ Wyłączona</span>}
                      {f.enabled && <span style={{background:"#f0fdf4",color:"#16a34a",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>✅ Aktywna</span>}
                    </div>
                  </div>

                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                    {[
                      ["Połączenie", CONN_TYPES.find(c=>c.id===f.connection)?.label || f.connection],
                      f.connection==="network" ? ["Adres", `${f.ipAddress}:${f.port}`] : null,
                      f.connection!=="network" ? ["Port", f.comPort] : null,
                      f.nipFirmy ? ["NIP", f.nipFirmy] : null,
                    ].filter(Boolean).map(([label, val]) => (
                      <div key={label} style={{background:"#f9fafb",borderRadius:7,padding:"4px 10px",fontSize:11,color:TA.muted}}>
                        <span style={{fontWeight:700}}>{label}:</span> {val}
                      </div>
                    ))}
                  </div>

                  {testMsg[f.id] && (
                    <div style={{marginBottom:8,padding:"6px 10px",borderRadius:7,background:"#f0fdf4",fontSize:12,fontWeight:700,
                      color:testMsg[f.id].startsWith("✅")?"#16a34a":testMsg[f.id].startsWith("⏳")?"#d97706":"#dc2626"}}>
                      {testMsg[f.id]}
                    </div>
                  )}

                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button onClick={()=>setEditFiscal({...f})} style={S.actBtn}>✏ Edytuj</button>
                    <button onClick={()=>testConnection(f.id,"fiscal")} style={S.actBtn}>🔌 Testuj</button>
                    <button onClick={()=>setHw(p=>({...p,fiscals:(p.fiscals||[]).map(x=>x.id===f.id?{...x,enabled:!x.enabled}:x)}))}
                      style={{...S.actBtn,color:f.enabled?"#dc2626":"#16a34a"}}>
                      {f.enabled?"⛔ Wyłącz":"✅ Włącz"}
                    </button>
                    <button onClick={()=>deleteFiscal(f.id)} style={{...S.actBtn,color:"#dc2626"}}>🗑 Usuń</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── DRUKARKI ── */}
      {tab==="printer" && (
        <div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}>
            <button onClick={()=>setShowAddDevice(p=>!p)} style={S.darkBtn}>+ Dodaj drukarkę</button>
          </div>

          {showAddDevice && (
            <div style={{...S.formCard,border:"2px solid "+TA.accent,marginBottom:16}}>
              <div style={S.formTitle}>➕ Nowa drukarka</div>
              <PrinterForm d={newDevice} setD={setNewDevice}/>
              <div style={{display:"flex",gap:8,marginTop:14}}>
                <button onClick={addDevice} style={S.darkBtn}>💾 Dodaj</button>
                <button onClick={()=>setShowAddDevice(false)} style={S.cancelBtn}>Anuluj</button>
              </div>
            </div>
          )}

          {devices.length === 0 && !showAddDevice && (
            <div style={S.empty}>
              <div style={{fontSize:48,marginBottom:8}}>🖨</div>
              <div style={{fontWeight:700,marginBottom:4}}>Brak skonfigurowanych drukarek</div>
              <div style={{fontSize:12}}>Kliknij „Dodaj drukarkę" aby skonfigurować pierwszą drukarkę.</div>
            </div>
          )}

          {devices.map(d => (
            <div key={d.id} style={{...S.card,border:`1.5px solid ${d.enabled?TA.border:"#fca5a5"}`}}>
              {editDevice?.id === d.id ? (
                <div>
                  <div style={S.formTitle}>✏ Edycja: {d.name}</div>
                  <PrinterForm d={editDevice} setD={setEditDevice}/>
                  <div style={{display:"flex",gap:8,marginTop:14}}>
                    <button onClick={saveDevice} style={S.darkBtn}>💾 Zapisz</button>
                    <button onClick={()=>setEditDevice(null)} style={S.cancelBtn}>Anuluj</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:22}}>{PRINTER_TYPES.find(t=>t.id===d.type)?.icon||"🖨"}</span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:14,color:TA.text}}>{d.name}</div>
                      <div style={{fontSize:12,color:TA.muted}}>
                        {PRINTER_TYPES.find(t=>t.id===d.type)?.label} · {FISCAL_DRIVERS.find(x=>x.id===d.driver)?.name} {d.model && `${d.model}`} · {d.paperWidth}mm
                      </div>
                    </div>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      {d.isDefault && <span style={{background:"#eff6ff",color:"#2563eb",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>⭐ Domyślna</span>}
                      {d.enabled ? <span style={{background:"#f0fdf4",color:"#16a34a",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>✅ Aktywna</span>
                                 : <span style={{background:"#ffebee",color:"#dc2626",borderRadius:8,padding:"3px 8px",fontSize:11,fontWeight:700}}>⛔ Wyłączona</span>}
                    </div>
                  </div>

                  <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                    {[
                      ["Połączenie", CONN_TYPES.find(c=>c.id===d.connection)?.label || d.connection],
                      d.connection==="network" ? ["Adres", `${d.ipAddress}:${d.port}`] : null,
                      d.connection!=="network" ? ["Port", d.comPort] : null,
                      ["Kodowanie", d.charEncoding],
                    ].filter(Boolean).map(([label, val]) => (
                      <div key={label} style={{background:"#f9fafb",borderRadius:7,padding:"4px 10px",fontSize:11,color:TA.muted}}>
                        <span style={{fontWeight:700}}>{label}:</span> {val}
                      </div>
                    ))}
                  </div>

                  {testMsg[d.id] && (
                    <div style={{marginBottom:8,padding:"6px 10px",borderRadius:7,background:"#f9fafb",fontSize:12,fontWeight:700,
                      color:testMsg[d.id].startsWith("✅")?"#16a34a":testMsg[d.id].startsWith("⏳")?"#d97706":"#dc2626"}}>
                      {testMsg[d.id]}
                    </div>
                  )}

                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button onClick={()=>setEditDevice({...d})} style={S.actBtn}>✏ Edytuj</button>
                    <button onClick={()=>testConnection(d.id,"printer")} style={S.actBtn}>🔌 Testuj połączenie</button>
                    <button onClick={()=>setHw(p=>({...p,devices:(p.devices||[]).map(x=>x.id===d.id?{...x,enabled:!x.enabled}:x)}))}
                      style={{...S.actBtn,color:d.enabled?"#dc2626":"#16a34a"}}>
                      {d.enabled?"⛔ Wyłącz":"✅ Włącz"}
                    </button>
                    <button onClick={()=>deleteDevice(d.id)} style={{...S.actBtn,color:"#dc2626"}}>🗑 Usuń</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── STEROWNIKI ── */}
      {tab==="drivers" && (
        <div>
          <div style={{...S.formCard,background:"#eff6ff",border:"1.5px solid #bfdbfe",marginBottom:16}}>
            <div style={{fontWeight:700,fontSize:13,color:"#1d4ed8",marginBottom:6}}>ℹ Obsługiwane sterowniki i protokoły</div>
            <div style={{fontSize:12,color:"#1e40af",lineHeight:1.7}}>
              System obsługuje komunikację z kasami fiskalnymi i drukarkami przez protokoły: <strong>USB/HID, TCP/IP Socket, RS-232 (COM), Bluetooth SPP</strong>.
              Poniżej lista obsługiwanych producentów i wymaganych sterowników.
            </div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:12}}>
            {FISCAL_DRIVERS.map(drv => (
              <div key={drv.id} style={{...S.card,border:"1.5px solid "+TA.border}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{width:40,height:40,borderRadius:10,background:"linear-gradient(135deg,#2563eb,#1e40af)",
                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                    {drv.id==="novitus"?"🏦":drv.id==="posnet"?"💰":drv.id==="elzab"?"🧾":drv.id==="thermal"?"🖨":drv.id==="brother"?"🏷":drv.id==="zebra"?"🦓":"⚙"}
                  </div>
                  <div>
                    <div style={{fontWeight:700,fontSize:14,color:TA.text}}>{drv.name}</div>
                    <div style={{fontSize:11,color:TA.muted}}>{drv.models.length} modeli</div>
                  </div>
                </div>

                <div style={{marginBottom:10}}>
                  <div style={{fontSize:11,fontWeight:700,color:TA.muted,marginBottom:5,textTransform:"uppercase",letterSpacing:0.5}}>Obsługiwane modele</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {drv.models.map(m=>(
                      <span key={m} style={{background:"#f3f4f6",color:TA.muted,borderRadius:6,padding:"2px 8px",fontSize:11}}>{m}</span>
                    ))}
                  </div>
                </div>

                <div style={{fontSize:11,color:TA.muted,lineHeight:1.6}}>
                  {drv.id==="novitus" && "Protokół: Novitus HS EJ v3. Wymagany sterownik USB z https://novitus.pl/sterowniki"}
                  {drv.id==="posnet" && "Protokół: Posnet Thermal v1.0. Sterownik POSNET z https://posnet.com.pl/pobierz"}
                  {drv.id==="elzab" && "Protokół: ELZAB Uniprot. Sterownik z https://elzab.pl/serwis/sterowniki"}
                  {drv.id==="thermal" && "Protokół: ESC/POS (Epson). Kompatybilny ze Starem, Bixolonem, Epson i innymi. Brak dodatkowych sterowników (RAW TCP/USB)."}
                  {drv.id==="brother" && "Sterownik: Brother P-touch Editor / iPrint&Label. Pobierz z https://support.brother.com"}
                  {drv.id==="zebra" && "Protokół: ZPL II / EPL2. Sterowniki z https://zebra.com/us/en/support-downloads.html"}
                  {drv.id==="custom" && "Konfiguracja ręczna — podaj własny adres IP i port lub ścieżkę COM."}
                </div>

                {drv.id!=="custom" && drv.id!=="thermal" && (
                  <div style={{marginTop:10,padding:"7px 10px",background:"#fffbeb",borderRadius:7,border:"1px solid #fde68a",fontSize:11,color:"#92400e"}}>
                    ⚠ Wymagana instalacja sterownika w systemie operacyjnym przed użyciem.
                  </div>
                )}

                {(drv.id==="thermal"||drv.id==="custom") && (
                  <div style={{marginTop:10,padding:"7px 10px",background:"#f0fdf4",borderRadius:7,border:"1px solid #86efac",fontSize:11,color:"#15803d"}}>
                    ✅ Działa bez dodatkowych sterowników (ESC/POS RAW).
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{...S.formCard,marginTop:16}}>
            <div style={S.formTitle}>🔧 Konfiguracja systemowa Windows</div>
            <div style={{fontSize:12,color:TA.muted,lineHeight:1.8}}>
              <div>1. Zainstaluj sterownik producenta lub użyj wbudowanego sterownika Windows dla ESC/POS.</div>
              <div>2. Drukarkę dodaj w <strong>Panel sterowania → Urządzenia i drukarki</strong>.</div>
              <div>3. Dla drukarek sieciowych — wybierz „Dodaj drukarkę sieciową" i podaj adres IP.</div>
              <div>4. Dla kas fiskalnych COM — zainstaluj sterownik producenta, sprawdź numer portu w Menedżerze urządzeń.</div>
              <div>5. Dla USB — Windows powinien automatycznie wykryć urządzenie po instalacji sterownika.</div>
            </div>
          </div>

          <div style={{...S.formCard}}>
            <div style={S.formTitle}>🐧 Konfiguracja Linux / macOS</div>
            <div style={{fontSize:12,color:TA.muted,lineHeight:1.8}}>
              <div>• Drukarki ESC/POS: <code style={{background:"#f3f4f6",padding:"0 4px",borderRadius:3}}>sudo apt install cups</code> lub CUPS z macOS.</div>
              <div>• Port USB: <code style={{background:"#f3f4f6",padding:"0 4px",borderRadius:3}}>/dev/usb/lp0</code> lub <code style={{background:"#f3f4f6",padding:"0 4px",borderRadius:3}}>/dev/tty.usbserial</code></div>
              <div>• Uprawnienia: <code style={{background:"#f3f4f6",padding:"0 4px",borderRadius:3}}>sudo usermod -aG lp $USER</code></div>
              <div>• Drukarki sieciowe: RAW socket na port 9100 (standard ESC/POS) lub 515 (LPD).</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════ ROOT ══════════════════════════════════════════ */
/* ════════════════════ LOCALSTORAGE HELPERS ══════════════════════════════════ */
/* ════════════════════ FIREBASE SYNC ═════════════════════════════════════════
   Dane synchronizowane przez Firebase Realtime Database.
   Skonfiguruj w Ustawieniach → Synchronizacja, podając dane swojego projektu Firebase.
   Instrukcja: https://firebase.google.com → Console → Create project → Realtime Database
══════════════════════════════════════════════════════════════════════════════ */

const FB_CONFIG_KEY  = "smakidomu_firebase_config";
const FB_DEFAULT_URL = "https://zamowienia-pokutynski-default-rtdb.europe-west1.firebasedatabase.app";
const FB_DEFAULT_SECRET = "";
// ── Proxy API — wszystkie zapytania do Firebase idą przez backend ──────────
const API_BASE  = "/api/db";
// Token autoryzacji — musi być taki sam jak API_TOKEN w ecosystem.config.js na serwerze
const API_TOKEN = (typeof window !== "undefined" && window.__API_TOKEN__) || "";
// Pomocnik fetch z nagłówkiem autoryzacji
function apiFetch(url, options = {}) {
  const headers = { ...(options.headers || {}), ...(API_TOKEN ? { "X-API-Token": API_TOKEN } : {}) };
  return fetch(url, { ...options, headers });
}

function getFirebaseConfig() {
  try {
    // Sprawdź oba klucze i użyj tego który ma secret
    const keys = [FB_CONFIG_KEY, "smakidomu_fbConfig"];
    let best = null;
    for (const key of keys) {
      const s = localStorage.getItem(key);
      if (!s) continue;
      const parsed = JSON.parse(s);
      if (parsed && parsed.databaseURL) {
        if (!best || (!best.secret && parsed.secret)) best = parsed;
      }
    }
    if (best) return { databaseURL: best.databaseURL, storageBucket: best.storageBucket || "", secret: best.secret || FB_DEFAULT_SECRET };
  } catch(e) {}
  return { databaseURL: FB_DEFAULT_URL, secret: FB_DEFAULT_SECRET };
}

// Zapisz domyślny config przy starcie jeśli brak
(function ensureFirebaseConfig() {
  try {
    const s = localStorage.getItem(FB_CONFIG_KEY);
    if (!s) {
      localStorage.setItem(FB_CONFIG_KEY, JSON.stringify({ databaseURL: FB_DEFAULT_URL, secret: FB_DEFAULT_SECRET }));
    } else {
      const p = JSON.parse(s);
      if (!p || !p.databaseURL) {
        localStorage.setItem(FB_CONFIG_KEY, JSON.stringify({ databaseURL: FB_DEFAULT_URL, secret: FB_DEFAULT_SECRET }));
      } else if (!p.secret) {
        // Uzupełnij brakujący secret w istniejącym configu
        localStorage.setItem(FB_CONFIG_KEY, JSON.stringify({ ...p, secret: FB_DEFAULT_SECRET }));
      }
    }
  } catch(e) {}
})();

function saveFirebaseConfig(cfg) {
  try { localStorage.setItem(FB_CONFIG_KEY, JSON.stringify(cfg)); } catch(e) {}
}

// Build Firebase REST URL for a given path
// eslint-disable-next-line no-unused-vars
function fbUrl(config, path) {
  const base = config.databaseURL.replace(/\/$/, "");
  return base + "/smakidomu/" + path + ".json";
}

// Konwertuje Firebase numeric-key objects {0:...,1:...} z powrotem na tablice
function firebaseToArray(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  const keys = Object.keys(data);
  if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
    return keys.sort((a,b) => Number(a)-Number(b)).map(k => data[k]);
  }
  return data;
}

// Normalizuje dane — jeśli oczekujemy tablicy a dostaliśmy obiekt, konwertuj
function normalizeData(data, defaultVal) {
  if (data === null || data === undefined) return defaultVal;
  if (Array.isArray(defaultVal)) {
    if (Array.isArray(data)) return data;
    const converted = firebaseToArray(data);
    return Array.isArray(converted) ? converted : defaultVal;
  }
  return data;
}

// Wykrywa czy obiekt Firebase jest naszym opakowanym formatem {_data, _ts, _dev}
// Firebase usuwa _data gdy jest null/[] — dlatego sprawdzamy też _ts lub _dev
function isWrappedFormat(json) {
  if (!json || typeof json !== "object" || Array.isArray(json)) return false;
  return "_data" in json || "_ts" in json || "_dev" in json || "_empty" in json;
}

// Wyciąga dane z opakowanego formatu — obsługuje brak _data (pusta tablica)
function unwrapFirebase(json, defaultVal) {
  if (!isWrappedFormat(json)) return { data: null, ts: 0, dev: "" };
  // _empty: true → Firebase przechował marker dla pustej tablicy
  if (json._empty === true) {
    return {
      data: Array.isArray(defaultVal) ? [] : defaultVal,
      ts:   json._ts  || 0,
      dev:  json._dev || "",
    };
  }
  // Jeśli _data jest null/undefined — Firebase usunął puste węzły → oryginał był []
  const raw = json._data !== undefined ? json._data : null;
  const data = raw === null || raw === undefined
    ? (Array.isArray(defaultVal) ? [] : defaultVal)
    : normalizeData(raw, defaultVal);
  return {
    data,
    ts:  json._ts  || 0,
    dev: json._dev || "",
  };
}

// Read from Firebase
// eslint-disable-next-line no-unused-vars

// Write to Firebase — zwraca true gdy zapis potwierdzony
async function fbSet(config, path, data) {
  try {
    const headers = { "Content-Type": "application/json" };
    let payload = data;
    if (payload && typeof payload === "object" && ("_data" in payload || "_ts" in payload)) {
      const d = payload._data;
      if (d === null || d === undefined || (Array.isArray(d) && d.length === 0)) {
        payload = { ...payload, _data: null, _empty: true };
      }
    }
    const res = await apiFetch(API_BASE + "/" + path, {
      method: "PUT",
      headers,
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch(e) { return false; }
}

/* ════════════════════ SCHEMA VERSION & MIGRATION ════════════════════════════
   Zwiększ SCHEMA_VERSION gdy zmienisz strukturę danych (np. dodasz pole,
   zmienisz format hasła). localStorage zostanie odrzucony i zastąpiony init.
   Kluczowe zmiany:
     v1 → v2: hasła z plain-text (password) na SHA-256 (passwordHash)
     v2 → v3: migracja roli — użytkownik login="admin" lub id=1 → superadmin
     v3 → v4: hasła z SHA-256 na PBKDF2(100k iter, sól=login) — migracja lazy
               przy pierwszym logowaniu (nie wymaga czyszczenia LS)
══════════════════════════════════════════════════════════════════════════════ */
const SCHEMA_VERSION = 4;
const SCHEMA_VER_KEY = "smakidomu_schema_v";

/** Jednorazowa migracja przy starcie: jeśli lokalny schemat jest starszy,
 *  wykonujemy odpowiednie kroki migracji i zapisujemy nową wersję.
 *  Zwraca true jeśli migracja została wykonana. */
function runSchemaMigration() {
  try {
    const stored = parseInt(localStorage.getItem(SCHEMA_VER_KEY) || "0", 10);
    if (stored >= SCHEMA_VERSION) return false;           // nic do zrobienia

    if (stored < 2) {
      // v1 → v2: czyścimy wszystkie klucze danych (hasła plain-text → hash)
      const SKIP = new Set([FB_CONFIG_KEY, SCHEMA_VER_KEY]);
      const toRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("smakidomu_") && !SKIP.has(k)) {
          toRemove.push(k);
        }
      }
      toRemove.forEach(k => localStorage.removeItem(k));
      // eslint-disable-next-line no-console

      // eslint-disable-next-line no-console
      console.info(`[smakidomu] Migracja v1→v2: wyczyszczono ${toRemove.length} kluczy localStorage.`);
    }

    if (stored >= 2 && stored < 3) {
      // v2 → v3: napraw rolę — użytkownik z login="admin" lub id=1 powinien mieć superadmin
      try {
        const usersRaw = localStorage.getItem("smakidomu_users");
        if (usersRaw) {
          const users = JSON.parse(usersRaw);
          if (Array.isArray(users)) {
            let changed = false;
            const updated = users.map(u => {
              if ((u.login === "admin" || u.id === 1) && u.role !== "superadmin") {
                // eslint-disable-next-line no-console

                // eslint-disable-next-line no-console
                console.info(`[smakidomu] Migracja v2→v3: użytkownik "${u.login}" (id=${u.id}) → rola superadmin`);
                changed = true;
                return { ...u, role: "superadmin" };
              }
              return u;
            });
            if (changed) {
              const newTs = Date.now();
              localStorage.setItem("smakidomu_users", JSON.stringify(updated));
              localStorage.setItem("smakidomu_ts_users", String(newTs));
            }
          }
        }
      } catch(e) {
        // eslint-disable-next-line no-console

        // eslint-disable-next-line no-console
        console.warn("[smakidomu] Migracja v2→v3: błąd migracji użytkowników", e);
      }
    }

    // v3 → v4: migracja SHA-256 → PBKDF2 odbywa się LAZY przy logowaniu
    // (hashPasswordLegacy + hashPassword w LoginScreen).
    // Nie wymaga czyszczenia LS — stare hashe SHA-256 zostaną nadpisane przy pierwszym logowaniu.
    if (stored >= 3 && stored < 4) {
      // eslint-disable-next-line no-console

      // eslint-disable-next-line no-console
      console.info("[smakidomu] Migracja v3→v4: hasła SHA-256 → PBKDF2 (lazy, przy logowaniu).");
    }

    localStorage.setItem(SCHEMA_VER_KEY, String(SCHEMA_VERSION));
    // eslint-disable-next-line no-console

    // eslint-disable-next-line no-console
    console.info(`[smakidomu] Migracja schematu v${stored}→v${SCHEMA_VERSION} zakończona.`);
    return true;
  } catch(e) { return false; }
}

// Wywołujemy migrację raz, synchronicznie, przed inicjalizacją hooków
runSchemaMigration();

// ── Wspólny SSE manager — JEDNO połączenie dla wszystkich kluczy ─────────────
// Zamiast 11 osobnych EventSource (ERR_INSUFFICIENT_RESOURCES),
// nasłuchujemy /smakidomu.json i dystrybuujemy dane do hooków.
const _sseListeners = new Map(); // key → Set<callback>
const _sseState = {
  es: null,
  active: false,
  fbUrl: null,
  reconnectTid: null,
  // getSecret przechowuje FUNKCJĘ zwracającą aktualny secret,
  // dzięki temu reconnect po błędzie zawsze użyje świeżego tokenu (Bug #3)
  getSecret: () => "",
};

function _sseSubscribe(key, cb) {
  if (!_sseListeners.has(key)) _sseListeners.set(key, new Set());
  _sseListeners.get(key).add(cb);
  return () => { const s = _sseListeners.get(key); if (s) s.delete(cb); };
}

function _sseNotify(key, data, force) {
  const listeners = _sseListeners.get(key);
  if (listeners) listeners.forEach(cb => { try { cb(data, force); } catch(_) {} });
}

// Bug #3 fix: przyjmuje getSecret jako FUNKCJĘ zamiast wartości,
// żeby reconnect zawsze miał aktualny token.
function _sseConnect(fbUrl, getSecret) {
  if (_sseState.es) { try { _sseState.es.close(); } catch(_) {} _sseState.es = null; }
  if (_sseState.reconnectTid) { clearTimeout(_sseState.reconnectTid); _sseState.reconnectTid = null; }
  _sseState.active = true;
  _sseState.fbUrl = fbUrl;
  _sseState.getSecret = typeof getSecret === "function" ? getSecret : () => (getSecret || "");

  const buildRootUrl = () => API_TOKEN ? `/api/stream?t=${encodeURIComponent(API_TOKEN)}` : "/api/stream";

  try {
    const rootUrl = buildRootUrl();
    const es = new EventSource(rootUrl);
    _sseState.es = es;

    es.addEventListener("put", (e) => {
      if (!_sseState.active) return;
      try {
        const msg = JSON.parse(e.data);
        if (!msg || msg.data === undefined) return;
        const path = msg.path || "/";
        if (path === "/") {
          // Pełna aktualizacja — dystrybuuj do każdego klucza
          const root = msg.data;
          if (root && typeof root === "object") {
            Object.keys(root).forEach(k => _sseNotify(k, root[k], true));
          }
        } else {
          // Aktualizacja konkretnego klucza np. "/orders"
          const key = path.replace(/^\//, "").split("/")[0];
          // Bug #4 fix: dla patch/put konkretnego węzła — aktualizujemy TYLKO ten klucz,
          // nie fetchujemy całego roota.
          if (key) _sseNotify(key, msg.data, true);
        }
      } catch(_) {}
    });

    // Bug #4 fix: patch — aktualizuj tylko zmieniony węzeł z e.data zamiast
    // fetchować cały /smakidomu.json (co powodowało race condition i nadmiarowy ruch).
    // Debounce: grupuj patch eventy dla tego samego klucza w oknie 200ms
    const _patchDebounce = {};
    es.addEventListener("patch", (e) => {
      if (!_sseState.active) return;
      try {
        const msg = JSON.parse(e.data);
        if (!msg) return;
        const path = (msg.path || "/").replace(/^\//, "");
        const topKey = path.split("/")[0];
        if (topKey) {
          // Debounce: anuluj poprzedni fetch dla tego klucza, poczekaj 200ms
          if (_patchDebounce[topKey]) clearTimeout(_patchDebounce[topKey]);
          _patchDebounce[topKey] = setTimeout(() => {
            delete _patchDebounce[topKey];
            const nodeUrl = API_BASE + "/" + topKey;
            fetch(nodeUrl, { cache: "no-store" })
              .then(r => r.ok ? r.json() : null)
              .then(data => { if (data !== null && data !== undefined) _sseNotify(topKey, data, true); })
              .catch(() => {});
          }, 200);
        } else {
          // Patch na root — pobierz wszystko (rzadki przypadek)
          apiFetch("/api/db", { cache: "no-store" })
            .then(r => r.ok ? r.json() : null)
            .then(root => {
              if (!root || typeof root !== "object") return;
              Object.keys(root).forEach(k => _sseNotify(k, root[k], true));
            })
            .catch(() => {});
        }
      } catch(_) {}
    });

    es.onerror = () => {
      if (!_sseState.active) return;
      try { es.close(); } catch(_) {}
      _sseState.es = null;
      // Bug #3 fix: reconnect wywołuje _sseConnect z aktualnym getSecret,
      // więc nowy secret z ustawień jest zawsze używany po błędzie.
      _sseState.reconnectTid = setTimeout(() => {
        if (_sseState.active) _sseConnect(_sseState.fbUrl, _sseState.getSecret);
      }, 5000);
    };
  } catch(_) {}
}

function _sseDisconnect() {
  _sseState.active = false;
  if (_sseState.es) { try { _sseState.es.close(); } catch(_) {} _sseState.es = null; }
  if (_sseState.reconnectTid) { clearTimeout(_sseState.reconnectTid); _sseState.reconnectTid = null; }
}

// Hook: local state + Firebase sync (jeden SSE + polling backup)
// Synchronizacja oparta na deviceId — odróżnia własne echo od zmian zewnętrznych
const _deviceId = (() => {
  try {
    let id = localStorage.getItem("smakidomu_device_id");
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("smakidomu_device_id", id);
    }
    return id;
  } catch(e) { return Math.random().toString(36).slice(2); }
})();

function usePersistedState(key, init, fbConfig) {
  const defaultVal = typeof init === "function" ? init() : init;

  const [val, setVal] = useState(() => {
    try {
      const stored = localStorage.getItem("smakidomu_" + key);
      if (stored !== null) return normalizeData(JSON.parse(stored), defaultVal);
    } catch(e) {}
    return defaultVal;
  });

  const defaultValRef = React.useRef(defaultVal);
  defaultValRef.current = defaultVal;

  const fbConfigRef = React.useRef(fbConfig);
  useEffect(() => { fbConfigRef.current = fbConfig; });

  // ts ostatniego lokalnego zapisu do Firebase
  const lastLocalTsRef = React.useRef(0);
  const pendingWriteRef = React.useRef(null);
  // Ref do resetu interwału pollingu po lokalnym zapisie
  const pollResetRef = React.useRef(null);

  // Callback wywoływany przez SSE manager lub polling
  const applyRemote = React.useCallback((json) => {
    if (json === null || json === undefined) return;

    // Użyj unwrapFirebase — obsługuje brak _data gdy Firebase usunął pustą tablicę
    let data, remoteTs, remoteDev;
    if (isWrappedFormat(json)) {
      const unwrapped = unwrapFirebase(json, defaultValRef.current);
      data      = unwrapped.data;
      remoteTs  = unwrapped.ts;
      remoteDev = unwrapped.dev;
    } else {
      data      = normalizeData(json, defaultValRef.current);
      remoteTs  = 0;
      remoteDev = "";
    }

    // Przypadek 1: echo własnego zapisu (identyczny _dev i _ts) → ignoruj zawsze
    if (remoteDev === _deviceId && remoteTs > 0) {
      if (pendingWriteRef.current && pendingWriteRef.current.ts === remoteTs) {
        if (pendingWriteRef.current._timer) clearTimeout(pendingWriteRef.current._timer);
        pendingWriteRef.current = null; // Firebase potwierdził — zdejmujemy blokadę
        // Wznów polling teraz gdy Firebase potwierdził zapis
        if (pollResetRef.current) pollResetRef.current();
      }
      return;
    }

    // Przypadek 2: lokalny zapis w toku (czekamy na Firebase) →
    // jeśli remote nie ma _dev lub ma stary _ts → to stare dane sprzed naszego zapisu → ignoruj
    if (pendingWriteRef.current) {
      const localTs = pendingWriteRef.current.ts;
      // remote jest starszy niż nasz lokalny zapis → stare dane → ignoruj
      if (remoteTs === 0 || remoteTs < localTs) return;
      // remote jest nowszy → pochodzi z innego urządzenia → aplikuj i zdejmij blokadę
      pendingWriteRef.current = null;
    }

    // Przypadek 3: dane z innego urządzenia — aplikuj
    lastLocalTsRef.current = remoteTs;
    try {
      localStorage.setItem("smakidomu_" + key, JSON.stringify(data));
      if (remoteTs > 0) localStorage.setItem("smakidomu_ts_" + key, String(remoteTs));
    } catch(e) {}
    setVal(data);
  }, [key]); // eslint-disable-line

  // ── BroadcastChannel: synchronizacja między zakładkami BEZ Firebase ──
  const bcRef = React.useRef(null);
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const bc = new BroadcastChannel("smakidomu_" + key);
    bcRef.current = bc;
    bc.onmessage = (e) => {
      const { ts, data, dev } = e.data || {};
      if (!data || !ts) return;
      if (dev === _deviceId) return; // własna zakładka — już ma dane
      const normalized = normalizeData(data, defaultValRef.current);
      lastLocalTsRef.current = ts;
      try {
        localStorage.setItem("smakidomu_" + key, JSON.stringify(normalized));
        localStorage.setItem("smakidomu_ts_" + key, String(ts));
      } catch(e) {}
      setVal(normalized);
    };
    return () => { bc.close(); bcRef.current = null; };
  }, [key]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "smakidomu_" + key) return;
      try {
        const data = normalizeData(JSON.parse(e.newValue || "null"), defaultValRef.current);
        setVal(data);
      } catch(_) {}
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [key]); // eslint-disable-line

  const fbUrl_ = fbConfig && fbConfig.databaseURL ? fbConfig.databaseURL.replace(/\/$/, "") : null;
  const fbSecret_ = fbConfig && fbConfig.secret ? fbConfig.secret : "";

  useEffect(() => {
    if (!fbUrl_) return;

    const unsub = _sseSubscribe(key, applyRemote);

    const poll = () => {
      // Nie polluj gdy mamy pending write (czekamy na Firebase)
      if (pendingWriteRef.current) return;
      apiFetch(API_BASE + "/" + key, { cache: "no-store" })
        .then(r => r.ok ? r.json() : null)
        .then(json => { if (json !== null) applyRemote(json); })
        .catch(() => {});
    };
    poll();

    const keyHash = key.split("").reduce((h,c)=>h+c.charCodeAt(0),0);
    const jitter = (keyHash % 10) * 200;
    let pollInterval = null;

    const startPolling = () => {
      if (pollInterval) clearInterval(pollInterval);
      pollInterval = setInterval(poll, 5000);
    };
    pollResetRef.current = startPolling;

    const tid = setTimeout(startPolling, jitter);

    const onVisible = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      unsub();
      clearTimeout(tid);
      if (pollInterval) clearInterval(pollInterval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [key, fbUrl_, fbSecret_, applyRemote]); // eslint-disable-line

  const setter = React.useCallback((v) => {
    setVal(prev => {
      const next = normalizeData(typeof v === "function" ? v(prev) : v, defaultValRef.current);
      try {
        const ts = Date.now();
        lastLocalTsRef.current = ts;
        localStorage.setItem("smakidomu_" + key, JSON.stringify(next));
        localStorage.setItem("smakidomu_ts_" + key, String(ts));
        if (bcRef.current) {
          try { bcRef.current.postMessage({ ts, data: next, dev: _deviceId }); } catch(_) {}
        }
        const cfg = fbConfigRef.current;
        if (cfg && cfg.databaseURL) {
          if (pendingWriteRef.current && pendingWriteRef.current._timer) {
            clearTimeout(pendingWriteRef.current._timer);
          }
          // Blokada + automatyczne wygaśnięcie po 5s + reset interwału pollingu
          const _timer = setTimeout(() => {
            if (pendingWriteRef.current && pendingWriteRef.current.ts === ts) {
              pendingWriteRef.current = null;
              // Wznów polling po wygaśnięciu blokady
              if (pollResetRef.current) pollResetRef.current();
            }
          }, 5000);
          pendingWriteRef.current = { ts, data: next, _timer };
          fbSet(cfg, key, { _data: next, _ts: ts, _dev: _deviceId });
        }
      } catch(e) {}
      return next;
    });
  }, [key]); // eslint-disable-line

  return [val, setter];
}

export default function App() {
  const [fbConfig, setFbConfigState] = useState(() => getFirebaseConfig());
  const setFbConfig = React.useCallback(cfg => {
    const newCfg = cfg || { databaseURL: FB_DEFAULT_URL, secret: FB_DEFAULT_SECRET };
    if (!newCfg.secret) newCfg.secret = FB_DEFAULT_SECRET;
    saveFirebaseConfig(newCfg); setFbConfigState(newCfg);
  }, []);

  // Uruchom wspólny SSE manager przy starcie i przy zmianie konfiguracji
  // Bug #3 fix: przekazujemy getter zamiast wartości, żeby reconnect po błędzie
  // zawsze użył aktualnego secret (np. po zmianie w ustawieniach).
  const fbSecretRef = React.useRef(fbConfig && fbConfig.secret ? fbConfig.secret : "");
  useEffect(() => { fbSecretRef.current = fbConfig && fbConfig.secret ? fbConfig.secret : ""; });

  useEffect(() => {
    if (fbConfig && fbConfig.databaseURL) {
      _sseConnect(fbConfig.databaseURL, () => fbSecretRef.current);
    }
    return () => _sseDisconnect();
  }, [fbConfig && fbConfig.databaseURL, fbConfig && fbConfig.secret]); // eslint-disable-line

  const [menu,        setMenu]        = usePersistedState("menu",        INIT_MENU,      fbConfig);
  const [cats,        setCats]        = usePersistedState("cats",        INIT_CATS,      fbConfig);
  const [users,       setUsers]       = usePersistedState("users",       INIT_USERS,     fbConfig);
  const [orders,      setOrders]      = usePersistedState("orders",      [],             fbConfig);
  const [settings,    setSettings]    = usePersistedState("settings",    INIT_SETTINGS,  fbConfig);
  const [customRoles, setCustomRoles] = usePersistedState("customRoles", [],             fbConfig);
  const [campaigns, setCampaigns] = usePersistedState("campaigns", INIT_CAMPAIGNS, fbConfig);
  const [vouchers,  setVouchers]  = usePersistedState("vouchers",  INIT_VOUCHERS,  fbConfig);
  // usedCodes — odczyt przez bezpieczny endpoint serwera (nie bezpośrednio z Firebase)
  const [usedCodes, setUsedCodes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("smakidomu_usedCodes") || "[]"); } catch(_) { return []; }
  });
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiFetch("/api/used-codes", { cache: "no-store" })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (cancelled || !data) return;
          const arr = Array.isArray(data) ? data : Object.values(data);
          setUsedCodes(arr);
          try { localStorage.setItem("smakidomu_usedCodes", JSON.stringify(arr)); } catch(_) {}
        })
        .catch(() => {});
    };
    load();
    const iv = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);
  const [tables,    setTables]    = usePersistedState("tables",    INIT_TABLES, fbConfig);
  const [clients,   setClients]   = usePersistedState("clients",   [],          fbConfig);
  const [onlineMenu,   setOnlineMenu]   = usePersistedState("onlineMenu",   [], fbConfig);
  const [onlineCats,   setOnlineCats]   = usePersistedState("onlineCats",   [
    {id:"bakery", name:"🥖 Piekarnicze", active:true},
    {id:"pastry", name:"🍰 Cukiernicze", active:true},
    {id:"custom", name:"⭐ Własne",       active:true},
  ], fbConfig);
  const [onlineOrders, setOnlineOrders] = usePersistedState("onlineOrders", [], fbConfig);

  // Wszystkie useState przed zwykłymi stałymi
  const [staffCalls,    setStaffCalls]    = usePersistedState("staffCalls", [], fbConfig);
  const [logs,          setLogs]          = usePersistedState("logs",       [], fbConfig);
  // ── Auto-login przez ?sc= URL param (zaszyfrowany token karty NFC) ──
  const staffCardToken = (() => {
    try { return new URLSearchParams(window.location.search).get("sc"); } catch(_) { return null; }
  })();

  const [view,      setView]      = useState(() =>
    // Jeśli jest ?sc= w URL — od razu pokaż panel admina (zalogujemy w useEffect)
    staffCardToken ? "admin" : "customer"
  );
  const [adminUser, setAdminUser] = useState(null);
  const [orderTaps,     setOrderTaps]     = useState(0);
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [logoTaps,      setLogoTaps]      = useState(0);
  const tapTimerRef     = useRef(null);
  const logoTapTimer    = useRef(null);
  useBreakpoint();
  const ac              = settings.accentColor;
  const pendingCount    = orders.filter(o=>!o.issued&&o.date===todayStr()).length;
  const navbarHidden    = settings.navbarHidden === true;

  // ── Obsługa ?sc= — odszyfruj token i zaloguj pracownika gdy users są gotowe ──
  const staffCardHandled = React.useRef(false);
  React.useEffect(() => {
    if (!staffCardToken || staffCardHandled.current || users.length === 0) return;
    staffCardHandled.current = true;
    // Wyczyść param z URL natychmiast (żeby nie był widoczny)
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("sc");
      window.history.replaceState({}, "", url.toString());
    } catch(_) {}
    // Asynchroniczne dopasowanie — próbujemy każdego użytkownika z kartą
    findUserByNfcToken(staffCardToken, users).then(u => {
      if (u) {
        setAdminUser(u);
        setView("admin");
      } else {
        // Token nieważny lub karta usunięta — wróć do logowania
        setView("admin"); // LoginScreen się pojawi (adminUser=null)
      }
    });
  }, [users, staffCardToken]);

  // handlePlaceOrder musi być zdefiniowany na poziomie hooka — nie inline w JSX
  const handlePlaceOrder = React.useCallback(async o => {
    // Zbierz IP i geolokalizację dla zamówienia
    let geoData = {};
    try {
      const res = await fetch("https://ipapi.co/json/");
      if (res.ok) {
        const geo = await res.json();
        geoData = {
          ip:      geo.ip           || "—",
          city:    geo.city         || "—",
          country: geo.country_name || "—",
          region:  geo.region       || "—",
          isp:     geo.org          || "—",
          ua:      navigator.userAgent || "",
        };
      }
    } catch(_) {}
    const enriched = { ...o, ...geoData };
    setOrders(p => [...p, enriched].slice(-500));
  }, [setOrders]);

  const handleOrderTap = () => {
    setView("customer");
    if (adminUnlocked) return;
    const next = orderTaps + 1;
    setOrderTaps(next);
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
    if (next >= 5) {
      setAdminUnlocked(true);
      setOrderTaps(0);
    } else {
      tapTimerRef.current = setTimeout(() => setOrderTaps(0), 3000);
    }
  };

  // 5× kliknięcie w logo → otwórz panel logowania
  const handleLogoTap = () => {
    const next = logoTaps + 1;
    setLogoTaps(next);
    if (logoTapTimer.current) clearTimeout(logoTapTimer.current);
    if (next >= 5) {
      setLogoTaps(0);
      setView("admin");
    } else {
      logoTapTimer.current = setTimeout(() => setLogoTaps(0), 3000);
    }
  };

  return (
    <ErrorBoundary>
    <BreakpointProvider>
    <>
    <GlobalStyles/>
    <div style={{fontFamily:"'Segoe UI',system-ui,sans-serif",background:view==="customer"
        ? (settings.customerBgStyle==="bakery"||!settings.customerBgStyle
            ? "linear-gradient(160deg,#3B1F0A 0%,#6B3F1E 45%,#C8621A 100%)"
            : settings.customerBgStyle==="plain" ? (settings.customerBgColor||"#FDF6EC")
            : settings.customerBgImage ? `url(${settings.customerBgImage}) center/cover` : "#FDF6EC")
        : T.bg,
      minHeight:"100vh",width:"100%"}}>

      {/* Pasek nawigacji — tylko dla widoku klienta */}
      {view==="customer" && !navbarHidden && (
        <div style={{
          display:"flex",
          background:(()=>{
            const hex = settings.navbarBgColor || "#2C1509";
            const op  = settings.navbarOpacity !== undefined ? settings.navbarOpacity : 1.0;
            if (op >= 0.99) return hex;
            const r = parseInt(hex.slice(1,3),16);
            const g = parseInt(hex.slice(3,5),16);
            const b = parseInt(hex.slice(5,7),16);
            return `rgba(${r},${g},${b},${op})`;
          })(),
          padding:"0",
          position:"relative",
          zIndex:200,
          alignItems:"stretch",
          boxShadow: (settings.navbarOpacity !== undefined && settings.navbarOpacity < 0.1)
            ? "none"
            : "0 3px 18px rgba(44,21,9,0.6)",
          overflow:"hidden",
          backdropFilter: (settings.navbarOpacity !== undefined && settings.navbarOpacity < 0.99) ? "blur(8px)" : "none",
          WebkitBackdropFilter: (settings.navbarOpacity !== undefined && settings.navbarOpacity < 0.99) ? "blur(8px)" : "none",
        }}>
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",flex:1,position:"relative"}}>
            {/* Logo — 5× kliknięcie otwiera panel logowania */}
            <div onClick={handleLogoTap} style={{position:"relative",cursor:"pointer",flexShrink:0,userSelect:"none"}}>
              {settings.logo
                ? <img src={settings.logo} alt="" style={{height:36,width:36,borderRadius:"50%",objectFit:"cover",border:"2px solid rgba(255,255,255,0.25)",display:"block"}}/>
                : <span style={{fontSize:22,filter:"drop-shadow(0 1px 2px rgba(0,0,0,0.4))",display:"block"}}>🥐</span>}

            </div>
            <button
              style={{
                flex:1,padding:"8px 14px",borderRadius:9,
                background:"rgba(255,255,255,0.08)",
                border:"1.5px solid rgba(255,255,255,0.18)",
                color:"#FFE8A0",fontWeight:700,cursor:"pointer",fontSize:13,
                position:"relative",letterSpacing:0.3,
                textShadow:"0 1px 3px rgba(0,0,0,0.4)",
                transition:"all .15s",
              }}
              onClick={handleOrderTap}>
              🛍 Zamówienia
              {orderTaps>0&&!adminUnlocked&&(
                <span style={{position:"absolute",top:4,right:4,width:6,height:6,borderRadius:"50%",
                  background:"#FFD700",opacity:orderTaps/5,transition:"opacity .2s",boxShadow:"0 0 4px #FFD700"}}/>
              )}
            </button>
            {adminUnlocked&&(
              <button
                style={{
                  padding:"8px 14px",borderRadius:9,
                  background:"rgba(255,255,255,0.08)",
                  border:"1.5px solid rgba(255,255,255,0.18)",
                  color:"#FFE8A0",fontWeight:700,cursor:"pointer",fontSize:13,
                  textShadow:"0 1px 3px rgba(0,0,0,0.4)",
                  animation:"popIn .3s ease",position:"relative",
                }}
                onClick={()=>setView("admin")}>
                ⚙ Admin
                {pendingCount>0&&<span style={S.badge}>{pendingCount}</span>}
              </button>
            )}
          </div>
        </div>
      )}
      <div style={{minHeight:"100vh"}}>
        <div style={{flex:1,overflowX:"hidden"}}>
        {view==="online"
          ?<OnlineShop
              onlineMenu={onlineMenu} onlineCats={onlineCats}
              menu={menu} cats={cats}
              settings={settings} ac={ac}
              onBack={()=>setView("customer")}
              onSaveClient={clientData=>{
                setClients(p=>{
                  const exists=(p||[]).find(c=>c.phone&&c.phone.replace(/\s/g,"")===clientData.phone.replace(/\s/g,""));
                  if(exists) return p;
                  return [...(p||[]),clientData];
                });
              }}
              onPlace={o=>{
                handlePlaceOrder(o);
                setOnlineOrders(p=>[...(p||[]),{...o,orderStatus:"new"}].slice(-500));
                setView("customer");
              }}/>
        :view==="customer"
          ?<CustomerApp menu={menu} cats={cats} settings={settings} tables={tables} setTables={setTables}
              orders={orders} campaigns={campaigns} vouchers={vouchers} usedCodes={usedCodes}
              setUsedCodes={setUsedCodes}
              onPlaceOrder={handlePlaceOrder}
              staffCalls={staffCalls} setStaffCalls={setStaffCalls}
              clients={clients} setClients={setClients}
              onlineMenu={onlineMenu} onlineCats={onlineCats} setOnlineOrders={setOnlineOrders}
              onOnlineShop={()=>setView("online")}
              onAdminTap={()=>setView("admin")}/>
          :adminUser
            ?<AdminPanel user={adminUser} users={users} setUsers={setUsers} menu={menu} setMenu={setMenu}
                cats={cats} setCats={setCats} settings={settings} setSettings={setSettings}
                campaigns={campaigns} setCampaigns={setCampaigns}
                vouchers={vouchers} setVouchers={setVouchers}
                tables={tables} setTables={setTables}
                usedCodes={usedCodes} setUsedCodes={setUsedCodes} fbConfig={fbConfig} setFbConfig={setFbConfig}
                orders={orders} setOrders={setOrders} onLogout={()=>{setAdminUser(null);setView("customer");}}
                staffCalls={staffCalls} setStaffCalls={setStaffCalls}
                clients={clients} setClients={setClients}
                customRoles={customRoles} setCustomRoles={setCustomRoles}
                logs={logs} setLogs={setLogs}
                onlineMenu={onlineMenu} setOnlineMenu={setOnlineMenu}
                onlineCats={onlineCats} setOnlineCats={setOnlineCats}
                onlineOrders={onlineOrders} setOnlineOrders={setOnlineOrders}/>
            :<LoginScreen users={users} setUsers={setUsers} settings={settings} onLogin={setAdminUser} setLogs={setLogs}/>
        }
        </div>
      </div>
    </div>
    </>
    </BreakpointProvider>
    </ErrorBoundary>
  );
}